export type Language = 'en' | 'es' | 'pt' | 'zh' | 'fr' | 'ar' | 'hi' | 'ko' | 'ja' | 'tr';

export const languageNames: Record<Language, string> = {
  en: 'English',
  es: 'Español',
  pt: 'Português',
  zh: '中文',
  fr: 'Français',
  ar: 'العربية',
  hi: 'हिन्दी',
  ko: '한국어',
  ja: '日本語',
  tr: 'Türkçe',
};

// RTL languages
export const rtlLanguages: Language[] = ['ar'];

export const isRTL = (lang: Language): boolean => rtlLanguages.includes(lang);

export const translations: Record<Language, Record<string, string>> = {
  en: {
    // Index
    'title': 'THE HUMAN ROOMS',
    'tagline': 'A collection of rooms that reflect who you are when nobody is watching.',
    'enter': 'Enter',
    'footer': 'Not therapy. Not social. Not productive.',
    
    // Hallway
    'back': '← Back',
    'hallway': 'The Hallway',
    'challenges': 'Challenges',
    'random': 'Random',
    'open_rooms': 'Open Rooms',
    'dark_rooms': 'The Dark Rooms',
    'enter_risk': 'enter at your own risk',
    'premium_rooms': 'Premium Rooms',
    'unlock_to_access': 'Unlock to access',
    'daily_rooms': 'Daily Rooms',
    'refreshes_daily': 'refreshes every day',
    'time_locked': 'Time-Locked Rooms',
    'seasonal_rooms': 'Seasonal Rooms',
    'available_now': 'Available now',
    'fragments': 'Fragments',
    'boss_unlocked': 'Boss Room Unlocked',
    'collect_unlock': 'Collect to unlock Boss Room',
    'dark_fragments': 'Dark Fragments',
    'dark_boss_unlocked': 'Dark Boss Unlocked',
    'hidden_discovered': 'Hidden Room Discovered',
    'stillness_revealed': 'Your stillness revealed something...',
    'constellation_map': 'Constellation Map',
    'your_stats': 'Your Stats',
    'completed': 'Completed',
    'game_rooms': 'Game Rooms',
    'experimental_rooms': 'Experimental Rooms',
    'deep_rooms': 'Deep Rooms',
    'meta_rooms': 'Meta Rooms',
    'search_rooms': 'Search rooms...',
    'no_results': 'No rooms match your search',
    'clear_filters': 'Clear filters',
    'explore': 'Explore',
    'all_categories': 'All',
    'random_room': 'Random Room',
    'ai_reflection': 'AI Reflection',
    'support': 'Support',
    'choose_direction': 'Choose a direction. Some doors respond.',
    'rooms_explored': 'rooms explored',
    'your_journey': 'Your Journey',
    'complete_rooms_fragments': 'Complete rooms to collect fragments.',
    'no_rooms_completed': 'No rooms completed yet.',
    
    // Daily Challenges
    'daily_challenges': 'Daily Challenges',
    'challenges_completed': 'completed',
    'challenges_total': 'total',
    'resets_midnight': 'Resets at midnight',
    
    // Weekly Summary
    'weekly_summary': 'Weekly Summary',
    'week': 'Week',
    'this_week': 'This Week',
    'rooms': 'Rooms',
    'days': 'Days',
    'vs_last_week': 'vs last week',
    'refresh_insight': 'Refresh insight',
    
    // About
    'what_is_this': 'What is this place?',
    'not_productivity': 'The Human Rooms is not a productivity tool.',
    'not_social': 'It is not a social network.',
    'not_therapy': 'It is not therapy.',
    'pause_experiences': 'It is a collection of small experiences designed to make you pause.',
    'each_room': 'Each room takes 30 seconds to 2 minutes.',
    'make_think': 'Some rooms will make you think.',
    'make_uncomfortable': 'Some will make you uncomfortable.',
    'make_calm': 'Some will make you calm.',
    'make_aware': 'All will make you aware.',
    'no_notifications': 'There are no notifications here.',
    'no_points': 'No points to collect.',
    'no_validation': 'No social validation.',
    'just_truth': 'Just you, a room, and the truth.',
    'enter_rooms': 'Enter the rooms',
    
    // Unlock
    'unlock_title': 'Unlock Premium Rooms',
    'unlock_subtitle': 'Access deeper experiences',
    'lifetime': 'Lifetime Access',
    'monthly': 'Monthly',
    'one_time': 'One-time payment',
    'per_month': 'per month',
    'unlock_now': 'Unlock Now',
    'subscribe': 'Subscribe',
    'already_premium': 'You have premium access',
    'premium_features': 'Premium Features',
    'redeem_code': 'Have a code?',
    'redeem_placeholder': 'Enter redeem code',
    'redeem_button': 'Redeem',
    'redeem_success': 'Code redeemed successfully!',
    'redeem_error': 'Invalid or expired code',
    
    // Stats
    'total_time': 'Total Time',
    'rooms_visited': 'Rooms Visited',
    'total_visits': 'Total Visits',
    'most_visited': 'Most Visited',
    'longest_session': 'Longest Session',
    'fragments_collected': 'Fragments Collected',
    'completed_rooms': 'Completed Rooms',
    'privacy_note': 'All data stored locally on your device',
    
    // Common
    'loading': 'Loading...',
    'error': 'Error',
    'continue': 'Continue',
    'submit': 'Submit',
    'cancel': 'Cancel',
    'close': 'Close',
    'next': 'Next',
    'previous': 'Previous',
    'start': 'Start',
    'stop': 'Stop',
    'yes': 'Yes',
    'no': 'No',
    'leave_room': '← Leave room',
    'return': '← Return',
    'return_hallway': 'Return to Hallway',
    'escape': '← Escape',
    'run': '← Run',
    'leave': '← Leave',
    'begin': 'Begin',
    'watch': 'Watch...',
    'correct': 'Correct',
    'round': 'Round',
    'enter_again': 'Enter again',
    'steps': 'Steps',
    'depth': 'Depth',
    
    // Keyboard Shortcuts
    'keyboard_shortcuts': 'Keyboard Shortcuts',
    'shortcut_hallway': 'Go to Hallway',
    'shortcut_random': 'Random Room',
    'shortcut_back': 'Go Back',
    'shortcut_bookmark': 'Toggle Bookmark',
    'shortcut_reading': 'Toggle Reading Mode',
    'shortcut_search': 'Focus Search',
    'shortcut_stats': 'View Stats',
    'shortcut_achievements': 'View Achievements',
    'shortcut_cat_nav': 'Navigation',
    'shortcut_cat_rooms': 'Rooms',
    'shortcut_cat_access': 'Accessibility',
    'press_to_close': 'Press',
    'to_close': 'to close',
    'or': 'or',
    
    // Bookmarks
    'add_bookmark': 'Add Bookmark',
    'remove_bookmark': 'Remove Bookmark',
    'bookmarks': 'Bookmarks',
    'bookmarked_rooms': 'Bookmarked',
    'no_bookmarks': 'No bookmarks yet',
    
    // Reading Mode
    'reading_mode': 'Reading Mode',
    'reading_mode_on': 'Reading mode enabled',
    'reading_mode_off': 'Reading mode disabled',
    
    // Settings Page
    'settings': 'Settings',
    'settings_subtitle': 'Customize your journey',
    'language': 'Language',
    'ambient_audio': 'Ambient Audio',
    'ambient_audio_desc': 'Background soundscapes for rooms',
    'reduced_motion': 'Reduced Motion',
    'reduced_motion_desc': 'Minimize animations and transitions',
    'data_management': 'Data Management',
    'export_journey': 'Export Journey Data',
    'clear_all_data': 'Clear All Data',
    'audio_enabled': 'Audio Enabled',
    'audio_enabled_desc': 'Ambient sounds will play in rooms',
    'audio_disabled': 'Audio Disabled',
    'audio_disabled_desc': 'Rooms will be silent',
    'reduced_motion_enabled': 'Reduced Motion Enabled',
    'reduced_motion_disabled': 'Reduced Motion Disabled',
    'data_exported': 'Data Exported',
    'data_exported_desc': 'Your journey has been saved',
    'clear_data_confirm': 'Are you sure? This will erase all your progress.',
    'data_cleared': 'Data Cleared',
    'data_cleared_desc': 'Your journey has been reset',
    'shared_room': 'Shared Room',
    'imagine_room': 'Imagine',
    'dream_room': 'Dreams',
    'future_room': 'Future',
    
    // Theme settings
    'theme': 'Theme',
    'theme_dark': 'Dark',
    'theme_dark_desc': 'Pure darkness with subtle grain',
    'theme_ash': 'Ash',
    'theme_ash_desc': 'Embers rising from the aftermath',
    'theme_twilight': 'Twilight',
    'theme_twilight_desc': 'Stars twinkling in the between',
    'theme_void': 'Void',
    'theme_void_desc': 'Nothing remains but shadows',
    'theme_eclipse': 'Eclipse',
    'theme_eclipse_desc': 'Light devoured by darkness',
    'theme_dawn': 'Dawn',
    'theme_dawn_desc': 'First light of awakening',
    'theme_changed': 'Theme updated',
    'font_size': 'Font Size',
    'font_small': 'Small',
    'font_medium': 'Medium',
    'font_large': 'Large',
    'font_size_changed': 'Font size updated',
    'high_contrast': 'High Contrast',
    'high_contrast_desc': 'Increase text and border contrast',
    'high_contrast_enabled': 'High contrast enabled',
    'high_contrast_disabled': 'High contrast disabled',
    
    // Ambient Music Control
    'ambient_music': 'Ambient Music',
    'ambient_music_desc': 'Control background soundscapes',
    'volume': 'Volume',
    'ambient_track': 'Ambient Track',
    'playing': 'Playing',
    'play': 'Play',
    'ambient_default': 'Calm',
    'ambient_night': 'Night',
    'ambient_scary': 'Eerie',
    'ambient_truth': 'Truth',
    'ambient_identity': 'Reflection',
    'ambient_mortality': 'Time',
    'ambient_nothing': 'Void',
    'ambient_meta': 'Digital',
    'ambient_daily': 'Warm',
    'ambient_seasonal': 'Magical',
    'ambient_premium': 'Deep',
    
    // Theme Exclusive Section
    'theme_exclusive_rooms': 'Theme-Exclusive Rooms',
    'theme_exclusive_hint': 'Switch themes in settings to unlock these rooms.',
    'requires_theme': 'Requires',
    'switch_to': 'Switch to',
    
    // Advanced Easter Eggs
    'corner_sequence': 'You traced the boundaries.',
    'morse_sos': 'S.O.S. received.',
    'reverse_scroll': 'Going back... but faster.',
    'midnight_visitor': 'The witching hour welcomes you.',
    'shake': 'You shook something loose.',
    
    // Achievements
    'achievements': 'Achievements',
    'achievements_subtitle': 'Milestones on your journey',
    'points': 'points',
    'unlocked': 'unlocked',
    'exploration': 'Exploration',
    'consistency': 'Consistency',
    'discovery': 'Discovery',
    'special': 'Special',
    'all': 'All',
    
    // Imagine Yourself Room
    'imagine_title': 'Imagine Yourself',
    'imagine_subtitle': 'Describe how you see yourself. Let the room show you what emerges from your words.',
    'imagine_placeholder': 'I see myself as someone who...\n\nDescribe your essence, your nature, how you feel inside. Not your appearance, but who you are.',
    'imagine_hint': 'Be honest. The image reflects your words.',
    'imagine_generate': 'See Yourself',
    'imagine_generating': 'Creating...',
    'imagine_try_again': 'Describe Again',
    'imagine_result_alt': 'Your self-portrait',
    'imagine_quote': 'We don\'t see ourselves clearly. We see what we believe.',
    'imagine_empty_error': 'Write something about yourself first',
    'imagine_short_error': 'Tell us more about yourself',
    'rate_limit_error': 'Too many requests. Please wait a moment.',
    'credits_error': 'Service temporarily unavailable',
    'generation_error': 'Could not create image. Try again.',
    
    // Dream Visualization Room
    'dream_title': 'Dream Visualization',
    'dream_subtitle': 'Describe a recurring dream. Watch it materialize before you.',
    'dream_placeholder': 'I keep dreaming about...\n\nDescribe the imagery, the feelings, the recurring elements. What lingers when you wake?',
    'dream_hint': 'Dreams speak in symbols.',
    'dream_generate': 'Visualize Dream',
    'dream_generating': 'Dreaming...',
    'dream_try_again': 'Dream Again',
    'dream_result_alt': 'Your dream visualization',
    'dream_quote': 'Dreams are the subconscious mind trying to tell us something.',
    'dream_empty_error': 'Describe your dream first',
    'dream_short_error': 'Tell us more about your dream',
    'room_dream-visualization': 'Dream Visualization',
    
    // Future Self Room
    'future_title': 'Future Self',
    'future_subtitle': 'Describe who you want to become. See your potential.',
    'future_placeholder': 'In the future, I see myself as...\n\nDescribe the person you are working towards becoming. The qualities, the achievements, the peace.',
    'future_hint': 'Aspiration shapes reality.',
    'future_generate': 'See Your Future',
    'future_generating': 'Envisioning...',
    'future_try_again': 'Envision Again',
    'future_result_alt': 'Your future self',
    'future_quote': 'The future is not something we enter. It is something we create.',
    'future_empty_error': 'Describe your future self first',
    'future_short_error': 'Tell us more about your future self',
    'room_future-self': 'Future Self',
    
    // AI Reflection Room
    'reflection_room_title': 'The Reflection Room',
    'reflection_room_desc': 'Write something honest. The mirror will show you what you might not see.',
    'reflection_room_privacy': 'Nothing is saved. Nothing is shared.',
    'reflection_begin': 'begin',
    'reflection_prompt': 'What is on your mind? Write freely.',
    'reflection_placeholder': 'Begin here...',
    'reflection_characters': 'characters',
    'reflection_min_chars': 'at least 20 needed',
    'reflection_send': 'send to the mirror',
    'reflection_waiting': 'The mirror considers...',
    'reflection_fragment_label': 'fragment',
    'reflection_label': 'reflection',
    'reflection_question_label': 'the question you might avoid',
    'reflection_leave': 'leave the mirror',
    'reflection_error': 'The mirror is clouded',
    'reflection_default_quote': 'The mirror remembers',
    
    // Updates Page
    'updates': 'Updates',
    'updates_title': 'What\'s New',
    'updates_subtitle': 'Recent changes and improvements',
    'updates_footer': 'Building rooms, one update at a time.',
    'new': 'NEW',
    'subscribe_rss': 'Subscribe via RSS',
    'latest': 'LATEST',
    'whats_new_intro': 'Latest updates:',
    'view_all_updates': 'View all updates',
    'update_updates_page': 'Added Updates page for patch notes',
    'update_full_i18n': 'Complete 6-language translation for all rooms',
    'update_hallway_animations': 'Smooth category switching animations',
    'update_daily_challenges': 'Improved Daily Challenges display',
    'update_game_rooms': 'Added 15 new game rooms',
    'update_experimental_rooms': 'Added 14 experimental rooms',
    'update_random_room': 'Random room shuffle button',
    'update_stats_route': 'Fixed stats page routing',
    'update_streak_system': 'Daily streak tracking system',
    'update_weekly_summary': 'Weekly progress summaries',
    'update_rtl_support': 'Right-to-left language support',
    'update_dark_rooms': 'New dark/horror room category',
    'update_boss_rooms': 'Boss rooms with fragment requirements',
    'update_audio_system': 'Ambient audio for rooms',
    'update_constellation_map': 'Visual journey constellation map',
    'update_fragment_system': 'Room completion fragment collection',
    'update_mobile_layout': 'Mobile responsive improvements',
    'update_multi_language': 'Multi-language support (6 languages)',
    'update_premium_system': 'Premium rooms and unlock system',
    'update_room_endings': 'Unique room ending quotes',
    'update_ui_redesign': 'Complete UI design refresh',
    'update_theme_effects': 'Theme-specific visual effects (embers, stars, glitches)',
    'update_void_theme': 'New Void theme - absolute darkness mode',
    'update_achievements': 'Achievement system with 19 milestones',
    'update_ai_reflection': 'AI Reflection room with personalized insights',
    'update_settings_redesign': 'Redesigned settings with 4 atmospheric themes',
    'update_theme_exclusive_rooms': 'Theme-exclusive rooms unlocked by atmosphere',
    'update_discovery_localization': 'Localized easter egg discovery messages',
    'update_site_mood_system': 'Site mood system - the site decides its emotions',
    'update_voice_imperfection': 'Imperfect voice rooms with breathing and pauses',
    'update_mobile_optimization': 'Mobile optimization with 48px tap targets',
    'update_interaction_clues': 'Subtle interaction clues for new users',
    'update_theme_exclusive_hallway': 'Theme-exclusive rooms visible in Hallway with unlock indicators',
    'update_advanced_easter_eggs': 'Advanced easter eggs: corner sequence, morse code, midnight visitor',
    'update_hidden_discoveries': 'Hidden text reveals and secret discovery mechanics',
    'update_dual_mode_system': 'Classic and Alternate dual-mode experience system',
    'update_alternate_mode': 'Alternate mode - a parallel, experimental reality',
    'update_hidden_discovery': 'Hidden discovery triggers: triple tap, scroll patterns, idle awakening',
    'update_mode_effects': 'Mode-specific visual effects, glitches, and ambient audio',
    'update_translation_navigator': 'Translation Navigator - visual coverage dashboard',
    'update_full_localization': 'Near 100% translation coverage across all 6 languages',
    'update_meta_room_translations': 'Localized meta room content (The Refusal, Parallel You, Your Shadow)',
    'update_animated_timeline': 'Animated changelog timeline with scroll-triggered animations',
    'update_toast_notifications': 'Update toast notification for first visit after new version',
    'update_rss_feed': 'RSS feed for changelog subscriptions',
    'update_bookmarked_filter': 'Bookmarked Rooms filter tab in Hallway for quick access to favorites',
    'update_keyboard_shortcuts': 'Keyboard shortcuts overlay (press ? to view)',
    'update_reading_mode': 'Reading mode for enhanced typography and focus',
    'update_accessibility': 'Improved focus indicators and keyboard navigation',
    'update_ambient_crossfade': 'Smooth fade-in/fade-out transitions between ambient tracks',
    'update_settings_audio': 'Centralized audio controls in Settings page',
    'update_arabic_complete': 'Complete Arabic translation coverage',
    'update_duplicate_icon_fix': 'Fixed duplicate audio icons in Hallway header',
    'update_ritual_rooms': 'Ritual Rooms - Morning, Evening, and Weekly scheduled experiences',
    'update_ai_voice_mode': 'AI Guide Voice Mode using Web Speech API',
    'update_proactive_guide': 'Proactive AI Guide that appears based on behavior patterns',
    'update_offline_support': 'Offline support via Service Worker',
    'update_community_submissions': 'Community room idea submissions',
    'update_constellation_interactive': 'Interactive Constellation Map with zoom/pan and animated trails',
    'update_notification_system': 'Browser notification system for time-locked content',
    'update_journey_export': 'Journey export/import functionality (JSON)',
    'update_game_rooms_new': 'New game rooms: Entropy Garden and Memory Palace',
    'update_new_achievements': '15 new achievements across all categories',
    'update_weather_system': 'Weather system affecting site atmosphere',
    'update_personal_timeline': 'Personal Timeline page for journey history',
    'update_ai_rooms_new': 'New AI rooms: Letters Never Sent and Breath Sync',
    'update_recommended_rooms': 'Personalized "Recommended For You" section based on visit history',
    'update_daily_highlight': 'Prominent Daily Highlight card featuring time-appropriate rooms',
    'update_exit_ritual': 'Exit ritual with session summary and graceful closure',
    'update_anonymous_reflections': 'Anonymous reflection sharing in rooms',
    
    // Phase 14-16 Updates
    'update_realtime_social': 'Real-time social: Whisper Wall, Shared Moments, and Collective Memory are now live multiplayer',
    'update_guided_journeys': 'Guided Journeys: Curated room sequences with narrative connectors',
    'update_pwa_polish': 'Mobile PWA polish: Install prompts and improved offline support',
    'update_haptic_feedback': 'Haptic feedback on mobile interactions',
    
    // v3.20.0 Updates
    'update_ui_refresh': 'UI refresh: Rounded cards, category accents, hover lift effects',
    'update_mood_selector': 'Mood check-in: One-tap mood selector that influences recommendations',
    'update_stats_bar': 'Mini stats bar: Rooms explored, streak, and fragments at a glance',
    'update_quick_links_redesign': 'Quick links reorganized into Explore and Your Space rows',
    
    // Mood Selector
    'mood_question': 'How are you feeling right now?',
    'mood_calm': 'Calm',
    'mood_anxious': 'Anxious',
    'mood_curious': 'Curious',
    'mood_heavy': 'Heavy',
    'mood_restless': 'Restless',
    'your_space': 'Your Space',
    'stats_streak': 'day streak',
    
    // Guided Journeys
    'guided_journeys': 'Journeys',
    'guided_journeys_title': 'Guided Journeys',
    'guided_journeys_subtitle': 'Curated room sequences with narrative connectors. Like a guided meditation playlist.',
    'journey_complete': 'Journey Complete',
    'journey_complete_desc': 'You walked the full path. The rooms remember your passage.',
    'more_journeys': 'More Journeys',
    'next_room': 'Next Room',
    'enter_room': 'Enter Room',
    'finish_journey': 'Finish Journey',
    'step': 'Step',
    
    // Journey: The Identity Path
    'journey_identity_title': 'The Identity Path',
    'journey_identity_subtitle': '5 rooms exploring who you are',
    'journey_identity_desc': 'Strip away the labels, the roles, the expectations. What remains?',
    'journey_identity_after_1': 'You wrote one honest thing. Now let\'s go deeper.',
    'journey_identity_before_2': 'If your job disappeared tomorrow, what would be left?',
    'journey_identity_after_2': 'The labels fell away. Something stayed.',
    'journey_identity_before_3': 'Now look at yourself — but through a blur.',
    'journey_identity_after_3': 'Focus reveals less than blur sometimes.',
    'journey_identity_before_4': 'Remove everything. What\'s the last thing to go?',
    'journey_identity_after_4': 'You found the irreducible.',
    'journey_identity_before_5': 'One final test: Watch the thought that won\'t leave.',
    
    // Journey: The Truth Spiral
    'journey_truth_title': 'The Truth Spiral',
    'journey_truth_subtitle': '4 rooms peeling back layers',
    'journey_truth_desc': 'Each room reveals another layer of comfortable lies.',
    'journey_truth_after_1': 'You chose the safe option. Or didn\'t. Either way — noticed.',
    'journey_truth_before_2': 'What shortcuts do you take without thinking?',
    'journey_truth_after_2': 'Patterns are just habits wearing masks.',
    'journey_truth_before_3': 'Now let\'s see where your time actually goes.',
    'journey_truth_after_3': 'The gap between what you say matters and what you spend time on.',
    'journey_truth_before_4': 'Time for the receipt.',
    
    // Journey: Night Descent
    'journey_night_title': 'Night Descent',
    'journey_night_subtitle': '3 rooms for the sleepless',
    'journey_night_desc': 'Best experienced after dark. The rooms get quieter.',
    'journey_night_after_1': 'The thing that keeps you up — it has a shape now.',
    'journey_night_before_2': 'There\'s a thought that comes back. Every night.',
    'journey_night_after_2': 'Naming it doesn\'t stop it. But you see it now.',
    'journey_night_before_3': 'It\'s 3:17 AM somewhere. Maybe here.',
    
    // Journey: Stillness Practice
    'journey_stillness_title': 'Stillness Practice',
    'journey_stillness_subtitle': '4 rooms of quiet',
    'journey_stillness_desc': 'Slow down. Then stop. Then stay.',
    'journey_stillness_after_1': 'Your breathing found a rhythm.',
    'journey_stillness_before_2': 'Now: nothing. Can you sit with nothing?',
    'journey_stillness_after_2': 'You stayed. That\'s enough.',
    'journey_stillness_before_3': 'Find a color. Stay in it.',
    'journey_stillness_after_3': 'Comfort doesn\'t need a reason.',
    'journey_stillness_before_4': 'This moment. Once. Now.',
    
    // Journey: Mortality Mirror
    'journey_mortality_title': 'Mortality Mirror',
    'journey_mortality_subtitle': '3 rooms of finite time',
    'journey_mortality_desc': 'Not a warning. Just a count.',
    'journey_mortality_after_1': 'The number is smaller than you thought.',
    'journey_mortality_before_2': 'Now see it in weeks. All of them.',
    'journey_mortality_after_2': 'Each filled square — gone.',
    'journey_mortality_before_3': 'If nothing changes, here\'s what happens.',
    
    // PWA
    'pwa_install_title': 'Install The Human Rooms',
    'pwa_install_desc': 'Add to your home screen for offline access and a native app experience.',
    'pwa_install_button': 'Install',
    
    // Live indicator
    'whisper_live_note': 'Others will see it in real-time.',
     
     // Phase 7 - Regret Machine Room
     'regret_machine_title': 'The Regret Machine',
     'regret_machine_subtitle': 'Describe a decision you wish you had made differently. The machine will show you what might have been.',
     'regret_placeholder': 'I regret...\n\nDescribe a choice you made that you wish had gone differently. What would you have done instead?',
     'regret_hint': 'This is not about blame. It is about understanding.',
     'regret_explore': 'Explore the Timeline',
     'regret_generating': 'Reconstructing an alternate reality...',
     'regret_alternate_timeline': 'The Alternate Timeline',
     'regret_explore_another': 'Explore Another',
     'regret_error_short': 'Please describe your regret in more detail',
     'room_regret-machine': 'The Regret Machine',
     'update_regret_machine': 'The Regret Machine - AI-powered alternate timeline narratives',
     
     // Phase 7 - Memory Reconstruction Room
     'memory_reconstruction_title': 'Memory Reconstruction',
     'memory_reconstruction_subtitle': 'Describe a fading memory. Let us help you feel your way back into it.',
     'memory_placeholder': 'I remember...\n\nDescribe a distant or fragmented memory. What fragments remain? What feelings linger?',
     'memory_timeframe_placeholder': 'When was this? (e.g., "childhood", "ten years ago")',
     'memory_hint': 'Memory is not a recording. It is a reconstruction.',
     'memory_reconstruct': 'Reconstruct Memory',
     'memory_reconstructing': 'Piecing together the fragments...',
     'memory_reconstructed': 'The Reconstructed Memory',
     'memory_explore_another': 'Explore Another Memory',
     'memory_error_short': 'Please describe your memory in more detail',
     'room_memory-reconstruction': 'Memory Reconstruction',
     'update_memory_reconstruction': 'Memory Reconstruction - AI fills in sensory details of fading memories',
    
    // Exit Ritual & Session
    'close_the_door': 'Close the Door',
    'stay_longer': 'Stay a little longer',
    'session_summary': 'This Session',
    'time_spent': 'spent here',
    'rooms_visited_session': 'rooms visited',
    'total_journey': 'Total journey',
    'the_rooms_wait': 'The rooms will wait for you.',
    
    // Recommendations
    'recommended_for_you': 'Recommended For You',
    'continue_journey': 'Continue Your Journey',
    'todays_room': "Today's room",
    'live': 'Live',
    
    // Anonymous Reflections
    'anonymous_reflections': 'Anonymous Reflections',
    'visitors': 'visitors',
    'no_reflections_yet': 'No reflections yet. Be the first.',
    'share_reflection': 'Share your reflection (anonymous)',
    'your_reflection': 'Your reflection...',
    'anonymous_note': 'Anonymous and stored locally',
    'reflection_shared': 'Your reflection has been shared.',
    
    // Interaction hints
    'hint_click_enter': 'Click to enter',
    'hint_take_time': 'Take your time',
    'hint_each_room': 'Each room holds a question',
    'hint_no_rush': 'There is no rush here',
    
    // Behavior reflections
    'behavior_stillness': 'You paused.',
    'behavior_patience': 'You wait longer than most.',
    'behavior_silence': 'The silence speaks.',
    
    // Easter Egg Discoveries
    'discovery_label': 'Discovery',
    'discovery_konami': 'You know the old ways.',
    'discovery_secret_sequence': 'You spelled the truth.',
    'discovery_long_press': 'Patience reveals secrets.',
    'discovery_triple_click': 'Persistence pays off.',
    'discovery_silence': 'Stillness speaks.',
    
    // Theme-Exclusive Rooms
    'ash_exclusive': 'Ash Theme Exclusive',
    'twilight_exclusive': 'Twilight Theme Exclusive',
    'void_exclusive': 'Void Theme Exclusive',
    'theme_required': 'Requires {theme} theme',
    
    // Ash Embers Room
    'ash_embers_title': 'From the Ashes',
    'ash_embers_line1': 'Everything burns eventually.',
    'ash_embers_line2': 'What matters is what rises after.',
    'ash_embers_line3': 'Touch the screen. Create embers.',
    'ash_embers_quote': 'From ashes, we remember what once burned.',
    'room_ash-embers': 'Ash & Embers',
    'sub_ash-embers': 'Only visible in Ash theme',
    
    // Twilight Stars Room
    'twilight_stars_title': 'Between Day and Night',
    'twilight_stars_line1': 'The stars remember everyone who looked up.',
    'twilight_stars_line2': 'Connect them. Make your own constellation.',
    'twilight_stars_quote': 'In twilight, we see what daylight hides.',
    'room_twilight-stars': 'Twilight Stars',
    'sub_twilight-stars': 'Only visible in Twilight theme',
    
    // Void Abyss Room
    'void_abyss_title': 'The Abyss',
    'void_abyss_line1': 'Look into the void. The void looks back.',
    'void_word_1': 'nothing',
    'void_word_2': 'empty',
    'void_word_3': 'silence',
    'void_word_4': 'gone',
    'void_word_5': 'alone',
    'void_word_6': 'end',
    'void_revealed': 'revealed',
    'void_abyss_quote': 'In absolute darkness, we finally see ourselves.',
    'room_void-abyss': 'The Abyss',
    'sub_void-abyss': 'Only visible in Void theme',
    
    // Game UI
    'game_watching': 'watching...',
    'game_repeat': 'repeat',
    'game_echo_fades': 'the echo fades...',
    'game_escaped': 'You escaped.',
    'game_found_exit': 'You found the exit.',
    'game_looping': 'You are looping.',
    'game_deeper': 'Going deeper...',
    'game_doors_shift': 'The doors shift.',
    'maze_left': 'left',
    'maze_right': 'right',
    'maze_forward': 'forward',
    'maze_back': 'back',
    'maze_past': 'past',
    'maze_future': 'future',
    'maze_here': 'here',
    'maze_there': 'there',
    'maze_truth': 'truth',
    'maze_lies': 'lies',
    'maze_light': 'light',
    'maze_dark': 'dark',
    'maze_memory': 'memory',
    'maze_dream': 'dream',
    'maze_silence': 'silence',
    'maze_noise': 'noise',
    
    // Daily Joke Room
    'joke_number': 'Joke #{number}',
    'joke_today': "Today's Joke",
    'joke_reveal': 'Reveal the punchline',
    'joke_come_back': 'Come back tomorrow for a new joke.',
    'joke_quote': 'Laughter is the shortest distance between two people.',
    
    // Daily Quote Room
    'quote_day_number': 'Day {day}',
    'quote_come_back': 'Come back tomorrow for a new quote.',
    
    // Milestones
    'milestone_reached': 'milestone reached',
    'tap_to_continue': 'tap to continue',
    'milestone_first-room': 'First Door Opened',
    'milestone_first-room_desc': 'You took the first step into the unknown.',
    'milestone_10-rooms': 'Ten Doors Deep',
    'milestone_10-rooms_desc': 'The hallway remembers you now.',
    'milestone_25-rooms': 'A Quarter Century',
    'milestone_25-rooms_desc': 'Each room left a mark. You carry them all.',
    'milestone_50-rooms': 'Half the Map',
    'milestone_50-rooms_desc': 'You have explored more than most ever will.',
    'milestone_100-rooms': 'The Centurion',
    'milestone_100-rooms_desc': 'A hundred doors, a hundred truths. You are not the same person who entered.',
    'milestone_7-streak': 'Seven Days',
    'milestone_7-streak_desc': 'A week of returning. This is becoming ritual.',
    'milestone_30-streak': 'Thirty Days',
    'milestone_30-streak_desc': 'A month. The rooms have become part of you.',
    
    // Leaderboard
    'leaderboard': 'Community Leaderboard',
    'leaderboard_subtitle': 'anonymous · collective · no competition',
    'collective_time': 'collective time',
    'achievements_count': 'achievements',
    'most_visited_rooms': 'Most Visited Rooms',
    'most_time_spent': 'Most Time Spent',
    'most_unlocked_achievements': 'Most Unlocked Achievements',
    'no_achievements_yet': 'No achievements unlocked yet.',
    
    // Transfer Data
    'transfer_data': 'Transfer Data',
    'transfer_data_desc': 'Move your progress to another device using a one-time code.',
    'generate_transfer_code': 'Generate Transfer Code',
    'enter_transfer_code': 'Enter code...',
    'transfer_code_expires': 'Code expires in 24 hours and can only be used once.',
    'generating': 'Generating...',
    'import': 'Import',
    'copied': 'Copied!',
    
    // Waiting Game Room
    'waiting_instruction': 'Don\'t move. Don\'t click. Just wait.',
    'waiting_slow': 'Slowing down is harder than it sounds.',
    'waiting_unfamiliar': 'This stillness feels unfamiliar, doesn\'t it?',
    'waiting_most': 'Most people leave by now.',
    'waiting_learning': 'You\'re learning something about yourself.',
    'waiting_rewards': 'Patience has its own rewards.',
    'waiting_seconds': 'seconds of stillness',
    'waiting_waiting': 'waiting...',
    'waiting_moved': 'You\'ve moved {count} times.',
    'waiting_waited': 'You waited. That\'s rare.',
    'waiting_most_cant': 'Most can\'t sit with nothing. You did.',
    'waiting_quote': 'The present moment is the only moment available to us, and it is the door to all moments.',
    
    // Experience Mode
    'experience_mode': 'Experience Mode',
    'classic_mode': 'Classic',
    'classic_mode_desc': 'The familiar reality',
    'alternate_mode': '▒ Alternate ▒',
    'alternate_mode_desc': '[ Y.o" u sh0u"ldnt b.e h"ere ]',
    'switch_to_alternate': '░▓█ descend █▓░',
    'switch_to_classic': '← Return to surface',
    'enter_parallel_reality': '[ The d00r is 0pen ]',
    'return_to_familiar': 'Return to the familiar',
    'something_shifted': '[ S0mething shifted ]',
    'reality_feels_different': '[ Reality feels different n0w ]',
    'alternate_tagline': 'The s.ame r00ms. D1fferent truths.',
    'alternate_enter': 'Descend',
    'alternate_footer': '[ Th1ngs are n0t what they seem ]',
    'alternate_rooms': '░▓ P.ar4llel Re4lity ▓░',
    'alternate_rooms_hint': '[ these r00ms exist only in the p4rallel ]',
    
    // Room Titles
    'room_how-many-times-left': 'How Many Times Left',
    'room_life-in-weeks': 'Your Life in Weeks',
    'room_delay-button': 'The Delay Button',
    'room_one-honest-sentence': 'One Honest Sentence',
    'room_this-moment': 'This Moment Will Never Happen Again',
    'room_reality-receipt': 'Reality Receipt',
    'room_nothing-happens': 'Nothing Happens Here',
    'room_default-choice': 'Your Default Choice',
    'room_thought-loop': 'The Thought Loop',
    'room_expect-vs-happens': 'What You Expect vs What Happens',
    'room_question-never-answer': 'The Question You Never Answer',
    'room_not-special': "You're Not Special (And That's Okay)",
    'room_shortcut-always-take': 'The Shortcut You Always Take',
    'room_blurred-self': 'Blurred Self',
    'room_image-zooming': 'The Image You Keep Zooming',
    'room_comfort-color': 'The Comfort Color',
    'room_line-you-crossed': 'The Line You Crossed',
    'room_read-aloud': 'Read This Aloud',
    'room_voice-without-meaning': 'Your Voice Without Meaning',
    'room_sentence-you-avoid': 'The Sentence You Avoid',
    'room_if-nothing-changes': 'If Nothing Changes',
    'room_real-priorities': 'Your Real Priorities',
    'room_last-conversation': 'The Last Conversation',
    'room_who-without-job': 'Who Are You Without Your Job?',
    'room_comfort-zone-scanner': 'Comfort Zone Scanner',
    'room_say-it-once': 'Say It Once',
    'room_listen-to-yourself': 'Listen to Yourself',
    'room_life-as-shapes': 'Your Life as Shapes',
    'room_face-without-features': 'Face Without Features',
    'room_what-keeps-awake': 'What Keeps You Awake',
    'room_you-at-317am': 'You at 3:17 AM',
    'room_thought-that-returns': 'The Thought That Returns',
    'room_room-you-visit-most': 'The Room You Visit Most',
    'room_room-you-avoid': 'The Room You Avoid',
    'room_left-too-early': 'You Left Too Early',
    'room_daily-quote': 'Quote of the Day',
    'room_daily-joke': 'Joke of the Day',
    'room_theyre-watching': "They're Watching",
    'room_the-last-visitor': 'The Last Visitor',
    'room_dont-look-away': "Don't Look Away",
    'room_the-whisper': 'The Whisper',
    'room_you-forgot-something': 'You Forgot Something',
    'room_the-mirror-lies': 'The Mirror Lies',
    'room_something-follows': 'Something Follows',
    'room_the-countdown': 'The Countdown',
    'room_eyes-in-the-dark': 'Eyes in the Dark',
    'room_the-voice': 'The Voice',
    'room_it-knows-your-name': 'It Knows Your Name',
    'room_the-door': 'The Door',
    'room_boss-room-free': 'The Sum of Your Truths',
    'room_boss-room-horror': 'The Heart of Fear',
    'room_boss-room-premium': 'The Deepest Truth',
    'room_mouse-tracker': 'The Mouse Tracker',
    'room_scroll-confession': 'Scroll Confession',
    'room_waiting-game': 'The Waiting Game',
    'room_day-in-pixels': 'Your Day in Pixels',
    'room_memory-maze': 'Memory Maze',
    'room_decision-shadow': 'Decision Shadow',
    'room_anonymous-mirrors': 'Anonymous Mirrors',
    'room_collective-room': 'The Collective Room',
    'room_secret-room': 'The Secret Room',
    'room_glitch-room': 'Unstable Room',
    'room_full-moon-room': 'Full Moon Room',
    'room_weekend-room': 'Weekend Room',
    'room_317am-room': '3:17 AM Room',
    'room_idle-room': 'The Still Room',
    'room_invite-friend-room': 'The Shared Room',
    'room_halloween-room': 'The Veil Room',
    'room_winter-solstice-room': 'The Longest Night',
    'room_new-year-room': 'Letter to Future Self',
    // Game Rooms
    'room_memory-puzzle': 'Memory Puzzle',
    'room_shadow-chase': 'Shadow Chase',
    'room_timing-room': 'Timing Room',
    'room_pattern-whisper': 'Pattern Whisper',
    'room_silence-puzzle': 'Silence Puzzle',
    'room_decay-game': 'Decay Game',
    'room_observation-test': 'Observation Test',
    'room_balance-room': 'Balance Room',
    'room_word-drift': 'Word Drift',
    'room_color-echo': 'Color Echo',
    'room_mirror-tap': 'Mirror Tap',
    'room_maze-of-doors': 'Maze of Doors',
    'room_pulse-match': 'Pulse Match',
    'room_light-path': 'Light Path',
    'room_sound-match': 'Sound Match',
    
    // Experimental Rooms
    'room_mirror-room': 'Mirror Room',
    'room_echo-room': 'Echo Room',
    'room_pulse-room': 'Pulse Room',
    'room_vanishing-room': 'Vanishing Room',
    'room_letter-room': 'Letter Room',
    'room_empty-room': 'Empty Room',
    'room_reverse-room': 'Reverse Room',
    'room_whisper-room': 'Whisper Room',
    'room_shadow-room': 'Shadow Room',
    'room_labyrinth-room': 'Labyrinth Room',
    'room_color-room': 'Color Room',
    'room_surrender-room': 'Surrender Room',
    'room_noise-room': 'Noise Room',
    'room_observer-room': 'Observer Room',
    'room_imagine-yourself': 'Imagine Yourself',
    'room_ai-reflection-room': 'AI Reflection Room',
    // Alternate Mode Exclusive Rooms
    'room_the-other-side': 'The Other Side',
    'room_the-unraveling': 'The Unraveling',
    'room_the-echo': 'The Echo',
    
    // Room Subtitles
    'sub_how-many-times-left': 'A count of what remains',
    'sub_life-in-weeks': "4,000 squares. That's all.",
    'sub_delay-button': 'It gets harder every time',
    'sub_one-honest-sentence': 'No edits. No delete.',
    'sub_this-moment': 'You are here. Once.',
    'sub_reality-receipt': 'A summary of your days',
    'sub_nothing-happens': 'Exactly what it says',
    'room_eclipse-shadows': 'Eclipse Shadows',
    'sub_eclipse-shadows': 'Speak into the total darkness',
    'room_dawn-awakening': 'Dawn Awakening',
    'sub_dawn-awakening': 'Greet the first light',
    'sub_default-choice': 'Safety or risk. Every time.',
    'sub_thought-loop': 'Watch your mind repeat itself',
    'sub_expect-vs-happens': 'The gap between hope and reality',
    'sub_question-never-answer': 'Most users leave',
    'sub_not-special': 'No comfort. Just truth.',
    'sub_shortcut-always-take': 'Patterns reveal themselves',
    'sub_blurred-self': 'Memory works like this',
    'sub_image-zooming': 'Seeking answers in nothing',
    'sub_comfort-color': 'How long will you stay?',
    'sub_line-you-crossed': 'We see where you hesitated',
    'sub_read-aloud': 'Words you must speak',
    'sub_voice-without-meaning': 'Sound without words',
    'sub_sentence-you-avoid': 'You cannot leave until you write it',
    'sub_if-nothing-changes': 'A projection of your next five years',
    'sub_real-priorities': 'Based on time, not words',
    'sub_last-conversation': 'Write what you will never send',
    'sub_who-without-job': 'Rewrite yourself',
    'sub_comfort-zone-scanner': 'We see your hesitation',
    'sub_say-it-once': 'No replay. No redo.',
    'sub_listen-to-yourself': 'Your voice, slowed down',
    'sub_life-as-shapes': 'Abstract. Unlabeled. Yours.',
    'sub_face-without-features': 'Remove until nothing remains',
    'sub_what-keeps-awake': 'Available after 10 PM',
    'sub_you-at-317am': 'Time-specific. Personal.',
    'sub_thought-that-returns': 'It comes back every visit',
    
    // Game Room Subtitles
    'sub_memory-puzzle': 'Match patterns that subtly shift',
    'sub_shadow-chase': 'Catch what moves unpredictably',
    'sub_timing-room': 'Click exactly in the moment',
    'sub_pattern-whisper': 'Repeat the fading sequence',
    'sub_silence-puzzle': 'Solve without instructions',
    'sub_decay-game': 'Touch to preserve fading things',
    'sub_observation-test': 'Find subtle differences',
    'sub_balance-room': 'Keep floating objects aligned',
    'sub_word-drift': 'Catch letters before they fall',
    'sub_color-echo': 'Repeat the subtle tone sequence',
    'sub_mirror-tap': 'Every tap echoes elsewhere',
    'sub_maze-of-doors': 'Doors rearrange after each choice',
    'sub_pulse-match': 'Move in rhythm with the room',
    'sub_light-path': 'Guide light through shifting darkness',
    'sub_sound-match': 'Tap shapes to match soft tones',
    
    // Experimental Room Subtitles
    'sub_mirror-room': 'Reflections slightly distorted',
    'sub_echo-room': 'Clicks trigger delayed echoes',
    'sub_pulse-room': 'The environment breathes',
    'sub_vanishing-room': 'Elements fade as you interact',
    'sub_letter-room': 'Words scramble like failing memory',
    'sub_empty-room': 'Blank until you find secrets',
    'sub_reverse-room': 'Actions behave opposite',
    'sub_whisper-room': 'Barely audible truths',
    'sub_shadow-room': 'Your dark companion',
    'sub_labyrinth-room': 'Find your way',
    'sub_color-room': 'Chromatic meditation',
    'sub_surrender-room': 'Let go of control',
    'sub_noise-room': 'Static thoughts',
    'sub_observer-room': 'Being watched',
    'sub_imagine-yourself': 'Describe yourself. See what emerges.',
    'sub_ai-reflection-room': 'Let AI reflect with you',
    // Alternate Mode Exclusive Room Subtitles
    'sub_the-other-side': 'move slowly through the dark',
    'sub_the-unraveling': 'reality frays with each word',
    'sub_the-echo': 'what you say never leaves',
    
    // Room Content - Common
    'how_old_are_you': 'How old are you?',
    'based_on_life_expectancy': 'Based on average life expectancy, you have approximately:',
    'summers': 'Summers',
    'meals': 'Meals',
    'birthdays': 'Birthdays',
    'full_moons': 'Full moons',
    'sunrises': 'Sunrises',
    'books_might_finish': 'Books you might finish',
    'quote_not_warning': 'This is not a warning. Just a count.',
    'each_square_is_week': 'Each square is one week. There are about 4,000.',
    'your_age': 'Your age',
    'see_your_life': 'See your life',
    'weeks_lived': '{count} weeks lived',
    'weeks_remaining': '{count} remaining (if you\'re average)',
    'quote_week_never_back': 'Each filled square is a week you will never get back.',
    'write_one_true_thing': 'Write one true thing about yourself.',
    'cannot_edit_delete': 'You cannot edit it. You cannot delete it.',
    'i_placeholder': 'I...',
    'submit_forever': 'Submit forever',
    'previous_truths': 'Your previous truths',
    'write_another': 'Write another',
    'quote_cannot_unsaid': 'This cannot be unsaid.',
    'content_nothing_waiting': 'You have been here for {seconds} seconds.',
    'content_nothing_still': 'Nothing is still happening.',
    'content_nothing_stayed': 'And yet, you stayed.',
    'content_thought_visit': 'Visit #{count}',
    'content_thought_always': 'This thought will be here every time you return.',
    'content_thought_never_leave': 'Some thoughts never leave. They just wait.',
    'click_it_harder': 'Click it. It will get harder.',
    'click': 'Click',
    'times_clicked': 'times clicked',
    'quote_things_chase': 'The things we chase get smaller the more we pursue them.',
    'scenario_stranger': 'A stranger asks for help',
    'scenario_help': 'Help them',
    'scenario_keep_walking': 'Keep walking',
    'scenario_free_hour': 'You have a free hour',
    'scenario_start_new': 'Start something new',
    'scenario_scroll': 'Scroll your phone',
    'scenario_disagrees': 'Someone disagrees with you',
    'scenario_engage': 'Engage',
    'scenario_avoid': 'Avoid conflict',
    'scenario_risky': 'An opportunity feels risky',
    'scenario_take_it': 'Take it',
    'scenario_wait_safer': 'Wait for a safer one',
    'scenario_speak_up': 'You could speak up now',
    'scenario_say_it': 'Say it',
    'scenario_stay_quiet': 'Stay quiet',
    'choose_safety': 'of the time, you choose safety',
    'quote_become_choose': 'We become what we repeatedly choose.',
    
    // Unlock page
    'unlock_locked_rooms': 'Locked Rooms',
    'unlock_not_free': 'Some rooms are not free.',
    'unlock_not_because': 'Not because they are better —',
    'unlock_go_further': 'but because they go further.',
    'unlock_whats_behind': "What's behind the door",
    'unlock_and_more': '...and {count} more rooms',
    'unlock_deepest_truth': 'The Deepest Truth',
    'unlock_premium_boss': 'The Premium Boss Room',
    'unlock_lifetime_desc': 'One time. Forever yours.',
    'unlock_monthly_desc': 'Cancel anytime.',
    'unlock_no_account': 'No account required. No emails. Just access.',
    'unlock_all_unlocked': 'All Rooms Unlocked',
    'unlock_have_access': 'You have access to every room in The Human Rooms.',
    'unlock_go_deeper': 'Go deeper. Take your time.',
    'unlock_enter_hallway': 'Enter the Hallway',
    'unlock_welcome_depths': 'Welcome to the depths',
    'unlock_verifying': 'Verifying your purchase...',
    'unlock_canceled': 'Payment canceled',
    'unlock_try_again': "You can try again whenever you're ready.",
    'unlock_error': 'Something went wrong',
    'unlock_try_later': 'Please try again later.',
    
    // Room additional keys
    'seconds_that_wont_return': 'that you will never get back',
    'moment_id': 'Moment ID',
    
    // This Moment room
    'this_moment_title': 'This Moment Will Never Happen Again',
    'this_moment_been_here': 'You have been in this moment for',
    'this_moment_quote': 'This exact configuration of time, place, and you will never occur again in the history of the universe.',
    
    // Thought Loop room
    'thought_loop_title': 'The Thought Loop',
    'thought_loop_prompt': 'Write a thought that keeps returning.',
    'thought_loop_placeholder': "The thought that won't leave...",
    'thought_loop_release': 'Release it',
    'thought_loop_quote': 'The mind repeats what it cannot release.',
    
    // Expect vs Happens room
    'expect_title': 'What You Expect vs What Happens',
    'expect_subtitle': 'Two truths. One gap.',
    'expect_what_expect': 'What you expect to happen',
    'expect_what_happens': 'What usually happens',
    'expect_show': 'Show me',
    'expect_distance': 'The distance between these two sentences is where you live.',
    'expect_quote': 'Hope is the gap between expectation and reality.',
    
    // Reality Receipt room
    'receipt_title': 'Reality Receipt',
    'receipt_sleep': 'Hours of sleep per night?',
    'receipt_work': 'Hours of work per day?',
    'receipt_screen': 'Hours of screen time (non-work)?',
    'receipt_commute': 'Hours commuting?',
    'receipt_next': 'Next',
    'receipt_generate': 'Generate receipt',
    'receipt_header': 'REALITY RECEIPT',
    'receipt_sleep_label': 'Sleep',
    'receipt_work_label': 'Work',
    'receipt_screen_label': 'Screen (leisure)',
    'receipt_commute_label': 'Commute',
    'receipt_total_used': 'TOTAL USED',
    'receipt_free_time': 'FREE TIME',
    'receipt_yearly': 'YEARLY PROJECTION:',
    'receipt_yearly_work': 'hours working',
    'receipt_yearly_screen': 'hours on screens',
    'receipt_yearly_sleep': 'hours sleeping',
    'receipt_yearly_free': 'hours free',
    'receipt_recalculate': 'Recalculate',
    'receipt_quote': 'This is how you spend your life.',
    
    // Question Never Answer room
    'question_title': 'The Question You Never Answer',
    'question_seconds': 'seconds',
    'question_most_left': 'Most people have already left.',
    'question_quote': 'The question remains. You stayed.',
    'question_1': 'What would you do if you knew you couldn\'t fail?',
    'question_2': 'Who would you be if no one was watching?',
    'question_3': 'What are you pretending not to know?',
    'question_4': 'What would your 10-year-old self think of you?',
    'question_5': 'What are you afraid to want?',
    'question_6': 'If you died today, what would remain unfinished?',
    'question_7': 'What lie do you tell yourself most often?',
    'question_8': 'What would you do if money wasn\'t real?',
    'question_9': 'Who have you not forgiven?',
    'question_10': 'What are you running from?',
    
    // Not Special room
    'not_special_right_now': 'Right now, approximately',
    'not_special_share_fear': 'people share your fear of',
    'not_special_title': "You're Not Special",
    'not_special_okay': "And that's okay.",
    'not_special_quote': 'Your fear is not unique. Neither is your courage.',
    'fear_forgotten': 'being forgotten',
    'fear_dying_alone': 'dying alone',
    'fear_not_enough': 'not being good enough',
    'fear_wasting_life': 'wasting their life',
    'fear_being_seen': 'being truly seen',
    'fear_failure': 'failure',
    'fear_rejection': 'rejection',
    'fear_missing_out': 'missing out',
    'fear_ordinary': 'being ordinary',
    'fear_time': 'running out of time',
    
    // Scary Room Content
    'watching_alone': "You're alone here.",
    'watching_completely': "Completely alone.",
    'watching_or_are_you': "Or are you?",
    'watching_been_here': "They've been here the whole time.",
    'watching_always': "They're always watching.",
    'watching_quote': "You are never truly alone with your thoughts.",
    'countdown_title': 'The Countdown',
    'countdown_something': 'Something happens when it reaches zero.',
    'countdown_sure': 'Are you sure you want to stay?',
    'countdown_almost': "It's almost here.",
    'countdown_end_1': "Nothing happened. Were you afraid?",
    'countdown_end_2': "The anticipation was the experience.",
    'countdown_end_3': "What did you expect?",
    'countdown_end_4': "Fear lives in the waiting.",
    'countdown_end_5': "The countdown was never about the end.",
    'countdown_quote': "The scariest moment is always just before you start.",
    'dont_look_title': "Don't Look Away",
    'dont_look_away_msg': "Don't look away.",
    'dont_look_looked': "You looked away.",
    'dont_look_seconds': '{count} seconds of attention',
    'dont_look_times': 'Looked away {count} time(s)',
    'dont_look_quote': "Presence is the hardest thing to give.",
    'whisper_hello': "Hello.",
    'whisper_came': "You came.",
    'whisper_expecting': "We've been expecting you.",
    'whisper_remember': "Do you remember this place?",
    'whisper_been_before': "You've been here before.",
    'whisper_know': "We know.",
    'whisper_remember_name': "Do you remember your name?",
    'whisper_quote': "Some rooms remember you better than you remember yourself.",
    
    // Additional Room Titles (only unique ones not already defined)
    'room_void-room': 'The Void',
    'room_pattern-room': 'Your Patterns',
    'room_time-capsule': 'Time Capsule',
    'room_the-revisit': 'The Revisit',
    'room_contradiction-room': 'The Contradiction Room',
    'room_export-journey': 'Export Your Journey',
    'room_anonymous-echoes': 'Anonymous Echoes',
    'room_the-waiting-room': 'The Waiting Room',
    
    // Additional Room Subtitles (only unique ones not already defined)
    'sub_secret-room': 'You found it',
    'sub_idle-room': 'Found through patience',
    'sub_glitch-room': 'Something is wrong',
    'sub_void-room': 'A wound in the code',
    'sub_pattern-room': 'What the site sees in you',
    'sub_collective-room': 'Everyone is here. No one stays.',
    'sub_theyre-watching': 'You are not alone',
    'sub_the-last-visitor': 'Someone was here before you',
    'sub_dont-look-away': 'It knows when you leave',
    'sub_the-whisper': 'Listen closely',
    'sub_you-forgot-something': 'What was it?',
    'sub_the-mirror-lies': 'Crack it to see the truth',
    'sub_something-follows': 'Keep running',
    'sub_the-countdown': 'Something happens at zero',
    'sub_eyes-in-the-dark': 'They multiply',
    'sub_the-voice': 'Someone is trapped here',
    'sub_it-knows-your-name': 'You should not have come',
    'sub_the-door': 'Something is knocking',
    'sub_boss-room-free': 'The final free room',
    'sub_boss-room-horror': 'Face the final darkness',
    'sub_boss-room-premium': 'The final deep room',
    'sub_halloween-room': 'October 31st - The veil is thin',
    'sub_winter-solstice-room': 'December 21st - Release and keep',
    'sub_new-year-room': 'December 31st - Write to who you will be',
    'sub_weekend-room': 'Rest is a destination',
    'sub_full-moon-room': 'Only opens during lunar peak',
    'sub_317am-room': 'Time-specific. Personal.',
    'sub_time-capsule': 'Write to your future self',
    'sub_the-revisit': 'See how your answers changed',
    'sub_contradiction-room': 'Your truths disagree',
    'sub_export-journey': 'Download your path',
    'sub_anonymous-echoes': 'Fragments from strangers',
    'sub_the-waiting-room': 'Unlock with presence',
    'sub_invite-friend-room': 'Some journeys are meant to be witnessed',
    'sub_room-you-visit-most': 'Your pattern revealed',
    'sub_room-you-avoid': 'What you skip says something',
    'sub_left-too-early': 'Triggered when you exit fast',
    'sub_daily-quote': 'Wisdom that changes daily',
    'sub_daily-joke': 'Dark humor, updated daily',
    'sub_dream-visualization': 'Describe a dream. Watch it materialize.',
    'sub_future-self': 'Who do you want to become?',
    'sub_regret-machine': 'Explore what might have been',
    'sub_memory-reconstruction': 'Rebuild a fading memory',
    'sub_mouse-tracker': 'We see how you move',
    'sub_scroll-confession': 'How fast do you rush?',
    'sub_waiting-game': 'Can you do nothing?',
    'sub_day-in-pixels': 'Time becomes shape',
    'sub_memory-maze': 'The pattern changes',
    'sub_decision-shadow': 'Your choices reveal patterns',
    'sub_anonymous-mirrors': 'Thoughts from strangers',
    'sub_whisper-wall': 'Anonymous truths float in the dark',
    'sub_collective-memory': 'Shared fragments of what we remember',
    'sub_shared-moments': 'How are you, honestly?',
    'sub_the-archive': 'Your complete journey history',
    'sub_mirror-of-all-selves': 'AI reveals your behavioral portrait',
    'sub_the-ending': 'A farewell you earned',
    
    // Meta Room Content - The Refusal
    'refusal_line1': 'This room refuses you.',
    'refusal_line2': 'Not everything opens when you want it to.',
    'refusal_line3': 'Sometimes the answer is simply: no.',
    'refusal_line4': "Try the button. It won't help.",
    'refusal_button': 'Enter Room',
    
    // Meta Room Content - Parallel You
    'parallel_choice_1': 'You chose speed.',
    'parallel_alternate_1': 'Another you chose patience.',
    'parallel_reflection_1': "They're still waiting.",
    'parallel_choice_2': 'You left early.',
    'parallel_alternate_2': 'Another you stayed until the end.',
    'parallel_reflection_2': 'They saw what you missed.',
    'parallel_choice_3': 'You avoided the hard question.',
    'parallel_alternate_3': 'Another you answered it.',
    'parallel_reflection_3': 'They carry that truth now.',
    'parallel_choice_4': 'You rushed through.',
    'parallel_alternate_4': 'Another you savored each moment.',
    'parallel_reflection_4': 'They remember more.',
    'parallel_choice_5': 'You kept scrolling.',
    'parallel_alternate_5': 'Another you stopped.',
    'parallel_reflection_5': 'They found something.',
    'parallel_choice_6': 'You chose comfort.',
    'parallel_alternate_6': 'Another you chose discomfort.',
    'parallel_reflection_6': 'They grew from it.',
    'parallel_quote': 'Every moment splits into infinite paths. You only walk one.',
    
    // Meta Room Content - Your Shadow
    'shadow_generated': 'Generated from your behavior',
    'shadow_intro': 'Your shadow...',
    'shadow_follows': 'This shadow follows you through every room.',
    'shadow_quote': 'We are not what we think. We are what we do.',
    'shadow_trait_completion': 'seeks completion',
    'shadow_trait_surface': 'prefers the surface',
    'shadow_trait_lingers': 'lingers in spaces',
    'shadow_trait_moves_quickly': 'moves quickly',
    'shadow_trait_avoids_depth': 'avoids depth',
    'shadow_trait_stillness': 'embraces stillness',
    'shadow_trait_chases': 'chases something unnamed',
    'shadow_trait_darkness': 'drawn to darkness',
    'shadow_trait_emptiness': 'comfortable with emptiness',
    'shadow_trait_undefined': 'undefined',
    'shadow_trait_forming': 'still forming',
    
    // Translation Navigator
    'translation_navigator': 'Translation Navigator',
    'translation_coverage': 'Room translation coverage by language',
    'keys': 'keys',
    'room_translations': 'Room Translations',
    'show_all': 'Show All',
    'show_missing': 'Show Missing',
    'all_translated': 'All rooms are translated!',
    'missing_translations': 'missing translations',
    
    // New Phase 1-3 Rooms
    'room_letters-never-sent': 'Letters Never Sent',
    'sub_letters-never-sent': 'Write to someone who will never read it',
    'room_breath-sync': 'Breath Sync',
    'sub_breath-sync': 'Breathe with the room',
    'room_entropy-garden': 'Entropy Garden',
    'sub_entropy-garden': 'Growth and decay intertwined',
    'room_memory-palace': 'Memory Palace',
    'sub_memory-palace': 'Build your mental sanctuary',
    'room_morning-ritual': 'Morning Ritual',
    'sub_morning-ritual': 'Start your day with intention',
    'room_evening-ritual': 'Evening Ritual',
    'sub_evening-ritual': 'Close the day with reflection',
    'room_weekly-ritual': 'Weekly Ritual',
    'sub_weekly-ritual': 'Sundays are for looking back',
    
    // Letters Never Sent Room
    'letters_title': 'Letters Never Sent',
    'letters_subtitle': 'Write to someone who will never read it. The AI will respond as they might.',
    'letters_select_recipient': 'Who are you writing to?',
    'letters_past_self': 'My Past Self',
    'letters_future_self': 'My Future Self',
    'letters_someone_lost': 'Someone I Lost',
    'letters_someone_wronged': 'Someone I Wronged',
    'letters_placeholder': 'Dear...',
    'letters_send': 'Send into the Void',
    'letters_sending': 'Sending...',
    'letters_response': 'A response emerges...',
    'letters_write_another': 'Write Another Letter',
    'letters_quote': 'Some words are meant to be written but never sent.',
    
    // Breath Sync Room
    'breath_title': 'Breath Sync',
    'breath_subtitle': 'Match your breath to the rhythm of the room.',
    'breath_inhale': 'Inhale',
    'breath_hold': 'Hold',
    'breath_exhale': 'Exhale',
    'breath_cycles': 'Cycles completed',
    'breath_quote': 'The breath is the bridge between body and mind.',
    
    // Entropy Garden Room
    'entropy_title': 'Entropy Garden',
    'entropy_subtitle': 'Plant seeds that grow with your visits. Neglect them, and they decay.',
    'entropy_plant': 'Plant a Seed',
    'entropy_water': 'Water',
    'entropy_days_alive': 'days alive',
    'entropy_health': 'Health',
    'entropy_empty': 'Your garden is empty. Plant something.',
    'entropy_quote': 'Everything grows with attention. Everything decays with neglect. This is true of gardens. And of selves.',
    
    // Memory Palace Room
    'memory_palace_title': 'Memory Palace',
    'memory_palace_subtitle': 'Build a mental sanctuary. Place objects in rooms you create.',
    'memory_palace_add_room': 'Add Room',
    'memory_palace_add_object': 'Add Object',
    'memory_palace_room_name': 'Room name...',
    'memory_palace_object_name': 'Object name...',
    'memory_palace_empty': 'Your palace is empty. Create your first room.',
    'memory_palace_quote': 'We build our minds one room at a time.',
    
    // Ritual Rooms
    'ritual_locked': 'This ritual is not yet available',
    'morning_ritual_title': 'Morning Ritual',
    'morning_ritual_intro': 'A moment to set intentions for the day ahead.',
    'morning_ritual_available': 'Available 6 AM - 9 AM',
    'morning_ritual_prompt_1': 'What do you want to carry with you today?',
    'morning_ritual_prompt_2': 'What will you let go of today?',
    'morning_ritual_prompt_3': 'What is one thing you can do for yourself today?',
    'evening_ritual_title': 'Evening Ritual',
    'evening_ritual_intro': 'A moment to reflect on the day that was.',
    'evening_ritual_available': 'Available 8 PM - 11 PM',
    'evening_ritual_prompt_1': 'What are you grateful for today?',
    'evening_ritual_prompt_2': 'What challenged you today?',
    'evening_ritual_prompt_3': 'What will you release before sleep?',
    'weekly_ritual_title': 'Weekly Ritual',
    'weekly_ritual_intro': 'A moment to reflect on the week that was, and set intentions for the one ahead.',
    'weekly_locked': 'The Weekly Ritual awaits you',
    'weekly_time': 'Return on Sunday',
    'today_is': 'Today is',
    'your_week': 'Your Week',
    'days_active': 'Days Active',
    'weekly_summary_note': 'Now, let us reflect more deeply.',
    'weekly_highlight': 'What was the highlight of your week?',
    'weekly_challenge': 'What challenged you most this week?',
    'weekly_growth': 'How did you grow this week?',
    'weekly_intention': 'What intention do you set for next week?',
    'weekly_placeholder': 'Take your time...',
    'reflect': 'Reflect',
    'reflecting': 'Reflecting...',
    'complete': 'Complete',
    'weekly_quote': 'A week ends. Another begins. You carry what matters.',
    
    // Personal Timeline
    'personal_timeline': 'Personal Timeline',
    'timeline_subtitle': 'Your journey through the rooms',
    'timeline_empty': 'Your timeline is empty. Start exploring.',
    'timeline_today': 'Today',
    'timeline_yesterday': 'Yesterday',
    'timeline_this_week': 'This Week',
    'timeline_earlier': 'Earlier',
    
    // Weather System
    'weather_clear': 'Clear',
    'weather_rain': 'Rain',
    'weather_fog': 'Fog',
    'weather_snow': 'Snow',
    
    // Notifications
    'notifications': 'Notifications',
    'notifications_desc': 'Get notified about time-locked content',
    'notify_time_locked': 'Time-locked rooms',
    'notify_full_moon': 'Full moon nights',
    'notify_daily_reset': 'Daily challenge resets',
    'notify_time_capsule': 'Time capsule unlocks',
    'notifications_enabled': 'Notifications enabled',
    'notifications_disabled': 'Notifications disabled',
    'notification_permission_denied': 'Notification permission denied',
    
    // Community Submissions
    'community_submissions': 'Community Submissions',
    'community_subtitle': 'Submit your room ideas',
    'community_room_title': 'Room Title',
    'community_room_description': 'Room Description',
    'community_room_category': 'Category',
    'community_submit': 'Submit Idea',
    'community_submitted': 'Thank you for your submission!',
    'community_your_submissions': 'Your Submissions',
    'community_empty': 'No submissions yet. Be the first!',
    
    // Proactive Guide
    'guide_hesitation': 'Taking your time? That\'s okay. Would you like a suggestion?',
    'guide_long_absence': 'Welcome back. It\'s been a while.',
    'guide_milestone': 'You\'ve come far. Would you like to reflect on your journey?',
    'guide_pattern': 'I notice you tend to visit certain rooms. Shall I suggest something different?',
    'guide_dismiss': 'Dismiss',
    'guide_accept': 'Show me',
    
    // Voice Mode
    'voice_mode': 'Voice Mode',
    'voice_mode_desc': 'Let the guide speak to you',
    'voice_mode_enabled': 'Voice mode enabled',
    'voice_mode_disabled': 'Voice mode disabled',
    
    // Offline Support
    'offline_available': 'Available offline',
    'offline_cached': 'This room is cached for offline viewing',
    'offline_not_available': 'Not available offline',
    
    // Journey Export
    'journey_export': 'Export Journey',
    'journey_import': 'Import Journey',
    'journey_export_json': 'Export as JSON',
    'journey_import_json': 'Import from JSON',
    'journey_exported': 'Journey exported successfully',
    'journey_imported': 'Journey imported successfully',
    'journey_import_error': 'Failed to import journey',
    
    // Phase 4 - Seasonal Rooms
    'room_spring-equinox-room': 'Spring Equinox',
    'sub_spring-equinox-room': 'Release and renew',
    'room_summer-solstice-room': 'Summer Solstice',
    'sub_summer-solstice-room': 'The longest day',
    'room_autumn-equinox-room': 'Autumn Equinox',
    'sub_autumn-equinox-room': 'Harvest and accept',
    'room_valentine-room': 'Self-Love Room',
    'sub_valentine-room': 'The love you deserve',
    
    // Spring Equinox Room
    'spring_date': 'MARCH 20TH',
    'spring_title': 'Spring Equinox',
    'spring_message_1': 'Day and night are equal.',
    'spring_message_2': 'A moment of perfect balance.',
    'spring_message_3': 'What will you release to make room for growth?',
    'spring_letting_go': 'I am letting go of:',
    'spring_letting_go_placeholder': 'What no longer serves you...',
    'spring_planting': 'I am planting:',
    'spring_planting_placeholder': 'What you wish to grow...',
    'spring_bloom': 'Let it bloom',
    'spring_released': 'Released:',
    'spring_planted': 'Planted:',
    'spring_closing': 'From this moment, new life begins.',
    'spring_quote': 'Nature does not hurry, yet everything is accomplished.',
    
    // Summer Solstice Room
    'summer_date': 'JUNE 21ST',
    'summer_title': 'The Longest Day',
    'summer_message_1': 'This is the longest day of the year.',
    'summer_message_2': 'Light reaches its peak.',
    'summer_message_3': 'What would you do with more time?',
    'summer_message_4': 'Describe your fullest, most vibrant day.',
    'summer_placeholder': 'On my fullest day, I would...',
    'summer_submit': 'Embrace the light',
    'summer_closing': 'The sun has heard you. Now it begins its descent.',
    'summer_quote': 'At the peak of light, we discover what we truly want to illuminate.',
    
    // Autumn Equinox Room
    'autumn_date': 'SEPTEMBER 22ND',
    'autumn_title': 'Autumn Equinox',
    'autumn_message_1': 'Balance returns.',
    'autumn_message_2': 'The harvest is ready.',
    'autumn_message_3': 'What have you grown? What can you accept?',
    'autumn_harvesting': 'I am harvesting:',
    'autumn_harvesting_placeholder': 'What you have grown this year...',
    'autumn_accepting': 'I am accepting:',
    'autumn_accepting_placeholder': 'What you cannot change...',
    'autumn_release': 'Let the leaves fall',
    'autumn_harvested': 'Harvested:',
    'autumn_accepted': 'Accepted:',
    'autumn_closing': 'Everything falls in its own time.',
    'autumn_quote': 'Autumn shows us how beautiful it is to let things go.',
    
    // Valentine's Day Room (Self-Love)
    'valentine_date': 'FEBRUARY 14TH',
    'valentine_title': 'The Love You Deserve',
    'valentine_message_1': 'Today is not about others.',
    'valentine_message_2': 'It is about the one who is always with you.',
    'valentine_message_3': 'The one who hears every thought.',
    'valentine_message_4': 'Write yourself a love letter.',
    'valentine_prompt': 'A letter to yourself:',
    'valentine_placeholder': 'Dear me, I want you to know...',
    'valentine_submit': 'Send with love',
    'valentine_closing': 'You are worthy of the love you give to others.',
    'valentine_quote': 'You yourself deserve your love and affection.',
    
    // Update changelog
    'update_seasonal_rooms': 'Four new seasonal rooms: Spring Equinox, Summer Solstice, Autumn Equinox, and Valentine\'s Day',
    'update_fragment_trading': 'Fragment Trading system - sacrifice fragments to unlock time-locked rooms, preview horror rooms, or receive AI messages',
    'update_new_themes': 'Two new themes: Eclipse (pure darkness) and Dawn (warm gradients, 5-7 AM only)',
    'update_eclipse_room': 'Eclipse Shadows - speak into total darkness, reveal in light flashes',
    'update_dawn_room': 'Dawn Awakening - greet the first light with breathing exercises and intentions',
    
    // New themes
    'eclipse_theme_required': 'This room requires the Eclipse theme. The total darkness awaits.',
    'dawn_theme_required': 'This room requires the Dawn theme during the early hours.',
    'dawn_time_hint': 'Available 5-7 AM only',
    'change_theme': 'Change theme in Settings',
    
    // Eclipse Shadows Room
    'eclipse_shadows_title': 'Eclipse Shadows',
    'eclipse_shadows_intro': 'In complete darkness, secrets feel safer. Answer honestly—only brief flashes will illuminate your words.',
    'enter_darkness': 'Enter the darkness',
    'shadow_question_1': 'What do you hide from yourself?',
    'shadow_question_2': 'What truth would you speak if no one could hear?',
    'shadow_question_3': 'What haunts you at 3 AM?',
    'shadow_question_4': 'What would you do if fear didn\'t exist?',
    'shadow_question_5': 'What are you not ready to face?',
    'speak_into_dark': 'Speak into the dark...',
    'release': 'Release',
    'eclipse_reveal_prompt': 'Click each shadow to reveal what you confessed.',
    'hidden_in_shadow': '▓▓▓▓▓▓▓▓▓▓▓▓',
    'eclipse_reveal_hint': 'Some truths are clearer in the dark.',
    'eclipse_shadows_quote': 'In total darkness, we finally see ourselves.',
    
    // Dawn Awakening Room
    'dawn_awakening_title': 'Dawn Awakening',
    'dawn_awakening_intro': 'The first light carries possibility. Before the world wakes, set your intentions.',
    'greet_the_light': 'Greet the light',
    'breathe_in_light': 'Breathe in the light',
    'hold_warmth': 'Hold the warmth',
    'release_night': 'Release the night',
    'dawn_prompt_1': 'What do you want to carry with you today?',
    'dawn_prompt_2': 'What will you let go of?',
    'dawn_prompt_3': 'What small act of kindness will you offer yourself?',
    'set_your_intention': 'Set your intention...',
    'plant_seed': 'Plant this seed',
    'dawn_awakening_quote': 'Each dawn is an invitation to begin again.',
    
    // Fragment Trading
    'fragment_trading': 'Fragment Trading',
    'sacrifice_to_unlock': 'Sacrifice fragments to unlock secrets',
    'your_fragments': 'Your Fragments',
    'fragments_any': 'any fragments',
    'fragments_free': 'clarity fragments',
    'fragments_premium': 'depth fragments',
    'fragments_horror': 'shadow fragments',
    'trade_successful': 'Trade Successful!',
    'trade_failed': 'Trade Failed',
    'not_enough_fragments': 'Not enough fragments',
    'continue_trading': 'Continue Trading',
    'trading_fragments': 'Trading fragments...',
    'fragment_trade_warning': 'Fragments sacrificed cannot be recovered',
    'unlock-time-locked': 'Unlock Time-Locked Room',
    'unlock-time-locked_desc': 'Access a time-locked room without waiting',
    'preview-horror': 'Horror Room Preview',
    'preview-horror_desc': 'Peek into a horror room before entering',
    'ai-reflection-trade': 'AI Reflection',
    'ai-reflection-trade_desc': 'Receive a personalized message from the AI',
    'unlock-premium-preview': 'Premium Room Trial',
    'unlock-premium-preview_desc': 'Access a premium room for one visit',
    'fragment_trading_room': 'The Fragment Altar',
    'fragment_trading_intro': 'Here, you may sacrifice your collected fragments to unlock secrets hidden within these rooms.',
    'your_collection': 'Your Collection',
    'fragment_clarity': 'Clarity',
    'fragment_depth': 'Depth',
    'fragment_shadow': 'Shadow',
    'total_fragments': 'Total',
    'open_altar': 'Approach the Altar',
    'recent_trades': 'Recent Offerings',
    'fragment_trade_ending': 'What was given cannot be retrieved. But what was gained... that remains.',
    'room_fragment-trade': 'The Fragment Altar',
    'sub_fragment-trade': 'Sacrifice to unlock secrets',
    // Phase 8 - Anniversary & Milestones
    'update_anniversary_system': 'Anniversary & Milestone System - track your journey over time',
    'update_archive_room': 'The Archive - full journey history unlocked at 25 rooms',
    'update_mirror_room': 'Mirror of All Selves - AI behavioral portrait at 50 rooms',
    'update_ending_room': 'The Ending - a personalized farewell at 75 rooms',
    'room_the-archive': 'The Archive',
    'room_mirror-of-all-selves': 'Mirror of All Selves',
    'room_the-ending': 'The Ending',
    'archive_locked': 'Complete 25 rooms to unlock your journey archive.',
    'archive_subtitle': 'Your complete journey through The Human Rooms',
    'mirror_locked': 'Complete 50 rooms to see your reflection.',
    'mirror_subtitle': 'AI-generated portrait from your behavioral patterns',
    'mirror_look': 'Look into the mirror',
    'mirror_loading': 'The mirror is studying you...',
    'mirror_error': 'The mirror is clouded. Try again.',
    'mirror_portrait': 'Your Portrait',
    'mirror_step_away': 'Step away from the mirror',
    'ending_locked': 'Complete 75 rooms to reach the ending.',
    'ending_final': 'Thank you for being here.',
    'days_since_first': 'Days since first visit',
    'rooms_completed_count': 'Rooms completed',
    'archive_timeline': 'Timeline',
    'archive_stats': 'Stats',
    'archive_patterns': 'Patterns',
    'close_the_archive': 'Close the archive',
    'click_continue': 'click to continue',
    'return_to_hallway': 'Return to the hallway',
    // Phase 9 - Fragment Collection
    'update_fragment_rarities': 'Fragment Rarities - Common, Rare, and Legendary collectible fragments',
    'update_fragment_gallery': 'Fragment Gallery - visual display case for your collection',
    'update_seasonal_fragments': 'Seasonal and secret fragments - time-limited collectibles',
    'fragment_collection': 'Collection',
    'fragment_collection_title': 'Fragment Collection',
    'fragment_collection_subtitle': 'Every room leaves something behind. Collect them all.',
    'collection_milestones': 'Milestones',
    'collection_secrets': 'Secrets',
    'collection_footer': 'Some fragments only appear under certain conditions.',
    'rarity_common': 'Common',
    'rarity_rare': 'Rare',
    'rarity_legendary': 'Legendary',
    // Phase 10 - Accessibility
    'accessibility': 'Accessibility',
    'accessibility_desc': 'Customize readability and motion preferences',
    'font_family': 'Font Family',
    'font_default': 'Monospace',
    'font_dyslexia': 'Dyslexia-Friendly',
    'font_sans': 'Sans Serif',
    'font_changed': 'Font updated',
    'line_height': 'Line Height',
    'line_compact': 'Compact',
    'line_normal': 'Normal',
    'line_relaxed': 'Relaxed',
    'line_spacious': 'Spacious',
    'line_height_changed': 'Line height updated',
    'reduced_motion_full_desc': 'Disable all animations, parallax, and particle effects',
    'larger_cursors': 'Larger Targets',
    'larger_cursors_desc': 'Increase click and tap target sizes',
    'larger_cursors_enabled': 'Larger targets enabled',
    'larger_cursors_disabled': 'Larger targets disabled',
    'update_accessibility_suite': 'Accessibility Suite - dyslexia fonts, line height control, enhanced reduced motion',
    'update_dyslexia_font': 'Dyslexia-friendly font option in settings',
    'update_line_height': 'Adjustable line height for improved readability',
    'update_enhanced_reduced_motion': 'Enhanced reduced motion - completely disables all particle effects and animations',
    // Phase 11 - Social Whispers & Shared Moments
    'update_shared_moments': 'Shared Moments - anonymous emotional check-ins with others',
    'update_whisper_wall': 'The Whisper Wall - leave anonymous truths in the dark',
    'update_collective_memory': 'Collective Memory - shared fragments of what we remember',
    'whisper_prompt': 'Leave a whisper on the wall. Anonymous. Permanent.',
    'whisper_placeholder': 'What do you carry silently?',
    'whisper_send': 'Whisper',
    'whisper_added': 'Your whisper joins the wall.',
    'whisper_another': 'Whisper again',
    'collective_memory_subtitle': 'A shared space for fragments of memory',
    'collective_prompt_1': 'What sound reminds you of childhood?',
    'collective_prompt_2': 'Describe a place you can never return to.',
    'collective_prompt_3': 'What does home smell like?',
    'collective_prompt_4': 'What song plays in your earliest memory?',
    'collective_prompt_5': 'Describe the light in the room where you felt safest.',
    'collective_prompt_6': 'What does silence sound like to you?',
    'others_said': 'Others have said:',
    'collective_placeholder': 'Share your memory...',
    'share_memory': 'Share this memory',
    'memory_absorbed': 'Your memory has been absorbed into the collective...',
    'collective_complete_title': 'The collective remembers.',
    'collective_complete_desc': 'Your memories now exist alongside those of others who have visited this room.',
    'memories_stored': 'memories stored',
    'shared_moments_title': 'Right now, in this moment...',
    'shared_moments_subtitle': 'How are you, honestly?',
    'feeling_grateful': 'Grateful',
    'feeling_lost': 'Lost',
    'feeling_hopeful': 'Hopeful',
    'feeling_heavy': 'Heavy',
    'feeling_peaceful': 'Peaceful',
    'feeling_restless': 'Restless',
    'moments_shared': 'moments shared in this room',
    'moment_detail_placeholder': 'Say more, if you want to... (optional)',
    'back_short': 'Back',
    'share_moment': 'Share this moment',
    'moment_shared_title': 'Your moment has been shared.',
    'moment_shared_desc': "You're not alone in feeling this way.",
    'others_felt': 'Others who felt the same:',
    'room_whisper-wall': 'The Whisper Wall',
    'room_collective-memory': 'Collective Memory',
    'room_shared-moments': 'Shared Moments',

    // v3.21.0 - New Rooms
    'room_mask-you-wear': 'The Mask You Wear',
    'sub_mask-you-wear': 'What face do you show?',
    'room_recurring-dream': 'Your Recurring Dream',
    'sub_recurring-dream': 'The dream that keeps coming back',
    'room_promise-you-broke': 'The Promise You Broke',
    'sub_promise-you-broke': 'Honesty about broken promises',
    'room_words-you-swallowed': 'Words You Swallowed',
    'sub_words-you-swallowed': 'Things you never said',
    'room_age-you-feel': 'The Age You Feel',
    'sub_age-you-feel': 'How old are you really?',
    'room_biggest-lie': 'Your Biggest Lie',
    'sub_biggest-lie': 'The lie you tell most',
    'room_person-you-pretend-to-be': 'The Person You Pretend To Be',
    'sub_person-you-pretend-to-be': 'Your performance',
    'room_person-you-pretend': 'The Person You Pretend To Be',
    'sub_person-you-pretend': 'Your performance',
    'room_what-you-collect': 'What You Collect',
    'sub_what-you-collect': 'Objects reveal identity',
    'room_apology-you-owe': 'The Apology You Owe',
    'sub_apology-you-owe': 'Write what you should have said',
    'room_unfinished-story': 'Your Unfinished Story',
    'sub_unfinished-story': 'The thing you never completed',
    'room_version-others-see': 'The Version Others See',
    'sub_version-others-see': 'How others perceive you',
    'room_secret-talent': 'Your Secret Talent',
    'sub_secret-talent': 'What no one knows you can do',
    'room_fear-you-feed': 'The Fear You Feed',
    'sub_fear-you-feed': 'Which fear controls you',
    'room_guilty-pleasure': 'Your Guilty Pleasure',
    'sub_guilty-pleasure': 'What you hide enjoying',
    'room_rule-you-break': 'The Rule You Always Break',
    'sub_rule-you-break': 'Your pattern of rebellion',
    'room_childhood-promise': 'Your Childhood Promise',
    'sub_childhood-promise': 'What you promised yourself',
    'room_compliment-you-reject': 'The Compliment You Reject',
    'sub_compliment-you-reject': 'Why you can\'t accept praise',
    'room_autopilot-mode': 'Your Autopilot Mode',
    'sub_autopilot-mode': 'What you do without thinking',
    'room_boundary-you-need': 'The Boundary You Need',
    'sub_boundary-you-need': 'What you should protect',
    'room_inner-critic': 'Your Inner Critic',
    'sub_inner-critic': 'The voice that judges',
    'room_last-sunday': 'Last Sunday',
    'sub_last-sunday': 'What did you actually do?',
    'room_wasted-hours': 'Your Wasted Hours',
    'sub_wasted-hours': 'Where time disappears',
    'room_year-that-changed': 'The Year That Changed You',
    'sub_year-that-changed': 'The turning point',
    'room_tomorrows-regret': 'Tomorrow\'s Regret',
    'sub_tomorrows-regret': 'What will you wish you did?',
    'room_morning-autopilot': 'Your Morning Autopilot',
    'sub_morning-autopilot': 'The first hour ritual',
    'room_season-you-dread': 'The Season You Dread',
    'sub_season-you-dread': 'Which season weighs heaviest?',
    'room_ten-years-ago': 'Ten Years Ago Today',
    'sub_ten-years-ago': 'Who were you then?',
    'room_last-normal-day': 'Your Last Normal Day',
    'sub_last-normal-day': 'Before everything changed',
    'room_clock-you-ignore': 'The Clock You Ignore',
    'sub_clock-you-ignore': 'Time you pretend doesn\'t pass',
    'room_your-deadline': 'Your Deadline',
    'sub_your-deadline': 'What are you postponing?',
    'room_the-pause': 'The Pause',
    'sub_the-pause': 'Just breathe for a moment',
    'room_static-room': 'Static',
    'sub_static-room': 'White noise and stillness',
    'room_gap-between-thoughts': 'The Gap Between Thoughts',
    'sub_gap-between-thoughts': 'Find the space',
    'room_sit-with-discomfort': 'Sit With Discomfort',
    'sub_sit-with-discomfort': 'Don\'t fix anything',
    'room_boring-room': 'The Boring Room',
    'sub_boring-room': 'Nothing interesting at all',
    'room_unplug-room': 'Unplug',
    'sub_unplug-room': 'Imagine no devices',
    'room_unplug': 'Unplug',
    'sub_unplug': 'Imagine no devices',
    'room_last-page': 'The Last Page',
    'sub_last-page': 'The story ends here',
    'room_scrolling-pattern': 'Your Scrolling Pattern',
    'sub_scrolling-pattern': 'How you consume content',
    'room_tab-you-keep-open': 'The Tab You Keep Open',
    'sub_tab-you-keep-open': 'What stays in your browser',
    'room_digital-shadow': 'Your Digital Shadow',
    'sub_digital-shadow': 'Your online footprint',
    'room_notification-you-ignore': 'The Notification You Ignore',
    'sub_notification-you-ignore': 'What you dismiss',
    'room_typing-speed': 'Your Typing Speed',
    'sub_typing-speed': 'How fast do you express?',
    'room_link-you-almost-clicked': 'The Link You Almost Clicked',
    'sub_link-you-almost-clicked': 'Hesitation patterns',
    'room_bookmark-graveyard': 'Your Bookmark Graveyard',
    'sub_bookmark-graveyard': 'Saved but never returned',
    'room_search-you-deleted': 'The Search You Deleted',
    'sub_search-you-deleted': 'What you looked for and erased',
    'room_gravity-room': 'Gravity Room',
    'sub_gravity-room': 'Everything falls slowly',
    'room_ink-blot': 'Ink Blot',
    'sub_ink-blot': 'What do you see?',
    'room_hallway-of-mirrors': 'The Hallway of Mirrors',
    'sub_hallway-of-mirrors': 'Infinite reflections',
    'room_dissolving-text': 'Dissolving Text',
    'sub_dissolving-text': 'Words melt as you read',
    'room_the-pendulum': 'The Pendulum',
    'sub_the-pendulum': 'Hypnotic motion',
    'room_pixel-rain': 'Pixel Rain',
    'sub_pixel-rain': 'Digital precipitation',
    'room_the-threshold': 'The Threshold',
    'sub_the-threshold': 'Between here and there',
    'most_loved_rooms': 'Most Loved Rooms',
    'rate_room': 'Rate this room',
    'update_52_new_rooms': '52 new rooms across Identity, Time, Contemplation, Meta, and Experimental categories',
    'update_room_rating': 'Room Rating system - thumbs up/down feedback with Most Loved Rooms section',
    'update_bug_fix_reflections': 'Fixed anonymous reflections display and storage',
    'update_100_rooms': 'Over 100 rooms now available in the Hallway',
    // v3.22.0
    'room_the-friend-you-lost': 'The Friend You Lost',
    'sub_the-friend-you-lost': 'Write to someone you drifted from.',
    'room_conversation-you-replay': 'The Conversation You Replay',
    'sub_conversation-you-replay': 'Recreate it from memory.',
    'room_the-thank-you': 'The Thank You',
    'sub_the-thank-you': 'Gratitude you never expressed.',
    'room_people-you-forgot': 'People You Forgot',
    'sub_people-you-forgot': 'Try to remember forgotten names.',
    'room_the-advice-you-give': 'The Advice You Give',
    'sub_the-advice-you-give': 'Advice you give but never follow.',
    'room_what-others-dont-know': "What Others Don't Know",
    'sub_what-others-dont-know': 'Your hidden layers.',
    'room_the-lie-you-believe': 'The Lie You Believe',
    'sub_the-lie-you-believe': 'A comforting untruth.',
    'room_your-shadow-self': 'Your Shadow Self',
    'sub_your-shadow-self': 'The part you suppress.',
    'room_the-story-you-tell': 'The Story You Tell',
    'sub_the-story-you-tell': 'How you narrate your life.',
    'room_your-safe-place': 'Your Safe Place',
    'sub_your-safe-place': 'Describe where you feel safe.',
    'room_the-risk-you-didnt-take': "The Risk You Didn't Take",
    'sub_the-risk-you-didnt-take': 'Roads not traveled.',
    'room_your-last-excuse': 'Your Last Excuse',
    'sub_your-last-excuse': 'The excuse you use most.',
    'room_the-habit-you-hide': 'The Habit You Hide',
    'sub_the-habit-you-hide': 'Secret routines.',
    'room_what-you-postpone': 'What You Postpone',
    'sub_what-you-postpone': 'The thing always "tomorrow".',
    'room_the-door-you-closed': 'The Door You Closed',
    'sub_the-door-you-closed': 'Opportunities you rejected.',
    'journal_title': 'Reflection Journal',
    'journal_subtitle': 'Your thoughts across rooms',
    'journal_empty': 'No reflections yet. Visit rooms and write your thoughts.',
    'journal': 'Journal',
    'room_chains': 'Room Chains',
    'chain_identity_journey': 'Identity Journey',
    'chain_time_reflection': 'Time Reflection',
    'chain_truth_path': 'Truth Path',
    'recently_visited': 'Recently Visited',
    'update_reflection_journal': 'Reflection Journal - save your thoughts across rooms',
    'update_room_chains': 'Room Chains - complete rooms in sequence for bonus fragments',
    'update_15_new_rooms': '15 new interactive rooms across 3 new categories',
    'update_recently_visited': 'Recently Visited section for quick room re-entry',
    'ai_companion': 'Companion',
    'companion_create': 'Create Your Companion',
    'companion_setup_desc': 'A reflection that walks beside you.',
    'companion_name': 'Name your companion',
    'companion_name_placeholder': 'e.g., Echo, Mirror, Shadow...',
    'companion_lens': 'Personality lens',
    'companion_mood': 'Communication mood',
    'companion_fears': 'What are you afraid of? (optional, comma-separated)',
    'companion_struggles': 'What do you struggle with? (optional, comma-separated)',
    'create_companion': 'Create',
    'companion_input': 'Say something...',
    'companion_greeting': 'Your companion is here. Say something.',
    'companion_starter_1': "What's on my mind today?",
    'companion_starter_2': 'I feel stuck.',
    'companion_starter_3': 'Tell me something I avoid.',
    'fears_placeholder': 'e.g., failure, rejection, being forgotten...',
    'struggles_placeholder': 'e.g., overthinking, procrastination, loneliness...',
    'back_step': 'Back',
    'lens_reflective': 'Reflective',
    'lens_reflective_desc': 'Deep questions, pattern recognition',
    'lens_supportive': 'Supportive',
    'lens_supportive_desc': 'Warm, encouraging, validating',
    'lens_challenging': 'Challenging',
    'lens_challenging_desc': 'Direct, thought-provoking',
    'lens_curious': 'Curious',
    'lens_curious_desc': 'Unexpected questions, wonder',
    'lens_shadow': 'Shadow',
    'lens_shadow_desc': 'The hidden side, honest discomfort',
    'companion_mood_calm': 'Calm',
    'companion_mood_calm_desc': 'Measured and peaceful',
    'companion_mood_intense': 'Intense',
    'companion_mood_intense_desc': 'Urgent and deep',
    'companion_mood_playful': 'Playful',
    'companion_mood_playful_desc': 'Witty and light',
    'companion_mood_melancholic': 'Melancholic',
    'companion_mood_melancholic_desc': 'Beautiful sadness',
    'update_ai_companion': 'AI Companion - a personal AI that remembers your journey',
    // v3.23.0
    'mood_history': 'Mood History',
    'top_rooms': 'Your Top Rooms',
    'visits_count': 'visits',
    'set_intention': 'Set an intention for today',
    'todays_intention': "Today's Intention",
    'intention_placeholder': 'I will...',
    'set': 'Set',
    'update_mood_history': 'Mood History - track how your mood changes over time',
    'update_daily_intention': 'Daily Intention - set a focus for each day',
    'update_top_rooms': 'Top Rooms - quick access to your most visited rooms',
    'update_10_new_rooms_v323': '10 new rooms: emotions, self-reflection, and connection',
    'room_the-emotion-you-hide': 'The Emotion You Hide',
    'sub_the-emotion-you-hide': 'Name it. Just once.',
    'emotion_hide_placeholder': 'The emotion I never show is...',
    'quote_emotion_hide': 'What we hide from others, we often hide from ourselves.',
    'room_your-comfort-lie': 'Your Comfort Lie',
    'sub_your-comfort-lie': 'The small lie that keeps you comfortable.',
    'comfort_lie_placeholder': 'The lie I tell myself is...',
    'admit_it': 'Admit it',
    'quote_comfort_lie': 'Comfort lies are the walls we build with soft fabric.',
    'room_the-silence-between': 'The Silence Between',
    'sub_the-silence-between': 'Sit in the gap between two thoughts.',
    'silence_instruction': 'Close your eyes. Find the gap between two thoughts. Then press the button.',
    'silence_sitting': 'Sitting in silence...',
    'silence_found': 'I found it',
    'silence_result': 'You sat for',
    'seconds': 'seconds',
    'quote_silence_between': 'Between every thought is an infinite space. You just visited it.',
    'room_what-you-carry': 'What You Carry',
    'sub_what-you-carry': 'Name the invisible weight.',
    'carry_placeholder': 'The weight I carry is...',
    'set_it_down': 'Set it down',
    'carry_released': 'For a moment, it was lighter.',
    'quote_carry': 'You cannot put down what you refuse to name.',
    'room_the-version-you-lost': 'The Version You Lost',
    'sub_the-version-you-lost': 'Who you were before life happened.',
    'version_lost_placeholder': 'Before everything changed, I was...',
    'remember': 'Remember',
    'quote_version_lost': "You didn't lose them. They became part of what you are now.",
    'room_letter-to-past-self': 'Letter to Past Self',
    'sub_letter-to-past-self': 'Write to who you used to be.',
    'letter_past_placeholder': 'Dear younger me,...',
    'send_letter': 'Send',
    'letter_sent': 'The letter was sent. Somewhere, they received it.',
    'quote_letter_past': 'Every word of comfort you write to your past self is really for who you are now.',
    'room_the-moment-you-knew': 'The Moment You Knew',
    'sub_the-moment-you-knew': 'The instant everything shifted.',
    'moment_knew_placeholder': 'The moment I knew was when...',
    'mark_it': 'Mark it',
    'quote_moment_knew': 'Some moments divide your life into before and after. You just named one.',
    'room_the-words-you-rehearse': 'The Words You Rehearse',
    'sub_the-words-you-rehearse': 'What you practice saying but never do.',
    'words_rehearse_placeholder': 'The words I keep rehearsing are...',
    'say_them': 'Say them',
    'words_spoken': 'You finally said them. Even if only here.',
    'quote_words_rehearse': 'Rehearsed words are cages. Spoken words are birds.',
    'room_someone-thinks-of-you': 'Someone Thinks of You',
    'sub_someone-thinks-of-you': 'Right now, somewhere, someone remembers you.',
    'someone_line_1': 'Right now, somewhere, someone remembers you.',
    'someone_line_2': 'Not because of what you did.',
    'someone_line_3': 'But because of how you made them feel.',
    'quote_someone_thinks': "You exist in memories you don't even know about.",
    'room_the-question-you-fear': 'The Question You Fear',
    'sub_the-question-you-fear': 'The one question you hope nobody asks.',
    'question_fear_placeholder': 'The question I fear most is...',
    'quote_question_fear': 'The question you fear most is usually the one with the most important answer.',
    // v3.24.0
    'behavior_insight': 'The site notices',
    'insight_pattern': "You're drawn to {category}. You explore most in the {time}.",
    'cat_identity': 'identity',
    'cat_truth': 'truth',
    'cat_time': 'time',
    'cat_mortality': 'mortality',
    'cat_stillness': 'stillness',
    'cat_darkness': 'darkness',
    'cat_patterns': 'patterns',
    'cat_fear': 'fear',
    'cat_play': 'play',
    'cat_experiments': 'experiments',
    'cat_ritual': 'ritual',
    'cat_secrets': 'secrets',
    'time_late_night': 'late night',
    'time_morning': 'morning',
    'time_afternoon': 'afternoon',
    'time_evening': 'evening',
    'anonymous_visitor': 'a visitor',
    'update_behavior_insight': 'Behavior Insight - the site reflects your patterns back to you',
    'update_anonymous_reflections_feed': 'Anonymous Reflections - see what others have felt',
    'update_daily_prompt': 'Daily Prompt - a thought to carry with you',
    // v4.0.0
    'room_recurring-dream-journal': 'Recurring Dream Journal',
    'sub_recurring-dream-journal': 'The dream that keeps returning.',
    'dream_journal_placeholder': 'The dream I keep having...',
    'record_dream': 'Record',
    'dream_journal_reflection': 'Dreams speak what the waking mind won\'t.',
    'quote_dream_journal': 'Recurring dreams are unfinished conversations with yourself.',
    'room_lucid-moment': 'The Lucid Moment',
    'sub_lucid-moment': 'Become aware inside the dream.',
    'lucid_line_1': 'You are reading these words.',
    'lucid_line_2': 'But are you awake?',
    'lucid_line_3': 'How would you know the difference?',
    'become_aware': 'I am aware',
    'lucid_aware': 'This moment of awareness is the only thing that\'s real.',
    'quote_lucid_moment': 'The moment you question reality is the moment you truly see it.',
    'room_nightmare-you-remember': 'The Nightmare You Remember',
    'sub_nightmare-you-remember': 'The one that still haunts you.',
    'nightmare_placeholder': 'The nightmare I can\'t forget...',
    'face_it': 'Face it',
    'nightmare_reflection': 'By writing it, you took away some of its power.',
    'quote_nightmare': 'Nightmares are fears that insist on being seen.',
    'room_dream-you-wish-was-real': 'The Dream You Wish Was Real',
    'sub_dream-you-wish-was-real': 'Wake up. It wasn\'t real.',
    'dream_wish_placeholder': 'I dreamed that...',
    'hold_onto_it': 'Hold onto it',
    'quote_dream_wish': 'The dreams we mourn losing reveal the life we truly want.',
    'room_the-dream-visitor': 'The Dream Visitor',
    'sub_the-dream-visitor': 'Someone keeps appearing in your dreams.',
    'dream_visitor_line_1': 'There is someone who visits you in dreams.',
    'dream_visitor_line_2': 'You didn\'t invite them.',
    'dream_visitor_line_3': 'But they keep coming back.',
    'dream_visitor_line_4': 'Maybe they\'re trying to tell you something.',
    'quote_dream_visitor': 'The people who appear in our dreams are messengers from our subconscious.',
    'room_the-blank-canvas': 'The Blank Canvas',
    'sub_the-blank-canvas': 'Create something from nothing.',
    'blank_canvas_placeholder': 'On this blank canvas, I create...',
    'create': 'Create',
    'quote_blank_canvas': 'Every creation begins with the courage to face the blank.',
    'room_song-that-is-you': 'The Song That Is You',
    'sub_song-that-is-you': 'If you were a song, which one?',
    'song_placeholder': 'The song that is me...',
    'play_it': 'Play it',
    'quote_song': 'Music is what feelings sound like.',
    'room_color-of-today': 'The Color of Today',
    'sub_color-of-today': 'Pick the color of your current state.',
    'color_chosen': 'This is how you feel today. In color.',
    'quote_color_today': 'Colors don\'t need words. Neither do feelings.',
    'room_the-unwritten-poem': 'The Unwritten Poem',
    'sub_the-unwritten-poem': 'Write what you\'ve been holding inside.',
    'poem_placeholder': 'Words that have been waiting...',
    'publish_it': 'Publish it',
    'quote_unwritten_poem': 'The poem was always there. You just finally let it out.',
    'room_your-inner-voice': 'Your Inner Voice',
    'sub_your-inner-voice': 'What does it actually say?',
    'inner_voice_placeholder': 'My inner voice says...',
    'listen': 'Listen',
    'inner_voice_reflection': 'Now that you\'ve heard it, what will you do?',
    'quote_inner_voice': 'Your inner voice has been speaking all along. You just started listening.',
    'room_fear-you-named': 'The Fear You Named',
    'sub_fear-you-named': 'Name it and it loses power.',
    'fear_named_placeholder': 'The fear I finally name...',
    'name_it': 'Name it',
    'fear_named_reflection': 'Named fears shrink. Unnamed fears grow.',
    'quote_fear_named': 'What you can name, you can face. What you can face, you can overcome.',
    'room_the-leap': 'The Leap',
    'sub_the-leap': 'What would you jump into?',
    'leap_placeholder': 'If I could leap, I would...',
    'leap': 'Leap',
    'quote_leap': 'The net appears after you jump, not before.',
    'room_courage-inventory': 'Courage Inventory',
    'sub_courage-inventory': 'List every brave thing you\'ve done.',
    'courage_placeholder': 'Something brave I did...',
    'courage_count': 'You listed {count} acts of courage.',
    'quote_courage': 'You are braver than you remember.',
    'room_what-scares-you-most': 'What Scares You Most',
    'sub_what-scares-you-most': 'The deepest fear.',
    'scares_placeholder': 'What scares me most is...',
    'quote_scares': 'Your deepest fear is usually your greatest teacher.',
    'room_facing-the-dark': 'Facing the Dark',
    'sub_facing-the-dark': 'Sit in complete darkness.',
    'darkness_counting': '{seconds} seconds in darkness...',
    'return_to_light': 'Return to light',
    'darkness_survived': 'You sat in darkness for {seconds} seconds.',
    'quote_facing_dark': 'In darkness, we discover what we carry inside.',
    'room_letter-to-future-self': 'Letter to Future Self',
    'sub_letter-to-future-self': 'Write to who you\'ll become.',
    'letter_future_placeholder': 'Dear future me,...',
    'seal_letter': 'Seal',
    'letter_future_sealed': 'The letter is sealed. Someday, you\'ll read it.',
    'quote_letter_future': 'Every letter to the future is really a promise to the present.',
    'room_the-world-you-want': 'The World You Want',
    'sub_the-world-you-want': 'Describe the world you wish existed.',
    'world_placeholder': 'The world I want...',
    'build_it': 'Build it',
    'quote_world_want': 'The world you imagine is the first step to the world you create.',
    'room_your-next-chapter': 'Your Next Chapter',
    'sub_your-next-chapter': 'What happens next in your story?',
    'next_chapter_placeholder': 'In my next chapter...',
    'turn_the_page': 'Turn the page',
    'quote_next_chapter': 'You are both the author and the protagonist.',
    'room_the-wish': 'The Wish',
    'sub_the-wish': 'One wish. No rules.',
    'wish_placeholder': 'I wish...',
    'make_wish': 'Make the wish',
    'quote_wish': 'What you wish for reveals what you value most.',
    'room_if-you-had-one-year': 'If You Had One Year',
    'sub_if-you-had-one-year': 'What would you do with 365 days?',
    'one_year_placeholder': 'If I had one year left...',
    'begin_now': 'Begin now',
    'one_year_reflection': 'So why aren\'t you doing it?',
    'quote_one_year': 'Live as if you had one year. Because you never know.',
    'update_v4_languages': '4 new languages: Hindi, Korean, Japanese, Turkish',
    'update_v4_20_rooms': '20 new rooms across Dreams, Creativity, Fear, and Future',
    'update_v4_dreams_category': 'Dreams & Subconscious room category',
    'update_v4_creativity_category': 'Creativity & Expression room category',
    'update_v4_fear_category': 'Fear & Courage room category',
    'update_v4_future_category': 'Future & Hope room category',

    // v4.0 Phase 2: Playlists & Dashboard
    'room_playlists': 'Room Playlists',
    'personal_dashboard': 'Dashboard',
    'dashboard_subtitle': 'Your journey at a glance',
    'visit_pattern': 'Visit Pattern (14 days)',
    'dashboard_top_rooms': 'Most Visited Rooms',
    'category_breakdown': 'Room Categories',
    'mood_trend': 'Mood Trend',
    'fragment_breakdown': 'Fragment Breakdown',
    'no_data_yet': 'No data yet',
    'current_streak': 'Day Streak',
    'playlist_morning': 'Morning Reflection',
    'playlist_morning_desc': 'Start your day with clarity and presence',
    'playlist_identity': 'Identity Deep Dive',
    'playlist_identity_desc': 'Who are you beneath the labels?',
    'playlist_creative': 'Creative Spark',
    'playlist_creative_desc': 'Unlock your creative expression',
    'playlist_courage': 'Courage Path',
    'playlist_courage_desc': 'Face your fears one room at a time',
    'playlist_dreams': 'Dream Journal',
    'playlist_dreams_desc': 'Explore the landscapes of your subconscious',
    'playlist_future': 'Future Vision',
    'playlist_future_desc': 'Letters and wishes to the you that\'s coming',
    'playlist_truth': 'Truth Confrontation',
    'playlist_truth_desc': 'Face the uncomfortable realities',
    'playlist_night': 'Night Journey',
    'playlist_night_desc': 'Embrace silence, void, and nothingness',

    // v4.1 Phase 4: Social, Badges, Milestones, Ambient
    'update_v41_badges': 'Collectible badge system replacing emojis with styled icons',
    'update_v41_whisper_wall': 'Real-time Whisper Wall — leave anonymous truths with others',
    'update_v41_milestones': 'Milestone notifications at key room counts',
    'update_v41_ambient': 'New ambient soundscapes for Dreams, Creativity, Fear, and Future rooms',
    'update_v41_achievements': '10 new achievements including Centurion, Dreamer, Fearless, and more',
    'whisper_wall': 'Whisper Wall',
    'leave_whisper': 'Leave a whisper...',
    'whisper_sent': 'Your whisper drifts into the wall.',
    'whispers_count': 'whispers',
    'anonymous': 'anonymous',

    // v4.2.0 Room translations
    'room_focus-timer': 'Focus Timer', 'sub_focus-timer': 'Sit in silence. Reflect.',
    'room_gratitude-jar': 'Gratitude Jar', 'sub_gratitude-jar': 'One thing you are grateful for',
    'room_mood-compass': 'Mood Compass', 'sub_mood-compass': 'Where are you right now?',
    'room_memory-fade': 'Memory Fade', 'sub_memory-fade': 'Watch a memory dissolve',
    'room_sound-of-your-day': 'Sound of Your Day', 'sub_sound-of-your-day': 'What does today sound like?',
    'room_the-apology-room': 'The Apology Room', 'sub_the-apology-room': 'Write what you never said',
    'room_your-unread-messages': 'Your Unread Messages', 'sub_your-unread-messages': 'Messages you never opened',
    'room_the-weight-room': 'The Weight Room', 'sub_the-weight-room': 'Name what weighs on you',
    'room_parallel-lives': 'Parallel Lives', 'sub_parallel-lives': 'In another life, you are...',
    'room_the-quiet-score': 'The Quiet Score', 'sub_the-quiet-score': 'How long can you be still?',

    // v4.2.0 Room content
    'focus_choose': 'How long do you want to sit in silence?',
    'focus_sit': 'Sit. Breathe. Let thoughts pass.',
    'focus_done': 'The silence has spoken.',
    'quote_focus_timer': 'In stillness, you hear what noise drowns out.',
    'minutes': 'min',
    'gratitude_prompt': 'Write one thing you are grateful for today.',
    'gratitude_placeholder': 'I am grateful for...',
    'gratitude_add': 'Add to jar',
    'gratitude_added': 'Your gratitude drifts into the jar.',
    'gratitude_count': 'moments of gratitude',
    'quote_gratitude_jar': 'Gratitude is not about having more. It is about noticing what is already here.',
    'mood_compass_prompt': 'Click where you feel you are right now.',
    'mood_high_energy': 'High Energy',
    'mood_low_energy': 'Low Energy',
    'mood_positive': 'Positive',
    'mood_negative': 'Negative',
    'quote_mood_compass': 'Knowing where you are is the first step to knowing where to go.',
    'memory_fade_prompt': 'Type a memory. Watch it dissolve.',
    'memory_fade_placeholder': 'A memory you want to release...',
    'memory_fade_start': 'Let it fade',
    'memory_fade_done': 'It is gone now. But it was real.',
    'quote_memory_fade': 'Memories dissolve whether we watch or not.',
    'sound_day_prompt': 'Pick 3-5 sounds that represent your day.',
    'sound_rain': 'Rain', 'sound_traffic': 'Traffic', 'sound_birds': 'Birds',
    'sound_keyboard': 'Keyboard', 'sound_silence': 'Silence', 'sound_wind': 'Wind',
    'sound_ocean': 'Ocean', 'sound_crowd': 'Crowd', 'sound_music': 'Music',
    'sound_day_confirm': 'This is my day',
    'sound_day_result': 'The soundtrack of your day.',
    'quote_sound_day': 'Every day has a sound. Most of us just forget to listen.',
    'apology_prompt': 'Write an apology you have never delivered.',
    'apology_placeholder': 'I am sorry for...',
    'apology_release': 'Release it',
    'apology_done': 'It compressed into nothing. But you said it.',
    'quote_apology': 'Some apologies are not for them. They are for you.',
    'unread_from_past': 'From: Your Past Self',
    'unread_from_future': 'From: Your Future Self',
    'unread_from_critic': 'From: Your Inner Critic',
    'unread_msg_past': 'Remember when you promised yourself things would be different?',
    'unread_msg_future': 'I hope you are kinder to yourself now than you used to be.',
    'unread_msg_critic': 'You know you could have done more. But will you?',
    'unread_count': 'unread',
    'unread_reply_placeholder': 'Your reply...',
    'unread_reply': 'Send',
    'unread_sent': 'Message sent into the void.',
    'quote_unread_messages': 'The messages we avoid are often the ones we need most.',
    'weight_prompt': 'Name the things weighing on you.',
    'weight_placeholder': 'Something that weighs on me...',
    'weight_release_start': 'Begin releasing',
    'weight_release_prompt': 'Click each weight to release it.',
    'weight_done': 'The weight has lifted. For now.',
    'quote_weight_room': 'You cannot put down what you refuse to name.',
    'parallel_intro': 'In another life, you are...',
    'parallel_life_1': 'In another life, you are a lighthouse keeper on a remote island.',
    'parallel_life_2': 'In another life, you never left your hometown.',
    'parallel_life_3': 'In another life, you said yes when you said no.',
    'parallel_life_4': 'In another life, you are someone else entirely.',
    'parallel_life_5': 'In another life, you are exactly who you are now.',
    'parallel_life_6': 'In another life, you chose the harder path.',
    'parallel_life_7': 'In another life, you are still looking for this room.',
    'quote_parallel_lives': 'Every choice creates a universe where you chose differently.',
    'quiet_instruction': 'Do not interact. Just be still.',
    'quiet_reveal_1': 'You are here.',
    'quiet_reveal_2': 'The noise is fading.',
    'quiet_reveal_3': 'Something is emerging from the quiet.',
    'quiet_reveal_4': 'You are more patient than you think.',
    'quiet_reveal_5': 'The stillness knows your name.',
    'quiet_score_label': 'Your quiet score',
    'quiet_impressive': 'Remarkable stillness.',
    'quiet_good': 'You found some quiet.',
    'quiet_try_again': 'The urge to act is strong.',
    'quote_quiet_score': 'Stillness is not the absence of movement. It is the presence of awareness.',

    // v4.2.0 Changelog
    'update_v42_10_rooms': '10 new interactive rooms: Focus Timer, Gratitude Jar, Mood Compass, and more',
    'update_v42_focus_timer': 'Focus Timer — sit in silence with a progressive darkening timer',
    'update_v42_gratitude_jar': 'Gratitude Jar — collect moments of gratitude over time',
    'update_v42_mood_compass': 'Mood Compass — place yourself on energy and valence axes',
    'update_v42_weight_room': 'The Weight Room — name and release what weighs on you',
    'update_v43_full_localization': 'Complete localization — all rooms now fully translated',
    'update_v43_40_rooms_translated': '40+ rooms translated — every interactive room supports all languages',
    'update_v43_10_languages': '10 languages supported — English, Arabic, Spanish, Portuguese, Chinese, French, Hindi, Korean, Japanese, Turkish',
    'update_v43_nav_consistency': 'Consistent navigation — unified room exit text across all rooms',
    // Anonymous Echoes Room
    'echoes_subtitle': 'Fragments from other travelers',
    'echoes_from': 'from',
    'echoes_ago': 'ago',
    'echoes_arriving': 'More echoes arriving...',
    'echoes_reveal': 'These are not their answers. They are yours.',
    'echoes_reveal2': '(Every echo was once your own reflection)',
    'echoes_quote': "We think we're unique. We're just echoes of each other.",
    // The Revisit Room
    'revisit_subtitle': "How you've changed",
    'revisit_not_enough': "You haven't answered enough questions yet.",
    'revisit_explore': 'Explore more rooms. Leave your marks. Return later.',
    'revisit_quote1': 'Change requires history. History requires time.',
    'revisit_none_changed': 'But none have changed yet. Revisit rooms. Answer again.',
    'revisit_show_evolved': "We'll show you how you've evolved.",
    'revisit_quote2': 'To see change, you must first change.',
    'revisit_then': 'Then',
    'revisit_now': 'Now',
    'revisit_one_shift': 'One shift. Subtle but real.',
    'revisit_each_change': 'Each change is a small death. And a small birth.',
    'revisit_quote3': 'You are not who you were. You are not yet who you will be.',
    // Room Content - Image Zooming
    'image_zoom_title': 'The Image You Keep Zooming',
    'image_zoom_zoom': 'Zoom',
    'image_zoom_quote': 'There is nothing more to find. There never was.',
    
    // Room Content - Shadow Chase
    'shadow_chase_title': 'Shadow Chase',
    'shadow_chase_subtitle': 'catch the shadow • it reacts unpredictably',
    'shadow_chase_catches': 'catches',
    'shadow_chase_behavior': 'behavior',
    'shadow_chase_flee': 'flee',
    'shadow_chase_wander': 'wander',
    'shadow_chase_mirror': 'mirror',
    'shadow_chase_msg_1': 'The shadow knows where you\'re going.',
    'shadow_chase_msg_2': 'What you chase often runs.',
    'shadow_chase_msg_3': 'Sometimes stillness catches more than speed.',
    'shadow_chase_msg_4': 'Your shadow remembers every movement.',
    'shadow_chase_msg_5': 'It learns from you.',
    
    // Room Content - Left Too Early
    'left_early_title': 'You Left Too Early',
    'left_early_subtitle': 'Rooms you exited in under 5 seconds.',
    'left_early_none': 'You haven\'t left any room too quickly.',
    'left_early_patience': 'Perhaps you give things time.',
    'left_early_quote': 'Sometimes we leave before the room can show us anything.',
    
    // Room Content - Face Without Features
    'face_title': 'Face Without Features',
    'face_instruction': 'Click a feature to remove it.',
    'face_nothing': 'Nothing remains.',
    'face_remove': 'Remove',
    'face_remaining': 'remaining',
    'face_eyes': 'Eyes',
    'face_nose': 'Nose',
    'face_mouth': 'Mouth',
    'face_eyebrows': 'Eyebrows',
    'face_outline': 'Face',
    'face_quote': 'Identity is just features we agree to recognize.',
    
    // Room Content - The Last Visitor
    'last_visitor_title': 'The Last Visitor',
    'last_visitor_someone': 'Someone was here',
    'last_visitor_first': 'You are the first visitor.',
    'last_visitor_no_one': 'No one came before you.',
    'last_visitor_next': 'The next person will see your timestamp.',
    'last_visitor_know': 'They\'ll know you were here.',
    'last_visitor_quote': 'We leave traces even in the dark.',
    'last_visitor_days_ago': '{count} day(s) ago',
    'last_visitor_hours_ago': '{count} hour(s) ago',
    'last_visitor_minutes_ago': '{count} minute(s) ago',
    'last_visitor_moments_ago': 'moments ago',
    
    // Room Content - Room You Visit Most
    'visit_most_title': 'The Room You Visit Most',
    'visit_most_subtitle': 'Your behavior reveals your patterns.',
    'visit_most_visits': '{count} visit(s)',
    'visit_most_none': 'No patterns yet. Keep exploring.',
    'visit_most_quote': 'We return to what we need, not what we want.',
    
    // Room Content - 3:17 AM Room
    'room_317_locked': 'This room only opens at 3:17 AM.',
    'room_317_opens_in': 'Opens in {hours}h {minutes}m',
    'room_317_msg_1': 'It\'s 3:17 AM.',
    'room_317_msg_2': 'You\'re awake.',
    'room_317_msg_3': 'Why?',
    'room_317_msg_4': 'The world sleeps. But not you.',
    'room_317_msg_5': 'What thought won\'t let you go?',
    'room_317_msg_6': 'At this hour, masks fall away.',
    'room_317_msg_7': 'There\'s only you and the silence.',
    'room_317_msg_8': 'This moment exists only now.',
    'room_317_quote': 'At this hour, masks fall away.',
    
    // Room Content - Weekend Room
    'weekend_locked': 'This room is only open on weekends.',
    'weekend_days_until': '{count} days until this room opens.',
    'weekend_label': 'Weekend Room',
    'weekend_prompt': 'The week is over. What did you carry into these two days?',
    'weekend_placeholder': 'A weight, a hope, a question...',
    'weekend_release': 'Release it',
    'weekend_wrote': 'You wrote:',
    'weekend_letting_go': 'The weekend is for letting go.',
    
    // Room Content - Something Follows
    'follows_title': 'Something Follows',
    'follows_distance': 'Distance',
    'follows_run': 'Run',
    'follows_keep_running': 'Keep running. It never stops.',
    'follows_caught': 'It caught you.',
    'follows_couldnt': 'You couldn\'t run forever.',
    'follows_quote': 'The thing that follows is always yourself.',
    
    // Room Content - Line You Crossed
    'line_title': 'The Line You Crossed',
    'line_instruction': 'Draw a single line from left to right.',
    'line_click_drag': 'Click and drag to draw',
    'line_hesitations': 'We found {count} hesitation(s).',
    'line_quote': 'Every pause reveals a doubt.',

    // Mirror Lies Room
    'mirror_lies_title': 'The Mirror Lies',
    'mirror_lies_msg1': 'Look at yourself.',
    'mirror_lies_msg2': 'Is that really you?',
    'mirror_lies_msg3': 'The mirror shows what you want to see.',
    'mirror_lies_msg4': 'Click to reveal the truth.',
    'mirror_lies_msg5': 'Each crack reveals more.',
    'mirror_lies_msg6': 'There is nothing behind the reflection.',
    'mirror_lies_quote': 'The mirror never showed you. Only what you believed.',

    // Winter Solstice Room
    'winter_title': 'The Longest Night',
    'winter_date': 'DECEMBER 21ST',
    'winter_msg1': 'The longest night is here.',
    'winter_msg2': 'In darkness, we find what we carry.',
    'winter_msg3': 'What will you release? What will you keep?',
    'winter_releasing': 'I am releasing:',
    'winter_keeping': 'I am keeping:',
    'winter_release_placeholder': 'What no longer serves you...',
    'winter_keep_placeholder': 'What gives you strength...',
    'winter_enter_light': 'Enter the light',
    'winter_released': 'Released:',
    'winter_kept': 'Kept:',
    'winter_light_returns': 'From this night, the light returns.',
    'winter_quote': 'The longest night teaches us that even in the deepest darkness, light is already returning.',

    // Halloween Room
    'halloween_title': 'The Veil Room',
    'halloween_date': 'OCTOBER 31ST',
    'halloween_msg1': 'The veil is thin tonight.',
    'halloween_msg2': 'What lurks in your shadows?',
    'halloween_msg3': 'Name the fear you carry with you...',
    'halloween_msg4': 'Give it form, and release it.',
    'halloween_placeholder': 'Type your fear...',
    'halloween_release': 'Release it',
    'halloween_dissolves': 'Your fear dissolves into the night...',
    'halloween_quote': 'The things we fear lose power when we name them. Let them go.',

    // What Keeps Awake Room
    'awake_title': 'What Keeps You Awake',
    'awake_locked': 'This room is only available after 10 PM.',
    'awake_return': 'Return when the night comes.',
    'awake_press_enter': 'Press Enter',
    'awake_named': "Now you've named it.",
    'awake_quote': 'The dark knows what you avoid in daylight.',

    // Voice Without Meaning Room
    'voice_meaning_title': 'Your Voice Without Meaning',
    'voice_meaning_prompt': 'Hum. Breathe. Make any sound. No words.',
    'voice_meaning_done': 'This was your presence. Nothing more.',
    'voice_meaning_hold': 'Hold',
    'voice_meaning_recording': 'Recording...',
    'voice_meaning_no_playback': 'No playback. The sound existed only in that moment.',
    'voice_meaning_quote': 'You exist. That is the only content.',

    // They're Watching Room
    'watching_msg1': "You're alone here.",
    'watching_msg2': 'Completely alone.',
    'watching_msg3': 'Or are you?',
    'watching_msg4': "They've been here the whole time.",
    'watching_msg5': "They're always watching.",
    // watching_quote already defined above

    // Shortcut Always Take Room
    'shortcut_title': 'The Shortcut You Always Take',
    'shortcut_instruction': 'Select the ones you recognize.',
    'shortcut_1': 'Saying "I\'m fine" when you\'re not',
    'shortcut_2': 'Checking your phone instead of being present',
    'shortcut_3': 'Staying quiet to avoid conflict',
    'shortcut_4': 'Buying instead of making',
    'shortcut_5': 'Waiting for the "right time"',
    'shortcut_6': 'Blaming circumstances instead of acting',
    'shortcut_7': 'Choosing comfort over growth',
    'shortcut_8': 'Scrolling instead of sleeping',
    'shortcut_9': 'Saying yes when you mean no',
    'shortcut_10': 'Planning instead of doing',
    'shortcut_pattern': 'Your Pattern',
    'shortcut_selected': 'You selected:',
    'shortcut_show_pattern': 'Show my pattern',
    'shortcut_quote': 'The shortcut becomes the path.',
    'shortcut_pattern_own': 'Your pattern is your own.',
    'pattern_avoidance': "You avoid what's uncomfortable.",
    'pattern_distraction': 'You escape into distraction.',
    'pattern_convenience': 'You trade effort for ease.',
    'pattern_delay': 'You wait for conditions that never come.',
    'pattern_comfort': 'You protect yourself from discomfort.',
    'pattern_pleasing': 'You disappear to keep others comfortable.',

    // Life As Shapes Room
    'shapes_q1': 'How often do you feel at peace?',
    'shapes_q2': 'How much do you trust yourself?',
    'shapes_q3': 'How connected do you feel to others?',
    'shapes_q4': 'How much of your time is truly yours?',
    'shapes_q5': 'How heavy does the future feel?',
    'shapes_result_title': 'Your Life as Shapes',
    'shapes_no_labels': 'No labels. No interpretation provided.',
    'shapes_yours': 'What you see is yours to understand.',
    'shapes_quote': 'Shape is meaning without words.',
    'shapes_scale': '1 = not at all, 5 = completely',

    // Read Aloud Room
    'read_title': 'Read This Aloud',
    'read_stmt1': 'I am avoiding something.',
    'read_stmt2': 'I am afraid.',
    'read_stmt3': "I don't know what I'm doing.",
    'read_stmt4': 'I need help.',
    'read_stmt5': 'I am enough.',
    'read_stmt6': 'I have been pretending.',
    'read_stmt7': 'I am tired.',
    'read_stmt8': 'I want more.',
    'read_i_read': 'I read it aloud',
    'read_really': 'Did you really say it?',
    'read_skipped': 'I skipped it',
    'read_result': 'You spoke truths aloud.',
    'read_quote': 'Speaking truth makes it real.',
    // 'yes' already defined above

    // Sentence You Avoid Room
    'sentence_title': 'The Sentence You Avoid',
    'sentence_sub': 'Complete this sentence. You cannot leave until you do.',
    'sentence_prompt': 'I would be happier if I stopped',
    'sentence_cant_leave': 'You cannot leave without writing something.',
    'sentence_knew': 'You knew this already.',
    'sentence_quote': 'The first step is saying it.',

    // Say It Once Room
    'say_once_title': 'Say It Once',
    'say_once_prompt': 'Record one sentence. You cannot replay it. You cannot re-record.',
    'say_once_done': 'It has been said. No replay. No redo.',
    'say_once_stop': 'Stop',
    'say_once_record': 'Record',
    'say_once_quote': 'Some things are only meant to be said once.',

    // New Year Room
    'newyear_title': 'Letter to Future Self',
    'newyear_threshold': 'THE THRESHOLD',
    'newyear_placeholder': 'Dear future me...',
    'newyear_seal': 'Seal the letter',
    'newyear_waiting': 'This letter will be waiting for you.',
    'newyear_quote': 'Every ending is a door. Step through it with intention.',

    // Room You Avoid
    'avoid_title': 'The Room You Avoid',
    'avoid_sub': 'What you skip says something.',
    'avoid_not_entered': "You haven't entered:",
    'avoid_visited_all': "You've visited every free room.",
    'avoid_not_pattern': "Perhaps avoidance isn't your pattern.",
    'avoid_quote': 'Avoidance is also a choice.',

    // Who Without Job Room
    'who_q1': 'What would you do if money was not a concern?',
    'who_q2': 'How would you introduce yourself at a party?',
    'who_q3': 'What makes you feel alive?',
    'who_q4': 'What would your friends say you are good at?',
    'who_q5': 'What did you love doing as a child?',
    'who_result_title': 'You, Without a Title',
    'who_done': 'This is who you are when the title is gone.',
    'who_quote': 'Identity is not what you do. It is what remains when you stop.',
    'who_placeholder': 'Be honest...',
    'who_see_myself': 'See Myself',
    'question': 'Question',
    // 'next' already defined above

    // Last Conversation Room
    'lastconv_title': 'The Last Conversation',
    'lastconv_sub': 'Write what you will never send.',
    'lastconv_to': 'To:',
    'lastconv_message': 'Message:',
    'lastconv_to_placeholder': 'Someone who needs to hear this...',
    'lastconv_msg_placeholder': 'What you have always wanted to say...',
    'lastconv_not_saved': 'This message will not be saved or shared.',
    'lastconv_seal': 'Seal the Letter',
    'lastconv_wrote_to': 'You wrote to:',
    'lastconv_never_sent': 'This message will never be sent.',
    'lastconv_point': 'But writing it was the point.',
    'lastconv_quote': 'Some words need to exist, even if they are never heard.',

    // Real Priorities Room
    'priorities_title': 'Your Real Priorities',
    'priorities_sub': 'How many hours per week do you spend on each?',
    'priorities_result': 'Your Real Priorities',
    'priorities_most_time': 'You spend most of your time on:',
    'priorities_truth': 'Time reveals truth. Words can lie, schedules cannot.',
    'priorities_quote': 'You are what you repeatedly do.',
    'priorities_show': 'Show Me My Truth',
    'priority_work': 'Work',
    'priority_family': 'Family',
    'priority_friends': 'Friends',
    'priority_health': 'Health',
    'priority_learning': 'Learning',
    'priority_entertainment': 'Entertainment',
    'priority_social': 'Social Media',
    'priority_sleep': 'Sleep',

    // The Voice Room
    'voice_intro': 'A voice speaks in the darkness...',
    'voice_silence': '(silence)',
    'voice_quote': 'Some voices never find their way home.',

    // It Knows Your Name Room
    'knows_tell': 'Tell me your name.',
    'knows_placeholder': 'Your name...',
    'knows_warning': "(You shouldn't have come here)",
    'knows_quote': 'Names have power. Yours was already known.',

    // The Door Room
    'door_msg1': 'Something is at the door.',
    'door_stopped': '(The knocking stopped)',
    'door_quote': 'Some doors are meant to stay closed.',

    // Eyes In The Dark Room
    'eyes_msg1': 'One pair watches.',
    'eyes_unsee': 'You can never unsee them.',
    'eyes_quote': 'Some things, once noticed, cannot be unnoticed.',

    // Full Moon Room
    'moon_locked': 'This room only opens during the full moon.',
    'moon_not_full': 'The moon is not full tonight. Return when the cycle completes.',
    'moon_quote': 'Some truths only reveal themselves in cycles.',

    // You At 3:17 AM Room
    'at317_title': 'You at 3:17 AM',
    'at317_locked': 'This room opens between 2 AM and 5 AM.',
    'at317_current': 'Current time:',
    'at317_quote': 'The night knows your name.',

    // Hallway of Mirrors
    'mirrors_you_are_here': 'You are here.',

    // Listen to Yourself Room
    'listen_title': 'Listen to Yourself',
    'listen_recording': 'Recording...',
    'listen_start': 'Start',
    'listen_play': 'Play',
    'listen_instruction': 'Record 10 seconds. Say anything.',
    'listen_wait': 'Wait. Then listen.',
    'listen_done': 'That was your voice. Slowed down. Strange.',
    'listen_speed': 'Playing at 0.5x speed',
    'listen_quote': 'Your voice sounds different because you finally heard it.',

    // Export Journey Room
    'export_title': 'Export Your Journey',
    'export_subtitle': 'Take a piece of this place with you',
    'export_tab_export': 'Export',
    'export_tab_import': 'Import',
    'export_your_journey': 'YOUR JOURNEY',
    'export_since': 'Since',
    'export_time_spent': 'time spent',
    'export_rooms_visited': 'rooms visited',
    'export_rooms_completed': 'rooms completed',
    'export_fragments': 'fragments',
    'export_achievements_unlocked': 'achievements unlocked',
    'export_truths_recorded': 'truths recorded',
    'export_text_file': 'Text file',
    'export_json_file': 'JSON file',
    'export_copied': 'Copied!',
    'export_copy_clipboard': 'Copy to clipboard',
    'export_data_stays': 'Your data stays with you. We don\'t keep it.',
    'export_import_desc': 'Import a previously exported journey',
    'export_json_only': 'JSON format only',
    'export_select_file': 'Select file',
    'export_merge_note': 'This will merge with your current journey data.',
    'export_done': 'Exported.',
    'export_done_desc': 'A record of where you\'ve been. What you\'ve faced.',
    'export_quote': 'Some journeys are worth documenting. Not for others. For yourself.',
    'export_import_success': 'Journey imported successfully',
    'export_import_error': 'Invalid journey file',

    // The Waiting Room
    'waitroom_title': 'The Waiting Room',
    'waitroom_subtitle': 'Some doors open only for groups.',
    'waitroom_open': 'The door is open.',
    'waitroom_waiting': 'waiting',
    'waitroom_time_waited': 'Time waited',
    'waitroom_users_needed': 'Users needed',
    'waitroom_someone_joined': 'Someone joined the wait.',
    'waitroom_someone_left': 'Someone left.',
    'waitroom_room_opens': 'The room opens.',
    'waitroom_patience': 'Patience and presence unlock what force cannot.',
    'waitroom_souls_waited': '{count} souls waited together.',
    'waitroom_seconds_stillness': '{count} seconds of collective stillness.',
    'waitroom_quote': 'Some things cannot be rushed. Some doors cannot be opened alone.',

    // Time Capsule Room
    'capsule_title': 'Time Capsule',
    'capsule_subtitle': 'A message to your future self',
    'capsule_what_tell': 'What do you need to tell yourself?',
    'capsule_placeholder': 'Dear future me...',
    'capsule_when_arrive': 'When should it arrive?',
    'capsule_30_days': '30 days',
    'capsule_90_days': '90 days',
    'capsule_1_year': '1 year',
    'capsule_seal': 'Seal the capsule',
    'capsule_waiting': 'capsule(s) waiting',
    'capsule_sealed': 'Sealed.',
    'capsule_will_wait': 'Your message will wait for you.',
    'capsule_return_in': 'Return in {days} days to open it.',
    'capsule_quote_sealed': 'Time changes everything. Even you.',
    'capsule_arrived': 'A message has arrived.',
    'capsule_from_past': 'From the you of {days} days ago.',
    'capsule_open': 'Open the capsule',
    'capsule_you_wrote': 'You wrote this to yourself:',
    'capsule_remember': 'Do you remember writing this?',
    'capsule_quote_opened': 'The person who wrote this is gone. You remain.',

    // Contradiction Room
    'contradiction_title': 'The Contradiction Room',
    'contradiction_subtitle': 'Where your truths collide',
    'contradiction_not_enough': 'Not enough data yet.',
    'contradiction_answer_more': 'Answer more questions across the rooms.',
    'contradiction_answers_recorded': '{count}/5 answers recorded',
    'contradiction_quote_early': 'Contradictions require complexity. Build yours first.',
    'contradiction_no_found': 'No contradictions found.',
    'contradiction_consistent': 'Either you are remarkably consistent...',
    'contradiction_careful': '...or remarkably careful.',
    'contradiction_quote_none': 'Consistency is either wisdom or performance.',
    'contradiction_found': 'We found {count} contradiction(s)',
    'contradiction_you_said': 'You said:',
    'contradiction_but_also': 'But also:',
    'contradiction_see_another': 'See another →',
    'contradiction_not_weakness': 'Contradiction is not weakness. It is proof of depth.',
    'contradiction_quote': 'We contain multitudes. Some of them disagree.',

    // Entropy Garden (additional keys)
    'entropy_living': 'living',
    'entropy_dead': 'dead',
    'entropy_plant_seed': 'Plant a seed',
    'entropy_instructions': 'Click empty spots to plant. Click plants to water. Dead plants can be removed.',
    'entropy_reflect': 'Reflect on your garden',
    'entropy_lives': 'Your garden lives.',
    'entropy_thrive': '{living} plants thrive because you returned.',
    'entropy_absence': '{dead} remind you of absence.',

    // Memory Palace
    'palace_title': 'Memory Palace',
    'palace_subtitle': 'Build a mental space. Place objects to remember. Walk through it later.',
    'palace_room_of': 'Room {current} of {total}',
    'palace_click_place': 'Click to place',
    'palace_what_represent': 'What does this represent?',
    'palace_cancel': 'Cancel',
    'palace_place_object': 'Place an object',
    'palace_objects_count': '{count} object(s) in your palace',
    'palace_take_tour': 'Take the tour',
    'palace_empty_room': 'This room is empty',
    'palace_previous': 'Previous',
    'palace_next_room': 'Next room',
    'palace_complete_tour': 'Complete tour',
    'palace_remembers': 'Your palace remembers.',
    'palace_geography': 'A geography of meaning you\'ve constructed.',
    'palace_quote': 'The mind is a palace. What you place within it determines who you become.',
  },
  
  // Arabic translations
  ar: {
    // Index
    'title': 'الغرف البشرية',
    'tagline': 'مجموعة من الغرف تعكس من أنت عندما لا يراقبك أحد.',
    'enter': 'ادخل',
    'footer': 'ليست علاجاً. ليست شبكة اجتماعية. ليست منتجة.',
    
    // Hallway
    'back': 'رجوع ←',
    'hallway': 'الممر',
    'challenges': 'التحديات',
    'random': 'عشوائي',
    'open_rooms': 'الغرف المفتوحة',
    'dark_rooms': 'الغرف المظلمة',
    'enter_risk': 'ادخل على مسؤوليتك',
    'premium_rooms': 'الغرف المميزة',
    'unlock_to_access': 'افتح للوصول',
    'daily_rooms': 'الغرف اليومية',
    'refreshes_daily': 'تتجدد يومياً',
    'time_locked': 'غرف مقفلة بالوقت',
    'seasonal_rooms': 'الغرف الموسمية',
    'available_now': 'متاحة الآن',
    'fragments': 'الشظايا',
    'boss_unlocked': 'غرفة الزعيم مفتوحة',
    'collect_unlock': 'اجمع لفتح غرفة الزعيم',
    'dark_fragments': 'الشظايا المظلمة',
    'dark_boss_unlocked': 'زعيم الظلام مفتوح',
    'hidden_discovered': 'تم اكتشاف غرفة مخفية',
    'stillness_revealed': 'سكونك كشف شيئاً...',
    'constellation_map': 'خريطة النجوم',
    'your_stats': 'إحصائياتك',
    'completed': 'مكتملة',
    'game_rooms': 'غرف الألعاب',
    'experimental_rooms': 'الغرف التجريبية',
    'deep_rooms': 'الغرف العميقة',
    'meta_rooms': 'الغرف الميتا',
    'search_rooms': 'ابحث عن غرفة...',
    'no_results': 'لا توجد غرف تطابق بحثك',
    'clear_filters': 'مسح الفلاتر',
    'explore': 'استكشف',
    'all_categories': 'الكل',
    
    // About
    'what_is_this': 'ما هذا المكان؟',
    'not_productivity': 'الغرف البشرية ليست أداة إنتاجية.',
    'not_social': 'ليست شبكة اجتماعية.',
    'not_therapy': 'ليست علاجاً.',
    'pause_experiences': 'هي مجموعة من التجارب الصغيرة المصممة لتجعلك تتوقف.',
    'each_room': 'كل غرفة تستغرق من 30 ثانية إلى دقيقتين.',
    'make_think': 'بعض الغرف ستجعلك تفكر.',
    'make_uncomfortable': 'بعضها سيجعلك غير مرتاح.',
    'make_calm': 'بعضها سيجعلك هادئاً.',
    'make_aware': 'كلها ستجعلك واعياً.',
    'no_notifications': 'لا توجد إشعارات هنا.',
    'no_points': 'لا نقاط للجمع.',
    'no_validation': 'لا تحقق اجتماعي.',
    'just_truth': 'فقط أنت، غرفة، والحقيقة.',
    'enter_rooms': 'ادخل الغرف',
    
    // Common
    'loading': 'جاري التحميل...',
    'error': 'خطأ',
    'continue': 'استمر',
    'submit': 'إرسال',
    'cancel': 'إلغاء',
    'close': 'إغلاق',
    'next': 'التالي',
    'previous': 'السابق',
    'start': 'ابدأ',
    'stop': 'توقف',
    'yes': 'نعم',
    'no': 'لا',
    'leave_room': 'غادر الغرفة ←',
    'return': 'عودة ←',
    'return_hallway': 'العودة للممر',
    'escape': 'هروب ←',
    'run': 'اركض ←',
    'leave': 'غادر ←',
    'begin': 'ابدأ',
    'watch': 'شاهد...',
    'correct': 'صحيح',
    'round': 'الجولة',
    'enter_again': 'ادخل مجدداً',
    'steps': 'خطوات',
    'depth': 'العمق',
    
    // Keyboard Shortcuts
    'keyboard_shortcuts': 'اختصارات لوحة المفاتيح',
    'shortcut_hallway': 'الذهاب للممر',
    'shortcut_random': 'غرفة عشوائية',
    'shortcut_back': 'العودة',
    'shortcut_bookmark': 'تبديل الإشارة المرجعية',
    'shortcut_reading': 'تبديل وضع القراءة',
    'shortcut_search': 'التركيز على البحث',
    'shortcut_stats': 'عرض الإحصائيات',
    'shortcut_achievements': 'عرض الإنجازات',
    'shortcut_cat_nav': 'التنقل',
    'shortcut_cat_rooms': 'الغرف',
    'shortcut_cat_access': 'إمكانية الوصول',
    'press_to_close': 'اضغط',
    'to_close': 'للإغلاق',
    'or': 'أو',
    
    // Bookmarks
    'add_bookmark': 'إضافة إشارة مرجعية',
    'remove_bookmark': 'إزالة الإشارة المرجعية',
    'bookmarks': 'الإشارات المرجعية',
    'bookmarked_rooms': 'المفضلة',
    'no_bookmarks': 'لا توجد إشارات مرجعية بعد',
    
    // Reading Mode
    'reading_mode': 'وضع القراءة',
    'reading_mode_on': 'تم تفعيل وضع القراءة',
    'reading_mode_off': 'تم إيقاف وضع القراءة',
    
    // Settings Page
    'settings': 'الإعدادات',
    'settings_subtitle': 'خصص رحلتك',
    'language': 'اللغة',
    'ambient_audio': 'الصوت المحيطي',
    'ambient_audio_desc': 'أصوات خلفية للغرف',
    'reduced_motion': 'تقليل الحركة',
    'reduced_motion_desc': 'تقليل الرسوم المتحركة',
    'data_management': 'إدارة البيانات',
    'export_journey': 'تصدير بيانات الرحلة',
    'clear_all_data': 'مسح جميع البيانات',
    'audio_enabled': 'تم تفعيل الصوت',
    'audio_disabled': 'تم إيقاف الصوت',
    'data_exported': 'تم تصدير البيانات',
    'clear_data_confirm': 'هل أنت متأكد؟ سيتم مسح كل تقدمك.',
    'data_cleared': 'تم مسح البيانات',
    // Theme settings
    'theme': 'السمة',
    'theme_dark': 'داكن',
    'theme_dark_desc': 'ظلام مطلق',
    'theme_bone': 'عظم',
    'theme_bone_desc': 'شحوب الموت',
    'theme_ash': 'رماد',
    'theme_ash_desc': 'ما بعد النار',
    'theme_fog': 'ضباب',
    'theme_fog_desc': 'غموض أثيري',
    'theme_twilight': 'شفق',
    'theme_twilight_desc': 'بين النهار والليل',
    'theme_eclipse': 'كسوف',
    'theme_eclipse_desc': 'الضوء تلتهمه الظلمة',
    'theme_dawn': 'فجر',
    'theme_dawn_desc': 'أول ضوء اليقظة',
    'theme_changed': 'تم تحديث السمة',
    'font_size': 'حجم الخط',
    'font_small': 'صغير',
    'font_medium': 'متوسط',
    'font_large': 'كبير',
    'font_size_changed': 'تم تحديث حجم الخط',
    'high_contrast': 'تباين عالي',
    'high_contrast_desc': 'زيادة تباين النص والحدود',
    'high_contrast_enabled': 'تم تفعيل التباين العالي',
    'high_contrast_disabled': 'تم إيقاف التباين العالي',
    
    // Ambient Music Control
    'ambient_music': 'موسيقى محيطة',
    'ambient_music_desc': 'التحكم في المقاطع الصوتية الخلفية',
    'volume': 'مستوى الصوت',
    'ambient_track': 'مسار صوتي',
    'playing': 'قيد التشغيل',
    'play': 'تشغيل',
    'ambient_default': 'هادئ',
    'ambient_night': 'ليلي',
    'ambient_scary': 'مخيف',
    'ambient_truth': 'حقيقة',
    'ambient_identity': 'تأمل',
    'ambient_mortality': 'زمن',
    'ambient_nothing': 'فراغ',
    'ambient_meta': 'رقمي',
    'ambient_daily': 'دافئ',
    'ambient_seasonal': 'سحري',
    'ambient_premium': 'عميق',
    
    'shared_room': 'الغرفة المشتركة',
    'imagine_room': 'تخيل',
    'imagine_title': 'تخيل نفسك',
    'imagine_subtitle': 'صف كيف ترى نفسك. دع الغرفة تظهر لك ما ينبثق من كلماتك.',
    'imagine_placeholder': 'أرى نفسي كشخص...\n\nصف جوهرك، طبيعتك، كيف تشعر من الداخل.',
    'imagine_hint': 'كن صادقاً. الصورة تعكس كلماتك.',
    'imagine_generate': 'اكتشف نفسك',
    'imagine_generating': 'جاري الإنشاء...',
    'imagine_try_again': 'صف مجدداً',
    'imagine_quote': 'لا نرى أنفسنا بوضوح. نرى ما نعتقده.',
    'room_imagine-yourself': 'تخيل نفسك',
    'sub_imagine-yourself': 'صف نفسك. انظر ما ينبثق.',
    'dream_room': 'الأحلام',
    'future_room': 'المستقبل',
    'dream_title': 'تصور الحلم',
    'dream_subtitle': 'صف حلماً متكرراً. شاهده يتجسد أمامك.',
    'dream_placeholder': 'أحلم دائماً بـ...\n\nصف الصور، المشاعر، العناصر المتكررة.',
    'dream_hint': 'الأحلام تتحدث بالرموز.',
    'dream_generate': 'تصور الحلم',
    'dream_generating': 'أحلم...',
    'dream_try_again': 'احلم مجدداً',
    'dream_quote': 'الأحلام هي محاولة العقل الباطن لإخبارنا بشيء.',
    'dream_empty_error': 'صف حلمك أولاً',
    'dream_short_error': 'أخبرنا المزيد عن حلمك',
    'room_dream-visualization': 'تصور الحلم',
    'future_title': 'الذات المستقبلية',
    'future_subtitle': 'صف من تريد أن تصبح. انظر إلى إمكانياتك.',
    'future_placeholder': 'في المستقبل، أرى نفسي كـ...\n\nصف الشخص الذي تسعى لتصبحه.',
    'future_hint': 'الطموح يشكل الواقع.',
    'future_generate': 'انظر مستقبلك',
    'future_generating': 'أتخيل...',
    'future_try_again': 'تخيل مجدداً',
    'future_quote': 'المستقبل ليس شيئاً ندخله. إنه شيء نصنعه.',
    'future_empty_error': 'صف ذاتك المستقبلية أولاً',
    'future_short_error': 'أخبرنا المزيد عن ذاتك المستقبلية',
    'room_future-self': 'الذات المستقبلية',
    
    // Updates Page
    'updates': 'التحديثات',
    'updates_title': 'ما الجديد',
    'updates_subtitle': 'التغييرات والتحسينات الأخيرة',
    'updates_footer': 'نبني الغرف، تحديث تلو الآخر.',
    'new': 'جديد',
    'subscribe_rss': 'اشترك عبر RSS',
    'latest': 'الأحدث',
    'whats_new_intro': 'آخر التحديثات:',
    'view_all_updates': 'عرض جميع التحديثات',
    'update_updates_page': 'إضافة صفحة التحديثات',
    'update_full_i18n': 'ترجمة كاملة لـ 6 لغات لجميع الغرف',
    'update_hallway_animations': 'رسوم متحركة سلسة للتبديل بين الفئات',
    'update_daily_challenges': 'تحسين عرض التحديات اليومية',
    'update_game_rooms': 'إضافة 15 غرفة ألعاب جديدة',
    'update_experimental_rooms': 'إضافة 14 غرفة تجريبية',
    'update_random_room': 'زر الغرفة العشوائية',
    'update_stats_route': 'إصلاح مسار صفحة الإحصائيات',
    'update_streak_system': 'نظام تتبع الخطوط اليومية',
    'update_weekly_summary': 'ملخصات التقدم الأسبوعية',
    'update_rtl_support': 'دعم اللغات من اليمين لليسار',
    'update_dark_rooms': 'فئة غرف مظلمة/مرعبة جديدة',
    'update_boss_rooms': 'غرف الزعيم مع متطلبات الشظايا',
    'update_audio_system': 'صوت محيطي للغرف',
    'update_constellation_map': 'خريطة النجوم المرئية للرحلة',
    'update_fragment_system': 'جمع شظايا إكمال الغرف',
    'update_mobile_layout': 'تحسينات التوافق مع الجوال',
    'update_multi_language': 'دعم متعدد اللغات (6 لغات)',
    'update_premium_system': 'الغرف المميزة ونظام الفتح',
    'update_room_endings': 'اقتباسات نهاية فريدة للغرف',
    'update_ui_redesign': 'تجديد كامل لتصميم الواجهة',
    'update_theme_effects': 'تأثيرات بصرية خاصة بالسمات',
    'update_void_theme': 'سمة الفراغ الجديدة',
    'update_achievements': 'نظام الإنجازات',
    'update_ai_reflection': 'غرفة التأمل بالذكاء الاصطناعي',
    'update_settings_redesign': 'إعادة تصميم الإعدادات',
    'update_theme_exclusive_rooms': 'غرف حصرية للسمات',
    'update_discovery_localization': 'ترجمة رسائل الاكتشاف',
    'update_site_mood_system': 'نظام مزاج الموقع',
    'update_voice_imperfection': 'غرف صوت غير مثالية',
    'update_mobile_optimization': 'تحسينات الجوال',
    'update_interaction_clues': 'تلميحات التفاعل الدقيقة',
    'update_theme_exclusive_hallway': 'غرف حصرية للسمات في الممر',
    'update_advanced_easter_eggs': 'بيض عيد الفصح المتقدم',
    'update_hidden_discoveries': 'اكتشافات النص المخفي',
    'update_dual_mode_system': 'نظام الوضع المزدوج',
    'update_alternate_mode': 'الوضع البديل - واقع موازي تجريبي',
    'update_hidden_discovery': 'مشغلات الاكتشاف المخفية',
    'update_mode_effects': 'تأثيرات بصرية خاصة بالوضع',
    'update_translation_navigator': 'متصفح الترجمة - لوحة تغطية مرئية',
    'update_full_localization': 'تغطية ترجمة كاملة تقريباً لـ 6 لغات',
    'update_meta_room_translations': 'محتوى الغرف الميتا مترجم',
    'update_animated_timeline': 'جدول زمني متحرك للتحديثات مع رسوم متحركة',
    'update_toast_notifications': 'إشعار منبثق للتحديثات عند الزيارة الأولى',
    'update_rss_feed': 'خلاصة RSS للاشتراك في التحديثات',
    'update_bookmarked_filter': 'علامة تبويب الغرف المفضلة في الممر',
    'update_keyboard_shortcuts': 'اختصارات لوحة المفاتيح (اضغط ؟ للعرض)',
    'update_reading_mode': 'وضع القراءة للتركيز وتحسين الخط',
    'update_accessibility': 'تحسين مؤشرات التركيز والتنقل بلوحة المفاتيح',
    'update_ambient_crossfade': 'انتقالات سلسة بين المقاطع الصوتية المحيطة',
    'update_settings_audio': 'عناصر تحكم الصوت المركزية في صفحة الإعدادات',
    'update_arabic_complete': 'تغطية ترجمة عربية كاملة',
    'update_duplicate_icon_fix': 'إصلاح أيقونات الصوت المكررة في رأس الممر',

    // Easter Egg Discoveries
    'discovery_label': 'اكتشاف',
    'discovery_konami': 'أنت تعرف الطرق القديمة.',
    'discovery_secret_sequence': 'كتبت الحقيقة.',
    'discovery_long_press': 'الصبر يكشف الأسرار.',
    'discovery_triple_click': 'المثابرة تؤتي ثمارها.',
    'discovery_silence': 'السكون يتحدث.',
    
    // Theme-Exclusive Rooms
    'ash_exclusive': 'حصري لسمة الرماد',
    'twilight_exclusive': 'حصري لسمة الشفق',
    'void_exclusive': 'حصري لسمة الفراغ',
    'theme_required': 'يتطلب سمة {theme}',
    'ash_embers_title': 'من الرماد',
    'ash_embers_line1': 'كل شيء يحترق في النهاية.',
    'ash_embers_line2': 'المهم هو ما ينهض بعد ذلك.',
    'ash_embers_line3': 'المس الشاشة. اخلق الجمر.',
    'ash_embers_quote': 'من الرماد، نتذكر ما احترق.',
    'room_ash-embers': 'الرماد والجمر',
    'sub_ash-embers': 'مرئي فقط في سمة الرماد',
    'twilight_stars_title': 'بين النهار والليل',
    'twilight_stars_line1': 'النجوم تتذكر كل من نظر إليها.',
    'twilight_stars_line2': 'اربطها. اصنع كوكبتك الخاصة.',
    'twilight_stars_quote': 'في الشفق، نرى ما يخفيه النهار.',
    'room_twilight-stars': 'نجوم الشفق',
    'sub_twilight-stars': 'مرئي فقط في سمة الشفق',
    'void_abyss_title': 'الهاوية',
    'void_abyss_line1': 'انظر إلى الفراغ. الفراغ ينظر إليك.',
    'void_word_1': 'لا شيء',
    'void_word_2': 'فارغ',
    'void_word_3': 'صمت',
    'void_word_4': 'ذهب',
    'void_word_5': 'وحيد',
    'void_word_6': 'نهاية',
    'void_revealed': 'مكشوف',
    'void_abyss_quote': 'في الظلام المطلق، نرى أنفسنا أخيراً.',
    'room_void-abyss': 'الهاوية',
    'sub_void-abyss': 'مرئي فقط في سمة الفراغ',
    'theme_void': 'الفراغ',
    'theme_void_desc': 'لا شيء يبقى سوى الظلال',
    
    // Game UI
    'game_watching': 'شاهد...',
    'game_repeat': 'كرر',
    'game_echo_fades': 'الصدى يتلاشى...',
    'game_escaped': 'هربت.',
    'game_found_exit': 'وجدت المخرج.',
    'game_looping': 'أنت تدور في حلقة.',
    'game_deeper': 'أعمق...',
    'game_doors_shift': 'الأبواب تتغير.',
    'maze_left': 'يسار',
    'maze_right': 'يمين',
    'maze_forward': 'أمام',
    'maze_back': 'خلف',
    'maze_past': 'الماضي',
    'maze_future': 'المستقبل',
    'maze_here': 'هنا',
    'maze_there': 'هناك',
    'maze_truth': 'الحقيقة',
    'maze_lies': 'الأكاذيب',
    'maze_light': 'الضوء',
    'maze_dark': 'الظلام',
    'maze_memory': 'الذاكرة',
    'maze_dream': 'الحلم',
    'maze_silence': 'الصمت',
    'maze_noise': 'الضجيج',
    
    // Daily Joke Room
    'joke_number': 'نكتة #{number}',
    'joke_today': 'نكتة اليوم',
    'joke_reveal': 'اكشف النهاية',
    'joke_come_back': 'عد غداً لنكتة جديدة.',
    'joke_quote': 'الضحك أقصر مسافة بين شخصين.',
    
    // Daily Quote Room
    'quote_day_number': 'اليوم {day}',
    'quote_come_back': 'عد غداً لاقتباس جديد.',
    
    // Leaderboard
    'leaderboard': 'لوحة المجتمع',
    'leaderboard_subtitle': 'مجهول · جماعي · بدون منافسة',
    'collective_time': 'الوقت الجماعي',
    'achievements_count': 'الإنجازات',
    'most_visited_rooms': 'الغرف الأكثر زيارة',
    'most_time_spent': 'أكثر وقت مستغرق',
    'most_unlocked_achievements': 'أكثر الإنجازات فتحاً',
    'no_achievements_yet': 'لم يتم فتح أي إنجازات بعد.',
    
    // Transfer Data
    'transfer_data': 'نقل البيانات',
    'transfer_data_desc': 'انقل تقدمك إلى جهاز آخر باستخدام رمز لمرة واحدة.',
    'generate_transfer_code': 'إنشاء رمز نقل',
    'enter_transfer_code': 'أدخل الرمز...',
    'transfer_code_expires': 'ينتهي الرمز خلال 24 ساعة ويمكن استخدامه مرة واحدة فقط.',
    'generating': 'جاري الإنشاء...',
    'import': 'استيراد',
    'copied': 'تم النسخ!',
    
    // Waiting Game Room
    'waiting_instruction': 'لا تتحرك. لا تنقر. فقط انتظر.',
    'waiting_slow': 'الإبطاء أصعب مما يبدو.',
    'waiting_unfamiliar': 'هذا السكون يبدو غريباً، أليس كذلك؟',
    'waiting_most': 'معظم الناس يغادرون الآن.',
    'waiting_learning': 'أنت تتعلم شيئاً عن نفسك.',
    'waiting_rewards': 'الصبر له مكافآته الخاصة.',
    'waiting_seconds': 'ثوانٍ من السكون',
    'waiting_waiting': 'في انتظار...',
    'waiting_moved': 'لقد تحركت {count} مرة.',
    'waiting_waited': 'لقد انتظرت. هذا نادر.',
    'waiting_most_cant': 'معظمهم لا يستطيعون الجلوس مع لا شيء. أنت فعلت.',
    'waiting_quote': 'اللحظة الحالية هي اللحظة الوحيدة المتاحة لنا، وهي الباب إلى كل اللحظات.',
    
    'random_room': 'غرفة عشوائية',
    'ai_reflection': 'تأمل الذكاء الاصطناعي',
    'support': 'الدعم',
    'choose_direction': 'اختر اتجاهاً. بعض الأبواب تستجيب.',
    'rooms_explored': 'غرفة مستكشفة',
    'your_journey': 'رحلتك',
    'complete_rooms_fragments': 'أكمل الغرف لجمع الشظايا.',
    'no_rooms_completed': 'لم تكتمل أي غرفة بعد.',
    
    // Stats
    'total_time': 'الوقت الكلي',
    'rooms_visited': 'الغرف المزارة',
    'total_visits': 'إجمالي الزيارات',
    'most_visited': 'الأكثر زيارة',
    'longest_session': 'أطول جلسة',
    'fragments_collected': 'الشظايا المجمعة',
    'completed_rooms': 'الغرف المكتملة',
    'privacy_note': 'جميع البيانات مخزنة محلياً على جهازك',
    
    // Daily Challenges
    'daily_challenges': 'التحديات اليومية',
    'challenges_completed': 'مكتملة',
    'challenges_total': 'الإجمالي',
    'resets_midnight': 'تتجدد عند منتصف الليل',
    
    // Weekly Summary
    'weekly_summary': 'ملخص الأسبوع',
    'week': 'أسبوع',
    'this_week': 'هذا الأسبوع',
    'rooms': 'غرف',
    'days': 'أيام',
    'vs_last_week': 'مقارنة بالأسبوع الماضي',
    'refresh_insight': 'تحديث الرؤية',
    
    // Room Titles (Arabic)
    'room_how-many-times-left': 'كم مرة متبقية',
    'room_life-in-weeks': 'حياتك بالأسابيع',
    'room_delay-button': 'زر التأخير',
    'room_one-honest-sentence': 'جملة صادقة واحدة',
    'room_this-moment': 'هذه اللحظة لن تتكرر',
    'room_reality-receipt': 'إيصال الواقع',
    'room_nothing-happens': 'لا شيء يحدث هنا',
    'room_default-choice': 'خيارك الافتراضي',
    'room_thought-loop': 'حلقة الأفكار',
    'room_expect-vs-happens': 'ما تتوقعه مقابل ما يحدث',
    'room_question-never-answer': 'السؤال الذي لا تجيب عليه',
    'room_not-special': 'أنت لست مميزاً (وهذا مقبول)',
    'room_memory-puzzle': 'لغز الذاكرة',
    'room_shadow-chase': 'مطاردة الظل',
    'room_timing-room': 'غرفة التوقيت',
    'room_pattern-whisper': 'همسات النمط',
    'room_silence-puzzle': 'لغز الصمت',
    'room_decay-game': 'لعبة التلاشي',
    'room_observation-test': 'اختبار الملاحظة',
    'room_mirror-room': 'غرفة المرآة',
    'room_echo-room': 'غرفة الصدى',
    'room_pulse-room': 'غرفة النبض',
    'room_vanishing-room': 'الغرفة المتلاشية',
    'room_letter-room': 'غرفة الرسائل',
    'room_empty-room': 'الغرفة الفارغة',
    'room_reverse-room': 'الغرفة المعكوسة',
    'room_balance-room': 'غرفة التوازن',
    'room_word-drift': 'انجراف الكلمات',
    'room_color-echo': 'صدى الألوان',
    'room_mirror-tap': 'نقرة المرآة',
    'room_maze-of-doors': 'متاهة الأبواب',
    'room_pulse-match': 'مطابقة النبض',
    'room_light-path': 'مسار الضوء',
    'room_sound-match': 'مطابقة الصوت',
    'room_whisper-room': 'غرفة الهمس',
    'room_shadow-room': 'غرفة الظل',
    'room_labyrinth-room': 'غرفة المتاهة',
    'room_color-room': 'غرفة الألوان',
    'room_surrender-room': 'غرفة الاستسلام',
    'room_noise-room': 'غرفة الضجيج',
    'room_observer-room': 'غرفة المراقب',
    
    // Room Subtitles (Arabic)
    'sub_how-many-times-left': 'عد ما تبقى',
    'sub_life-in-weeks': '٤٠٠٠ مربع. هذا كل شيء.',
    'sub_delay-button': 'يصبح أصعب في كل مرة',
    'sub_one-honest-sentence': 'بدون تعديل. بدون حذف.',
    'sub_this-moment': 'أنت هنا. مرة واحدة.',
    'sub_reality-receipt': 'ملخص أيامك',
    'sub_nothing-happens': 'بالضبط كما يقول',
    'sub_memory-puzzle': 'اختبر ما تتذكره',
    'sub_shadow-chase': 'امسك ما يتبعك',
    'sub_timing-room': 'اشعر بالإيقاع',
    'sub_pattern-whisper': 'استمع للنمط',
    'sub_silence-puzzle': 'حل بدون صوت',
    'sub_decay-game': 'كل شيء يتلاشى',
    'sub_observation-test': 'شاهد ما يفوت الآخرين',
    'sub_mirror-room': 'واجه نفسك',
    'sub_echo-room': 'اسمع نفسك تعود',
    'sub_pulse-room': 'اشعر بالنبض',
    'sub_vanishing-room': 'شاهده يختفي',
    'sub_letter-room': 'اكتب لنفسك',
    'sub_empty-room': 'لا شيء هنا',
    'sub_reverse-room': 'كل شيء معكوس',
    'sub_balance-room': 'جد التوازن',
    'sub_word-drift': 'الكلمات تطفو بعيداً',
    'sub_color-echo': 'الألوان تتذكر',
    'sub_mirror-tap': 'انقر الانعكاس',
    'sub_maze-of-doors': 'أي باب يقود للخارج؟',
    'sub_pulse-match': 'طابق نبضات القلب',
    'sub_light-path': 'اتبع الضوء',
    'sub_sound-match': 'طابق النغمات',
    'sub_whisper-room': 'حقائق بالكاد مسموعة',
    'sub_shadow-room': 'رفيقك المظلم',
    'sub_labyrinth-room': 'جد طريقك',
    'sub_color-room': 'تأمل لوني',
    'sub_surrender-room': 'تخلى عن السيطرة',
    'sub_noise-room': 'أفكار ثابتة',
    'sub_observer-room': 'تتم مراقبتك',
    
    // Alternate Mode Exclusive Rooms (Arabic)
    'room_the-other-side': 'الجانب الآخر',
    'room_the-unraveling': 'التفكك',
    'room_the-echo': 'الصدى',
    'sub_the-other-side': 'تحرك ببطء في الظلام',
    'sub_the-unraveling': 'الواقع يتلاشى مع كل كلمة',
    'sub_the-echo': 'ما تقوله لا يغادر أبداً',
    
    // AI Rooms Subtitles (Arabic)
    'room_ai-reflection-room': 'غرفة التأمل الذكي',
    'sub_ai-reflection-room': 'دع الذكاء يتأمل معك',
    'sub_dream-visualization': 'صف حلمك. شاهده يتحقق.',
    'sub_future-self': 'رسالة من المستقبل',
    
    // Meta/Behavioral Rooms (Arabic)
    'room_the-refusal': 'الرفض',
    'room_the-silence-counter': 'عداد الصمت',
    'room_parallel-you': 'أنت الموازي',
    'room_your-shadow': 'ظلك',
    'room_the-exit-reflection': 'تأمل الخروج',
    'room_unvisited-rooms': 'الغرف غير المزارة',
    'sub_the-refusal': 'أحياناً يرفض الموقع',
    'sub_the-silence-counter': 'كم من الوقت يمكنك البقاء صامتاً',
    'sub_parallel-you': 'نسخة أخرى منك',
    'sub_your-shadow': 'ما يتبعك',
    'sub_the-exit-reflection': 'لحظة قبل المغادرة',
    'sub_unvisited-rooms': 'الأبواب التي لم تفتحها',
    
    // Theme Exclusive Section
    'theme_exclusive_rooms': 'غرف حصرية للسمات',
    'theme_exclusive_hint': 'غيّر السمة في الإعدادات لفتح هذه الغرف.',
    'requires_theme': 'يتطلب',
    'switch_to': 'التبديل إلى',
    // Advanced Easter Eggs
    'corner_sequence': 'لقد رسمت الحدود.',
    'morse_sos': 'تم استلام S.O.S.',
    'reverse_scroll': 'العودة للخلف... لكن أسرع.',
    'midnight_visitor': 'ساعة السحر ترحب بك.',
    'shake': 'لقد هززت شيئاً ما.',
    
    // Experience Mode
    'experience_mode': 'وضع التجربة',
    'classic_mode': 'كلاسيكي',
    'classic_mode_desc': 'الواقع المألوف',
    'alternate_mode': '▒ بديل ▒',
    'alternate_mode_desc': '[ لا يجب أن تكون هنا ]',
    'switch_to_alternate': '░▓█ انزل █▓░',
    'switch_to_classic': '← العودة للسطح',
    'enter_parallel_reality': '[ الباب مفتوح ]',
    'return_to_familiar': 'العودة للمألوف',
    'something_shifted': '[ شيء ما تغير ]',
    'reality_feels_different': '[ الواقع يبدو مختلفاً الآن ]',
    'alternate_tagline': 'نفس الغرف. حقائق مختلفة.',
    'alternate_enter': 'انزل',
    'alternate_footer': '[ الأشياء ليست كما تبدو ]',
    'alternate_rooms': '░▓ الواقع الم0ازي ▓░',
    'alternate_rooms_hint': '[ هذه الغرف م0جودة فقط في الم0ازي ]',
    
    // Meta Room Content - The Refusal (Arabic)
    'refusal_line1': 'هذه الغرفة ترفضك.',
    'refusal_line2': 'ليس كل شيء يفتح عندما تريد.',
    'refusal_line3': 'أحياناً الجواب ببساطة: لا.',
    'refusal_line4': 'جرب الزر. لن يساعد.',
    'refusal_button': 'دخول الغرفة',
    
    // Meta Room Content - Parallel You (Arabic)
    'parallel_choice_1': 'اخترت السرعة.',
    'parallel_alternate_1': 'أنت آخر اختار الصبر.',
    'parallel_reflection_1': 'لا يزالون ينتظرون.',
    'parallel_choice_2': 'غادرت مبكراً.',
    'parallel_alternate_2': 'أنت آخر بقي حتى النهاية.',
    'parallel_reflection_2': 'رأوا ما فاتك.',
    'parallel_choice_3': 'تجنبت السؤال الصعب.',
    'parallel_alternate_3': 'أنت آخر أجاب عليه.',
    'parallel_reflection_3': 'يحملون تلك الحقيقة الآن.',
    'parallel_choice_4': 'مررت مسرعاً.',
    'parallel_alternate_4': 'أنت آخر تذوق كل لحظة.',
    'parallel_reflection_4': 'يتذكرون أكثر.',
    'parallel_choice_5': 'واصلت التمرير.',
    'parallel_alternate_5': 'أنت آخر توقف.',
    'parallel_reflection_5': 'وجدوا شيئاً.',
    'parallel_choice_6': 'اخترت الراحة.',
    'parallel_alternate_6': 'أنت آخر اختار عدم الراحة.',
    'parallel_reflection_6': 'نموا منه.',
    'parallel_quote': 'كل لحظة تنقسم إلى مسارات لا نهائية. أنت تمشي واحداً فقط.',
    
    // Meta Room Content - Your Shadow (Arabic)
    'shadow_generated': 'مُولَّد من سلوكك',
    'shadow_intro': 'ظلك...',
    'shadow_follows': 'هذا الظل يتبعك عبر كل غرفة.',
    'shadow_quote': 'لسنا ما نفكر. نحن ما نفعل.',
    'shadow_trait_completion': 'يسعى للإكمال',
    'shadow_trait_surface': 'يفضل السطح',
    'shadow_trait_lingers': 'يبقى في الأماكن',
    'shadow_trait_moves_quickly': 'يتحرك بسرعة',
    'shadow_trait_avoids_depth': 'يتجنب العمق',
    'shadow_trait_stillness': 'يحتضن السكون',
    'shadow_trait_chases': 'يطارد شيئاً بلا اسم',
    'shadow_trait_darkness': 'منجذب للظلام',
    'shadow_trait_emptiness': 'مرتاح مع الفراغ',
    'shadow_trait_undefined': 'غير محدد',
    'shadow_trait_forming': 'لا يزال يتشكل',
    
    // Translation Navigator (Arabic)
    'translation_navigator': 'متصفح الترجمات',
    'translation_coverage': 'تغطية ترجمة الغرف حسب اللغة',
    'keys': 'مفاتيح',
    'room_translations': 'ترجمات الغرف',
    'show_all': 'عرض الكل',
    'show_missing': 'عرض المفقود',
    'all_translated': 'جميع الغرف مترجمة!',
    'missing_translations': 'ترجمات مفقودة',
    
    // Unlock page (Arabic)
    'unlock_title': 'فتح الغرف المميزة',
    'unlock_subtitle': 'الوصول لتجارب أعمق',
    'lifetime': 'وصول مدى الحياة',
    'monthly': 'شهري',
    'one_time': 'دفعة واحدة',
    'per_month': 'شهرياً',
    'unlock_now': 'افتح الآن',
    'subscribe': 'اشترك',
    'already_premium': 'لديك وصول مميز',
    'premium_features': 'المميزات المميزة',
    'redeem_code': 'لديك كود؟',
    'redeem_placeholder': 'أدخل كود الاسترداد',
    'redeem_button': 'استرداد',
    'redeem_success': 'تم استرداد الكود بنجاح!',
    'redeem_error': 'كود غير صالح أو منتهي الصلاحية',
    'unlock_locked_rooms': 'الغرف المقفلة',
    'unlock_not_free': 'بعض الغرف ليست مجانية.',
    'unlock_not_because': 'ليس لأنها أفضل —',
    'unlock_go_further': 'لكن لأنها تذهب أبعد.',
    'unlock_whats_behind': 'ما وراء الباب',
    'unlock_and_more': '...و {count} غرف إضافية',
    'unlock_deepest_truth': 'الحقيقة الأعمق',
    'unlock_premium_boss': 'غرفة الزعيم المميزة',
    'unlock_lifetime_desc': 'مرة واحدة. ملكك للأبد.',
    'unlock_monthly_desc': 'ألغِ في أي وقت.',
    'unlock_no_account': 'لا حساب مطلوب. لا إيميلات. فقط الوصول.',
    'unlock_all_unlocked': 'جميع الغرف مفتوحة',
    'unlock_have_access': 'لديك وصول لكل غرفة في الغرف البشرية.',
    'unlock_go_deeper': 'اذهب أعمق. خذ وقتك.',
    'unlock_enter_hallway': 'ادخل الممر',
    'unlock_welcome_depths': 'مرحباً بك في الأعماق',
    'unlock_verifying': 'جاري التحقق من الشراء...',
    'unlock_canceled': 'تم إلغاء الدفع',
    'unlock_try_again': 'يمكنك المحاولة مجدداً عندما تكون مستعداً.',
    'unlock_error': 'حدث خطأ ما',
    'unlock_try_later': 'يرجى المحاولة لاحقاً.',
    
    // Room additional keys (Arabic)
    'seconds_that_wont_return': 'لن تعود أبداً',
    'moment_id': 'معرف اللحظة',
    
    // This Moment room (Arabic)
    'this_moment_title': 'هذه اللحظة لن تتكرر أبداً',
    'this_moment_been_here': 'كنت في هذه اللحظة لمدة',
    'this_moment_quote': 'هذا التكوين الدقيق من الزمن والمكان وأنت لن يحدث مرة أخرى في تاريخ الكون.',
    
    // Thought Loop room (Arabic)
    'thought_loop_title': 'حلقة الأفكار',
    'thought_loop_prompt': 'اكتب فكرة تعود باستمرار.',
    'thought_loop_placeholder': 'الفكرة التي لا تغادر...',
    'thought_loop_release': 'أطلقها',
    'thought_loop_quote': 'العقل يكرر ما لا يستطيع إطلاقه.',
    
    // Expect vs Happens room (Arabic)
    'expect_title': 'ما تتوقعه مقابل ما يحدث',
    'expect_subtitle': 'حقيقتان. فجوة واحدة.',
    'expect_what_expect': 'ما تتوقع حدوثه',
    'expect_what_happens': 'ما يحدث عادةً',
    'expect_show': 'أرني',
    'expect_distance': 'المسافة بين هاتين الجملتين هي حيث تعيش.',
    'expect_quote': 'الأمل هو الفجوة بين التوقع والواقع.',
    
    // Reality Receipt room (Arabic)
    'receipt_title': 'إيصال الواقع',
    'receipt_sleep': 'ساعات النوم في الليلة؟',
    'receipt_work': 'ساعات العمل يومياً؟',
    'receipt_screen': 'ساعات الشاشة (غير العمل)؟',
    'receipt_commute': 'ساعات التنقل؟',
    'receipt_next': 'التالي',
    'receipt_generate': 'إنشاء الإيصال',
    'receipt_header': 'إيصال الواقع',
    'receipt_sleep_label': 'النوم',
    'receipt_work_label': 'العمل',
    'receipt_screen_label': 'الشاشة (ترفيه)',
    'receipt_commute_label': 'التنقل',
    'receipt_total_used': 'المجموع المستخدم',
    'receipt_free_time': 'الوقت الحر',
    'receipt_yearly': 'التوقع السنوي:',
    'receipt_yearly_work': 'ساعات عمل',
    'receipt_yearly_screen': 'ساعات على الشاشات',
    'receipt_yearly_sleep': 'ساعات نوم',
    'receipt_yearly_free': 'ساعات حرة',
    'receipt_recalculate': 'إعادة الحساب',
    'receipt_quote': 'هكذا تقضي حياتك.',
    
    // Question Never Answer room (Arabic)
    'question_title': 'السؤال الذي لا تجيب عليه أبداً',
    'question_seconds': 'ثانية',
    'question_most_left': 'معظم الناس غادروا بالفعل.',
    'question_quote': 'السؤال يبقى. أنت بقيت.',
    'question_1': 'ماذا ستفعل لو علمت أنك لن تفشل؟',
    'question_2': 'من ستكون لو لم يكن أحد يراقب؟',
    'question_3': 'ما الذي تتظاهر بأنك لا تعرفه؟',
    'question_4': 'ماذا سيفكر فيك طفلك ذو العشر سنوات؟',
    'question_5': 'ما الذي تخاف من أن تريده؟',
    'question_6': 'إذا مت اليوم، ما الذي سيبقى ناقصاً؟',
    'question_7': 'ما الكذبة التي تخبرها لنفسك أكثر؟',
    'question_8': 'ماذا ستفعل لو لم يكن المال حقيقياً؟',
    'question_9': 'من لم تسامحه بعد؟',
    'question_10': 'ما الذي تهرب منه؟',
    
    // Not Special room (Arabic)
    'not_special_right_now': 'الآن، تقريباً',
    'not_special_share_fear': 'شخص يشاركك خوفك من',
    'not_special_title': 'أنت لست مميزاً',
    'not_special_okay': 'وهذا مقبول.',
    'not_special_quote': 'خوفك ليس فريداً. شجاعتك أيضاً ليست كذلك.',
    'fear_forgotten': 'أن يُنسى',
    'fear_dying_alone': 'الموت وحيداً',
    'fear_not_enough': 'ألا يكون جيداً بما فيه الكفاية',
    'fear_wasting_life': 'إضاعة حياتهم',
    'fear_being_seen': 'أن يُرى حقاً',
    'fear_failure': 'الفشل',
    'fear_rejection': 'الرفض',
    'fear_missing_out': 'تفويت الفرص',
    'fear_ordinary': 'أن يكون عادياً',
    'fear_time': 'نفاد الوقت',
    
    // Scary Room Content (Arabic)
    'watching_alone': 'أنت وحدك هنا.',
    'watching_completely': 'وحدك تماماً.',
    'watching_or_are_you': 'أم هل أنت؟',
    'watching_been_here': 'كانوا هنا طوال الوقت.',
    'watching_always': 'هم دائماً يراقبون.',
    'watching_quote': 'لست وحدك أبداً مع أفكارك.',
    'countdown_title': 'العد التنازلي',
    'countdown_something': 'شيء ما يحدث عندما يصل للصفر.',
    'countdown_sure': 'هل أنت متأكد أنك تريد البقاء؟',
    'countdown_almost': 'إنه يقترب.',
    'countdown_end_1': 'لم يحدث شيء. هل كنت خائفاً؟',
    'countdown_end_2': 'الترقب كان التجربة.',
    'countdown_end_3': 'ماذا كنت تتوقع؟',
    'countdown_end_4': 'الخوف يعيش في الانتظار.',
    'countdown_end_5': 'العد التنازلي لم يكن أبداً عن النهاية.',
    'countdown_quote': 'أخوف لحظة هي دائماً قبل أن تبدأ.',
    'dont_look_title': 'لا تنظر بعيداً',
    'dont_look_away_msg': 'لا تنظر بعيداً.',
    'dont_look_looked': 'نظرت بعيداً.',
    'dont_look_seconds': '{count} ثانية من الانتباه',
    'dont_look_times': 'نظرت بعيداً {count} مرة',
    'dont_look_quote': 'الحضور هو أصعب شيء للعطاء.',
    'whisper_hello': 'مرحباً.',
    'whisper_came': 'لقد جئت.',
    'whisper_expecting': 'كنا ننتظرك.',
    'whisper_remember': 'هل تتذكر هذا المكان؟',
    'whisper_been_before': 'كنت هنا من قبل.',
    'whisper_know': 'نحن نعلم.',
    'whisper_remember_name': 'هل تتذكر اسمك؟',
    'whisper_quote': 'بعض الغرف تتذكرك أفضل مما تتذكر نفسك.',
    
    // Additional Room Titles (Arabic)
    'room_void-room': 'الفراغ',
    'room_pattern-room': 'أنماطك',
    'room_time-capsule': 'كبسولة الزمن',
    'room_the-revisit': 'إعادة الزيارة',
    'room_contradiction-room': 'غرفة التناقض',
    'room_export-journey': 'تصدير رحلتك',
    'room_anonymous-echoes': 'أصداء مجهولة',
    'room_the-waiting-room': 'غرفة الانتظار',
    'room_eclipse-shadows': 'ظلال الكسوف',
    'room_dawn-awakening': 'يقظة الفجر',
    'room_shortcut-always-take': 'الاختصار الذي تتخذه دائماً',
    'room_blurred-self': 'الذات الضبابية',
    'room_image-zooming': 'الصورة التي تكبرها',
    'room_comfort-color': 'لون الراحة',
    'room_line-you-crossed': 'الخط الذي تجاوزته',
    'room_read-aloud': 'اقرأ هذا بصوت عالٍ',
    'room_voice-without-meaning': 'صوتك بدون معنى',
    'room_sentence-you-avoid': 'الجملة التي تتجنبها',
    'room_if-nothing-changes': 'إذا لم يتغير شيء',
    'room_real-priorities': 'أولوياتك الحقيقية',
    'room_last-conversation': 'المحادثة الأخيرة',
    'room_who-without-job': 'من أنت بدون وظيفتك؟',
    'room_comfort-zone-scanner': 'ماسح منطقة الراحة',
    'room_say-it-once': 'قلها مرة واحدة',
    'room_listen-to-yourself': 'استمع لنفسك',
    'room_life-as-shapes': 'حياتك كأشكال',
    'room_face-without-features': 'وجه بدون ملامح',
    'room_what-keeps-awake': 'ما يبقيك مستيقظاً',
    'room_you-at-317am': 'أنت في 3:17 صباحاً',
    'room_thought-that-returns': 'الفكرة التي تعود',
    'room_room-you-visit-most': 'الغرفة التي تزورها أكثر',
    'room_room-you-avoid': 'الغرفة التي تتجنبها',
    'room_left-too-early': 'غادرت مبكراً جداً',
    'room_daily-quote': 'اقتباس اليوم',
    'room_daily-joke': 'نكتة اليوم',
    'room_theyre-watching': 'هم يراقبون',
    'room_the-last-visitor': 'الزائر الأخير',
    'room_dont-look-away': 'لا تنظر بعيداً',
    'room_the-whisper': 'الهمسة',
    'room_you-forgot-something': 'نسيت شيئاً',
    'room_the-mirror-lies': 'المرآة تكذب',
    'room_something-follows': 'شيء يتبعك',
    'room_the-countdown': 'العد التنازلي',
    'room_eyes-in-the-dark': 'عيون في الظلام',
    'room_the-voice': 'الصوت',
    'room_it-knows-your-name': 'يعرف اسمك',
    'room_the-door': 'الباب',
    'room_boss-room-free': 'مجموع حقائقك',
    'room_boss-room-horror': 'قلب الخوف',
    'room_boss-room-premium': 'الحقيقة الأعمق',
    'room_mouse-tracker': 'متتبع الفأرة',
    'room_scroll-confession': 'اعتراف التمرير',
    'room_waiting-game': 'لعبة الانتظار',
    'room_day-in-pixels': 'يومك بالبكسل',
    'room_memory-maze': 'متاهة الذاكرة',
    'room_decision-shadow': 'ظل القرار',
    'room_anonymous-mirrors': 'مرايا مجهولة',
    'room_collective-room': 'الغرفة الجماعية',
    'room_secret-room': 'الغرفة السرية',
    'room_glitch-room': 'غرفة غير مستقرة',
    'room_full-moon-room': 'غرفة البدر',
    'room_weekend-room': 'غرفة نهاية الأسبوع',
    'room_317am-room': 'غرفة 3:17 صباحاً',
    'room_idle-room': 'الغرفة الساكنة',
    'room_invite-friend-room': 'الغرفة المشتركة',
    'room_halloween-room': 'غرفة الحجاب',
    'room_winter-solstice-room': 'أطول ليلة',
    'room_new-year-room': 'رسالة للذات المستقبلية',
    
    // Additional Room Subtitles (Arabic)
    'sub_default-choice': 'الأمان أم المخاطرة. كل مرة.',
    'sub_thought-loop': 'شاهد عقلك يكرر نفسه',
    'sub_expect-vs-happens': 'الفجوة بين الأمل والواقع',
    'sub_question-never-answer': 'معظم المستخدمين يغادرون',
    'sub_not-special': 'لا راحة. فقط الحقيقة.',
    'sub_shortcut-always-take': 'الأنماط تكشف نفسها',
    'sub_blurred-self': 'الذاكرة تعمل هكذا',
    'sub_image-zooming': 'البحث عن إجابات في لا شيء',
    'sub_comfort-color': 'كم ستبقى؟',
    'sub_line-you-crossed': 'نرى أين ترددت',
    'sub_read-aloud': 'كلمات يجب أن تنطقها',
    'sub_voice-without-meaning': 'صوت بدون كلمات',
    'sub_sentence-you-avoid': 'لا يمكنك المغادرة حتى تكتبها',
    'sub_if-nothing-changes': 'إسقاط لسنواتك الخمس القادمة',
    'sub_real-priorities': 'بناءً على الوقت، ليس الكلمات',
    'sub_last-conversation': 'اكتب ما لن ترسله أبداً',
    'sub_who-without-job': 'أعد كتابة نفسك',
    'sub_comfort-zone-scanner': 'نرى ترددك',
    'sub_say-it-once': 'لا إعادة. لا إعادة تنفيذ.',
    'sub_listen-to-yourself': 'صوتك، بطيء',
    'sub_life-as-shapes': 'تجريدي. بدون تسميات. ملكك.',
    'sub_face-without-features': 'أزل حتى لا يبقى شيء',
    'sub_what-keeps-awake': 'متاح بعد 10 مساءً',
    'sub_you-at-317am': 'محدد بالوقت. شخصي.',
    'sub_thought-that-returns': 'تعود كل زيارة',
    'sub_room-you-visit-most': 'أين تذهب أكثر؟',
    'sub_room-you-avoid': 'ماذا تتجنب؟',
    'sub_left-too-early': 'كان هناك المزيد',
    'sub_secret-room': 'لقد وجدتها',
    'sub_idle-room': 'وجدت من خلال الصبر',
    'sub_glitch-room': 'شيء ما خطأ',
    'sub_void-room': 'جرح في الكود',
    'sub_pattern-room': 'ما يراه الموقع فيك',
    'sub_collective-room': 'الجميع هنا. لا أحد يبقى.',
    'sub_theyre-watching': 'أنت لست وحدك',
    'sub_the-last-visitor': 'شخص كان هنا قبلك',
    'sub_dont-look-away': 'يعرف متى تغادر',
    'sub_the-whisper': 'استمع جيداً',
    'sub_you-forgot-something': 'ما هو؟',
    'sub_the-mirror-lies': 'اكسرها لترى الحقيقة',
    'sub_something-follows': 'استمر بالركض',
    'sub_the-countdown': 'شيء يحدث عند الصفر',
    'sub_eyes-in-the-dark': 'يتكاثرون',
    'sub_the-voice': 'شخص محبوس هنا',
    'sub_it-knows-your-name': 'لم يكن عليك المجيء',
    'sub_the-door': 'شخص يطرق',
    'sub_boss-room-free': 'الغرفة الحرة النهائية',
    'sub_boss-room-horror': 'واجه الظلام النهائي',
    'sub_boss-room-premium': 'الغرفة العميقة النهائية',
    'sub_halloween-room': '31 أكتوبر - الحجاب رقيق',
    'sub_winter-solstice-room': '21 ديسمبر - أطلق واحتفظ',
    'sub_new-year-room': '31 ديسمبر - اكتب لمن ستصبح',
    'sub_weekend-room': 'الراحة وجهة',
    'sub_full-moon-room': 'يفتح فقط في ذروة القمر',
    'sub_317am-room': 'محدد بالوقت. شخصي.',
    'sub_time-capsule': 'اكتب لذاتك المستقبلية',
    'sub_the-revisit': 'انظر كيف تغيرت إجاباتك',
    'sub_contradiction-room': 'حقائقك تختلف',
    'sub_export-journey': 'حمّل مسارك',
    'sub_anonymous-echoes': 'شظايا من غرباء',
    'sub_the-waiting-room': 'افتح بالحضور',
    'sub_invite-friend-room': 'بعض الرحلات يجب أن تُشهد',
    'sub_daily-quote': 'حكمة تتغير يومياً',
    'sub_daily-joke': 'فكاهة سوداء، تتحدث يومياً',
    'sub_mouse-tracker': 'نرى كيف تتحرك',
    'sub_scroll-confession': 'ما مدى سرعة تمريرك؟',
    'sub_waiting-game': 'هل تستطيع ألا تفعل شيئاً؟',
    'sub_day-in-pixels': 'الوقت يصبح شكلاً',
    'sub_memory-maze': 'النمط يتغير',
    'sub_decision-shadow': 'اختياراتك تكشف الأنماط',
    'sub_anonymous-mirrors': 'أفكار من غرباء',
    'sub_regret-machine': 'استكشف ما كان يمكن أن يكون',
    'sub_memory-reconstruction': 'أعد بناء ذاكرة تتلاشى',
    'sub_whisper-wall': 'حقائق مجهولة تطفو في الظلام',
    'sub_collective-memory': 'شظايا مشتركة مما نتذكر',
    'sub_shared-moments': 'كيف حالك، بصدق؟',
    'sub_eclipse-shadows': 'تحدث في الظلام التام',
    'sub_dawn-awakening': 'استقبل أول ضوء',
    'sub_the-archive': 'تاريخ رحلتك الكامل',
    'sub_mirror-of-all-selves': 'الذكاء الاصطناعي يكشف صورتك السلوكية',
    'sub_the-ending': 'وداع استحققته',
    
    // Interaction hints (Arabic)
    'hint_click_enter': 'انقر للدخول',
    'hint_take_time': 'خذ وقتك',
    'hint_each_room': 'كل غرفة تحمل سؤالاً',
    'hint_no_rush': 'لا استعجال هنا',
    
    // Behavior reflections (Arabic)
    'behavior_stillness': 'توقفت.',
    'behavior_patience': 'تنتظر أطول من معظمهم.',
    'behavior_silence': 'الصمت يتحدث.',
    
    // Achievements (Arabic)
    'achievements': 'الإنجازات',
    'achievements_subtitle': 'معالم في رحلتك',
    'points': 'نقاط',
    'unlocked': 'مفتوح',
    'exploration': 'الاستكشاف',
    'consistency': 'الاستمرارية',
    'discovery': 'الاكتشاف',
    'special': 'خاص',
    'all': 'الكل',
    
    // Room Content - Common (Arabic)
    'how_old_are_you': 'كم عمرك؟',
    'based_on_life_expectancy': 'بناءً على متوسط العمر المتوقع، لديك تقريباً:',
    'summers': 'صيف',
    'meals': 'وجبات',
    'birthdays': 'أعياد ميلاد',
    'full_moons': 'بدر',
    'sunrises': 'شروق شمس',
    'books_might_finish': 'كتب قد تنهيها',
    'quote_not_warning': 'هذا ليس تحذيراً. مجرد عد.',
    'each_square_is_week': 'كل مربع هو أسبوع واحد. هناك حوالي 4000.',
    'your_age': 'عمرك',
    'see_your_life': 'انظر حياتك',
    'weeks_lived': '{count} أسبوع عاشها',
    'weeks_remaining': '{count} متبقية (إذا كنت متوسطاً)',
    'quote_week_never_back': 'كل مربع ممتلئ هو أسبوع لن يعود.',
    'write_one_true_thing': 'اكتب شيئاً حقيقياً عن نفسك.',
    'cannot_edit_delete': 'لا يمكنك تعديله. لا يمكنك حذفه.',
    'i_placeholder': 'أنا...',
    'submit_forever': 'أرسل للأبد',
    'previous_truths': 'حقائقك السابقة',
    'write_another': 'اكتب أخرى',
    'quote_cannot_unsaid': 'هذا لا يمكن أن يُقال مرة أخرى.',
    'content_nothing_waiting': 'كنت هنا لمدة {seconds} ثانية.',
    'content_nothing_still': 'لا شيء لا يزال يحدث.',
    'content_nothing_stayed': 'ومع ذلك، بقيت.',
    'content_thought_visit': 'زيارة #{count}',
    'content_thought_always': 'هذه الفكرة ستكون هنا كلما عدت.',
    'content_thought_never_leave': 'بعض الأفكار لا تغادر أبداً. فقط تنتظر.',
    'click_it_harder': 'انقر عليه. سيصبح أصعب.',
    'click': 'انقر',
    'times_clicked': 'مرات النقر',
    'quote_things_chase': 'الأشياء التي نطاردها تصغر كلما طاردناها.',
    'scenario_stranger': 'غريب يطلب المساعدة',
    'scenario_help': 'ساعده',
    'scenario_keep_walking': 'واصل المشي',
    'scenario_free_hour': 'لديك ساعة فراغ',
    'scenario_start_new': 'ابدأ شيئاً جديداً',
    'scenario_scroll': 'تصفح هاتفك',
    'scenario_disagrees': 'شخص يختلف معك',
    'scenario_engage': 'شارك',
    'scenario_avoid': 'تجنب الصراع',
    'scenario_risky': 'فرصة تبدو محفوفة بالمخاطر',
    'scenario_take_it': 'خذها',
    'scenario_wait_safer': 'انتظر واحدة أكثر أماناً',
    'scenario_speak_up': 'يمكنك التحدث الآن',
    'scenario_say_it': 'قلها',
    'scenario_stay_quiet': 'ابق صامتاً',
    'choose_safety': 'من الوقت، تختار الأمان',
    'quote_become_choose': 'نصبح ما نختاره مراراً.',
    
    // AI Room Content (Arabic)
    'imagine_result_alt': 'صورتك الذاتية',
    'imagine_empty_error': 'اكتب شيئاً عن نفسك أولاً',
    'imagine_short_error': 'أخبرنا المزيد عن نفسك',
    'rate_limit_error': 'طلبات كثيرة جداً. انتظر لحظة.',
    'credits_error': 'الخدمة غير متاحة مؤقتاً',
    'generation_error': 'لم نتمكن من إنشاء الصورة. حاول مجدداً.',
    'dream_result_alt': 'تصور حلمك',
    'future_result_alt': 'ذاتك المستقبلية',
    
    // Audio descriptions (Arabic)
    'audio_enabled_desc': 'ستُشغل الأصوات المحيطية في الغرف',
    'audio_disabled_desc': 'ستكون الغرف صامتة',
    'data_exported_desc': 'تم حفظ رحلتك',
    'data_cleared_desc': 'تم إعادة تعيين رحلتك',
    
    // New Phase 1-3 Rooms (Arabic)
    'room_letters-never-sent': 'رسائل لم تُرسل',
    'sub_letters-never-sent': 'اكتب لشخص لن يقرأها أبداً',
    'room_breath-sync': 'تناغم النَفَس',
    'sub_breath-sync': 'تنفس مع الغرفة',
    'room_entropy-garden': 'حديقة الفوضى',
    'sub_entropy-garden': 'النمو والتلاشي متشابكان',
    'room_memory-palace': 'قصر الذاكرة',
    'sub_memory-palace': 'ابنِ ملاذك الذهني',
    'room_morning-ritual': 'طقس الصباح',
    'sub_morning-ritual': 'ابدأ يومك بنية',
    'room_evening-ritual': 'طقس المساء',
    'sub_evening-ritual': 'أغلق اليوم بتأمل',
    'room_weekly-ritual': 'الطقس الأسبوعي',
    'sub_weekly-ritual': 'أيام الأحد للنظر للوراء',
    
    // Letters Never Sent Room (Arabic)
    'letters_title': 'رسائل لم تُرسل',
    'letters_subtitle': 'اكتب لشخص لن يقرأها أبداً. سيرد الذكاء الاصطناعي كما قد يفعلون.',
    'letters_select_recipient': 'لمن تكتب؟',
    'letters_past_self': 'ذاتي السابقة',
    'letters_future_self': 'ذاتي المستقبلية',
    'letters_someone_lost': 'شخص فقدته',
    'letters_someone_wronged': 'شخص أخطأت بحقه',
    'letters_placeholder': 'عزيزي...',
    'letters_send': 'أرسل إلى الفراغ',
    'letters_sending': 'جاري الإرسال...',
    'letters_response': 'يظهر رد...',
    'letters_write_another': 'اكتب رسالة أخرى',
    'letters_quote': 'بعض الكلمات مقدر لها أن تُكتب لكن لا تُرسل.',
    
    // Breath Sync Room (Arabic)
    'breath_title': 'تناغم النَفَس',
    'breath_subtitle': 'طابق تنفسك مع إيقاع الغرفة.',
    'breath_inhale': 'شهيق',
    'breath_hold': 'احبس',
    'breath_exhale': 'زفير',
    'breath_cycles': 'دورات مكتملة',
    'breath_quote': 'النَفَس هو الجسر بين الجسد والعقل.',
    
    // Entropy Garden Room (Arabic)
    'entropy_title': 'حديقة الفوضى',
    'entropy_subtitle': 'نباتاتك تنمو مع كل زيارة، لكنها تذبل عندما تبتعد.',
    'entropy_plant': 'ازرع بذرة',
    'entropy_water': 'اسقِ',
    'entropy_days_alive': 'أيام حية',
    'entropy_health': 'الصحة',
    'entropy_empty': 'حديقتك فارغة. ازرع شيئاً.',
    'entropy_quote': 'كل شيء ينمو. كل شيء يذبل. هذا هو الطريق.',
    
    // Memory Palace Room (Arabic)
    'memory_palace_title': 'قصر الذاكرة',
    'memory_palace_subtitle': 'ابنِ ملاذاً ذهنياً. ضع أشياء في غرف تصنعها.',
    'memory_palace_add_room': 'أضف غرفة',
    'memory_palace_add_object': 'أضف شيء',
    'memory_palace_room_name': 'اسم الغرفة...',
    'memory_palace_object_name': 'اسم الشيء...',
    'memory_palace_empty': 'قصرك فارغ. أنشئ غرفتك الأولى.',
    'memory_palace_quote': 'نبني عقولنا غرفة تلو الأخرى.',
    
    // Ritual Rooms (Arabic)
    'ritual_locked': 'هذا الطقس غير متاح بعد',
    'morning_ritual_title': 'طقس الصباح',
    'morning_ritual_intro': 'لحظة لتحديد نوايا لليوم القادم.',
    'morning_ritual_available': 'متاح من 6 ص - 9 ص',
    'evening_ritual_title': 'طقس المساء',
    'evening_ritual_intro': 'لحظة للتأمل في اليوم الذي مضى.',
    'evening_ritual_available': 'متاح من 8 م - 11 م',
    'weekly_ritual_title': 'الطقس الأسبوعي',
    'weekly_ritual_intro': 'لحظة للتأمل في الأسبوع الماضي وتحديد نوايا للقادم.',
    'weekly_locked': 'الطقس الأسبوعي ينتظرك',
    'weekly_time': 'عد يوم الأحد',
    'today_is': 'اليوم هو',
    'your_week': 'أسبوعك',
    'days_active': 'أيام نشطة',
    'weekly_summary_note': 'الآن، دعنا نتأمل بعمق أكبر.',
    'weekly_highlight': 'ما كان أبرز حدث في أسبوعك؟',
    'weekly_challenge': 'ما الذي تحداك أكثر هذا الأسبوع؟',
    'weekly_growth': 'كيف نموت هذا الأسبوع؟',
    'weekly_intention': 'ما النية التي تحددها للأسبوع القادم؟',
    'weekly_placeholder': 'خذ وقتك...',
    'reflect': 'تأمل',
    'reflecting': 'جاري التأمل...',
    'complete': 'اكتمل',
    'weekly_quote': 'ينتهي أسبوع. يبدأ آخر. تحمل ما يهم.',
    
    // Personal Timeline (Arabic)
    'personal_timeline': 'الجدول الزمني الشخصي',
    'timeline_subtitle': 'رحلتك عبر الغرف',
    'timeline_empty': 'جدولك الزمني فارغ. ابدأ الاستكشاف.',
    'timeline_today': 'اليوم',
    'timeline_yesterday': 'أمس',
    'timeline_this_week': 'هذا الأسبوع',
    'timeline_earlier': 'سابقاً',
    
    // Weather System (Arabic)
    'weather_clear': 'صافٍ',
    'weather_rain': 'مطر',
    'weather_fog': 'ضباب',
    'weather_snow': 'ثلج',
    
    // Notifications (Arabic)
    'notifications': 'الإشعارات',
    'notifications_desc': 'احصل على إشعارات للمحتوى المقفل بالوقت',
    'notify_time_locked': 'الغرف المقفلة بالوقت',
    'notify_full_moon': 'ليالي البدر',
    'notify_daily_reset': 'إعادة تعيين التحديات اليومية',
    'notify_time_capsule': 'فتح كبسولات الزمن',
    'notifications_enabled': 'تم تفعيل الإشعارات',
    'notifications_disabled': 'تم إيقاف الإشعارات',
    
    // Community Submissions (Arabic)
    'community_submissions': 'مساهمات المجتمع',
    'community_subtitle': 'قدم أفكار غرفك',
    'community_room_title': 'عنوان الغرفة',
    'community_room_description': 'وصف الغرفة',
    'community_room_category': 'الفئة',
    'community_submit': 'قدم الفكرة',
    'community_submitted': 'شكراً لمساهمتك!',
    'community_your_submissions': 'مساهماتك',
    'community_empty': 'لا توجد مساهمات بعد. كن الأول!',
    
    // Proactive Guide (Arabic)
    'guide_hesitation': 'تأخذ وقتك؟ لا بأس. هل تريد اقتراحاً؟',
    'guide_long_absence': 'أهلاً بعودتك. مضى وقت طويل.',
    'guide_milestone': 'لقد قطعت شوطاً طويلاً. هل تريد التأمل في رحلتك؟',
    'guide_dismiss': 'تجاهل',
    'guide_accept': 'أرني',
    
    // Voice Mode (Arabic)
    'voice_mode': 'وضع الصوت',
    'voice_mode_desc': 'دع المرشد يتحدث إليك',
    'voice_mode_enabled': 'تم تفعيل وضع الصوت',
    'voice_mode_disabled': 'تم إيقاف وضع الصوت',
    
    // Offline Support (Arabic)
    'offline_available': 'متاح بدون اتصال',
    'offline_cached': 'هذه الغرفة محفوظة للعرض بدون اتصال',
    
    // Journey Export (Arabic)
    'journey_export': 'تصدير الرحلة',
    'journey_import': 'استيراد الرحلة',
    'journey_export_json': 'تصدير كـ JSON',
    'journey_import_json': 'استيراد من JSON',
    'journey_exported': 'تم تصدير الرحلة بنجاح',
    'journey_imported': 'تم استيراد الرحلة بنجاح',
    
    // Phase 4 - Seasonal Rooms (Arabic)
    'room_spring-equinox-room': 'الاعتدال الربيعي',
    'sub_spring-equinox-room': 'أطلق وتجدد',
    'room_summer-solstice-room': 'الانقلاب الصيفي',
    'sub_summer-solstice-room': 'أطول يوم',
    'room_autumn-equinox-room': 'الاعتدال الخريفي',
    'sub_autumn-equinox-room': 'احصد وتقبل',
    'room_valentine-room': 'غرفة حب الذات',
    'sub_valentine-room': 'الحب الذي تستحقه',
    
    // Spring Equinox Room (Arabic)
    'spring_date': '20 مارس',
    'spring_title': 'الاعتدال الربيعي',
    'spring_message_1': 'الليل والنهار متساويان.',
    'spring_message_2': 'لحظة توازن مثالية.',
    'spring_message_3': 'ماذا ستطلق لتفسح المجال للنمو؟',
    'spring_letting_go': 'أنا أتخلى عن:',
    'spring_letting_go_placeholder': 'ما لم يعد يخدمك...',
    'spring_planting': 'أنا أزرع:',
    'spring_planting_placeholder': 'ما تريد أن ينمو...',
    'spring_bloom': 'دعها تزهر',
    'spring_released': 'تم إطلاقه:',
    'spring_planted': 'تم زرعه:',
    'spring_closing': 'من هذه اللحظة، تبدأ حياة جديدة.',
    'spring_quote': 'الطبيعة لا تتعجل، ومع ذلك كل شيء يتحقق.',
    
    // Summer Solstice Room (Arabic)
    'summer_date': '21 يونيو',
    'summer_title': 'أطول يوم',
    'summer_message_1': 'هذا أطول يوم في السنة.',
    'summer_message_2': 'الضوء يصل إلى ذروته.',
    'summer_message_3': 'ماذا ستفعل بمزيد من الوقت؟',
    'summer_message_4': 'صف يومك الأكثر امتلاءً وحيوية.',
    'summer_placeholder': 'في يومي الأكثر امتلاءً، سأ...',
    'summer_submit': 'احتضن الضوء',
    'summer_closing': 'الشمس سمعتك. الآن تبدأ بالغروب.',
    'summer_quote': 'في ذروة الضوء، نكتشف ما نريد حقاً إضاءته.',
    
    // Autumn Equinox Room (Arabic)
    'autumn_date': '22 سبتمبر',
    'autumn_title': 'الاعتدال الخريفي',
    'autumn_message_1': 'يعود التوازن.',
    'autumn_message_2': 'الحصاد جاهز.',
    'autumn_message_3': 'ماذا زرعت؟ ما الذي يمكنك تقبله؟',
    'autumn_harvesting': 'أنا أحصد:',
    'autumn_harvesting_placeholder': 'ما زرعته هذا العام...',
    'autumn_accepting': 'أنا أتقبل:',
    'autumn_accepting_placeholder': 'ما لا يمكنك تغييره...',
    'autumn_release': 'دع الأوراق تسقط',
    'autumn_harvested': 'تم حصاده:',
    'autumn_accepted': 'تم قبوله:',
    'autumn_closing': 'كل شيء يسقط في وقته.',
    'autumn_quote': 'الخريف يرينا كم هو جميل أن نترك الأشياء تذهب.',
    
    // Valentine's Day Room (Arabic)
    'valentine_date': '14 فبراير',
    'valentine_title': 'الحب الذي تستحقه',
    'valentine_message_1': 'اليوم ليس عن الآخرين.',
    'valentine_message_2': 'إنه عن الشخص الذي يكون معك دائماً.',
    'valentine_message_3': 'الشخص الذي يسمع كل فكرة.',
    'valentine_message_4': 'اكتب لنفسك رسالة حب.',
    'valentine_prompt': 'رسالة لنفسك:',
    'valentine_placeholder': 'عزيزتي أنا، أريدك أن تعرفي...',
    'valentine_submit': 'أرسل بحب',
    'valentine_closing': 'أنت تستحق الحب الذي تمنحه للآخرين.',
    'valentine_quote': 'أنت نفسك تستحق حبك وعاطفتك.',
    
    // Update changelog
    'update_seasonal_rooms': 'أربع غرف موسمية جديدة: الاعتدال الربيعي، الانقلاب الصيفي، الاعتدال الخريفي، وعيد الحب',
    'update_fragment_trading': 'نظام تداول الشظايا - ضحِّ بالشظايا لفتح الغرف المقفلة زمنياً أو معاينة غرف الرعب أو تلقي رسائل الذكاء الاصطناعي',
    'update_new_themes': 'سمتان جديدتان: الكسوف (ظلام تام) والفجر (تدرجات دافئة، 5-7 صباحاً فقط)',
     'update_regret_machine': 'آلة الندم - سرديات جدول زمني بديل مدعومة بالذكاء الاصطناعي',
     'update_memory_reconstruction': 'إعادة بناء الذاكرة - الذكاء الاصطناعي يملأ التفاصيل الحسية للذكريات الباهتة',
     
     // Phase 7 - Regret Machine Room
     'regret_machine_title': 'آلة الندم',
     'regret_machine_subtitle': 'صِف قراراً تتمنى لو اتخذته بشكل مختلف. ستريك الآلة ما كان يمكن أن يحدث.',
     'regret_placeholder': 'أنا نادم على...\n\nصِف خياراً اتخذته وتتمنى لو كان مختلفاً. ماذا كنت ستفعل بدلاً من ذلك؟',
     'regret_hint': 'هذا ليس عن اللوم. إنه عن الفهم.',
     'regret_explore': 'استكشف الجدول الزمني',
     'regret_generating': 'إعادة بناء واقع بديل...',
     'regret_alternate_timeline': 'الجدول الزمني البديل',
     'regret_explore_another': 'استكشف آخر',
     'regret_error_short': 'يرجى وصف ندمك بمزيد من التفصيل',
     'room_regret-machine': 'آلة الندم',
     
     // Phase 7 - Memory Reconstruction Room
     'memory_reconstruction_title': 'إعادة بناء الذاكرة',
     'memory_reconstruction_subtitle': 'صِف ذكرى باهتة. دعنا نساعدك على الشعور بالعودة إليها.',
     'memory_placeholder': 'أتذكر...\n\nصِف ذكرى بعيدة أو مجزأة. ما الشظايا المتبقية؟ ما المشاعر الباقية؟',
     'memory_timeframe_placeholder': 'متى كان هذا؟ (مثل: "الطفولة"، "قبل عشر سنوات")',
     'memory_hint': 'الذاكرة ليست تسجيلاً. إنها إعادة بناء.',
     'memory_reconstruct': 'أعِد بناء الذاكرة',
     'memory_reconstructing': 'جمع الشظايا معاً...',
     'memory_reconstructed': 'الذاكرة المُعاد بناؤها',
     'memory_explore_another': 'استكشف ذكرى أخرى',
     'memory_error_short': 'يرجى وصف ذاكرتك بمزيد من التفصيل',
     'room_memory-reconstruction': 'إعادة بناء الذاكرة',
    'update_eclipse_room': 'ظلال الكسوف - تحدث في الظلام التام، اكشف في ومضات الضوء',
    'update_dawn_room': 'يقظة الفجر - استقبل أول ضوء بتمارين التنفس والنوايا',
    
    // New themes
    'eclipse_theme_required': 'تتطلب هذه الغرفة سمة الكسوف. الظلام التام ينتظر.',
    'dawn_theme_required': 'تتطلب هذه الغرفة سمة الفجر خلال الساعات الأولى.',
    'dawn_time_hint': 'متاح من 5 إلى 7 صباحاً فقط',
    'change_theme': 'غيّر السمة في الإعدادات',
    
    // Eclipse Shadows Room
    'eclipse_shadows_title': 'ظلال الكسوف',
    'eclipse_shadows_intro': 'في الظلام التام، تبدو الأسرار أكثر أماناً. أجب بصدق—فقط ومضات قصيرة ستُضيء كلماتك.',
    'enter_darkness': 'ادخل الظلام',
    'shadow_question_1': 'ما الذي تخفيه عن نفسك؟',
    'shadow_question_2': 'ما الحقيقة التي ستقولها لو لم يسمعك أحد؟',
    'shadow_question_3': 'ما الذي يطاردك في الثالثة صباحاً؟',
    'shadow_question_4': 'ماذا ستفعل لو لم يكن الخوف موجوداً؟',
    'shadow_question_5': 'ما الذي لست مستعداً لمواجهته؟',
    'speak_into_dark': 'تحدث في الظلام...',
    'release': 'أطلق',
    'eclipse_reveal_prompt': 'انقر على كل ظل لكشف ما اعترفت به.',
    'hidden_in_shadow': '▓▓▓▓▓▓▓▓▓▓▓▓',
    'eclipse_reveal_hint': 'بعض الحقائق أوضح في الظلام.',
    'eclipse_shadows_quote': 'في الظلام التام، نرى أنفسنا أخيراً.',
    
    // Dawn Awakening Room
    'dawn_awakening_title': 'يقظة الفجر',
    'dawn_awakening_intro': 'أول ضوء يحمل الإمكانية. قبل أن يستيقظ العالم، حدد نواياك.',
    'greet_the_light': 'استقبل الضوء',
    'breathe_in_light': 'تنفس الضوء',
    'hold_warmth': 'احتفظ بالدفء',
    'release_night': 'أطلق الليل',
    'dawn_prompt_1': 'ما الذي تريد أن تحمله معك اليوم؟',
    'dawn_prompt_2': 'ما الذي ستتخلى عنه؟',
    'dawn_prompt_3': 'ما هو فعل اللطف الصغير الذي ستقدمه لنفسك؟',
    'set_your_intention': 'حدد نيتك...',
    'plant_seed': 'ازرع هذه البذرة',
    'dawn_awakening_quote': 'كل فجر دعوة للبدء من جديد.',
    
    // Fragment Trading
    'fragment_trading': 'تداول الشظايا',
    'sacrifice_to_unlock': 'ضحِّ بالشظايا لكشف الأسرار',
    'your_fragments': 'شظاياك',
    'fragments_any': 'أي شظايا',
    'fragments_free': 'شظايا الوضوح',
    'fragments_premium': 'شظايا العمق',
    'fragments_horror': 'شظايا الظل',
    'trade_successful': 'تمت المبادلة بنجاح!',
    'trade_failed': 'فشلت المبادلة',
    'not_enough_fragments': 'شظايا غير كافية',
    'continue_trading': 'متابعة التداول',
    'trading_fragments': 'جارٍ تداول الشظايا...',
    'fragment_trade_warning': 'لا يمكن استعادة الشظايا المضحى بها',
    'unlock-time-locked': 'فتح غرفة مقفلة زمنياً',
    'unlock-time-locked_desc': 'ادخل غرفة مقفلة زمنياً دون انتظار',
    'preview-horror': 'معاينة غرفة رعب',
    'preview-horror_desc': 'ألقِ نظرة على غرفة رعب قبل الدخول',
    'ai-reflection-trade': 'تأمل الذكاء الاصطناعي',
    'ai-reflection-trade_desc': 'احصل على رسالة شخصية من الذكاء الاصطناعي',
    'unlock-premium-preview': 'تجربة غرفة مميزة',
    'unlock-premium-preview_desc': 'ادخل غرفة مميزة لزيارة واحدة',
    'fragment_trading_room': 'مذبح الشظايا',
    'fragment_trading_intro': 'هنا، يمكنك التضحية بشظاياك المجمعة لكشف الأسرار المخفية في هذه الغرف.',
    'your_collection': 'مجموعتك',
    'fragment_clarity': 'الوضوح',
    'fragment_depth': 'العمق',
    'fragment_shadow': 'الظل',
    'total_fragments': 'المجموع',
    'open_altar': 'اقترب من المذبح',
    'recent_trades': 'القرابين الأخيرة',
    'fragment_trade_ending': 'ما أُعطي لا يمكن استرداده. لكن ما اكتُسب... يبقى.',
    'room_fragment-trade': 'مذبح الشظايا',
    'sub_fragment-trade': 'ضحِّ لكشف الأسرار',
    // Phase 8
    'update_anniversary_system': 'نظام الذكرى السنوية والمعالم - تتبع رحلتك عبر الزمن',
    'update_archive_room': 'الأرشيف - تاريخ الرحلة الكامل يُفتح عند 25 غرفة',
    'update_mirror_room': 'مرآة كل الذوات - صورة سلوكية بالذكاء الاصطناعي عند 50 غرفة',
    'update_ending_room': 'النهاية - وداع شخصي عند 75 غرفة',
    'room_the-archive': 'الأرشيف',
    'room_mirror-of-all-selves': 'مرآة كل الذوات',
    'room_the-ending': 'النهاية',
    // Phase 9
    'update_fragment_rarities': 'ندرة الشظايا - شظايا قابلة للتحصيل عادية ونادرة وأسطورية',
    'update_fragment_gallery': 'معرض الشظايا - عرض مرئي لمجموعتك',
    'update_seasonal_fragments': 'شظايا موسمية وسرية - مقتنيات محدودة الوقت',
    'fragment_collection': 'المجموعة',
    'fragment_collection_title': 'مجموعة الشظايا',
    'fragment_collection_subtitle': 'كل غرفة تترك شيئاً خلفها. اجمعها جميعاً.',
    'collection_milestones': 'المعالم',
    'collection_secrets': 'الأسرار',
    'collection_footer': 'بعض الشظايا تظهر فقط في ظروف معينة.',
    'rarity_common': 'عادي',
    'rarity_rare': 'نادر',
    'rarity_legendary': 'أسطوري',
    // Phase 10
    'accessibility': 'إمكانية الوصول',
    'accessibility_desc': 'تخصيص القراءة وتفضيلات الحركة',
    'font_family': 'عائلة الخط',
    'font_default': 'أحادي المسافة',
    'font_dyslexia': 'صديق لعسر القراءة',
    'font_sans': 'بدون حواف',
    'font_changed': 'تم تحديث الخط',
    'line_height': 'ارتفاع السطر',
    'line_compact': 'مضغوط',
    'line_normal': 'عادي',
    'line_relaxed': 'مريح',
    'line_spacious': 'واسع',
    'line_height_changed': 'تم تحديث ارتفاع السطر',
    'reduced_motion_full_desc': 'تعطيل جميع الرسوم المتحركة والتأثيرات',
    'larger_cursors': 'أهداف أكبر',
    'larger_cursors_desc': 'زيادة حجم أهداف النقر',
    'update_accessibility_suite': 'مجموعة إمكانية الوصول - خطوط عسر القراءة والتحكم في ارتفاع السطر',
    'update_dyslexia_font': 'خيار خط صديق لعسر القراءة في الإعدادات',
    'update_line_height': 'ارتفاع سطر قابل للتعديل لتحسين القراءة',
    'update_enhanced_reduced_motion': 'حركة مخفضة محسنة - تعطيل كامل لجميع التأثيرات',
    // Phase 11
    'update_shared_moments': 'لحظات مشتركة - تسجيلات عاطفية مجهولة مع الآخرين',
    'update_whisper_wall': 'جدار الهمس - اترك حقائق مجهولة في الظلام',
    'update_collective_memory': 'الذاكرة الجماعية - شظايا مشتركة مما نتذكره',
    'whisper_prompt': 'اترك همسة على الجدار. مجهولة. دائمة.',
    'whisper_placeholder': 'ما الذي تحمله بصمت؟',
    'whisper_send': 'اهمس',
    'whisper_added': 'همستك انضمت إلى الجدار.',
    'whisper_another': 'اهمس مرة أخرى',
    'shared_moments_title': 'الآن، في هذه اللحظة...',
    'shared_moments_subtitle': 'كيف حالك، بصدق؟',
    'feeling_grateful': 'ممتن',
    'feeling_lost': 'ضائع',
    'feeling_hopeful': 'متفائل',
    'feeling_heavy': 'ثقيل',
    'feeling_peaceful': 'مسالم',
    'feeling_restless': 'قلق',
    'room_whisper-wall': 'جدار الهمس',
    'room_collective-memory': 'الذاكرة الجماعية',
    'room_shared-moments': 'لحظات مشتركة',

    // v3.21.0 - New Rooms
    'room_mask-you-wear': 'القناع الذي ترتديه',
    'sub_mask-you-wear': 'أي وجه تظهره؟',
    'room_recurring-dream': 'حلمك المتكرر',
    'sub_recurring-dream': 'الحلم الذي يعود دائماً',
    'room_promise-you-broke': 'الوعد الذي كسرته',
    'sub_promise-you-broke': 'صدق حول الوعود المكسورة',
    'room_words-you-swallowed': 'كلمات ابتلعتها',
    'sub_words-you-swallowed': 'أشياء لم تقلها أبداً',
    'room_age-you-feel': 'العمر الذي تشعر به',
    'sub_age-you-feel': 'كم عمرك حقاً؟',
    'room_biggest-lie': 'أكبر كذبة لديك',
    'sub_biggest-lie': 'الكذبة التي تقولها أكثر',
    'room_person-you-pretend-to-be': 'الشخص الذي تتظاهر أنك هو',
    'sub_person-you-pretend-to-be': 'أداؤك',
    'room_person-you-pretend': 'الشخص الذي تتظاهر أنك هو',
    'sub_person-you-pretend': 'أداؤك',
    'room_what-you-collect': 'ماذا تجمع',
    'sub_what-you-collect': 'الأشياء تكشف الهوية',
    'room_apology-you-owe': 'الاعتذار الذي تدين به',
    'sub_apology-you-owe': 'اكتب ما كان يجب أن تقوله',
    'room_unfinished-story': 'قصتك غير المكتملة',
    'sub_unfinished-story': 'الشيء الذي لم تكمله',
    'room_version-others-see': 'النسخة التي يراها الآخرون',
    'sub_version-others-see': 'كيف يراك الآخرون',
    'room_secret-talent': 'موهبتك السرية',
    'sub_secret-talent': 'ما لا يعرفه أحد عنك',
    'room_fear-you-feed': 'الخوف الذي تغذيه',
    'sub_fear-you-feed': 'أي خوف يتحكم بك',
    'room_guilty-pleasure': 'متعتك المذنبة',
    'sub_guilty-pleasure': 'ما تخفي استمتاعك به',
    'room_rule-you-break': 'القاعدة التي تكسرها دائماً',
    'sub_rule-you-break': 'نمط تمردك',
    'room_childhood-promise': 'وعد طفولتك',
    'sub_childhood-promise': 'ما وعدت به نفسك',
    'room_compliment-you-reject': 'المجاملة التي ترفضها',
    'sub_compliment-you-reject': 'لماذا لا تقبل المديح',
    'room_autopilot-mode': 'وضع الطيار الآلي',
    'sub_autopilot-mode': 'ما تفعله بدون تفكير',
    'room_boundary-you-need': 'الحد الذي تحتاجه',
    'sub_boundary-you-need': 'ما يجب أن تحميه',
    'room_inner-critic': 'ناقدك الداخلي',
    'sub_inner-critic': 'الصوت الذي يحكم',
    'room_last-sunday': 'الأحد الماضي',
    'sub_last-sunday': 'ماذا فعلت فعلاً؟',
    'room_wasted-hours': 'ساعاتك الضائعة',
    'sub_wasted-hours': 'أين يختفي الوقت',
    'room_year-that-changed': 'السنة التي غيرتك',
    'sub_year-that-changed': 'نقطة التحول',
    'room_tomorrows-regret': 'ندم الغد',
    'sub_tomorrows-regret': 'ماذا ستتمنى لو فعلت؟',
    'room_morning-autopilot': 'طيارك الآلي الصباحي',
    'sub_morning-autopilot': 'طقوس الساعة الأولى',
    'room_season-you-dread': 'الموسم الذي تخشاه',
    'sub_season-you-dread': 'أي فصل أثقل؟',
    'room_ten-years-ago': 'قبل عشر سنوات',
    'sub_ten-years-ago': 'من كنت حينها؟',
    'room_last-normal-day': 'آخر يوم عادي',
    'sub_last-normal-day': 'قبل أن يتغير كل شيء',
    'room_clock-you-ignore': 'الساعة التي تتجاهلها',
    'sub_clock-you-ignore': 'وقت تتظاهر أنه لا يمر',
    'room_your-deadline': 'موعدك النهائي',
    'sub_your-deadline': 'ماذا تؤجل؟',
    'room_the-pause': 'التوقف',
    'sub_the-pause': 'تنفس للحظة',
    'room_static-room': 'ثابت',
    'sub_static-room': 'ضوضاء بيضاء وسكون',
    'room_gap-between-thoughts': 'الفجوة بين الأفكار',
    'sub_gap-between-thoughts': 'اعثر على المساحة',
    'room_sit-with-discomfort': 'اجلس مع الانزعاج',
    'sub_sit-with-discomfort': 'لا تصلح شيئاً',
    'room_boring-room': 'الغرفة المملة',
    'sub_boring-room': 'لا شيء مثير على الإطلاق',
    'room_unplug-room': 'افصل',
    'sub_unplug-room': 'تخيل بلا أجهزة',
    'room_unplug': 'افصل',
    'sub_unplug': 'تخيل بلا أجهزة',
    'room_last-page': 'الصفحة الأخيرة',
    'sub_last-page': 'القصة تنتهي هنا',
    'room_scrolling-pattern': 'نمط التمرير',
    'sub_scrolling-pattern': 'كيف تستهلك المحتوى',
    'room_tab-you-keep-open': 'التبويب المفتوح دائماً',
    'sub_tab-you-keep-open': 'ما يبقى في متصفحك',
    'room_digital-shadow': 'ظلك الرقمي',
    'sub_digital-shadow': 'بصمتك الرقمية',
    'room_notification-you-ignore': 'الإشعار الذي تتجاهله',
    'sub_notification-you-ignore': 'ما ترفضه',
    'room_typing-speed': 'سرعة كتابتك',
    'sub_typing-speed': 'بأي سرعة تعبر؟',
    'room_link-you-almost-clicked': 'الرابط الذي كدت تضغطه',
    'sub_link-you-almost-clicked': 'أنماط التردد',
    'room_bookmark-graveyard': 'مقبرة المفضلات',
    'sub_bookmark-graveyard': 'حُفظت ولم تُعد زيارتها',
    'room_search-you-deleted': 'البحث الذي حذفته',
    'sub_search-you-deleted': 'ما بحثت عنه ومحوته',
    'room_gravity-room': 'غرفة الجاذبية',
    'sub_gravity-room': 'كل شيء يسقط ببطء',
    'room_ink-blot': 'بقعة حبر',
    'sub_ink-blot': 'ماذا ترى؟',
    'room_hallway-of-mirrors': 'ممر المرايا',
    'sub_hallway-of-mirrors': 'انعكاسات لا نهائية',
    'room_dissolving-text': 'نص يذوب',
    'sub_dissolving-text': 'الكلمات تذوب أثناء القراءة',
    'room_the-pendulum': 'البندول',
    'sub_the-pendulum': 'حركة مُنوِّمة',
    'room_pixel-rain': 'مطر البكسل',
    'sub_pixel-rain': 'هطول رقمي',
    'room_the-threshold': 'العتبة',
    'sub_the-threshold': 'بين هنا وهناك',
    'most_loved_rooms': 'الغرف الأكثر حباً',
    'rate_room': 'قيّم هذه الغرفة',
    'update_52_new_rooms': '52 غرفة جديدة عبر فئات الهوية والوقت والتأمل والميتا والتجريبية',
    'update_room_rating': 'نظام تقييم الغرف - إعجاب/عدم إعجاب مع قسم الغرف الأكثر حباً',
    'update_bug_fix_reflections': 'إصلاح عرض وتخزين التأملات المجهولة',
    'update_100_rooms': 'أكثر من 100 غرفة متاحة الآن في الممر',
    // v3.22.0
    'room_the-friend-you-lost': 'الصديق الذي فقدته',
    'sub_the-friend-you-lost': 'اكتب لشخص ابتعدت عنه.',
    'room_conversation-you-replay': 'المحادثة التي تعيدها',
    'sub_conversation-you-replay': 'أعد بناءها من الذاكرة.',
    'room_the-thank-you': 'الشكر',
    'sub_the-thank-you': 'امتنان لم تعبّر عنه أبداً.',
    'room_people-you-forgot': 'أشخاص نسيتهم',
    'sub_people-you-forgot': 'حاول تذكر أسماء منسية.',
    'room_the-advice-you-give': 'النصيحة التي تقدمها',
    'sub_the-advice-you-give': 'نصيحة تعطيها لكن لا تتبعها.',
    'room_what-others-dont-know': 'ما لا يعرفه الآخرون',
    'sub_what-others-dont-know': 'طبقاتك المخفية.',
    'room_the-lie-you-believe': 'الكذبة التي تصدقها',
    'sub_the-lie-you-believe': 'حقيقة مريحة غير صحيحة.',
    'room_your-shadow-self': 'ظلك الداخلي',
    'sub_your-shadow-self': 'الجزء الذي تقمعه.',
    'room_the-story-you-tell': 'القصة التي ترويها',
    'sub_the-story-you-tell': 'كيف تروي حياتك.',
    'room_your-safe-place': 'مكانك الآمن',
    'sub_your-safe-place': 'صف المكان الذي تشعر فيه بالأمان.',
    'room_the-risk-you-didnt-take': 'المخاطرة التي لم تأخذها',
    'sub_the-risk-you-didnt-take': 'طرق لم تسلكها.',
    'room_your-last-excuse': 'عذرك الأخير',
    'sub_your-last-excuse': 'العذر الذي تستخدمه أكثر.',
    'room_the-habit-you-hide': 'العادة التي تخفيها',
    'sub_the-habit-you-hide': 'روتين سري.',
    'room_what-you-postpone': 'ما تؤجله',
    'sub_what-you-postpone': 'الشيء الذي دائماً "غداً".',
    'room_the-door-you-closed': 'الباب الذي أغلقته',
    'sub_the-door-you-closed': 'فرص رفضتها.',
    'journal_title': 'يوميات التأمل',
    'journal_subtitle': 'أفكارك عبر الغرف',
    'journal_empty': 'لا تأملات بعد. زر الغرف واكتب أفكارك.',
    'journal': 'اليوميات',
    'room_chains': 'سلاسل الغرف',
    'chain_identity_journey': 'رحلة الهوية',
    'chain_time_reflection': 'تأمل الزمن',
    'chain_truth_path': 'مسار الحقيقة',
    'recently_visited': 'زيارات أخيرة',
    'update_reflection_journal': 'يوميات التأمل - احفظ أفكارك عبر الغرف',
    'update_room_chains': 'سلاسل الغرف - أكمل الغرف بالتسلسل لأجزاء إضافية',
    'update_15_new_rooms': '15 غرفة تفاعلية جديدة عبر 3 فئات',
    'update_recently_visited': 'قسم الزيارات الأخيرة للعودة السريعة',
    'ai_companion': 'الرفيق',
    'companion_create': 'أنشئ رفيقك',
    'companion_setup_desc': 'انعكاس يسير بجانبك.',
    'companion_name': 'سمّ رفيقك',
    'companion_name_placeholder': 'مثلاً: صدى، مرآة، ظل...',
    'companion_lens': 'عدسة الشخصية',
    'companion_mood': 'مزاج التواصل',
    'companion_fears': 'ما الذي تخاف منه؟ (اختياري)',
    'companion_struggles': 'ما الذي تعاني منه؟ (اختياري)',
    'create_companion': 'إنشاء',
    'companion_input': 'قل شيئاً...',
    'companion_greeting': 'رفيقك هنا. قل شيئاً.',
    'companion_starter_1': 'ما الذي يشغل بالي اليوم؟',
    'companion_starter_2': 'أشعر بأنني عالق.',
    'companion_starter_3': 'أخبرني بشيء أتجنبه.',
    'fears_placeholder': 'مثلاً: الفشل، الرفض، النسيان...',
    'struggles_placeholder': 'مثلاً: الإفراط في التفكير، التسويف، الوحدة...',
    'back_step': 'رجوع',
    'lens_reflective': 'تأملي',
    'lens_reflective_desc': 'أسئلة عميقة، التعرف على الأنماط',
    'lens_supportive': 'داعم',
    'lens_supportive_desc': 'دافئ، مشجع، مُصدّق',
    'lens_challenging': 'تحدّي',
    'lens_challenging_desc': 'مباشر، مثير للتفكير',
    'lens_curious': 'فضولي',
    'lens_curious_desc': 'أسئلة غير متوقعة، دهشة',
    'lens_shadow': 'ظل',
    'lens_shadow_desc': 'الجانب الخفي، انزعاج صادق',
    'companion_mood_calm': 'هادئ',
    'companion_mood_calm_desc': 'متزن وسلمي',
    'companion_mood_intense': 'حاد',
    'companion_mood_intense_desc': 'عاجل وعميق',
    'companion_mood_playful': 'مرح',
    'companion_mood_playful_desc': 'ذكي وخفيف',
    'companion_mood_melancholic': 'حزين',
    'companion_mood_melancholic_desc': 'حزن جميل',
    'update_ai_companion': 'رفيق ذكي - ذكاء اصطناعي يتذكر رحلتك',
    // v3.23.0
    'mood_history': 'سجل المزاج',
    'top_rooms': 'غرفك المفضلة',
    'visits_count': 'زيارات',
    'set_intention': 'حدد نيتك لليوم',
    'todays_intention': 'نية اليوم',
    'intention_placeholder': 'سأفعل...',
    'set': 'تعيين',
    'update_mood_history': 'سجل المزاج - تتبع تغيرات مزاجك',
    'update_daily_intention': 'النية اليومية - حدد تركيزك لكل يوم',
    'update_top_rooms': 'الغرف المفضلة - وصول سريع لأكثر غرفك زيارة',
    'update_10_new_rooms_v323': '10 غرف جديدة: المشاعر، التأمل الذاتي، والتواصل',
    'room_the-emotion-you-hide': 'المشاعر التي تخفيها',
    'sub_the-emotion-you-hide': 'سمّها. مرة واحدة فقط.',
    'emotion_hide_placeholder': 'المشاعر التي لا أظهرها هي...',
    'quote_emotion_hide': 'ما نخفيه عن الآخرين، غالباً نخفيه عن أنفسنا.',
    'room_your-comfort-lie': 'كذبتك المريحة',
    'sub_your-comfort-lie': 'الكذبة الصغيرة التي تبقيك مرتاحاً.',
    'comfort_lie_placeholder': 'الكذبة التي أقولها لنفسي هي...',
    'admit_it': 'اعترف بها',
    'quote_comfort_lie': 'الأكاذيب المريحة هي جدران نبنيها بقماش ناعم.',
    'room_the-silence-between': 'الصمت بين',
    'sub_the-silence-between': 'اجلس في الفجوة بين فكرتين.',
    'silence_instruction': 'أغمض عينيك. ابحث عن الفجوة بين فكرتين. ثم اضغط الزر.',
    'silence_sitting': 'جالس في صمت...',
    'silence_found': 'وجدتها',
    'silence_result': 'جلست لمدة',
    'seconds': 'ثواني',
    'quote_silence_between': 'بين كل فكرة مساحة لا نهائية. لقد زرتها للتو.',
    'room_what-you-carry': 'ما تحمله',
    'sub_what-you-carry': 'سمّ الثقل الخفي.',
    'carry_placeholder': 'الثقل الذي أحمله هو...',
    'set_it_down': 'ضعه أرضاً',
    'carry_released': 'للحظة، كان أخف.',
    'quote_carry': 'لا يمكنك وضع ما ترفض تسميته.',
    'room_the-version-you-lost': 'النسخة التي فقدتها',
    'sub_the-version-you-lost': 'من كنت قبل أن تحدث الحياة.',
    'version_lost_placeholder': 'قبل أن يتغير كل شيء، كنت...',
    'remember': 'تذكّر',
    'quote_version_lost': 'لم تفقدهم. أصبحوا جزءاً مما أنت عليه الآن.',
    'room_letter-to-past-self': 'رسالة للماضي',
    'sub_letter-to-past-self': 'اكتب لمن كنت.',
    'letter_past_placeholder': 'عزيزي أنا الأصغر...',
    'send_letter': 'أرسل',
    'letter_sent': 'أُرسلت الرسالة. في مكان ما، استلموها.',
    'quote_letter_past': 'كل كلمة عزاء تكتبها لنفسك الماضية هي حقاً لمن أنت الآن.',
    'room_the-moment-you-knew': 'اللحظة التي عرفت',
    'sub_the-moment-you-knew': 'اللحظة التي تغير فيها كل شيء.',
    'moment_knew_placeholder': 'اللحظة التي عرفت فيها كانت عندما...',
    'mark_it': 'علّمها',
    'quote_moment_knew': 'بعض اللحظات تقسم حياتك إلى قبل وبعد. لقد سمّيت واحدة.',
    'room_the-words-you-rehearse': 'الكلمات التي تتدرب عليها',
    'sub_the-words-you-rehearse': 'ما تتمرن على قوله لكن لا تفعل.',
    'words_rehearse_placeholder': 'الكلمات التي أتدرب عليها هي...',
    'say_them': 'قُلها',
    'words_spoken': 'أخيراً قلتها. حتى لو هنا فقط.',
    'quote_words_rehearse': 'الكلمات المتدرب عليها أقفاص. الكلمات المنطوقة طيور.',
    'room_someone-thinks-of-you': 'شخص يفكر بك',
    'sub_someone-thinks-of-you': 'الآن، في مكان ما، شخص يتذكرك.',
    'someone_line_1': 'الآن، في مكان ما، شخص يتذكرك.',
    'someone_line_2': 'ليس بسبب ما فعلت.',
    'someone_line_3': 'بل بسبب كيف جعلتهم يشعرون.',
    'quote_someone_thinks': 'أنت موجود في ذكريات لا تعرف عنها.',
    'room_the-question-you-fear': 'السؤال الذي تخافه',
    'sub_the-question-you-fear': 'السؤال الذي تأمل ألا يسأله أحد.',
    'question_fear_placeholder': 'السؤال الذي أخافه أكثر هو...',
    'quote_question_fear': 'السؤال الذي تخافه أكثر عادة ما يحمل الإجابة الأهم.',
    // v3.24.0
    'behavior_insight': 'الموقع يلاحظ',
    'insight_pattern': 'أنت منجذب إلى {category}. تستكشف أكثر في {time}.',
    'cat_identity': 'الهوية',
    'cat_truth': 'الحقيقة',
    'cat_time': 'الوقت',
    'cat_mortality': 'الفناء',
    'cat_stillness': 'السكون',
    'cat_darkness': 'الظلام',
    'cat_patterns': 'الأنماط',
    'cat_fear': 'الخوف',
    'cat_play': 'اللعب',
    'cat_experiments': 'التجارب',
    'cat_ritual': 'الطقوس',
    'cat_secrets': 'الأسرار',
    'time_late_night': 'وقت متأخر من الليل',
    'time_morning': 'الصباح',
    'time_afternoon': 'بعد الظهر',
    'time_evening': 'المساء',
    
    'anonymous_visitor': 'زائر',
    'update_behavior_insight': 'رؤية السلوك - الموقع يعكس أنماطك لك',
    'update_anonymous_reflections_feed': 'تأملات مجهولة - شاهد ما شعر به الآخرون',
    'update_daily_prompt': 'تأمل يومي - فكرة تحملها معك',
    'room_recurring-dream-journal': 'يوميات الحلم المتكرر',
    'sub_recurring-dream-journal': 'الحلم الذي يعود دائماً.',
    'dream_journal_placeholder': 'الحلم الذي أراه دائماً...',
    'record_dream': 'سجّل',
    'dream_journal_reflection': 'الأحلام تقول ما لا يقوله العقل اليقظ.',
    'quote_dream_journal': 'الأحلام المتكررة هي محادثات لم تنتهِ مع نفسك.',
    'room_lucid-moment': 'اللحظة الواعية',
    'sub_lucid-moment': 'كن واعياً داخل الحلم.',
    'lucid_line_1': 'أنت تقرأ هذه الكلمات.',
    'lucid_line_2': 'لكن هل أنت مستيقظ؟',
    'lucid_line_3': 'كيف ستعرف الفرق؟',
    'become_aware': 'أنا واعٍ',
    'lucid_aware': 'هذه اللحظة من الوعي هي الشيء الحقيقي الوحيد.',
    'quote_lucid_moment': 'اللحظة التي تسائل فيها الواقع هي اللحظة التي تراه فيها حقاً.',
    'room_nightmare-you-remember': 'الكابوس الذي تتذكره',
    'sub_nightmare-you-remember': 'الذي لا يزال يطاردك.',
    'nightmare_placeholder': 'الكابوس الذي لا أنساه...',
    'face_it': 'واجهه',
    'nightmare_reflection': 'بكتابته، سلبت منه بعض قوته.',
    'quote_nightmare': 'الكوابيس هي مخاوف تصر على أن تُرى.',
    'room_dream-you-wish-was-real': 'الحلم الذي تتمنى لو كان حقيقياً',
    'sub_dream-you-wish-was-real': 'استيقظ. لم يكن حقيقياً.',
    'dream_wish_placeholder': 'حلمت أن...',
    'hold_onto_it': 'تمسك به',
    'quote_dream_wish': 'الأحلام التي نحزن على فقدانها تكشف الحياة التي نريدها حقاً.',
    'room_the-dream-visitor': 'زائر الحلم',
    'sub_the-dream-visitor': 'شخص يظهر في أحلامك باستمرار.',
    'dream_visitor_line_1': 'هناك شخص يزورك في الأحلام.',
    'dream_visitor_line_2': 'لم تدعه.',
    'dream_visitor_line_3': 'لكنه يعود دائماً.',
    'dream_visitor_line_4': 'ربما يحاول إخبارك بشيء.',
    'quote_dream_visitor': 'الأشخاص الذين يظهرون في أحلامنا هم رسل من اللاوعي.',
    'room_the-blank-canvas': 'اللوحة الفارغة',
    'sub_the-blank-canvas': 'اصنع شيئاً من لا شيء.',
    'blank_canvas_placeholder': 'على هذه اللوحة الفارغة، أصنع...',
    'create': 'أنشئ',
    'quote_blank_canvas': 'كل إبداع يبدأ بشجاعة مواجهة الفراغ.',
    'room_song-that-is-you': 'الأغنية التي هي أنت',
    'sub_song-that-is-you': 'لو كنت أغنية، أي واحدة؟',
    'song_placeholder': 'الأغنية التي هي أنا...',
    'play_it': 'شغّلها',
    'quote_song': 'الموسيقى هي صوت المشاعر.',
    'room_color-of-today': 'لون اليوم',
    'sub_color-of-today': 'اختر لون حالتك الحالية.',
    'color_chosen': 'هذا هو شعورك اليوم. بالألوان.',
    'quote_color_today': 'الألوان لا تحتاج كلمات. وكذلك المشاعر.',
    'room_the-unwritten-poem': 'القصيدة غير المكتوبة',
    'sub_the-unwritten-poem': 'اكتب ما كنت تحمله بداخلك.',
    'poem_placeholder': 'كلمات كانت تنتظر...',
    'publish_it': 'انشرها',
    'quote_unwritten_poem': 'القصيدة كانت دائماً هناك. أنت فقط أطلقتها أخيراً.',
    'room_your-inner-voice': 'صوتك الداخلي',
    'sub_your-inner-voice': 'ماذا يقول فعلاً؟',
    'inner_voice_placeholder': 'صوتي الداخلي يقول...',
    'listen': 'استمع',
    'inner_voice_reflection': 'الآن بعد أن سمعته، ماذا ستفعل؟',
    'quote_inner_voice': 'صوتك الداخلي كان يتحدث طوال الوقت. أنت فقط بدأت تستمع.',
    'room_fear-you-named': 'الخوف الذي سميته',
    'sub_fear-you-named': 'سمّه وسيفقد قوته.',
    'fear_named_placeholder': 'الخوف الذي أسمّيه أخيراً...',
    'name_it': 'سمّه',
    'fear_named_reflection': 'المخاوف المسماة تتقلص. غير المسماة تنمو.',
    'quote_fear_named': 'ما يمكنك تسميته يمكنك مواجهته. وما يمكنك مواجهته يمكنك التغلب عليه.',
    'room_the-leap': 'القفزة',
    'sub_the-leap': 'ماذا ستقفز إليه؟',
    'leap_placeholder': 'لو استطعت القفز...',
    'leap': 'اقفز',
    'quote_leap': 'الشبكة تظهر بعد أن تقفز، لا قبلها.',
    'room_courage-inventory': 'جرد الشجاعة',
    'sub_courage-inventory': 'اذكر كل شيء شجاع فعلته.',
    'courage_placeholder': 'شيء شجاع فعلته...',
    'courage_count': 'ذكرت {count} من أعمال الشجاعة.',
    'quote_courage': 'أنت أشجع مما تتذكر.',
    'room_what-scares-you-most': 'ما يخيفك أكثر',
    'sub_what-scares-you-most': 'الخوف الأعمق.',
    'scares_placeholder': 'ما يخيفني أكثر هو...',
    'quote_scares': 'خوفك الأعمق عادة ما يكون أعظم معلم لك.',
    'room_facing-the-dark': 'مواجهة الظلام',
    'sub_facing-the-dark': 'اجلس في ظلام تام.',
    'darkness_counting': '{seconds} ثانية في الظلام...',
    'return_to_light': 'عد إلى النور',
    'darkness_survived': 'جلست في الظلام لمدة {seconds} ثانية.',
    'quote_facing_dark': 'في الظلام، نكتشف ما نحمله بداخلنا.',
    'room_letter-to-future-self': 'رسالة إلى نفسك المستقبلية',
    'sub_letter-to-future-self': 'اكتب لمن ستصبح.',
    'letter_future_placeholder': 'عزيزي أنا المستقبلي...',
    'seal_letter': 'اختم',
    'letter_future_sealed': 'الرسالة مختومة. يوماً ما ستقرأها.',
    'quote_letter_future': 'كل رسالة للمستقبل هي في الحقيقة وعد للحاضر.',
    'room_the-world-you-want': 'العالم الذي تريده',
    'sub_the-world-you-want': 'صف العالم الذي تتمنى وجوده.',
    'world_placeholder': 'العالم الذي أريده...',
    'build_it': 'ابنِه',
    'quote_world_want': 'العالم الذي تتخيله هو الخطوة الأولى نحو العالم الذي تصنعه.',
    'room_your-next-chapter': 'فصلك القادم',
    'sub_your-next-chapter': 'ماذا يحدث بعد ذلك في قصتك؟',
    'next_chapter_placeholder': 'في فصلي القادم...',
    'turn_the_page': 'اقلب الصفحة',
    'quote_next_chapter': 'أنت الكاتب والبطل في آن واحد.',
    'room_the-wish': 'الأمنية',
    'sub_the-wish': 'أمنية واحدة. بلا قواعد.',
    'wish_placeholder': 'أتمنى...',
    'make_wish': 'تمنَّ',
    'quote_wish': 'ما تتمناه يكشف ما تقدّره أكثر.',
    'room_if-you-had-one-year': 'لو كان لديك سنة واحدة',
    'sub_if-you-had-one-year': 'ماذا ستفعل بـ 365 يوماً؟',
    'one_year_placeholder': 'لو كان لدي سنة واحدة...',
    'begin_now': 'ابدأ الآن',
    'one_year_reflection': 'إذاً لماذا لا تفعلها؟',
    'quote_one_year': 'عش كأن لديك سنة. لأنك لا تعرف أبداً.',
    'update_v4_languages': '4 لغات جديدة: الهندية والكورية واليابانية والتركية',
    'update_v4_20_rooms': '20 غرفة جديدة عبر الأحلام والإبداع والخوف والمستقبل',
    'update_v4_dreams_category': 'فئة غرف الأحلام واللاوعي',
    'update_v4_creativity_category': 'فئة غرف الإبداع والتعبير',
    'update_v4_fear_category': 'فئة غرف الخوف والشجاعة',
    'update_v4_future_category': 'فئة غرف المستقبل والأمل',
    'room_playlists': 'قوائم الغرف', 'personal_dashboard': 'لوحة التحكم', 'dashboard_subtitle': 'رحلتك في لمحة', 'visit_pattern': 'نمط الزيارات (14 يوم)', 'dashboard_top_rooms': 'أكثر الغرف زيارة', 'category_breakdown': 'فئات الغرف', 'mood_trend': 'اتجاه المزاج', 'fragment_breakdown': 'تفصيل الشظايا', 'no_data_yet': 'لا توجد بيانات بعد', 'current_streak': 'سلسلة الأيام',
    'playlist_morning': 'تأمل الصباح', 'playlist_morning_desc': 'ابدأ يومك بوضوح وحضور', 'playlist_identity': 'الغوص في الهوية', 'playlist_identity_desc': 'من أنت تحت العناوين؟', 'playlist_creative': 'شرارة إبداعية', 'playlist_creative_desc': 'أطلق تعبيرك الإبداعي', 'playlist_courage': 'مسار الشجاعة', 'playlist_courage_desc': 'واجه مخاوفك غرفة تلو الأخرى', 'playlist_dreams': 'يوميات الأحلام', 'playlist_dreams_desc': 'استكشف عوالم لاوعيك', 'playlist_future': 'رؤية المستقبل', 'playlist_future_desc': 'رسائل وأمنيات لك القادم', 'playlist_truth': 'مواجهة الحقيقة', 'playlist_truth_desc': 'واجه الحقائق المزعجة', 'playlist_night': 'رحلة الليل', 'playlist_night_desc': 'احتضن الصمت والفراغ والعدم',
    // v4.2.0
    'room_focus-timer': 'مؤقت التركيز', 'sub_focus-timer': 'اجلس في صمت. تأمل.',
    'room_gratitude-jar': 'جرة الامتنان', 'sub_gratitude-jar': 'شيء واحد أنت ممتن له',
    'room_mood-compass': 'بوصلة المزاج', 'sub_mood-compass': 'أين أنت الآن؟',
    'room_memory-fade': 'تلاشي الذاكرة', 'sub_memory-fade': 'شاهد ذكرى تذوب',
    'room_sound-of-your-day': 'صوت يومك', 'sub_sound-of-your-day': 'كيف يبدو اليوم؟',
    'room_the-apology-room': 'غرفة الاعتذار', 'sub_the-apology-room': 'اكتب ما لم تقله',
    'room_your-unread-messages': 'رسائلك غير المقروءة', 'sub_your-unread-messages': 'رسائل لم تفتحها',
    'room_the-weight-room': 'غرفة الأثقال', 'sub_the-weight-room': 'سمِّ ما يثقل عليك',
    'room_parallel-lives': 'حيوات موازية', 'sub_parallel-lives': 'في حياة أخرى، أنت...',
    'room_the-quiet-score': 'درجة الهدوء', 'sub_the-quiet-score': 'كم يمكنك البقاء ساكناً؟',
    'focus_choose': 'كم تريد أن تجلس في صمت؟', 'focus_sit': 'اجلس. تنفس. دع الأفكار تمر.', 'focus_done': 'الصمت تحدث.', 'quote_focus_timer': 'في السكون، تسمع ما يُغرقه الضجيج.', 'minutes': 'دقيقة',
    'gratitude_prompt': 'اكتب شيئاً تشعر بالامتنان له اليوم.', 'gratitude_placeholder': 'أنا ممتن لـ...', 'gratitude_add': 'أضف للجرة', 'gratitude_added': 'امتنانك يطفو في الجرة.', 'gratitude_count': 'لحظات امتنان', 'quote_gratitude_jar': 'الامتنان ليس عن امتلاك المزيد. إنه عن ملاحظة ما هو موجود.',
    'mood_compass_prompt': 'انقر حيث تشعر أنك الآن.', 'mood_high_energy': 'طاقة عالية', 'mood_low_energy': 'طاقة منخفضة', 'mood_positive': 'إيجابي', 'mood_negative': 'سلبي', 'quote_mood_compass': 'معرفة أين أنت هي الخطوة الأولى لمعرفة إلى أين تذهب.',
    'memory_fade_prompt': 'اكتب ذكرى. شاهدها تذوب.', 'memory_fade_placeholder': 'ذكرى تريد التخلي عنها...', 'memory_fade_start': 'دعها تتلاشى', 'memory_fade_done': 'ذهبت الآن. لكنها كانت حقيقية.', 'quote_memory_fade': 'الذكريات تذوب سواء شاهدنا أم لا.',
    'sound_day_prompt': 'اختر 3-5 أصوات تمثل يومك.', 'sound_rain': 'مطر', 'sound_traffic': 'مرور', 'sound_birds': 'طيور', 'sound_keyboard': 'لوحة مفاتيح', 'sound_silence': 'صمت', 'sound_wind': 'رياح', 'sound_ocean': 'محيط', 'sound_crowd': 'حشد', 'sound_music': 'موسيقى', 'sound_day_confirm': 'هذا يومي', 'sound_day_result': 'الموسيقى التصويرية ليومك.', 'quote_sound_day': 'لكل يوم صوت. معظمنا ينسى الاستماع.',
    'apology_prompt': 'اكتب اعتذاراً لم تقدمه أبداً.', 'apology_placeholder': 'أعتذر عن...', 'apology_release': 'أطلقه', 'apology_done': 'انضغط إلى لا شيء. لكنك قلته.', 'quote_apology': 'بعض الاعتذارات ليست لهم. إنها لك.',
    'unread_from_past': 'من: ذاتك الماضية', 'unread_from_future': 'من: ذاتك المستقبلية', 'unread_from_critic': 'من: ناقدك الداخلي', 'unread_msg_past': 'هل تذكر عندما وعدت نفسك أن الأمور ستكون مختلفة؟', 'unread_msg_future': 'آمل أنك ألطف مع نفسك الآن مما كنت.', 'unread_msg_critic': 'تعلم أنك كنت تستطيع فعل المزيد. لكن هل ستفعل؟', 'unread_count': 'غير مقروءة', 'unread_reply_placeholder': 'ردك...', 'unread_reply': 'إرسال', 'unread_sent': 'أُرسلت الرسالة إلى الفراغ.', 'quote_unread_messages': 'الرسائل التي نتجنبها غالباً ما تكون الأكثر حاجة.',
    'weight_prompt': 'سمِّ الأشياء التي تثقل عليك.', 'weight_placeholder': 'شيء يثقل عليّ...', 'weight_release_start': 'ابدأ التحرر', 'weight_release_prompt': 'انقر على كل ثقل لتحرره.', 'weight_done': 'ارتفع الثقل. في الوقت الحالي.', 'quote_weight_room': 'لا يمكنك وضع ما ترفض تسميته.',
    'parallel_intro': 'في حياة أخرى، أنت...', 'parallel_life_1': 'في حياة أخرى، أنت حارس منارة في جزيرة نائية.', 'parallel_life_2': 'في حياة أخرى، لم تغادر مسقط رأسك أبداً.', 'parallel_life_3': 'في حياة أخرى، قلت نعم عندما قلت لا.', 'parallel_life_4': 'في حياة أخرى، أنت شخص آخر تماماً.', 'parallel_life_5': 'في حياة أخرى، أنت تماماً من أنت الآن.', 'parallel_life_6': 'في حياة أخرى، اخترت الطريق الأصعب.', 'parallel_life_7': 'في حياة أخرى، لا تزال تبحث عن هذه الغرفة.', 'quote_parallel_lives': 'كل اختيار يخلق كوناً اخترت فيه بشكل مختلف.',
    'quiet_instruction': 'لا تتفاعل. فقط ابقَ ساكناً.', 'quiet_reveal_1': 'أنت هنا.', 'quiet_reveal_2': 'الضجيج يتلاشى.', 'quiet_reveal_3': 'شيء ما يظهر من الهدوء.', 'quiet_reveal_4': 'أنت أكثر صبراً مما تظن.', 'quiet_reveal_5': 'السكون يعرف اسمك.', 'quiet_score_label': 'درجة هدوئك', 'quiet_impressive': 'سكون رائع.', 'quiet_good': 'وجدت بعض الهدوء.', 'quiet_try_again': 'الرغبة في الفعل قوية.', 'quote_quiet_score': 'السكون ليس غياب الحركة. إنه حضور الوعي.',
    'update_v42_10_rooms': '10 غرف تفاعلية جديدة: مؤقت التركيز، جرة الامتنان، بوصلة المزاج والمزيد', 'update_v42_focus_timer': 'مؤقت التركيز — اجلس في صمت مع مؤقت يعتم تدريجياً', 'update_v42_gratitude_jar': 'جرة الامتنان — اجمع لحظات الامتنان مع الوقت', 'update_v42_mood_compass': 'بوصلة المزاج — ضع نفسك على محاور الطاقة والمشاعر', 'update_v42_weight_room': 'غرفة الأثقال — سمِّ وحرر ما يثقل عليك',
    'update_v43_full_localization': 'ترجمة كاملة — جميع الغرف مترجمة بالكامل الآن', 'update_v43_40_rooms_translated': '40+ غرفة مترجمة — كل غرفة تفاعلية تدعم جميع اللغات', 'update_v43_10_languages': '10 لغات مدعومة — الإنجليزية والعربية والإسبانية والبرتغالية والصينية والفرنسية والهندية والكورية واليابانية والتركية', 'update_v43_nav_consistency': 'تنقل موحد — نص خروج موحد في جميع الغرف',
    // Mirror Lies Room
    'mirror_lies_title': 'المرآة تكذب',
    'mirror_lies_msg1': 'انظر إلى نفسك.',
    'mirror_lies_msg2': 'هل هذا أنت حقاً؟',
    'mirror_lies_msg3': 'المرآة تُظهر ما تريد أن تراه.',
    'mirror_lies_msg4': 'انقر لكشف الحقيقة.',
    'mirror_lies_msg5': 'كل شرخ يكشف المزيد.',
    'mirror_lies_msg6': 'لا شيء خلف الانعكاس.',
    'mirror_lies_quote': 'المرآة لم تُظهرك أبداً. فقط ما كنت تؤمن به.',
    // Winter Solstice Room
    'winter_title': 'أطول ليلة',
    'winter_date': '21 ديسمبر',
    'winter_msg1': 'أطول ليلة هنا.',
    'winter_msg2': 'في الظلام، نجد ما نحمله.',
    'winter_msg3': 'ماذا ستُطلق؟ ماذا ستحتفظ؟',
    'winter_releasing': 'أنا أُطلق:',
    'winter_keeping': 'أنا أحتفظ بـ:',
    'winter_release_placeholder': 'ما لم يعد يخدمك...',
    'winter_keep_placeholder': 'ما يمنحك القوة...',
    'winter_enter_light': 'ادخل النور',
    'winter_released': 'تم إطلاقه:',
    'winter_kept': 'تم الاحتفاظ به:',
    'winter_light_returns': 'من هذه الليلة، يعود النور.',
    'winter_quote': 'أطول ليلة تعلمنا أنه حتى في أعمق ظلام، النور يعود بالفعل.',
    // Halloween Room
    'halloween_title': 'غرفة الحجاب',
    'halloween_date': '31 أكتوبر',
    'halloween_msg1': 'الحجاب رقيق الليلة.',
    'halloween_msg2': 'ما الذي يتربص في ظلالك؟',
    'halloween_msg3': 'سمِّ الخوف الذي تحمله معك...',
    'halloween_msg4': 'أعطه شكلاً، وأطلقه.',
    'halloween_placeholder': 'اكتب خوفك...',
    'halloween_release': 'أطلقه',
    'halloween_dissolves': 'خوفك يذوب في الليل...',
    'halloween_quote': 'الأشياء التي نخافها تفقد قوتها عندما نسميها. دعها تذهب.',
    // What Keeps Awake
    'awake_title': 'ما يبقيك مستيقظاً',
    'awake_locked': 'هذه الغرفة متاحة فقط بعد الساعة 10 مساءً.',
    'awake_return': 'عد عندما يأتي الليل.',
    'awake_press_enter': 'اضغط إدخال',
    'awake_named': 'الآن سميته.',
    'awake_quote': 'الظلام يعرف ما تتجنبه في وضح النهار.',
    // Voice Without Meaning
    'voice_meaning_title': 'صوتك بدون معنى',
    'voice_meaning_prompt': 'دندن. تنفس. أصدر أي صوت. بلا كلمات.',
    'voice_meaning_done': 'هذا كان حضورك. لا شيء أكثر.',
    'voice_meaning_hold': 'استمر',
    'voice_meaning_recording': 'جارٍ التسجيل...',
    'voice_meaning_no_playback': 'لا إعادة. الصوت وُجد فقط في تلك اللحظة.',
    'voice_meaning_quote': 'أنت موجود. هذا هو المحتوى الوحيد.',
    // They're Watching
    'watching_msg1': 'أنت وحدك هنا.',
    'watching_msg2': 'وحدك تماماً.',
    'watching_msg3': 'أم أنت؟',
    'watching_msg4': 'كانوا هنا طوال الوقت.',
    'watching_msg5': 'هم دائماً يراقبون.',
    // Shortcut Always Take
    'shortcut_title': 'الاختصار الذي تأخذه دائماً',
    'shortcut_instruction': 'اختر ما تعرفه.',
    'shortcut_1': 'قول "أنا بخير" عندما لست كذلك',
    'shortcut_2': 'التحقق من هاتفك بدلاً من التواجد',
    'shortcut_3': 'البقاء صامتاً لتجنب الصراع',
    'shortcut_4': 'الشراء بدلاً من الصنع',
    'shortcut_5': 'انتظار "الوقت المناسب"',
    'shortcut_6': 'إلقاء اللوم على الظروف بدلاً من الفعل',
    'shortcut_7': 'اختيار الراحة على النمو',
    'shortcut_8': 'التمرير بدلاً من النوم',
    'shortcut_9': 'قول نعم عندما تعني لا',
    'shortcut_10': 'التخطيط بدلاً من الفعل',
    'shortcut_pattern': 'نمطك',
    'shortcut_selected': 'اخترت:',
    'shortcut_show_pattern': 'أظهر نمطي',
    'shortcut_quote': 'الاختصار يصبح الطريق.',
    'shortcut_pattern_own': 'نمطك هو ملكك.',
    'pattern_avoidance': 'تتجنب ما هو غير مريح.',
    'pattern_distraction': 'تهرب إلى الإلهاء.',
    'pattern_convenience': 'تستبدل الجهد بالسهولة.',
    'pattern_delay': 'تنتظر ظروفاً لن تأتي أبداً.',
    'pattern_comfort': 'تحمي نفسك من الانزعاج.',
    'pattern_pleasing': 'تختفي لتبقي الآخرين مرتاحين.',
    // Life As Shapes
    'shapes_q1': 'كم مرة تشعر بالسلام؟',
    'shapes_q2': 'كم تثق بنفسك؟',
    'shapes_q3': 'كم تشعر بالتواصل مع الآخرين؟',
    'shapes_q4': 'كم من وقتك هو حقاً لك؟',
    'shapes_q5': 'كم يبدو المستقبل ثقيلاً؟',
    'shapes_result_title': 'حياتك كأشكال',
    'shapes_no_labels': 'بلا تسميات. بلا تفسير.',
    'shapes_yours': 'ما تراه هو لك لتفهمه.',
    'shapes_quote': 'الشكل معنى بلا كلمات.',
    'shapes_scale': '1 = لا على الإطلاق، 5 = تماماً',
    // Read Aloud
    'read_title': 'اقرأ هذا بصوت عالٍ',
    'read_stmt1': 'أنا أتجنب شيئاً ما.',
    'read_stmt2': 'أنا خائف.',
    'read_stmt3': 'لا أعرف ما أفعله.',
    'read_stmt4': 'أحتاج مساعدة.',
    'read_stmt5': 'أنا كافٍ.',
    'read_stmt6': 'كنت أتظاهر.',
    'read_stmt7': 'أنا متعب.',
    'read_stmt8': 'أريد المزيد.',
    'read_i_read': 'قرأتها بصوت عالٍ',
    'read_really': 'هل قلتها حقاً؟',
    'read_skipped': 'تخطيتها',
    'read_result': 'نطقت بحقائق بصوت عالٍ.',
    'read_quote': 'النطق بالحقيقة يجعلها واقعاً.',
    // Sentence You Avoid
    'sentence_title': 'الجملة التي تتجنبها',
    'sentence_sub': 'أكمل هذه الجملة. لا يمكنك المغادرة حتى تفعل.',
    'sentence_prompt': 'سأكون أسعد لو توقفت عن',
    'sentence_cant_leave': 'لا يمكنك المغادرة دون كتابة شيء.',
    'sentence_knew': 'كنت تعرف هذا بالفعل.',
    'sentence_quote': 'الخطوة الأولى هي قولها.',
    // Say It Once
    'say_once_title': 'قلها مرة واحدة',
    'say_once_prompt': 'سجّل جملة واحدة. لا يمكنك إعادتها. لا يمكنك إعادة التسجيل.',
    'say_once_done': 'قيلت. لا إعادة. لا إعادة تسجيل.',
    'say_once_stop': 'توقف',
    'say_once_record': 'سجّل',
    'say_once_quote': 'بعض الأشياء من المفترض أن تُقال مرة واحدة فقط.',
    // New Year Room
    'newyear_title': 'رسالة إلى نفسك المستقبلية',
    'newyear_threshold': 'العتبة',
    'newyear_placeholder': 'عزيزي أنا المستقبلي...',
    'newyear_seal': 'اختم الرسالة',
    'newyear_waiting': 'هذه الرسالة ستنتظرك.',
    'newyear_quote': 'كل نهاية هي باب. اعبره بنية.',
    // Room You Avoid
    'avoid_title': 'الغرفة التي تتجنبها',
    'avoid_sub': 'ما تتخطاه يقول شيئاً.',
    'avoid_not_entered': 'لم تدخل:',
    'avoid_visited_all': 'زرت كل غرفة مجانية.',
    'avoid_not_pattern': 'ربما التجنب ليس نمطك.',
    'avoid_quote': 'التجنب هو أيضاً اختيار.',
    // Who Without Job
    'who_q1': 'ماذا ستفعل لو لم يكن المال مصدر قلق؟',
    'who_q2': 'كيف ستعرّف نفسك في حفلة؟',
    'who_q3': 'ما الذي يجعلك تشعر بالحياة؟',
    'who_q4': 'ما الذي سيقول أصدقاؤك أنك جيد فيه؟',
    'who_q5': 'ما الذي أحببت فعله كطفل؟',
    'who_result_title': 'أنت، بدون لقب',
    'who_done': 'هذا من أنت عندما يختفي اللقب.',
    'who_quote': 'الهوية ليست ما تفعله. إنها ما يبقى عندما تتوقف.',
    'who_placeholder': 'كن صادقاً...',
    'who_see_myself': 'أرَ نفسي',
    'question': 'سؤال',
    // Last Conversation
    'lastconv_title': 'المحادثة الأخيرة',
    'lastconv_sub': 'اكتب ما لن ترسله أبداً.',
    'lastconv_to': 'إلى:',
    'lastconv_message': 'رسالة:',
    'lastconv_to_placeholder': 'شخص يحتاج أن يسمع هذا...',
    'lastconv_msg_placeholder': 'ما أردت دائماً قوله...',
    'lastconv_not_saved': 'لن تُحفظ هذه الرسالة أو تُشارك.',
    'lastconv_seal': 'اختم الرسالة',
    'lastconv_wrote_to': 'كتبت إلى:',
    'lastconv_never_sent': 'هذه الرسالة لن تُرسل أبداً.',
    'lastconv_point': 'لكن كتابتها كانت الهدف.',
    'lastconv_quote': 'بعض الكلمات تحتاج أن توجد، حتى لو لم تُسمع أبداً.',
    // Real Priorities
    'priorities_title': 'أولوياتك الحقيقية',
    'priorities_sub': 'كم ساعة في الأسبوع تقضيها على كل منها؟',
    'priorities_result': 'أولوياتك الحقيقية',
    'priorities_most_time': 'تقضي معظم وقتك على:',
    'priorities_truth': 'الوقت يكشف الحقيقة. الكلمات يمكن أن تكذب، الجداول لا.',
    'priorities_quote': 'أنت ما تفعله مراراً.',
    'priorities_show': 'أرني حقيقتي',
    'priority_work': 'العمل',
    'priority_family': 'العائلة',
    'priority_friends': 'الأصدقاء',
    'priority_health': 'الصحة',
    'priority_learning': 'التعلم',
    'priority_entertainment': 'الترفيه',
    'priority_social': 'وسائل التواصل',
    'priority_sleep': 'النوم',
    // The Voice Room
    'voice_intro': 'صوت يتحدث في الظلام...',
    'voice_silence': '(صمت)',
    'voice_quote': 'بعض الأصوات لا تجد طريقها للمنزل أبداً.',
    // It Knows Your Name
    'knows_tell': 'أخبرني اسمك.',
    'knows_placeholder': 'اسمك...',
    'knows_warning': '(ما كان يجب أن تأتي إلى هنا)',
    'knows_quote': 'الأسماء لها قوة. اسمك كان معروفاً بالفعل.',
    // The Door
    'door_msg1': 'هناك شيء عند الباب.',
    'door_stopped': '(توقف الطرق)',
    'door_quote': 'بعض الأبواب من المفترض أن تبقى مغلقة.',
    // Eyes In The Dark
    'eyes_msg1': 'عين واحدة تراقب.',
    'eyes_unsee': 'لا يمكنك أن تنسى ما رأيته.',
    'eyes_quote': 'بعض الأشياء، بمجرد ملاحظتها، لا يمكن تجاهلها.',
    // Full Moon Room
    'moon_locked': 'هذه الغرفة تُفتح فقط أثناء البدر.',
    'moon_not_full': 'القمر ليس بدراً الليلة. عد عندما تكتمل الدورة.',
    'moon_quote': 'بعض الحقائق لا تتكشف إلا في دورات.',
    // You At 3:17 AM
    'at317_title': 'أنت في الساعة 3:17 صباحاً',
    'at317_locked': 'هذه الغرفة تُفتح بين 2 و 5 صباحاً.',
    'at317_current': 'الوقت الحالي:',
    'at317_quote': 'الليل يعرف اسمك.',
    // Hallway of Mirrors
    'mirrors_you_are_here': 'أنت هنا.',

    // Listen to Yourself Room
    'listen_title': 'استمع لنفسك',
    'listen_recording': 'جارٍ التسجيل...',
    'listen_start': 'ابدأ',
    'listen_play': 'تشغيل',
    'listen_instruction': 'سجّل 10 ثوانٍ. قل أي شيء.',
    'listen_wait': 'انتظر. ثم استمع.',
    'listen_done': 'كان ذلك صوتك. مبطأ. غريب.',
    'listen_speed': 'التشغيل بسرعة 0.5x',
    'listen_quote': 'صوتك يبدو مختلفاً لأنك سمعته أخيراً.',

    // Export Journey Room
    'export_title': 'تصدير رحلتك',
    'export_subtitle': 'خذ قطعة من هذا المكان معك',
    'export_tab_export': 'تصدير',
    'export_tab_import': 'استيراد',
    'export_your_journey': 'رحلتك',
    'export_since': 'منذ',
    'export_time_spent': 'الوقت المستغرق',
    'export_rooms_visited': 'غرف تمت زيارتها',
    'export_rooms_completed': 'غرف مكتملة',
    'export_fragments': 'شظايا',
    'export_achievements_unlocked': 'إنجازات مفتوحة',
    'export_truths_recorded': 'حقائق مسجلة',
    'export_text_file': 'ملف نصي',
    'export_json_file': 'ملف JSON',
    'export_copied': 'تم النسخ!',
    'export_copy_clipboard': 'نسخ إلى الحافظة',
    'export_data_stays': 'بياناتك تبقى معك. نحن لا نحتفظ بها.',
    'export_import_desc': 'استيراد رحلة تم تصديرها سابقاً',
    'export_json_only': 'صيغة JSON فقط',
    'export_select_file': 'اختر ملفاً',
    'export_merge_note': 'سيتم دمجها مع بيانات رحلتك الحالية.',
    'export_done': 'تم التصدير.',
    'export_done_desc': 'سجل لأين كنت. ما واجهته.',
    'export_quote': 'بعض الرحلات تستحق التوثيق. ليس للآخرين. لنفسك.',
    'export_import_success': 'تم استيراد الرحلة بنجاح',
    'export_import_error': 'ملف رحلة غير صالح',

    // The Waiting Room
    'waitroom_title': 'غرفة الانتظار',
    'waitroom_subtitle': 'بعض الأبواب تفتح فقط للمجموعات.',
    'waitroom_open': 'الباب مفتوح.',
    'waitroom_waiting': 'ينتظرون',
    'waitroom_time_waited': 'الوقت المنتظر',
    'waitroom_users_needed': 'المستخدمون المطلوبون',
    'waitroom_someone_joined': 'انضم شخص ما إلى الانتظار.',
    'waitroom_someone_left': 'غادر شخص ما.',
    'waitroom_room_opens': 'الغرفة تفتح.',
    'waitroom_patience': 'الصبر والحضور يفتحان ما لا يستطيع القوة فتحه.',
    'waitroom_souls_waited': '{count} أرواح انتظرت معاً.',
    'waitroom_seconds_stillness': '{count} ثانية من السكون الجماعي.',
    'waitroom_quote': 'بعض الأشياء لا يمكن استعجالها. بعض الأبواب لا يمكن فتحها وحدك.',

    // Time Capsule Room
    'capsule_title': 'كبسولة الزمن',
    'capsule_subtitle': 'رسالة إلى نفسك المستقبلية',
    'capsule_what_tell': 'ماذا تحتاج أن تقول لنفسك؟',
    'capsule_placeholder': 'عزيزي أنا المستقبلي...',
    'capsule_when_arrive': 'متى يجب أن تصل؟',
    'capsule_30_days': '30 يوماً',
    'capsule_90_days': '90 يوماً',
    'capsule_1_year': 'سنة واحدة',
    'capsule_seal': 'اختم الكبسولة',
    'capsule_waiting': 'كبسولة(كبسولات) في الانتظار',
    'capsule_sealed': 'مختومة.',
    'capsule_will_wait': 'رسالتك ستنتظرك.',
    'capsule_return_in': 'عد بعد {days} يوماً لفتحها.',
    'capsule_quote_sealed': 'الزمن يغير كل شيء. حتى أنت.',
    'capsule_arrived': 'وصلت رسالة.',
    'capsule_from_past': 'من أنت قبل {days} يوماً.',
    'capsule_open': 'افتح الكبسولة',
    'capsule_you_wrote': 'كتبت هذا لنفسك:',
    'capsule_remember': 'هل تتذكر كتابة هذا؟',
    'capsule_quote_opened': 'الشخص الذي كتب هذا رحل. أنت باقٍ.',

    // Contradiction Room
    'contradiction_title': 'غرفة التناقض',
    'contradiction_subtitle': 'حيث تتصادم حقائقك',
    'contradiction_not_enough': 'لا توجد بيانات كافية بعد.',
    'contradiction_answer_more': 'أجب عن المزيد من الأسئلة عبر الغرف.',
    'contradiction_answers_recorded': '{count}/5 إجابات مسجلة',
    'contradiction_quote_early': 'التناقضات تتطلب تعقيداً. ابنِ تعقيدك أولاً.',
    'contradiction_no_found': 'لم يتم العثور على تناقضات.',
    'contradiction_consistent': 'إما أنك متسق بشكل ملحوظ...',
    'contradiction_careful': '...أو حذر بشكل ملحوظ.',
    'contradiction_quote_none': 'الاتساق إما حكمة أو أداء.',
    'contradiction_found': 'وجدنا {count} تناقض(تناقضات)',
    'contradiction_you_said': 'قلت:',
    'contradiction_but_also': 'لكن أيضاً:',
    'contradiction_see_another': 'شاهد آخر ←',
    'contradiction_not_weakness': 'التناقض ليس ضعفاً. إنه دليل على العمق.',
    'contradiction_quote': 'نحن نحتوي على تعددات. بعضها يختلف.',

    // Entropy Garden
    'entropy_living': 'حية',
    'entropy_dead': 'ميتة',
    'entropy_plant_seed': 'ازرع بذرة',
    'entropy_instructions': 'انقر على الأماكن الفارغة للزراعة. انقر على النباتات لسقيها. يمكن إزالة النباتات الميتة.',
    'entropy_reflect': 'تأمل في حديقتك',
    'entropy_lives': 'حديقتك حية.',
    'entropy_thrive': '{living} نبتة تزدهر لأنك عدت.',
    'entropy_absence': '{dead} تذكرك بالغياب.',

    // Memory Palace
    'palace_title': 'قصر الذاكرة',
    'palace_subtitle': 'ابنِ مساحة ذهنية. ضع أشياء لتتذكرها. تجوّل فيها لاحقاً.',
    'palace_room_of': 'الغرفة {current} من {total}',
    'palace_click_place': 'انقر للوضع',
    'palace_what_represent': 'ماذا يمثل هذا؟',
    'palace_cancel': 'إلغاء',
    'palace_place_object': 'ضع شيئاً',
    'palace_objects_count': '{count} شيء(أشياء) في قصرك',
    'palace_take_tour': 'ابدأ الجولة',
    'palace_empty_room': 'هذه الغرفة فارغة',
    'palace_previous': 'السابق',
    'palace_next_room': 'الغرفة التالية',
    'palace_complete_tour': 'أكمل الجولة',
    'palace_remembers': 'قصرك يتذكر.',
    'palace_geography': 'جغرافيا من المعنى بنيتها.',
    'palace_quote': 'العقل قصر. ما تضعه فيه يحدد من تصبح.',
    // Anonymous Echoes Room
    'echoes_subtitle': 'شظايا من مسافرين آخرين',
    'echoes_from': 'من',
    'echoes_ago': 'منذ',
    'echoes_arriving': 'المزيد من الأصداء قادمة...',
    'echoes_reveal': 'هذه ليست إجاباتهم. إنها إجاباتك.',
    'echoes_reveal2': '(كل صدى كان ذات مرة انعكاسك)',
    'echoes_quote': 'نظن أننا فريدون. نحن مجرد أصداء لبعضنا البعض.',
    // The Revisit Room
    'revisit_subtitle': 'كيف تغيرت',
    'revisit_not_enough': 'لم تجب على أسئلة كافية بعد.',
    'revisit_explore': 'استكشف المزيد من الغرف. اترك بصماتك. عد لاحقاً.',
    'revisit_quote1': 'التغيير يتطلب تاريخاً. التاريخ يتطلب وقتاً.',
    'revisit_none_changed': 'لكن لم يتغير أي منها بعد. أعد زيارة الغرف. أجب مرة أخرى.',
    'revisit_show_evolved': 'سنريك كيف تطورت.',
    'revisit_quote2': 'لترى التغيير، يجب أن تتغير أولاً.',
    'revisit_then': 'حينها',
    'revisit_now': 'الآن',
    'revisit_one_shift': 'تحول واحد. خفي لكنه حقيقي.',
    'revisit_each_change': 'كل تغيير هو موت صغير. وولادة صغيرة.',
    'revisit_quote3': 'لست من كنت. ولست بعد من ستكون.',
  },

  es: {
    // Index
    'title': 'LAS HABITACIONES HUMANAS',
    'tagline': 'Una colección de habitaciones que reflejan quién eres cuando nadie te mira.',
    'enter': 'Entrar',
    'footer': 'No es terapia. No es social. No es productivo.',
    
    // Hallway
    'back': '← Volver',
    'hallway': 'El Pasillo',
    'challenges': 'Desafíos',
    'random': 'Aleatorio',
    'open_rooms': 'Habitaciones Abiertas',
    'dark_rooms': 'Las Habitaciones Oscuras',
    'enter_risk': 'entra bajo tu propio riesgo',
    'premium_rooms': 'Habitaciones Premium',
    'unlock_to_access': 'Desbloquear para acceder',
    'daily_rooms': 'Habitaciones Diarias',
    'refreshes_daily': 'se actualiza cada día',
    'time_locked': 'Habitaciones Bloqueadas por Tiempo',
    'seasonal_rooms': 'Habitaciones Estacionales',
    'available_now': 'Disponible ahora',
    'fragments': 'Fragmentos',
    'boss_unlocked': 'Sala del Jefe Desbloqueada',
    'collect_unlock': 'Colecta para desbloquear la Sala del Jefe',
    'dark_fragments': 'Fragmentos Oscuros',
    'dark_boss_unlocked': 'Jefe Oscuro Desbloqueado',
    'hidden_discovered': 'Habitación Oculta Descubierta',
    'stillness_revealed': 'Tu quietud reveló algo...',
    'constellation_map': 'Mapa de Constelaciones',
    'your_stats': 'Tus Estadísticas',
    'completed': 'Completado',
    'game_rooms': 'Salas de Juego',
    'experimental_rooms': 'Salas Experimentales',
    'deep_rooms': 'Salas Profundas',
    'meta_rooms': 'Salas Meta',
    'search_rooms': 'Buscar salas...',
    'no_results': 'No hay salas que coincidan',
    'clear_filters': 'Limpiar filtros',
    'explore': 'Explorar',
    'all_categories': 'Todas',
    
    // About
    'what_is_this': '¿Qué es este lugar?',
    'not_productivity': 'Las Habitaciones Humanas no es una herramienta de productividad.',
    'not_social': 'No es una red social.',
    'not_therapy': 'No es terapia.',
    'pause_experiences': 'Es una colección de pequeñas experiencias diseñadas para hacerte pausar.',
    'each_room': 'Cada habitación toma de 30 segundos a 2 minutos.',
    'make_think': 'Algunas habitaciones te harán pensar.',
    'make_uncomfortable': 'Algunas te harán sentir incómodo.',
    'make_calm': 'Algunas te calmarán.',
    'make_aware': 'Todas te harán consciente.',
    'no_notifications': 'No hay notificaciones aquí.',
    'no_points': 'No hay puntos que coleccionar.',
    'no_validation': 'Sin validación social.',
    'just_truth': 'Solo tú, una habitación, y la verdad.',
    'enter_rooms': 'Entrar a las habitaciones',
    
    // Common
    'loading': 'Cargando...',
    'error': 'Error',
    'continue': 'Continuar',
    'submit': 'Enviar',
    'cancel': 'Cancelar',
    'close': 'Cerrar',
    'next': 'Siguiente',
    'previous': 'Anterior',
    'start': 'Iniciar',
    'stop': 'Detener',
    'yes': 'Sí',
    'no': 'No',
    'leave_room': '← Salir de la habitación',
    'return': '← Volver',
    'return_hallway': 'Volver al Pasillo',
    'escape': '← Escapar',
    'run': '← Huir',
    'leave': '← Salir',
    'begin': 'Comenzar',
    'watch': 'Mira...',
    'correct': 'Correcto',
    'round': 'Ronda',
    'enter_again': 'Entrar de nuevo',
    'steps': 'Pasos',
    'depth': 'Profundidad',
    
    // Keyboard Shortcuts
    'keyboard_shortcuts': 'Atajos de Teclado',
    'shortcut_hallway': 'Ir al Pasillo',
    'shortcut_random': 'Habitación Aleatoria',
    'shortcut_back': 'Volver',
    'shortcut_bookmark': 'Alternar Marcador',
    'shortcut_reading': 'Modo Lectura',
    'shortcut_search': 'Enfocar Búsqueda',
    'shortcut_stats': 'Ver Estadísticas',
    'shortcut_achievements': 'Ver Logros',
    'shortcut_cat_nav': 'Navegación',
    'shortcut_cat_rooms': 'Habitaciones',
    'shortcut_cat_access': 'Accesibilidad',
    'press_to_close': 'Presiona',
    'to_close': 'para cerrar',
    'or': 'o',
    
    // Bookmarks
    'add_bookmark': 'Añadir Marcador',
    'remove_bookmark': 'Quitar Marcador',
    'bookmarks': 'Marcadores',
    'bookmarked_rooms': 'Marcados',
    'no_bookmarks': 'Sin marcadores aún',
    
    // Reading Mode
    'reading_mode': 'Modo Lectura',
    'reading_mode_on': 'Modo lectura activado',
    'reading_mode_off': 'Modo lectura desactivado',
    
    // Updates Page
    'updates': 'Actualizaciones',
    'updates_title': 'Novedades',
    'updates_subtitle': 'Cambios y mejoras recientes',
    'updates_footer': 'Construyendo habitaciones, una actualización a la vez.',
    'new': 'NUEVO',
    'subscribe_rss': 'Suscribirse vía RSS',
    'latest': 'ÚLTIMO',
    'whats_new_intro': 'Últimas actualizaciones:',
    'view_all_updates': 'Ver todas las actualizaciones',
    'update_updates_page': 'Página de actualizaciones añadida',
    'update_full_i18n': 'Traducción completa a 6 idiomas',
    'update_hallway_animations': 'Animaciones suaves al cambiar categorías',
    'update_daily_challenges': 'Mejora en los desafíos diarios',
    'update_game_rooms': '15 nuevas salas de juego',
    'update_experimental_rooms': '14 salas experimentales',
    'update_random_room': 'Botón de habitación aleatoria',
    'update_stats_route': 'Ruta de estadísticas corregida',
    'update_streak_system': 'Sistema de rachas diarias',
    'update_weekly_summary': 'Resúmenes de progreso semanal',
    'update_rtl_support': 'Soporte de idiomas RTL',
    'update_dark_rooms': 'Nueva categoría de habitaciones oscuras',
    'update_boss_rooms': 'Salas de jefe con fragmentos',
    'update_audio_system': 'Audio ambiental en habitaciones',
    'update_constellation_map': 'Mapa de constelaciones del viaje',
    'update_fragment_system': 'Sistema de fragmentos de habitaciones',
    'update_mobile_layout': 'Mejoras en diseño móvil',
    'update_multi_language': 'Soporte multiidioma (6 idiomas)',
    'update_premium_system': 'Sistema de habitaciones premium',
    'update_room_endings': 'Citas finales únicas',
    'update_ui_redesign': 'Rediseño completo de la interfaz',
    'update_theme_effects': 'Efectos visuales por tema (brasas, estrellas, glitches)',
    'update_void_theme': 'Nuevo tema Vacío - modo oscuridad absoluta',
    'update_achievements': 'Sistema de logros con 19 hitos',
    'update_ai_reflection': 'Sala de reflexión con IA',
    'update_settings_redesign': 'Configuraciones rediseñadas con 4 temas',
    'update_theme_exclusive_rooms': 'Salas exclusivas por tema',
    'update_discovery_localization': 'Mensajes de descubrimiento traducidos',
    'update_site_mood_system': 'Sistema de estado de ánimo del sitio',
    'update_voice_imperfection': 'Salas con voz imperfecta',
    'update_mobile_optimization': 'Optimización móvil con objetivos de 48px',
    'update_interaction_clues': 'Pistas sutiles de interacción',
    'update_theme_exclusive_hallway': 'Salas exclusivas visibles en el pasillo',
    'update_advanced_easter_eggs': 'Easter eggs avanzados: secuencia de esquinas, morse, visitante nocturno',
    'update_hidden_discoveries': 'Revelaciones de texto oculto',
    'update_dual_mode_system': 'Sistema dual Clásico y Alternativo',
    'update_alternate_mode': 'Modo alternativo - una realidad paralela experimental',
    'update_hidden_discovery': 'Disparadores de descubrimiento ocultos: triple toque, patrones de scroll',
    'update_mode_effects': 'Efectos visuales específicos por modo, glitches y audio ambiental',
    'update_translation_navigator': 'Navegador de Traducción - panel de cobertura visual',
    'update_full_localization': 'Cobertura de traducción casi completa en 6 idiomas',
    'update_meta_room_translations': 'Contenido de salas meta traducido (La Negativa, Tú Paralelo, Tu Sombra)',
    'update_animated_timeline': 'Línea de tiempo animada con transiciones al desplazar',
    'update_toast_notifications': 'Notificación emergente para nuevas actualizaciones',
    'update_rss_feed': 'Feed RSS para suscripciones de cambios',
    'update_bookmarked_filter': 'Pestaña de Habitaciones Marcadas en el Pasillo',
    'update_keyboard_shortcuts': 'Atajos de teclado (presiona ? para ver)',
    'update_reading_mode': 'Modo lectura para mejor tipografía y enfoque',
    'update_accessibility': 'Indicadores de enfoque y navegación con teclado mejorados',
    'update_ambient_crossfade': 'Transiciones suaves entre pistas de audio ambiental',
    'update_settings_audio': 'Controles de audio centralizados en Configuración',
    'update_arabic_complete': 'Cobertura completa de traducción árabe',
    'update_duplicate_icon_fix': 'Iconos de audio duplicados corregidos en el encabezado',

    // Game UI
    'game_watching': 'observando...',
    'game_repeat': 'repetir',
    'game_echo_fades': 'el eco se desvanece...',
    'game_escaped': 'Escapaste.',
    'game_found_exit': 'Encontraste la salida.',
    'game_looping': 'Estás en un bucle.',
    'game_deeper': 'Más profundo...',
    'game_doors_shift': 'Las puertas cambian.',
    'maze_left': 'izquierda',
    'maze_right': 'derecha',
    'maze_forward': 'adelante',
    'maze_back': 'atrás',
    'maze_past': 'pasado',
    'maze_future': 'futuro',
    'maze_here': 'aquí',
    'maze_there': 'allí',
    'maze_truth': 'verdad',
    'maze_lies': 'mentiras',
    'maze_light': 'luz',
    'maze_dark': 'oscuridad',
    'maze_memory': 'memoria',
    'maze_dream': 'sueño',
    'maze_silence': 'silencio',
    'maze_noise': 'ruido',
    'random_room': 'Habitación Aleatoria',
    'ai_reflection': 'Reflexión IA',
    'support': 'Soporte',
    'choose_direction': 'Elige una dirección. Algunas puertas responden.',
    'rooms_explored': 'habitaciones exploradas',
    'your_journey': 'Tu Viaje',
    'complete_rooms_fragments': 'Completa habitaciones para recolectar fragmentos.',
    'no_rooms_completed': 'Ninguna habitación completada aún.',
    
    // Stats
    'total_time': 'Tiempo Total',
    'rooms_visited': 'Habitaciones Visitadas',
    'total_visits': 'Total de Visitas',
    'most_visited': 'Más Visitada',
    'longest_session': 'Sesión Más Larga',
    'fragments_collected': 'Fragmentos Recolectados',
    'completed_rooms': 'Habitaciones Completadas',
    'privacy_note': 'Todos los datos se almacenan localmente en tu dispositivo',
    
    // Daily Challenges
    'daily_challenges': 'Desafíos Diarios',
    'challenges_completed': 'completado',
    'challenges_total': 'total',
    'resets_midnight': 'Se reinicia a medianoche',
    
    // Weekly Summary
    'weekly_summary': 'Resumen Semanal',
    'week': 'Semana',
    'this_week': 'Esta Semana',
    'rooms': 'Habitaciones',
    'days': 'Días',
    'vs_last_week': 'vs semana pasada',
    'refresh_insight': 'Actualizar perspectiva',
    
    // Game Room Titles
    'room_memory-puzzle': 'Rompecabezas de Memoria',
    'room_shadow-chase': 'Persecución de Sombras',
    'room_timing-room': 'Sala de Tiempo',
    'room_pattern-whisper': 'Susurro de Patrones',
    'room_silence-puzzle': 'Rompecabezas del Silencio',
    'room_decay-game': 'Juego de Decadencia',
    'room_observation-test': 'Prueba de Observación',
    'room_balance-room': 'Sala del Equilibrio',
    'room_word-drift': 'Deriva de Palabras',
    'room_color-echo': 'Eco de Color',
    'room_mirror-tap': 'Toque del Espejo',
    'room_maze-of-doors': 'Laberinto de Puertas',
    'room_pulse-match': 'Coincidencia de Pulso',
    'room_light-path': 'Camino de Luz',
    'room_sound-match': 'Coincidencia de Sonido',
    
    // Game Room Subtitles
    'sub_memory-puzzle': 'Combina patrones que cambian sutilmente',
    'sub_shadow-chase': 'Atrapa lo que se mueve impredeciblemente',
    'sub_timing-room': 'Haz clic exactamente en el momento',
    'sub_pattern-whisper': 'Repite la secuencia que se desvanece',
    'sub_silence-puzzle': 'Resuelve sin instrucciones',
    'sub_decay-game': 'Toca para preservar lo que se desvanece',
    'sub_observation-test': 'Encuentra diferencias sutiles',
    'sub_balance-room': 'Mantén los objetos flotantes alineados',
    'sub_word-drift': 'Atrapa las letras antes de que caigan',
    'sub_color-echo': 'Repite la secuencia de tonos sutiles',
    'sub_mirror-tap': 'Cada toque resuena en otro lugar',
    'sub_maze-of-doors': 'Las puertas se reorganizan después de cada elección',
    'sub_pulse-match': 'Muévete al ritmo de la sala',
    'sub_light-path': 'Guía la luz a través de la oscuridad cambiante',
    'sub_sound-match': 'Toca formas para coincidir tonos suaves',
    
    // Experimental Room Titles
    'room_mirror-room': 'Sala del Espejo',
    'room_echo-room': 'Sala del Eco',
    'room_pulse-room': 'Sala del Pulso',
    'room_vanishing-room': 'Sala que Desvanece',
    'room_letter-room': 'Sala de Cartas',
    'room_empty-room': 'Sala Vacía',
    'room_reverse-room': 'Sala Inversa',
    'room_whisper-room': 'Sala del Susurro',
    'room_shadow-room': 'Sala de Sombras',
    'room_labyrinth-room': 'Sala del Laberinto',
    'room_color-room': 'Sala de Colores',
    'room_surrender-room': 'Sala de Rendición',
    'room_noise-room': 'Sala del Ruido',
    'room_observer-room': 'Sala del Observador',
    
    // Experimental Room Subtitles
    'sub_mirror-room': 'Reflejos ligeramente distorsionados',
    'sub_echo-room': 'Los clics desencadenan ecos retardados',
    'sub_pulse-room': 'El ambiente respira',
    'sub_vanishing-room': 'Los elementos se desvanecen al interactuar',
    'sub_letter-room': 'Las palabras se revuelven como memoria fallida',
    'sub_empty-room': 'Vacío hasta que encuentres secretos',
    'sub_reverse-room': 'Las acciones se comportan al revés',
    'sub_whisper-room': 'Verdades apenas audibles',
    'sub_shadow-room': 'Tu compañero oscuro',
    'sub_labyrinth-room': 'Encuentra tu camino',
    'sub_color-room': 'Meditación cromática',
    'sub_surrender-room': 'Suelta el control',
    'sub_noise-room': 'Pensamientos estáticos',
    'sub_observer-room': 'Siendo observado',
    
    // Daily Joke Room
    'joke_number': 'Chiste #{number}',
    'joke_today': 'El Chiste de Hoy',
    'joke_reveal': 'Revelar el remate',
    'joke_come_back': 'Vuelve mañana para un nuevo chiste.',
    'joke_quote': 'La risa es la distancia más corta entre dos personas.',
    
    // Daily Quote Room
    'quote_day_number': 'Día {day}',
    'quote_come_back': 'Vuelve mañana para una nueva cita.',
    
    // Leaderboard
    'leaderboard': 'Tabla de la Comunidad',
    'leaderboard_subtitle': 'anónimo · colectivo · sin competencia',
    'collective_time': 'tiempo colectivo',
    'achievements_count': 'logros',
    'most_visited_rooms': 'Salas más visitadas',
    'most_time_spent': 'Más tiempo dedicado',
    'most_unlocked_achievements': 'Logros más desbloqueados',
    'no_achievements_yet': 'Aún no se han desbloqueado logros.',
    
    // Transfer Data
    'transfer_data': 'Transferir datos',
    'transfer_data_desc': 'Mueve tu progreso a otro dispositivo usando un código de un solo uso.',
    'generate_transfer_code': 'Generar código de transferencia',
    'enter_transfer_code': 'Ingresa el código...',
    'transfer_code_expires': 'El código expira en 24 horas y solo se puede usar una vez.',
    'generating': 'Generando...',
    'import': 'Importar',
    'copied': '¡Copiado!',
    
    // Waiting Game Room
    'waiting_instruction': 'No te muevas. No hagas clic. Solo espera.',
    'waiting_slow': 'Ir más despacio es más difícil de lo que parece.',
    'waiting_unfamiliar': 'Esta quietud se siente desconocida, ¿verdad?',
    'waiting_most': 'La mayoría se va a estas alturas.',
    'waiting_learning': 'Estás aprendiendo algo sobre ti mismo.',
    'waiting_rewards': 'La paciencia tiene sus propias recompensas.',
    'waiting_seconds': 'segundos de quietud',
    'waiting_waiting': 'esperando...',
    'waiting_moved': 'Te has movido {count} veces.',
    'waiting_waited': 'Esperaste. Eso es raro.',
    'waiting_most_cant': 'La mayoría no puede quedarse quieta. Tú lo hiciste.',
    'waiting_quote': 'El momento presente es el único momento disponible para nosotros, y es la puerta a todos los momentos.',
    
    // Settings Page
    'settings': 'Configuración',
    'settings_subtitle': 'Personaliza tu viaje',
    'language': 'Idioma',
    'ambient_audio': 'Audio Ambiental',
    'ambient_audio_desc': 'Sonidos de fondo para las salas',
    'reduced_motion': 'Movimiento Reducido',
    'reduced_motion_desc': 'Minimizar animaciones',
    'data_management': 'Gestión de Datos',
    'export_journey': 'Exportar Datos del Viaje',
    'clear_all_data': 'Borrar Todos los Datos',
    'audio_enabled': 'Audio Activado',
    'audio_disabled': 'Audio Desactivado',
    'data_exported': 'Datos Exportados',
    'clear_data_confirm': '¿Estás seguro? Esto borrará todo tu progreso.',
    'data_cleared': 'Datos Borrados',
    // Theme settings
    'theme': 'Tema',
    'theme_dark': 'Oscuro',
    'theme_dark_desc': 'Oscuridad pura',
    'theme_bone': 'Hueso',
    'theme_bone_desc': 'Palidez mortal',
    'theme_ash': 'Ceniza',
    'theme_ash_desc': 'Restos del fuego',
    'theme_fog': 'Niebla',
    'theme_fog_desc': 'Incertidumbre etérea',
    'theme_twilight': 'Crepúsculo',
    'theme_twilight_desc': 'Entre día y noche',
    'theme_eclipse': 'Eclipse',
    'theme_eclipse_desc': 'Luz devorada por la oscuridad',
    'theme_dawn': 'Amanecer',
    'theme_dawn_desc': 'Primera luz del despertar',
    'theme_changed': 'Tema actualizado',
    'font_size': 'Tamaño de Fuente',
    'font_small': 'Pequeño',
    'font_medium': 'Mediano',
    'font_large': 'Grande',
    'font_size_changed': 'Tamaño de fuente actualizado',
    'high_contrast': 'Alto Contraste',
    'high_contrast_desc': 'Aumentar contraste de texto y bordes',
    'high_contrast_enabled': 'Alto contraste activado',
    'high_contrast_disabled': 'Alto contraste desactivado',
    'theme_void': 'Vacío',
    'theme_void_desc': 'Solo quedan sombras',
    
    // Ambient Music Control
    'ambient_music': 'Música Ambiental',
    'ambient_music_desc': 'Controla los paisajes sonoros de fondo',
    'volume': 'Volumen',
    'ambient_track': 'Pista Ambiental',
    'playing': 'Reproduciendo',
    'play': 'Reproducir',
    'ambient_default': 'Calma',
    'ambient_night': 'Noche',
    'ambient_scary': 'Espeluznante',
    'ambient_truth': 'Verdad',
    'ambient_identity': 'Reflejo',
    'ambient_mortality': 'Tiempo',
    'ambient_nothing': 'Vacío',
    'ambient_meta': 'Digital',
    'ambient_daily': 'Cálido',
    'ambient_seasonal': 'Mágico',
    'ambient_premium': 'Profundo',
    
    // Easter Egg Discoveries
    'discovery_label': 'Descubrimiento',
    'discovery_konami': 'Conoces los viejos caminos.',
    'discovery_secret_sequence': 'Deletreaste la verdad.',
    'discovery_long_press': 'La paciencia revela secretos.',
    'discovery_triple_click': 'La persistencia da frutos.',
    'discovery_silence': 'La quietud habla.',
    
    // Theme-Exclusive Rooms
    'ash_exclusive': 'Exclusivo del tema Ceniza',
    'twilight_exclusive': 'Exclusivo del tema Crepúsculo',
    'void_exclusive': 'Exclusivo del tema Vacío',
    'theme_required': 'Requiere tema {theme}',
    'ash_embers_title': 'De las Cenizas',
    'ash_embers_line1': 'Todo arde eventualmente.',
    'ash_embers_line2': 'Lo que importa es lo que surge después.',
    'ash_embers_line3': 'Toca la pantalla. Crea brasas.',
    'ash_embers_quote': 'De las cenizas, recordamos lo que una vez ardió.',
    'room_ash-embers': 'Cenizas y Brasas',
    'sub_ash-embers': 'Solo visible en tema Ceniza',
    'twilight_stars_title': 'Entre Día y Noche',
    'twilight_stars_line1': 'Las estrellas recuerdan a todos los que miraron hacia arriba.',
    'twilight_stars_line2': 'Conéctalas. Haz tu propia constelación.',
    'twilight_stars_quote': 'En el crepúsculo, vemos lo que la luz del día oculta.',
    'room_twilight-stars': 'Estrellas del Crepúsculo',
    'sub_twilight-stars': 'Solo visible en tema Crepúsculo',
    'void_abyss_title': 'El Abismo',
    'void_abyss_line1': 'Mira al vacío. El vacío te devuelve la mirada.',
    'void_word_1': 'nada',
    'void_word_2': 'vacío',
    'void_word_3': 'silencio',
    'void_word_4': 'ido',
    'void_word_5': 'solo',
    'void_word_6': 'fin',
    'void_revealed': 'revelado',
    'void_abyss_quote': 'En la oscuridad absoluta, finalmente nos vemos.',
    'room_void-abyss': 'El Abismo',
    'sub_void-abyss': 'Solo visible en tema Vacío',
    'shared_room': 'Sala Compartida',
    'imagine_room': 'Imaginar',
    'imagine_title': 'Imagínate',
    'imagine_subtitle': 'Describe cómo te ves. Deja que la sala te muestre lo que emerge de tus palabras.',
    'imagine_placeholder': 'Me veo como alguien que...\n\nDescribe tu esencia, tu naturaleza, cómo te sientes por dentro.',
    'imagine_hint': 'Sé honesto. La imagen refleja tus palabras.',
    'imagine_generate': 'Ver Yo',
    'imagine_generating': 'Creando...',
    'imagine_try_again': 'Describir de Nuevo',
    'imagine_quote': 'No nos vemos claramente. Vemos lo que creemos.',
    'room_imagine-yourself': 'Imagínate',
    'sub_imagine-yourself': 'Descríbete. Ve lo que emerge.',
    
    // Theme Exclusive Section
    'theme_exclusive_rooms': 'Salas Exclusivas por Tema',
    'theme_exclusive_hint': 'Cambia el tema en configuración para desbloquear estas salas.',
    'requires_theme': 'Requiere',
    'unlocked': 'Desbloqueado',
    'switch_to': 'Cambiar a',
    
    // Advanced Easter Eggs
    'corner_sequence': 'Trazaste los límites.',
    'morse_sos': 'S.O.S. recibido.',
    'reverse_scroll': 'Volviendo... pero más rápido.',
    'midnight_visitor': 'La hora de las brujas te da la bienvenida.',
    'shake': 'Sacudiste algo.',
    
    // Experience Mode
    'classic_mode': 'Clásico',
    'alternate_mode': 'Alternativo',
    'switch_to_alternate': 'Entrar a realidad alternativa',
    'switch_to_classic': 'Volver al clásico',
    'enter_parallel_reality': 'Entrar a una realidad paralela',
    'return_to_familiar': 'Volver a lo familiar',
    'something_shifted': 'Algo cambió',
    'reality_feels_different': 'La realidad se siente diferente ahora.',
    'alternate_tagline': 'Las mismas habitaciones. Verdades diferentes.',
    'alternate_enter': 'Descender',
    'alternate_footer': 'Las cosas no son lo que parecen.',
    'alternate_rooms': '░▓ Re4l1dad P4ralela ▓░',
    'alternate_rooms_hint': '[ estas hab1taciones solo ex1sten en el paralelo ]',
    'experience_mode': 'Modo de Experiencia',
    'classic_mode_desc': 'La realidad familiar',
    'alternate_mode_desc': 'Una dimensión paralela',
    
    // Alternate Mode Exclusive Rooms (Spanish)
    'room_the-other-side': 'El Otro Lado',
    'room_the-unraveling': 'El Deshilado',
    'room_the-echo': 'El Eco',
    'sub_the-other-side': 'muévete lentamente en la oscuridad',
    'sub_the-unraveling': 'la realidad se deshace con cada palabra',
    'sub_the-echo': 'lo que dices nunca se va',
    
    // Meta Rooms (Spanish)
    'room_the-refusal': 'El Rechazo',
    'sub_the-refusal': 'A veces el sitio te rechaza',
    'room_parallel-you': 'Tú Paralelo',
    'sub_parallel-you': 'Otra versión de ti',
    'room_your-shadow': 'Tu Sombra',
    'sub_your-shadow': 'Lo que te sigue',
    
    // AI Rooms (Spanish)
    'room_ai-reflection-room': 'Sala de Reflexión IA',
    'room_dream-visualization': 'Visualización de Sueños',
    'room_future-self': 'Tu Yo Futuro',
    'sub_ai-reflection-room': 'Deja que la IA reflexione contigo',
    'sub_dream-visualization': 'Describe tu sueño. Míralo tomar forma.',
    'sub_future-self': 'Un mensaje del futuro',
    
    // Meta Room Content - The Refusal (Spanish)
    'refusal_line1': 'Esta sala te rechaza.',
    'refusal_line2': 'No todo se abre cuando tú quieres.',
    'refusal_line3': 'A veces la respuesta es simplemente: no.',
    'refusal_line4': 'Prueba el botón. No ayudará.',
    'refusal_button': 'Entrar a la Sala',
    
    // Meta Room Content - Parallel You (Spanish)
    'parallel_choice_1': 'Elegiste la velocidad.',
    'parallel_alternate_1': 'Otro tú eligió la paciencia.',
    'parallel_reflection_1': 'Todavía están esperando.',
    'parallel_choice_2': 'Te fuiste temprano.',
    'parallel_alternate_2': 'Otro tú se quedó hasta el final.',
    'parallel_reflection_2': 'Vieron lo que te perdiste.',
    'parallel_choice_3': 'Evitaste la pregunta difícil.',
    'parallel_alternate_3': 'Otro tú la respondió.',
    'parallel_reflection_3': 'Cargan esa verdad ahora.',
    'parallel_choice_4': 'Pasaste corriendo.',
    'parallel_alternate_4': 'Otro tú saboreó cada momento.',
    'parallel_reflection_4': 'Recuerdan más.',
    'parallel_choice_5': 'Seguiste desplazándote.',
    'parallel_alternate_5': 'Otro tú se detuvo.',
    'parallel_reflection_5': 'Encontraron algo.',
    'parallel_choice_6': 'Elegiste la comodidad.',
    'parallel_alternate_6': 'Otro tú eligió la incomodidad.',
    'parallel_reflection_6': 'Crecieron con ello.',
    'parallel_quote': 'Cada momento se divide en caminos infinitos. Solo caminas uno.',
    
    // Meta Room Content - Your Shadow (Spanish)
    'shadow_generated': 'Generado desde tu comportamiento',
    'shadow_intro': 'Tu sombra...',
    'shadow_follows': 'Esta sombra te sigue por cada sala.',
    'shadow_quote': 'No somos lo que pensamos. Somos lo que hacemos.',
    'shadow_trait_completion': 'busca completar',
    'shadow_trait_surface': 'prefiere la superficie',
    'shadow_trait_lingers': 'permanece en espacios',
    'shadow_trait_moves_quickly': 'se mueve rápido',
    'shadow_trait_avoids_depth': 'evita la profundidad',
    'shadow_trait_stillness': 'abraza la quietud',
    'shadow_trait_chases': 'persigue algo sin nombre',
    'shadow_trait_darkness': 'atraído por la oscuridad',
    'shadow_trait_emptiness': 'cómodo con el vacío',
    'shadow_trait_undefined': 'indefinido',
    'shadow_trait_forming': 'aún formándose',
    
    // Translation Navigator (Spanish)
    'translation_navigator': 'Navegador de Traducciones',
    'translation_coverage': 'Cobertura de traducciones por idioma',
    'keys': 'claves',
    'room_translations': 'Traducciones de Salas',
    'show_all': 'Mostrar Todo',
    'show_missing': 'Mostrar Faltantes',
    'all_translated': '¡Todas las salas están traducidas!',
    'missing_translations': 'traducciones faltantes',
    
    // Additional Room Titles (Spanish)
    'room_how-many-times-left': 'Cuántas Veces Quedan',
    'room_life-in-weeks': 'Tu Vida en Semanas',
    'room_delay-button': 'El Botón de Retraso',
    'room_one-honest-sentence': 'Una Frase Honesta',
    'room_this-moment': 'Este Momento No Se Repetirá',
    'room_reality-receipt': 'Recibo de Realidad',
    'room_nothing-happens': 'Aquí No Pasa Nada',
    'room_default-choice': 'Tu Elección por Defecto',
    'room_thought-loop': 'El Bucle de Pensamientos',
    'room_expect-vs-happens': 'Lo que Esperas vs Lo que Pasa',
    'room_question-never-answer': 'La Pregunta que Nunca Respondes',
    'room_not-special': 'No Eres Especial (Y Está Bien)',
    'room_shortcut-always-take': 'El Atajo que Siempre Tomas',
    'room_blurred-self': 'Yo Borroso',
    'room_image-zooming': 'La Imagen que Amplías',
    'room_comfort-color': 'El Color de Confort',
    'room_line-you-crossed': 'La Línea que Cruzaste',
    'room_read-aloud': 'Lee Esto en Voz Alta',
    'room_voice-without-meaning': 'Tu Voz Sin Significado',
    'room_sentence-you-avoid': 'La Frase que Evitas',
    'room_if-nothing-changes': 'Si Nada Cambia',
    'room_real-priorities': 'Tus Prioridades Reales',
    'room_last-conversation': 'La Última Conversación',
    'room_who-without-job': '¿Quién Eres Sin Tu Trabajo?',
    'room_comfort-zone-scanner': 'Escáner de Zona de Confort',
    'room_say-it-once': 'Dilo Una Vez',
    'room_listen-to-yourself': 'Escúchate',
    'room_life-as-shapes': 'Tu Vida como Formas',
    'room_face-without-features': 'Rostro Sin Rasgos',
    'room_what-keeps-awake': 'Lo que Te Mantiene Despierto',
    'room_you-at-317am': 'Tú a las 3:17 AM',
    'room_thought-that-returns': 'El Pensamiento que Regresa',
    'room_room-you-visit-most': 'La Sala que Más Visitas',
    'room_room-you-avoid': 'La Sala que Evitas',
    'room_left-too-early': 'Te Fuiste Muy Pronto',
    'room_daily-quote': 'Cita del Día',
    'room_daily-joke': 'Chiste del Día',
    'room_theyre-watching': 'Te Están Observando',
    'room_the-last-visitor': 'El Último Visitante',
    'room_dont-look-away': 'No Mires a Otro Lado',
    'room_the-whisper': 'El Susurro',
    'room_you-forgot-something': 'Olvidaste Algo',
    'room_the-mirror-lies': 'El Espejo Miente',
    'room_something-follows': 'Algo Te Sigue',
    'room_the-countdown': 'La Cuenta Regresiva',
    'room_eyes-in-the-dark': 'Ojos en la Oscuridad',
    'room_the-voice': 'La Voz',
    'room_it-knows-your-name': 'Sabe Tu Nombre',
    'room_the-door': 'La Puerta',
    'room_boss-room-free': 'La Suma de Tus Verdades',
    'room_boss-room-horror': 'El Corazón del Miedo',
    'room_boss-room-premium': 'La Verdad Más Profunda',
    'room_mouse-tracker': 'El Rastreador de Ratón',
    'room_scroll-confession': 'Confesión de Desplazamiento',
    'room_waiting-game': 'El Juego de Espera',
    'room_day-in-pixels': 'Tu Día en Píxeles',
    'room_memory-maze': 'Laberinto de Memoria',
    'room_decision-shadow': 'Sombra de Decisión',
    'room_anonymous-mirrors': 'Espejos Anónimos',
    'room_collective-room': 'La Sala Colectiva',
    'room_secret-room': 'La Sala Secreta',
    'room_glitch-room': 'Sala Inestable',
    'room_full-moon-room': 'Sala de Luna Llena',
    'room_weekend-room': 'Sala de Fin de Semana',
    'room_317am-room': 'Sala de las 3:17 AM',
    'room_idle-room': 'La Sala Quieta',
    'room_invite-friend-room': 'La Sala Compartida',
    'room_halloween-room': 'La Sala del Velo',
    'room_winter-solstice-room': 'La Noche Más Larga',
    'room_new-year-room': 'Carta a Tu Yo Futuro',
    'room_void-room': 'El Vacío',
    'room_pattern-room': 'Tus Patrones',
    'room_time-capsule': 'Cápsula del Tiempo',
    'room_the-revisit': 'La Revisita',
    'room_contradiction-room': 'La Sala de Contradicción',
    'room_export-journey': 'Exportar Tu Viaje',
    'room_anonymous-echoes': 'Ecos Anónimos',
    'room_the-waiting-room': 'La Sala de Espera',
    'room_eclipse-shadows': 'Sombras del Eclipse',
    'room_dawn-awakening': 'Despertar del Amanecer',
    
    // Additional Room Subtitles (Spanish)
    'sub_how-many-times-left': 'Un conteo de lo que queda',
    'sub_life-in-weeks': '4.000 cuadrados. Eso es todo.',
    'sub_delay-button': 'Se hace más difícil cada vez',
    'sub_one-honest-sentence': 'Sin editar. Sin borrar.',
    'sub_this-moment': 'Estás aquí. Una vez.',
    'sub_reality-receipt': 'Un resumen de tus días',
    'sub_nothing-happens': 'Exactamente lo que dice',
    'sub_eclipse-shadows': 'Habla en la oscuridad total',
    'sub_dawn-awakening': 'Saluda a la primera luz',
    'sub_default-choice': 'Seguridad o riesgo. Siempre.',
    'sub_thought-loop': 'Mira cómo tu mente se repite',
    'sub_expect-vs-happens': 'La brecha entre esperanza y realidad',
    'sub_question-never-answer': 'La mayoría se va',
    'sub_not-special': 'Sin consuelo. Solo verdad.',
    'sub_shortcut-always-take': 'Los patrones se revelan',
    'sub_blurred-self': 'La memoria funciona así',
    'sub_image-zooming': 'Buscando respuestas en nada',
    'sub_comfort-color': '¿Cuánto tiempo te quedarás?',
    'sub_line-you-crossed': 'Vemos dónde dudaste',
    'sub_read-aloud': 'Palabras que debes pronunciar',
    'sub_voice-without-meaning': 'Sonido sin palabras',
    'sub_sentence-you-avoid': 'No puedes irte hasta escribirla',
    'sub_if-nothing-changes': 'Una proyección de tus próximos cinco años',
    'sub_real-priorities': 'Basado en tiempo, no en palabras',
    'sub_last-conversation': 'Escribe lo que nunca enviarás',
    'sub_who-without-job': 'Reescríbete',
    'sub_comfort-zone-scanner': 'Vemos tu vacilación',
    'sub_say-it-once': 'Sin repetición. Sin rehacer.',
    'sub_listen-to-yourself': 'Tu voz, ralentizada',
    'sub_life-as-shapes': 'Abstracto. Sin etiquetas. Tuyo.',
    'sub_face-without-features': 'Quita hasta que no quede nada',
    'sub_what-keeps-awake': 'Disponible después de las 10 PM',
    'sub_you-at-317am': 'Específico del tiempo. Personal.',
    'sub_thought-that-returns': 'Regresa cada visita',
    'sub_room-you-visit-most': 'Tu patrón revelado',
    'sub_room-you-avoid': 'Lo que evitas dice algo',
    'sub_left-too-early': 'Se activa cuando sales rápido',
    'sub_daily-quote': 'Sabiduría que cambia diariamente',
    'sub_daily-joke': 'Humor negro, actualizado diariamente',
    'sub_theyre-watching': 'No estás solo',
    'sub_the-last-visitor': 'Alguien estuvo aquí antes que tú',
    'sub_dont-look-away': 'Sabe cuándo te vas',
    'sub_the-whisper': 'Escucha con atención',
    'sub_you-forgot-something': '¿Qué era?',
    'sub_the-mirror-lies': 'Rompe para ver la verdad',
    'sub_something-follows': 'Sigue corriendo',
    'sub_the-countdown': 'Algo pasa en cero',
    'sub_eyes-in-the-dark': 'Se multiplican',
    'sub_the-voice': 'Alguien está atrapado aquí',
    'sub_it-knows-your-name': 'No deberías haber venido',
    'sub_the-door': 'Algo está tocando',
    'sub_boss-room-free': 'La sala libre final',
    'sub_boss-room-horror': 'Enfrenta la oscuridad final',
    'sub_boss-room-premium': 'La sala profunda final',
    'sub_mouse-tracker': 'Vemos cómo te mueves',
    'sub_scroll-confession': '¿Qué tan rápido te apresuras?',
    'sub_waiting-game': '¿Puedes no hacer nada?',
    'sub_day-in-pixels': 'El tiempo se vuelve forma',
    'sub_memory-maze': 'El patrón cambia',
    'sub_decision-shadow': 'Tus elecciones revelan patrones',
    'sub_anonymous-mirrors': 'Pensamientos de desconocidos',
    'sub_collective-room': 'Todos están aquí. Nadie se queda.',
    'sub_secret-room': 'Lo encontraste',
    'sub_glitch-room': 'Algo está mal',
    'sub_full-moon-room': 'Solo abre durante el pico lunar',
    'sub_weekend-room': 'El descanso es un destino',
    'sub_317am-room': 'Específico del tiempo. Personal.',
    'sub_idle-room': 'Encontrado a través de la paciencia',
    'sub_invite-friend-room': 'Algunos viajes deben ser presenciados',
    'sub_halloween-room': '31 de octubre - El velo es delgado',
    'sub_winter-solstice-room': '21 de diciembre - Libera y guarda',
    'sub_new-year-room': '31 de diciembre - Escribe a quien serás',
    'sub_void-room': 'Una herida en el código',
    'sub_pattern-room': 'Lo que el sitio ve en ti',
    'sub_time-capsule': 'Escribe a tu yo futuro',
    'sub_the-revisit': 'Mira cómo cambiaron tus respuestas',
    'sub_contradiction-room': 'Tus verdades no concuerdan',
    'sub_export-journey': 'Descarga tu camino',
    'sub_anonymous-echoes': 'Fragmentos de desconocidos',
    'sub_the-waiting-room': 'Desbloquea con presencia',
    
    // New Phase 1-3 Rooms (Spanish)
    'room_letters-never-sent': 'Cartas Nunca Enviadas',
    'sub_letters-never-sent': 'Escribe a alguien que nunca lo leerá',
    'room_breath-sync': 'Sincronía de Respiración',
    'sub_breath-sync': 'Respira con la sala',
    'room_entropy-garden': 'Jardín de Entropía',
    'sub_entropy-garden': 'Crecimiento y decadencia entrelazados',
    'room_memory-palace': 'Palacio de la Memoria',
    'sub_memory-palace': 'Construye tu santuario mental',
    'room_morning-ritual': 'Ritual Matutino',
    'sub_morning-ritual': 'Comienza tu día con intención',
    'room_evening-ritual': 'Ritual Nocturno',
    'sub_evening-ritual': 'Cierra el día con reflexión',
    'room_weekly-ritual': 'Ritual Semanal',
    'sub_weekly-ritual': 'Los domingos son para mirar atrás',
    
    // Letters Never Sent Room (Spanish)
    'letters_title': 'Cartas Nunca Enviadas',
    'letters_subtitle': 'Escribe a alguien que nunca lo leerá. La IA responderá como ellos podrían.',
    'letters_select_recipient': '¿A quién escribes?',
    'letters_past_self': 'Mi Yo Pasado',
    'letters_future_self': 'Mi Yo Futuro',
    'letters_someone_lost': 'Alguien que Perdí',
    'letters_someone_wronged': 'Alguien a quien Lastimé',
    'letters_placeholder': 'Querido/a...',
    'letters_send': 'Enviar al Vacío',
    'letters_sending': 'Enviando...',
    'letters_response': 'Una respuesta emerge...',
    'letters_write_another': 'Escribir Otra Carta',
    'letters_quote': 'Algunas palabras están destinadas a escribirse pero nunca enviarse.',
    
    // Breath Sync Room (Spanish)
    'breath_title': 'Sincronía de Respiración',
    'breath_subtitle': 'Sincroniza tu respiración con el ritmo de la sala.',
    'breath_inhale': 'Inhala',
    'breath_hold': 'Mantén',
    'breath_exhale': 'Exhala',
    'breath_cycles': 'Ciclos completados',
    'breath_quote': 'La respiración es el puente entre cuerpo y mente.',
    
    // Entropy Garden Room (Spanish)
    'entropy_title': 'Jardín de Entropía',
    'entropy_subtitle': 'Tus plantas crecen con cada visita, pero decaen cuando te alejas.',
    'entropy_plant': 'Plantar una Semilla',
    'entropy_water': 'Regar',
    'entropy_days_alive': 'días vivo',
    'entropy_health': 'Salud',
    'entropy_empty': 'Tu jardín está vacío. Planta algo.',
    'entropy_quote': 'Todo crece. Todo se marchita. Ese es el camino.',
    
    // Memory Palace Room (Spanish)
    'memory_palace_title': 'Palacio de la Memoria',
    'memory_palace_subtitle': 'Construye un santuario mental. Coloca objetos en salas que creas.',
    'memory_palace_add_room': 'Añadir Sala',
    'memory_palace_add_object': 'Añadir Objeto',
    'memory_palace_room_name': 'Nombre de la sala...',
    'memory_palace_object_name': 'Nombre del objeto...',
    'memory_palace_empty': 'Tu palacio está vacío. Crea tu primera sala.',
    'memory_palace_quote': 'Construimos nuestras mentes una sala a la vez.',
    
    // Ritual Rooms (Spanish)
    'ritual_locked': 'Este ritual aún no está disponible',
    'morning_ritual_title': 'Ritual Matutino',
    'morning_ritual_intro': 'Un momento para establecer intenciones para el día.',
    'morning_ritual_available': 'Disponible 6 AM - 9 AM',
    'evening_ritual_title': 'Ritual Nocturno',
    'evening_ritual_intro': 'Un momento para reflexionar sobre el día.',
    'evening_ritual_available': 'Disponible 8 PM - 11 PM',
    'weekly_ritual_title': 'Ritual Semanal',
    'weekly_ritual_intro': 'Un momento para reflexionar sobre la semana que fue, y establecer intenciones para la próxima.',
    'weekly_locked': 'El Ritual Semanal te espera',
    'weekly_time': 'Regresa el domingo',
    'today_is': 'Hoy es',
    'your_week': 'Tu Semana',
    'days_active': 'Días Activos',
    'weekly_summary_note': 'Ahora, reflexionemos más profundamente.',
    'weekly_highlight': '¿Cuál fue el momento destacado de tu semana?',
    'weekly_challenge': '¿Qué te desafió más esta semana?',
    'weekly_growth': '¿Cómo creciste esta semana?',
    'weekly_intention': '¿Qué intención estableces para la próxima semana?',
    'weekly_placeholder': 'Tómate tu tiempo...',
    'reflect': 'Reflexionar',
    'reflecting': 'Reflexionando...',
    'complete': 'Completar',
    'weekly_quote': 'Una semana termina. Otra comienza. Llevas lo que importa.',
    
    // Personal Timeline (Spanish)
    'personal_timeline': 'Línea de Tiempo Personal',
    'timeline_subtitle': 'Tu viaje a través de las salas',
    'timeline_empty': 'Tu línea de tiempo está vacía. Comienza a explorar.',
    'timeline_today': 'Hoy',
    'timeline_yesterday': 'Ayer',
    'timeline_this_week': 'Esta Semana',
    'timeline_earlier': 'Antes',
    
    // Weather System (Spanish)
    'weather_clear': 'Despejado',
    'weather_rain': 'Lluvia',
    'weather_fog': 'Niebla',
    'weather_snow': 'Nieve',
    
    // Notifications (Spanish)
    'notifications': 'Notificaciones',
    'notifications_desc': 'Recibe notificaciones sobre contenido bloqueado por tiempo',
    'notify_time_locked': 'Salas bloqueadas por tiempo',
    'notify_full_moon': 'Noches de luna llena',
    'notify_daily_reset': 'Reinicio de desafíos diarios',
    'notify_time_capsule': 'Desbloqueo de cápsulas de tiempo',
    'notifications_enabled': 'Notificaciones habilitadas',
    'notifications_disabled': 'Notificaciones deshabilitadas',
    
    // Community Submissions (Spanish)
    'community_submissions': 'Contribuciones de la Comunidad',
    'community_subtitle': 'Envía tus ideas de salas',
    'community_room_title': 'Título de la Sala',
    'community_room_description': 'Descripción de la Sala',
    'community_room_category': 'Categoría',
    'community_submit': 'Enviar Idea',
    'community_submitted': '¡Gracias por tu contribución!',
    'community_your_submissions': 'Tus Contribuciones',
    'community_empty': 'Aún no hay contribuciones. ¡Sé el primero!',
    
    // Proactive Guide (Spanish)
    'guide_hesitation': '¿Tomándote tu tiempo? Está bien. ¿Te gustaría una sugerencia?',
    'guide_long_absence': 'Bienvenido de vuelta. Ha pasado un tiempo.',
    'guide_milestone': 'Has llegado lejos. ¿Te gustaría reflexionar sobre tu viaje?',
    'guide_dismiss': 'Descartar',
    'guide_accept': 'Muéstrame',
    
    // Voice Mode (Spanish)
    'voice_mode': 'Modo de Voz',
    'voice_mode_desc': 'Deja que el guía te hable',
    'voice_mode_enabled': 'Modo de voz habilitado',
    'voice_mode_disabled': 'Modo de voz deshabilitado',
    
    // Offline Support (Spanish)
    'offline_available': 'Disponible sin conexión',
    'offline_cached': 'Esta sala está almacenada para ver sin conexión',
    
    // Journey Export (Spanish)
    'journey_export': 'Exportar Viaje',
    'journey_import': 'Importar Viaje',
    'journey_export_json': 'Exportar como JSON',
    'journey_import_json': 'Importar desde JSON',
    'journey_exported': 'Viaje exportado con éxito',
    'journey_imported': 'Viaje importado con éxito',
    
    // Phase 4 - Seasonal Rooms (Spanish)
    'room_spring-equinox-room': 'Equinoccio de Primavera',
    'sub_spring-equinox-room': 'Libera y renueva',
    'room_summer-solstice-room': 'Solsticio de Verano',
    'sub_summer-solstice-room': 'El día más largo',
    'room_autumn-equinox-room': 'Equinoccio de Otoño',
    'sub_autumn-equinox-room': 'Cosecha y acepta',
    'room_valentine-room': 'Sala de Amor Propio',
    'sub_valentine-room': 'El amor que mereces',
    
    // Spring Equinox Room (Spanish)
    'spring_date': '20 DE MARZO',
    'spring_title': 'Equinoccio de Primavera',
    'spring_message_1': 'El día y la noche son iguales.',
    'spring_message_2': 'Un momento de equilibrio perfecto.',
    'spring_message_3': '¿Qué liberarás para dar espacio al crecimiento?',
    'spring_letting_go': 'Estoy soltando:',
    'spring_letting_go_placeholder': 'Lo que ya no te sirve...',
    'spring_planting': 'Estoy plantando:',
    'spring_planting_placeholder': 'Lo que deseas que crezca...',
    'spring_bloom': 'Déjalo florecer',
    'spring_released': 'Liberado:',
    'spring_planted': 'Plantado:',
    'spring_closing': 'Desde este momento, comienza una nueva vida.',
    'spring_quote': 'La naturaleza no se apresura, pero todo se cumple.',
    
    // Summer Solstice Room (Spanish)
    'summer_date': '21 DE JUNIO',
    'summer_title': 'El Día Más Largo',
    'summer_message_1': 'Este es el día más largo del año.',
    'summer_message_2': 'La luz alcanza su punto máximo.',
    'summer_message_3': '¿Qué harías con más tiempo?',
    'summer_message_4': 'Describe tu día más pleno y vibrante.',
    'summer_placeholder': 'En mi día más pleno, yo...',
    'summer_submit': 'Abraza la luz',
    'summer_closing': 'El sol te ha escuchado. Ahora comienza su descenso.',
    'summer_quote': 'En el pico de la luz, descubrimos lo que realmente queremos iluminar.',
    
    // Autumn Equinox Room (Spanish)
    'autumn_date': '22 DE SEPTIEMBRE',
    'autumn_title': 'Equinoccio de Otoño',
    'autumn_message_1': 'El equilibrio regresa.',
    'autumn_message_2': 'La cosecha está lista.',
    'autumn_message_3': '¿Qué has cultivado? ¿Qué puedes aceptar?',
    'autumn_harvesting': 'Estoy cosechando:',
    'autumn_harvesting_placeholder': 'Lo que has cultivado este año...',
    'autumn_accepting': 'Estoy aceptando:',
    'autumn_accepting_placeholder': 'Lo que no puedes cambiar...',
    'autumn_release': 'Deja caer las hojas',
    'autumn_harvested': 'Cosechado:',
    'autumn_accepted': 'Aceptado:',
    'autumn_closing': 'Todo cae en su momento.',
    'autumn_quote': 'El otoño nos muestra lo hermoso que es dejar ir las cosas.',
    
    // Valentine's Day Room (Spanish)
    'valentine_date': '14 DE FEBRERO',
    'valentine_title': 'El Amor Que Mereces',
    'valentine_message_1': 'Hoy no se trata de otros.',
    'valentine_message_2': 'Se trata de quien siempre está contigo.',
    'valentine_message_3': 'Quien escucha cada pensamiento.',
    'valentine_message_4': 'Escríbete una carta de amor.',
    'valentine_prompt': 'Una carta para ti:',
    'valentine_placeholder': 'Querido/a yo, quiero que sepas...',
    'valentine_submit': 'Enviar con amor',
    'valentine_closing': 'Mereces el amor que das a otros.',
    'valentine_quote': 'Tú mismo mereces tu amor y afecto.',
    
    // Update changelog
    'update_seasonal_rooms': 'Cuatro nuevas salas estacionales: Equinoccio de Primavera, Solsticio de Verano, Equinoccio de Otoño y San Valentín',
    'update_fragment_trading': 'Sistema de intercambio de fragmentos - sacrifica fragmentos para desbloquear salas con tiempo, previsualizar salas de terror o recibir mensajes de IA',
    'update_new_themes': 'Dos nuevos temas: Eclipse (oscuridad total) y Amanecer (gradientes cálidos, solo 5-7 AM)',
     'update_regret_machine': 'La Máquina del Arrepentimiento - narrativas de líneas temporales alternativas impulsadas por IA',
     'update_memory_reconstruction': 'Reconstrucción de Memoria - la IA completa los detalles sensoriales de recuerdos que se desvanecen',
     
     // Phase 7 - Regret Machine Room
     'regret_machine_title': 'La Máquina del Arrepentimiento',
     'regret_machine_subtitle': 'Describe una decisión que desearías haber tomado de manera diferente. La máquina te mostrará lo que podría haber sido.',
     'regret_placeholder': 'Me arrepiento de...\n\nDescribe una elección que hiciste y que desearías que hubiera sido diferente. ¿Qué habrías hecho en su lugar?',
     'regret_hint': 'Esto no es sobre culpa. Es sobre comprensión.',
     'regret_explore': 'Explorar la Línea Temporal',
     'regret_generating': 'Reconstruyendo una realidad alternativa...',
     'regret_alternate_timeline': 'La Línea Temporal Alternativa',
     'regret_explore_another': 'Explorar Otra',
     'regret_error_short': 'Por favor describe tu arrepentimiento con más detalle',
     'room_regret-machine': 'La Máquina del Arrepentimiento',
     
     // Phase 7 - Memory Reconstruction Room
     'memory_reconstruction_title': 'Reconstrucción de Memoria',
     'memory_reconstruction_subtitle': 'Describe un recuerdo que se desvanece. Déjanos ayudarte a sentir el camino de regreso.',
     'memory_placeholder': 'Recuerdo...\n\nDescribe un recuerdo distante o fragmentado. ¿Qué fragmentos quedan? ¿Qué sentimientos persisten?',
     'memory_timeframe_placeholder': '¿Cuándo fue esto? (ej: "infancia", "hace diez años")',
     'memory_hint': 'La memoria no es una grabación. Es una reconstrucción.',
     'memory_reconstruct': 'Reconstruir Memoria',
     'memory_reconstructing': 'Uniendo los fragmentos...',
     'memory_reconstructed': 'El Recuerdo Reconstruido',
     'memory_explore_another': 'Explorar Otro Recuerdo',
     'memory_error_short': 'Por favor describe tu recuerdo con más detalle',
     'room_memory-reconstruction': 'Reconstrucción de Memoria',
    'update_eclipse_room': 'Sombras del Eclipse - habla en la oscuridad total, revela en destellos de luz',
    'update_dawn_room': 'Despertar del Amanecer - saluda la primera luz con ejercicios de respiración e intenciones',
    
    // New themes
    'eclipse_theme_required': 'Esta sala requiere el tema Eclipse. La oscuridad total te espera.',
    'dawn_theme_required': 'Esta sala requiere el tema Amanecer durante las primeras horas.',
    'dawn_time_hint': 'Disponible solo de 5 a 7 AM',
    'change_theme': 'Cambiar tema en Configuración',
    
    // Eclipse Shadows Room
    'eclipse_shadows_title': 'Sombras del Eclipse',
    'eclipse_shadows_intro': 'En la oscuridad completa, los secretos se sienten más seguros. Responde honestamente—solo breves destellos iluminarán tus palabras.',
    'enter_darkness': 'Entrar en la oscuridad',
    'shadow_question_1': '¿Qué escondes de ti mismo?',
    'shadow_question_2': '¿Qué verdad dirías si nadie pudiera escuchar?',
    'shadow_question_3': '¿Qué te persigue a las 3 AM?',
    'shadow_question_4': '¿Qué harías si el miedo no existiera?',
    'shadow_question_5': '¿Qué no estás listo para enfrentar?',
    'speak_into_dark': 'Habla en la oscuridad...',
    'release': 'Liberar',
    'eclipse_reveal_prompt': 'Haz clic en cada sombra para revelar lo que confesaste.',
    'hidden_in_shadow': '▓▓▓▓▓▓▓▓▓▓▓▓',
    'eclipse_reveal_hint': 'Algunas verdades son más claras en la oscuridad.',
    'eclipse_shadows_quote': 'En la oscuridad total, finalmente nos vemos a nosotros mismos.',
    
    // Dawn Awakening Room
    'dawn_awakening_title': 'Despertar del Amanecer',
    'dawn_awakening_intro': 'La primera luz trae posibilidad. Antes de que el mundo despierte, establece tus intenciones.',
    'greet_the_light': 'Saluda la luz',
    'breathe_in_light': 'Respira la luz',
    'hold_warmth': 'Mantén el calor',
    'release_night': 'Libera la noche',
    'dawn_prompt_1': '¿Qué quieres llevar contigo hoy?',
    'dawn_prompt_2': '¿Qué dejarás ir?',
    'dawn_prompt_3': '¿Qué pequeño acto de amabilidad te ofrecerás?',
    'set_your_intention': 'Establece tu intención...',
    'plant_seed': 'Planta esta semilla',
    'dawn_awakening_quote': 'Cada amanecer es una invitación para comenzar de nuevo.',
    
    // Fragment Trading
    'fragment_trading': 'Intercambio de Fragmentos',
    'sacrifice_to_unlock': 'Sacrifica fragmentos para desbloquear secretos',
    'your_fragments': 'Tus Fragmentos',
    'fragments_any': 'cualquier fragmento',
    'fragments_free': 'fragmentos de claridad',
    'fragments_premium': 'fragmentos de profundidad',
    'fragments_horror': 'fragmentos de sombra',
    'trade_successful': '¡Intercambio Exitoso!',
    'trade_failed': 'Intercambio Fallido',
    'not_enough_fragments': 'Fragmentos insuficientes',
    'continue_trading': 'Continuar Intercambiando',
    'trading_fragments': 'Intercambiando fragmentos...',
    'fragment_trade_warning': 'Los fragmentos sacrificados no se pueden recuperar',
    'unlock-time-locked': 'Desbloquear Sala con Tiempo',
    'unlock-time-locked_desc': 'Accede a una sala con tiempo sin esperar',
    'preview-horror': 'Previsualización de Terror',
    'preview-horror_desc': 'Observa una sala de terror antes de entrar',
    'ai-reflection-trade': 'Reflexión IA',
    'ai-reflection-trade_desc': 'Recibe un mensaje personalizado de la IA',
    'unlock-premium-preview': 'Prueba de Sala Premium',
    'unlock-premium-preview_desc': 'Accede a una sala premium por una visita',
    'fragment_trading_room': 'El Altar de Fragmentos',
    'fragment_trading_intro': 'Aquí puedes sacrificar tus fragmentos recolectados para desbloquear secretos ocultos en estas salas.',
    'your_collection': 'Tu Colección',
    'fragment_clarity': 'Claridad',
    'fragment_depth': 'Profundidad',
    'fragment_shadow': 'Sombra',
    'total_fragments': 'Total',
    'open_altar': 'Acercarse al Altar',
    'recent_trades': 'Ofrendas Recientes',
    'fragment_trade_ending': 'Lo que se dio no puede recuperarse. Pero lo que se ganó... eso permanece.',
    'room_fragment-trade': 'El Altar de Fragmentos',
    'sub_fragment-trade': 'Sacrifica para desbloquear secretos',
    'update_anniversary_system': 'Sistema de Aniversario y Hitos - sigue tu viaje a lo largo del tiempo',
    'update_archive_room': 'El Archivo - historial completo desbloqueado a las 25 salas',
    'update_mirror_room': 'Espejo de Todos los Yoes - retrato conductual IA a las 50 salas',
    'update_ending_room': 'El Final - una despedida personalizada a las 75 salas',
    'room_the-archive': 'El Archivo',
    'room_mirror-of-all-selves': 'Espejo de Todos los Yoes',
    'room_the-ending': 'El Final',
    // Phase 9
    'update_fragment_rarities': 'Rarezas de Fragmentos - fragmentos coleccionables Comunes, Raros y Legendarios',
    'update_fragment_gallery': 'Galería de Fragmentos - vitrina visual para tu colección',
    'update_seasonal_fragments': 'Fragmentos estacionales y secretos - coleccionables por tiempo limitado',
    'fragment_collection': 'Colección',
    'fragment_collection_title': 'Colección de Fragmentos',
    'fragment_collection_subtitle': 'Cada sala deja algo atrás. Colecciónalos todos.',
    'collection_milestones': 'Hitos',
    'collection_secrets': 'Secretos',
    'collection_footer': 'Algunos fragmentos solo aparecen bajo ciertas condiciones.',
    'rarity_common': 'Común',
    'rarity_rare': 'Raro',
    'rarity_legendary': 'Legendario',
    // Phase 10-11
    'accessibility': 'Accesibilidad',
    'accessibility_desc': 'Personalizar legibilidad y preferencias de movimiento',
    'font_family': 'Familia de Fuente',
    'font_default': 'Monoespaciada',
    'font_dyslexia': 'Amigable con Dislexia',
    'font_sans': 'Sans Serif',
    'font_changed': 'Fuente actualizada',
    'line_height': 'Altura de Línea',
    'line_compact': 'Compacto',
    'line_normal': 'Normal',
    'line_relaxed': 'Relajado',
    'line_spacious': 'Espacioso',
    'line_height_changed': 'Altura de línea actualizada',
    'reduced_motion_full_desc': 'Deshabilitar todas las animaciones y efectos de partículas',
    'larger_cursors': 'Objetivos Más Grandes',
    'larger_cursors_desc': 'Aumentar el tamaño de los objetivos de clic',
    'update_accessibility_suite': 'Suite de Accesibilidad - fuentes para dislexia, control de altura de línea',
    'update_dyslexia_font': 'Opción de fuente amigable con dislexia en ajustes',
    'update_line_height': 'Altura de línea ajustable para mejor legibilidad',
    'update_enhanced_reduced_motion': 'Movimiento reducido mejorado - desactiva completamente todos los efectos',
    'update_shared_moments': 'Momentos Compartidos - registros emocionales anónimos',
    'update_whisper_wall': 'El Muro de Susurros - deja verdades anónimas en la oscuridad',
    'update_collective_memory': 'Memoria Colectiva - fragmentos compartidos de lo que recordamos',
    'whisper_prompt': 'Deja un susurro en el muro. Anónimo. Permanente.',
    'whisper_placeholder': '¿Qué llevas en silencio?',
    'whisper_send': 'Susurrar',
    'whisper_added': 'Tu susurro se une al muro.',
    'whisper_another': 'Susurrar de nuevo',
    'shared_moments_title': 'Ahora mismo, en este momento...',
    'shared_moments_subtitle': '¿Cómo estás, honestamente?',
    'feeling_grateful': 'Agradecido',
    'feeling_lost': 'Perdido',
    'feeling_hopeful': 'Esperanzado',
    'feeling_heavy': 'Pesado',
    'feeling_peaceful': 'En paz',
    'feeling_restless': 'Inquieto',
    'room_whisper-wall': 'El Muro de Susurros',
    'room_collective-memory': 'Memoria Colectiva',
    'room_shared-moments': 'Momentos Compartidos',
    'sub_regret-machine': 'Explora lo que podría haber sido',
    'sub_memory-reconstruction': 'Reconstruye un recuerdo que se desvanece',
    'sub_whisper-wall': 'Verdades anónimas flotan en la oscuridad',
    'sub_collective-memory': 'Fragmentos compartidos de lo que recordamos',
    'sub_shared-moments': '¿Cómo estás, honestamente?',
    'sub_the-archive': 'Tu historial completo de viaje',
    'sub_mirror-of-all-selves': 'La IA revela tu retrato conductual',
    'sub_the-ending': 'Una despedida que te ganaste',

    // v3.21.0 - New Rooms
    'room_mask-you-wear': 'La Máscara que Usas',
    'sub_mask-you-wear': '¿Qué cara muestras?',
    'room_recurring-dream': 'Tu Sueño Recurrente',
    'sub_recurring-dream': 'El sueño que sigue volviendo',
    'room_promise-you-broke': 'La Promesa que Rompiste',
    'sub_promise-you-broke': 'Honestidad sobre promesas rotas',
    'room_words-you-swallowed': 'Palabras que Tragaste',
    'sub_words-you-swallowed': 'Cosas que nunca dijiste',
    'room_age-you-feel': 'La Edad que Sientes',
    'sub_age-you-feel': '¿Cuántos años tienes realmente?',
    'room_biggest-lie': 'Tu Mayor Mentira',
    'sub_biggest-lie': 'La mentira que más cuentas',
    'room_person-you-pretend-to-be': 'La Persona que Finges Ser',
    'sub_person-you-pretend-to-be': 'Tu actuación',
    'room_person-you-pretend': 'La Persona que Finges Ser',
    'sub_person-you-pretend': 'Tu actuación',
    'room_what-you-collect': 'Lo que Coleccionas',
    'sub_what-you-collect': 'Los objetos revelan identidad',
    'room_apology-you-owe': 'La Disculpa que Debes',
    'sub_apology-you-owe': 'Escribe lo que debiste decir',
    'room_unfinished-story': 'Tu Historia Inconclusa',
    'sub_unfinished-story': 'Lo que nunca completaste',
    'room_version-others-see': 'La Versión que Otros Ven',
    'sub_version-others-see': 'Cómo te perciben los demás',
    'room_secret-talent': 'Tu Talento Secreto',
    'sub_secret-talent': 'Lo que nadie sabe que puedes hacer',
    'room_fear-you-feed': 'El Miedo que Alimentas',
    'sub_fear-you-feed': '¿Qué miedo te controla?',
    'room_guilty-pleasure': 'Tu Placer Culpable',
    'sub_guilty-pleasure': 'Lo que escondes disfrutar',
    'room_rule-you-break': 'La Regla que Siempre Rompes',
    'sub_rule-you-break': 'Tu patrón de rebelión',
    'room_childhood-promise': 'Tu Promesa de Infancia',
    'sub_childhood-promise': 'Lo que te prometiste',
    'room_compliment-you-reject': 'El Cumplido que Rechazas',
    'sub_compliment-you-reject': 'Por qué no aceptas elogios',
    'room_autopilot-mode': 'Tu Modo Automático',
    'sub_autopilot-mode': 'Lo que haces sin pensar',
    'room_boundary-you-need': 'El Límite que Necesitas',
    'sub_boundary-you-need': 'Lo que debes proteger',
    'room_inner-critic': 'Tu Crítico Interior',
    'sub_inner-critic': 'La voz que juzga',
    'room_last-sunday': 'El Domingo Pasado',
    'sub_last-sunday': '¿Qué hiciste realmente?',
    'room_wasted-hours': 'Tus Horas Perdidas',
    'sub_wasted-hours': 'Donde desaparece el tiempo',
    'room_year-that-changed': 'El Año que te Cambió',
    'sub_year-that-changed': 'El punto de inflexión',
    'room_tomorrows-regret': 'El Arrepentimiento de Mañana',
    'sub_tomorrows-regret': '¿Qué desearías haber hecho?',
    'room_morning-autopilot': 'Tu Piloto Automático Matutino',
    'sub_morning-autopilot': 'El ritual de la primera hora',
    'room_season-you-dread': 'La Estación que Temes',
    'sub_season-you-dread': '¿Qué estación pesa más?',
    'room_ten-years-ago': 'Hace Diez Años',
    'sub_ten-years-ago': '¿Quién eras entonces?',
    'room_last-normal-day': 'Tu Último Día Normal',
    'sub_last-normal-day': 'Antes de que todo cambiara',
    'room_clock-you-ignore': 'El Reloj que Ignoras',
    'sub_clock-you-ignore': 'Tiempo que finges que no pasa',
    'room_your-deadline': 'Tu Fecha Límite',
    'sub_your-deadline': '¿Qué estás posponiendo?',
    'room_the-pause': 'La Pausa',
    'sub_the-pause': 'Solo respira un momento',
    'room_static-room': 'Estático',
    'sub_static-room': 'Ruido blanco y quietud',
    'room_gap-between-thoughts': 'El Espacio Entre Pensamientos',
    'sub_gap-between-thoughts': 'Encuentra el espacio',
    'room_sit-with-discomfort': 'Siéntate con la Incomodidad',
    'sub_sit-with-discomfort': 'No arregles nada',
    'room_boring-room': 'La Habitación Aburrida',
    'sub_boring-room': 'Nada interesante en absoluto',
    'room_unplug-room': 'Desconectar',
    'sub_unplug-room': 'Imagina sin dispositivos',
    'room_unplug': 'Desconectar',
    'sub_unplug': 'Imagina sin dispositivos',
    'room_last-page': 'La Última Página',
    'sub_last-page': 'La historia termina aquí',
    'room_scrolling-pattern': 'Tu Patrón de Scroll',
    'sub_scrolling-pattern': 'Cómo consumes contenido',
    'room_tab-you-keep-open': 'La Pestaña que Mantienes Abierta',
    'sub_tab-you-keep-open': 'Lo que permanece en tu navegador',
    'room_digital-shadow': 'Tu Sombra Digital',
    'sub_digital-shadow': 'Tu huella digital',
    'room_notification-you-ignore': 'La Notificación que Ignoras',
    'sub_notification-you-ignore': 'Lo que descartas',
    'room_typing-speed': 'Tu Velocidad al Escribir',
    'sub_typing-speed': '¿Qué tan rápido te expresas?',
    'room_link-you-almost-clicked': 'El Enlace que Casi Clicaste',
    'sub_link-you-almost-clicked': 'Patrones de duda',
    'room_bookmark-graveyard': 'Tu Cementerio de Marcadores',
    'sub_bookmark-graveyard': 'Guardados pero nunca revisitados',
    'room_search-you-deleted': 'La Búsqueda que Borraste',
    'sub_search-you-deleted': 'Lo que buscaste y borraste',
    'room_gravity-room': 'Sala de Gravedad',
    'sub_gravity-room': 'Todo cae lentamente',
    'room_ink-blot': 'Mancha de Tinta',
    'sub_ink-blot': '¿Qué ves?',
    'room_hallway-of-mirrors': 'El Pasillo de Espejos',
    'sub_hallway-of-mirrors': 'Reflejos infinitos',
    'room_dissolving-text': 'Texto Disolviéndose',
    'sub_dissolving-text': 'Las palabras se derriten al leer',
    'room_the-pendulum': 'El Péndulo',
    'sub_the-pendulum': 'Movimiento hipnótico',
    'room_pixel-rain': 'Lluvia de Píxeles',
    'sub_pixel-rain': 'Precipitación digital',
    'room_the-threshold': 'El Umbral',
    'sub_the-threshold': 'Entre aquí y allá',
    'most_loved_rooms': 'Habitaciones Más Queridas',
    'rate_room': 'Califica esta habitación',
    'update_52_new_rooms': '52 nuevas habitaciones en categorías de Identidad, Tiempo, Contemplación, Meta y Experimental',
    'update_room_rating': 'Sistema de calificación - pulgar arriba/abajo con sección de Habitaciones Más Queridas',
    'update_bug_fix_reflections': 'Corrección en la visualización y almacenamiento de reflexiones anónimas',
    'update_100_rooms': 'Más de 100 habitaciones disponibles en el Pasillo',
    // v3.22.0
    'room_the-friend-you-lost': 'El Amigo Que Perdiste',
    'sub_the-friend-you-lost': 'Escribe a alguien de quien te alejaste.',
    'room_conversation-you-replay': 'La Conversación Que Repites',
    'sub_conversation-you-replay': 'Recréala de memoria.',
    'room_the-thank-you': 'El Agradecimiento',
    'sub_the-thank-you': 'Gratitud que nunca expresaste.',
    'room_people-you-forgot': 'Personas Que Olvidaste',
    'sub_people-you-forgot': 'Intenta recordar nombres olvidados.',
    'room_the-advice-you-give': 'El Consejo Que Das',
    'sub_the-advice-you-give': 'Consejo que das pero nunca sigues.',
    'room_what-others-dont-know': 'Lo Que Otros No Saben',
    'sub_what-others-dont-know': 'Tus capas ocultas.',
    'room_the-lie-you-believe': 'La Mentira Que Crees',
    'sub_the-lie-you-believe': 'Una verdad cómoda que no es real.',
    'room_your-shadow-self': 'Tu Yo Sombra',
    'sub_your-shadow-self': 'La parte que reprimes.',
    'room_the-story-you-tell': 'La Historia Que Cuentas',
    'sub_the-story-you-tell': 'Cómo narras tu vida.',
    'room_your-safe-place': 'Tu Lugar Seguro',
    'sub_your-safe-place': 'Describe dónde te sientes seguro.',
    'room_the-risk-you-didnt-take': 'El Riesgo Que No Tomaste',
    'sub_the-risk-you-didnt-take': 'Caminos no recorridos.',
    'room_your-last-excuse': 'Tu Última Excusa',
    'sub_your-last-excuse': 'La excusa que más usas.',
    'room_the-habit-you-hide': 'El Hábito Que Ocultas',
    'sub_the-habit-you-hide': 'Rutinas secretas.',
    'room_what-you-postpone': 'Lo Que Pospones',
    'sub_what-you-postpone': 'Lo que siempre es "mañana".',
    'room_the-door-you-closed': 'La Puerta Que Cerraste',
    'sub_the-door-you-closed': 'Oportunidades que rechazaste.',
    'journal_title': 'Diario de Reflexiones',
    'journal_subtitle': 'Tus pensamientos a través de las habitaciones',
    'journal_empty': 'Sin reflexiones aún. Visita habitaciones y escribe tus pensamientos.',
    'journal': 'Diario',
    'room_chains': 'Cadenas de Habitaciones',
    'chain_identity_journey': 'Viaje de Identidad',
    'chain_time_reflection': 'Reflexión del Tiempo',
    'chain_truth_path': 'Camino de la Verdad',
    'recently_visited': 'Visitados Recientemente',
    'update_reflection_journal': 'Diario de Reflexiones - guarda tus pensamientos entre habitaciones',
    'update_room_chains': 'Cadenas de Habitaciones - completa habitaciones en secuencia',
    'update_15_new_rooms': '15 nuevas habitaciones interactivas en 3 categorías',
    'update_recently_visited': 'Sección de visitados recientemente para volver rápido',
    'ai_companion': 'Compañero',
    'companion_create': 'Crea Tu Compañero',
    'companion_setup_desc': 'Un reflejo que camina a tu lado.',
    'companion_name': 'Nombra a tu compañero',
    'companion_name_placeholder': 'ej., Eco, Espejo, Sombra...',
    'companion_lens': 'Lente de personalidad',
    'companion_mood': 'Tono de comunicación',
    'companion_fears': '¿A qué le temes? (opcional)',
    'companion_struggles': '¿Con qué luchas? (opcional)',
    'create_companion': 'Crear',
    'companion_input': 'Di algo...',
    'companion_greeting': 'Tu compañero está aquí. Di algo.',
    'companion_starter_1': '¿Qué tengo en mente hoy?',
    'companion_starter_2': 'Me siento atascado.',
    'companion_starter_3': 'Dime algo que evito.',
    'fears_placeholder': 'ej., fracaso, rechazo, ser olvidado...',
    'struggles_placeholder': 'ej., pensar demasiado, procrastinar, soledad...',
    'back_step': 'Atrás',
    'lens_reflective': 'Reflexivo',
    'lens_reflective_desc': 'Preguntas profundas, reconocimiento de patrones',
    'lens_supportive': 'Solidario',
    'lens_supportive_desc': 'Cálido, alentador, validador',
    'lens_challenging': 'Desafiante',
    'lens_challenging_desc': 'Directo, provocador',
    'lens_curious': 'Curioso',
    'lens_curious_desc': 'Preguntas inesperadas, asombro',
    'lens_shadow': 'Sombra',
    'lens_shadow_desc': 'El lado oculto, incomodidad honesta',
    'companion_mood_calm': 'Calma',
    'companion_mood_calm_desc': 'Mesurado y pacífico',
    'companion_mood_intense': 'Intenso',
    'companion_mood_intense_desc': 'Urgente y profundo',
    'companion_mood_playful': 'Juguetón',
    'companion_mood_playful_desc': 'Ingenioso y ligero',
    'companion_mood_melancholic': 'Melancólico',
    'companion_mood_melancholic_desc': 'Tristeza hermosa',
    'update_ai_companion': 'Compañero IA - una IA personal que recuerda tu viaje',
    // v3.23.0
    'mood_history': 'Historial de Ánimo',
    'top_rooms': 'Tus Salas Favoritas',
    'visits_count': 'visitas',
    'set_intention': 'Establece una intención para hoy',
    'todays_intention': 'Intención de Hoy',
    'intention_placeholder': 'Voy a...',
    'set': 'Establecer',
    'update_mood_history': 'Historial de Ánimo - rastrea cómo cambia tu ánimo',
    'update_daily_intention': 'Intención Diaria - establece un enfoque para cada día',
    'update_top_rooms': 'Salas Favoritas - acceso rápido a tus salas más visitadas',
    'update_10_new_rooms_v323': '10 nuevas salas: emociones, autorreflexión y conexión',
    'room_the-emotion-you-hide': 'La Emoción que Ocultas',
    'sub_the-emotion-you-hide': 'Nómbrala. Solo una vez.',
    'emotion_hide_placeholder': 'La emoción que nunca muestro es...',
    'quote_emotion_hide': 'Lo que ocultamos a otros, a menudo lo ocultamos de nosotros mismos.',
    'room_your-comfort-lie': 'Tu Mentira Cómoda',
    'sub_your-comfort-lie': 'La pequeña mentira que te mantiene cómodo.',
    'comfort_lie_placeholder': 'La mentira que me digo es...',
    'admit_it': 'Admítelo',
    'quote_comfort_lie': 'Las mentiras cómodas son muros que construimos con tela suave.',
    'room_the-silence-between': 'El Silencio Entre',
    'sub_the-silence-between': 'Siéntate en el espacio entre dos pensamientos.',
    'silence_instruction': 'Cierra los ojos. Encuentra el espacio entre dos pensamientos. Luego presiona el botón.',
    'silence_sitting': 'Sentado en silencio...',
    'silence_found': 'Lo encontré',
    'silence_result': 'Te sentaste por',
    'seconds': 'segundos',
    'quote_silence_between': 'Entre cada pensamiento hay un espacio infinito. Acabas de visitarlo.',
    'room_what-you-carry': 'Lo que Cargas',
    'sub_what-you-carry': 'Nombra el peso invisible.',
    'carry_placeholder': 'El peso que cargo es...',
    'set_it_down': 'Déjalo',
    'carry_released': 'Por un momento, fue más ligero.',
    'quote_carry': 'No puedes soltar lo que te niegas a nombrar.',
    'room_the-version-you-lost': 'La Versión que Perdiste',
    'sub_the-version-you-lost': 'Quién eras antes de que la vida pasara.',
    'version_lost_placeholder': 'Antes de que todo cambiara, yo era...',
    'remember': 'Recordar',
    'quote_version_lost': 'No los perdiste. Se convirtieron en parte de lo que eres ahora.',
    'room_letter-to-past-self': 'Carta al Yo del Pasado',
    'sub_letter-to-past-self': 'Escribe a quien solías ser.',
    'letter_past_placeholder': 'Querido yo joven,...',
    'send_letter': 'Enviar',
    'letter_sent': 'La carta fue enviada. En algún lugar, la recibieron.',
    'quote_letter_past': 'Cada palabra de consuelo que escribes a tu yo pasado es realmente para quien eres ahora.',
    'room_the-moment-you-knew': 'El Momento que Supiste',
    'sub_the-moment-you-knew': 'El instante en que todo cambió.',
    'moment_knew_placeholder': 'El momento en que supe fue cuando...',
    'mark_it': 'Márcalo',
    'quote_moment_knew': 'Algunos momentos dividen tu vida en antes y después. Acabas de nombrar uno.',
    'room_the-words-you-rehearse': 'Las Palabras que Ensayas',
    'sub_the-words-you-rehearse': 'Lo que practicas decir pero nunca haces.',
    'words_rehearse_placeholder': 'Las palabras que sigo ensayando son...',
    'say_them': 'Dilas',
    'words_spoken': 'Finalmente las dijiste. Aunque solo aquí.',
    'quote_words_rehearse': 'Las palabras ensayadas son jaulas. Las palabras dichas son pájaros.',
    'room_someone-thinks-of-you': 'Alguien Piensa en Ti',
    'sub_someone-thinks-of-you': 'Ahora mismo, en algún lugar, alguien te recuerda.',
    'someone_line_1': 'Ahora mismo, en algún lugar, alguien te recuerda.',
    'someone_line_2': 'No por lo que hiciste.',
    'someone_line_3': 'Sino por cómo les hiciste sentir.',
    'quote_someone_thinks': 'Existes en recuerdos que ni siquiera conoces.',
    'room_the-question-you-fear': 'La Pregunta que Temes',
    'sub_the-question-you-fear': 'La pregunta que esperas que nadie haga.',
    'question_fear_placeholder': 'La pregunta que más temo es...',
    'quote_question_fear': 'La pregunta que más temes suele ser la que tiene la respuesta más importante.',
    // v3.24.0
    'behavior_insight': 'El sitio nota',
    'insight_pattern': 'Te atrae {category}. Exploras más por la {time}.',
    'cat_identity': 'identidad',
    'cat_truth': 'verdad',
    'cat_time': 'tiempo',
    'cat_mortality': 'mortalidad',
    'cat_stillness': 'quietud',
    'cat_darkness': 'oscuridad',
    'cat_patterns': 'patrones',
    'cat_fear': 'miedo',
    'cat_play': 'juego',
    'cat_experiments': 'experimentos',
    'cat_ritual': 'ritual',
    'cat_secrets': 'secretos',
    'time_late_night': 'noche',
    'time_morning': 'mañana',
    'time_afternoon': 'tarde',
    'time_evening': 'noche',
    
    'anonymous_visitor': 'un visitante',
    'update_behavior_insight': 'Perspectiva de Comportamiento - el sitio refleja tus patrones',
    'update_anonymous_reflections_feed': 'Reflexiones Anónimas - ve lo que otros han sentido',
    'update_daily_prompt': 'Reflexión Diaria - un pensamiento para llevar contigo',
    'room_recurring-dream-journal': 'Diario de Sueños Recurrentes', 'sub_recurring-dream-journal': 'El sueño que sigue volviendo.', 'dream_journal_placeholder': 'El sueño que sigo teniendo...', 'record_dream': 'Registrar', 'dream_journal_reflection': 'Los sueños dicen lo que la mente despierta no dice.', 'quote_dream_journal': 'Los sueños recurrentes son conversaciones sin terminar contigo mismo.',
    'room_lucid-moment': 'El Momento Lúcido', 'sub_lucid-moment': 'Toma conciencia dentro del sueño.', 'lucid_line_1': 'Estás leyendo estas palabras.', 'lucid_line_2': '¿Pero estás despierto?', 'lucid_line_3': '¿Cómo sabrías la diferencia?', 'become_aware': 'Soy consciente', 'lucid_aware': 'Este momento de conciencia es lo único real.', 'quote_lucid_moment': 'El momento en que cuestionas la realidad es cuando realmente la ves.',
    'room_nightmare-you-remember': 'La Pesadilla Que Recuerdas', 'sub_nightmare-you-remember': 'La que aún te persigue.', 'nightmare_placeholder': 'La pesadilla que no puedo olvidar...', 'face_it': 'Enfréntala', 'nightmare_reflection': 'Al escribirla, le quitaste algo de poder.', 'quote_nightmare': 'Las pesadillas son miedos que insisten en ser vistos.',
    'room_dream-you-wish-was-real': 'El Sueño Que Desearías Fuera Real', 'sub_dream-you-wish-was-real': 'Despierta. No era real.', 'dream_wish_placeholder': 'Soñé que...', 'hold_onto_it': 'Aférrate', 'quote_dream_wish': 'Los sueños que lamentamos perder revelan la vida que realmente queremos.',
    'room_the-dream-visitor': 'El Visitante del Sueño', 'sub_the-dream-visitor': 'Alguien sigue apareciendo en tus sueños.', 'dream_visitor_line_1': 'Hay alguien que te visita en sueños.', 'dream_visitor_line_2': 'No los invitaste.', 'dream_visitor_line_3': 'Pero siguen volviendo.', 'dream_visitor_line_4': 'Quizás intentan decirte algo.', 'quote_dream_visitor': 'Las personas que aparecen en nuestros sueños son mensajeros del subconsciente.',
    'room_the-blank-canvas': 'El Lienzo en Blanco', 'sub_the-blank-canvas': 'Crea algo de la nada.', 'blank_canvas_placeholder': 'En este lienzo en blanco, creo...', 'create': 'Crear', 'quote_blank_canvas': 'Toda creación comienza con la valentía de enfrentar el vacío.',
    'room_song-that-is-you': 'La Canción Que Eres Tú', 'sub_song-that-is-you': 'Si fueras una canción, ¿cuál?', 'song_placeholder': 'La canción que soy yo...', 'play_it': 'Reprodúcela', 'quote_song': 'La música es el sonido de los sentimientos.',
    'room_color-of-today': 'El Color de Hoy', 'sub_color-of-today': 'Elige el color de tu estado actual.', 'color_chosen': 'Así te sientes hoy. En color.', 'quote_color_today': 'Los colores no necesitan palabras. Los sentimientos tampoco.',
    'room_the-unwritten-poem': 'El Poema No Escrito', 'sub_the-unwritten-poem': 'Escribe lo que guardabas dentro.', 'poem_placeholder': 'Palabras que esperaban...', 'publish_it': 'Publícalo', 'quote_unwritten_poem': 'El poema siempre estuvo ahí. Solo lo dejaste salir.',
    'room_your-inner-voice': 'Tu Voz Interior', 'sub_your-inner-voice': '¿Qué dice realmente?', 'inner_voice_placeholder': 'Mi voz interior dice...', 'listen': 'Escucha', 'inner_voice_reflection': 'Ahora que la escuchaste, ¿qué harás?', 'quote_inner_voice': 'Tu voz interior ha estado hablando todo el tiempo. Solo empezaste a escuchar.',
    'room_fear-you-named': 'El Miedo Que Nombraste', 'sub_fear-you-named': 'Nómbralo y pierde su poder.', 'fear_named_placeholder': 'El miedo que finalmente nombro...', 'name_it': 'Nómbralo', 'fear_named_reflection': 'Los miedos nombrados se reducen. Los sin nombre crecen.', 'quote_fear_named': 'Lo que puedes nombrar, puedes enfrentar.',
    'room_the-leap': 'El Salto', 'sub_the-leap': '¿A qué saltarías?', 'leap_placeholder': 'Si pudiera saltar...', 'leap': 'Salta', 'quote_leap': 'La red aparece después de saltar, no antes.',
    'room_courage-inventory': 'Inventario de Coraje', 'sub_courage-inventory': 'Lista cada cosa valiente que hiciste.', 'courage_placeholder': 'Algo valiente que hice...', 'courage_count': 'Listaste {count} actos de coraje.', 'quote_courage': 'Eres más valiente de lo que recuerdas.',
    'room_what-scares-you-most': 'Lo Que Más Te Asusta', 'sub_what-scares-you-most': 'El miedo más profundo.', 'scares_placeholder': 'Lo que más me asusta es...', 'quote_scares': 'Tu miedo más profundo suele ser tu mayor maestro.',
    'room_facing-the-dark': 'Enfrentando la Oscuridad', 'sub_facing-the-dark': 'Siéntate en oscuridad total.', 'darkness_counting': '{seconds} segundos en la oscuridad...', 'return_to_light': 'Volver a la luz', 'darkness_survived': 'Estuviste en la oscuridad {seconds} segundos.', 'quote_facing_dark': 'En la oscuridad, descubrimos lo que llevamos dentro.',
    'room_letter-to-future-self': 'Carta a Tu Yo Futuro', 'sub_letter-to-future-self': 'Escribe a quien serás.', 'letter_future_placeholder': 'Querido yo futuro,...', 'seal_letter': 'Sellar', 'letter_future_sealed': 'La carta está sellada. Algún día la leerás.', 'quote_letter_future': 'Cada carta al futuro es realmente una promesa al presente.',
    'room_the-world-you-want': 'El Mundo Que Quieres', 'sub_the-world-you-want': 'Describe el mundo que desearías.', 'world_placeholder': 'El mundo que quiero...', 'build_it': 'Constrúyelo', 'quote_world_want': 'El mundo que imaginas es el primer paso hacia el mundo que creas.',
    'room_your-next-chapter': 'Tu Próximo Capítulo', 'sub_your-next-chapter': '¿Qué pasa después en tu historia?', 'next_chapter_placeholder': 'En mi próximo capítulo...', 'turn_the_page': 'Pasa la página', 'quote_next_chapter': 'Eres tanto el autor como el protagonista.',
    'room_the-wish': 'El Deseo', 'sub_the-wish': 'Un deseo. Sin reglas.', 'wish_placeholder': 'Deseo...', 'make_wish': 'Pide el deseo', 'quote_wish': 'Lo que deseas revela lo que más valoras.',
    'room_if-you-had-one-year': 'Si Tuvieras Un Año', 'sub_if-you-had-one-year': '¿Qué harías con 365 días?', 'one_year_placeholder': 'Si me quedara un año...', 'begin_now': 'Empieza ahora', 'one_year_reflection': '¿Entonces por qué no lo haces?', 'quote_one_year': 'Vive como si tuvieras un año. Porque nunca se sabe.',
    'update_v4_languages': '4 nuevos idiomas: Hindi, Coreano, Japonés, Turco', 'update_v4_20_rooms': '20 nuevas salas en Sueños, Creatividad, Miedo y Futuro', 'update_v4_dreams_category': 'Categoría de salas de Sueños y Subconsciente', 'update_v4_creativity_category': 'Categoría de salas de Creatividad y Expresión', 'update_v4_fear_category': 'Categoría de salas de Miedo y Coraje', 'update_v4_future_category': 'Categoría de salas de Futuro y Esperanza',
    'room_playlists': 'Listas de Salas', 'personal_dashboard': 'Panel', 'dashboard_subtitle': 'Tu viaje de un vistazo', 'visit_pattern': 'Patrón de visitas (14 días)', 'dashboard_top_rooms': 'Salas más visitadas', 'category_breakdown': 'Categorías de salas', 'mood_trend': 'Tendencia de ánimo', 'fragment_breakdown': 'Desglose de fragmentos', 'no_data_yet': 'Sin datos aún', 'current_streak': 'Racha de días',
    'playlist_morning': 'Reflexión Matutina', 'playlist_morning_desc': 'Comienza tu día con claridad', 'playlist_identity': 'Inmersión de Identidad', 'playlist_identity_desc': '¿Quién eres bajo las etiquetas?', 'playlist_creative': 'Chispa Creativa', 'playlist_creative_desc': 'Desbloquea tu expresión creativa', 'playlist_courage': 'Camino del Coraje', 'playlist_courage_desc': 'Enfrenta tus miedos sala por sala', 'playlist_dreams': 'Diario de Sueños', 'playlist_dreams_desc': 'Explora los paisajes de tu subconsciente', 'playlist_future': 'Visión Futura', 'playlist_future_desc': 'Cartas y deseos para tu yo futuro', 'playlist_truth': 'Confrontación de Verdad', 'playlist_truth_desc': 'Enfrenta las realidades incómodas', 'playlist_night': 'Viaje Nocturno', 'playlist_night_desc': 'Abraza el silencio, vacío y la nada',
    // v4.2.0
    'room_focus-timer': 'Temporizador de Enfoque', 'sub_focus-timer': 'Siéntate en silencio. Reflexiona.',
    'room_gratitude-jar': 'Frasco de Gratitud', 'sub_gratitude-jar': 'Una cosa por la que estás agradecido',
    'room_mood-compass': 'Brújula del Ánimo', 'sub_mood-compass': '¿Dónde estás ahora mismo?',
    'room_memory-fade': 'Desvanecimiento de Memoria', 'sub_memory-fade': 'Mira un recuerdo disolverse',
    'room_sound-of-your-day': 'Sonido de Tu Día', 'sub_sound-of-your-day': '¿Cómo suena hoy?',
    'room_the-apology-room': 'La Sala de Disculpas', 'sub_the-apology-room': 'Escribe lo que nunca dijiste',
    'room_your-unread-messages': 'Tus Mensajes No Leídos', 'sub_your-unread-messages': 'Mensajes que nunca abriste',
    'room_the-weight-room': 'La Sala del Peso', 'sub_the-weight-room': 'Nombra lo que te pesa',
    'room_parallel-lives': 'Vidas Paralelas', 'sub_parallel-lives': 'En otra vida, eres...',
    'room_the-quiet-score': 'La Puntuación del Silencio', 'sub_the-quiet-score': '¿Cuánto puedes estar quieto?',
    'focus_choose': '¿Cuánto tiempo quieres sentarte en silencio?', 'focus_sit': 'Siéntate. Respira. Deja pasar los pensamientos.', 'focus_done': 'El silencio ha hablado.', 'quote_focus_timer': 'En la quietud, escuchas lo que el ruido ahoga.', 'minutes': 'min',
    'gratitude_prompt': 'Escribe algo por lo que estés agradecido hoy.', 'gratitude_placeholder': 'Estoy agradecido por...', 'gratitude_add': 'Añadir al frasco', 'gratitude_added': 'Tu gratitud flota en el frasco.', 'gratitude_count': 'momentos de gratitud', 'quote_gratitude_jar': 'La gratitud no es tener más. Es notar lo que ya está aquí.',
    'mood_compass_prompt': 'Haz clic donde sientes que estás ahora.', 'mood_high_energy': 'Alta Energía', 'mood_low_energy': 'Baja Energía', 'mood_positive': 'Positivo', 'mood_negative': 'Negativo', 'quote_mood_compass': 'Saber dónde estás es el primer paso para saber a dónde ir.',
    'memory_fade_prompt': 'Escribe un recuerdo. Míralo disolverse.', 'memory_fade_placeholder': 'Un recuerdo que quieres soltar...', 'memory_fade_start': 'Déjalo desvanecerse', 'memory_fade_done': 'Ya se fue. Pero fue real.', 'quote_memory_fade': 'Los recuerdos se disuelven los miremos o no.',
    'sound_day_prompt': 'Elige 3-5 sonidos que representen tu día.', 'sound_rain': 'Lluvia', 'sound_traffic': 'Tráfico', 'sound_birds': 'Pájaros', 'sound_keyboard': 'Teclado', 'sound_silence': 'Silencio', 'sound_wind': 'Viento', 'sound_ocean': 'Océano', 'sound_crowd': 'Multitud', 'sound_music': 'Música', 'sound_day_confirm': 'Este es mi día', 'sound_day_result': 'La banda sonora de tu día.', 'quote_sound_day': 'Cada día tiene un sonido. La mayoría olvida escuchar.',
    'apology_prompt': 'Escribe una disculpa que nunca entregaste.', 'apology_placeholder': 'Lo siento por...', 'apology_release': 'Libéralo', 'apology_done': 'Se comprimió en nada. Pero lo dijiste.', 'quote_apology': 'Algunas disculpas no son para ellos. Son para ti.',
    'unread_from_past': 'De: Tu Yo Pasado', 'unread_from_future': 'De: Tu Yo Futuro', 'unread_from_critic': 'De: Tu Crítico Interior', 'unread_msg_past': '¿Recuerdas cuando te prometiste que las cosas serían diferentes?', 'unread_msg_future': 'Espero que seas más amable contigo mismo ahora.', 'unread_msg_critic': 'Sabes que podrías haber hecho más. ¿Pero lo harás?', 'unread_count': 'no leídos', 'unread_reply_placeholder': 'Tu respuesta...', 'unread_reply': 'Enviar', 'unread_sent': 'Mensaje enviado al vacío.', 'quote_unread_messages': 'Los mensajes que evitamos suelen ser los que más necesitamos.',
    'weight_prompt': 'Nombra las cosas que te pesan.', 'weight_placeholder': 'Algo que me pesa...', 'weight_release_start': 'Empezar a soltar', 'weight_release_prompt': 'Haz clic en cada peso para soltarlo.', 'weight_done': 'El peso se ha levantado. Por ahora.', 'quote_weight_room': 'No puedes soltar lo que te niegas a nombrar.',
    'parallel_intro': 'En otra vida, eres...', 'parallel_life_1': 'En otra vida, eres un guardián de faro en una isla remota.', 'parallel_life_2': 'En otra vida, nunca dejaste tu ciudad natal.', 'parallel_life_3': 'En otra vida, dijiste sí cuando dijiste no.', 'parallel_life_4': 'En otra vida, eres alguien completamente diferente.', 'parallel_life_5': 'En otra vida, eres exactamente quien eres ahora.', 'parallel_life_6': 'En otra vida, elegiste el camino más difícil.', 'parallel_life_7': 'En otra vida, aún buscas esta sala.', 'quote_parallel_lives': 'Cada elección crea un universo donde elegiste diferente.',
    'quiet_instruction': 'No interactúes. Solo quédate quieto.', 'quiet_reveal_1': 'Estás aquí.', 'quiet_reveal_2': 'El ruido se desvanece.', 'quiet_reveal_3': 'Algo emerge del silencio.', 'quiet_reveal_4': 'Eres más paciente de lo que crees.', 'quiet_reveal_5': 'La quietud sabe tu nombre.', 'quiet_score_label': 'Tu puntuación de silencio', 'quiet_impressive': 'Quietud notable.', 'quiet_good': 'Encontraste algo de silencio.', 'quiet_try_again': 'La urgencia de actuar es fuerte.', 'quote_quiet_score': 'La quietud no es ausencia de movimiento. Es presencia de conciencia.',
    'update_v42_10_rooms': '10 nuevas salas interactivas: Temporizador, Gratitud, Brújula y más', 'update_v42_focus_timer': 'Temporizador de Enfoque — siéntate en silencio con oscurecimiento progresivo', 'update_v42_gratitude_jar': 'Frasco de Gratitud — colecciona momentos de gratitud', 'update_v42_mood_compass': 'Brújula del Ánimo — ubícate en ejes de energía y valencia', 'update_v42_weight_room': 'La Sala del Peso — nombra y suelta lo que te pesa',
    'update_v43_full_localization': 'Localización completa — todas las salas ahora totalmente traducidas', 'update_v43_40_rooms_translated': '40+ salas traducidas — cada sala interactiva soporta todos los idiomas', 'update_v43_10_languages': '10 idiomas soportados — inglés, árabe, español, portugués, chino, francés, hindi, coreano, japonés, turco', 'update_v43_nav_consistency': 'Navegación consistente — texto de salida unificado en todas las salas',
    'mirror_lies_title': 'El Espejo Miente', 'mirror_lies_msg1': 'Mírate.', 'mirror_lies_msg2': '¿Eres realmente tú?', 'mirror_lies_msg3': 'El espejo muestra lo que quieres ver.', 'mirror_lies_msg4': 'Haz clic para revelar la verdad.', 'mirror_lies_msg5': 'Cada grieta revela más.', 'mirror_lies_msg6': 'No hay nada detrás del reflejo.', 'mirror_lies_quote': 'El espejo nunca te mostró. Solo lo que creías.',
    'winter_title': 'La Noche Más Larga', 'winter_date': '21 DE DICIEMBRE', 'winter_msg1': 'La noche más larga está aquí.', 'winter_msg2': 'En la oscuridad, encontramos lo que llevamos.', 'winter_msg3': '¿Qué liberarás? ¿Qué conservarás?', 'winter_releasing': 'Estoy liberando:', 'winter_keeping': 'Estoy conservando:', 'winter_release_placeholder': 'Lo que ya no te sirve...', 'winter_keep_placeholder': 'Lo que te da fuerza...', 'winter_enter_light': 'Entra en la luz', 'winter_released': 'Liberado:', 'winter_kept': 'Conservado:', 'winter_light_returns': 'Desde esta noche, la luz regresa.', 'winter_quote': 'La noche más larga nos enseña que incluso en la oscuridad más profunda, la luz ya está regresando.',
    'halloween_title': 'La Sala del Velo', 'halloween_date': '31 DE OCTUBRE', 'halloween_msg1': 'El velo es delgado esta noche.', 'halloween_msg2': '¿Qué acecha en tus sombras?', 'halloween_msg3': 'Nombra el miedo que llevas contigo...', 'halloween_msg4': 'Dale forma, y libéralo.', 'halloween_placeholder': 'Escribe tu miedo...', 'halloween_release': 'Libéralo', 'halloween_dissolves': 'Tu miedo se disuelve en la noche...', 'halloween_quote': 'Las cosas que tememos pierden su poder cuando las nombramos. Déjalas ir.',
    'awake_title': 'Lo Que Te Mantiene Despierto', 'awake_locked': 'Esta sala solo está disponible después de las 10 PM.', 'awake_return': 'Regresa cuando llegue la noche.', 'awake_press_enter': 'Presiona Enter', 'awake_named': 'Ahora lo has nombrado.', 'awake_quote': 'La oscuridad sabe lo que evitas en la luz del día.',
    'voice_meaning_title': 'Tu Voz Sin Significado', 'voice_meaning_prompt': 'Tararea. Respira. Haz cualquier sonido. Sin palabras.', 'voice_meaning_done': 'Esta fue tu presencia. Nada más.', 'voice_meaning_hold': 'Mantén', 'voice_meaning_recording': 'Grabando...', 'voice_meaning_no_playback': 'Sin reproducción. El sonido existió solo en ese momento.', 'voice_meaning_quote': 'Existes. Ese es el único contenido.',
    'watching_msg1': 'Estás solo aquí.', 'watching_msg2': 'Completamente solo.', 'watching_msg3': '¿O lo estás?', 'watching_msg4': 'Han estado aquí todo el tiempo.', 'watching_msg5': 'Siempre están observando.',
    'shortcut_title': 'El Atajo Que Siempre Tomas', 'shortcut_instruction': 'Selecciona los que reconoces.', 'shortcut_1': 'Decir "estoy bien" cuando no lo estás', 'shortcut_2': 'Revisar tu teléfono en vez de estar presente', 'shortcut_3': 'Quedarte callado para evitar conflicto', 'shortcut_4': 'Comprar en vez de hacer', 'shortcut_5': 'Esperar el "momento adecuado"', 'shortcut_6': 'Culpar a las circunstancias en vez de actuar', 'shortcut_7': 'Elegir comodidad sobre crecimiento', 'shortcut_8': 'Desplazarte en vez de dormir', 'shortcut_9': 'Decir sí cuando quieres decir no', 'shortcut_10': 'Planificar en vez de hacer', 'shortcut_pattern': 'Tu Patrón', 'shortcut_selected': 'Seleccionaste:', 'shortcut_show_pattern': 'Muestra mi patrón', 'shortcut_quote': 'El atajo se convierte en el camino.', 'shortcut_pattern_own': 'Tu patrón es tuyo.', 'pattern_avoidance': 'Evitas lo que es incómodo.', 'pattern_distraction': 'Escapas hacia la distracción.', 'pattern_convenience': 'Cambias esfuerzo por facilidad.', 'pattern_delay': 'Esperas condiciones que nunca llegan.', 'pattern_comfort': 'Te proteges de la incomodidad.', 'pattern_pleasing': 'Desapareces para mantener cómodos a los demás.',
    'shapes_q1': '¿Con qué frecuencia te sientes en paz?', 'shapes_q2': '¿Cuánto confías en ti mismo?', 'shapes_q3': '¿Qué tan conectado te sientes con otros?', 'shapes_q4': '¿Cuánto de tu tiempo es realmente tuyo?', 'shapes_q5': '¿Qué tan pesado se siente el futuro?', 'shapes_result_title': 'Tu Vida como Formas', 'shapes_no_labels': 'Sin etiquetas. Sin interpretación.', 'shapes_yours': 'Lo que ves es tuyo para entender.', 'shapes_quote': 'La forma es significado sin palabras.', 'shapes_scale': '1 = nada, 5 = completamente',
    'read_title': 'Lee Esto en Voz Alta', 'read_stmt1': 'Estoy evitando algo.', 'read_stmt2': 'Tengo miedo.', 'read_stmt3': 'No sé lo que estoy haciendo.', 'read_stmt4': 'Necesito ayuda.', 'read_stmt5': 'Soy suficiente.', 'read_stmt6': 'He estado fingiendo.', 'read_stmt7': 'Estoy cansado.', 'read_stmt8': 'Quiero más.', 'read_i_read': 'Lo leí en voz alta', 'read_really': '¿Realmente lo dijiste?', 'read_skipped': 'Lo salté', 'read_result': 'Dijiste verdades en voz alta.', 'read_quote': 'Decir la verdad la hace real.',
    'sentence_title': 'La Frase Que Evitas', 'sentence_sub': 'Completa esta frase. No puedes irte hasta hacerlo.', 'sentence_prompt': 'Sería más feliz si dejara de', 'sentence_cant_leave': 'No puedes irte sin escribir algo.', 'sentence_knew': 'Ya lo sabías.', 'sentence_quote': 'El primer paso es decirlo.',
    'say_once_title': 'Dilo Una Vez', 'say_once_prompt': 'Graba una frase. No puedes reproducirla. No puedes regrabar.', 'say_once_done': 'Ha sido dicho. Sin reproducción. Sin repetición.', 'say_once_stop': 'Detener', 'say_once_record': 'Grabar', 'say_once_quote': 'Algunas cosas solo están destinadas a decirse una vez.',
    'newyear_title': 'Carta a Tu Yo Futuro', 'newyear_threshold': 'EL UMBRAL', 'newyear_placeholder': 'Querido yo futuro...', 'newyear_seal': 'Sellar la carta', 'newyear_waiting': 'Esta carta te estará esperando.', 'newyear_quote': 'Cada final es una puerta. Crúzala con intención.',
    'avoid_title': 'La Sala Que Evitas', 'avoid_sub': 'Lo que omites dice algo.', 'avoid_not_entered': 'No has entrado a:', 'avoid_visited_all': 'Has visitado cada sala gratuita.', 'avoid_not_pattern': 'Quizás la evasión no es tu patrón.', 'avoid_quote': 'Evitar es también una elección.',
    'who_q1': '¿Qué harías si el dinero no fuera un problema?', 'who_q2': '¿Cómo te presentarías en una fiesta?', 'who_q3': '¿Qué te hace sentir vivo?', 'who_q4': '¿Qué dirían tus amigos que haces bien?', 'who_q5': '¿Qué amabas hacer de niño?', 'who_result_title': 'Tú, Sin Título', 'who_done': 'Esto es quién eres cuando el título desaparece.', 'who_quote': 'La identidad no es lo que haces. Es lo que queda cuando dejas de hacerlo.', 'who_placeholder': 'Sé honesto...', 'who_see_myself': 'Verme', 'question': 'Pregunta',
    'lastconv_title': 'La Última Conversación', 'lastconv_sub': 'Escribe lo que nunca enviarás.', 'lastconv_to': 'Para:', 'lastconv_message': 'Mensaje:', 'lastconv_to_placeholder': 'Alguien que necesita escuchar esto...', 'lastconv_msg_placeholder': 'Lo que siempre quisiste decir...', 'lastconv_not_saved': 'Este mensaje no será guardado ni compartido.', 'lastconv_seal': 'Sellar la Carta', 'lastconv_wrote_to': 'Escribiste a:', 'lastconv_never_sent': 'Este mensaje nunca será enviado.', 'lastconv_point': 'Pero escribirlo era el punto.', 'lastconv_quote': 'Algunas palabras necesitan existir, aunque nunca sean escuchadas.',
    'priorities_title': 'Tus Prioridades Reales', 'priorities_sub': '¿Cuántas horas por semana dedicas a cada una?', 'priorities_result': 'Tus Prioridades Reales', 'priorities_most_time': 'Pasas la mayor parte de tu tiempo en:', 'priorities_truth': 'El tiempo revela la verdad. Las palabras mienten, los horarios no.', 'priorities_quote': 'Eres lo que haces repetidamente.', 'priorities_show': 'Muéstrame Mi Verdad', 'priority_work': 'Trabajo', 'priority_family': 'Familia', 'priority_friends': 'Amigos', 'priority_health': 'Salud', 'priority_learning': 'Aprendizaje', 'priority_entertainment': 'Entretenimiento', 'priority_social': 'Redes Sociales', 'priority_sleep': 'Sueño',
    'voice_intro': 'Una voz habla en la oscuridad...', 'voice_silence': '(silencio)', 'voice_quote': 'Algunas voces nunca encuentran su camino a casa.',
    'knows_tell': 'Dime tu nombre.', 'knows_placeholder': 'Tu nombre...', 'knows_warning': '(No deberías haber venido aquí)', 'knows_quote': 'Los nombres tienen poder. El tuyo ya era conocido.',
    'door_msg1': 'Algo está en la puerta.', 'door_stopped': '(Los golpes cesaron)', 'door_quote': 'Algunas puertas están destinadas a permanecer cerradas.',
    'eyes_msg1': 'Un par de ojos observa.', 'eyes_unsee': 'Nunca podrás dejar de verlos.', 'eyes_quote': 'Algunas cosas, una vez notadas, no pueden ignorarse.',
    'moon_locked': 'Esta sala solo se abre durante la luna llena.', 'moon_not_full': 'La luna no está llena esta noche. Regresa cuando el ciclo se complete.', 'moon_quote': 'Algunas verdades solo se revelan en ciclos.',
    'at317_title': 'Tú a las 3:17 AM', 'at317_locked': 'Esta sala se abre entre las 2 AM y las 5 AM.', 'at317_current': 'Hora actual:', 'at317_quote': 'La noche conoce tu nombre.',
    'mirrors_you_are_here': 'Estás aquí.',
    'listen_title': 'Escúchate', 'listen_recording': 'Grabando...', 'listen_start': 'Iniciar', 'listen_play': 'Reproducir', 'listen_instruction': 'Graba 10 segundos. Di lo que quieras.', 'listen_wait': 'Espera. Luego escucha.', 'listen_done': 'Esa era tu voz. Más lenta. Extraña.', 'listen_speed': 'Reproduciendo a velocidad 0.5x', 'listen_quote': 'Tu voz suena diferente porque finalmente la escuchaste.',
    'export_title': 'Exporta Tu Viaje', 'export_subtitle': 'Lleva una parte de este lugar contigo', 'export_tab_export': 'Exportar', 'export_tab_import': 'Importar', 'export_your_journey': 'TU VIAJE', 'export_since': 'Desde', 'export_time_spent': 'tiempo empleado', 'export_rooms_visited': 'salas visitadas', 'export_rooms_completed': 'salas completadas', 'export_fragments': 'fragmentos', 'export_achievements_unlocked': 'logros desbloqueados', 'export_truths_recorded': 'verdades registradas', 'export_text_file': 'Archivo de texto', 'export_json_file': 'Archivo JSON', 'export_copied': '¡Copiado!', 'export_copy_clipboard': 'Copiar al portapapeles', 'export_data_stays': 'Tus datos se quedan contigo. No los guardamos.', 'export_import_desc': 'Importar un viaje exportado previamente', 'export_json_only': 'Solo formato JSON', 'export_select_file': 'Seleccionar archivo', 'export_merge_note': 'Se fusionará con los datos de tu viaje actual.', 'export_done': 'Exportado.', 'export_done_desc': 'Un registro de dónde has estado. Lo que has enfrentado.', 'export_quote': 'Algunos viajes merecen ser documentados. No para otros. Para ti.', 'export_import_success': 'Viaje importado exitosamente', 'export_import_error': 'Archivo de viaje inválido',
    'waitroom_title': 'La Sala de Espera', 'waitroom_subtitle': 'Algunas puertas solo se abren para grupos.', 'waitroom_open': 'La puerta está abierta.', 'waitroom_waiting': 'esperando', 'waitroom_time_waited': 'Tiempo esperado', 'waitroom_users_needed': 'Usuarios necesarios', 'waitroom_someone_joined': 'Alguien se unió a la espera.', 'waitroom_someone_left': 'Alguien se fue.', 'waitroom_room_opens': 'La sala se abre.', 'waitroom_patience': 'La paciencia y la presencia desbloquean lo que la fuerza no puede.', 'waitroom_souls_waited': '{count} almas esperaron juntas.', 'waitroom_seconds_stillness': '{count} segundos de quietud colectiva.', 'waitroom_quote': 'Algunas cosas no se pueden apresurar. Algunas puertas no se pueden abrir solo.',
    'capsule_title': 'Cápsula del Tiempo', 'capsule_subtitle': 'Un mensaje para tu yo futuro', 'capsule_what_tell': '¿Qué necesitas decirte?', 'capsule_placeholder': 'Querido yo del futuro...', 'capsule_when_arrive': '¿Cuándo debe llegar?', 'capsule_30_days': '30 días', 'capsule_90_days': '90 días', 'capsule_1_year': '1 año', 'capsule_seal': 'Sellar la cápsula', 'capsule_waiting': 'cápsula(s) esperando', 'capsule_sealed': 'Sellada.', 'capsule_will_wait': 'Tu mensaje te esperará.', 'capsule_return_in': 'Vuelve en {days} días para abrirla.', 'capsule_quote_sealed': 'El tiempo lo cambia todo. Incluso a ti.', 'capsule_arrived': 'Ha llegado un mensaje.', 'capsule_from_past': 'Del tú de hace {days} días.', 'capsule_open': 'Abrir la cápsula', 'capsule_you_wrote': 'Te escribiste esto:', 'capsule_remember': '¿Recuerdas haber escrito esto?', 'capsule_quote_opened': 'La persona que escribió esto se fue. Tú permaneces.',
    'contradiction_title': 'La Sala de Contradicción', 'contradiction_subtitle': 'Donde tus verdades chocan', 'contradiction_not_enough': 'No hay suficientes datos aún.', 'contradiction_answer_more': 'Responde más preguntas en las salas.', 'contradiction_answers_recorded': '{count}/5 respuestas registradas', 'contradiction_quote_early': 'Las contradicciones requieren complejidad. Construye la tuya primero.', 'contradiction_no_found': 'No se encontraron contradicciones.', 'contradiction_consistent': 'O eres notablemente consistente...', 'contradiction_careful': '...o notablemente cuidadoso.', 'contradiction_quote_none': 'La consistencia es sabiduría o actuación.', 'contradiction_found': 'Encontramos {count} contradicción(es)', 'contradiction_you_said': 'Dijiste:', 'contradiction_but_also': 'Pero también:', 'contradiction_see_another': 'Ver otra →', 'contradiction_not_weakness': 'La contradicción no es debilidad. Es prueba de profundidad.', 'contradiction_quote': 'Contenemos multitudes. Algunas de ellas no están de acuerdo.',
    'entropy_living': 'vivas', 'entropy_dead': 'muertas', 'entropy_plant_seed': 'Plantar una semilla', 'entropy_instructions': 'Haz clic en espacios vacíos para plantar. Haz clic en plantas para regar. Las plantas muertas se pueden eliminar.', 'entropy_reflect': 'Reflexionar sobre tu jardín', 'entropy_lives': 'Tu jardín vive.', 'entropy_thrive': '{living} plantas prosperan porque volviste.', 'entropy_absence': '{dead} te recuerdan la ausencia.',
    'palace_title': 'Palacio de Memoria', 'palace_subtitle': 'Construye un espacio mental. Coloca objetos para recordar. Recórrelo después.', 'palace_room_of': 'Sala {current} de {total}', 'palace_click_place': 'Haz clic para colocar', 'palace_what_represent': '¿Qué representa esto?', 'palace_cancel': 'Cancelar', 'palace_place_object': 'Colocar un objeto', 'palace_objects_count': '{count} objeto(s) en tu palacio', 'palace_take_tour': 'Hacer el recorrido', 'palace_empty_room': 'Esta sala está vacía', 'palace_previous': 'Anterior', 'palace_next_room': 'Siguiente sala', 'palace_complete_tour': 'Completar recorrido', 'palace_remembers': 'Tu palacio recuerda.', 'palace_geography': 'Una geografía de significado que has construido.', 'palace_quote': 'La mente es un palacio. Lo que colocas dentro determina quién te conviertes.',
    'echoes_subtitle': 'Fragmentos de otros viajeros', 'echoes_from': 'de', 'echoes_ago': 'hace', 'echoes_arriving': 'Más ecos llegando...', 'echoes_reveal': 'Estas no son sus respuestas. Son las tuyas.', 'echoes_reveal2': '(Cada eco fue una vez tu propio reflejo)', 'echoes_quote': 'Creemos que somos únicos. Solo somos ecos de los demás.',
    'revisit_subtitle': 'Cómo has cambiado', 'revisit_not_enough': 'No has respondido suficientes preguntas aún.', 'revisit_explore': 'Explora más salas. Deja tus marcas. Vuelve después.', 'revisit_quote1': 'El cambio requiere historia. La historia requiere tiempo.', 'revisit_none_changed': 'Pero ninguna ha cambiado aún. Revisita salas. Responde de nuevo.', 'revisit_show_evolved': 'Te mostraremos cómo has evolucionado.', 'revisit_quote2': 'Para ver el cambio, primero debes cambiar.', 'revisit_then': 'Entonces', 'revisit_now': 'Ahora', 'revisit_one_shift': 'Un cambio. Sutil pero real.', 'revisit_each_change': 'Cada cambio es una pequeña muerte. Y un pequeño nacimiento.', 'revisit_quote3': 'No eres quien eras. Aún no eres quien serás.',
  },

  pt: {
    // Index
    'title': 'OS QUARTOS HUMANOS',
    'tagline': 'Uma coleção de quartos que refletem quem você é quando ninguém está olhando.',
    'enter': 'Entrar',
    'footer': 'Não é terapia. Não é social. Não é produtivo.',
    
    // Hallway
    'back': '← Voltar',
    'hallway': 'O Corredor',
    'challenges': 'Desafios',
    'random': 'Aleatório',
    'open_rooms': 'Quartos Abertos',
    'dark_rooms': 'Os Quartos Escuros',
    'enter_risk': 'entre por sua conta e risco',
    'premium_rooms': 'Quartos Premium',
    'unlock_to_access': 'Desbloqueie para acessar',
    'daily_rooms': 'Quartos Diários',
    'refreshes_daily': 'atualiza todos os dias',
    'time_locked': 'Quartos Bloqueados por Tempo',
    'seasonal_rooms': 'Quartos Sazonais',
    'available_now': 'Disponível agora',
    'fragments': 'Fragmentos',
    'boss_unlocked': 'Sala do Chefe Desbloqueada',
    'collect_unlock': 'Colete para desbloquear a Sala do Chefe',
    'game_rooms': 'Salas de Jogos',
    'experimental_rooms': 'Salas Experimentais',
    'deep_rooms': 'Salas Profundas',
    'meta_rooms': 'Salas Meta',
    'search_rooms': 'Buscar salas...',
    'no_results': 'Nenhuma sala corresponde à sua busca',
    'clear_filters': 'Limpar filtros',
    'explore': 'Explorar',
    'all_categories': 'Todas',
    
    // About
    'what_is_this': 'O que é este lugar?',
    'not_productivity': 'Os Quartos Humanos não é uma ferramenta de produtividade.',
    'not_social': 'Não é uma rede social.',
    'not_therapy': 'Não é terapia.',
    'pause_experiences': 'É uma coleção de pequenas experiências projetadas para fazer você pausar.',
    'each_room': 'Cada quarto leva de 30 segundos a 2 minutos.',
    'make_think': 'Alguns quartos vão fazer você pensar.',
    'make_uncomfortable': 'Alguns vão fazer você se sentir desconfortável.',
    'make_calm': 'Alguns vão te acalmar.',
    'make_aware': 'Todos vão te tornar consciente.',
    'no_notifications': 'Não há notificações aqui.',
    'no_points': 'Não há pontos para coletar.',
    'no_validation': 'Sem validação social.',
    'just_truth': 'Apenas você, um quarto, e a verdade.',
    'enter_rooms': 'Entrar nos quartos',
    
    // Common
    'loading': 'Carregando...',
    'error': 'Erro',
    'continue': 'Continuar',
    'submit': 'Enviar',
    'cancel': 'Cancelar',
    'close': 'Fechar',
    'next': 'Próximo',
    'previous': 'Anterior',
    'start': 'Iniciar',
    'stop': 'Parar',
    'yes': 'Sim',
    'no': 'Não',
    'leave_room': '← Sair do quarto',
    'return': '← Voltar',
    'return_hallway': 'Voltar ao Corredor',
    'begin': 'Começar',
    'watch': 'Observe...',
    'correct': 'Correto',
    'round': 'Rodada',
    'enter_again': 'Entrar novamente',
    'steps': 'Passos',
    'depth': 'Profundidade',
    
    // Keyboard Shortcuts
    'keyboard_shortcuts': 'Atalhos de Teclado',
    'shortcut_hallway': 'Ir ao Corredor',
    'shortcut_random': 'Quarto Aleatório',
    'shortcut_back': 'Voltar',
    'shortcut_bookmark': 'Alternar Favorito',
    'shortcut_reading': 'Modo Leitura',
    'shortcut_search': 'Focar Busca',
    'shortcut_stats': 'Ver Estatísticas',
    'shortcut_achievements': 'Ver Conquistas',
    'shortcut_cat_nav': 'Navegação',
    'shortcut_cat_rooms': 'Quartos',
    'shortcut_cat_access': 'Acessibilidade',
    'press_to_close': 'Pressione',
    'to_close': 'para fechar',
    'or': 'ou',
    
    // Bookmarks
    'add_bookmark': 'Adicionar Favorito',
    'remove_bookmark': 'Remover Favorito',
    'bookmarks': 'Favoritos',
    'bookmarked_rooms': 'Salvos',
    'no_bookmarks': 'Sem favoritos ainda',
    
    // Reading Mode
    'reading_mode': 'Modo Leitura',
    'reading_mode_on': 'Modo leitura ativado',
    'reading_mode_off': 'Modo leitura desativado',
    
    // Updates Page
    'updates': 'Atualizações',
    'updates_title': 'Novidades',
    'updates_subtitle': 'Mudanças e melhorias recentes',
    'updates_footer': 'Construindo quartos, uma atualização por vez.',
    'new': 'NOVO',
    'subscribe_rss': 'Inscreva-se via RSS',
    'latest': 'MAIS RECENTE',
    'whats_new_intro': 'Últimas atualizações:',
    'view_all_updates': 'Ver todas as atualizações',
    'update_updates_page': 'Página de atualizações adicionada',
    'update_full_i18n': 'Tradução completa para 6 idiomas',
    'update_hallway_animations': 'Animações suaves ao trocar categorias',
    'update_daily_challenges': 'Melhorias nos desafios diários',
    'update_game_rooms': '15 novos quartos de jogo',
    'update_experimental_rooms': '14 quartos experimentais',
    'update_random_room': 'Botão de quarto aleatório',
    'update_stats_route': 'Rota de estatísticas corrigida',
    'update_streak_system': 'Sistema de sequências diárias',
    'update_weekly_summary': 'Resumos de progresso semanal',
    'update_rtl_support': 'Suporte a idiomas RTL',
    'update_dark_rooms': 'Nova categoria de quartos escuros',
    'update_boss_rooms': 'Salas de chefe com fragmentos',
    'update_audio_system': 'Áudio ambiente nos quartos',
    'update_constellation_map': 'Mapa de constelações da jornada',
    'update_fragment_system': 'Sistema de fragmentos de quartos',
    'update_mobile_layout': 'Melhorias no layout mobile',
    'update_multi_language': 'Suporte multi-idioma (6 idiomas)',
    'update_premium_system': 'Sistema de quartos premium',
    'update_room_endings': 'Citações finais únicas',
    'update_ui_redesign': 'Redesenho completo da interface',
    'update_theme_effects': 'Efeitos visuais específicos por tema',
    'update_void_theme': 'Novo tema Vazio',
    'update_achievements': 'Sistema de conquistas',
    'update_ai_reflection': 'Sala de reflexão com IA',
    'update_settings_redesign': 'Configurações redesenhadas',
    'update_theme_exclusive_rooms': 'Salas exclusivas por tema',
    'update_discovery_localization': 'Mensagens de descoberta traduzidas',
    'update_site_mood_system': 'Sistema de humor do site',
    'update_voice_imperfection': 'Salas com voz imperfeita',
    'update_mobile_optimization': 'Otimização mobile',
    'update_interaction_clues': 'Dicas sutis de interação',
    'update_theme_exclusive_hallway': 'Salas exclusivas visíveis no corredor',
    'update_advanced_easter_eggs': 'Easter eggs avançados',
    'update_hidden_discoveries': 'Revelações de texto oculto',
    'update_dual_mode_system': 'Sistema de modo duplo Clássico/Alternativo',
    'update_alternate_mode': 'Modo alternativo - uma realidade paralela',
    'update_hidden_discovery': 'Gatilhos de descoberta ocultos',
    'update_mode_effects': 'Efeitos visuais específicos por modo',
    'update_translation_navigator': 'Navegador de Tradução - painel visual',
    'update_full_localization': 'Cobertura quase total em 6 idiomas',
    'update_meta_room_translations': 'Conteúdo das meta salas traduzido',
    'update_animated_timeline': 'Timeline animada com transições ao rolar',
    'update_toast_notifications': 'Notificação toast para novas atualizações',
    'update_rss_feed': 'Feed RSS para assinatura de mudanças',
    'update_bookmarked_filter': 'Aba de Quartos Favoritos no Corredor',
    'update_keyboard_shortcuts': 'Atalhos de teclado (pressione ? para ver)',
    'update_reading_mode': 'Modo leitura para tipografia e foco aprimorados',
    'update_accessibility': 'Indicadores de foco e navegação por teclado melhorados',
    'update_ambient_crossfade': 'Transições suaves entre faixas de áudio ambiente',
    'update_settings_audio': 'Controles de áudio centralizados na página de Configurações',
    'update_arabic_complete': 'Cobertura completa da tradução árabe',
    'update_duplicate_icon_fix': 'Ícones de áudio duplicados corrigidos no cabeçalho',

    // Game UI
    'game_watching': 'observando...',
    'game_repeat': 'repetir',
    'game_echo_fades': 'o eco desvanece...',
    'game_escaped': 'Você escapou.',
    'game_found_exit': 'Você encontrou a saída.',
    'game_looping': 'Você está em loop.',
    'game_deeper': 'Indo mais fundo...',
    'game_doors_shift': 'As portas mudam.',
    'maze_left': 'esquerda',
    'maze_right': 'direita',
    'maze_forward': 'frente',
    'maze_back': 'trás',
    'maze_past': 'passado',
    'maze_future': 'futuro',
    'maze_here': 'aqui',
    'maze_there': 'lá',
    'maze_truth': 'verdade',
    'maze_lies': 'mentiras',
    'maze_light': 'luz',
    'maze_dark': 'escuridão',
    'maze_memory': 'memória',
    'maze_dream': 'sonho',
    'maze_silence': 'silêncio',
    'maze_noise': 'ruído',
    'random_room': 'Quarto Aleatório',
    'ai_reflection': 'Reflexão IA',
    'support': 'Suporte',
    'choose_direction': 'Escolha uma direção. Algumas portas respondem.',
    'rooms_explored': 'quartos explorados',
    'your_journey': 'Sua Jornada',
    'complete_rooms_fragments': 'Complete quartos para coletar fragmentos.',
    'no_rooms_completed': 'Nenhum quarto completado ainda.',
    'constellation_map': 'Mapa das Constelações',
    'your_stats': 'Suas Estatísticas',
    
    // Stats
    'total_time': 'Tempo Total',
    'rooms_visited': 'Quartos Visitados',
    'total_visits': 'Total de Visitas',
    'most_visited': 'Mais Visitado',
    'longest_session': 'Sessão Mais Longa',
    'fragments_collected': 'Fragmentos Coletados',
    'completed_rooms': 'Quartos Completados',
    'privacy_note': 'Todos os dados são armazenados localmente no seu dispositivo',
    
    // Daily Challenges
    'daily_challenges': 'Desafios Diários',
    'challenges_completed': 'completado',
    'challenges_total': 'total',
    'resets_midnight': 'Reinicia à meia-noite',
    
    // Weekly Summary
    'weekly_summary': 'Resumo Semanal',
    'week': 'Semana',
    'this_week': 'Esta Semana',
    'rooms': 'Quartos',
    'days': 'Dias',
    'vs_last_week': 'vs semana passada',
    'refresh_insight': 'Atualizar insight',
    
    // Game Room Titles
    'room_memory-puzzle': 'Quebra-Cabeça de Memória',
    'room_shadow-chase': 'Perseguição de Sombras',
    'room_timing-room': 'Sala de Tempo',
    'room_pattern-whisper': 'Sussurro de Padrões',
    'room_silence-puzzle': 'Quebra-Cabeça do Silêncio',
    'room_decay-game': 'Jogo da Decadência',
    'room_observation-test': 'Teste de Observação',
    'room_balance-room': 'Sala do Equilíbrio',
    'room_word-drift': 'Deriva de Palavras',
    'room_color-echo': 'Eco de Cores',
    'room_mirror-tap': 'Toque do Espelho',
    'room_maze-of-doors': 'Labirinto de Portas',
    'room_pulse-match': 'Combinação de Pulso',
    'room_light-path': 'Caminho de Luz',
    'room_sound-match': 'Combinação de Som',
    
    // Game Room Subtitles
    'sub_memory-puzzle': 'Combine padrões que mudam sutilmente',
    'sub_shadow-chase': 'Pegue o que se move imprevisivelmente',
    'sub_timing-room': 'Clique exatamente no momento certo',
    'sub_pattern-whisper': 'Repita a sequência que desaparece',
    'sub_silence-puzzle': 'Resolva sem instruções',
    'sub_decay-game': 'Toque para preservar o que desvanece',
    'sub_observation-test': 'Encontre diferenças sutis',
    'sub_balance-room': 'Mantenha objetos flutuantes alinhados',
    'sub_word-drift': 'Pegue as letras antes de caírem',
    'sub_color-echo': 'Repita a sequência de tons sutis',
    'sub_mirror-tap': 'Cada toque ecoa em outro lugar',
    'sub_maze-of-doors': 'As portas se reorganizam após cada escolha',
    'sub_pulse-match': 'Mova-se no ritmo da sala',
    'sub_light-path': 'Guie a luz através da escuridão mutável',
    'sub_sound-match': 'Toque formas para combinar tons suaves',
    
    // Experimental Room Titles
    'room_mirror-room': 'Sala do Espelho',
    'room_echo-room': 'Sala do Eco',
    'room_pulse-room': 'Sala do Pulso',
    'room_vanishing-room': 'Sala que Desaparece',
    'room_letter-room': 'Sala das Cartas',
    'room_empty-room': 'Sala Vazia',
    'room_reverse-room': 'Sala Inversa',
    'room_whisper-room': 'Sala do Sussurro',
    'room_shadow-room': 'Sala das Sombras',
    'room_labyrinth-room': 'Sala do Labirinto',
    'room_color-room': 'Sala das Cores',
    'room_surrender-room': 'Sala da Rendição',
    'room_noise-room': 'Sala do Ruído',
    'room_observer-room': 'Sala do Observador',
    
    // Experimental Room Subtitles
    'sub_mirror-room': 'Reflexos ligeiramente distorcidos',
    'sub_echo-room': 'Cliques desencadeiam ecos atrasados',
    'sub_pulse-room': 'O ambiente respira',
    'sub_vanishing-room': 'Elementos desaparecem ao interagir',
    'sub_letter-room': 'Palavras se embaralham como memória falha',
    'sub_empty-room': 'Vazio até você encontrar segredos',
    'sub_reverse-room': 'Ações se comportam ao contrário',
    'sub_whisper-room': 'Verdades mal audíveis',
    'sub_shadow-room': 'Seu companheiro sombrio',
    'sub_labyrinth-room': 'Encontre seu caminho',
    'sub_color-room': 'Meditação cromática',
    'sub_surrender-room': 'Libere o controle',
    'sub_noise-room': 'Pensamentos estáticos',
    'sub_observer-room': 'Sendo observado',
    
    // Daily Joke Room
    'joke_number': 'Piada #{number}',
    'joke_today': 'Piada do Dia',
    'joke_reveal': 'Revelar a graça',
    'joke_come_back': 'Volte amanhã para uma nova piada.',
    'joke_quote': 'O riso é a menor distância entre duas pessoas.',
    
    // Daily Quote Room
    'quote_day_number': 'Dia {day}',
    'quote_come_back': 'Volte amanhã para uma nova citação.',
    
    // Leaderboard
    'leaderboard': 'Painel da Comunidade',
    'leaderboard_subtitle': 'anônimo · coletivo · sem competição',
    'collective_time': 'tempo coletivo',
    'achievements_count': 'conquistas',
    'most_visited_rooms': 'Salas mais visitadas',
    'most_time_spent': 'Mais tempo gasto',
    'most_unlocked_achievements': 'Conquistas mais desbloqueadas',
    'no_achievements_yet': 'Nenhuma conquista desbloqueada ainda.',
    
    // Transfer Data
    'transfer_data': 'Transferir dados',
    'transfer_data_desc': 'Mova seu progresso para outro dispositivo usando um código de uso único.',
    'generate_transfer_code': 'Gerar código de transferência',
    'enter_transfer_code': 'Digite o código...',
    'transfer_code_expires': 'O código expira em 24 horas e só pode ser usado uma vez.',
    'generating': 'Gerando...',
    'import': 'Importar',
    'copied': 'Copiado!',
    
    // Waiting Game Room
    'waiting_instruction': 'Não se mova. Não clique. Apenas espere.',
    'waiting_slow': 'Desacelerar é mais difícil do que parece.',
    'waiting_unfamiliar': 'Essa quietude parece desconhecida, não é?',
    'waiting_most': 'A maioria sai a essa altura.',
    'waiting_learning': 'Você está aprendendo algo sobre si mesmo.',
    'waiting_rewards': 'A paciência tem suas próprias recompensas.',
    'waiting_seconds': 'segundos de quietude',
    'waiting_waiting': 'esperando...',
    'waiting_moved': 'Você se moveu {count} vezes.',
    'waiting_waited': 'Você esperou. Isso é raro.',
    'waiting_most_cant': 'A maioria não consegue ficar parada. Você conseguiu.',
    'waiting_quote': 'O momento presente é o único momento disponível para nós, e é a porta para todos os momentos.',
    
    // Settings Page
    'settings': 'Configurações',
    'settings_subtitle': 'Personalize sua jornada',
    'language': 'Idioma',
    'ambient_audio': 'Áudio Ambiente',
    'ambient_audio_desc': 'Sons de fundo para os quartos',
    'reduced_motion': 'Movimento Reduzido',
    'reduced_motion_desc': 'Minimizar animações',
    'data_management': 'Gerenciamento de Dados',
    'export_journey': 'Exportar Dados da Jornada',
    'clear_all_data': 'Limpar Todos os Dados',
    'audio_enabled': 'Áudio Ativado',
    'audio_disabled': 'Áudio Desativado',
    'data_exported': 'Dados Exportados',
    'clear_data_confirm': 'Tem certeza? Isso apagará todo o seu progresso.',
    'data_cleared': 'Dados Limpos',
    // Theme settings
    'theme': 'Tema',
    'theme_dark': 'Escuro',
    'theme_dark_desc': 'Escuridão pura',
    'theme_bone': 'Osso',
    'theme_bone_desc': 'Palidez mortal',
    'theme_ash': 'Cinza',
    'theme_ash_desc': 'Após o fogo',
    'theme_fog': 'Névoa',
    'theme_fog_desc': 'Incerteza etérea',
    'theme_twilight': 'Crepúsculo',
    'theme_twilight_desc': 'Entre dia e noite',
    'theme_eclipse': 'Eclipse',
    'theme_eclipse_desc': 'Luz devorada pela escuridão',
    'theme_dawn': 'Aurora',
    'theme_dawn_desc': 'Primeira luz do despertar',
    'theme_changed': 'Tema atualizado',
    'font_size': 'Tamanho da Fonte',
    'font_small': 'Pequeno',
    'font_medium': 'Médio',
    'font_large': 'Grande',
    'font_size_changed': 'Tamanho da fonte atualizado',
    'high_contrast': 'Alto Contraste',
    'high_contrast_desc': 'Aumentar contraste de texto e bordas',
    'high_contrast_enabled': 'Alto contraste ativado',
    'high_contrast_disabled': 'Alto contraste desativado',
    'theme_void': 'Vazio',
    'theme_void_desc': 'Só restam sombras',
    
    // Ambient Music Control
    'ambient_music': 'Música Ambiente',
    'ambient_music_desc': 'Controle as paisagens sonoras de fundo',
    'volume': 'Volume',
    'ambient_track': 'Faixa Ambiente',
    'playing': 'Tocando',
    'play': 'Tocar',
    'ambient_default': 'Calmo',
    'ambient_night': 'Noite',
    'ambient_scary': 'Assustador',
    'ambient_truth': 'Verdade',
    'ambient_identity': 'Reflexão',
    'ambient_mortality': 'Tempo',
    'ambient_nothing': 'Vazio',
    'ambient_meta': 'Digital',
    'ambient_daily': 'Quente',
    'ambient_seasonal': 'Mágico',
    'ambient_premium': 'Profundo',
    
    // Easter Egg Discoveries
    'discovery_label': 'Descoberta',
    'discovery_konami': 'Você conhece os velhos caminhos.',
    'discovery_secret_sequence': 'Você soletrou a verdade.',
    'discovery_long_press': 'A paciência revela segredos.',
    'discovery_triple_click': 'A persistência compensa.',
    'discovery_silence': 'A quietude fala.',
    
    // Theme-Exclusive Rooms
    'ash_exclusive': 'Exclusivo do tema Cinza',
    'twilight_exclusive': 'Exclusivo do tema Crepúsculo',
    'void_exclusive': 'Exclusivo do tema Vazio',
    'theme_required': 'Requer tema {theme}',
    'ash_embers_title': 'Das Cinzas',
    'ash_embers_line1': 'Tudo queima eventualmente.',
    'ash_embers_line2': 'O que importa é o que surge depois.',
    'ash_embers_line3': 'Toque na tela. Crie brasas.',
    'ash_embers_quote': 'Das cinzas, lembramos o que uma vez queimou.',
    'room_ash-embers': 'Cinzas e Brasas',
    'sub_ash-embers': 'Só visível no tema Cinza',
    'twilight_stars_title': 'Entre Dia e Noite',
    'twilight_stars_line1': 'As estrelas lembram de todos que olharam para cima.',
    'twilight_stars_line2': 'Conecte-as. Faça sua própria constelação.',
    'twilight_stars_quote': 'No crepúsculo, vemos o que a luz do dia esconde.',
    'room_twilight-stars': 'Estrelas do Crepúsculo',
    'sub_twilight-stars': 'Só visível no tema Crepúsculo',
    'void_abyss_title': 'O Abismo',
    'void_abyss_line1': 'Olhe para o vazio. O vazio olha de volta.',
    'void_word_1': 'nada',
    'void_word_2': 'vazio',
    'void_word_3': 'silêncio',
    'void_word_4': 'ido',
    'void_word_5': 'sozinho',
    'void_word_6': 'fim',
    'void_revealed': 'revelado',
    'void_abyss_quote': 'Na escuridão absoluta, finalmente nos vemos.',
    'room_void-abyss': 'O Abismo',
    'sub_void-abyss': 'Só visível no tema Vazio',
    'shared_room': 'Quarto Compartilhado',
    'imagine_room': 'Imaginar',
    'imagine_title': 'Imagine Você',
    'imagine_subtitle': 'Descreva como você se vê. Deixe o quarto mostrar o que emerge das suas palavras.',
    'imagine_placeholder': 'Me vejo como alguém que...\n\nDescreva sua essência, sua natureza, como você se sente por dentro.',
    'imagine_hint': 'Seja honesto. A imagem reflete suas palavras.',
    'imagine_generate': 'Ver a Mim',
    'imagine_generating': 'Criando...',
    'imagine_try_again': 'Descrever Novamente',
    'imagine_quote': 'Não nos vemos claramente. Vemos o que acreditamos.',
    'room_imagine-yourself': 'Imagine Você',
    'sub_imagine-yourself': 'Descreva-se. Veja o que emerge.',
    
    // Theme Exclusive Section
    'theme_exclusive_rooms': 'Quartos Exclusivos por Tema',
    'theme_exclusive_hint': 'Mude o tema nas configurações para desbloquear estes quartos.',
    'requires_theme': 'Requer',
    'unlocked': 'Desbloqueado',
    'switch_to': 'Mudar para',
    
    // Advanced Easter Eggs
    'corner_sequence': 'Você traçou os limites.',
    'morse_sos': 'S.O.S. recebido.',
    'reverse_scroll': 'Voltando... mas mais rápido.',
    'midnight_visitor': 'A hora das bruxas te dá boas-vindas.',
    'shake': 'Você sacudiu algo.',
    
    // Experience Mode
    'experience_mode': 'Modo de Experiência',
    'classic_mode': 'Clássico',
    'classic_mode_desc': 'A realidade familiar',
    'alternate_mode': '▒ Alternativo ▒',
    'alternate_mode_desc': '[ Você não deveria estar aqui ]',
    'switch_to_alternate': '░▓█ descer █▓░',
    'switch_to_classic': '← Voltar à superfície',
    'enter_parallel_reality': '[ A porta está aberta ]',
    'return_to_familiar': 'Voltar ao familiar',
    'something_shifted': '[ Algo mudou ]',
    'reality_feels_different': '[ A realidade parece diferente agora ]',
    'alternate_tagline': 'Os mesmos quartos. Verdades diferentes.',
    'alternate_enter': 'Descer',
    'alternate_footer': '[ As coisas não são o que parecem ]',
    'alternate_rooms': '░▓ Re4l1dade P4ralela ▓░',
    'alternate_rooms_hint': '[ estes qu4rtos só ex1stem no paralelo ]',
    
    // Alternate Mode Exclusive Rooms (Portuguese)
    'room_the-other-side': 'O Outro Lado',
    'room_the-unraveling': 'O Desfiamento',
    'room_the-echo': 'O Eco',
    'sub_the-other-side': 'mova-se lentamente no escuro',
    'sub_the-unraveling': 'a realidade se desfaz a cada palavra',
    'sub_the-echo': 'o que você diz nunca vai embora',
    
    // Meta Rooms (Portuguese)
    'room_the-refusal': 'A Recusa',
    'sub_the-refusal': 'Às vezes o site te recusa',
    'room_parallel-you': 'Você Paralelo',
    'sub_parallel-you': 'Outra versão de você',
    'room_your-shadow': 'Sua Sombra',
    'sub_your-shadow': 'O que te segue',
    
    // AI Rooms (Portuguese)
    'room_ai-reflection-room': 'Sala de Reflexão IA',
    'sub_ai-reflection-room': 'Deixe a IA refletir com você',
    'sub_dream-visualization': 'Descreva seu sonho. Veja-o tomar forma.',
    'sub_future-self': 'Uma mensagem do futuro',
    
    // Meta Room Content - The Refusal (Portuguese)
    'refusal_line1': 'Esta sala te recusa.',
    'refusal_line2': 'Nem tudo se abre quando você quer.',
    'refusal_line3': 'Às vezes a resposta é simplesmente: não.',
    'refusal_line4': 'Tente o botão. Não vai ajudar.',
    'refusal_button': 'Entrar na Sala',
    
    // Meta Room Content - Parallel You (Portuguese)
    'parallel_choice_1': 'Você escolheu velocidade.',
    'parallel_alternate_1': 'Outro você escolheu paciência.',
    'parallel_reflection_1': 'Eles ainda estão esperando.',
    'parallel_choice_2': 'Você saiu cedo.',
    'parallel_alternate_2': 'Outro você ficou até o fim.',
    'parallel_reflection_2': 'Eles viram o que você perdeu.',
    'parallel_choice_3': 'Você evitou a pergunta difícil.',
    'parallel_alternate_3': 'Outro você respondeu.',
    'parallel_reflection_3': 'Eles carregam essa verdade agora.',
    'parallel_choice_4': 'Você passou correndo.',
    'parallel_alternate_4': 'Outro você saboreou cada momento.',
    'parallel_reflection_4': 'Eles lembram mais.',
    'parallel_choice_5': 'Você continuou rolando.',
    'parallel_alternate_5': 'Outro você parou.',
    'parallel_reflection_5': 'Eles encontraram algo.',
    'parallel_choice_6': 'Você escolheu conforto.',
    'parallel_alternate_6': 'Outro você escolheu desconforto.',
    'parallel_reflection_6': 'Eles cresceram com isso.',
    'parallel_quote': 'Cada momento se divide em caminhos infinitos. Você só caminha um.',
    
    // Meta Room Content - Your Shadow (Portuguese)
    'shadow_generated': 'Gerado do seu comportamento',
    'shadow_intro': 'Sua sombra...',
    'shadow_follows': 'Esta sombra te segue por cada sala.',
    'shadow_quote': 'Não somos o que pensamos. Somos o que fazemos.',
    'shadow_trait_completion': 'busca completar',
    'shadow_trait_surface': 'prefere a superfície',
    'shadow_trait_lingers': 'permanece em espaços',
    'shadow_trait_moves_quickly': 'move-se rápido',
    'shadow_trait_avoids_depth': 'evita profundidade',
    'shadow_trait_stillness': 'abraça a quietude',
    'shadow_trait_chases': 'persegue algo sem nome',
    'shadow_trait_darkness': 'atraído pela escuridão',
    'shadow_trait_emptiness': 'confortável com o vazio',
    'shadow_trait_undefined': 'indefinido',
    'shadow_trait_forming': 'ainda se formando',
    
    // Translation Navigator (Portuguese)
    'translation_navigator': 'Navegador de Traduções',
    'translation_coverage': 'Cobertura de tradução por idioma',
    'keys': 'chaves',
    'room_translations': 'Traduções de Salas',
    'show_all': 'Mostrar Tudo',
    'show_missing': 'Mostrar Faltantes',
    'all_translated': 'Todas as salas estão traduzidas!',
    'missing_translations': 'traduções faltantes',
    
    // Additional Room Titles (Portuguese)
    'room_how-many-times-left': 'Quantas Vezes Restam',
    'room_life-in-weeks': 'Sua Vida em Semanas',
    'room_delay-button': 'O Botão de Atraso',
    'room_one-honest-sentence': 'Uma Frase Honesta',
    'room_this-moment': 'Este Momento Não Se Repetirá',
    'room_reality-receipt': 'Recibo da Realidade',
    'room_nothing-happens': 'Nada Acontece Aqui',
    'room_default-choice': 'Sua Escolha Padrão',
    'room_thought-loop': 'O Loop de Pensamentos',
    'room_expect-vs-happens': 'O que Você Espera vs O que Acontece',
    'room_question-never-answer': 'A Pergunta que Você Nunca Responde',
    'room_not-special': 'Você Não É Especial (E Tudo Bem)',
    'room_shortcut-always-take': 'O Atalho que Você Sempre Pega',
    'room_blurred-self': 'Eu Borrado',
    'room_image-zooming': 'A Imagem que Você Amplia',
    'room_comfort-color': 'A Cor do Conforto',
    'room_line-you-crossed': 'A Linha que Você Cruzou',
    'room_read-aloud': 'Leia Isso em Voz Alta',
    'room_voice-without-meaning': 'Sua Voz Sem Significado',
    'room_sentence-you-avoid': 'A Frase que Você Evita',
    'room_if-nothing-changes': 'Se Nada Mudar',
    'room_real-priorities': 'Suas Prioridades Reais',
    'room_last-conversation': 'A Última Conversa',
    'room_who-without-job': 'Quem É Você Sem Seu Trabalho?',
    'room_comfort-zone-scanner': 'Scanner de Zona de Conforto',
    'room_say-it-once': 'Diga Uma Vez',
    'room_listen-to-yourself': 'Ouça a Si Mesmo',
    'room_life-as-shapes': 'Sua Vida como Formas',
    'room_face-without-features': 'Rosto Sem Traços',
    'room_what-keeps-awake': 'O que Te Mantém Acordado',
    'room_you-at-317am': 'Você às 3:17 AM',
    'room_thought-that-returns': 'O Pensamento que Retorna',
    'room_room-you-visit-most': 'A Sala que Você Mais Visita',
    'room_room-you-avoid': 'A Sala que Você Evita',
    'room_left-too-early': 'Você Saiu Muito Cedo',
    'room_daily-quote': 'Citação do Dia',
    'room_daily-joke': 'Piada do Dia',
    'room_theyre-watching': 'Estão Te Observando',
    'room_the-last-visitor': 'O Último Visitante',
    'room_dont-look-away': 'Não Desvie o Olhar',
    'room_the-whisper': 'O Sussurro',
    'room_you-forgot-something': 'Você Esqueceu Algo',
    'room_the-mirror-lies': 'O Espelho Mente',
    'room_something-follows': 'Algo Te Segue',
    'room_the-countdown': 'A Contagem Regressiva',
    'room_eyes-in-the-dark': 'Olhos no Escuro',
    'room_the-voice': 'A Voz',
    'room_it-knows-your-name': 'Sabe Seu Nome',
    'room_the-door': 'A Porta',
    'room_boss-room-free': 'A Soma das Suas Verdades',
    'room_boss-room-horror': 'O Coração do Medo',
    'room_boss-room-premium': 'A Verdade Mais Profunda',
    'room_mouse-tracker': 'O Rastreador de Mouse',
    'room_scroll-confession': 'Confissão de Rolagem',
    'room_waiting-game': 'O Jogo da Espera',
    'room_day-in-pixels': 'Seu Dia em Pixels',
    'room_memory-maze': 'Labirinto de Memória',
    'room_decision-shadow': 'Sombra de Decisão',
    'room_anonymous-mirrors': 'Espelhos Anônimos',
    'room_collective-room': 'A Sala Coletiva',
    'room_secret-room': 'A Sala Secreta',
    'room_glitch-room': 'Sala Instável',
    'room_full-moon-room': 'Sala da Lua Cheia',
    'room_weekend-room': 'Sala de Fim de Semana',
    'room_317am-room': 'Sala das 3:17 AM',
    'room_idle-room': 'A Sala Parada',
    'room_invite-friend-room': 'A Sala Compartilhada',
    'room_halloween-room': 'A Sala do Véu',
    'room_winter-solstice-room': 'A Noite Mais Longa',
    'room_new-year-room': 'Carta ao Seu Eu Futuro',
    'room_void-room': 'O Vazio',
    'room_pattern-room': 'Seus Padrões',
    'room_time-capsule': 'Cápsula do Tempo',
    'room_the-revisit': 'A Revisita',
    'room_contradiction-room': 'A Sala da Contradição',
    'room_export-journey': 'Exportar Sua Jornada',
    'room_anonymous-echoes': 'Ecos Anônimos',
    'room_the-waiting-room': 'A Sala de Espera',
    'room_dream-visualization': 'Visualização de Sonhos',
    'room_future-self': 'Seu Eu Futuro',
    'room_eclipse-shadows': 'Sombras do Eclipse',
    'room_dawn-awakening': 'Despertar da Aurora',
    
    // Additional Room Subtitles (Portuguese)
    'sub_how-many-times-left': 'Uma contagem do que resta',
    'sub_life-in-weeks': '4.000 quadrados. É tudo.',
    'sub_delay-button': 'Fica mais difícil a cada vez',
    'sub_one-honest-sentence': 'Sem editar. Sem apagar.',
    'sub_this-moment': 'Você está aqui. Uma vez.',
    'sub_reality-receipt': 'Um resumo dos seus dias',
    'sub_nothing-happens': 'Exatamente o que diz',
    'sub_eclipse-shadows': 'Fale na escuridão total',
    'sub_dawn-awakening': 'Saudações à primeira luz',
    'sub_default-choice': 'Segurança ou risco. Sempre.',
    'sub_thought-loop': 'Veja sua mente se repetir',
    'sub_expect-vs-happens': 'A lacuna entre esperança e realidade',
    'sub_question-never-answer': 'A maioria sai',
    'sub_not-special': 'Sem conforto. Só verdade.',
    'sub_shortcut-always-take': 'Os padrões se revelam',
    'sub_blurred-self': 'A memória funciona assim',
    'sub_image-zooming': 'Buscando respostas no nada',
    'sub_comfort-color': 'Quanto tempo você vai ficar?',
    'sub_line-you-crossed': 'Vemos onde você hesitou',
    'sub_read-aloud': 'Palavras que você deve falar',
    'sub_voice-without-meaning': 'Som sem palavras',
    'sub_sentence-you-avoid': 'Você não pode sair até escrever',
    'sub_if-nothing-changes': 'Uma projeção dos seus próximos cinco anos',
    'sub_real-priorities': 'Baseado em tempo, não em palavras',
    'sub_last-conversation': 'Escreva o que nunca enviará',
    'sub_who-without-job': 'Reescreva-se',
    'sub_comfort-zone-scanner': 'Vemos sua hesitação',
    'sub_say-it-once': 'Sem repetição. Sem refazer.',
    'sub_listen-to-yourself': 'Sua voz, desacelerada',
    'sub_life-as-shapes': 'Abstrato. Sem rótulos. Seu.',
    'sub_face-without-features': 'Remova até não restar nada',
    'sub_what-keeps-awake': 'Disponível após 22h',
    'sub_you-at-317am': 'Específico do tempo. Pessoal.',
    'sub_thought-that-returns': 'Retorna a cada visita',
    'sub_room-you-visit-most': 'Seu padrão revelado',
    'sub_room-you-avoid': 'O que você evita diz algo',
    'sub_left-too-early': 'Acionado quando você sai rápido',
    'sub_daily-quote': 'Sabedoria que muda diariamente',
    'sub_daily-joke': 'Humor negro, atualizado diariamente',
    'sub_theyre-watching': 'Você não está sozinho',
    'sub_the-last-visitor': 'Alguém esteve aqui antes de você',
    'sub_dont-look-away': 'Sabe quando você sai',
    'sub_the-whisper': 'Ouça com atenção',
    'sub_you-forgot-something': 'O que era?',
    'sub_the-mirror-lies': 'Quebre para ver a verdade',
    'sub_something-follows': 'Continue correndo',
    'sub_the-countdown': 'Algo acontece no zero',
    'sub_eyes-in-the-dark': 'Eles se multiplicam',
    'sub_the-voice': 'Alguém está preso aqui',
    'sub_it-knows-your-name': 'Você não deveria ter vindo',
    'sub_the-door': 'Algo está batendo',
    'sub_boss-room-free': 'A sala livre final',
    'sub_boss-room-horror': 'Enfrente a escuridão final',
    'sub_boss-room-premium': 'A sala profunda final',
    'sub_mouse-tracker': 'Vemos como você se move',
    'sub_scroll-confession': 'Quão rápido você se apressa?',
    'sub_waiting-game': 'Você consegue não fazer nada?',
    'sub_day-in-pixels': 'O tempo vira forma',
    'sub_memory-maze': 'O padrão muda',
    'sub_decision-shadow': 'Suas escolhas revelam padrões',
    'sub_anonymous-mirrors': 'Pensamentos de estranhos',
    'sub_collective-room': 'Todos estão aqui. Ninguém fica.',
    'sub_secret-room': 'Você encontrou',
    'sub_glitch-room': 'Algo está errado',
    'sub_full-moon-room': 'Só abre durante o pico lunar',
    'sub_weekend-room': 'O descanso é um destino',
    'sub_317am-room': 'Específico do tempo. Pessoal.',
    'sub_idle-room': 'Encontrado através da paciência',
    'sub_invite-friend-room': 'Algumas jornadas devem ser testemunhadas',
    'sub_halloween-room': '31 de outubro - O véu está fino',
    'sub_winter-solstice-room': '21 de dezembro - Libere e guarde',
    'sub_new-year-room': '31 de dezembro - Escreva para quem você será',
    'sub_void-room': 'Uma ferida no código',
    'sub_pattern-room': 'O que o site vê em você',
    'sub_time-capsule': 'Escreva ao seu eu futuro',
    'sub_the-revisit': 'Veja como suas respostas mudaram',
    'sub_contradiction-room': 'Suas verdades não concordam',
    'sub_export-journey': 'Baixe seu caminho',
    'sub_anonymous-echoes': 'Fragmentos de estranhos',
    'sub_the-waiting-room': 'Desbloqueie com presença',
    
    // New Phase 1-3 Rooms (Portuguese)
    'room_letters-never-sent': 'Cartas Nunca Enviadas',
    'sub_letters-never-sent': 'Escreva para alguém que nunca lerá',
    'room_breath-sync': 'Sincronização de Respiração',
    'sub_breath-sync': 'Respire com a sala',
    'room_entropy-garden': 'Jardim de Entropia',
    'sub_entropy-garden': 'Crescimento e decadência entrelaçados',
    'room_memory-palace': 'Palácio da Memória',
    'sub_memory-palace': 'Construa seu santuário mental',
    'room_morning-ritual': 'Ritual Matinal',
    'sub_morning-ritual': 'Comece seu dia com intenção',
    'room_evening-ritual': 'Ritual Noturno',
    'sub_evening-ritual': 'Feche o dia com reflexão',
    'room_weekly-ritual': 'Ritual Semanal',
    'sub_weekly-ritual': 'Domingos são para olhar para trás',
    
    // Letters Never Sent Room (Portuguese)
    'letters_title': 'Cartas Nunca Enviadas',
    'letters_subtitle': 'Escreva para alguém que nunca lerá. A IA responderá como eles poderiam.',
    'letters_select_recipient': 'Para quem você está escrevendo?',
    'letters_past_self': 'Meu Eu Passado',
    'letters_future_self': 'Meu Eu Futuro',
    'letters_someone_lost': 'Alguém que Perdi',
    'letters_someone_wronged': 'Alguém que Magoei',
    'letters_placeholder': 'Querido/a...',
    'letters_send': 'Enviar ao Vazio',
    'letters_sending': 'Enviando...',
    'letters_response': 'Uma resposta emerge...',
    'letters_write_another': 'Escrever Outra Carta',
    'letters_quote': 'Algumas palavras são feitas para serem escritas mas nunca enviadas.',
    
    // Breath Sync Room (Portuguese)
    'breath_title': 'Sincronização de Respiração',
    'breath_subtitle': 'Sincronize sua respiração com o ritmo da sala.',
    'breath_inhale': 'Inspire',
    'breath_hold': 'Segure',
    'breath_exhale': 'Expire',
    'breath_cycles': 'Ciclos completados',
    'breath_quote': 'A respiração é a ponte entre corpo e mente.',
    
    // Entropy Garden Room (Portuguese)
    'entropy_title': 'Jardim de Entropia',
    'entropy_subtitle': 'Suas plantas crescem a cada visita, mas decaem quando você se afasta.',
    'entropy_plant': 'Plantar uma Semente',
    'entropy_water': 'Regar',
    'entropy_days_alive': 'dias viva',
    'entropy_health': 'Saúde',
    'entropy_empty': 'Seu jardim está vazio. Plante algo.',
    'entropy_quote': 'Tudo cresce. Tudo murcha. Esse é o caminho.',
    
    // Memory Palace Room (Portuguese)
    'memory_palace_title': 'Palácio da Memória',
    'memory_palace_subtitle': 'Construa um santuário mental. Coloque objetos em salas que você cria.',
    'memory_palace_add_room': 'Adicionar Sala',
    'memory_palace_add_object': 'Adicionar Objeto',
    'memory_palace_room_name': 'Nome da sala...',
    'memory_palace_object_name': 'Nome do objeto...',
    'memory_palace_empty': 'Seu palácio está vazio. Crie sua primeira sala.',
    'memory_palace_quote': 'Construímos nossas mentes uma sala de cada vez.',
    
    // Ritual Rooms (Portuguese)
    'ritual_locked': 'Este ritual ainda não está disponível',
    'morning_ritual_title': 'Ritual Matinal',
    'morning_ritual_intro': 'Um momento para definir intenções para o dia.',
    'morning_ritual_available': 'Disponível 6h - 9h',
    'evening_ritual_title': 'Ritual Noturno',
    'evening_ritual_intro': 'Um momento para refletir sobre o dia.',
    'evening_ritual_available': 'Disponível 20h - 23h',
    'weekly_ritual_title': 'Ritual Semanal',
    'weekly_ritual_intro': 'Um momento para refletir sobre a semana que passou, e definir intenções para a próxima.',
    'weekly_locked': 'O Ritual Semanal te espera',
    'weekly_time': 'Volte no domingo',
    'today_is': 'Hoje é',
    'your_week': 'Sua Semana',
    'days_active': 'Dias Ativos',
    'weekly_summary_note': 'Agora, vamos refletir mais profundamente.',
    'weekly_highlight': 'Qual foi o destaque da sua semana?',
    'weekly_challenge': 'O que mais te desafiou esta semana?',
    'weekly_growth': 'Como você cresceu esta semana?',
    'weekly_intention': 'Que intenção você define para a próxima semana?',
    'weekly_placeholder': 'Leve seu tempo...',
    'reflect': 'Refletir',
    'reflecting': 'Refletindo...',
    'complete': 'Completar',
    'weekly_quote': 'Uma semana termina. Outra começa. Você carrega o que importa.',
    
    // Personal Timeline (Portuguese)
    'personal_timeline': 'Linha do Tempo Pessoal',
    'timeline_subtitle': 'Sua jornada através das salas',
    'timeline_empty': 'Sua linha do tempo está vazia. Comece a explorar.',
    'timeline_today': 'Hoje',
    'timeline_yesterday': 'Ontem',
    'timeline_this_week': 'Esta Semana',
    'timeline_earlier': 'Antes',
    
    // Weather System (Portuguese)
    'weather_clear': 'Limpo',
    'weather_rain': 'Chuva',
    'weather_fog': 'Névoa',
    'weather_snow': 'Neve',
    
    // Notifications (Portuguese)
    'notifications': 'Notificações',
    'notifications_desc': 'Receba notificações sobre conteúdo bloqueado por tempo',
    'notify_time_locked': 'Salas bloqueadas por tempo',
    'notify_full_moon': 'Noites de lua cheia',
    'notify_daily_reset': 'Reinício de desafios diários',
    'notify_time_capsule': 'Desbloqueio de cápsulas do tempo',
    'notifications_enabled': 'Notificações habilitadas',
    'notifications_disabled': 'Notificações desabilitadas',
    
    // Community Submissions (Portuguese)
    'community_submissions': 'Contribuições da Comunidade',
    'community_subtitle': 'Envie suas ideias de salas',
    'community_room_title': 'Título da Sala',
    'community_room_description': 'Descrição da Sala',
    'community_room_category': 'Categoria',
    'community_submit': 'Enviar Ideia',
    'community_submitted': 'Obrigado pela sua contribuição!',
    'community_your_submissions': 'Suas Contribuições',
    'community_empty': 'Ainda não há contribuições. Seja o primeiro!',
    
    // Proactive Guide (Portuguese)
    'guide_hesitation': 'Tomando seu tempo? Tudo bem. Gostaria de uma sugestão?',
    'guide_long_absence': 'Bem-vindo de volta. Faz um tempo.',
    'guide_milestone': 'Você veio longe. Gostaria de refletir sobre sua jornada?',
    'guide_dismiss': 'Dispensar',
    'guide_accept': 'Mostre-me',
    
    // Voice Mode (Portuguese)
    'voice_mode': 'Modo de Voz',
    'voice_mode_desc': 'Deixe o guia falar com você',
    'voice_mode_enabled': 'Modo de voz habilitado',
    'voice_mode_disabled': 'Modo de voz desabilitado',
    
    // Offline Support (Portuguese)
    'offline_available': 'Disponível offline',
    'offline_cached': 'Esta sala está armazenada para visualização offline',
    
    // Journey Export (Portuguese)
    'journey_export': 'Exportar Jornada',
    'journey_import': 'Importar Jornada',
    'journey_export_json': 'Exportar como JSON',
    'journey_import_json': 'Importar de JSON',
    'journey_exported': 'Jornada exportada com sucesso',
    'journey_imported': 'Jornada importada com sucesso',
    
    // Phase 4 - Seasonal Rooms (Portuguese)
    'room_spring-equinox-room': 'Equinócio de Primavera',
    'sub_spring-equinox-room': 'Liberte e renove',
    'room_summer-solstice-room': 'Solstício de Verão',
    'sub_summer-solstice-room': 'O dia mais longo',
    'room_autumn-equinox-room': 'Equinócio de Outono',
    'sub_autumn-equinox-room': 'Colha e aceite',
    'room_valentine-room': 'Sala de Amor Próprio',
    'sub_valentine-room': 'O amor que você merece',
    
    // Spring Equinox Room (Portuguese)
    'spring_date': '20 DE MARÇO',
    'spring_title': 'Equinócio de Primavera',
    'spring_message_1': 'Dia e noite são iguais.',
    'spring_message_2': 'Um momento de equilíbrio perfeito.',
    'spring_message_3': 'O que você vai liberar para dar espaço ao crescimento?',
    'spring_letting_go': 'Estou deixando ir:',
    'spring_letting_go_placeholder': 'O que não te serve mais...',
    'spring_planting': 'Estou plantando:',
    'spring_planting_placeholder': 'O que você quer que cresça...',
    'spring_bloom': 'Deixe florescer',
    'spring_released': 'Liberado:',
    'spring_planted': 'Plantado:',
    'spring_closing': 'A partir deste momento, uma nova vida começa.',
    'spring_quote': 'A natureza não tem pressa, mas tudo se realiza.',
    
    // Summer Solstice Room (Portuguese)
    'summer_date': '21 DE JUNHO',
    'summer_title': 'O Dia Mais Longo',
    'summer_message_1': 'Este é o dia mais longo do ano.',
    'summer_message_2': 'A luz atinge seu pico.',
    'summer_message_3': 'O que você faria com mais tempo?',
    'summer_message_4': 'Descreva seu dia mais pleno e vibrante.',
    'summer_placeholder': 'No meu dia mais pleno, eu...',
    'summer_submit': 'Abrace a luz',
    'summer_closing': 'O sol te ouviu. Agora começa sua descida.',
    'summer_quote': 'No auge da luz, descobrimos o que realmente queremos iluminar.',
    
    // Autumn Equinox Room (Portuguese)
    'autumn_date': '22 DE SETEMBRO',
    'autumn_title': 'Equinócio de Outono',
    'autumn_message_1': 'O equilíbrio retorna.',
    'autumn_message_2': 'A colheita está pronta.',
    'autumn_message_3': 'O que você cultivou? O que pode aceitar?',
    'autumn_harvesting': 'Estou colhendo:',
    'autumn_harvesting_placeholder': 'O que você cultivou este ano...',
    'autumn_accepting': 'Estou aceitando:',
    'autumn_accepting_placeholder': 'O que você não pode mudar...',
    'autumn_release': 'Deixe as folhas caírem',
    'autumn_harvested': 'Colhido:',
    'autumn_accepted': 'Aceito:',
    'autumn_closing': 'Tudo cai no seu tempo.',
    'autumn_quote': 'O outono nos mostra como é bonito deixar as coisas irem.',
    
    // Valentine's Day Room (Portuguese)
    'valentine_date': '14 DE FEVEREIRO',
    'valentine_title': 'O Amor Que Você Merece',
    'valentine_message_1': 'Hoje não é sobre os outros.',
    'valentine_message_2': 'É sobre quem está sempre com você.',
    'valentine_message_3': 'Quem ouve cada pensamento.',
    'valentine_message_4': 'Escreva uma carta de amor para si mesmo.',
    'valentine_prompt': 'Uma carta para você:',
    'valentine_placeholder': 'Querido eu, quero que você saiba...',
    'valentine_submit': 'Enviar com amor',
    'valentine_closing': 'Você merece o amor que dá aos outros.',
    'valentine_quote': 'Você mesmo merece seu amor e carinho.',
    
    // Update changelog
    'update_seasonal_rooms': 'Quatro novas salas sazonais: Equinócio de Primavera, Solstício de Verão, Equinócio de Outono e Dia dos Namorados',
    'update_fragment_trading': 'Sistema de troca de fragmentos - sacrifique fragmentos para desbloquear salas com tempo, pré-visualizar salas de terror ou receber mensagens de IA',
    'update_new_themes': 'Dois novos temas: Eclipse (escuridão total) e Aurora (gradientes quentes, apenas 5-7 AM)',
     'update_regret_machine': 'A Máquina do Arrependimento - narrativas de linhas do tempo alternativas movidas por IA',
     'update_memory_reconstruction': 'Reconstrução de Memória - a IA preenche detalhes sensoriais de memórias que desvanecem',
     
     // Phase 7 - Regret Machine Room
     'regret_machine_title': 'A Máquina do Arrependimento',
     'regret_machine_subtitle': 'Descreva uma decisão que gostaria de ter tomado diferente. A máquina mostrará o que poderia ter sido.',
     'regret_placeholder': 'Eu me arrependo de...\n\nDescreva uma escolha que você fez e que gostaria que tivesse sido diferente. O que você teria feito em vez disso?',
     'regret_hint': 'Isso não é sobre culpa. É sobre compreensão.',
     'regret_explore': 'Explorar a Linha do Tempo',
     'regret_generating': 'Reconstruindo uma realidade alternativa...',
     'regret_alternate_timeline': 'A Linha do Tempo Alternativa',
     'regret_explore_another': 'Explorar Outra',
     'regret_error_short': 'Por favor descreva seu arrependimento com mais detalhes',
     'room_regret-machine': 'A Máquina do Arrependimento',
     
     // Phase 7 - Memory Reconstruction Room
     'memory_reconstruction_title': 'Reconstrução de Memória',
     'memory_reconstruction_subtitle': 'Descreva uma memória que desvanece. Deixe-nos ajudá-lo a sentir o caminho de volta.',
     'memory_placeholder': 'Eu lembro...\n\nDescreva uma memória distante ou fragmentada. Que fragmentos permanecem? Que sentimentos persistem?',
     'memory_timeframe_placeholder': 'Quando foi isso? (ex: "infância", "dez anos atrás")',
     'memory_hint': 'Memória não é uma gravação. É uma reconstrução.',
     'memory_reconstruct': 'Reconstruir Memória',
     'memory_reconstructing': 'Juntando os fragmentos...',
     'memory_reconstructed': 'A Memória Reconstruída',
     'memory_explore_another': 'Explorar Outra Memória',
     'memory_error_short': 'Por favor descreva sua memória com mais detalhes',
     'room_memory-reconstruction': 'Reconstrução de Memória',
    'update_eclipse_room': 'Sombras do Eclipse - fale na escuridão total, revele em flashes de luz',
    'update_dawn_room': 'Despertar da Aurora - saúde a primeira luz com exercícios de respiração e intenções',
    
    // New themes
    'eclipse_theme_required': 'Esta sala requer o tema Eclipse. A escuridão total aguarda.',
    'dawn_theme_required': 'Esta sala requer o tema Aurora durante as primeiras horas.',
    'dawn_time_hint': 'Disponível apenas das 5 às 7 AM',
    'change_theme': 'Alterar tema nas Configurações',
    
    // Eclipse Shadows Room
    'eclipse_shadows_title': 'Sombras do Eclipse',
    'eclipse_shadows_intro': 'Na escuridão completa, os segredos parecem mais seguros. Responda honestamente—apenas breves flashes iluminarão suas palavras.',
    'enter_darkness': 'Entrar na escuridão',
    'shadow_question_1': 'O que você esconde de si mesmo?',
    'shadow_question_2': 'Que verdade você diria se ninguém pudesse ouvir?',
    'shadow_question_3': 'O que te assombra às 3 AM?',
    'shadow_question_4': 'O que você faria se o medo não existisse?',
    'shadow_question_5': 'O que você não está pronto para enfrentar?',
    'speak_into_dark': 'Fale na escuridão...',
    'release': 'Liberar',
    'eclipse_reveal_prompt': 'Clique em cada sombra para revelar o que você confessou.',
    'hidden_in_shadow': '▓▓▓▓▓▓▓▓▓▓▓▓',
    'eclipse_reveal_hint': 'Algumas verdades são mais claras no escuro.',
    'eclipse_shadows_quote': 'Na escuridão total, finalmente nos vemos.',
    
    // Dawn Awakening Room
    'dawn_awakening_title': 'Despertar da Aurora',
    'dawn_awakening_intro': 'A primeira luz traz possibilidade. Antes do mundo acordar, defina suas intenções.',
    'greet_the_light': 'Saúde a luz',
    'breathe_in_light': 'Respire a luz',
    'hold_warmth': 'Segure o calor',
    'release_night': 'Libere a noite',
    'dawn_prompt_1': 'O que você quer levar consigo hoje?',
    'dawn_prompt_2': 'O que você vai deixar ir?',
    'dawn_prompt_3': 'Que pequeno ato de gentileza você vai oferecer a si mesmo?',
    'set_your_intention': 'Defina sua intenção...',
    'plant_seed': 'Plante esta semente',
    'dawn_awakening_quote': 'Cada aurora é um convite para recomeçar.',
    
    // Fragment Trading
    'fragment_trading': 'Troca de Fragmentos',
    'sacrifice_to_unlock': 'Sacrifique fragmentos para desbloquear segredos',
    'your_fragments': 'Seus Fragmentos',
    'fragments_any': 'quaisquer fragmentos',
    'fragments_free': 'fragmentos de clareza',
    'fragments_premium': 'fragmentos de profundidade',
    'fragments_horror': 'fragmentos de sombra',
    'trade_successful': 'Troca Bem-sucedida!',
    'trade_failed': 'Troca Falhou',
    'not_enough_fragments': 'Fragmentos insuficientes',
    'continue_trading': 'Continuar Trocando',
    'trading_fragments': 'Trocando fragmentos...',
    'fragment_trade_warning': 'Fragmentos sacrificados não podem ser recuperados',
    'unlock-time-locked': 'Desbloquear Sala com Tempo',
    'unlock-time-locked_desc': 'Acesse uma sala com tempo sem esperar',
    'preview-horror': 'Pré-visualização de Terror',
    'preview-horror_desc': 'Espie uma sala de terror antes de entrar',
    'ai-reflection-trade': 'Reflexão IA',
    'ai-reflection-trade_desc': 'Receba uma mensagem personalizada da IA',
    'unlock-premium-preview': 'Teste de Sala Premium',
    'unlock-premium-preview_desc': 'Acesse uma sala premium por uma visita',
    'fragment_trading_room': 'O Altar de Fragmentos',
    'fragment_trading_intro': 'Aqui, você pode sacrificar seus fragmentos coletados para desbloquear segredos escondidos nestas salas.',
    'your_collection': 'Sua Coleção',
    'fragment_clarity': 'Clareza',
    'fragment_depth': 'Profundidade',
    'fragment_shadow': 'Sombra',
    'total_fragments': 'Total',
    'open_altar': 'Aproximar-se do Altar',
    'recent_trades': 'Oferendas Recentes',
    'fragment_trade_ending': 'O que foi dado não pode ser recuperado. Mas o que foi ganho... isso permanece.',
    'room_fragment-trade': 'O Altar de Fragmentos',
    'sub_fragment-trade': 'Sacrifique para desbloquear segredos',
    'update_anniversary_system': 'Sistema de Aniversário e Marcos - acompanhe sua jornada ao longo do tempo',
    'update_archive_room': 'O Arquivo - histórico completo desbloqueado em 25 salas',
    'update_mirror_room': 'Espelho de Todos os Eus - retrato comportamental IA em 50 salas',
    'update_ending_room': 'O Final - uma despedida personalizada em 75 salas',
    'room_the-archive': 'O Arquivo',
    'room_mirror-of-all-selves': 'Espelho de Todos os Eus',
    'room_the-ending': 'O Final',
    // Phase 9
    'update_fragment_rarities': 'Raridades de Fragmentos - fragmentos colecionáveis Comuns, Raros e Lendários',
    'update_fragment_gallery': 'Galeria de Fragmentos - vitrine visual para sua coleção',
    'update_seasonal_fragments': 'Fragmentos sazonais e secretos - colecionáveis por tempo limitado',
    'fragment_collection': 'Coleção',
    'fragment_collection_title': 'Coleção de Fragmentos',
    'fragment_collection_subtitle': 'Cada sala deixa algo para trás. Colete todos.',
    'collection_milestones': 'Marcos',
    'collection_secrets': 'Segredos',
    'collection_footer': 'Alguns fragmentos só aparecem em certas condições.',
    'rarity_common': 'Comum',
    'rarity_rare': 'Raro',
    'rarity_legendary': 'Lendário',
    // Phase 10-11
    'accessibility': 'Acessibilidade',
    'accessibility_desc': 'Personalizar legibilidade e preferências de movimento',
    'font_family': 'Família da Fonte',
    'font_default': 'Monoespaçada',
    'font_dyslexia': 'Amigável para Dislexia',
    'font_sans': 'Sans Serif',
    'font_changed': 'Fonte atualizada',
    'line_height': 'Altura da Linha',
    'line_compact': 'Compacto',
    'line_normal': 'Normal',
    'line_relaxed': 'Relaxado',
    'line_spacious': 'Espaçoso',
    'line_height_changed': 'Altura da linha atualizada',
    'reduced_motion_full_desc': 'Desabilitar todas as animações e efeitos de partículas',
    'larger_cursors': 'Alvos Maiores',
    'larger_cursors_desc': 'Aumentar o tamanho dos alvos de clique',
    'update_accessibility_suite': 'Suite de Acessibilidade - fontes para dislexia, controle de altura de linha',
    'update_dyslexia_font': 'Opção de fonte amigável para dislexia nas configurações',
    'update_line_height': 'Altura de linha ajustável para melhor legibilidade',
    'update_enhanced_reduced_motion': 'Movimento reduzido aprimorado - desativa completamente todos os efeitos',
    'update_shared_moments': 'Momentos Compartilhados - check-ins emocionais anônimos',
    'update_whisper_wall': 'O Muro dos Sussurros - deixe verdades anônimas no escuro',
    'update_collective_memory': 'Memória Coletiva - fragmentos compartilhados do que lembramos',
    'whisper_prompt': 'Deixe um sussurro no muro. Anônimo. Permanente.',
    'whisper_placeholder': 'O que você carrega em silêncio?',
    'whisper_send': 'Sussurrar',
    'whisper_added': 'Seu sussurro se junta ao muro.',
    'whisper_another': 'Sussurrar novamente',
    'shared_moments_title': 'Agora mesmo, neste momento...',
    'shared_moments_subtitle': 'Como você está, honestamente?',
    'feeling_grateful': 'Grato',
    'feeling_lost': 'Perdido',
    'feeling_hopeful': 'Esperançoso',
    'feeling_heavy': 'Pesado',
    'feeling_peaceful': 'Em paz',
    'feeling_restless': 'Inquieto',
    'room_whisper-wall': 'O Muro dos Sussurros',
    'room_collective-memory': 'Memória Coletiva',
    'room_shared-moments': 'Momentos Compartilhados',
    'sub_regret-machine': 'Explore o que poderia ter sido',
    'sub_memory-reconstruction': 'Reconstrua uma memória que se desvanece',
    'sub_whisper-wall': 'Verdades anônimas flutuam no escuro',
    'sub_collective-memory': 'Fragmentos compartilhados do que lembramos',
    'sub_shared-moments': 'Como você está, honestamente?',
    'sub_the-archive': 'Seu histórico completo de jornada',
    'sub_mirror-of-all-selves': 'A IA revela seu retrato comportamental',
    'sub_the-ending': 'Uma despedida que você mereceu',

    // v3.21.0 - New Rooms
    'room_mask-you-wear': 'A Máscara que Você Usa',
    'sub_mask-you-wear': 'Que rosto você mostra?',
    'room_recurring-dream': 'Seu Sonho Recorrente',
    'sub_recurring-dream': 'O sonho que continua voltando',
    'room_promise-you-broke': 'A Promessa que Você Quebrou',
    'sub_promise-you-broke': 'Honestidade sobre promessas quebradas',
    'room_words-you-swallowed': 'Palavras que Você Engoliu',
    'sub_words-you-swallowed': 'Coisas que você nunca disse',
    'room_age-you-feel': 'A Idade que Você Sente',
    'sub_age-you-feel': 'Quantos anos você realmente tem?',
    'room_biggest-lie': 'Sua Maior Mentira',
    'sub_biggest-lie': 'A mentira que você mais conta',
    'room_person-you-pretend-to-be': 'A Pessoa que Você Finge Ser',
    'sub_person-you-pretend-to-be': 'Sua atuação',
    'room_person-you-pretend': 'A Pessoa que Você Finge Ser',
    'sub_person-you-pretend': 'Sua atuação',
    'room_what-you-collect': 'O que Você Coleciona',
    'sub_what-you-collect': 'Objetos revelam identidade',
    'room_apology-you-owe': 'O Pedido de Desculpas que Você Deve',
    'sub_apology-you-owe': 'Escreva o que deveria ter dito',
    'room_unfinished-story': 'Sua História Inacabada',
    'sub_unfinished-story': 'O que você nunca completou',
    'room_version-others-see': 'A Versão que os Outros Veem',
    'sub_version-others-see': 'Como os outros te percebem',
    'room_secret-talent': 'Seu Talento Secreto',
    'sub_secret-talent': 'O que ninguém sabe que você faz',
    'room_fear-you-feed': 'O Medo que Você Alimenta',
    'sub_fear-you-feed': 'Qual medo te controla?',
    'room_guilty-pleasure': 'Seu Prazer Culpado',
    'sub_guilty-pleasure': 'O que você esconde gostar',
    'room_rule-you-break': 'A Regra que Você Sempre Quebra',
    'sub_rule-you-break': 'Seu padrão de rebelião',
    'room_childhood-promise': 'Sua Promessa de Infância',
    'sub_childhood-promise': 'O que você prometeu a si mesmo',
    'room_compliment-you-reject': 'O Elogio que Você Rejeita',
    'sub_compliment-you-reject': 'Por que você não aceita elogios',
    'room_autopilot-mode': 'Seu Modo Automático',
    'sub_autopilot-mode': 'O que você faz sem pensar',
    'room_boundary-you-need': 'O Limite que Você Precisa',
    'sub_boundary-you-need': 'O que você deve proteger',
    'room_inner-critic': 'Seu Crítico Interior',
    'sub_inner-critic': 'A voz que julga',
    'room_last-sunday': 'Último Domingo',
    'sub_last-sunday': 'O que você realmente fez?',
    'room_wasted-hours': 'Suas Horas Perdidas',
    'sub_wasted-hours': 'Onde o tempo desaparece',
    'room_year-that-changed': 'O Ano que te Mudou',
    'sub_year-that-changed': 'O ponto de virada',
    'room_tomorrows-regret': 'O Arrependimento de Amanhã',
    'sub_tomorrows-regret': 'O que você desejará ter feito?',
    'room_morning-autopilot': 'Seu Piloto Automático Matinal',
    'sub_morning-autopilot': 'O ritual da primeira hora',
    'room_season-you-dread': 'A Estação que Você Teme',
    'sub_season-you-dread': 'Qual estação pesa mais?',
    'room_ten-years-ago': 'Dez Anos Atrás',
    'sub_ten-years-ago': 'Quem era você então?',
    'room_last-normal-day': 'Seu Último Dia Normal',
    'sub_last-normal-day': 'Antes de tudo mudar',
    'room_clock-you-ignore': 'O Relógio que Você Ignora',
    'sub_clock-you-ignore': 'Tempo que finge não passar',
    'room_your-deadline': 'Seu Prazo',
    'sub_your-deadline': 'O que está adiando?',
    'room_the-pause': 'A Pausa',
    'sub_the-pause': 'Apenas respire por um momento',
    'room_static-room': 'Estático',
    'sub_static-room': 'Ruído branco e quietude',
    'room_gap-between-thoughts': 'O Espaço Entre Pensamentos',
    'sub_gap-between-thoughts': 'Encontre o espaço',
    'room_sit-with-discomfort': 'Sente com o Desconforto',
    'sub_sit-with-discomfort': 'Não conserte nada',
    'room_boring-room': 'O Quarto Chato',
    'sub_boring-room': 'Nada interessante',
    'room_unplug-room': 'Desconectar',
    'sub_unplug-room': 'Imagine sem dispositivos',
    'room_unplug': 'Desconectar',
    'sub_unplug': 'Imagine sem dispositivos',
    'room_last-page': 'A Última Página',
    'sub_last-page': 'A história termina aqui',
    'room_scrolling-pattern': 'Seu Padrão de Rolagem',
    'sub_scrolling-pattern': 'Como você consome conteúdo',
    'room_tab-you-keep-open': 'A Aba que Mantém Aberta',
    'sub_tab-you-keep-open': 'O que fica no navegador',
    'room_digital-shadow': 'Sua Sombra Digital',
    'sub_digital-shadow': 'Sua pegada digital',
    'room_notification-you-ignore': 'A Notificação que Ignora',
    'sub_notification-you-ignore': 'O que você descarta',
    'room_typing-speed': 'Sua Velocidade de Digitação',
    'sub_typing-speed': 'Quão rápido você se expressa?',
    'room_link-you-almost-clicked': 'O Link que Quase Clicou',
    'sub_link-you-almost-clicked': 'Padrões de hesitação',
    'room_bookmark-graveyard': 'Seu Cemitério de Favoritos',
    'sub_bookmark-graveyard': 'Salvos mas nunca revisitados',
    'room_search-you-deleted': 'A Busca que Deletou',
    'sub_search-you-deleted': 'O que procurou e apagou',
    'room_gravity-room': 'Sala da Gravidade',
    'sub_gravity-room': 'Tudo cai devagar',
    'room_ink-blot': 'Mancha de Tinta',
    'sub_ink-blot': 'O que você vê?',
    'room_hallway-of-mirrors': 'O Corredor de Espelhos',
    'sub_hallway-of-mirrors': 'Reflexos infinitos',
    'room_dissolving-text': 'Texto Dissolvendo',
    'sub_dissolving-text': 'Palavras derretem ao ler',
    'room_the-pendulum': 'O Pêndulo',
    'sub_the-pendulum': 'Movimento hipnótico',
    'room_pixel-rain': 'Chuva de Pixels',
    'sub_pixel-rain': 'Precipitação digital',
    'room_the-threshold': 'O Limiar',
    'sub_the-threshold': 'Entre aqui e lá',
    'most_loved_rooms': 'Quartos Mais Amados',
    'rate_room': 'Avalie este quarto',
    'update_52_new_rooms': '52 novos quartos nas categorias Identidade, Tempo, Contemplação, Meta e Experimental',
    'update_room_rating': 'Sistema de avaliação - polegar para cima/baixo com seção Quartos Mais Amados',
    'update_bug_fix_reflections': 'Correção na exibição e armazenamento de reflexões anônimas',
    'update_100_rooms': 'Mais de 100 quartos disponíveis no Corredor',
    // v3.22.0
    'room_the-friend-you-lost': 'O Amigo Que Você Perdeu',
    'sub_the-friend-you-lost': 'Escreva para alguém de quem se afastou.',
    'room_conversation-you-replay': 'A Conversa Que Você Repete',
    'sub_conversation-you-replay': 'Recrie-a de memória.',
    'room_the-thank-you': 'O Agradecimento',
    'sub_the-thank-you': 'Gratidão que nunca expressou.',
    'room_people-you-forgot': 'Pessoas Que Você Esqueceu',
    'sub_people-you-forgot': 'Tente lembrar nomes esquecidos.',
    'room_the-advice-you-give': 'O Conselho Que Você Dá',
    'sub_the-advice-you-give': 'Conselho que dá mas nunca segue.',
    'room_what-others-dont-know': 'O Que Os Outros Não Sabem',
    'sub_what-others-dont-know': 'Suas camadas escondidas.',
    'room_the-lie-you-believe': 'A Mentira Que Você Acredita',
    'sub_the-lie-you-believe': 'Uma inverdade confortável.',
    'room_your-shadow-self': 'Seu Eu Sombra',
    'sub_your-shadow-self': 'A parte que você suprime.',
    'room_the-story-you-tell': 'A História Que Você Conta',
    'sub_the-story-you-tell': 'Como você narra sua vida.',
    'room_your-safe-place': 'Seu Lugar Seguro',
    'sub_your-safe-place': 'Descreva onde se sente seguro.',
    'room_the-risk-you-didnt-take': 'O Risco Que Você Não Correu',
    'sub_the-risk-you-didnt-take': 'Caminhos não percorridos.',
    'room_your-last-excuse': 'Sua Última Desculpa',
    'sub_your-last-excuse': 'A desculpa que mais usa.',
    'room_the-habit-you-hide': 'O Hábito Que Você Esconde',
    'sub_the-habit-you-hide': 'Rotinas secretas.',
    'room_what-you-postpone': 'O Que Você Adia',
    'sub_what-you-postpone': 'A coisa sempre "amanhã".',
    'room_the-door-you-closed': 'A Porta Que Você Fechou',
    'sub_the-door-you-closed': 'Oportunidades que rejeitou.',
    'journal_title': 'Diário de Reflexões',
    'journal_subtitle': 'Seus pensamentos entre quartos',
    'journal_empty': 'Sem reflexões ainda. Visite quartos e escreva seus pensamentos.',
    'journal': 'Diário',
    'room_chains': 'Cadeias de Quartos',
    'chain_identity_journey': 'Jornada de Identidade',
    'chain_time_reflection': 'Reflexão do Tempo',
    'chain_truth_path': 'Caminho da Verdade',
    'recently_visited': 'Visitados Recentemente',
    'update_reflection_journal': 'Diário de Reflexões - salve seus pensamentos entre quartos',
    'update_room_chains': 'Cadeias de Quartos - complete quartos em sequência',
    'update_15_new_rooms': '15 novos quartos interativos em 3 categorias',
    'update_recently_visited': 'Seção de visitados recentemente para retorno rápido',
    'ai_companion': 'Companheiro',
    'companion_create': 'Crie Seu Companheiro',
    'companion_setup_desc': 'Um reflexo que caminha ao seu lado.',
    'companion_name': 'Nomeie seu companheiro',
    'companion_name_placeholder': 'ex., Eco, Espelho, Sombra...',
    'companion_lens': 'Lente de personalidade',
    'companion_mood': 'Tom de comunicação',
    'companion_fears': 'Do que você tem medo? (opcional)',
    'companion_struggles': 'Com o que você luta? (opcional)',
    'create_companion': 'Criar',
    'companion_input': 'Diga algo...',
    'companion_greeting': 'Seu companheiro está aqui. Diga algo.',
    'companion_starter_1': 'O que está na minha mente hoje?',
    'companion_starter_2': 'Me sinto travado.',
    'companion_starter_3': 'Me diga algo que eu evito.',
    'fears_placeholder': 'ex., fracasso, rejeição, ser esquecido...',
    'struggles_placeholder': 'ex., pensar demais, procrastinar, solidão...',
    'back_step': 'Voltar',
    'lens_reflective': 'Reflexivo',
    'lens_reflective_desc': 'Perguntas profundas, reconhecimento de padrões',
    'lens_supportive': 'Apoiador',
    'lens_supportive_desc': 'Caloroso, encorajador, validador',
    'lens_challenging': 'Desafiador',
    'lens_challenging_desc': 'Direto, provocador',
    'lens_curious': 'Curioso',
    'lens_curious_desc': 'Perguntas inesperadas, admiração',
    'lens_shadow': 'Sombra',
    'lens_shadow_desc': 'O lado oculto, desconforto honesto',
    'companion_mood_calm': 'Calmo',
    'companion_mood_calm_desc': 'Comedido e pacífico',
    'companion_mood_intense': 'Intenso',
    'companion_mood_intense_desc': 'Urgente e profundo',
    'companion_mood_playful': 'Brincalhão',
    'companion_mood_playful_desc': 'Espirituoso e leve',
    'companion_mood_melancholic': 'Melancólico',
    'companion_mood_melancholic_desc': 'Tristeza bonita',
    'update_ai_companion': 'Companheiro IA - uma IA pessoal que lembra sua jornada',
    // v3.23.0
    'mood_history': 'Histórico de Humor',
    'top_rooms': 'Suas Salas Favoritas',
    'visits_count': 'visitas',
    'set_intention': 'Defina uma intenção para hoje',
    'todays_intention': 'Intenção de Hoje',
    'intention_placeholder': 'Eu vou...',
    'set': 'Definir',
    'update_mood_history': 'Histórico de Humor - acompanhe como seu humor muda',
    'update_daily_intention': 'Intenção Diária - defina um foco para cada dia',
    'update_top_rooms': 'Salas Favoritas - acesso rápido às suas salas mais visitadas',
    'update_10_new_rooms_v323': '10 novas salas: emoções, autorreflexão e conexão',
    'room_the-emotion-you-hide': 'A Emoção que Você Esconde',
    'sub_the-emotion-you-hide': 'Nomeie. Só uma vez.',
    'emotion_hide_placeholder': 'A emoção que nunca mostro é...',
    'quote_emotion_hide': 'O que escondemos dos outros, muitas vezes escondemos de nós mesmos.',
    'room_your-comfort-lie': 'Sua Mentira Confortável',
    'sub_your-comfort-lie': 'A pequena mentira que te mantém confortável.',
    'comfort_lie_placeholder': 'A mentira que digo a mim mesmo é...',
    'admit_it': 'Admita',
    'quote_comfort_lie': 'Mentiras confortáveis são paredes que construímos com tecido macio.',
    'room_the-silence-between': 'O Silêncio Entre',
    'sub_the-silence-between': 'Sente-se no espaço entre dois pensamentos.',
    'silence_instruction': 'Feche os olhos. Encontre o espaço entre dois pensamentos. Depois pressione o botão.',
    'silence_sitting': 'Sentado em silêncio...',
    'silence_found': 'Eu encontrei',
    'silence_result': 'Você ficou por',
    'seconds': 'segundos',
    'quote_silence_between': 'Entre cada pensamento há um espaço infinito. Você acabou de visitá-lo.',
    'room_what-you-carry': 'O que Você Carrega',
    'sub_what-you-carry': 'Nomeie o peso invisível.',
    'carry_placeholder': 'O peso que carrego é...',
    'set_it_down': 'Largue',
    'carry_released': 'Por um momento, ficou mais leve.',
    'quote_carry': 'Você não pode largar o que se recusa a nomear.',
    'room_the-version-you-lost': 'A Versão que Você Perdeu',
    'sub_the-version-you-lost': 'Quem você era antes da vida acontecer.',
    'version_lost_placeholder': 'Antes de tudo mudar, eu era...',
    'remember': 'Lembrar',
    'quote_version_lost': 'Você não os perdeu. Eles se tornaram parte do que você é agora.',
    'room_letter-to-past-self': 'Carta ao Eu do Passado',
    'sub_letter-to-past-self': 'Escreva para quem você era.',
    'letter_past_placeholder': 'Querido eu mais jovem,...',
    'send_letter': 'Enviar',
    'letter_sent': 'A carta foi enviada. Em algum lugar, eles a receberam.',
    'quote_letter_past': 'Cada palavra de conforto que você escreve para seu eu passado é na verdade para quem você é agora.',
    'room_the-moment-you-knew': 'O Momento que Você Soube',
    'sub_the-moment-you-knew': 'O instante em que tudo mudou.',
    'moment_knew_placeholder': 'O momento que eu soube foi quando...',
    'mark_it': 'Marque',
    'quote_moment_knew': 'Alguns momentos dividem sua vida em antes e depois. Você acabou de nomear um.',
    'room_the-words-you-rehearse': 'As Palavras que Você Ensaia',
    'sub_the-words-you-rehearse': 'O que você pratica dizer mas nunca faz.',
    'words_rehearse_placeholder': 'As palavras que ensaio são...',
    'say_them': 'Diga-as',
    'words_spoken': 'Você finalmente as disse. Mesmo que só aqui.',
    'quote_words_rehearse': 'Palavras ensaiadas são gaiolas. Palavras ditas são pássaros.',
    'room_someone-thinks-of-you': 'Alguém Pensa em Você',
    'sub_someone-thinks-of-you': 'Agora, em algum lugar, alguém se lembra de você.',
    'someone_line_1': 'Agora, em algum lugar, alguém se lembra de você.',
    'someone_line_2': 'Não pelo que você fez.',
    'someone_line_3': 'Mas por como você os fez sentir.',
    'quote_someone_thinks': 'Você existe em memórias que nem sabe que existem.',
    'room_the-question-you-fear': 'A Pergunta que Você Teme',
    'sub_the-question-you-fear': 'A pergunta que você espera que ninguém faça.',
    'question_fear_placeholder': 'A pergunta que mais temo é...',
    'quote_question_fear': 'A pergunta que você mais teme geralmente é a que tem a resposta mais importante.',
    // v3.24.0
    'behavior_insight': 'O site percebe',
    'insight_pattern': 'Você é atraído por {category}. Explora mais de {time}.',
    'cat_identity': 'identidade',
    'cat_truth': 'verdade',
    'cat_time': 'tempo',
    'cat_mortality': 'mortalidade',
    'cat_stillness': 'quietude',
    'cat_darkness': 'escuridão',
    'cat_patterns': 'padrões',
    'cat_fear': 'medo',
    'cat_play': 'jogo',
    'cat_experiments': 'experimentos',
    'cat_ritual': 'ritual',
    'cat_secrets': 'segredos',
    'time_late_night': 'madrugada',
    'time_morning': 'manhã',
    'time_afternoon': 'tarde',
    'time_evening': 'noite',
    
    'anonymous_visitor': 'um visitante',
    'update_behavior_insight': 'Perspectiva Comportamental - o site reflete seus padrões',
    'update_anonymous_reflections_feed': 'Reflexões Anônimas - veja o que outros sentiram',
    'update_daily_prompt': 'Reflexão Diária - um pensamento para levar com você',
    'room_recurring-dream-journal': 'Diário de Sonhos Recorrentes', 'sub_recurring-dream-journal': 'O sonho que continua voltando.', 'dream_journal_placeholder': 'O sonho que eu continuo tendo...', 'record_dream': 'Registrar', 'dream_journal_reflection': 'Sonhos dizem o que a mente acordada não diz.', 'quote_dream_journal': 'Sonhos recorrentes são conversas inacabadas consigo mesmo.',
    'room_lucid-moment': 'O Momento Lúcido', 'sub_lucid-moment': 'Tome consciência dentro do sonho.', 'lucid_line_1': 'Você está lendo estas palavras.', 'lucid_line_2': 'Mas você está acordado?', 'lucid_line_3': 'Como saberia a diferença?', 'become_aware': 'Estou consciente', 'lucid_aware': 'Este momento de consciência é a única coisa real.', 'quote_lucid_moment': 'O momento em que você questiona a realidade é quando realmente a vê.',
    'room_nightmare-you-remember': 'O Pesadelo Que Você Lembra', 'sub_nightmare-you-remember': 'Aquele que ainda te assombra.', 'nightmare_placeholder': 'O pesadelo que não consigo esquecer...', 'face_it': 'Enfrente', 'nightmare_reflection': 'Ao escrevê-lo, você tirou parte do seu poder.', 'quote_nightmare': 'Pesadelos são medos que insistem em ser vistos.',
    'room_dream-you-wish-was-real': 'O Sonho Que Você Queria Que Fosse Real', 'sub_dream-you-wish-was-real': 'Acorde. Não era real.', 'dream_wish_placeholder': 'Eu sonhei que...', 'hold_onto_it': 'Segure', 'quote_dream_wish': 'Os sonhos que lamentamos perder revelam a vida que realmente queremos.',
    'room_the-dream-visitor': 'O Visitante do Sonho', 'sub_the-dream-visitor': 'Alguém continua aparecendo nos seus sonhos.', 'dream_visitor_line_1': 'Há alguém que te visita em sonhos.', 'dream_visitor_line_2': 'Você não o convidou.', 'dream_visitor_line_3': 'Mas ele continua voltando.', 'dream_visitor_line_4': 'Talvez esteja tentando te dizer algo.', 'quote_dream_visitor': 'As pessoas que aparecem em nossos sonhos são mensageiros do subconsciente.',
    'room_the-blank-canvas': 'A Tela em Branco', 'sub_the-blank-canvas': 'Crie algo do nada.', 'blank_canvas_placeholder': 'Nesta tela em branco, eu crio...', 'create': 'Criar', 'quote_blank_canvas': 'Toda criação começa com a coragem de enfrentar o vazio.',
    'room_song-that-is-you': 'A Música Que É Você', 'sub_song-that-is-you': 'Se você fosse uma música, qual seria?', 'song_placeholder': 'A música que sou eu...', 'play_it': 'Toque', 'quote_song': 'Música é o som dos sentimentos.',
    'room_color-of-today': 'A Cor de Hoje', 'sub_color-of-today': 'Escolha a cor do seu estado atual.', 'color_chosen': 'É assim que você se sente hoje. Em cores.', 'quote_color_today': 'Cores não precisam de palavras. Sentimentos também não.',
    'room_the-unwritten-poem': 'O Poema Não Escrito', 'sub_the-unwritten-poem': 'Escreva o que guardava dentro de si.', 'poem_placeholder': 'Palavras que esperavam...', 'publish_it': 'Publique', 'quote_unwritten_poem': 'O poema sempre esteve lá. Você apenas o deixou sair.',
    'room_your-inner-voice': 'Sua Voz Interior', 'sub_your-inner-voice': 'O que ela realmente diz?', 'inner_voice_placeholder': 'Minha voz interior diz...', 'listen': 'Ouça', 'inner_voice_reflection': 'Agora que a ouviu, o que fará?', 'quote_inner_voice': 'Sua voz interior sempre falou. Você só começou a ouvir.',
    'room_fear-you-named': 'O Medo Que Você Nomeou', 'sub_fear-you-named': 'Nomeie e ele perde o poder.', 'fear_named_placeholder': 'O medo que finalmente nomeio...', 'name_it': 'Nomeie', 'fear_named_reflection': 'Medos nomeados diminuem. Sem nome, crescem.', 'quote_fear_named': 'O que você pode nomear, pode enfrentar.',
    'room_the-leap': 'O Salto', 'sub_the-leap': 'Para onde você saltaria?', 'leap_placeholder': 'Se eu pudesse saltar...', 'leap': 'Salte', 'quote_leap': 'A rede aparece depois que você pula, não antes.',
    'room_courage-inventory': 'Inventário de Coragem', 'sub_courage-inventory': 'Liste cada coisa corajosa que fez.', 'courage_placeholder': 'Algo corajoso que fiz...', 'courage_count': 'Você listou {count} atos de coragem.', 'quote_courage': 'Você é mais corajoso do que lembra.',
    'room_what-scares-you-most': 'O Que Mais Te Assusta', 'sub_what-scares-you-most': 'O medo mais profundo.', 'scares_placeholder': 'O que mais me assusta é...', 'quote_scares': 'Seu medo mais profundo geralmente é seu maior professor.',
    'room_facing-the-dark': 'Enfrentando o Escuro', 'sub_facing-the-dark': 'Sente-se na escuridão total.', 'darkness_counting': '{seconds} segundos no escuro...', 'return_to_light': 'Voltar à luz', 'darkness_survived': 'Você ficou no escuro por {seconds} segundos.', 'quote_facing_dark': 'Na escuridão, descobrimos o que carregamos dentro.',
    'room_letter-to-future-self': 'Carta Para Seu Eu Futuro', 'sub_letter-to-future-self': 'Escreva para quem você será.', 'letter_future_placeholder': 'Querido eu futuro,...', 'seal_letter': 'Selar', 'letter_future_sealed': 'A carta está selada. Um dia você a lerá.', 'quote_letter_future': 'Toda carta ao futuro é uma promessa ao presente.',
    'room_the-world-you-want': 'O Mundo Que Você Quer', 'sub_the-world-you-want': 'Descreva o mundo que desejaria.', 'world_placeholder': 'O mundo que eu quero...', 'build_it': 'Construa', 'quote_world_want': 'O mundo que você imagina é o primeiro passo para o mundo que cria.',
    'room_your-next-chapter': 'Seu Próximo Capítulo', 'sub_your-next-chapter': 'O que acontece depois na sua história?', 'next_chapter_placeholder': 'No meu próximo capítulo...', 'turn_the_page': 'Vire a página', 'quote_next_chapter': 'Você é tanto o autor quanto o protagonista.',
    'room_the-wish': 'O Desejo', 'sub_the-wish': 'Um desejo. Sem regras.', 'wish_placeholder': 'Eu desejo...', 'make_wish': 'Faça o desejo', 'quote_wish': 'O que você deseja revela o que mais valoriza.',
    'room_if-you-had-one-year': 'Se Você Tivesse Um Ano', 'sub_if-you-had-one-year': 'O que faria com 365 dias?', 'one_year_placeholder': 'Se eu tivesse um ano...', 'begin_now': 'Comece agora', 'one_year_reflection': 'Então por que não está fazendo?', 'quote_one_year': 'Viva como se tivesse um ano. Porque você nunca sabe.',
    'update_v4_languages': '4 novos idiomas: Hindi, Coreano, Japonês, Turco', 'update_v4_20_rooms': '20 novas salas em Sonhos, Criatividade, Medo e Futuro', 'update_v4_dreams_category': 'Categoria de salas de Sonhos e Subconsciente', 'update_v4_creativity_category': 'Categoria de salas de Criatividade e Expressão', 'update_v4_fear_category': 'Categoria de salas de Medo e Coragem', 'update_v4_future_category': 'Categoria de salas de Futuro e Esperança',
    'room_playlists': 'Listas de Salas', 'personal_dashboard': 'Painel', 'dashboard_subtitle': 'Sua jornada em um olhar', 'visit_pattern': 'Padrão de visitas (14 dias)', 'dashboard_top_rooms': 'Salas mais visitadas', 'category_breakdown': 'Categorias de salas', 'mood_trend': 'Tendência de humor', 'fragment_breakdown': 'Detalhamento de fragmentos', 'no_data_yet': 'Sem dados ainda', 'current_streak': 'Sequência de dias',
    'playlist_morning': 'Reflexão Matinal', 'playlist_morning_desc': 'Comece seu dia com clareza', 'playlist_identity': 'Mergulho na Identidade', 'playlist_identity_desc': 'Quem é você por baixo dos rótulos?', 'playlist_creative': 'Faísca Criativa', 'playlist_creative_desc': 'Libere sua expressão criativa', 'playlist_courage': 'Caminho da Coragem', 'playlist_courage_desc': 'Enfrente seus medos sala por sala', 'playlist_dreams': 'Diário de Sonhos', 'playlist_dreams_desc': 'Explore as paisagens do seu subconsciente', 'playlist_future': 'Visão do Futuro', 'playlist_future_desc': 'Cartas e desejos para o você que virá', 'playlist_truth': 'Confronto da Verdade', 'playlist_truth_desc': 'Enfrente as realidades desconfortáveis', 'playlist_night': 'Jornada Noturna', 'playlist_night_desc': 'Abrace o silêncio, vazio e nada',
    // v4.2.0
    'room_focus-timer': 'Temporizador de Foco', 'sub_focus-timer': 'Sente em silêncio. Reflita.',
    'room_gratitude-jar': 'Pote de Gratidão', 'sub_gratitude-jar': 'Uma coisa pela qual é grato',
    'room_mood-compass': 'Bússola do Humor', 'sub_mood-compass': 'Onde você está agora?',
    'room_memory-fade': 'Memória que Desvanece', 'sub_memory-fade': 'Veja uma memória se dissolver',
    'room_sound-of-your-day': 'Som do Seu Dia', 'sub_sound-of-your-day': 'Como soa hoje?',
    'room_the-apology-room': 'A Sala do Pedido de Desculpas', 'sub_the-apology-room': 'Escreva o que nunca disse',
    'room_your-unread-messages': 'Suas Mensagens Não Lidas', 'sub_your-unread-messages': 'Mensagens que nunca abriu',
    'room_the-weight-room': 'A Sala do Peso', 'sub_the-weight-room': 'Nomeie o que pesa em você',
    'room_parallel-lives': 'Vidas Paralelas', 'sub_parallel-lives': 'Em outra vida, você é...',
    'room_the-quiet-score': 'A Pontuação do Silêncio', 'sub_the-quiet-score': 'Quanto tempo consegue ficar quieto?',
    'focus_choose': 'Quanto tempo quer sentar em silêncio?', 'focus_sit': 'Sente. Respire. Deixe os pensamentos passarem.', 'focus_done': 'O silêncio falou.', 'quote_focus_timer': 'Na quietude, você ouve o que o barulho abafa.', 'minutes': 'min',
    'gratitude_prompt': 'Escreva algo pelo qual é grato hoje.', 'gratitude_placeholder': 'Sou grato por...', 'gratitude_add': 'Adicionar ao pote', 'gratitude_added': 'Sua gratidão flutua no pote.', 'gratitude_count': 'momentos de gratidão', 'quote_gratitude_jar': 'Gratidão não é ter mais. É perceber o que já está aqui.',
    'mood_compass_prompt': 'Clique onde sente que está agora.', 'mood_high_energy': 'Alta Energia', 'mood_low_energy': 'Baixa Energia', 'mood_positive': 'Positivo', 'mood_negative': 'Negativo', 'quote_mood_compass': 'Saber onde você está é o primeiro passo para saber para onde ir.',
    'memory_fade_prompt': 'Digite uma memória. Veja-a se dissolver.', 'memory_fade_placeholder': 'Uma memória que quer soltar...', 'memory_fade_start': 'Deixe desaparecer', 'memory_fade_done': 'Foi embora. Mas foi real.', 'quote_memory_fade': 'Memórias se dissolvem quer assistamos ou não.',
    'sound_day_prompt': 'Escolha 3-5 sons que representam seu dia.', 'sound_rain': 'Chuva', 'sound_traffic': 'Trânsito', 'sound_birds': 'Pássaros', 'sound_keyboard': 'Teclado', 'sound_silence': 'Silêncio', 'sound_wind': 'Vento', 'sound_ocean': 'Oceano', 'sound_crowd': 'Multidão', 'sound_music': 'Música', 'sound_day_confirm': 'Este é meu dia', 'sound_day_result': 'A trilha sonora do seu dia.', 'quote_sound_day': 'Todo dia tem um som. A maioria esquece de ouvir.',
    'apology_prompt': 'Escreva um pedido de desculpas que nunca entregou.', 'apology_placeholder': 'Peço desculpas por...', 'apology_release': 'Libere', 'apology_done': 'Comprimiu-se em nada. Mas você disse.', 'quote_apology': 'Alguns pedidos de desculpa não são para eles. São para você.',
    'unread_from_past': 'De: Seu Eu Passado', 'unread_from_future': 'De: Seu Eu Futuro', 'unread_from_critic': 'De: Seu Crítico Interior', 'unread_msg_past': 'Lembra quando prometeu que as coisas seriam diferentes?', 'unread_msg_future': 'Espero que seja mais gentil consigo agora.', 'unread_msg_critic': 'Você sabe que poderia ter feito mais. Mas fará?', 'unread_count': 'não lidas', 'unread_reply_placeholder': 'Sua resposta...', 'unread_reply': 'Enviar', 'unread_sent': 'Mensagem enviada ao vazio.', 'quote_unread_messages': 'As mensagens que evitamos costumam ser as que mais precisamos.',
    'weight_prompt': 'Nomeie as coisas que pesam em você.', 'weight_placeholder': 'Algo que pesa em mim...', 'weight_release_start': 'Começar a soltar', 'weight_release_prompt': 'Clique em cada peso para soltá-lo.', 'weight_done': 'O peso foi levantado. Por enquanto.', 'quote_weight_room': 'Você não pode soltar o que se recusa a nomear.',
    'parallel_intro': 'Em outra vida, você é...', 'parallel_life_1': 'Em outra vida, você é um faroleiro em uma ilha remota.', 'parallel_life_2': 'Em outra vida, você nunca saiu da sua cidade natal.', 'parallel_life_3': 'Em outra vida, você disse sim quando disse não.', 'parallel_life_4': 'Em outra vida, você é outra pessoa completamente.', 'parallel_life_5': 'Em outra vida, você é exatamente quem é agora.', 'parallel_life_6': 'Em outra vida, você escolheu o caminho mais difícil.', 'parallel_life_7': 'Em outra vida, você ainda procura esta sala.', 'quote_parallel_lives': 'Cada escolha cria um universo onde você escolheu diferente.',
    'quiet_instruction': 'Não interaja. Apenas fique quieto.', 'quiet_reveal_1': 'Você está aqui.', 'quiet_reveal_2': 'O barulho está diminuindo.', 'quiet_reveal_3': 'Algo emerge do silêncio.', 'quiet_reveal_4': 'Você é mais paciente do que pensa.', 'quiet_reveal_5': 'A quietude sabe seu nome.', 'quiet_score_label': 'Sua pontuação de silêncio', 'quiet_impressive': 'Quietude notável.', 'quiet_good': 'Você encontrou algum silêncio.', 'quiet_try_again': 'A urgência de agir é forte.', 'quote_quiet_score': 'A quietude não é ausência de movimento. É presença de consciência.',
    'update_v42_10_rooms': '10 novas salas interactivas: Temporizador, Gratidão, Bússola e mais', 'update_v42_focus_timer': 'Temporizador de Foco — sente em silêncio com escurecimento progressivo', 'update_v42_gratitude_jar': 'Pote de Gratidão — colecione momentos de gratidão', 'update_v42_mood_compass': 'Bússola do Humor — posicione-se nos eixos de energia e valência', 'update_v42_weight_room': 'A Sala do Peso — nomeie e solte o que pesa em você',
    'update_v43_full_localization': 'Localização completa — todas as salas agora totalmente traduzidas', 'update_v43_40_rooms_translated': '40+ salas traduzidas — cada sala interativa suporta todos os idiomas', 'update_v43_10_languages': '10 idiomas suportados — inglês, árabe, espanhol, português, chinês, francês, hindi, coreano, japonês, turco', 'update_v43_nav_consistency': 'Navegação consistente — texto de saída unificado em todas as salas',
    'mirror_lies_title': 'O Espelho Mente', 'mirror_lies_msg1': 'Olhe para si mesmo.', 'mirror_lies_msg2': 'É realmente você?', 'mirror_lies_msg3': 'O espelho mostra o que você quer ver.', 'mirror_lies_msg4': 'Clique para revelar a verdade.', 'mirror_lies_msg5': 'Cada rachadura revela mais.', 'mirror_lies_msg6': 'Não há nada atrás do reflexo.', 'mirror_lies_quote': 'O espelho nunca mostrou você. Apenas o que você acreditava.',
    'winter_title': 'A Noite Mais Longa', 'winter_date': '21 DE DEZEMBRO', 'winter_msg1': 'A noite mais longa está aqui.', 'winter_msg2': 'Na escuridão, encontramos o que carregamos.', 'winter_msg3': 'O que você vai soltar? O que vai guardar?', 'winter_releasing': 'Estou soltando:', 'winter_keeping': 'Estou guardando:', 'winter_release_placeholder': 'O que não serve mais...', 'winter_keep_placeholder': 'O que te dá força...', 'winter_enter_light': 'Entre na luz', 'winter_released': 'Solto:', 'winter_kept': 'Guardado:', 'winter_light_returns': 'A partir desta noite, a luz retorna.', 'winter_quote': 'A noite mais longa nos ensina que mesmo na escuridão mais profunda, a luz já está voltando.',
    'halloween_title': 'A Sala do Véu', 'halloween_date': '31 DE OUTUBRO', 'halloween_msg1': 'O véu está fino esta noite.', 'halloween_msg2': 'O que espreita nas suas sombras?', 'halloween_msg3': 'Nomeie o medo que carrega consigo...', 'halloween_msg4': 'Dê-lhe forma, e liberte-o.', 'halloween_placeholder': 'Digite seu medo...', 'halloween_release': 'Liberte-o', 'halloween_dissolves': 'Seu medo se dissolve na noite...', 'halloween_quote': 'As coisas que tememos perdem poder quando as nomeamos. Deixe-as ir.',
    'awake_title': 'O Que Te Mantém Acordado', 'awake_locked': 'Esta sala só está disponível após as 22h.', 'awake_return': 'Volte quando a noite chegar.', 'awake_press_enter': 'Pressione Enter', 'awake_named': 'Agora você o nomeou.', 'awake_quote': 'A escuridão sabe o que você evita na luz do dia.',
    'voice_meaning_title': 'Sua Voz Sem Significado', 'voice_meaning_prompt': 'Cantarole. Respire. Faça qualquer som. Sem palavras.', 'voice_meaning_done': 'Esta foi sua presença. Nada mais.', 'voice_meaning_hold': 'Segure', 'voice_meaning_recording': 'Gravando...', 'voice_meaning_no_playback': 'Sem reprodução. O som existiu apenas naquele momento.', 'voice_meaning_quote': 'Você existe. Esse é o único conteúdo.',
    'watching_msg1': 'Você está sozinho aqui.', 'watching_msg2': 'Completamente sozinho.', 'watching_msg3': 'Ou está?', 'watching_msg4': 'Eles estiveram aqui o tempo todo.', 'watching_msg5': 'Eles estão sempre observando.',
    'shortcut_title': 'O Atalho Que Você Sempre Toma', 'shortcut_instruction': 'Selecione os que reconhece.', 'shortcut_1': 'Dizer "estou bem" quando não está', 'shortcut_2': 'Verificar o celular em vez de estar presente', 'shortcut_3': 'Ficar quieto para evitar conflito', 'shortcut_4': 'Comprar em vez de fazer', 'shortcut_5': 'Esperar o "momento certo"', 'shortcut_6': 'Culpar as circunstâncias em vez de agir', 'shortcut_7': 'Escolher conforto ao invés de crescimento', 'shortcut_8': 'Rolar em vez de dormir', 'shortcut_9': 'Dizer sim quando quer dizer não', 'shortcut_10': 'Planejar em vez de fazer', 'shortcut_pattern': 'Seu Padrão', 'shortcut_selected': 'Você selecionou:', 'shortcut_show_pattern': 'Mostre meu padrão', 'shortcut_quote': 'O atalho se torna o caminho.', 'shortcut_pattern_own': 'Seu padrão é seu.', 'pattern_avoidance': 'Você evita o que é desconfortável.', 'pattern_distraction': 'Você foge para a distração.', 'pattern_convenience': 'Você troca esforço por facilidade.', 'pattern_delay': 'Você espera condições que nunca chegam.', 'pattern_comfort': 'Você se protege do desconforto.', 'pattern_pleasing': 'Você desaparece para manter os outros confortáveis.',
    'shapes_q1': 'Com que frequência você se sente em paz?', 'shapes_q2': 'Quanto você confia em si mesmo?', 'shapes_q3': 'Quão conectado você se sente com os outros?', 'shapes_q4': 'Quanto do seu tempo é realmente seu?', 'shapes_q5': 'Quão pesado o futuro parece?', 'shapes_result_title': 'Sua Vida como Formas', 'shapes_no_labels': 'Sem rótulos. Sem interpretação.', 'shapes_yours': 'O que você vê é seu para entender.', 'shapes_quote': 'Forma é significado sem palavras.', 'shapes_scale': '1 = nada, 5 = completamente',
    'read_title': 'Leia Isto em Voz Alta', 'read_stmt1': 'Estou evitando algo.', 'read_stmt2': 'Tenho medo.', 'read_stmt3': 'Não sei o que estou fazendo.', 'read_stmt4': 'Preciso de ajuda.', 'read_stmt5': 'Sou suficiente.', 'read_stmt6': 'Tenho fingido.', 'read_stmt7': 'Estou cansado.', 'read_stmt8': 'Quero mais.', 'read_i_read': 'Eu li em voz alta', 'read_really': 'Você realmente disse?', 'read_skipped': 'Eu pulei', 'read_result': 'Você falou verdades em voz alta.', 'read_quote': 'Falar a verdade a torna real.',
    'sentence_title': 'A Frase Que Você Evita', 'sentence_sub': 'Complete esta frase. Você não pode sair até fazer isso.', 'sentence_prompt': 'Eu seria mais feliz se parasse de', 'sentence_cant_leave': 'Você não pode sair sem escrever algo.', 'sentence_knew': 'Você já sabia disso.', 'sentence_quote': 'O primeiro passo é dizer.',
    'say_once_title': 'Diga Uma Vez', 'say_once_prompt': 'Grave uma frase. Não pode reproduzir. Não pode regravar.', 'say_once_done': 'Foi dito. Sem reprodução. Sem repetição.', 'say_once_stop': 'Parar', 'say_once_record': 'Gravar', 'say_once_quote': 'Algumas coisas só devem ser ditas uma vez.',
    'newyear_title': 'Carta ao Seu Eu Futuro', 'newyear_threshold': 'O LIMIAR', 'newyear_placeholder': 'Querido eu futuro...', 'newyear_seal': 'Selar a carta', 'newyear_waiting': 'Esta carta estará esperando por você.', 'newyear_quote': 'Todo fim é uma porta. Atravesse-a com intenção.',
    'avoid_title': 'A Sala Que Você Evita', 'avoid_sub': 'O que você pula diz algo.', 'avoid_not_entered': 'Você não entrou em:', 'avoid_visited_all': 'Você visitou cada sala gratuita.', 'avoid_not_pattern': 'Talvez evitar não seja seu padrão.', 'avoid_quote': 'Evitar é também uma escolha.',
    'who_q1': 'O que você faria se dinheiro não fosse um problema?', 'who_q2': 'Como você se apresentaria em uma festa?', 'who_q3': 'O que te faz sentir vivo?', 'who_q4': 'O que seus amigos diriam que você faz bem?', 'who_q5': 'O que você amava fazer quando criança?', 'who_result_title': 'Você, Sem Título', 'who_done': 'Isso é quem você é quando o título some.', 'who_quote': 'Identidade não é o que você faz. É o que resta quando você para.', 'who_placeholder': 'Seja honesto...', 'who_see_myself': 'Ver-me', 'question': 'Pergunta',
    'lastconv_title': 'A Última Conversa', 'lastconv_sub': 'Escreva o que nunca enviará.', 'lastconv_to': 'Para:', 'lastconv_message': 'Mensagem:', 'lastconv_to_placeholder': 'Alguém que precisa ouvir isso...', 'lastconv_msg_placeholder': 'O que sempre quis dizer...', 'lastconv_not_saved': 'Esta mensagem não será salva ou compartilhada.', 'lastconv_seal': 'Selar a Carta', 'lastconv_wrote_to': 'Você escreveu para:', 'lastconv_never_sent': 'Esta mensagem nunca será enviada.', 'lastconv_point': 'Mas escrevê-la era o objetivo.', 'lastconv_quote': 'Algumas palavras precisam existir, mesmo que nunca sejam ouvidas.',
    'priorities_title': 'Suas Prioridades Reais', 'priorities_sub': 'Quantas horas por semana você dedica a cada uma?', 'priorities_result': 'Suas Prioridades Reais', 'priorities_most_time': 'Você passa a maior parte do tempo em:', 'priorities_truth': 'O tempo revela a verdade. Palavras mentem, agendas não.', 'priorities_quote': 'Você é o que faz repetidamente.', 'priorities_show': 'Mostre Minha Verdade', 'priority_work': 'Trabalho', 'priority_family': 'Família', 'priority_friends': 'Amigos', 'priority_health': 'Saúde', 'priority_learning': 'Aprendizado', 'priority_entertainment': 'Entretenimento', 'priority_social': 'Redes Sociais', 'priority_sleep': 'Sono',
    'voice_intro': 'Uma voz fala na escuridão...', 'voice_silence': '(silêncio)', 'voice_quote': 'Algumas vozes nunca encontram o caminho de casa.',
    'knows_tell': 'Diga-me seu nome.', 'knows_placeholder': 'Seu nome...', 'knows_warning': '(Você não deveria ter vindo aqui)', 'knows_quote': 'Nomes têm poder. O seu já era conhecido.',
    'door_msg1': 'Algo está na porta.', 'door_stopped': '(As batidas pararam)', 'door_quote': 'Algumas portas devem permanecer fechadas.',
    'eyes_msg1': 'Um par de olhos observa.', 'eyes_unsee': 'Você nunca poderá desvê-los.', 'eyes_quote': 'Algumas coisas, uma vez notadas, não podem ser ignoradas.',
    'moon_locked': 'Esta sala só abre durante a lua cheia.', 'moon_not_full': 'A lua não está cheia esta noite. Volte quando o ciclo se completar.', 'moon_quote': 'Algumas verdades só se revelam em ciclos.',
    'at317_title': 'Você às 3:17 AM', 'at317_locked': 'Esta sala abre entre 2 AM e 5 AM.', 'at317_current': 'Hora atual:', 'at317_quote': 'A noite sabe seu nome.',
    'mirrors_you_are_here': 'Você está aqui.',
    'listen_title': 'Ouça a Si Mesmo', 'listen_recording': 'Gravando...', 'listen_start': 'Iniciar', 'listen_play': 'Reproduzir', 'listen_instruction': 'Grave 10 segundos. Diga qualquer coisa.', 'listen_wait': 'Espere. Depois ouça.', 'listen_done': 'Era a sua voz. Mais lenta. Estranha.', 'listen_speed': 'Reproduzindo a 0.5x', 'listen_quote': 'Sua voz soa diferente porque você finalmente a ouviu.',
    'export_title': 'Exportar Sua Jornada', 'export_subtitle': 'Leve um pedaço deste lugar com você', 'export_tab_export': 'Exportar', 'export_tab_import': 'Importar', 'export_your_journey': 'SUA JORNADA', 'export_since': 'Desde', 'export_time_spent': 'tempo gasto', 'export_rooms_visited': 'salas visitadas', 'export_rooms_completed': 'salas completadas', 'export_fragments': 'fragmentos', 'export_achievements_unlocked': 'conquistas desbloqueadas', 'export_truths_recorded': 'verdades registradas', 'export_text_file': 'Arquivo de texto', 'export_json_file': 'Arquivo JSON', 'export_copied': 'Copiado!', 'export_copy_clipboard': 'Copiar para área de transferência', 'export_data_stays': 'Seus dados ficam com você. Nós não os guardamos.', 'export_import_desc': 'Importar uma jornada exportada anteriormente', 'export_json_only': 'Apenas formato JSON', 'export_select_file': 'Selecionar arquivo', 'export_merge_note': 'Será mesclado com os dados da sua jornada atual.', 'export_done': 'Exportado.', 'export_done_desc': 'Um registro de onde você esteve. O que enfrentou.', 'export_quote': 'Algumas jornadas merecem ser documentadas. Não para outros. Para você.', 'export_import_success': 'Jornada importada com sucesso', 'export_import_error': 'Arquivo de jornada inválido',
    'waitroom_title': 'A Sala de Espera', 'waitroom_subtitle': 'Algumas portas só abrem para grupos.', 'waitroom_open': 'A porta está aberta.', 'waitroom_waiting': 'esperando', 'waitroom_time_waited': 'Tempo esperado', 'waitroom_users_needed': 'Usuários necessários', 'waitroom_someone_joined': 'Alguém entrou na espera.', 'waitroom_someone_left': 'Alguém saiu.', 'waitroom_room_opens': 'A sala abre.', 'waitroom_patience': 'Paciência e presença destrancam o que a força não consegue.', 'waitroom_souls_waited': '{count} almas esperaram juntas.', 'waitroom_seconds_stillness': '{count} segundos de quietude coletiva.', 'waitroom_quote': 'Algumas coisas não podem ser apressadas. Algumas portas não podem ser abertas sozinho.',
    'capsule_title': 'Cápsula do Tempo', 'capsule_subtitle': 'Uma mensagem para o seu eu futuro', 'capsule_what_tell': 'O que você precisa dizer a si mesmo?', 'capsule_placeholder': 'Querido eu do futuro...', 'capsule_when_arrive': 'Quando deve chegar?', 'capsule_30_days': '30 dias', 'capsule_90_days': '90 dias', 'capsule_1_year': '1 ano', 'capsule_seal': 'Selar a cápsula', 'capsule_waiting': 'cápsula(s) esperando', 'capsule_sealed': 'Selada.', 'capsule_will_wait': 'Sua mensagem vai esperar por você.', 'capsule_return_in': 'Volte em {days} dias para abri-la.', 'capsule_quote_sealed': 'O tempo muda tudo. Até você.', 'capsule_arrived': 'Uma mensagem chegou.', 'capsule_from_past': 'Do você de {days} dias atrás.', 'capsule_open': 'Abrir a cápsula', 'capsule_you_wrote': 'Você escreveu isso para si mesmo:', 'capsule_remember': 'Você se lembra de ter escrito isso?', 'capsule_quote_opened': 'A pessoa que escreveu isso se foi. Você permanece.',
    'contradiction_title': 'A Sala da Contradição', 'contradiction_subtitle': 'Onde suas verdades colidem', 'contradiction_not_enough': 'Dados insuficientes ainda.', 'contradiction_answer_more': 'Responda mais perguntas nas salas.', 'contradiction_answers_recorded': '{count}/5 respostas registradas', 'contradiction_quote_early': 'Contradições exigem complexidade. Construa a sua primeiro.', 'contradiction_no_found': 'Nenhuma contradição encontrada.', 'contradiction_consistent': 'Ou você é notavelmente consistente...', 'contradiction_careful': '...ou notavelmente cuidadoso.', 'contradiction_quote_none': 'Consistência é sabedoria ou performance.', 'contradiction_found': 'Encontramos {count} contradição(ões)', 'contradiction_you_said': 'Você disse:', 'contradiction_but_also': 'Mas também:', 'contradiction_see_another': 'Ver outra →', 'contradiction_not_weakness': 'Contradição não é fraqueza. É prova de profundidade.', 'contradiction_quote': 'Contemos multidões. Algumas delas discordam.',
    'entropy_living': 'vivas', 'entropy_dead': 'mortas', 'entropy_plant_seed': 'Plantar uma semente', 'entropy_instructions': 'Clique em espaços vazios para plantar. Clique em plantas para regar. Plantas mortas podem ser removidas.', 'entropy_reflect': 'Refletir sobre seu jardim', 'entropy_lives': 'Seu jardim vive.', 'entropy_thrive': '{living} plantas prosperam porque você voltou.', 'entropy_absence': '{dead} te lembram da ausência.',
    'palace_title': 'Palácio da Memória', 'palace_subtitle': 'Construa um espaço mental. Coloque objetos para lembrar. Percorra depois.', 'palace_room_of': 'Sala {current} de {total}', 'palace_click_place': 'Clique para colocar', 'palace_what_represent': 'O que isso representa?', 'palace_cancel': 'Cancelar', 'palace_place_object': 'Colocar um objeto', 'palace_objects_count': '{count} objeto(s) no seu palácio', 'palace_take_tour': 'Fazer o passeio', 'palace_empty_room': 'Esta sala está vazia', 'palace_previous': 'Anterior', 'palace_next_room': 'Próxima sala', 'palace_complete_tour': 'Completar passeio', 'palace_remembers': 'Seu palácio lembra.', 'palace_geography': 'Uma geografia de significado que você construiu.', 'palace_quote': 'A mente é um palácio. O que você coloca dentro determina quem você se torna.',
    'echoes_subtitle': 'Fragmentos de outros viajantes', 'echoes_from': 'de', 'echoes_ago': 'atrás', 'echoes_arriving': 'Mais ecos chegando...', 'echoes_reveal': 'Estas não são as respostas deles. São as suas.', 'echoes_reveal2': '(Cada eco foi um dia o seu próprio reflexo)', 'echoes_quote': 'Pensamos que somos únicos. Somos apenas ecos uns dos outros.',
    'revisit_subtitle': 'Como você mudou', 'revisit_not_enough': 'Você ainda não respondeu perguntas suficientes.', 'revisit_explore': 'Explore mais salas. Deixe suas marcas. Volte depois.', 'revisit_quote1': 'Mudança requer história. História requer tempo.', 'revisit_none_changed': 'Mas nenhuma mudou ainda. Revisite salas. Responda novamente.', 'revisit_show_evolved': 'Mostraremos como você evoluiu.', 'revisit_quote2': 'Para ver a mudança, você deve primeiro mudar.', 'revisit_then': 'Então', 'revisit_now': 'Agora', 'revisit_one_shift': 'Uma mudança. Sutil mas real.', 'revisit_each_change': 'Cada mudança é uma pequena morte. E um pequeno nascimento.', 'revisit_quote3': 'Você não é quem era. Ainda não é quem será.',
  },

  zh: {
    // Index
    'title': '人类房间',
    'tagline': '一系列反映你无人关注时真实自我的房间。',
    'enter': '进入',
    'footer': '不是治疗。不是社交。不是效率工具。',
    
    // Hallway
    'back': '← 返回',
    'hallway': '走廊',
    'challenges': '挑战',
    'random': '随机',
    'open_rooms': '开放房间',
    'dark_rooms': '黑暗房间',
    'enter_risk': '后果自负',
    'premium_rooms': '高级房间',
    'unlock_to_access': '解锁访问',
    'daily_rooms': '每日房间',
    'refreshes_daily': '每天刷新',
    'time_locked': '时间锁定房间',
    'seasonal_rooms': '季节房间',
    'available_now': '现在可用',
    'fragments': '碎片',
    'boss_unlocked': 'Boss房间已解锁',
    'collect_unlock': '收集以解锁Boss房间',
    'game_rooms': '游戏房间',
    'experimental_rooms': '实验房间',
    'deep_rooms': '深层房间',
    'meta_rooms': '元房间',
    'search_rooms': '搜索房间...',
    'no_results': '没有匹配的房间',
    'clear_filters': '清除筛选',
    'explore': '探索',
    'all_categories': '全部',
    
    // About
    'what_is_this': '这是什么地方？',
    'not_productivity': '人类房间不是效率工具。',
    'not_social': '不是社交网络。',
    'not_therapy': '不是治疗。',
    'pause_experiences': '这是一系列旨在让你暂停的小体验。',
    'each_room': '每个房间需要30秒到2分钟。',
    'make_think': '有些房间会让你思考。',
    'make_uncomfortable': '有些会让你不舒服。',
    'make_calm': '有些会让你平静。',
    'make_aware': '所有房间都会让你觉醒。',
    'no_notifications': '这里没有通知。',
    'no_points': '没有积分可收集。',
    'no_validation': '没有社交验证。',
    'just_truth': '只有你、房间和真相。',
    'enter_rooms': '进入房间',
    
    // Common
    'loading': '加载中...',
    'error': '错误',
    'continue': '继续',
    'submit': '提交',
    'cancel': '取消',
    'close': '关闭',
    'next': '下一个',
    'previous': '上一个',
    'start': '开始',
    'stop': '停止',
    'yes': '是',
    'no': '否',
    'leave_room': '← 离开房间',
    'return': '← 返回',
    'return_hallway': '返回走廊',
    'begin': '开始',
    'watch': '观看...',
    'correct': '正确',
    'round': '轮',
    'enter_again': '再次进入',
    'steps': '步数',
    'depth': '深度',
    
    // Keyboard Shortcuts
    'keyboard_shortcuts': '键盘快捷键',
    'shortcut_hallway': '前往走廊',
    'shortcut_random': '随机房间',
    'shortcut_back': '返回',
    'shortcut_bookmark': '切换书签',
    'shortcut_reading': '阅读模式',
    'shortcut_search': '聚焦搜索',
    'shortcut_stats': '查看统计',
    'shortcut_achievements': '查看成就',
    'shortcut_cat_nav': '导航',
    'shortcut_cat_rooms': '房间',
    'shortcut_cat_access': '辅助功能',
    'press_to_close': '按',
    'to_close': '关闭',
    'or': '或',
    
    // Bookmarks
    'add_bookmark': '添加书签',
    'remove_bookmark': '移除书签',
    'bookmarks': '书签',
    'bookmarked_rooms': '收藏',
    'no_bookmarks': '暂无书签',
    
    // Reading Mode
    'reading_mode': '阅读模式',
    'reading_mode_on': '阅读模式已启用',
    'reading_mode_off': '阅读模式已禁用',
    
    // Updates Page
    'updates': '更新',
    'updates_title': '新功能',
    'updates_subtitle': '最近的更改和改进',
    'updates_footer': '一次更新，构建一个房间。',
    'new': '新',
    'subscribe_rss': '通过RSS订阅',
    'latest': '最新',
    'whats_new_intro': '最新更新:',
    'view_all_updates': '查看所有更新',
    'update_updates_page': '添加更新页面',
    'update_full_i18n': '完整的6语言翻译',
    'update_hallway_animations': '平滑的分类切换动画',
    'update_daily_challenges': '改进每日挑战显示',
    'update_game_rooms': '添加15个新游戏房间',
    'update_experimental_rooms': '添加14个实验房间',
    'update_random_room': '随机房间按钮',
    'update_stats_route': '修复统计页面路由',
    'update_streak_system': '每日连续系统',
    'update_weekly_summary': '每周进度总结',
    'update_rtl_support': '从右到左语言支持',
    'update_dark_rooms': '新的黑暗房间类别',
    'update_boss_rooms': '带碎片要求的Boss房间',
    'update_audio_system': '房间环境音效',
    'update_constellation_map': '视觉旅程星座图',
    'update_fragment_system': '房间完成碎片收集',
    'update_mobile_layout': '移动响应式改进',
    'update_multi_language': '多语言支持（6种语言）',
    'update_premium_system': '高级房间和解锁系统',
    'update_room_endings': '独特的房间结尾引语',
    'update_ui_redesign': '完整UI设计刷新',
    'update_theme_effects': '主题特定视觉效果（余烬、星星、故障）',
    'update_void_theme': '新的虚空主题 - 绝对黑暗模式',
    'update_achievements': '成就系统（19个里程碑）',
    'update_ai_reflection': 'AI反思房间',
    'update_settings_redesign': '重新设计的设置（4个主题）',
    'update_theme_exclusive_rooms': '主题专属房间',
    'update_discovery_localization': '发现消息本地化',
    'update_site_mood_system': '网站情绪系统',
    'update_voice_imperfection': '不完美语音房间',
    'update_mobile_optimization': '移动端优化（48px触摸目标）',
    'update_interaction_clues': '微妙的交互提示',
    'update_theme_exclusive_hallway': '走廊中可见主题专属房间',
    'update_advanced_easter_eggs': '高级彩蛋：角落序列、摩尔斯电码、午夜访客',
    'update_hidden_discoveries': '隐藏文本揭示',
    'update_dual_mode_system': '经典和交替双模式体验系统',
    'update_alternate_mode': '交替模式 - 平行实验现实',
    'update_hidden_discovery': '隐藏发现触发器：三次点击、滚动模式、空闲唤醒',
    'update_mode_effects': '模式特定视觉效果、故障和环境音频',
    'update_translation_navigator': '翻译导航器 - 可视覆盖面板',
    'update_full_localization': '6种语言近100%翻译覆盖',
    'update_meta_room_translations': '元房间内容本地化（拒绝、平行你、你的影子）',
    'update_animated_timeline': '动画更新时间线，滚动触发动画',
    'update_toast_notifications': '新版本首次访问时的更新提示',
    'update_rss_feed': 'RSS订阅更新日志',
    'update_bookmarked_filter': '走廊中的收藏房间标签页',
    'update_keyboard_shortcuts': '键盘快捷键（按 ? 查看）',
    'update_reading_mode': '阅读模式，增强排版和专注',
    'update_accessibility': '改进的焦点指示器和键盘导航',
    'update_ambient_crossfade': '环境音轨之间平滑淡入淡出过渡',
    'update_settings_audio': '设置页面中的集中音频控制',
    'update_arabic_complete': '完整的阿拉伯语翻译覆盖',
    'update_duplicate_icon_fix': '修复标题中重复的音频图标',

    // Game UI
    'game_watching': '观看中...',
    'game_repeat': '重复',
    'game_echo_fades': '回声消逝...',
    'game_escaped': '你逃脱了。',
    'game_found_exit': '你找到了出口。',
    'game_looping': '你在循环。',
    'game_deeper': '更深...',
    'game_doors_shift': '门在移动。',
    'maze_left': '左',
    'maze_right': '右',
    'maze_forward': '前',
    'maze_back': '后',
    'maze_past': '过去',
    'maze_future': '未来',
    'maze_here': '这里',
    'maze_there': '那里',
    'maze_truth': '真相',
    'maze_lies': '谎言',
    'maze_light': '光明',
    'maze_dark': '黑暗',
    'maze_memory': '记忆',
    'maze_dream': '梦',
    'maze_silence': '沉默',
    'maze_noise': '噪音',
    'random_room': '随机房间',
    'ai_reflection': 'AI反思',
    'support': '支持',
    'choose_direction': '选择一个方向。有些门会回应。',
    'rooms_explored': '已探索房间',
    'your_journey': '你的旅程',
    'complete_rooms_fragments': '完成房间以收集碎片。',
    'no_rooms_completed': '尚未完成任何房间。',
    'constellation_map': '星座图',
    'your_stats': '你的统计',
    
    // Stats
    'total_time': '总时间',
    'rooms_visited': '访问的房间',
    'total_visits': '总访问次数',
    'most_visited': '最常访问',
    'longest_session': '最长会话',
    'fragments_collected': '收集的碎片',
    'completed_rooms': '完成的房间',
    'privacy_note': '所有数据都存储在您的设备本地',
    
    // Daily Challenges
    'daily_challenges': '每日挑战',
    'challenges_completed': '已完成',
    'challenges_total': '总计',
    'resets_midnight': '午夜重置',
    
    // Weekly Summary
    'weekly_summary': '周总结',
    'week': '周',
    'this_week': '本周',
    'rooms': '房间',
    'days': '天',
    'vs_last_week': '对比上周',
    'refresh_insight': '刷新见解',
    
    // Game Room Titles
    'room_memory-puzzle': '记忆拼图',
    'room_shadow-chase': '追影',
    'room_timing-room': '时机房间',
    'room_pattern-whisper': '图案低语',
    'room_silence-puzzle': '沉默拼图',
    'room_decay-game': '衰变游戏',
    'room_observation-test': '观察测试',
    'room_balance-room': '平衡房间',
    'room_word-drift': '漂流的文字',
    'room_color-echo': '色彩回声',
    'room_mirror-tap': '镜子敲击',
    'room_maze-of-doors': '门的迷宫',
    'room_pulse-match': '脉搏匹配',
    'room_light-path': '光之路径',
    'room_sound-match': '声音匹配',
    
    // Game Room Subtitles
    'sub_memory-puzzle': '匹配微妙变化的图案',
    'sub_shadow-chase': '捕捉不可预测的移动',
    'sub_timing-room': '在精确的时刻点击',
    'sub_pattern-whisper': '重复渐逝的序列',
    'sub_silence-puzzle': '无指示解谜',
    'sub_decay-game': '触摸保存褪去的事物',
    'sub_observation-test': '发现细微差异',
    'sub_balance-room': '保持浮动物体对齐',
    'sub_word-drift': '在字母落下前抓住它们',
    'sub_color-echo': '重复微妙的色调序列',
    'sub_mirror-tap': '每次敲击在别处回响',
    'sub_maze-of-doors': '每次选择后门会重新排列',
    'sub_pulse-match': '随房间的节奏移动',
    'sub_light-path': '引导光穿过变化的黑暗',
    'sub_sound-match': '敲击形状匹配柔和音调',
    
    // Experimental Room Titles
    'room_mirror-room': '镜子房间',
    'room_echo-room': '回声房间',
    'room_pulse-room': '脉搏房间',
    'room_vanishing-room': '消失的房间',
    'room_letter-room': '信件房间',
    'room_empty-room': '空房间',
    'room_reverse-room': '反转房间',
    'room_whisper-room': '低语房间',
    'room_shadow-room': '影子房间',
    'room_labyrinth-room': '迷宫房间',
    'room_color-room': '色彩房间',
    'room_surrender-room': '投降房间',
    'room_noise-room': '噪音房间',
    'room_observer-room': '观察者房间',
    
    // Experimental Room Subtitles
    'sub_mirror-room': '略微扭曲的倒影',
    'sub_echo-room': '点击触发延迟回声',
    'sub_pulse-room': '环境在呼吸',
    'sub_vanishing-room': '互动时元素渐隐',
    'sub_letter-room': '文字像失败的记忆一样混乱',
    'sub_empty-room': '空白直到你发现秘密',
    'sub_reverse-room': '动作表现相反',
    'sub_whisper-room': '几乎听不到的真相',
    'sub_shadow-room': '你的黑暗伙伴',
    'sub_labyrinth-room': '找到你的路',
    'sub_color-room': '色彩冥想',
    'sub_surrender-room': '放下控制',
    'sub_noise-room': '静态思想',
    'sub_observer-room': '被注视着',
    
    // Daily Joke Room
    'joke_number': '笑话 #{number}',
    'joke_today': '今日笑话',
    'joke_reveal': '揭晓笑点',
    'joke_come_back': '明天再来看新笑话。',
    'joke_quote': '笑声是两个人之间最短的距离。',
    
    // Daily Quote Room
    'quote_day_number': '第 {day} 天',
    'quote_come_back': '明天回来看新的名言。',
    
    // Leaderboard
    'leaderboard': '社区排行榜',
    'leaderboard_subtitle': '匿名 · 集体 · 无竞争',
    'collective_time': '集体时间',
    'achievements_count': '成就',
    'most_visited_rooms': '最多访问的房间',
    'most_time_spent': '最多花费时间',
    'most_unlocked_achievements': '最多解锁成就',
    'no_achievements_yet': '还没有解锁任何成就。',
    
    // Transfer Data
    'transfer_data': '数据迁移',
    'transfer_data_desc': '使用一次性代码将进度迁移到另一台设备。',
    'generate_transfer_code': '生成迁移代码',
    'enter_transfer_code': '输入代码...',
    'transfer_code_expires': '代码24小时后过期，只能使用一次。',
    'generating': '生成中...',
    'import': '导入',
    'copied': '已复制！',
    
    // Waiting Game Room
    'waiting_instruction': '不要动。不要点击。只是等待。',
    'waiting_slow': '放慢脚步比听起来更难。',
    'waiting_unfamiliar': '这种静止感觉陌生，不是吗？',
    'waiting_most': '大多数人到现在已经离开了。',
    'waiting_learning': '你正在了解自己。',
    'waiting_rewards': '耐心有它自己的回报。',
    'waiting_seconds': '秒的静止',
    'waiting_waiting': '等待中...',
    'waiting_moved': '你已经动了{count}次。',
    'waiting_waited': '你等了。这很少见。',
    'waiting_most_cant': '大多数人无法安静地坐着。你做到了。',
    'waiting_quote': '当下是我们唯一可用的时刻，它是通往所有时刻的大门。',
    
    // Settings Page
    'settings': '设置',
    'settings_subtitle': '自定义你的旅程',
    'language': '语言',
    'ambient_audio': '环境音效',
    'ambient_audio_desc': '房间背景音',
    'reduced_motion': '减少动效',
    'reduced_motion_desc': '减少动画和过渡',
    'data_management': '数据管理',
    'export_journey': '导出旅程数据',
    'clear_all_data': '清除所有数据',
    'audio_enabled': '音效已启用',
    'audio_disabled': '音效已禁用',
    'data_exported': '数据已导出',
    'clear_data_confirm': '确定吗？这将清除你的所有进度。',
    'data_cleared': '数据已清除',
    // Theme settings
    'theme': '主题',
    'theme_dark': '黑暗',
    'theme_dark_desc': '纯粹的黑暗',
    'theme_bone': '骨白',
    'theme_bone_desc': '苍白的死亡',
    'theme_ash': '灰烬',
    'theme_ash_desc': '火后余烬',
    'theme_fog': '迷雾',
    'theme_fog_desc': '虚幻的不确定',
    'theme_twilight': '暮光',
    'theme_twilight_desc': '昼夜之间',
    'theme_eclipse': '日蚀',
    'theme_eclipse_desc': '光被黑暗吞噬',
    'theme_dawn': '黎明',
    'theme_dawn_desc': '觉醒的第一道光',
    'theme_changed': '主题已更新',
    'font_size': '字体大小',
    'font_small': '小',
    'font_medium': '中',
    'font_large': '大',
    'font_size_changed': '字体大小已更新',
    'high_contrast': '高对比度',
    'high_contrast_desc': '增加文字和边框对比度',
    'high_contrast_enabled': '高对比度已启用',
    'high_contrast_disabled': '高对比度已禁用',
    
    // Ambient Music Control
    'ambient_music': '环境音乐',
    'ambient_music_desc': '控制背景音景',
    'volume': '音量',
    'ambient_track': '环境音轨',
    'playing': '播放中',
    'play': '播放',
    'ambient_default': '平静',
    'ambient_night': '夜晚',
    'ambient_scary': '诡异',
    'ambient_truth': '真相',
    'ambient_identity': '反思',
    'ambient_mortality': '时间',
    'ambient_nothing': '虚空',
    'ambient_meta': '数字',
    'ambient_daily': '温暖',
    'ambient_seasonal': '神奇',
    'ambient_premium': '深沉',
    
    'shared_room': '共享房间',
    'imagine_room': '想象',
    'imagine_title': '想象你自己',
    'imagine_subtitle': '描述你如何看待自己。让房间展示从你的话语中浮现的形象。',
    'imagine_placeholder': '我把自己看作一个...\n\n描述你的本质，你的性格，你内心的感受。',
    'imagine_hint': '诚实一点。图像反映你的话语。',
    'imagine_generate': '看见自己',
    'imagine_generating': '创建中...',
    'imagine_try_again': '重新描述',
    'imagine_quote': '我们无法清晰地看到自己。我们只看到我们相信的。',
    'room_imagine-yourself': '想象你自己',
    'sub_imagine-yourself': '描述你自己。看看会浮现什么。',
    'theme_void': '虚空',
    'theme_void_desc': '只剩下阴影',
    
    // Easter Egg Discoveries
    'discovery_label': '发现',
    'discovery_konami': '你知道古老的方法。',
    'discovery_secret_sequence': '你拼写出了真相。',
    'discovery_long_press': '耐心揭示秘密。',
    'discovery_triple_click': '坚持得到回报。',
    'discovery_silence': '寂静在说话。',
    
    // Theme-Exclusive Rooms
    'ash_exclusive': '灰烬主题专属',
    'twilight_exclusive': '暮光主题专属',
    'void_exclusive': '虚空主题专属',
    'theme_required': '需要{theme}主题',
    'ash_embers_title': '从灰烬中',
    'ash_embers_line1': '一切终将燃烧。',
    'ash_embers_line2': '重要的是之后升起的东西。',
    'ash_embers_line3': '触摸屏幕。创造余烬。',
    'ash_embers_quote': '从灰烬中，我们记得曾经燃烧的东西。',
    'room_ash-embers': '灰烬与余烬',
    'sub_ash-embers': '仅在灰烬主题中可见',
    'twilight_stars_title': '在昼夜之间',
    'twilight_stars_line1': '星星记得每一个仰望过的人。',
    'twilight_stars_line2': '连接它们。创造你自己的星座。',
    'twilight_stars_quote': '在暮光中，我们看到白昼隐藏的东西。',
    'room_twilight-stars': '暮光之星',
    'sub_twilight-stars': '仅在暮光主题中可见',
    'void_abyss_title': '深渊',
    'void_abyss_line1': '凝视虚空。虚空也在凝视你。',
    'void_word_1': '虚无',
    'void_word_2': '空虚',
    'void_word_3': '寂静',
    'void_word_4': '消逝',
    'void_word_5': '孤独',
    'void_word_6': '终结',
    'void_revealed': '已揭示',
    'void_abyss_quote': '在绝对的黑暗中，我们终于看见自己。',
    'room_void-abyss': '深渊',
    'sub_void-abyss': '仅在虚空主题中可见',
    
    // Theme Exclusive Section
    'theme_exclusive_rooms': '主题专属房间',
    'theme_exclusive_hint': '在设置中更改主题以解锁这些房间。',
    'requires_theme': '需要',
    'unlocked': '已解锁',
    'switch_to': '切换到',
    
    // Advanced Easter Eggs
    'corner_sequence': '你描绘了边界。',
    'morse_sos': '收到S.O.S.。',
    'reverse_scroll': '返回... 但更快。',
    'midnight_visitor': '午夜时分欢迎你。',
    'shake': '你摇动了什么。',
    
    // Experience Mode
    'experience_mode': '体验模式',
    'classic_mode': '经典',
    'classic_mode_desc': '熟悉的现实',
    'alternate_mode': '▒ 异界 ▒',
    'alternate_mode_desc': '[ 你不该在这里 ]',
    'switch_to_alternate': '░▓█ 下沉 █▓░',
    'switch_to_classic': '← 返回表面',
    'enter_parallel_reality': '[ 门已打开 ]',
    'return_to_familiar': '返回熟悉',
    'something_shifted': '[ 有什么改变了 ]',
    'reality_feels_different': '[ 现实感觉不同了 ]',
    'alternate_tagline': '同样的房间。不同的真相。',
    'alternate_enter': '下沉',
    'alternate_footer': '[ 事物并非表象 ]',
    'alternate_rooms': '░▓ 平行现0实 ▓░',
    'alternate_rooms_hint': '[ 这些房间只存在于平0行 ]',
    
    // Alternate Mode Exclusive Rooms (Chinese)
    'room_the-other-side': '另一边',
    'room_the-unraveling': '瓦解',
    'room_the-echo': '回声',
    'sub_the-other-side': '在黑暗中缓慢移动',
    'sub_the-unraveling': '每一个字都让现实崩塌',
    'sub_the-echo': '你说的话永远不会离开',
    
    // Meta Rooms (Chinese)
    'room_the-refusal': '拒绝',
    'sub_the-refusal': '有时网站会拒绝你',
    'room_parallel-you': '平行的你',
    'sub_parallel-you': '你的另一个版本',
    'room_your-shadow': '你的影子',
    'sub_your-shadow': '跟随你的东西',
    
    // AI Rooms (Chinese)
    'room_ai-reflection-room': 'AI反思室',
    'sub_ai-reflection-room': '让AI与你一起反思',
    'sub_dream-visualization': '描述你的梦。看它成形。',
    'sub_future-self': '来自未来的信息',
    
    // Meta Room Content - The Refusal (Chinese)
    'refusal_line1': '这个房间拒绝你。',
    'refusal_line2': '不是所有东西都在你想要时打开。',
    'refusal_line3': '有时答案就是：不。',
    'refusal_line4': '试试按钮。没有用的。',
    'refusal_button': '进入房间',
    
    // Meta Room Content - Parallel You (Chinese)
    'parallel_choice_1': '你选择了速度。',
    'parallel_alternate_1': '另一个你选择了耐心。',
    'parallel_reflection_1': '他们还在等待。',
    'parallel_choice_2': '你提前离开了。',
    'parallel_alternate_2': '另一个你留到了最后。',
    'parallel_reflection_2': '他们看到了你错过的。',
    'parallel_choice_3': '你避开了难题。',
    'parallel_alternate_3': '另一个你回答了。',
    'parallel_reflection_3': '他们现在承载着那个真相。',
    'parallel_choice_4': '你匆匆而过。',
    'parallel_alternate_4': '另一个你品味每一刻。',
    'parallel_reflection_4': '他们记得更多。',
    'parallel_choice_5': '你继续滑动。',
    'parallel_alternate_5': '另一个你停了下来。',
    'parallel_reflection_5': '他们发现了什么。',
    'parallel_choice_6': '你选择了舒适。',
    'parallel_alternate_6': '另一个你选择了不适。',
    'parallel_reflection_6': '他们从中成长。',
    'parallel_quote': '每一刻都分裂成无限条路。你只走一条。',
    
    // Meta Room Content - Your Shadow (Chinese)
    'shadow_generated': '根据你的行为生成',
    'shadow_intro': '你的影子...',
    'shadow_follows': '这个影子跟随你穿过每个房间。',
    'shadow_quote': '我们不是我们所想的。我们是我们所做的。',
    'shadow_trait_completion': '寻求完成',
    'shadow_trait_surface': '偏好表面',
    'shadow_trait_lingers': '在空间中停留',
    'shadow_trait_moves_quickly': '行动迅速',
    'shadow_trait_avoids_depth': '回避深度',
    'shadow_trait_stillness': '拥抱静止',
    'shadow_trait_chases': '追逐无名之物',
    'shadow_trait_darkness': '被黑暗吸引',
    'shadow_trait_emptiness': '对空虚感到舒适',
    'shadow_trait_undefined': '未定义',
    'shadow_trait_forming': '仍在形成',
    
    // Translation Navigator (Chinese)
    'translation_navigator': '翻译导航',
    'translation_coverage': '各语言房间翻译覆盖率',
    'keys': '键',
    'room_translations': '房间翻译',
    'show_all': '显示全部',
    'show_missing': '显示缺失',
    'all_translated': '所有房间已翻译！',
    'missing_translations': '缺失翻译',
    
    // Additional Room Titles (Chinese)
    'room_how-many-times-left': '还剩多少次',
    'room_life-in-weeks': '你的人生周历',
    'room_delay-button': '延迟按钮',
    'room_one-honest-sentence': '一句真话',
    'room_this-moment': '此刻不会再来',
    'room_reality-receipt': '现实收据',
    'room_nothing-happens': '这里什么都不会发生',
    'room_default-choice': '你的默认选择',
    'room_thought-loop': '思维循环',
    'room_expect-vs-happens': '期望与现实',
    'room_question-never-answer': '你从不回答的问题',
    'room_not-special': '你并不特别（没关系）',
    'room_shortcut-always-take': '你总是走的捷径',
    'room_blurred-self': '模糊的自我',
    'room_image-zooming': '你一直放大的图片',
    'room_comfort-color': '舒适的颜色',
    'room_line-you-crossed': '你跨过的那条线',
    'room_read-aloud': '大声读出来',
    'room_voice-without-meaning': '没有意义的声音',
    'room_sentence-you-avoid': '你回避的句子',
    'room_if-nothing-changes': '如果什么都不改变',
    'room_real-priorities': '你真正的优先级',
    'room_last-conversation': '最后的对话',
    'room_who-without-job': '没有工作的你是谁？',
    'room_comfort-zone-scanner': '舒适区扫描仪',
    'room_say-it-once': '只说一次',
    'room_listen-to-yourself': '倾听自己',
    'room_life-as-shapes': '形状中的人生',
    'room_face-without-features': '没有特征的脸',
    'room_what-keeps-awake': '让你失眠的事',
    'room_you-at-317am': '凌晨3:17的你',
    'room_thought-that-returns': '不断回来的想法',
    'room_room-you-visit-most': '你最常访问的房间',
    'room_room-you-avoid': '你回避的房间',
    'room_left-too-early': '你离开得太早',
    'room_daily-quote': '每日名言',
    'room_daily-joke': '每日笑话',
    'room_theyre-watching': '他们在看着你',
    'room_the-last-visitor': '最后的访客',
    'room_dont-look-away': '不要移开目光',
    'room_the-whisper': '低语',
    'room_you-forgot-something': '你忘了什么',
    'room_the-mirror-lies': '镜子在说谎',
    'room_something-follows': '有东西跟着你',
    'room_the-countdown': '倒计时',
    'room_eyes-in-the-dark': '黑暗中的眼睛',
    'room_the-voice': '那个声音',
    'room_it-knows-your-name': '它知道你的名字',
    'room_the-door': '那扇门',
    'room_boss-room-free': '你真理的总和',
    'room_boss-room-horror': '恐惧之心',
    'room_boss-room-premium': '最深的真相',
    'room_mouse-tracker': '鼠标追踪器',
    'room_scroll-confession': '滚动的忏悔',
    'room_waiting-game': '等待游戏',
    'room_day-in-pixels': '像素化的一天',
    'room_memory-maze': '记忆迷宫',
    'room_decision-shadow': '决定的影子',
    'room_anonymous-mirrors': '匿名镜子',
    'room_collective-room': '集体房间',
    'room_secret-room': '秘密房间',
    'room_glitch-room': '不稳定的房间',
    'room_full-moon-room': '满月房间',
    'room_weekend-room': '周末房间',
    'room_317am-room': '凌晨3:17房间',
    'room_idle-room': '静止房间',
    'room_invite-friend-room': '共享房间',
    'room_halloween-room': '面纱房间',
    'room_winter-solstice-room': '最长的夜',
    'room_new-year-room': '给未来自己的信',
    'room_void-room': '虚空',
    'room_pattern-room': '你的模式',
    'room_time-capsule': '时间胶囊',
    'room_the-revisit': '重访',
    'room_contradiction-room': '矛盾房间',
    'room_export-journey': '导出你的旅程',
    'room_anonymous-echoes': '匿名回声',
    'room_the-waiting-room': '等候室',
    'room_dream-visualization': '梦境可视化',
    'room_future-self': '未来的自己',
    'room_eclipse-shadows': '日食之影',
    'room_dawn-awakening': '黎明觉醒',
    
    // Additional Room Subtitles (Chinese)
    'sub_how-many-times-left': '剩余的次数',
    'sub_life-in-weeks': '4000个方格。仅此而已。',
    'sub_delay-button': '每次都更难',
    'sub_one-honest-sentence': '不能编辑。不能删除。',
    'sub_this-moment': '你在这里。仅此一次。',
    'sub_reality-receipt': '你日子的总结',
    'sub_nothing-happens': '正如所说',
    'sub_eclipse-shadows': '在完全黑暗中说话',
    'sub_dawn-awakening': '迎接第一缕光',
    'sub_default-choice': '安全还是冒险。每次都是。',
    'sub_thought-loop': '看着你的思维重复',
    'sub_expect-vs-happens': '希望与现实的差距',
    'sub_question-never-answer': '大多数人都离开了',
    'sub_not-special': '没有安慰。只有真相。',
    'sub_shortcut-always-take': '模式会显现',
    'sub_blurred-self': '记忆就是这样运作的',
    'sub_image-zooming': '在虚无中寻找答案',
    'sub_comfort-color': '你会待多久？',
    'sub_line-you-crossed': '我们看到你犹豫的地方',
    'sub_read-aloud': '你必须说出的话',
    'sub_voice-without-meaning': '没有文字的声音',
    'sub_sentence-you-avoid': '写完才能离开',
    'sub_if-nothing-changes': '对你未来五年的预测',
    'sub_real-priorities': '基于时间，而非言语',
    'sub_last-conversation': '写下你永远不会发送的话',
    'sub_who-without-job': '重新书写自己',
    'sub_comfort-zone-scanner': '我们看到你的犹豫',
    'sub_say-it-once': '不能重来。不能重做。',
    'sub_listen-to-yourself': '你的声音，慢放',
    'sub_life-as-shapes': '抽象。无标签。你的。',
    'sub_face-without-features': '移除直到什么都不剩',
    'sub_what-keeps-awake': '晚上10点后可用',
    'sub_you-at-317am': '时间特定。个人。',
    'sub_thought-that-returns': '每次访问都会回来',
    'sub_room-you-visit-most': '你的模式揭示',
    'sub_room-you-avoid': '你跳过的说明了什么',
    'sub_left-too-early': '当你快速离开时触发',
    'sub_daily-quote': '每天更新的智慧',
    'sub_daily-joke': '每天更新的黑色幽默',
    'sub_theyre-watching': '你并不孤单',
    'sub_the-last-visitor': '有人在你之前来过',
    'sub_dont-look-away': '它知道你什么时候离开',
    'sub_the-whisper': '仔细听',
    'sub_you-forgot-something': '是什么？',
    'sub_the-mirror-lies': '打碎它看真相',
    'sub_something-follows': '继续跑',
    'sub_the-countdown': '零时有事发生',
    'sub_eyes-in-the-dark': '它们在增加',
    'sub_the-voice': '有人被困在这里',
    'sub_it-knows-your-name': '你不该来的',
    'sub_the-door': '有东西在敲门',
    'sub_boss-room-free': '最终免费房间',
    'sub_boss-room-horror': '面对最终的黑暗',
    'sub_boss-room-premium': '最终深层房间',
    'sub_mouse-tracker': '我们看到你如何移动',
    'sub_scroll-confession': '你滚动得有多快？',
    'sub_waiting-game': '你能什么都不做吗？',
    'sub_day-in-pixels': '时间变成形状',
    'sub_memory-maze': '图案在变化',
    'sub_decision-shadow': '你的选择揭示模式',
    'sub_anonymous-mirrors': '来自陌生人的想法',
    'sub_collective-room': '每个人都在这里。没人留下。',
    'sub_secret-room': '你找到了',
    'sub_glitch-room': '有什么不对劲',
    'sub_full-moon-room': '只在月亮高峰时开放',
    'sub_weekend-room': '休息是一个目的地',
    'sub_317am-room': '时间特定。个人。',
    'sub_idle-room': '通过耐心发现',
    'sub_invite-friend-room': '有些旅程需要见证',
    'sub_halloween-room': '10月31日 - 面纱很薄',
    'sub_winter-solstice-room': '12月21日 - 释放与保留',
    'sub_new-year-room': '12月31日 - 写给未来的你',
    'sub_void-room': '代码中的伤口',
    'sub_pattern-room': '网站在你身上看到的',
    'sub_time-capsule': '写给未来的自己',
    'sub_the-revisit': '看看你的答案如何改变',
    'sub_contradiction-room': '你的真理不一致',
    'sub_export-journey': '下载你的路径',
    'sub_anonymous-echoes': '来自陌生人的碎片',
    'sub_the-waiting-room': '用存在解锁',
    
    // New Phase 1-3 Rooms (Chinese)
    'room_letters-never-sent': '从未寄出的信',
    'sub_letters-never-sent': '写给永远不会读到的人',
    'room_breath-sync': '呼吸同步',
    'sub_breath-sync': '与房间一起呼吸',
    'room_entropy-garden': '熵变花园',
    'sub_entropy-garden': '生长与衰败交织',
    'room_memory-palace': '记忆宫殿',
    'sub_memory-palace': '建造你的心灵圣所',
    'room_morning-ritual': '晨间仪式',
    'sub_morning-ritual': '带着意图开始新的一天',
    'room_evening-ritual': '夜间仪式',
    'sub_evening-ritual': '以反思结束一天',
    'room_weekly-ritual': '每周仪式',
    'sub_weekly-ritual': '周日是回顾的日子',
    
    // Letters Never Sent Room (Chinese)
    'letters_title': '从未寄出的信',
    'letters_subtitle': '写给永远不会读到的人。AI会像他们可能的那样回复。',
    'letters_select_recipient': '你在写给谁？',
    'letters_past_self': '过去的自己',
    'letters_future_self': '未来的自己',
    'letters_someone_lost': '失去的人',
    'letters_someone_wronged': '我伤害过的人',
    'letters_placeholder': '亲爱的...',
    'letters_send': '发送到虚空',
    'letters_sending': '发送中...',
    'letters_response': '一个回复浮现...',
    'letters_write_another': '再写一封信',
    'letters_quote': '有些话注定要写下但永不寄出。',
    
    // Breath Sync Room (Chinese)
    'breath_title': '呼吸同步',
    'breath_subtitle': '让你的呼吸与房间的节奏同步。',
    'breath_inhale': '吸气',
    'breath_hold': '屏息',
    'breath_exhale': '呼气',
    'breath_cycles': '完成的循环',
    'breath_quote': '呼吸是身体与心灵之间的桥梁。',
    
    // Entropy Garden Room (Chinese)
    'entropy_title': '熵变花园',
    'entropy_subtitle': '你的植物随每次访问生长，但当你离开时会衰败。',
    'entropy_plant': '种下种子',
    'entropy_water': '浇水',
    'entropy_days_alive': '存活天数',
    'entropy_health': '健康',
    'entropy_empty': '你的花园是空的。种点什么吧。',
    'entropy_quote': '万物生长。万物凋零。这就是道。',
    
    // Memory Palace Room (Chinese)
    'memory_palace_title': '记忆宫殿',
    'memory_palace_subtitle': '建造一个心灵圣所。在你创建的房间里放置物品。',
    'memory_palace_add_room': '添加房间',
    'memory_palace_add_object': '添加物品',
    'memory_palace_room_name': '房间名称...',
    'memory_palace_object_name': '物品名称...',
    'memory_palace_empty': '你的宫殿是空的。创建你的第一个房间。',
    'memory_palace_quote': '我们一间房间一间房间地构建我们的心智。',
    
    // Ritual Rooms (Chinese)
    'ritual_locked': '这个仪式尚不可用',
    'morning_ritual_title': '晨间仪式',
    'morning_ritual_intro': '为新的一天设定意图的时刻。',
    'morning_ritual_available': '可用时间：早6点-9点',
    'evening_ritual_title': '夜间仪式',
    'evening_ritual_intro': '反思一天的时刻。',
    'evening_ritual_available': '可用时间：晚8点-11点',
    'weekly_ritual_title': '每周仪式',
    'weekly_ritual_intro': '反思过去一周，为下周设定意图的时刻。',
    'weekly_locked': '每周仪式等待着你',
    'weekly_time': '周日再来',
    'today_is': '今天是',
    'your_week': '你的一周',
    'days_active': '活跃天数',
    'weekly_summary_note': '现在，让我们更深入地反思。',
    'weekly_highlight': '你这周的亮点是什么？',
    'weekly_challenge': '这周什么最挑战你？',
    'weekly_growth': '这周你如何成长了？',
    'weekly_intention': '你为下周设定什么意图？',
    'weekly_placeholder': '慢慢来...',
    'reflect': '反思',
    'reflecting': '反思中...',
    'complete': '完成',
    'weekly_quote': '一周结束。另一周开始。你带走重要的东西。',
    
    // Personal Timeline (Chinese)
    'personal_timeline': '个人时间线',
    'timeline_subtitle': '你穿越房间的旅程',
    'timeline_empty': '你的时间线是空的。开始探索吧。',
    'timeline_today': '今天',
    'timeline_yesterday': '昨天',
    'timeline_this_week': '本周',
    'timeline_earlier': '更早',
    
    // Weather System (Chinese)
    'weather_clear': '晴朗',
    'weather_rain': '雨',
    'weather_fog': '雾',
    'weather_snow': '雪',
    
    // Notifications (Chinese)
    'notifications': '通知',
    'notifications_desc': '获取时间锁定内容的通知',
    'notify_time_locked': '时间锁定房间',
    'notify_full_moon': '满月之夜',
    'notify_daily_reset': '每日挑战重置',
    'notify_time_capsule': '时间胶囊解锁',
    'notifications_enabled': '通知已启用',
    'notifications_disabled': '通知已禁用',
    
    // Community Submissions (Chinese)
    'community_submissions': '社区投稿',
    'community_subtitle': '提交你的房间创意',
    'community_room_title': '房间标题',
    'community_room_description': '房间描述',
    'community_room_category': '类别',
    'community_submit': '提交创意',
    'community_submitted': '感谢你的投稿！',
    'community_your_submissions': '你的投稿',
    'community_empty': '还没有投稿。成为第一个！',
    
    // Proactive Guide (Chinese)
    'guide_hesitation': '在思考？没关系。想要一个建议吗？',
    'guide_long_absence': '欢迎回来。已经有一段时间了。',
    'guide_milestone': '你已经走了很远。想要反思你的旅程吗？',
    'guide_dismiss': '忽略',
    'guide_accept': '给我看看',
    
    // Voice Mode (Chinese)
    'voice_mode': '语音模式',
    'voice_mode_desc': '让向导对你说话',
    'voice_mode_enabled': '语音模式已启用',
    'voice_mode_disabled': '语音模式已禁用',
    
    // Offline Support (Chinese)
    'offline_available': '离线可用',
    'offline_cached': '此房间已缓存供离线查看',
    
    // Journey Export (Chinese)
    'journey_export': '导出旅程',
    'journey_import': '导入旅程',
    'journey_export_json': '导出为JSON',
    'journey_import_json': '从JSON导入',
    'journey_exported': '旅程导出成功',
    'journey_imported': '旅程导入成功',
    
    // Phase 4 - Seasonal Rooms (Chinese)
    'room_spring-equinox-room': '春分',
    'sub_spring-equinox-room': '释放与更新',
    'room_summer-solstice-room': '夏至',
    'sub_summer-solstice-room': '最长的一天',
    'room_autumn-equinox-room': '秋分',
    'sub_autumn-equinox-room': '收获与接受',
    'room_valentine-room': '自爱房间',
    'sub_valentine-room': '你值得拥有的爱',
    
    // Spring Equinox Room (Chinese)
    'spring_date': '3月20日',
    'spring_title': '春分',
    'spring_message_1': '昼夜等长。',
    'spring_message_2': '完美平衡的时刻。',
    'spring_message_3': '你会释放什么来为成长腾出空间？',
    'spring_letting_go': '我要放下：',
    'spring_letting_go_placeholder': '那些不再服务于你的...',
    'spring_planting': '我要播种：',
    'spring_planting_placeholder': '你希望成长的...',
    'spring_bloom': '让它绽放',
    'spring_released': '已释放：',
    'spring_planted': '已播种：',
    'spring_closing': '从这一刻起，新生命开始了。',
    'spring_quote': '大自然从不匆忙，但一切都会完成。',
    
    // Summer Solstice Room (Chinese)
    'summer_date': '6月21日',
    'summer_title': '最长的一天',
    'summer_message_1': '这是一年中最长的一天。',
    'summer_message_2': '光芒达到巅峰。',
    'summer_message_3': '如果有更多时间，你会做什么？',
    'summer_message_4': '描述你最充实、最有活力的一天。',
    'summer_placeholder': '在我最充实的一天，我会...',
    'summer_submit': '拥抱光明',
    'summer_closing': '太阳听到了你。现在它开始下沉。',
    'summer_quote': '在光芒的巅峰，我们发现我们真正想要照亮的是什么。',
    
    // Autumn Equinox Room (Chinese)
    'autumn_date': '9月22日',
    'autumn_title': '秋分',
    'autumn_message_1': '平衡回归。',
    'autumn_message_2': '收获已经准备好。',
    'autumn_message_3': '你培育了什么？你能接受什么？',
    'autumn_harvesting': '我在收获：',
    'autumn_harvesting_placeholder': '今年你培育的...',
    'autumn_accepting': '我在接受：',
    'autumn_accepting_placeholder': '你无法改变的...',
    'autumn_release': '让树叶落下',
    'autumn_harvested': '已收获：',
    'autumn_accepted': '已接受：',
    'autumn_closing': '万物在其时刻落下。',
    'autumn_quote': '秋天向我们展示了放手是多么美丽。',
    
    // Valentine's Day Room (Chinese)
    'valentine_date': '2月14日',
    'valentine_title': '你值得拥有的爱',
    'valentine_message_1': '今天不是关于别人。',
    'valentine_message_2': '而是关于那个一直陪伴你的人。',
    'valentine_message_3': '那个听到每一个想法的人。',
    'valentine_message_4': '给自己写一封情书。',
    'valentine_prompt': '给自己的一封信：',
    'valentine_placeholder': '亲爱的我，我想让你知道...',
    'valentine_submit': '带着爱发送',
    'valentine_closing': '你值得拥有你给予他人的爱。',
    'valentine_quote': '你自己值得你的爱和关怀。',
    
    // Update changelog
    'update_seasonal_rooms': '四个新的季节房间：春分、夏至、秋分和情人节',
    'update_fragment_trading': '碎片交易系统 - 牺牲碎片解锁时间锁定房间、预览恐怖房间或接收AI消息',
    'update_new_themes': '两个新主题：日蚀（纯黑暗）和黎明（暖色渐变，仅限早上5-7点）',
     'update_regret_machine': '遗憾机器 - AI驱动的替代时间线叙事',
     'update_memory_reconstruction': '记忆重建 - AI填充褪色记忆的感官细节',
     
     // Phase 7 - Regret Machine Room
     'regret_machine_title': '遗憾机器',
     'regret_machine_subtitle': '描述一个你希望做出不同选择的决定。机器会向你展示可能发生的事情。',
     'regret_placeholder': '我后悔...\n\n描述一个你做出的选择，你希望它有所不同。你会怎么做？',
     'regret_hint': '这不是关于责备。这是关于理解。',
     'regret_explore': '探索时间线',
     'regret_generating': '重建替代现实...',
     'regret_alternate_timeline': '替代时间线',
     'regret_explore_another': '探索另一个',
     'regret_error_short': '请更详细地描述你的遗憾',
     'room_regret-machine': '遗憾机器',
     
     // Phase 7 - Memory Reconstruction Room
     'memory_reconstruction_title': '记忆重建',
     'memory_reconstruction_subtitle': '描述一段褪色的记忆。让我们帮助你找回那种感觉。',
     'memory_placeholder': '我记得...\n\n描述一段遥远或支离破碎的记忆。还剩下什么碎片？什么感觉还在？',
     'memory_timeframe_placeholder': '这是什么时候？（例如："童年"、"十年前"）',
     'memory_hint': '记忆不是录像。它是一种重建。',
     'memory_reconstruct': '重建记忆',
     'memory_reconstructing': '拼凑碎片...',
     'memory_reconstructed': '重建的记忆',
     'memory_explore_another': '探索另一段记忆',
     'memory_error_short': '请更详细地描述你的记忆',
     'room_memory-reconstruction': '记忆重建',
    'update_eclipse_room': '日蚀之影 - 在完全黑暗中说话，在闪光中揭示',
    'update_dawn_room': '黎明觉醒 - 用呼吸练习和意念迎接第一道光',
    
    // New themes
    'eclipse_theme_required': '此房间需要日蚀主题。完全的黑暗在等待。',
    'dawn_theme_required': '此房间需要在清晨时段使用黎明主题。',
    'dawn_time_hint': '仅限早上5-7点',
    'change_theme': '在设置中更改主题',
    
    // Eclipse Shadows Room
    'eclipse_shadows_title': '日蚀之影',
    'eclipse_shadows_intro': '在完全的黑暗中，秘密感觉更安全。诚实回答——只有短暂的闪光会照亮你的话语。',
    'enter_darkness': '进入黑暗',
    'shadow_question_1': '你对自己隐藏了什么？',
    'shadow_question_2': '如果没人能听到，你会说什么真话？',
    'shadow_question_3': '凌晨3点什么困扰着你？',
    'shadow_question_4': '如果恐惧不存在，你会做什么？',
    'shadow_question_5': '你还没准备好面对什么？',
    'speak_into_dark': '在黑暗中说话...',
    'release': '释放',
    'eclipse_reveal_prompt': '点击每个阴影揭示你的坦白。',
    'hidden_in_shadow': '▓▓▓▓▓▓▓▓▓▓▓▓',
    'eclipse_reveal_hint': '有些真相在黑暗中更清晰。',
    'eclipse_shadows_quote': '在完全的黑暗中，我们终于看见了自己。',
    
    // Dawn Awakening Room
    'dawn_awakening_title': '黎明觉醒',
    'dawn_awakening_intro': '第一道光带来可能。在世界醒来之前，设定你的意念。',
    'greet_the_light': '迎接光芒',
    'breathe_in_light': '吸入光芒',
    'hold_warmth': '保持温暖',
    'release_night': '释放夜晚',
    'dawn_prompt_1': '今天你想带着什么？',
    'dawn_prompt_2': '你会放下什么？',
    'dawn_prompt_3': '你会给自己什么小小的善意？',
    'set_your_intention': '设定你的意念...',
    'plant_seed': '种下这颗种子',
    'dawn_awakening_quote': '每一个黎明都是重新开始的邀请。',
    
    // Fragment Trading
    'fragment_trading': '碎片交易',
    'sacrifice_to_unlock': '牺牲碎片解锁秘密',
    'your_fragments': '你的碎片',
    'fragments_any': '任意碎片',
    'fragments_free': '清晰碎片',
    'fragments_premium': '深度碎片',
    'fragments_horror': '阴影碎片',
    'trade_successful': '交易成功！',
    'trade_failed': '交易失败',
    'not_enough_fragments': '碎片不足',
    'continue_trading': '继续交易',
    'trading_fragments': '正在交易碎片...',
    'fragment_trade_warning': '牺牲的碎片无法恢复',
    'unlock-time-locked': '解锁时间锁定房间',
    'unlock-time-locked_desc': '无需等待即可进入时间锁定房间',
    'preview-horror': '恐怖房间预览',
    'preview-horror_desc': '进入前先窥探恐怖房间',
    'ai-reflection-trade': 'AI反思',
    'ai-reflection-trade_desc': '接收来自AI的个性化消息',
    'unlock-premium-preview': '高级房间试用',
    'unlock-premium-preview_desc': '访问高级房间一次',
    'fragment_trading_room': '碎片祭坛',
    'fragment_trading_intro': '在这里，你可以牺牲收集的碎片来解锁这些房间中隐藏的秘密。',
    'your_collection': '你的收藏',
    'fragment_clarity': '清晰',
    'fragment_depth': '深度',
    'fragment_shadow': '阴影',
    'total_fragments': '总计',
    'open_altar': '接近祭坛',
    'recent_trades': '最近的供奉',
    'fragment_trade_ending': '给予的无法收回。但获得的...会留下。',
    'room_fragment-trade': '碎片祭坛',
    'sub_fragment-trade': '牺牲以解锁秘密',
    'update_anniversary_system': '周年纪念与里程碑系统 - 追踪你的旅程',
    'update_archive_room': '档案馆 - 25个房间解锁完整旅程历史',
    'update_mirror_room': '万我之镜 - 50个房间解锁AI行为画像',
    'update_ending_room': '结局 - 75个房间解锁个性化告别',
    'room_the-archive': '档案馆',
    'room_mirror-of-all-selves': '万我之镜',
    'room_the-ending': '结局',
    // Phase 9
    'update_fragment_rarities': '碎片稀有度 - 普通、稀有和传奇可收集碎片',
    'update_fragment_gallery': '碎片画廊 - 你的收藏展示柜',
    'update_seasonal_fragments': '季节和秘密碎片 - 限时收藏品',
    'fragment_collection': '收藏',
    'fragment_collection_title': '碎片收藏',
    'fragment_collection_subtitle': '每个房间都会留下什么。收集它们全部。',
    'collection_milestones': '里程碑',
    'collection_secrets': '秘密',
    'collection_footer': '某些碎片只在特定条件下出现。',
    'rarity_common': '普通',
    'rarity_rare': '稀有',
    'rarity_legendary': '传奇',
    // Phase 10-11
    'accessibility': '无障碍',
    'accessibility_desc': '自定义可读性和动效偏好',
    'font_family': '字体系列',
    'font_default': '等宽字体',
    'font_dyslexia': '阅读障碍友好',
    'font_sans': '无衬线',
    'font_changed': '字体已更新',
    'line_height': '行高',
    'line_compact': '紧凑',
    'line_normal': '正常',
    'line_relaxed': '宽松',
    'line_spacious': '宽敞',
    'line_height_changed': '行高已更新',
    'reduced_motion_full_desc': '禁用所有动画和粒子效果',
    'larger_cursors': '更大目标',
    'larger_cursors_desc': '增加点击目标大小',
    'update_accessibility_suite': '无障碍套件 - 阅读障碍字体、行高控制',
    'update_dyslexia_font': '设置中的阅读障碍友好字体选项',
    'update_line_height': '可调节行高以提高可读性',
    'update_enhanced_reduced_motion': '增强减少动效 - 完全禁用所有效果',
    'update_shared_moments': '共享时刻 - 与他人的匿名情感签到',
    'update_whisper_wall': '低语墙 - 在黑暗中留下匿名真相',
    'update_collective_memory': '集体记忆 - 我们记忆的共享碎片',
    'whisper_prompt': '在墙上留下一句低语。匿名。永久。',
    'whisper_placeholder': '你默默承受着什么？',
    'whisper_send': '低语',
    'whisper_added': '你的低语加入了墙壁。',
    'whisper_another': '再次低语',
    'shared_moments_title': '此刻，在这一刻...',
    'shared_moments_subtitle': '你真的好吗？',
    'feeling_grateful': '感恩',
    'feeling_lost': '迷失',
    'feeling_hopeful': '充满希望',
    'feeling_heavy': '沉重',
    'feeling_peaceful': '平静',
    'feeling_restless': '不安',
    'room_whisper-wall': '低语墙',
    'room_collective-memory': '集体记忆',
    'room_shared-moments': '共享时刻',
    'sub_regret-machine': '探索可能发生的事',
    'sub_memory-reconstruction': '重建一段褪色的记忆',
    'sub_whisper-wall': '匿名真相在黑暗中漂浮',
    'sub_collective-memory': '我们记忆的共享碎片',
    'sub_shared-moments': '你好吗，说实话？',
    'sub_the-archive': '你完整的旅程历史',
    'sub_mirror-of-all-selves': 'AI揭示你的行为画像',
    'sub_the-ending': '你赢得的告别',

    // v3.21.0 - New Rooms
    'room_mask-you-wear': '你戴的面具',
    'sub_mask-you-wear': '你展示的是哪张脸？',
    'room_recurring-dream': '你的重复之梦',
    'sub_recurring-dream': '那个不断回来的梦',
    'room_promise-you-broke': '你违背的承诺',
    'sub_promise-you-broke': '关于破碎承诺的诚实',
    'room_words-you-swallowed': '你咽下的话',
    'sub_words-you-swallowed': '你从未说出的话',
    'room_age-you-feel': '你感觉的年龄',
    'sub_age-you-feel': '你真正多大了？',
    'room_biggest-lie': '你最大的谎言',
    'sub_biggest-lie': '你说得最多的谎言',
    'room_person-you-pretend-to-be': '你假装的人',
    'sub_person-you-pretend-to-be': '你的表演',
    'room_person-you-pretend': '你假装的人',
    'sub_person-you-pretend': '你的表演',
    'room_what-you-collect': '你收集什么',
    'sub_what-you-collect': '物品揭示身份',
    'room_apology-you-owe': '你欠的道歉',
    'sub_apology-you-owe': '写下你该说的话',
    'room_unfinished-story': '你未完成的故事',
    'sub_unfinished-story': '你从未完成的事',
    'room_version-others-see': '别人看到的你',
    'sub_version-others-see': '别人如何看待你',
    'room_secret-talent': '你的秘密才能',
    'sub_secret-talent': '没人知道你能做什么',
    'room_fear-you-feed': '你喂养的恐惧',
    'sub_fear-you-feed': '哪个恐惧控制着你',
    'room_guilty-pleasure': '你的愧疚快乐',
    'sub_guilty-pleasure': '你隐藏享受的东西',
    'room_rule-you-break': '你总是打破的规则',
    'sub_rule-you-break': '你的叛逆模式',
    'room_childhood-promise': '你童年的承诺',
    'sub_childhood-promise': '你向自己承诺的',
    'room_compliment-you-reject': '你拒绝的赞美',
    'sub_compliment-you-reject': '为什么你不能接受赞美',
    'room_autopilot-mode': '你的自动驾驶模式',
    'sub_autopilot-mode': '你不假思索做的事',
    'room_boundary-you-need': '你需要的界限',
    'sub_boundary-you-need': '你应该保护什么',
    'room_inner-critic': '你内心的批评者',
    'sub_inner-critic': '评判的声音',
    'room_last-sunday': '上个星期天',
    'sub_last-sunday': '你实际做了什么？',
    'room_wasted-hours': '你浪费的时间',
    'sub_wasted-hours': '时间消失在哪里',
    'room_year-that-changed': '改变你的那一年',
    'sub_year-that-changed': '转折点',
    'room_tomorrows-regret': '明天的遗憾',
    'sub_tomorrows-regret': '你会希望做了什么？',
    'room_morning-autopilot': '你的早晨自动驾驶',
    'sub_morning-autopilot': '第一小时的仪式',
    'room_season-you-dread': '你恐惧的季节',
    'sub_season-you-dread': '哪个季节最沉重？',
    'room_ten-years-ago': '十年前的今天',
    'sub_ten-years-ago': '那时你是谁？',
    'room_last-normal-day': '你最后的正常日子',
    'sub_last-normal-day': '在一切改变之前',
    'room_clock-you-ignore': '你忽略的时钟',
    'sub_clock-you-ignore': '你假装不在流逝的时间',
    'room_your-deadline': '你的截止日期',
    'sub_your-deadline': '你在推迟什么？',
    'room_the-pause': '暂停',
    'sub_the-pause': '呼吸一下',
    'room_static-room': '静态',
    'sub_static-room': '白噪音和寂静',
    'room_gap-between-thoughts': '思想之间的间隙',
    'sub_gap-between-thoughts': '找到那个空间',
    'room_sit-with-discomfort': '与不适同坐',
    'sub_sit-with-discomfort': '不要修复任何事',
    'room_boring-room': '无聊的房间',
    'sub_boring-room': '完全没有趣味',
    'room_unplug-room': '断开连接',
    'sub_unplug-room': '想象没有设备',
    'room_unplug': '断开连接',
    'sub_unplug': '想象没有设备',
    'room_last-page': '最后一页',
    'sub_last-page': '故事在这里结束',
    'room_scrolling-pattern': '你的滚动模式',
    'sub_scrolling-pattern': '你如何消费内容',
    'room_tab-you-keep-open': '你一直打开的标签页',
    'sub_tab-you-keep-open': '什么留在你的浏览器中',
    'room_digital-shadow': '你的数字影子',
    'sub_digital-shadow': '你的网络足迹',
    'room_notification-you-ignore': '你忽略的通知',
    'sub_notification-you-ignore': '你无视的东西',
    'room_typing-speed': '你的打字速度',
    'sub_typing-speed': '你表达得有多快？',
    'room_link-you-almost-clicked': '你差点点击的链接',
    'sub_link-you-almost-clicked': '犹豫模式',
    'room_bookmark-graveyard': '你的书签墓地',
    'sub_bookmark-graveyard': '保存了却从未回访',
    'room_search-you-deleted': '你删除的搜索',
    'sub_search-you-deleted': '你搜索并删除的东西',
    'room_gravity-room': '重力房间',
    'sub_gravity-room': '一切缓慢下落',
    'room_ink-blot': '墨迹',
    'sub_ink-blot': '你看到了什么？',
    'room_hallway-of-mirrors': '镜子走廊',
    'sub_hallway-of-mirrors': '无尽的倒影',
    'room_dissolving-text': '溶解的文字',
    'sub_dissolving-text': '文字在阅读时融化',
    'room_the-pendulum': '钟摆',
    'sub_the-pendulum': '催眠般的运动',
    'room_pixel-rain': '像素雨',
    'sub_pixel-rain': '数字降水',
    'room_the-threshold': '门槛',
    'sub_the-threshold': '在这里和那里之间',
    'most_loved_rooms': '最受喜爱的房间',
    'rate_room': '评价这个房间',
    'update_52_new_rooms': '52个新房间，涵盖身份、时间、沉思、元和实验类别',
    'update_room_rating': '房间评分系统 - 点赞/点踩反馈及最受喜爱房间板块',
    'update_bug_fix_reflections': '修复匿名反思的显示和存储问题',
    'update_100_rooms': '走廊中已有超过100个房间',
    // v3.22.0
    'room_the-friend-you-lost': '失去的朋友',
    'sub_the-friend-you-lost': '写给一个渐行渐远的人。',
    'room_conversation-you-replay': '反复回放的对话',
    'sub_conversation-you-replay': '从记忆中重现它。',
    'room_the-thank-you': '感谢',
    'sub_the-thank-you': '从未表达过的感激。',
    'room_people-you-forgot': '你忘记的人',
    'sub_people-you-forgot': '试着回忆被遗忘的名字。',
    'room_the-advice-you-give': '你给的建议',
    'sub_the-advice-you-give': '你给别人但自己不遵循的建议。',
    'room_what-others-dont-know': '别人不知道的',
    'sub_what-others-dont-know': '你隐藏的层面。',
    'room_the-lie-you-believe': '你相信的谎言',
    'sub_the-lie-you-believe': '一个令人安慰的不真实。',
    'room_your-shadow-self': '你的影子自我',
    'sub_your-shadow-self': '你压抑的部分。',
    'room_the-story-you-tell': '你讲述的故事',
    'sub_the-story-you-tell': '你如何叙述自己的人生。',
    'room_your-safe-place': '你的安全之地',
    'sub_your-safe-place': '描述你感到安全的地方。',
    'room_the-risk-you-didnt-take': '你没有冒的险',
    'sub_the-risk-you-didnt-take': '未曾走过的路。',
    'room_your-last-excuse': '你的最后借口',
    'sub_your-last-excuse': '你最常用的借口。',
    'room_the-habit-you-hide': '你隐藏的习惯',
    'sub_the-habit-you-hide': '秘密的日常。',
    'room_what-you-postpone': '你推迟的事',
    'sub_what-you-postpone': '总是"明天"的事情。',
    'room_the-door-you-closed': '你关上的门',
    'sub_the-door-you-closed': '你拒绝的机会。',
    'journal_title': '反思日记',
    'journal_subtitle': '你在各房间的想法',
    'journal_empty': '还没有反思。访问房间并写下你的想法。',
    'journal': '日记',
    'room_chains': '房间链',
    'chain_identity_journey': '身份之旅',
    'chain_time_reflection': '时间反思',
    'chain_truth_path': '真相之路',
    'recently_visited': '最近访问',
    'update_reflection_journal': '反思日记 - 在各房间保存你的想法',
    'update_room_chains': '房间链 - 按顺序完成房间获得额外碎片',
    'update_15_new_rooms': '3个新类别中的15个新互动房间',
    'update_recently_visited': '最近访问区域便于快速返回',
    'ai_companion': '伙伴',
    'companion_create': '创建你的伙伴',
    'companion_setup_desc': '一个与你同行的倒影。',
    'companion_name': '给伙伴起个名字',
    'companion_name_placeholder': '例如：回声、镜像、影子...',
    'companion_lens': '性格镜头',
    'companion_mood': '沟通氛围',
    'companion_fears': '你害怕什么？（可选）',
    'companion_struggles': '你在挣扎什么？（可选）',
    'create_companion': '创建',
    'companion_input': '说些什么...',
    'companion_greeting': '你的伙伴在这里。说些什么。',
    'companion_starter_1': '今天我在想什么？',
    'companion_starter_2': '我感到困住了。',
    'companion_starter_3': '告诉我一些我在逃避的事。',
    'fears_placeholder': '例如：失败、被拒绝、被遗忘...',
    'struggles_placeholder': '例如：过度思考、拖延、孤独...',
    'back_step': '返回',
    'lens_reflective': '反思型',
    'lens_reflective_desc': '深层问题，模式识别',
    'lens_supportive': '支持型',
    'lens_supportive_desc': '温暖、鼓励、认可',
    'lens_challenging': '挑战型',
    'lens_challenging_desc': '直接、发人深省',
    'lens_curious': '好奇型',
    'lens_curious_desc': '意想不到的问题，惊奇',
    'lens_shadow': '阴影型',
    'lens_shadow_desc': '隐藏的一面，诚实的不适',
    'companion_mood_calm': '平静',
    'companion_mood_calm_desc': '沉稳而平和',
    'companion_mood_intense': '激烈',
    'companion_mood_intense_desc': '紧迫而深刻',
    'companion_mood_playful': '俏皮',
    'companion_mood_playful_desc': '机智而轻快',
    'companion_mood_melancholic': '忧郁',
    'companion_mood_melancholic_desc': '美丽的悲伤',
    'update_ai_companion': 'AI伙伴 - 记住你旅程的个人AI',
    // v3.23.0
    'mood_history': '心情历史',
    'top_rooms': '你最常去的房间',
    'visits_count': '次访问',
    'set_intention': '设定今天的意图',
    'todays_intention': '今日意图',
    'intention_placeholder': '我要...',
    'set': '设定',
    'update_mood_history': '心情历史 - 追踪你的心情变化',
    'update_daily_intention': '每日意图 - 为每天设定一个焦点',
    'update_top_rooms': '热门房间 - 快速访问你最常去的房间',
    'update_10_new_rooms_v323': '10个新房间：情感、自我反思与连接',
    'room_the-emotion-you-hide': '你隐藏的情感',
    'sub_the-emotion-you-hide': '说出它的名字。就一次。',
    'emotion_hide_placeholder': '我从不展现的情感是...',
    'quote_emotion_hide': '我们对他人隐藏的，往往也是对自己隐藏的。',
    'room_your-comfort-lie': '你的舒适谎言',
    'sub_your-comfort-lie': '让你感到舒适的小谎言。',
    'comfort_lie_placeholder': '我对自己说的谎是...',
    'admit_it': '承认吧',
    'quote_comfort_lie': '舒适的谎言是我们用柔软布料建造的墙。',
    'room_the-silence-between': '思绪之间的沉默',
    'sub_the-silence-between': '坐在两个念头之间的缝隙里。',
    'silence_instruction': '闭上眼睛。找到两个念头之间的缝隙。然后按下按钮。',
    'silence_sitting': '在沉默中坐着...',
    'silence_found': '我找到了',
    'silence_result': '你坐了',
    'seconds': '秒',
    'quote_silence_between': '每个念头之间都有一个无限的空间。你刚刚造访了它。',
    'room_what-you-carry': '你承载的',
    'sub_what-you-carry': '说出那无形的重量。',
    'carry_placeholder': '我承载的重量是...',
    'set_it_down': '放下它',
    'carry_released': '那一刻，它轻了。',
    'quote_carry': '你无法放下你拒绝说出名字的东西。',
    'room_the-version-you-lost': '你失去的版本',
    'sub_the-version-you-lost': '在生活发生之前你是谁。',
    'version_lost_placeholder': '在一切改变之前，我是...',
    'remember': '记住',
    'quote_version_lost': '你没有失去他们。他们成为了现在的你的一部分。',
    'room_letter-to-past-self': '给过去自己的信',
    'sub_letter-to-past-self': '写给曾经的你。',
    'letter_past_placeholder': '亲爱的年轻的我,...',
    'send_letter': '发送',
    'letter_sent': '信已寄出。在某处，他们收到了。',
    'quote_letter_past': '你写给过去自己的每一句安慰，其实都是给现在的你的。',
    'room_the-moment-you-knew': '你知道的那一刻',
    'sub_the-moment-you-knew': '一切改变的瞬间。',
    'moment_knew_placeholder': '我知道的那一刻是...',
    'mark_it': '标记它',
    'quote_moment_knew': '有些时刻将你的人生分为之前和之后。你刚刚说出了其中一个。',
    'room_the-words-you-rehearse': '你排练的话',
    'sub_the-words-you-rehearse': '你练习要说但从未说出口的话。',
    'words_rehearse_placeholder': '我一直排练的话是...',
    'say_them': '说出来',
    'words_spoken': '你终于说出来了。即使只是在这里。',
    'quote_words_rehearse': '排练的话是笼子。说出的话是鸟。',
    'room_someone-thinks-of-you': '有人在想你',
    'sub_someone-thinks-of-you': '此刻，在某处，有人记着你。',
    'someone_line_1': '此刻，在某处，有人记着你。',
    'someone_line_2': '不是因为你做了什么。',
    'someone_line_3': '而是因为你让他们感受到的。',
    'quote_someone_thinks': '你存在于你甚至不知道的记忆中。',
    'room_the-question-you-fear': '你害怕的问题',
    'sub_the-question-you-fear': '你希望没人问的那个问题。',
    'question_fear_placeholder': '我最害怕的问题是...',
    'quote_question_fear': '你最害怕的问题通常有着最重要的答案。',
    // v3.24.0
    'behavior_insight': '网站注意到',
    'insight_pattern': '你被{category}吸引。你在{time}探索最多。',
    'cat_identity': '身份',
    'cat_truth': '真相',
    'cat_time': '时间',
    'cat_mortality': '死亡',
    'cat_stillness': '寂静',
    'cat_darkness': '黑暗',
    'cat_patterns': '模式',
    'cat_fear': '恐惧',
    'cat_play': '游戏',
    'cat_experiments': '实验',
    'cat_ritual': '仪式',
    'cat_secrets': '秘密',
    'time_late_night': '深夜',
    'time_morning': '上午',
    'time_afternoon': '下午',
    'time_evening': '晚上',
    
    'anonymous_visitor': '一位访客',
    'update_behavior_insight': '行为洞察 - 网站向你反映你的模式',
    'update_anonymous_reflections_feed': '匿名反思 - 看看其他人的感受',
    'update_daily_prompt': '每日提示 - 一个伴随你的思考',
    'room_recurring-dream-journal': '反复梦境日记', 'sub_recurring-dream-journal': '不断重复的梦。', 'dream_journal_placeholder': '我一直做的梦...', 'record_dream': '记录', 'dream_journal_reflection': '梦说出了清醒时不愿说的话。', 'quote_dream_journal': '反复的梦是与自己未完成的对话。',
    'room_lucid-moment': '清醒时刻', 'sub_lucid-moment': '在梦中变得清醒。', 'lucid_line_1': '你正在阅读这些文字。', 'lucid_line_2': '但你醒着吗？', 'lucid_line_3': '你怎么知道区别？', 'become_aware': '我清醒了', 'lucid_aware': '这个清醒的瞬间是唯一真实的东西。', 'quote_lucid_moment': '当你质疑现实的那一刻，才是你真正看到它的时刻。',
    'room_nightmare-you-remember': '你记得的噩梦', 'sub_nightmare-you-remember': '仍然困扰你的那个。', 'nightmare_placeholder': '我无法忘记的噩梦...', 'face_it': '面对它', 'nightmare_reflection': '写下来，你就夺走了它的一些力量。', 'quote_nightmare': '噩梦是坚持要被看到的恐惧。',
    'room_dream-you-wish-was-real': '你希望是真实的梦', 'sub_dream-you-wish-was-real': '醒来吧。那不是真的。', 'dream_wish_placeholder': '我梦到...', 'hold_onto_it': '抓住它', 'quote_dream_wish': '我们悲伤失去的梦揭示了我们真正想要的生活。',
    'room_the-dream-visitor': '梦中访客', 'sub_the-dream-visitor': '有人不断出现在你的梦中。', 'dream_visitor_line_1': '有人在梦中拜访你。', 'dream_visitor_line_2': '你没有邀请他们。', 'dream_visitor_line_3': '但他们不断回来。', 'dream_visitor_line_4': '也许他们在试图告诉你什么。', 'quote_dream_visitor': '出现在我们梦中的人是潜意识的信使。',
    'room_the-blank-canvas': '空白画布', 'sub_the-blank-canvas': '从无创造有。', 'blank_canvas_placeholder': '在这张空白画布上，我创造...', 'create': '创造', 'quote_blank_canvas': '每一个创造都始于面对空白的勇气。',
    'room_song-that-is-you': '代表你的歌', 'sub_song-that-is-you': '如果你是一首歌，是哪首？', 'song_placeholder': '代表我的歌...', 'play_it': '播放', 'quote_song': '音乐是感情的声音。',
    'room_color-of-today': '今天的颜色', 'sub_color-of-today': '选择你当前状态的颜色。', 'color_chosen': '这就是你今天的感受。用颜色表达。', 'quote_color_today': '颜色不需要语言。感受也不需要。',
    'room_the-unwritten-poem': '未写的诗', 'sub_the-unwritten-poem': '写出你一直藏在心里的。', 'poem_placeholder': '一直在等待的文字...', 'publish_it': '发表', 'quote_unwritten_poem': '诗一直都在那里。你只是终于让它出来了。',
    'room_your-inner-voice': '你的内心声音', 'sub_your-inner-voice': '它到底在说什么？', 'inner_voice_placeholder': '我的内心声音说...', 'listen': '倾听', 'inner_voice_reflection': '现在你听到了，你会怎么做？', 'quote_inner_voice': '你的内心声音一直在说话。你只是开始倾听了。',
    'room_fear-you-named': '你命名的恐惧', 'sub_fear-you-named': '命名它，它就失去力量。', 'fear_named_placeholder': '我终于命名的恐惧...', 'name_it': '命名它', 'fear_named_reflection': '被命名的恐惧缩小。未命名的恐惧增长。', 'quote_fear_named': '你能命名的，就能面对。你能面对的，就能克服。',
    'room_the-leap': '跳跃', 'sub_the-leap': '你会跳向什么？', 'leap_placeholder': '如果我能跳跃...', 'leap': '跳', 'quote_leap': '网在你跳之后出现，而不是之前。',
    'room_courage-inventory': '勇气清单', 'sub_courage-inventory': '列出你做过的每件勇敢的事。', 'courage_placeholder': '我做过的勇敢的事...', 'courage_count': '你列出了{count}个勇敢行为。', 'quote_courage': '你比你记忆中更勇敢。',
    'room_what-scares-you-most': '最让你害怕的', 'sub_what-scares-you-most': '最深的恐惧。', 'scares_placeholder': '最让我害怕的是...', 'quote_scares': '你最深的恐惧通常是你最伟大的老师。',
    'room_facing-the-dark': '面对黑暗', 'sub_facing-the-dark': '坐在完全的黑暗中。', 'darkness_counting': '在黑暗中{seconds}秒...', 'return_to_light': '回到光明', 'darkness_survived': '你在黑暗中坐了{seconds}秒。', 'quote_facing_dark': '在黑暗中，我们发现自己内心承载的东西。',
    'room_letter-to-future-self': '给未来自己的信', 'sub_letter-to-future-self': '写给你将成为的人。', 'letter_future_placeholder': '亲爱的未来的我...', 'seal_letter': '密封', 'letter_future_sealed': '信已密封。总有一天你会读到它。', 'quote_letter_future': '每一封写给未来的信实际上都是对现在的承诺。',
    'room_the-world-you-want': '你想要的世界', 'sub_the-world-you-want': '描述你希望存在的世界。', 'world_placeholder': '我想要的世界...', 'build_it': '建造它', 'quote_world_want': '你想象的世界是你创造的世界的第一步。',
    'room_your-next-chapter': '你的下一章', 'sub_your-next-chapter': '你的故事接下来会发生什么？', 'next_chapter_placeholder': '在我的下一章...', 'turn_the_page': '翻页', 'quote_next_chapter': '你既是作者也是主角。',
    'room_the-wish': '愿望', 'sub_the-wish': '一个愿望。没有规则。', 'wish_placeholder': '我希望...', 'make_wish': '许愿', 'quote_wish': '你许的愿揭示了你最重视什么。',
    'room_if-you-had-one-year': '如果你有一年', 'sub_if-you-had-one-year': '你会用365天做什么？', 'one_year_placeholder': '如果我还有一年...', 'begin_now': '现在开始', 'one_year_reflection': '那你为什么不去做呢？', 'quote_one_year': '像只有一年一样生活。因为你永远不知道。',
    'update_v4_languages': '4种新语言：印地语、韩语、日语、土耳其语', 'update_v4_20_rooms': '20个新房间：梦境、创意、恐惧和未来', 'update_v4_dreams_category': '梦境与潜意识房间类别', 'update_v4_creativity_category': '创意与表达房间类别', 'update_v4_fear_category': '恐惧与勇气房间类别', 'update_v4_future_category': '未来与希望房间类别',
    'room_playlists': '房间播放列表', 'personal_dashboard': '仪表板', 'dashboard_subtitle': '一览你的旅程', 'visit_pattern': '访问模式（14天）', 'dashboard_top_rooms': '最常访问的房间', 'category_breakdown': '房间类别', 'mood_trend': '情绪趋势', 'fragment_breakdown': '碎片分布', 'no_data_yet': '暂无数据', 'current_streak': '连续天数',
    'playlist_morning': '晨间冥想', 'playlist_morning_desc': '以清晰和当下开始你的一天', 'playlist_identity': '身份深潜', 'playlist_identity_desc': '标签之下的你是谁？', 'playlist_creative': '创意火花', 'playlist_creative_desc': '释放你的创造性表达', 'playlist_courage': '勇气之路', 'playlist_courage_desc': '一间一间面对你的恐惧', 'playlist_dreams': '梦境日记', 'playlist_dreams_desc': '探索你潜意识的风景', 'playlist_future': '未来愿景', 'playlist_future_desc': '给未来的你的信和愿望', 'playlist_truth': '真相对峙', 'playlist_truth_desc': '面对令人不安的现实', 'playlist_night': '夜间旅程', 'playlist_night_desc': '拥抱寂静、虚空和无',
    // v4.2.0
    'room_focus-timer': '专注计时器', 'sub_focus-timer': '静坐。反思。',
    'room_gratitude-jar': '感恩罐', 'sub_gratitude-jar': '你感恩的一件事',
    'room_mood-compass': '情绪指南针', 'sub_mood-compass': '你现在在哪里？',
    'room_memory-fade': '记忆消逝', 'sub_memory-fade': '看着记忆消散',
    'room_sound-of-your-day': '你一天的声音', 'sub_sound-of-your-day': '今天听起来像什么？',
    'room_the-apology-room': '道歉室', 'sub_the-apology-room': '写下你从未说过的话',
    'room_your-unread-messages': '你的未读消息', 'sub_your-unread-messages': '你从未打开的消息',
    'room_the-weight-room': '重量室', 'sub_the-weight-room': '说出压在你身上的东西',
    'room_parallel-lives': '平行人生', 'sub_parallel-lives': '在另一个人生中，你是...',
    'room_the-quiet-score': '安静分数', 'sub_the-quiet-score': '你能静止多久？',
    'focus_choose': '你想静坐多长时间？', 'focus_sit': '坐下。呼吸。让思绪流过。', 'focus_done': '沉默已经说话。', 'quote_focus_timer': '在寂静中，你听到噪音淹没的声音。', 'minutes': '分钟',
    'gratitude_prompt': '写下今天你感恩的一件事。', 'gratitude_placeholder': '我感恩...', 'gratitude_add': '加入罐中', 'gratitude_added': '你的感恩飘入罐中。', 'gratitude_count': '感恩时刻', 'quote_gratitude_jar': '感恩不是拥有更多。而是注意到已经在这里的。',
    'mood_compass_prompt': '点击你感觉自己所在的位置。', 'mood_high_energy': '高能量', 'mood_low_energy': '低能量', 'mood_positive': '积极', 'mood_negative': '消极', 'quote_mood_compass': '知道你在哪里是知道去哪里的第一步。',
    'memory_fade_prompt': '输入一段记忆。看它消散。', 'memory_fade_placeholder': '一段你想释放的记忆...', 'memory_fade_start': '让它消逝', 'memory_fade_done': '它已经消失了。但它是真实的。', 'quote_memory_fade': '无论我们是否注视，记忆都会消散。',
    'sound_day_prompt': '选择3-5个代表你一天的声音。', 'sound_rain': '雨', 'sound_traffic': '车流', 'sound_birds': '鸟', 'sound_keyboard': '键盘', 'sound_silence': '寂静', 'sound_wind': '风', 'sound_ocean': '海洋', 'sound_crowd': '人群', 'sound_music': '音乐', 'sound_day_confirm': '这是我的一天', 'sound_day_result': '你一天的配乐。', 'quote_sound_day': '每一天都有声音。大多数人只是忘记了聆听。',
    'apology_prompt': '写一个你从未送出的道歉。', 'apology_placeholder': '我很抱歉...', 'apology_release': '释放它', 'apology_done': '它压缩成了虚无。但你说出来了。', 'quote_apology': '有些道歉不是为了他们。而是为了你自己。',
    'unread_from_past': '来自：过去的你', 'unread_from_future': '来自：未来的你', 'unread_from_critic': '来自：你的内心批评者', 'unread_msg_past': '你记得你曾承诺一切会不同吗？', 'unread_msg_future': '我希望你现在对自己比以前更温柔。', 'unread_msg_critic': '你知道你本可以做得更多。但你会吗？', 'unread_count': '未读', 'unread_reply_placeholder': '你的回复...', 'unread_reply': '发送', 'unread_sent': '消息发送到虚空中。', 'quote_unread_messages': '我们回避的消息往往是我们最需要的。',
    'weight_prompt': '说出压在你身上的东西。', 'weight_placeholder': '压在我身上的东西...', 'weight_release_start': '开始释放', 'weight_release_prompt': '点击每个重量来释放它。', 'weight_done': '重量已经解除。暂时。', 'quote_weight_room': '你无法放下你拒绝命名的东西。',
    'parallel_intro': '在另一个人生中，你是...', 'parallel_life_1': '在另一个人生中，你是偏远岛屿上的灯塔守护者。', 'parallel_life_2': '在另一个人生中，你从未离开家乡。', 'parallel_life_3': '在另一个人生中，你在说不的时候说了是。', 'parallel_life_4': '在另一个人生中，你是完全不同的人。', 'parallel_life_5': '在另一个人生中，你正是现在的你。', 'parallel_life_6': '在另一个人生中，你选择了更难的路。', 'parallel_life_7': '在另一个人生中，你仍在寻找这个房间。', 'quote_parallel_lives': '每个选择都创造了一个你选择不同的宇宙。',
    'quiet_instruction': '不要互动。只是静静地待着。', 'quiet_reveal_1': '你在这里。', 'quiet_reveal_2': '噪音正在消退。', 'quiet_reveal_3': '有什么东西从寂静中浮现。', 'quiet_reveal_4': '你比你想的更有耐心。', 'quiet_reveal_5': '寂静知道你的名字。', 'quiet_score_label': '你的安静分数', 'quiet_impressive': '非凡的寂静。', 'quiet_good': '你找到了一些安静。', 'quiet_try_again': '行动的冲动很强。', 'quote_quiet_score': '寂静不是没有运动。而是有意识的存在。',
    'update_v42_10_rooms': '10个新互动房间：专注计时器、感恩罐、情绪指南针等', 'update_v42_focus_timer': '专注计时器 — 在渐暗的计时器中静坐', 'update_v42_gratitude_jar': '感恩罐 — 随时间收集感恩时刻', 'update_v42_mood_compass': '情绪指南针 — 在能量和情感轴上定位自己', 'update_v42_weight_room': '重量室 — 命名并释放压在你身上的东西',
    'update_v43_full_localization': '完整本地化 — 所有房间现已全面翻译', 'update_v43_40_rooms_translated': '40+房间已翻译 — 每个互动房间支持所有语言', 'update_v43_10_languages': '支持10种语言 — 英语、阿拉伯语、西班牙语、葡萄牙语、中文、法语、印地语、韩语、日语、土耳其语', 'update_v43_nav_consistency': '统一导航 — 所有房间的退出文本统一',
    'mirror_lies_title': '镜子在撒谎', 'mirror_lies_msg1': '看看自己。', 'mirror_lies_msg2': '那真的是你吗？', 'mirror_lies_msg3': '镜子展示你想看到的。', 'mirror_lies_msg4': '点击揭示真相。', 'mirror_lies_msg5': '每道裂缝都揭示更多。', 'mirror_lies_msg6': '倒影背后什么也没有。', 'mirror_lies_quote': '镜子从未展示过你。只是你所相信的。',
    'winter_title': '最长的夜', 'winter_date': '12月21日', 'winter_msg1': '最长的夜来了。', 'winter_msg2': '在黑暗中，我们找到我们所承载的。', 'winter_msg3': '你要释放什么？你要留下什么？', 'winter_releasing': '我正在释放：', 'winter_keeping': '我正在留下：', 'winter_release_placeholder': '不再服务于你的...', 'winter_keep_placeholder': '给你力量的...', 'winter_enter_light': '走入光明', 'winter_released': '已释放：', 'winter_kept': '已留下：', 'winter_light_returns': '从这个夜晚起，光明回归。', 'winter_quote': '最长的夜教会我们，即使在最深的黑暗中，光明已经在回归。',
    'halloween_title': '面纱之室', 'halloween_date': '10月31日', 'halloween_msg1': '今夜面纱很薄。', 'halloween_msg2': '什么潜伏在你的阴影中？', 'halloween_msg3': '说出你携带的恐惧...', 'halloween_msg4': '给它形态，然后释放它。', 'halloween_placeholder': '输入你的恐惧...', 'halloween_release': '释放它', 'halloween_dissolves': '你的恐惧溶入夜色...', 'halloween_quote': '我们恐惧的东西在被命名时就失去力量。让它们走。',
    'awake_title': '让你无法入睡的', 'awake_locked': '这个房间仅在晚上10点后可用。', 'awake_return': '等夜晚降临时再来。', 'awake_press_enter': '按回车键', 'awake_named': '现在你已经命名了它。', 'awake_quote': '黑暗知道你在白天回避什么。',
    'voice_meaning_title': '没有意义的声音', 'voice_meaning_prompt': '哼。呼吸。发出任何声音。没有文字。', 'voice_meaning_done': '这是你的存在。仅此而已。', 'voice_meaning_hold': '按住', 'voice_meaning_recording': '录制中...', 'voice_meaning_no_playback': '不能回放。声音只存在于那一刻。', 'voice_meaning_quote': '你存在。这是唯一的内容。',
    'watching_msg1': '你在这里是独自一人。', 'watching_msg2': '完全独自一人。', 'watching_msg3': '或者是吗？', 'watching_msg4': '他们一直都在这里。', 'watching_msg5': '他们一直在观察。',
    'shortcut_title': '你总是走的捷径', 'shortcut_instruction': '选择你认识的那些。', 'shortcut_1': '说"我很好"但其实不好', 'shortcut_2': '看手机而不是在场', 'shortcut_3': '保持沉默以避免冲突', 'shortcut_4': '买而不是做', 'shortcut_5': '等待"合适的时机"', 'shortcut_6': '责怪环境而不是行动', 'shortcut_7': '选择舒适而不是成长', 'shortcut_8': '刷手机而不是睡觉', 'shortcut_9': '说是当你想说不', 'shortcut_10': '计划而不是行动', 'shortcut_pattern': '你的模式', 'shortcut_selected': '你选择了：', 'shortcut_show_pattern': '显示我的模式', 'shortcut_quote': '捷径变成了道路。', 'shortcut_pattern_own': '你的模式是你自己的。', 'pattern_avoidance': '你回避不舒服的事。', 'pattern_distraction': '你逃入分心中。', 'pattern_convenience': '你用轻松换取努力。', 'pattern_delay': '你等待永远不会来的条件。', 'pattern_comfort': '你保护自己免受不适。', 'pattern_pleasing': '你消失以让别人舒适。',
    'shapes_q1': '你多久感到平静一次？', 'shapes_q2': '你有多信任自己？', 'shapes_q3': '你感觉与他人有多紧密？', 'shapes_q4': '你的时间有多少真正属于你？', 'shapes_q5': '未来感觉有多重？', 'shapes_result_title': '你的生活化为形状', 'shapes_no_labels': '没有标签。没有解读。', 'shapes_yours': '你看到的是由你来理解的。', 'shapes_quote': '形状是无言的意义。', 'shapes_scale': '1 = 完全不，5 = 完全',
    'read_title': '大声朗读这个', 'read_stmt1': '我在逃避某些事。', 'read_stmt2': '我害怕。', 'read_stmt3': '我不知道我在做什么。', 'read_stmt4': '我需要帮助。', 'read_stmt5': '我足够了。', 'read_stmt6': '我一直在假装。', 'read_stmt7': '我很累。', 'read_stmt8': '我想要更多。', 'read_i_read': '我大声读了', 'read_really': '你真的说了吗？', 'read_skipped': '我跳过了', 'read_result': '你大声说出了真相。', 'read_quote': '说出真相让它变得真实。',
    'sentence_title': '你回避的句子', 'sentence_sub': '完成这个句子。你必须完成才能离开。', 'sentence_prompt': '如果我停止...我会更快乐', 'sentence_cant_leave': '不写点什么你无法离开。', 'sentence_knew': '你已经知道了。', 'sentence_quote': '第一步是说出来。',
    'say_once_title': '只说一次', 'say_once_prompt': '录制一句话。不能回放。不能重录。', 'say_once_done': '已经说了。没有回放。没有重来。', 'say_once_stop': '停止', 'say_once_record': '录制', 'say_once_quote': '有些话只该说一次。',
    'newyear_title': '给未来自己的信', 'newyear_threshold': '门槛', 'newyear_placeholder': '亲爱的未来的我...', 'newyear_seal': '封信', 'newyear_waiting': '这封信会等着你。', 'newyear_quote': '每个结束都是一扇门。带着意图走过去。',
    'avoid_title': '你回避的房间', 'avoid_sub': '你跳过的东西说明了什么。', 'avoid_not_entered': '你没有进入：', 'avoid_visited_all': '你已经访问了每个免费房间。', 'avoid_not_pattern': '也许回避不是你的模式。', 'avoid_quote': '回避也是一种选择。',
    'who_q1': '如果不用担心钱你会做什么？', 'who_q2': '你会在派对上怎样介绍自己？', 'who_q3': '什么让你感到活着？', 'who_q4': '你的朋友会说你擅长什么？', 'who_q5': '你小时候喜欢做什么？', 'who_result_title': '没有头衔的你', 'who_done': '这就是头衔消失后的你。', 'who_quote': '身份不是你做什么。而是你停下后剩下的。', 'who_placeholder': '诚实地说...', 'who_see_myself': '看看自己', 'question': '问题',
    'lastconv_title': '最后的对话', 'lastconv_sub': '写下你永远不会发送的。', 'lastconv_to': '致：', 'lastconv_message': '消息：', 'lastconv_to_placeholder': '需要听到这些的人...', 'lastconv_msg_placeholder': '你一直想说的话...', 'lastconv_not_saved': '这条消息不会被保存或分享。', 'lastconv_seal': '封信', 'lastconv_wrote_to': '你写给了：', 'lastconv_never_sent': '这条消息永远不会被发送。', 'lastconv_point': '但写下来就是目的。', 'lastconv_quote': '有些话需要存在，即使永远不会被听到。',
    'priorities_title': '你的真实优先级', 'priorities_sub': '你每周花多少小时在每件事上？', 'priorities_result': '你的真实优先级', 'priorities_most_time': '你把大部分时间花在：', 'priorities_truth': '时间揭示真相。话语可以撒谎，时间表不会。', 'priorities_quote': '你就是你反复做的事。', 'priorities_show': '展示我的真相', 'priority_work': '工作', 'priority_family': '家庭', 'priority_friends': '朋友', 'priority_health': '健康', 'priority_learning': '学习', 'priority_entertainment': '娱乐', 'priority_social': '社交媒体', 'priority_sleep': '睡眠',
    'voice_intro': '一个声音在黑暗中说话...', 'voice_silence': '（沉默）', 'voice_quote': '有些声音永远找不到回家的路。',
    'knows_tell': '告诉我你的名字。', 'knows_placeholder': '你的名字...', 'knows_warning': '（你不该来这里的）', 'knows_quote': '名字有力量。你的名字早已为人所知。',
    'door_msg1': '门口有什么东西。', 'door_stopped': '（敲门声停了）', 'door_quote': '有些门注定要保持关闭。',
    'eyes_msg1': '一双眼睛在注视。', 'eyes_unsee': '你永远无法忘记它们。', 'eyes_quote': '有些东西，一旦被注意到，就无法被忽视。',
    'moon_locked': '这个房间只在满月时开放。', 'moon_not_full': '今晚不是满月。等月亮圆了再来。', 'moon_quote': '有些真相只在循环中显现。',
    'at317_title': '凌晨3:17的你', 'at317_locked': '这个房间在凌晨2点到5点之间开放。', 'at317_current': '当前时间：', 'at317_quote': '夜晚知道你的名字。',
    'mirrors_you_are_here': '你在这里。',
    'listen_title': '倾听自己', 'listen_recording': '录制中...', 'listen_start': '开始', 'listen_play': '播放', 'listen_instruction': '录制10秒。说什么都行。', 'listen_wait': '等等。然后听。', 'listen_done': '那是你的声音。慢速播放。奇怪。', 'listen_speed': '以0.5倍速播放', 'listen_quote': '你的声音听起来不同，因为你终于听到了它。',
    'export_title': '导出你的旅程', 'export_subtitle': '带走这个地方的一部分', 'export_tab_export': '导出', 'export_tab_import': '导入', 'export_your_journey': '你的旅程', 'export_since': '自从', 'export_time_spent': '花费时间', 'export_rooms_visited': '访问的房间', 'export_rooms_completed': '完成的房间', 'export_fragments': '碎片', 'export_achievements_unlocked': '已解锁成就', 'export_truths_recorded': '记录的真相', 'export_text_file': '文本文件', 'export_json_file': 'JSON文件', 'export_copied': '已复制！', 'export_copy_clipboard': '复制到剪贴板', 'export_data_stays': '你的数据留在你身边。我们不保留。', 'export_import_desc': '导入之前导出的旅程', 'export_json_only': '仅JSON格式', 'export_select_file': '选择文件', 'export_merge_note': '将与你当前的旅程数据合并。', 'export_done': '已导出。', 'export_done_desc': '你去过哪里的记录。你面对过什么。', 'export_quote': '有些旅程值得记录。不是为了别人。为了你自己。', 'export_import_success': '旅程导入成功', 'export_import_error': '无效的旅程文件',
    'waitroom_title': '等候室', 'waitroom_subtitle': '有些门只为群体打开。', 'waitroom_open': '门开了。', 'waitroom_waiting': '等待中', 'waitroom_time_waited': '已等待时间', 'waitroom_users_needed': '需要的用户', 'waitroom_someone_joined': '有人加入了等待。', 'waitroom_someone_left': '有人离开了。', 'waitroom_room_opens': '房间打开了。', 'waitroom_patience': '耐心和存在打开了力量无法打开的东西。', 'waitroom_souls_waited': '{count}个灵魂一起等待。', 'waitroom_seconds_stillness': '{count}秒的集体静默。', 'waitroom_quote': '有些事不能急。有些门不能独自打开。',
    'capsule_title': '时间胶囊', 'capsule_subtitle': '给未来自己的信', 'capsule_what_tell': '你需要告诉自己什么？', 'capsule_placeholder': '亲爱的未来的我...', 'capsule_when_arrive': '什么时候到达？', 'capsule_30_days': '30天', 'capsule_90_days': '90天', 'capsule_1_year': '1年', 'capsule_seal': '封存胶囊', 'capsule_waiting': '个胶囊等待中', 'capsule_sealed': '已封存。', 'capsule_will_wait': '你的信息会等你。', 'capsule_return_in': '{days}天后回来打开。', 'capsule_quote_sealed': '时间改变一切。包括你。', 'capsule_arrived': '一条信息到了。', 'capsule_from_past': '来自{days}天前的你。', 'capsule_open': '打开胶囊', 'capsule_you_wrote': '你写给自己的：', 'capsule_remember': '你还记得写这个吗？', 'capsule_quote_opened': '写这个的人已经不在了。你还在。',
    'contradiction_title': '矛盾之室', 'contradiction_subtitle': '你的真相在这里碰撞', 'contradiction_not_enough': '数据还不够。', 'contradiction_answer_more': '在更多房间回答问题。', 'contradiction_answers_recorded': '{count}/5个答案已记录', 'contradiction_quote_early': '矛盾需要复杂性。先建立你的复杂性。', 'contradiction_no_found': '没有发现矛盾。', 'contradiction_consistent': '要么你非常一致...', 'contradiction_careful': '...要么你非常小心。', 'contradiction_quote_none': '一致性要么是智慧，要么是表演。', 'contradiction_found': '我们发现了{count}个矛盾', 'contradiction_you_said': '你说过：', 'contradiction_but_also': '但你也说过：', 'contradiction_see_another': '看另一个 →', 'contradiction_not_weakness': '矛盾不是弱点。它是深度的证明。', 'contradiction_quote': '我们包含众多。其中一些意见不合。',
    'entropy_living': '存活', 'entropy_dead': '死亡', 'entropy_plant_seed': '种下种子', 'entropy_instructions': '点击空位种植。点击植物浇水。死亡植物可以移除。', 'entropy_reflect': '反思你的花园', 'entropy_lives': '你的花园活着。', 'entropy_thrive': '{living}株植物因你的回归而茁壮。', 'entropy_absence': '{dead}提醒你缺席。',
    'palace_title': '记忆宫殿', 'palace_subtitle': '建造一个心理空间。放置物品来记忆。之后漫步其中。', 'palace_room_of': '房间{current}/{total}', 'palace_click_place': '点击放置', 'palace_what_represent': '这代表什么？', 'palace_cancel': '取消', 'palace_place_object': '放置物品', 'palace_objects_count': '宫殿中有{count}个物品', 'palace_take_tour': '开始游览', 'palace_empty_room': '这个房间是空的', 'palace_previous': '上一个', 'palace_next_room': '下一个房间', 'palace_complete_tour': '完成游览', 'palace_remembers': '你的宫殿记得。', 'palace_geography': '你构建的意义地理。', 'palace_quote': '心灵是宫殿。你放在里面的东西决定你成为谁。',
    'echoes_subtitle': '其他旅者的碎片', 'echoes_from': '来自', 'echoes_ago': '前', 'echoes_arriving': '更多回声正在到来...', 'echoes_reveal': '这些不是他们的答案。它们是你的。', 'echoes_reveal2': '（每个回声曾经都是你自己的倒影）', 'echoes_quote': '我们以为自己独特。我们只是彼此的回声。',
    'revisit_subtitle': '你如何改变了', 'revisit_not_enough': '你还没有回答足够的问题。', 'revisit_explore': '探索更多房间。留下你的痕迹。以后再回来。', 'revisit_quote1': '改变需要历史。历史需要时间。', 'revisit_none_changed': '但还没有任何改变。重新访问房间。再次回答。', 'revisit_show_evolved': '我们会展示你如何进化。', 'revisit_quote2': '要看到改变，你必须先改变。', 'revisit_then': '那时', 'revisit_now': '现在', 'revisit_one_shift': '一个转变。微妙但真实。', 'revisit_each_change': '每一次改变都是一次小小的死亡。和一次小小的诞生。', 'revisit_quote3': '你不是曾经的你。你还不是将要成为的你。',
  },

  fr: {
    // Index
    'title': 'LES CHAMBRES HUMAINES',
    'tagline': 'Une collection de chambres qui reflètent qui vous êtes quand personne ne regarde.',
    'enter': 'Entrer',
    'footer': 'Pas de thérapie. Pas de social. Pas de productivité.',
    
    // Hallway
    'back': '← Retour',
    'hallway': 'Le Couloir',
    'challenges': 'Défis',
    'random': 'Aléatoire',
    'open_rooms': 'Chambres Ouvertes',
    'dark_rooms': 'Les Chambres Sombres',
    'enter_risk': 'entrez à vos risques',
    'premium_rooms': 'Chambres Premium',
    'unlock_to_access': 'Débloquer pour accéder',
    'daily_rooms': 'Chambres Quotidiennes',
    'refreshes_daily': 'se rafraîchit chaque jour',
    'time_locked': 'Chambres Verrouillées par le Temps',
    'seasonal_rooms': 'Chambres Saisonnières',
    'available_now': 'Disponible maintenant',
    'fragments': 'Fragments',
    'boss_unlocked': 'Salle du Boss Débloquée',
    'collect_unlock': 'Collectez pour débloquer la Salle du Boss',
    'game_rooms': 'Salles de Jeux',
    'experimental_rooms': 'Salles Expérimentales',
    'deep_rooms': 'Salles Profondes',
    'meta_rooms': 'Salles Méta',
    'search_rooms': 'Rechercher des salles...',
    'no_results': 'Aucune salle ne correspond',
    'clear_filters': 'Effacer les filtres',
    'explore': 'Explorer',
    'all_categories': 'Toutes',
    
    // About
    'what_is_this': "Qu'est-ce que cet endroit?",
    'not_productivity': "Les Chambres Humaines n'est pas un outil de productivité.",
    'not_social': "Ce n'est pas un réseau social.",
    'not_therapy': "Ce n'est pas une thérapie.",
    'pause_experiences': "C'est une collection de petites expériences conçues pour vous faire marquer une pause.",
    'each_room': 'Chaque chambre prend de 30 secondes à 2 minutes.',
    'make_think': 'Certaines chambres vous feront réfléchir.',
    'make_uncomfortable': 'Certaines vous mettront mal à l\'aise.',
    'make_calm': 'Certaines vous calmeront.',
    'make_aware': 'Toutes vous rendront conscient.',
    'no_notifications': "Il n'y a pas de notifications ici.",
    'no_points': 'Pas de points à collecter.',
    'no_validation': 'Pas de validation sociale.',
    'just_truth': 'Juste vous, une chambre, et la vérité.',
    'enter_rooms': 'Entrer dans les chambres',
    
    // Common
    'loading': 'Chargement...',
    'error': 'Erreur',
    'continue': 'Continuer',
    'submit': 'Soumettre',
    'cancel': 'Annuler',
    'close': 'Fermer',
    'next': 'Suivant',
    'previous': 'Précédent',
    'start': 'Commencer',
    'stop': 'Arrêter',
    'yes': 'Oui',
    'no': 'Non',
    'leave_room': '← Quitter la chambre',
    'return': '← Retour',
    'return_hallway': 'Retourner au Couloir',
    'begin': 'Commencer',
    'watch': 'Regardez...',
    'correct': 'Correct',
    'round': 'Manche',
    'enter_again': 'Entrer à nouveau',
    'steps': 'Pas',
    'depth': 'Profondeur',
    
    // Keyboard Shortcuts
    'keyboard_shortcuts': 'Raccourcis Clavier',
    'shortcut_hallway': 'Aller au Couloir',
    'shortcut_random': 'Chambre Aléatoire',
    'shortcut_back': 'Retour',
    'shortcut_bookmark': 'Basculer Favori',
    'shortcut_reading': 'Mode Lecture',
    'shortcut_search': 'Focaliser Recherche',
    'shortcut_stats': 'Voir Statistiques',
    'shortcut_achievements': 'Voir Réalisations',
    'shortcut_cat_nav': 'Navigation',
    'shortcut_cat_rooms': 'Chambres',
    'shortcut_cat_access': 'Accessibilité',
    'press_to_close': 'Appuyez sur',
    'to_close': 'pour fermer',
    'or': 'ou',
    
    // Bookmarks
    'add_bookmark': 'Ajouter Favori',
    'remove_bookmark': 'Retirer Favori',
    'bookmarks': 'Favoris',
    'bookmarked_rooms': 'Favoris',
    'no_bookmarks': 'Pas encore de favoris',
    
    // Reading Mode
    'reading_mode': 'Mode Lecture',
    'reading_mode_on': 'Mode lecture activé',
    'reading_mode_off': 'Mode lecture désactivé',
    
    // Updates Page
    'updates': 'Mises à jour',
    'updates_title': 'Nouveautés',
    'updates_subtitle': 'Changements et améliorations récents',
    'updates_footer': 'Construction de chambres, une mise à jour à la fois.',
    'new': 'NOUVEAU',
    'subscribe_rss': "S'abonner via RSS",
    'latest': 'DERNIER',
    'whats_new_intro': 'Dernières mises à jour:',
    'view_all_updates': 'Voir toutes les mises à jour',
    'update_updates_page': 'Page de mises à jour ajoutée',
    'update_full_i18n': 'Traduction complète en 6 langues',
    'update_hallway_animations': 'Animations fluides de changement de catégorie',
    'update_daily_challenges': 'Amélioration des défis quotidiens',
    'update_game_rooms': '15 nouvelles salles de jeu',
    'update_experimental_rooms': '14 salles expérimentales',
    'update_random_room': 'Bouton de chambre aléatoire',
    'update_stats_route': 'Route des statistiques corrigée',
    'update_streak_system': 'Système de séries quotidiennes',
    'update_weekly_summary': 'Résumés de progression hebdomadaire',
    'update_rtl_support': 'Support des langues RTL',
    'update_dark_rooms': 'Nouvelle catégorie de chambres sombres',
    'update_boss_rooms': 'Salles de boss avec fragments',
    'update_audio_system': 'Audio ambiant dans les chambres',
    'update_constellation_map': 'Carte constellation du voyage',
    'update_fragment_system': 'Système de fragments de chambres',
    'update_mobile_layout': 'Améliorations du design mobile',
    'update_multi_language': 'Support multilingue (6 langues)',
    'update_premium_system': 'Système de chambres premium',
    'update_room_endings': 'Citations de fin uniques',
    'update_ui_redesign': 'Refonte complète de l\'interface',
    'update_theme_effects': 'Effets visuels par thème (braises, étoiles, glitches)',
    'update_void_theme': 'Nouveau thème Vide - mode obscurité absolue',
    'update_achievements': 'Système de réalisations avec 19 jalons',
    'update_ai_reflection': 'Chambre de réflexion IA',
    'update_settings_redesign': 'Paramètres redessinés avec 4 thèmes',
    'update_theme_exclusive_rooms': 'Chambres exclusives par thème',
    'update_discovery_localization': 'Messages de découverte traduits',
    'update_site_mood_system': 'Système d\'humeur du site',
    'update_voice_imperfection': 'Chambres à voix imparfaite',
    'update_mobile_optimization': 'Optimisation mobile avec cibles de 48px',
    'update_interaction_clues': 'Indices d\'interaction subtils',
    'update_theme_exclusive_hallway': 'Chambres exclusives visibles dans le couloir',
    'update_advanced_easter_eggs': 'Easter eggs avancés : séquence de coins, morse, visiteur de minuit',
    'update_hidden_discoveries': 'Révélations de texte caché',
    'update_dual_mode_system': 'Système double mode Classique et Alternatif',
    'update_alternate_mode': 'Mode alternatif - une réalité parallèle expérimentale',
    'update_hidden_discovery': 'Déclencheurs de découverte cachés : triple tap, motifs de défilement',
    'update_mode_effects': 'Effets visuels spécifiques au mode, glitches et audio ambiant',
    'update_translation_navigator': 'Navigateur de Traduction - tableau de couverture visuel',
    'update_full_localization': 'Couverture de traduction presque complète en 6 langues',
    'update_meta_room_translations': 'Contenu des méta-chambres traduit (Le Refus, Toi Parallèle, Ton Ombre)',
    'update_animated_timeline': 'Timeline animée avec transitions au défilement',
    'update_toast_notifications': 'Notification toast pour les nouvelles mises à jour',
    'update_rss_feed': 'Flux RSS pour les abonnements aux changements',
    'update_bookmarked_filter': 'Onglet Chambres Favoris dans le Couloir',
    'update_keyboard_shortcuts': 'Raccourcis clavier (appuyez sur ? pour afficher)',
    'update_reading_mode': 'Mode lecture pour typographie et concentration améliorées',
    'update_accessibility': 'Indicateurs de focus et navigation clavier améliorés',
    'update_ambient_crossfade': 'Transitions fluides entre les pistes audio ambiantes',
    'update_settings_audio': 'Contrôles audio centralisés dans la page Paramètres',
    'update_arabic_complete': 'Couverture complète de la traduction arabe',
    'update_duplicate_icon_fix': 'Icônes audio en double corrigées dans l\'en-tête',

    // Game UI
    'game_watching': 'regardant...',
    'game_repeat': 'répéter',
    'game_echo_fades': 'l\'écho s\'estompe...',
    'game_escaped': 'Vous vous êtes échappé.',
    'game_found_exit': 'Vous avez trouvé la sortie.',
    'game_looping': 'Vous tournez en boucle.',
    'game_deeper': 'Plus profond...',
    'game_doors_shift': 'Les portes changent.',
    'maze_left': 'gauche',
    'maze_right': 'droite',
    'maze_forward': 'avant',
    'maze_back': 'arrière',
    'maze_past': 'passé',
    'maze_future': 'futur',
    'maze_here': 'ici',
    'maze_there': 'là-bas',
    'maze_truth': 'vérité',
    'maze_lies': 'mensonges',
    'maze_light': 'lumière',
    'maze_dark': 'obscurité',
    'maze_memory': 'mémoire',
    'maze_dream': 'rêve',
    'maze_silence': 'silence',
    'maze_noise': 'bruit',
    'random_room': 'Chambre Aléatoire',
    'ai_reflection': 'Réflexion IA',
    'support': 'Soutien',
    'choose_direction': 'Choisissez une direction. Certaines portes répondent.',
    'rooms_explored': 'chambres explorées',
    'your_journey': 'Votre Voyage',
    'complete_rooms_fragments': 'Complétez des chambres pour collecter des fragments.',
    'no_rooms_completed': 'Aucune chambre terminée pour le moment.',
    'constellation_map': 'Carte des Constellations',
    'your_stats': 'Vos Statistiques',
    
    // Stats
    'total_time': 'Temps Total',
    'rooms_visited': 'Chambres Visitées',
    'total_visits': 'Total des Visites',
    'most_visited': 'Plus Visitée',
    'longest_session': 'Session la Plus Longue',
    'fragments_collected': 'Fragments Collectés',
    'completed_rooms': 'Chambres Terminées',
    'privacy_note': 'Toutes les données sont stockées localement sur votre appareil',
    
    // Daily Challenges
    'daily_challenges': 'Défis Quotidiens',
    'challenges_completed': 'terminé',
    'challenges_total': 'total',
    'resets_midnight': 'Réinitialise à minuit',
    
    // Weekly Summary
    'weekly_summary': 'Résumé Hebdomadaire',
    'week': 'Semaine',
    'this_week': 'Cette Semaine',
    'rooms': 'Chambres',
    'days': 'Jours',
    'vs_last_week': 'vs semaine dernière',
    'refresh_insight': 'Rafraîchir la perspective',
    
    // Game Room Titles
    'room_memory-puzzle': 'Puzzle de Mémoire',
    'room_shadow-chase': 'Chasse aux Ombres',
    'room_timing-room': 'Salle du Timing',
    'room_pattern-whisper': 'Murmure de Motifs',
    'room_silence-puzzle': 'Puzzle du Silence',
    'room_decay-game': 'Jeu de Décadence',
    'room_observation-test': "Test d'Observation",
    'room_balance-room': "Salle de l'Équilibre",
    'room_word-drift': 'Dérive des Mots',
    'room_color-echo': 'Écho de Couleur',
    'room_mirror-tap': 'Tape du Miroir',
    'room_maze-of-doors': 'Labyrinthe de Portes',
    'room_pulse-match': 'Correspondance du Pouls',
    'room_light-path': 'Chemin de Lumière',
    'room_sound-match': 'Correspondance de Son',
    
    // Game Room Subtitles
    'sub_memory-puzzle': 'Associez des motifs qui changent subtilement',
    'sub_shadow-chase': 'Attrapez ce qui bouge de manière imprévisible',
    'sub_timing-room': 'Cliquez exactement au bon moment',
    'sub_pattern-whisper': 'Répétez la séquence qui s\'estompe',
    'sub_silence-puzzle': 'Résolvez sans instructions',
    'sub_decay-game': 'Touchez pour préserver ce qui disparaît',
    'sub_observation-test': 'Trouvez les différences subtiles',
    'sub_balance-room': 'Gardez les objets flottants alignés',
    'sub_word-drift': 'Attrapez les lettres avant qu\'elles ne tombent',
    'sub_color-echo': 'Répétez la séquence de tons subtils',
    'sub_mirror-tap': 'Chaque tape résonne ailleurs',
    'sub_maze-of-doors': 'Les portes se réorganisent après chaque choix',
    'sub_pulse-match': 'Bougez au rythme de la salle',
    'sub_light-path': "Guidez la lumière à travers l'obscurité changeante",
    'sub_sound-match': 'Tapez des formes pour correspondre aux tons doux',
    
    // Experimental Room Titles
    'room_mirror-room': 'Salle du Miroir',
    'room_echo-room': "Salle de l'Écho",
    'room_pulse-room': 'Salle du Pouls',
    'room_vanishing-room': 'Salle qui Disparaît',
    'room_letter-room': 'Salle des Lettres',
    'room_empty-room': 'Salle Vide',
    'room_reverse-room': 'Salle Inversée',
    'room_whisper-room': 'Salle du Murmure',
    'room_shadow-room': 'Salle des Ombres',
    'room_labyrinth-room': 'Salle du Labyrinthe',
    'room_color-room': 'Salle des Couleurs',
    'room_surrender-room': 'Salle de la Reddition',
    'room_noise-room': 'Salle du Bruit',
    'room_observer-room': "Salle de l'Observateur",
    
    // Experimental Room Subtitles
    'sub_mirror-room': 'Reflets légèrement déformés',
    'sub_echo-room': 'Les clics déclenchent des échos retardés',
    'sub_pulse-room': "L'environnement respire",
    'sub_vanishing-room': 'Les éléments disparaissent en interagissant',
    'sub_letter-room': 'Les mots se brouillent comme une mémoire défaillante',
    'sub_empty-room': 'Vide jusqu\'à ce que vous trouviez des secrets',
    'sub_reverse-room': 'Les actions se comportent à l\'opposé',
    'sub_whisper-room': 'Vérités à peine audibles',
    'sub_shadow-room': 'Votre compagnon sombre',
    'sub_labyrinth-room': 'Trouvez votre chemin',
    'sub_color-room': 'Méditation chromatique',
    'sub_surrender-room': 'Lâchez le contrôle',
    'sub_noise-room': 'Pensées statiques',
    'sub_observer-room': 'Être observé',
    
    // Daily Joke Room
    'joke_number': 'Blague #{number}',
    'joke_today': 'La Blague du Jour',
    'joke_reveal': 'Révéler la chute',
    'joke_come_back': 'Revenez demain pour une nouvelle blague.',
    'joke_quote': 'Le rire est la plus courte distance entre deux personnes.',
    
    // Daily Quote Room
    'quote_day_number': 'Jour {day}',
    'quote_come_back': 'Revenez demain pour une nouvelle citation.',
    
    // Leaderboard
    'leaderboard': 'Classement communautaire',
    'leaderboard_subtitle': 'anonyme · collectif · sans compétition',
    'collective_time': 'temps collectif',
    'achievements_count': 'réalisations',
    'most_visited_rooms': 'Salles les plus visitées',
    'most_time_spent': 'Plus de temps passé',
    'most_unlocked_achievements': 'Réalisations les plus débloquées',
    'no_achievements_yet': 'Aucune réalisation débloquée.',
    
    // Transfer Data
    'transfer_data': 'Transférer les données',
    'transfer_data_desc': 'Transférez votre progression vers un autre appareil avec un code à usage unique.',
    'generate_transfer_code': 'Générer un code de transfert',
    'enter_transfer_code': 'Entrez le code...',
    'transfer_code_expires': 'Le code expire dans 24 heures et ne peut être utilisé qu\'une seule fois.',
    'generating': 'Génération...',
    'import': 'Importer',
    'copied': 'Copié !',
    
    // Waiting Game Room
    'waiting_instruction': 'Ne bougez pas. Ne cliquez pas. Attendez simplement.',
    'waiting_slow': 'Ralentir est plus difficile qu\'il n\'y paraît.',
    'waiting_unfamiliar': 'Cette immobilité semble inhabituelle, n\'est-ce pas ?',
    'waiting_most': 'La plupart des gens partent à ce stade.',
    'waiting_learning': 'Vous apprenez quelque chose sur vous-même.',
    'waiting_rewards': 'La patience a ses propres récompenses.',
    'waiting_seconds': 'secondes d\'immobilité',
    'waiting_waiting': 'en attente...',
    'waiting_moved': 'Vous avez bougé {count} fois.',
    'waiting_waited': 'Vous avez attendu. C\'est rare.',
    'waiting_most_cant': 'La plupart ne peuvent pas rester assis sans rien faire. Vous l\'avez fait.',
    'waiting_quote': 'Le moment présent est le seul moment disponible pour nous, et c\'est la porte vers tous les moments.',
    
    // Settings Page
    'settings': 'Paramètres',
    'settings_subtitle': 'Personnalisez votre voyage',
    'language': 'Langue',
    'ambient_audio': 'Audio Ambiant',
    'ambient_audio_desc': 'Sons de fond pour les chambres',
    'reduced_motion': 'Mouvement Réduit',
    'reduced_motion_desc': 'Minimiser les animations',
    'data_management': 'Gestion des Données',
    'export_journey': 'Exporter les Données du Voyage',
    'clear_all_data': 'Effacer Toutes les Données',
    'audio_enabled': 'Audio Activé',
    'audio_disabled': 'Audio Désactivé',
    'data_exported': 'Données Exportées',
    'clear_data_confirm': 'Êtes-vous sûr ? Cela effacera toute votre progression.',
    'data_cleared': 'Données Effacées',
    // Theme settings
    'theme': 'Thème',
    'theme_dark': 'Sombre',
    'theme_dark_desc': 'Obscurité pure',
    'theme_bone': 'Os',
    'theme_bone_desc': 'Pâleur mortelle',
    'theme_ash': 'Cendre',
    'theme_ash_desc': 'Après le feu',
    'theme_fog': 'Brouillard',
    'theme_fog_desc': 'Incertitude éthérée',
    'theme_twilight': 'Crépuscule',
    'theme_twilight_desc': 'Entre jour et nuit',
    'theme_eclipse': 'Éclipse',
    'theme_eclipse_desc': 'Lumière dévorée par l\'obscurité',
    'theme_dawn': 'Aube',
    'theme_dawn_desc': 'Première lumière de l\'éveil',
    'theme_changed': 'Thème mis à jour',
    'font_size': 'Taille de Police',
    'font_small': 'Petite',
    'font_medium': 'Moyenne',
    'font_large': 'Grande',
    'font_size_changed': 'Taille de police mise à jour',
    'high_contrast': 'Contraste Élevé',
    'high_contrast_desc': 'Augmenter le contraste du texte et des bordures',
    'high_contrast_enabled': 'Contraste élevé activé',
    'high_contrast_disabled': 'Contraste élevé désactivé',
    
    // Ambient Music Control
    'ambient_music': 'Musique Ambiante',
    'ambient_music_desc': 'Contrôlez les paysages sonores de fond',
    'volume': 'Volume',
    'ambient_track': 'Piste Ambiante',
    'playing': 'Lecture',
    'play': 'Jouer',
    'ambient_default': 'Calme',
    'ambient_night': 'Nuit',
    'ambient_scary': 'Inquiétant',
    'ambient_truth': 'Vérité',
    'ambient_identity': 'Réflexion',
    'ambient_mortality': 'Temps',
    'ambient_nothing': 'Vide',
    'ambient_meta': 'Numérique',
    'ambient_daily': 'Chaleureux',
    'ambient_seasonal': 'Magique',
    'ambient_premium': 'Profond',
    
    'shared_room': 'Chambre Partagée',
    'imagine_room': 'Imaginer',
    'imagine_title': 'Imaginez-Vous',
    'imagine_subtitle': 'Décrivez comment vous vous voyez. Laissez la chambre vous montrer ce qui émerge de vos mots.',
    'imagine_placeholder': 'Je me vois comme quelqu\'un qui...\n\nDécrivez votre essence, votre nature, comment vous vous sentez à l\'intérieur.',
    'imagine_hint': 'Soyez honnête. L\'image reflète vos mots.',
    'imagine_generate': 'Voir Moi-Même',
    'imagine_generating': 'Création...',
    'imagine_try_again': 'Décrire à Nouveau',
    'imagine_quote': 'Nous ne nous voyons pas clairement. Nous voyons ce que nous croyons.',
    'room_imagine-yourself': 'Imaginez-Vous',
    'sub_imagine-yourself': 'Décrivez-vous. Voyez ce qui émerge.',
    'theme_void': 'Vide',
    'theme_void_desc': 'Il ne reste que des ombres',
    
    // Easter Egg Discoveries
    'discovery_label': 'Découverte',
    'discovery_konami': 'Vous connaissez les anciennes voies.',
    'discovery_secret_sequence': 'Vous avez épelé la vérité.',
    'discovery_long_press': 'La patience révèle des secrets.',
    'discovery_triple_click': 'La persévérance paie.',
    'discovery_silence': 'Le silence parle.',
    
    // Theme-Exclusive Rooms
    'ash_exclusive': 'Exclusif au thème Cendre',
    'twilight_exclusive': 'Exclusif au thème Crépuscule',
    'void_exclusive': 'Exclusif au thème Vide',
    'theme_required': 'Nécessite le thème {theme}',
    'ash_embers_title': 'Des Cendres',
    'ash_embers_line1': 'Tout brûle éventuellement.',
    'ash_embers_line2': 'Ce qui compte, c\'est ce qui s\'élève après.',
    'ash_embers_line3': 'Touchez l\'écran. Créez des braises.',
    'ash_embers_quote': 'Des cendres, nous nous souvenons de ce qui a brûlé.',
    'room_ash-embers': 'Cendres et Braises',
    'sub_ash-embers': 'Visible uniquement dans le thème Cendre',
    'twilight_stars_title': 'Entre Jour et Nuit',
    'twilight_stars_line1': 'Les étoiles se souviennent de tous ceux qui ont levé les yeux.',
    'twilight_stars_line2': 'Connectez-les. Créez votre propre constellation.',
    'twilight_stars_quote': 'Au crépuscule, nous voyons ce que le jour cache.',
    'room_twilight-stars': 'Étoiles du Crépuscule',
    'sub_twilight-stars': 'Visible uniquement dans le thème Crépuscule',
    'void_abyss_title': 'L\'Abîme',
    'void_abyss_line1': 'Regardez dans le vide. Le vide vous regarde.',
    'void_word_1': 'rien',
    'void_word_2': 'vide',
    'void_word_3': 'silence',
    'void_word_4': 'parti',
    'void_word_5': 'seul',
    'void_word_6': 'fin',
    'void_revealed': 'révélé',
    'void_abyss_quote': 'Dans l\'obscurité absolue, nous nous voyons enfin.',
    'room_void-abyss': 'L\'Abîme',
    'sub_void-abyss': 'Visible uniquement dans le thème Vide',
    
    // Theme Exclusive Section
    'theme_exclusive_rooms': 'Chambres Exclusives par Thème',
    'theme_exclusive_hint': 'Changez de thème dans les paramètres pour déverrouiller ces chambres.',
    'requires_theme': 'Nécessite',
    'unlocked': 'Déverrouillé',
    'switch_to': 'Passer à',
    
    // Advanced Easter Eggs
    'corner_sequence': 'Vous avez tracé les limites.',
    'morse_sos': 'S.O.S. reçu.',
    'reverse_scroll': 'Retour en arrière... plus vite.',
    'midnight_visitor': 'L\'heure des sorcières vous accueille.',
    'shake': 'Vous avez secoué quelque chose.',
    
    // Experience Mode
    'experience_mode': "Mode d'Expérience",
    'classic_mode': 'Classique',
    'classic_mode_desc': 'La réalité familière',
    'alternate_mode': 'Alt̶erné',
    'alternate_mode_desc': 'Vous ne devriez pas être ici',
    'switch_to_alternate': '░ descendre ░',
    'switch_to_classic': '← Revenir à la surface',
    'enter_parallel_reality': 'La porte est ouverte',
    'return_to_familiar': 'Revenir au familier',
    'something_shifted': 'Quelque chose a changé',
    'reality_feels_different': 'La réalité semble différente maintenant.',
    'alternate_tagline': 'Les mêmes chambres. Des vérités différentes.',
    'alternate_enter': 'Descendre',
    'alternate_footer': 'Les choses ne sont pas ce quelles semblent.',
    'alternate_rooms': '░▓ Réal1té P4rallèle ▓░',
    'alternate_rooms_hint': '[ ces chambres n\'ex1stent que dans le parallèle ]',
    
    // Alternate Mode Exclusive Rooms (French)
    'room_the-other-side': 'L\'Autre Côté',
    'room_the-unraveling': 'L\'Effilochage',
    'room_the-echo': 'L\'Écho',
    'sub_the-other-side': 'avancez lentement dans l\'obscurité',
    'sub_the-unraveling': 'la réalité s\'effiloche à chaque mot',
    'sub_the-echo': 'ce que vous dites ne part jamais',
    
    // Meta Rooms (French)
    'room_the-refusal': 'Le Refus',
    'sub_the-refusal': 'Parfois le site vous refuse',
    'room_parallel-you': 'Vous Parallèle',
    'sub_parallel-you': 'Une autre version de vous',
    'room_your-shadow': 'Votre Ombre',
    'sub_your-shadow': 'Ce qui vous suit',
    
    // AI Rooms (French)
    'room_ai-reflection-room': 'Salle de Réflexion IA',
    'sub_ai-reflection-room': 'Laissez l\'IA réfléchir avec vous',
    'sub_dream-visualization': 'Décrivez votre rêve. Voyez-le prendre forme.',
    'sub_future-self': 'Un message du futur',
    
    // Meta Room Content - The Refusal (French)
    'refusal_line1': 'Cette chambre vous refuse.',
    'refusal_line2': 'Tout ne s\'ouvre pas quand vous le voulez.',
    'refusal_line3': 'Parfois la réponse est simplement : non.',
    'refusal_line4': 'Essayez le bouton. Ça n\'aidera pas.',
    'refusal_button': 'Entrer dans la Chambre',
    
    // Meta Room Content - Parallel You (French)
    'parallel_choice_1': 'Vous avez choisi la vitesse.',
    'parallel_alternate_1': 'Un autre vous a choisi la patience.',
    'parallel_reflection_1': 'Ils attendent encore.',
    'parallel_choice_2': 'Vous êtes parti tôt.',
    'parallel_alternate_2': 'Un autre vous est resté jusqu\'à la fin.',
    'parallel_reflection_2': 'Ils ont vu ce que vous avez manqué.',
    'parallel_choice_3': 'Vous avez évité la question difficile.',
    'parallel_alternate_3': 'Un autre vous y a répondu.',
    'parallel_reflection_3': 'Ils portent cette vérité maintenant.',
    'parallel_choice_4': 'Vous êtes passé en courant.',
    'parallel_alternate_4': 'Un autre vous a savouré chaque moment.',
    'parallel_reflection_4': 'Ils se souviennent de plus.',
    'parallel_choice_5': 'Vous avez continué à défiler.',
    'parallel_alternate_5': 'Un autre vous s\'est arrêté.',
    'parallel_reflection_5': 'Ils ont trouvé quelque chose.',
    'parallel_choice_6': 'Vous avez choisi le confort.',
    'parallel_alternate_6': 'Un autre vous a choisi l\'inconfort.',
    'parallel_reflection_6': 'Ils ont grandi avec.',
    'parallel_quote': 'Chaque moment se divise en chemins infinis. Vous n\'en marchez qu\'un.',
    
    // Meta Room Content - Your Shadow (French)
    'shadow_generated': 'Généré depuis votre comportement',
    'shadow_intro': 'Votre ombre...',
    'shadow_follows': 'Cette ombre vous suit dans chaque chambre.',
    'shadow_quote': 'Nous ne sommes pas ce que nous pensons. Nous sommes ce que nous faisons.',
    'shadow_trait_completion': 'cherche à compléter',
    'shadow_trait_surface': 'préfère la surface',
    'shadow_trait_lingers': 'reste dans les espaces',
    'shadow_trait_moves_quickly': 'se déplace vite',
    'shadow_trait_avoids_depth': 'évite la profondeur',
    'shadow_trait_stillness': 'embrasse l\'immobilité',
    'shadow_trait_chases': 'poursuit quelque chose sans nom',
    'shadow_trait_darkness': 'attiré par l\'obscurité',
    'shadow_trait_emptiness': 'à l\'aise avec le vide',
    'shadow_trait_undefined': 'non défini',
    'shadow_trait_forming': 'encore en formation',
    
    // Translation Navigator (French)
    'translation_navigator': 'Navigateur de Traductions',
    'translation_coverage': 'Couverture de traduction par langue',
    'keys': 'clés',
    'room_translations': 'Traductions de Chambres',
    'show_all': 'Afficher Tout',
    'show_missing': 'Afficher Manquantes',
    'all_translated': 'Toutes les chambres sont traduites !',
    'missing_translations': 'traductions manquantes',
    
    // Additional Room Titles (French)
    'room_how-many-times-left': 'Combien de Fois Restantes',
    'room_life-in-weeks': 'Votre Vie en Semaines',
    'room_delay-button': 'Le Bouton de Délai',
    'room_one-honest-sentence': 'Une Phrase Honnête',
    'room_this-moment': 'Ce Moment Ne Se Répétera Jamais',
    'room_reality-receipt': 'Reçu de Réalité',
    'room_nothing-happens': 'Rien Ne Se Passe Ici',
    'room_default-choice': 'Votre Choix par Défaut',
    'room_thought-loop': 'La Boucle de Pensées',
    'room_expect-vs-happens': 'Ce que Vous Attendez vs Ce qui Arrive',
    'room_question-never-answer': 'La Question à Laquelle Vous Ne Répondez Jamais',
    'room_not-special': 'Vous N\'êtes Pas Spécial (Et C\'est Bien)',
    'room_shortcut-always-take': 'Le Raccourci que Vous Prenez Toujours',
    'room_blurred-self': 'Moi Flou',
    'room_image-zooming': 'L\'Image que Vous Zoomez',
    'room_comfort-color': 'La Couleur de Confort',
    'room_line-you-crossed': 'La Ligne que Vous Avez Franchie',
    'room_read-aloud': 'Lisez Ceci à Voix Haute',
    'room_voice-without-meaning': 'Votre Voix Sans Signification',
    'room_sentence-you-avoid': 'La Phrase que Vous Évitez',
    'room_if-nothing-changes': 'Si Rien Ne Change',
    'room_real-priorities': 'Vos Vraies Priorités',
    'room_last-conversation': 'La Dernière Conversation',
    'room_who-without-job': 'Qui Êtes-Vous Sans Votre Travail?',
    'room_comfort-zone-scanner': 'Scanner de Zone de Confort',
    'room_say-it-once': 'Dites-le Une Fois',
    'room_listen-to-yourself': 'Écoutez-Vous',
    'room_life-as-shapes': 'Votre Vie en Formes',
    'room_face-without-features': 'Visage Sans Traits',
    'room_what-keeps-awake': 'Ce qui Vous Garde Éveillé',
    'room_you-at-317am': 'Vous à 3h17',
    'room_thought-that-returns': 'La Pensée qui Revient',
    'room_room-you-visit-most': 'La Chambre que Vous Visitez le Plus',
    'room_room-you-avoid': 'La Chambre que Vous Évitez',
    'room_left-too-early': 'Vous Êtes Parti Trop Tôt',
    'room_daily-quote': 'Citation du Jour',
    'room_daily-joke': 'Blague du Jour',
    'room_theyre-watching': 'Ils Vous Observent',
    'room_the-last-visitor': 'Le Dernier Visiteur',
    'room_dont-look-away': 'Ne Détournez Pas le Regard',
    'room_the-whisper': 'Le Murmure',
    'room_you-forgot-something': 'Vous Avez Oublié Quelque Chose',
    'room_the-mirror-lies': 'Le Miroir Ment',
    'room_something-follows': 'Quelque Chose Vous Suit',
    'room_the-countdown': 'Le Compte à Rebours',
    'room_eyes-in-the-dark': 'Des Yeux dans le Noir',
    'room_the-voice': 'La Voix',
    'room_it-knows-your-name': 'Il Connaît Votre Nom',
    'room_the-door': 'La Porte',
    'room_boss-room-free': 'La Somme de Vos Vérités',
    'room_boss-room-horror': 'Le Cœur de la Peur',
    'room_boss-room-premium': 'La Vérité la Plus Profonde',
    'room_mouse-tracker': 'Le Traqueur de Souris',
    'room_scroll-confession': 'Confession de Défilement',
    'room_waiting-game': 'Le Jeu de l\'Attente',
    'room_day-in-pixels': 'Votre Jour en Pixels',
    'room_memory-maze': 'Labyrinthe de Mémoire',
    'room_decision-shadow': 'Ombre de Décision',
    'room_anonymous-mirrors': 'Miroirs Anonymes',
    'room_collective-room': 'La Chambre Collective',
    'room_secret-room': 'La Chambre Secrète',
    'room_glitch-room': 'Chambre Instable',
    'room_full-moon-room': 'Chambre de Pleine Lune',
    'room_weekend-room': 'Chambre du Week-end',
    'room_317am-room': 'Chambre de 3h17',
    'room_idle-room': 'La Chambre Immobile',
    'room_invite-friend-room': 'La Chambre Partagée',
    'room_halloween-room': 'La Chambre du Voile',
    'room_winter-solstice-room': 'La Nuit la Plus Longue',
    'room_new-year-room': 'Lettre à Votre Futur Vous',
    'room_void-room': 'Le Vide',
    'room_pattern-room': 'Vos Motifs',
    'room_time-capsule': 'Capsule Temporelle',
    'room_the-revisit': 'La Revisite',
    'room_contradiction-room': 'La Chambre de Contradiction',
    'room_export-journey': 'Exporter Votre Voyage',
    'room_anonymous-echoes': 'Échos Anonymes',
    'room_the-waiting-room': 'La Salle d\'Attente',
    'room_dream-visualization': 'Visualisation de Rêve',
    'room_future-self': 'Votre Moi Futur',
    'room_eclipse-shadows': 'Ombres de l\'Éclipse',
    'room_dawn-awakening': 'Éveil de l\'Aube',
    
    // Additional Room Subtitles (French)
    'sub_how-many-times-left': 'Un décompte de ce qui reste',
    'sub_life-in-weeks': '4 000 carrés. C\'est tout.',
    'sub_delay-button': 'Ça devient plus dur à chaque fois',
    'sub_one-honest-sentence': 'Pas de modification. Pas de suppression.',
    'sub_this-moment': 'Vous êtes ici. Une fois.',
    'sub_reality-receipt': 'Un résumé de vos jours',
    'sub_nothing-happens': 'Exactement ce que ça dit',
    'sub_eclipse-shadows': 'Parlez dans l\'obscurité totale',
    'sub_dawn-awakening': 'Saluez la première lumière',
    'sub_default-choice': 'Sécurité ou risque. À chaque fois.',
    'sub_thought-loop': 'Regardez votre esprit se répéter',
    'sub_expect-vs-happens': 'L\'écart entre espoir et réalité',
    'sub_question-never-answer': 'La plupart partent',
    'sub_not-special': 'Pas de réconfort. Juste la vérité.',
    'sub_shortcut-always-take': 'Les motifs se révèlent',
    'sub_blurred-self': 'La mémoire fonctionne ainsi',
    'sub_image-zooming': 'Chercher des réponses dans le néant',
    'sub_comfort-color': 'Combien de temps resterez-vous?',
    'sub_line-you-crossed': 'Nous voyons où vous avez hésité',
    'sub_read-aloud': 'Des mots que vous devez prononcer',
    'sub_voice-without-meaning': 'Du son sans paroles',
    'sub_sentence-you-avoid': 'Vous ne pouvez pas partir sans l\'écrire',
    'sub_if-nothing-changes': 'Une projection de vos cinq prochaines années',
    'sub_real-priorities': 'Basé sur le temps, pas les mots',
    'sub_last-conversation': 'Écrivez ce que vous n\'enverrez jamais',
    'sub_who-without-job': 'Réécrivez-vous',
    'sub_comfort-zone-scanner': 'Nous voyons votre hésitation',
    'sub_say-it-once': 'Pas de reprise. Pas de refaire.',
    'sub_listen-to-yourself': 'Votre voix, ralentie',
    'sub_life-as-shapes': 'Abstrait. Sans étiquettes. Le vôtre.',
    'sub_face-without-features': 'Enlevez jusqu\'à ce qu\'il ne reste rien',
    'sub_what-keeps-awake': 'Disponible après 22h',
    'sub_you-at-317am': 'Spécifique au temps. Personnel.',
    'sub_thought-that-returns': 'Revient à chaque visite',
    'sub_room-you-visit-most': 'Votre motif révélé',
    'sub_room-you-avoid': 'Ce que vous évitez dit quelque chose',
    'sub_left-too-early': 'Déclenché quand vous partez vite',
    'sub_daily-quote': 'Sagesse qui change chaque jour',
    'sub_daily-joke': 'Humour noir, mis à jour quotidiennement',
    'sub_theyre-watching': 'Vous n\'êtes pas seul',
    'sub_the-last-visitor': 'Quelqu\'un était là avant vous',
    'sub_dont-look-away': 'Il sait quand vous partez',
    'sub_the-whisper': 'Écoutez attentivement',
    'sub_you-forgot-something': 'Qu\'était-ce?',
    'sub_the-mirror-lies': 'Brisez-le pour voir la vérité',
    'sub_something-follows': 'Continuez à courir',
    'sub_the-countdown': 'Quelque chose arrive à zéro',
    'sub_eyes-in-the-dark': 'Ils se multiplient',
    'sub_the-voice': 'Quelqu\'un est piégé ici',
    'sub_it-knows-your-name': 'Vous n\'auriez pas dû venir',
    'sub_the-door': 'Quelque chose frappe',
    'sub_boss-room-free': 'La chambre libre finale',
    'sub_boss-room-horror': 'Affrontez l\'obscurité finale',
    'sub_boss-room-premium': 'La chambre profonde finale',
    'sub_mouse-tracker': 'Nous voyons comment vous bougez',
    'sub_scroll-confession': 'À quelle vitesse vous précipitez-vous?',
    'sub_waiting-game': 'Pouvez-vous ne rien faire?',
    'sub_day-in-pixels': 'Le temps devient forme',
    'sub_memory-maze': 'Le motif change',
    'sub_decision-shadow': 'Vos choix révèlent des motifs',
    'sub_anonymous-mirrors': 'Pensées d\'inconnus',
    'sub_collective-room': 'Tout le monde est là. Personne ne reste.',
    'sub_secret-room': 'Vous l\'avez trouvée',
    'sub_glitch-room': 'Quelque chose ne va pas',
    'sub_full-moon-room': 'S\'ouvre uniquement pendant le pic lunaire',
    'sub_weekend-room': 'Le repos est une destination',
    'sub_317am-room': 'Spécifique au temps. Personnel.',
    'sub_idle-room': 'Trouvé par la patience',
    'sub_invite-friend-room': 'Certains voyages doivent être témoignés',
    'sub_halloween-room': '31 octobre - Le voile est mince',
    'sub_winter-solstice-room': '21 décembre - Libérer et garder',
    'sub_new-year-room': '31 décembre - Écrivez à qui vous serez',
    'sub_void-room': 'Une blessure dans le code',
    'sub_pattern-room': 'Ce que le site voit en vous',
    'sub_time-capsule': 'Écrivez à votre futur vous',
    'sub_the-revisit': 'Voyez comment vos réponses ont changé',
    'sub_contradiction-room': 'Vos vérités ne concordent pas',
    'sub_export-journey': 'Téléchargez votre chemin',
    'sub_anonymous-echoes': 'Fragments d\'inconnus',
    'sub_the-waiting-room': 'Débloquez avec présence',
    
    // New Phase 1-3 Rooms (French)
    'room_letters-never-sent': 'Lettres Jamais Envoyées',
    'sub_letters-never-sent': 'Écrivez à quelqu\'un qui ne lira jamais',
    'room_breath-sync': 'Synchronisation Respiratoire',
    'sub_breath-sync': 'Respirez avec la salle',
    'room_entropy-garden': 'Jardin d\'Entropie',
    'sub_entropy-garden': 'Croissance et décadence entrelacées',
    'room_memory-palace': 'Palais de la Mémoire',
    'sub_memory-palace': 'Construisez votre sanctuaire mental',
    'room_morning-ritual': 'Rituel du Matin',
    'sub_morning-ritual': 'Commencez votre journée avec intention',
    'room_evening-ritual': 'Rituel du Soir',
    'sub_evening-ritual': 'Terminez la journée avec réflexion',
    'room_weekly-ritual': 'Rituel Hebdomadaire',
    'sub_weekly-ritual': 'Les dimanches sont pour regarder en arrière',
    
    // Letters Never Sent Room (French)
    'letters_title': 'Lettres Jamais Envoyées',
    'letters_subtitle': 'Écrivez à quelqu\'un qui ne lira jamais. L\'IA répondra comme ils pourraient.',
    'letters_select_recipient': 'À qui écrivez-vous?',
    'letters_past_self': 'Mon Moi Passé',
    'letters_future_self': 'Mon Moi Futur',
    'letters_someone_lost': 'Quelqu\'un que J\'ai Perdu',
    'letters_someone_wronged': 'Quelqu\'un que J\'ai Blessé',
    'letters_placeholder': 'Cher/Chère...',
    'letters_send': 'Envoyer dans le Vide',
    'letters_sending': 'Envoi...',
    'letters_response': 'Une réponse émerge...',
    'letters_write_another': 'Écrire Une Autre Lettre',
    'letters_quote': 'Certains mots sont destinés à être écrits mais jamais envoyés.',
    
    // Breath Sync Room (French)
    'breath_title': 'Synchronisation Respiratoire',
    'breath_subtitle': 'Synchronisez votre respiration avec le rythme de la salle.',
    'breath_inhale': 'Inspirez',
    'breath_hold': 'Retenez',
    'breath_exhale': 'Expirez',
    'breath_cycles': 'Cycles complétés',
    'breath_quote': 'La respiration est le pont entre le corps et l\'esprit.',
    
    // Entropy Garden Room (French)
    'entropy_title': 'Jardin d\'Entropie',
    'entropy_subtitle': 'Vos plantes poussent à chaque visite, mais dépérissent quand vous vous éloignez.',
    'entropy_plant': 'Planter une Graine',
    'entropy_water': 'Arroser',
    'entropy_days_alive': 'jours en vie',
    'entropy_health': 'Santé',
    'entropy_empty': 'Votre jardin est vide. Plantez quelque chose.',
    'entropy_quote': 'Tout pousse. Tout se fane. C\'est le chemin.',
    
    // Memory Palace Room (French)
    'memory_palace_title': 'Palais de la Mémoire',
    'memory_palace_subtitle': 'Construisez un sanctuaire mental. Placez des objets dans des salles que vous créez.',
    'memory_palace_add_room': 'Ajouter une Salle',
    'memory_palace_add_object': 'Ajouter un Objet',
    'memory_palace_room_name': 'Nom de la salle...',
    'memory_palace_object_name': 'Nom de l\'objet...',
    'memory_palace_empty': 'Votre palais est vide. Créez votre première salle.',
    'memory_palace_quote': 'Nous construisons nos esprits une salle à la fois.',
    
    // Ritual Rooms (French)
    'ritual_locked': 'Ce rituel n\'est pas encore disponible',
    'morning_ritual_title': 'Rituel du Matin',
    'morning_ritual_intro': 'Un moment pour définir des intentions pour la journée.',
    'morning_ritual_available': 'Disponible 6h - 9h',
    'evening_ritual_title': 'Rituel du Soir',
    'evening_ritual_intro': 'Un moment pour réfléchir sur la journée.',
    'evening_ritual_available': 'Disponible 20h - 23h',
    'weekly_ritual_title': 'Rituel Hebdomadaire',
    'weekly_ritual_intro': 'Un moment pour réfléchir sur la semaine passée, et définir des intentions pour la prochaine.',
    'weekly_locked': 'Le Rituel Hebdomadaire vous attend',
    'weekly_time': 'Revenez dimanche',
    'today_is': 'Aujourd\'hui c\'est',
    'your_week': 'Votre Semaine',
    'days_active': 'Jours Actifs',
    'weekly_summary_note': 'Maintenant, réfléchissons plus profondément.',
    'weekly_highlight': 'Quel a été le moment fort de votre semaine?',
    'weekly_challenge': 'Qu\'est-ce qui vous a le plus défié cette semaine?',
    'weekly_growth': 'Comment avez-vous grandi cette semaine?',
    'weekly_intention': 'Quelle intention définissez-vous pour la semaine prochaine?',
    'weekly_placeholder': 'Prenez votre temps...',
    'reflect': 'Réfléchir',
    'reflecting': 'Réflexion...',
    'complete': 'Terminer',
    'weekly_quote': 'Une semaine se termine. Une autre commence. Vous emportez ce qui compte.',
    
    // Personal Timeline (French)
    'personal_timeline': 'Chronologie Personnelle',
    'timeline_subtitle': 'Votre voyage à travers les chambres',
    'timeline_empty': 'Votre chronologie est vide. Commencez à explorer.',
    'timeline_today': 'Aujourd\'hui',
    'timeline_yesterday': 'Hier',
    'timeline_this_week': 'Cette Semaine',
    'timeline_earlier': 'Plus tôt',
    
    // Weather System (French)
    'weather_clear': 'Clair',
    'weather_rain': 'Pluie',
    'weather_fog': 'Brouillard',
    'weather_snow': 'Neige',
    
    // Notifications (French)
    'notifications': 'Notifications',
    'notifications_desc': 'Recevez des notifications pour le contenu verrouillé par le temps',
    'notify_time_locked': 'Chambres verrouillées par le temps',
    'notify_full_moon': 'Nuits de pleine lune',
    'notify_daily_reset': 'Réinitialisation des défis quotidiens',
    'notify_time_capsule': 'Déverrouillage des capsules temporelles',
    'notifications_enabled': 'Notifications activées',
    'notifications_disabled': 'Notifications désactivées',
    
    // Community Submissions (French)
    'community_submissions': 'Contributions de la Communauté',
    'community_subtitle': 'Soumettez vos idées de chambres',
    'community_room_title': 'Titre de la Chambre',
    'community_room_description': 'Description de la Chambre',
    'community_room_category': 'Catégorie',
    'community_submit': 'Soumettre l\'Idée',
    'community_submitted': 'Merci pour votre contribution!',
    'community_your_submissions': 'Vos Contributions',
    'community_empty': 'Pas encore de contributions. Soyez le premier!',
    
    // Proactive Guide (French)
    'guide_hesitation': 'Vous prenez votre temps? C\'est bien. Voulez-vous une suggestion?',
    'guide_long_absence': 'Bon retour. Ça fait un moment.',
    'guide_milestone': 'Vous avez fait du chemin. Voulez-vous réfléchir à votre voyage?',
    'guide_dismiss': 'Ignorer',
    'guide_accept': 'Montrez-moi',
    
    // Voice Mode (French)
    'voice_mode': 'Mode Vocal',
    'voice_mode_desc': 'Laissez le guide vous parler',
    'voice_mode_enabled': 'Mode vocal activé',
    'voice_mode_disabled': 'Mode vocal désactivé',
    
    // Offline Support (French)
    'offline_available': 'Disponible hors ligne',
    'offline_cached': 'Cette chambre est mise en cache pour la visualisation hors ligne',
    
    // Journey Export (French)
    'journey_export': 'Exporter le Voyage',
    'journey_import': 'Importer le Voyage',
    'journey_export_json': 'Exporter en JSON',
    'journey_import_json': 'Importer depuis JSON',
    'journey_exported': 'Voyage exporté avec succès',
    'journey_imported': 'Voyage importé avec succès',
    
    // Phase 4 - Seasonal Rooms (French)
    'room_spring-equinox-room': 'Équinoxe de Printemps',
    'sub_spring-equinox-room': 'Libérez et renouvelez',
    'room_summer-solstice-room': 'Solstice d\'Été',
    'sub_summer-solstice-room': 'Le jour le plus long',
    'room_autumn-equinox-room': 'Équinoxe d\'Automne',
    'sub_autumn-equinox-room': 'Récoltez et acceptez',
    'room_valentine-room': 'Chambre d\'Amour de Soi',
    'sub_valentine-room': 'L\'amour que vous méritez',
    
    // Spring Equinox Room (French)
    'spring_date': '20 MARS',
    'spring_title': 'Équinoxe de Printemps',
    'spring_message_1': 'Le jour et la nuit sont égaux.',
    'spring_message_2': 'Un moment d\'équilibre parfait.',
    'spring_message_3': 'Que libérerez-vous pour faire place à la croissance?',
    'spring_letting_go': 'Je laisse partir:',
    'spring_letting_go_placeholder': 'Ce qui ne vous sert plus...',
    'spring_planting': 'Je plante:',
    'spring_planting_placeholder': 'Ce que vous souhaitez voir grandir...',
    'spring_bloom': 'Laissez fleurir',
    'spring_released': 'Libéré:',
    'spring_planted': 'Planté:',
    'spring_closing': 'À partir de ce moment, une nouvelle vie commence.',
    'spring_quote': 'La nature ne se presse pas, mais tout s\'accomplit.',
    
    // Summer Solstice Room (French)
    'summer_date': '21 JUIN',
    'summer_title': 'Le Jour le Plus Long',
    'summer_message_1': 'C\'est le jour le plus long de l\'année.',
    'summer_message_2': 'La lumière atteint son apogée.',
    'summer_message_3': 'Que feriez-vous avec plus de temps?',
    'summer_message_4': 'Décrivez votre journée la plus pleine et vibrante.',
    'summer_placeholder': 'Dans ma journée la plus pleine, je...',
    'summer_submit': 'Embrasser la lumière',
    'summer_closing': 'Le soleil vous a entendu. Maintenant il commence sa descente.',
    'summer_quote': 'Au sommet de la lumière, nous découvrons ce que nous voulons vraiment illuminer.',
    
    // Autumn Equinox Room (French)
    'autumn_date': '22 SEPTEMBRE',
    'autumn_title': 'Équinoxe d\'Automne',
    'autumn_message_1': 'L\'équilibre revient.',
    'autumn_message_2': 'La récolte est prête.',
    'autumn_message_3': 'Qu\'avez-vous cultivé? Que pouvez-vous accepter?',
    'autumn_harvesting': 'Je récolte:',
    'autumn_harvesting_placeholder': 'Ce que vous avez cultivé cette année...',
    'autumn_accepting': 'J\'accepte:',
    'autumn_accepting_placeholder': 'Ce que vous ne pouvez pas changer...',
    'autumn_release': 'Laissez tomber les feuilles',
    'autumn_harvested': 'Récolté:',
    'autumn_accepted': 'Accepté:',
    'autumn_closing': 'Tout tombe en son temps.',
    'autumn_quote': 'L\'automne nous montre combien il est beau de laisser partir les choses.',
    
    // Valentine's Day Room (French)
    'valentine_date': '14 FÉVRIER',
    'valentine_title': 'L\'Amour Que Vous Méritez',
    'valentine_message_1': 'Aujourd\'hui n\'est pas question des autres.',
    'valentine_message_2': 'C\'est question de celui qui est toujours avec vous.',
    'valentine_message_3': 'Celui qui entend chaque pensée.',
    'valentine_message_4': 'Écrivez-vous une lettre d\'amour.',
    'valentine_prompt': 'Une lettre pour vous:',
    'valentine_placeholder': 'Cher moi, je veux que tu saches...',
    'valentine_submit': 'Envoyer avec amour',
    'valentine_closing': 'Vous méritez l\'amour que vous donnez aux autres.',
    'valentine_quote': 'Vous-même méritez votre amour et votre affection.',
    
    // Update changelog
    'update_seasonal_rooms': 'Quatre nouvelles chambres saisonnières: Équinoxe de Printemps, Solstice d\'Été, Équinoxe d\'Automne et Saint-Valentin',
    'update_fragment_trading': 'Système d\'échange de fragments - sacrifiez des fragments pour débloquer des salles à temps, prévisualiser des salles d\'horreur ou recevoir des messages IA',
    'update_new_themes': 'Deux nouveaux thèmes: Éclipse (obscurité totale) et Aube (dégradés chauds, seulement 5-7h)',
     'update_regret_machine': 'La Machine à Regrets - récits de chronologies alternatives alimentés par l\'IA',
     'update_memory_reconstruction': 'Reconstruction de Mémoire - l\'IA remplit les détails sensoriels des souvenirs qui s\'estompent',
     
     // Phase 7 - Regret Machine Room
     'regret_machine_title': 'La Machine à Regrets',
     'regret_machine_subtitle': 'Décrivez une décision que vous auriez aimé prendre différemment. La machine vous montrera ce qui aurait pu être.',
     'regret_placeholder': 'Je regrette...\n\nDécrivez un choix que vous avez fait et que vous auriez voulu différent. Qu\'auriez-vous fait à la place?',
     'regret_hint': 'Ce n\'est pas une question de blâme. C\'est une question de compréhension.',
     'regret_explore': 'Explorer la Chronologie',
     'regret_generating': 'Reconstruction d\'une réalité alternative...',
     'regret_alternate_timeline': 'La Chronologie Alternative',
     'regret_explore_another': 'Explorer Une Autre',
     'regret_error_short': 'Veuillez décrire votre regret plus en détail',
     'room_regret-machine': 'La Machine à Regrets',
     
     // Phase 7 - Memory Reconstruction Room
     'memory_reconstruction_title': 'Reconstruction de Mémoire',
     'memory_reconstruction_subtitle': 'Décrivez un souvenir qui s\'estompe. Laissez-nous vous aider à retrouver le chemin.',
     'memory_placeholder': 'Je me souviens...\n\nDécrivez un souvenir lointain ou fragmenté. Quels fragments restent? Quels sentiments persistent?',
     'memory_timeframe_placeholder': 'Quand était-ce? (ex: "enfance", "il y a dix ans")',
     'memory_hint': 'La mémoire n\'est pas un enregistrement. C\'est une reconstruction.',
     'memory_reconstruct': 'Reconstruire le Souvenir',
     'memory_reconstructing': 'Rassembler les fragments...',
     'memory_reconstructed': 'Le Souvenir Reconstruit',
     'memory_explore_another': 'Explorer Un Autre Souvenir',
     'memory_error_short': 'Veuillez décrire votre souvenir plus en détail',
     'room_memory-reconstruction': 'Reconstruction de Mémoire',
    'update_eclipse_room': 'Ombres de l\'Éclipse - parlez dans l\'obscurité totale, révélez dans les éclairs de lumière',
    'update_dawn_room': 'Éveil de l\'Aube - saluez la première lumière avec des exercices de respiration et des intentions',
    
    // New themes
    'eclipse_theme_required': 'Cette salle nécessite le thème Éclipse. L\'obscurité totale vous attend.',
    'dawn_theme_required': 'Cette salle nécessite le thème Aube pendant les premières heures.',
    'dawn_time_hint': 'Disponible uniquement de 5h à 7h',
    'change_theme': 'Changer de thème dans les Paramètres',
    
    // Eclipse Shadows Room
    'eclipse_shadows_title': 'Ombres de l\'Éclipse',
    'eclipse_shadows_intro': 'Dans l\'obscurité complète, les secrets semblent plus sûrs. Répondez honnêtement—seuls de brefs éclairs illumineront vos mots.',
    'enter_darkness': 'Entrer dans l\'obscurité',
    'shadow_question_1': 'Que cachez-vous de vous-même?',
    'shadow_question_2': 'Quelle vérité diriez-vous si personne ne pouvait entendre?',
    'shadow_question_3': 'Qu\'est-ce qui vous hante à 3h du matin?',
    'shadow_question_4': 'Que feriez-vous si la peur n\'existait pas?',
    'shadow_question_5': 'Qu\'est-ce que vous n\'êtes pas prêt à affronter?',
    'speak_into_dark': 'Parlez dans l\'obscurité...',
    'release': 'Libérer',
    'eclipse_reveal_prompt': 'Cliquez sur chaque ombre pour révéler ce que vous avez confessé.',
    'hidden_in_shadow': '▓▓▓▓▓▓▓▓▓▓▓▓',
    'eclipse_reveal_hint': 'Certaines vérités sont plus claires dans l\'obscurité.',
    'eclipse_shadows_quote': 'Dans l\'obscurité totale, nous nous voyons enfin.',
    
    // Dawn Awakening Room
    'dawn_awakening_title': 'Éveil de l\'Aube',
    'dawn_awakening_intro': 'La première lumière apporte la possibilité. Avant que le monde ne s\'éveille, définissez vos intentions.',
    'greet_the_light': 'Saluez la lumière',
    'breathe_in_light': 'Respirez la lumière',
    'hold_warmth': 'Gardez la chaleur',
    'release_night': 'Libérez la nuit',
    'dawn_prompt_1': 'Que voulez-vous emporter avec vous aujourd\'hui?',
    'dawn_prompt_2': 'Qu\'allez-vous laisser aller?',
    'dawn_prompt_3': 'Quel petit acte de gentillesse vous offrirez-vous?',
    'set_your_intention': 'Définissez votre intention...',
    'plant_seed': 'Plantez cette graine',
    'dawn_awakening_quote': 'Chaque aube est une invitation à recommencer.',
    
    // Fragment Trading
    'fragment_trading': 'Échange de Fragments',
    'sacrifice_to_unlock': 'Sacrifiez des fragments pour débloquer des secrets',
    'your_fragments': 'Vos Fragments',
    'fragments_any': 'n\'importe quels fragments',
    'fragments_free': 'fragments de clarté',
    'fragments_premium': 'fragments de profondeur',
    'fragments_horror': 'fragments d\'ombre',
    'trade_successful': 'Échange Réussi !',
    'trade_failed': 'Échange Échoué',
    'not_enough_fragments': 'Fragments insuffisants',
    'continue_trading': 'Continuer à Échanger',
    'trading_fragments': 'Échange de fragments...',
    'fragment_trade_warning': 'Les fragments sacrifiés ne peuvent pas être récupérés',
    'unlock-time-locked': 'Débloquer Salle Temporelle',
    'unlock-time-locked_desc': 'Accédez à une salle temporelle sans attendre',
    'preview-horror': 'Aperçu Salle d\'Horreur',
    'preview-horror_desc': 'Jetez un œil à une salle d\'horreur avant d\'entrer',
    'ai-reflection-trade': 'Réflexion IA',
    'ai-reflection-trade_desc': 'Recevez un message personnalisé de l\'IA',
    'unlock-premium-preview': 'Essai Salle Premium',
    'unlock-premium-preview_desc': 'Accédez à une salle premium pour une visite',
    'fragment_trading_room': 'L\'Autel des Fragments',
    'fragment_trading_intro': 'Ici, vous pouvez sacrifier vos fragments collectés pour débloquer des secrets cachés dans ces salles.',
    'your_collection': 'Votre Collection',
    'fragment_clarity': 'Clarté',
    'fragment_depth': 'Profondeur',
    'fragment_shadow': 'Ombre',
    'total_fragments': 'Total',
    'open_altar': 'Approcher l\'Autel',
    'recent_trades': 'Offrandes Récentes',
    'fragment_trade_ending': 'Ce qui a été donné ne peut être récupéré. Mais ce qui a été gagné... cela reste.',
    'room_fragment-trade': 'L\'Autel des Fragments',
    'sub_fragment-trade': 'Sacrifiez pour débloquer des secrets',
    'update_anniversary_system': 'Système d\'Anniversaire et Jalons - suivez votre parcours dans le temps',
    'update_archive_room': 'Les Archives - historique complet débloqué à 25 salles',
    'update_mirror_room': 'Miroir de Tous les Soi - portrait comportemental IA à 50 salles',
    'update_ending_room': 'La Fin - un adieu personnalisé à 75 salles',
    'room_the-archive': 'Les Archives',
    'room_mirror-of-all-selves': 'Miroir de Tous les Soi',
    'room_the-ending': 'La Fin',
    // Phase 9
    'update_fragment_rarities': 'Raretés de Fragments - fragments collectibles Communs, Rares et Légendaires',
    'update_fragment_gallery': 'Galerie de Fragments - vitrine visuelle pour votre collection',
    'update_seasonal_fragments': 'Fragments saisonniers et secrets - collectibles à durée limitée',
    'fragment_collection': 'Collection',
    'fragment_collection_title': 'Collection de Fragments',
    'fragment_collection_subtitle': 'Chaque salle laisse quelque chose derrière. Collectionnez-les tous.',
    'collection_milestones': 'Jalons',
    'collection_secrets': 'Secrets',
    'collection_footer': 'Certains fragments n\'apparaissent que dans certaines conditions.',
    'rarity_common': 'Commun',
    'rarity_rare': 'Rare',
    'rarity_legendary': 'Légendaire',
    // Phase 10-11
    'accessibility': 'Accessibilité',
    'accessibility_desc': 'Personnaliser la lisibilité et les préférences de mouvement',
    'font_family': 'Famille de Police',
    'font_default': 'Monospace',
    'font_dyslexia': 'Adaptée à la Dyslexie',
    'font_sans': 'Sans Empattement',
    'font_changed': 'Police mise à jour',
    'line_height': 'Hauteur de Ligne',
    'line_compact': 'Compact',
    'line_normal': 'Normal',
    'line_relaxed': 'Détendu',
    'line_spacious': 'Spacieux',
    'line_height_changed': 'Hauteur de ligne mise à jour',
    'reduced_motion_full_desc': 'Désactiver toutes les animations et effets de particules',
    'larger_cursors': 'Cibles Plus Grandes',
    'larger_cursors_desc': 'Augmenter la taille des cibles de clic',
    'update_accessibility_suite': 'Suite d\'Accessibilité - polices pour dyslexie, contrôle de hauteur de ligne',
    'update_dyslexia_font': 'Option de police adaptée à la dyslexie dans les paramètres',
    'update_line_height': 'Hauteur de ligne ajustable pour une meilleure lisibilité',
    'update_enhanced_reduced_motion': 'Mouvement réduit amélioré - désactive complètement tous les effets',
    'update_shared_moments': 'Moments Partagés - bilans émotionnels anonymes',
    'update_whisper_wall': 'Le Mur des Murmures - laissez des vérités anonymes dans l\'obscurité',
    'update_collective_memory': 'Mémoire Collective - fragments partagés de ce dont nous nous souvenons',
    'whisper_prompt': 'Laissez un murmure sur le mur. Anonyme. Permanent.',
    'whisper_placeholder': 'Que portez-vous en silence ?',
    'whisper_send': 'Murmurer',
    'whisper_added': 'Votre murmure rejoint le mur.',
    'whisper_another': 'Murmurer encore',
    'shared_moments_title': 'En ce moment même...',
    'shared_moments_subtitle': 'Comment allez-vous, honnêtement ?',
    'feeling_grateful': 'Reconnaissant',
    'feeling_lost': 'Perdu',
    'feeling_hopeful': 'Plein d\'espoir',
    'feeling_heavy': 'Lourd',
    'feeling_peaceful': 'En paix',
    'feeling_restless': 'Agité',
    'room_whisper-wall': 'Le Mur des Murmures',
    'room_collective-memory': 'Mémoire Collective',
    'room_shared-moments': 'Moments Partagés',
    'sub_regret-machine': 'Explorez ce qui aurait pu être',
    'sub_memory-reconstruction': 'Reconstruisez un souvenir qui s\'estompe',
    'sub_whisper-wall': 'Des vérités anonymes flottent dans l\'obscurité',
    'sub_collective-memory': 'Fragments partagés de ce dont nous nous souvenons',
    'sub_shared-moments': 'Comment allez-vous, honnêtement?',
    'sub_the-archive': 'Votre historique complet de voyage',
    'sub_mirror-of-all-selves': 'L\'IA révèle votre portrait comportemental',
    'sub_the-ending': 'Un adieu que vous avez mérité',

    // v3.21.0 - New Rooms
    'room_mask-you-wear': 'Le Masque que Vous Portez',
    'sub_mask-you-wear': 'Quel visage montrez-vous ?',
    'room_recurring-dream': 'Votre Rêve Récurrent',
    'sub_recurring-dream': 'Le rêve qui revient sans cesse',
    'room_promise-you-broke': 'La Promesse que Vous Avez Brisée',
    'sub_promise-you-broke': 'Honnêteté sur les promesses brisées',
    'room_words-you-swallowed': 'Les Mots Avalés',
    'sub_words-you-swallowed': 'Ce que vous n\'avez jamais dit',
    'room_age-you-feel': 'L\'Âge que Vous Ressentez',
    'sub_age-you-feel': 'Quel âge avez-vous vraiment ?',
    'room_biggest-lie': 'Votre Plus Grand Mensonge',
    'sub_biggest-lie': 'Le mensonge que vous dites le plus',
    'room_person-you-pretend-to-be': 'La Personne que Vous Prétendez Être',
    'sub_person-you-pretend-to-be': 'Votre performance',
    'room_person-you-pretend': 'La Personne que Vous Prétendez Être',
    'sub_person-you-pretend': 'Votre performance',
    'room_what-you-collect': 'Ce que Vous Collectionnez',
    'sub_what-you-collect': 'Les objets révèlent l\'identité',
    'room_apology-you-owe': 'Les Excuses que Vous Devez',
    'sub_apology-you-owe': 'Écrivez ce que vous auriez dû dire',
    'room_unfinished-story': 'Votre Histoire Inachevée',
    'sub_unfinished-story': 'Ce que vous n\'avez jamais terminé',
    'room_version-others-see': 'La Version que les Autres Voient',
    'sub_version-others-see': 'Comment les autres vous perçoivent',
    'room_secret-talent': 'Votre Talent Secret',
    'sub_secret-talent': 'Ce que personne ne sait que vous faites',
    'room_fear-you-feed': 'La Peur que Vous Nourrissez',
    'sub_fear-you-feed': 'Quelle peur vous contrôle ?',
    'room_guilty-pleasure': 'Votre Plaisir Coupable',
    'sub_guilty-pleasure': 'Ce que vous cachez apprécier',
    'room_rule-you-break': 'La Règle que Vous Brisez Toujours',
    'sub_rule-you-break': 'Votre schéma de rébellion',
    'room_childhood-promise': 'Votre Promesse d\'Enfance',
    'sub_childhood-promise': 'Ce que vous vous êtes promis',
    'room_compliment-you-reject': 'Le Compliment que Vous Rejetez',
    'sub_compliment-you-reject': 'Pourquoi vous ne pouvez pas accepter les éloges',
    'room_autopilot-mode': 'Votre Mode Pilote Automatique',
    'sub_autopilot-mode': 'Ce que vous faites sans réfléchir',
    'room_boundary-you-need': 'La Limite dont Vous Avez Besoin',
    'sub_boundary-you-need': 'Ce que vous devez protéger',
    'room_inner-critic': 'Votre Critique Intérieur',
    'sub_inner-critic': 'La voix qui juge',
    'room_last-sunday': 'Dimanche Dernier',
    'sub_last-sunday': 'Qu\'avez-vous vraiment fait ?',
    'room_wasted-hours': 'Vos Heures Perdues',
    'sub_wasted-hours': 'Où le temps disparaît',
    'room_year-that-changed': 'L\'Année qui Vous a Changé',
    'sub_year-that-changed': 'Le tournant',
    'room_tomorrows-regret': 'Le Regret de Demain',
    'sub_tomorrows-regret': 'Que souhaiteriez-vous avoir fait ?',
    'room_morning-autopilot': 'Votre Pilote Automatique du Matin',
    'sub_morning-autopilot': 'Le rituel de la première heure',
    'room_season-you-dread': 'La Saison que Vous Redoutez',
    'sub_season-you-dread': 'Quelle saison pèse le plus ?',
    'room_ten-years-ago': 'Il y a Dix Ans',
    'sub_ten-years-ago': 'Qui étiez-vous alors ?',
    'room_last-normal-day': 'Votre Dernier Jour Normal',
    'sub_last-normal-day': 'Avant que tout change',
    'room_clock-you-ignore': 'L\'Horloge que Vous Ignorez',
    'sub_clock-you-ignore': 'Le temps que vous prétendez ne pas passer',
    'room_your-deadline': 'Votre Date Limite',
    'sub_your-deadline': 'Que reportez-vous ?',
    'room_the-pause': 'La Pause',
    'sub_the-pause': 'Respirez un instant',
    'room_static-room': 'Statique',
    'sub_static-room': 'Bruit blanc et immobilité',
    'room_gap-between-thoughts': 'L\'Espace Entre les Pensées',
    'sub_gap-between-thoughts': 'Trouvez l\'espace',
    'room_sit-with-discomfort': 'Asseyez-vous avec l\'Inconfort',
    'sub_sit-with-discomfort': 'Ne réparez rien',
    'room_boring-room': 'La Chambre Ennuyeuse',
    'sub_boring-room': 'Rien d\'intéressant du tout',
    'room_unplug-room': 'Débrancher',
    'sub_unplug-room': 'Imaginez sans appareils',
    'room_unplug': 'Débrancher',
    'sub_unplug': 'Imaginez sans appareils',
    'room_last-page': 'La Dernière Page',
    'sub_last-page': 'L\'histoire se termine ici',
    'room_scrolling-pattern': 'Votre Défilement',
    'sub_scrolling-pattern': 'Comment vous consommez le contenu',
    'room_tab-you-keep-open': 'L\'Onglet Toujours Ouvert',
    'sub_tab-you-keep-open': 'Ce qui reste dans votre navigateur',
    'room_digital-shadow': 'Votre Ombre Numérique',
    'sub_digital-shadow': 'Votre empreinte numérique',
    'room_notification-you-ignore': 'La Notification Ignorée',
    'sub_notification-you-ignore': 'Ce que vous rejetez',
    'room_typing-speed': 'Votre Vitesse de Frappe',
    'sub_typing-speed': 'À quelle vitesse vous exprimez-vous ?',
    'room_link-you-almost-clicked': 'Le Lien Presque Cliqué',
    'sub_link-you-almost-clicked': 'Schémas d\'hésitation',
    'room_bookmark-graveyard': 'Votre Cimetière de Favoris',
    'sub_bookmark-graveyard': 'Sauvegardés mais jamais revisités',
    'room_search-you-deleted': 'La Recherche Supprimée',
    'sub_search-you-deleted': 'Ce que vous avez cherché et effacé',
    'room_gravity-room': 'Salle de Gravité',
    'sub_gravity-room': 'Tout tombe lentement',
    'room_ink-blot': 'Tache d\'Encre',
    'sub_ink-blot': 'Que voyez-vous ?',
    'room_hallway-of-mirrors': 'Le Couloir des Miroirs',
    'sub_hallway-of-mirrors': 'Reflets infinis',
    'room_dissolving-text': 'Texte qui Se Dissout',
    'sub_dissolving-text': 'Les mots fondent pendant la lecture',
    'room_the-pendulum': 'Le Pendule',
    'sub_the-pendulum': 'Mouvement hypnotique',
    'room_pixel-rain': 'Pluie de Pixels',
    'sub_pixel-rain': 'Précipitation numérique',
    'room_the-threshold': 'Le Seuil',
    'sub_the-threshold': 'Entre ici et là-bas',
    'most_loved_rooms': 'Chambres les Plus Aimées',
    'rate_room': 'Évaluez cette chambre',
    'update_52_new_rooms': '52 nouvelles chambres dans les catégories Identité, Temps, Contemplation, Méta et Expérimental',
    'update_room_rating': 'Système de notation - pouce haut/bas avec section Chambres les Plus Aimées',
    'update_bug_fix_reflections': 'Correction de l\'affichage et du stockage des réflexions anonymes',
    'update_100_rooms': 'Plus de 100 chambres disponibles dans le Couloir',
    // v3.22.0
    'room_the-friend-you-lost': "L'Ami Que Vous Avez Perdu",
    'sub_the-friend-you-lost': "Écrivez à quelqu'un dont vous vous êtes éloigné.",
    'room_conversation-you-replay': 'La Conversation Que Vous Rejouez',
    'sub_conversation-you-replay': 'Recréez-la de mémoire.',
    'room_the-thank-you': 'Le Merci',
    'sub_the-thank-you': "Gratitude que vous n'avez jamais exprimée.",
    'room_people-you-forgot': 'Les Gens Que Vous Avez Oubliés',
    'sub_people-you-forgot': 'Essayez de vous rappeler des noms oubliés.',
    'room_the-advice-you-give': 'Le Conseil Que Vous Donnez',
    'sub_the-advice-you-give': 'Un conseil que vous donnez mais ne suivez jamais.',
    'room_what-others-dont-know': 'Ce Que Les Autres Ne Savent Pas',
    'sub_what-others-dont-know': 'Vos couches cachées.',
    'room_the-lie-you-believe': 'Le Mensonge Que Vous Croyez',
    'sub_the-lie-you-believe': 'Une contre-vérité réconfortante.',
    'room_your-shadow-self': "Votre Moi de l'Ombre",
    'sub_your-shadow-self': 'La partie que vous réprimez.',
    'room_the-story-you-tell': "L'Histoire Que Vous Racontez",
    'sub_the-story-you-tell': 'Comment vous narrez votre vie.',
    'room_your-safe-place': 'Votre Lieu Sûr',
    'sub_your-safe-place': 'Décrivez où vous vous sentez en sécurité.',
    'room_the-risk-you-didnt-take': "Le Risque Que Vous N'Avez Pas Pris",
    'sub_the-risk-you-didnt-take': 'Chemins non empruntés.',
    'room_your-last-excuse': 'Votre Dernière Excuse',
    'sub_your-last-excuse': "L'excuse que vous utilisez le plus.",
    'room_the-habit-you-hide': "L'Habitude Que Vous Cachez",
    'sub_the-habit-you-hide': 'Routines secrètes.',
    'room_what-you-postpone': 'Ce Que Vous Reportez',
    'sub_what-you-postpone': 'La chose toujours "demain".',
    'room_the-door-you-closed': 'La Porte Que Vous Avez Fermée',
    'sub_the-door-you-closed': 'Opportunités que vous avez rejetées.',
    'journal_title': 'Journal de Réflexion',
    'journal_subtitle': 'Vos pensées à travers les chambres',
    'journal_empty': "Pas encore de réflexions. Visitez des chambres et écrivez vos pensées.",
    'journal': 'Journal',
    'room_chains': 'Chaînes de Chambres',
    'chain_identity_journey': "Voyage d'Identité",
    'chain_time_reflection': 'Réflexion du Temps',
    'chain_truth_path': 'Chemin de la Vérité',
    'recently_visited': 'Récemment Visités',
    'update_reflection_journal': 'Journal de Réflexion - sauvegardez vos pensées entre les chambres',
    'update_room_chains': 'Chaînes de Chambres - complétez des chambres en séquence',
    'update_15_new_rooms': '15 nouvelles chambres interactives dans 3 catégories',
    'update_recently_visited': 'Section récemment visités pour un retour rapide',
    'ai_companion': 'Compagnon',
    'companion_create': 'Créez Votre Compagnon',
    'companion_setup_desc': 'Un reflet qui marche à vos côtés.',
    'companion_name': 'Nommez votre compagnon',
    'companion_name_placeholder': 'ex., Écho, Miroir, Ombre...',
    'companion_lens': 'Lentille de personnalité',
    'companion_mood': 'Ton de communication',
    'companion_fears': "De quoi avez-vous peur ? (optionnel)",
    'companion_struggles': 'Avec quoi luttez-vous ? (optionnel)',
    'create_companion': 'Créer',
    'companion_input': 'Dites quelque chose...',
    'companion_greeting': 'Votre compagnon est là. Dites quelque chose.',
    'companion_starter_1': "Qu'est-ce qui occupe mon esprit aujourd'hui ?",
    'companion_starter_2': 'Je me sens bloqué.',
    'companion_starter_3': "Dis-moi quelque chose que j'évite.",
    'fears_placeholder': "ex., l'échec, le rejet, être oublié...",
    'struggles_placeholder': 'ex., trop réfléchir, procrastiner, solitude...',
    'back_step': 'Retour',
    'lens_reflective': 'Réflectif',
    'lens_reflective_desc': 'Questions profondes, reconnaissance de schémas',
    'lens_supportive': 'Bienveillant',
    'lens_supportive_desc': 'Chaleureux, encourageant, validant',
    'lens_challenging': 'Stimulant',
    'lens_challenging_desc': 'Direct, provocateur',
    'lens_curious': 'Curieux',
    'lens_curious_desc': 'Questions inattendues, émerveillement',
    'lens_shadow': 'Ombre',
    'lens_shadow_desc': 'Le côté caché, inconfort honnête',
    'companion_mood_calm': 'Calme',
    'companion_mood_calm_desc': 'Mesuré et paisible',
    'companion_mood_intense': 'Intense',
    'companion_mood_intense_desc': 'Urgent et profond',
    'companion_mood_playful': 'Espiègle',
    'companion_mood_playful_desc': 'Spirituel et léger',
    'companion_mood_melancholic': 'Mélancolique',
    'companion_mood_melancholic_desc': 'Belle tristesse',
    'update_ai_companion': "Compagnon IA - une IA personnelle qui se souvient de votre voyage",
    // v3.23.0
    'mood_history': "Historique d'Humeur",
    'top_rooms': 'Vos Salles Préférées',
    'visits_count': 'visites',
    'set_intention': "Définissez une intention pour aujourd'hui",
    'todays_intention': "Intention du Jour",
    'intention_placeholder': 'Je vais...',
    'set': 'Définir',
    'update_mood_history': "Historique d'Humeur - suivez l'évolution de votre humeur",
    'update_daily_intention': 'Intention Quotidienne - définissez un focus pour chaque jour',
    'update_top_rooms': 'Salles Préférées - accès rapide à vos salles les plus visitées',
    'update_10_new_rooms_v323': '10 nouvelles salles : émotions, introspection et connexion',
    'room_the-emotion-you-hide': "L'Émotion que Vous Cachez",
    'sub_the-emotion-you-hide': 'Nommez-la. Juste une fois.',
    'emotion_hide_placeholder': "L'émotion que je ne montre jamais est...",
    'quote_emotion_hide': 'Ce que nous cachons aux autres, nous le cachons souvent à nous-mêmes.',
    'room_your-comfort-lie': 'Votre Mensonge Confortable',
    'sub_your-comfort-lie': 'Le petit mensonge qui vous garde confortable.',
    'comfort_lie_placeholder': 'Le mensonge que je me dis est...',
    'admit_it': 'Admettez-le',
    'quote_comfort_lie': 'Les mensonges confortables sont des murs que nous construisons en tissu doux.',
    'room_the-silence-between': 'Le Silence Entre',
    'sub_the-silence-between': "Asseyez-vous dans l'espace entre deux pensées.",
    'silence_instruction': "Fermez les yeux. Trouvez l'espace entre deux pensées. Puis appuyez sur le bouton.",
    'silence_sitting': 'Assis en silence...',
    'silence_found': "Je l'ai trouvé",
    'silence_result': 'Vous êtes resté',
    'seconds': 'secondes',
    'quote_silence_between': 'Entre chaque pensée se trouve un espace infini. Vous venez de le visiter.',
    'room_what-you-carry': 'Ce que Vous Portez',
    'sub_what-you-carry': 'Nommez le poids invisible.',
    'carry_placeholder': 'Le poids que je porte est...',
    'set_it_down': 'Posez-le',
    'carry_released': "Pour un instant, c'était plus léger.",
    'quote_carry': 'Vous ne pouvez pas poser ce que vous refusez de nommer.',
    'room_the-version-you-lost': 'La Version que Vous Avez Perdue',
    'sub_the-version-you-lost': "Qui vous étiez avant que la vie n'arrive.",
    'version_lost_placeholder': "Avant que tout ne change, j'étais...",
    'remember': 'Se souvenir',
    'quote_version_lost': "Vous ne les avez pas perdus. Ils sont devenus partie de ce que vous êtes maintenant.",
    'room_letter-to-past-self': 'Lettre au Moi du Passé',
    'sub_letter-to-past-self': 'Écrivez à qui vous étiez.',
    'letter_past_placeholder': 'Cher moi plus jeune,...',
    'send_letter': 'Envoyer',
    'letter_sent': "La lettre a été envoyée. Quelque part, ils l'ont reçue.",
    'quote_letter_past': "Chaque mot de réconfort que vous écrivez à votre moi passé est en réalité pour qui vous êtes maintenant.",
    'room_the-moment-you-knew': 'Le Moment où Vous Avez Su',
    'sub_the-moment-you-knew': "L'instant où tout a changé.",
    'moment_knew_placeholder': "Le moment où j'ai su, c'est quand...",
    'mark_it': 'Marquez-le',
    'quote_moment_knew': "Certains moments divisent votre vie en avant et après. Vous venez d'en nommer un.",
    'room_the-words-you-rehearse': 'Les Mots que Vous Répétez',
    'sub_the-words-you-rehearse': 'Ce que vous pratiquez à dire mais ne faites jamais.',
    'words_rehearse_placeholder': 'Les mots que je répète sont...',
    'say_them': 'Dites-les',
    'words_spoken': "Vous les avez enfin dits. Même si seulement ici.",
    'quote_words_rehearse': 'Les mots répétés sont des cages. Les mots prononcés sont des oiseaux.',
    'room_someone-thinks-of-you': 'Quelqu\'un Pense à Vous',
    'sub_someone-thinks-of-you': 'En ce moment, quelque part, quelqu\'un se souvient de vous.',
    'someone_line_1': 'En ce moment, quelque part, quelqu\'un se souvient de vous.',
    'someone_line_2': 'Pas pour ce que vous avez fait.',
    'someone_line_3': 'Mais pour ce que vous leur avez fait ressentir.',
    'quote_someone_thinks': 'Vous existez dans des souvenirs dont vous ignorez l\'existence.',
    'room_the-question-you-fear': 'La Question que Vous Craignez',
    'sub_the-question-you-fear': 'La question que vous espérez que personne ne pose.',
    'question_fear_placeholder': 'La question que je crains le plus est...',
    'quote_question_fear': 'La question que vous craignez le plus est généralement celle qui a la réponse la plus importante.',
    // v3.24.0
    'behavior_insight': 'Le site remarque',
    'insight_pattern': 'Vous êtes attiré par {category}. Vous explorez le plus le {time}.',
    'cat_identity': "l'identité",
    'cat_truth': 'la vérité',
    'cat_time': 'le temps',
    'cat_mortality': 'la mortalité',
    'cat_stillness': 'la quietude',
    'cat_darkness': "l'obscurité",
    'cat_patterns': 'les motifs',
    'cat_fear': 'la peur',
    'cat_play': 'le jeu',
    'cat_experiments': 'les expériences',
    'cat_ritual': 'le rituel',
    'cat_secrets': 'les secrets',
    'time_late_night': 'tard le soir',
    'time_morning': 'matin',
    'time_afternoon': 'après-midi',
    'time_evening': 'soir',
    
    'anonymous_visitor': 'un visiteur',
    'update_behavior_insight': 'Aperçu Comportemental - le site vous reflète vos schémas',
    'update_anonymous_reflections_feed': 'Réflexions Anonymes - voyez ce que les autres ont ressenti',
    'update_daily_prompt': 'Réflexion Quotidienne - une pensée à emporter avec vous',
    'room_recurring-dream-journal': 'Journal de Rêves Récurrents', 'sub_recurring-dream-journal': 'Le rêve qui revient.', 'dream_journal_placeholder': 'Le rêve que je fais souvent...', 'record_dream': 'Enregistrer', 'dream_journal_reflection': 'Les rêves disent ce que l\'esprit éveillé ne dit pas.', 'quote_dream_journal': 'Les rêves récurrents sont des conversations inachevées avec soi-même.',
    'room_lucid-moment': 'Le Moment Lucide', 'sub_lucid-moment': 'Prenez conscience dans le rêve.', 'lucid_line_1': 'Vous lisez ces mots.', 'lucid_line_2': 'Mais êtes-vous éveillé ?', 'lucid_line_3': 'Comment sauriez-vous la différence ?', 'become_aware': 'Je suis conscient', 'lucid_aware': 'Ce moment de conscience est la seule chose réelle.', 'quote_lucid_moment': 'Le moment où vous questionnez la réalité est celui où vous la voyez vraiment.',
    'room_nightmare-you-remember': 'Le Cauchemar Dont Vous Vous Souvenez', 'sub_nightmare-you-remember': 'Celui qui vous hante encore.', 'nightmare_placeholder': 'Le cauchemar que je ne peux pas oublier...', 'face_it': 'Affrontez-le', 'nightmare_reflection': 'En l\'écrivant, vous lui avez pris du pouvoir.', 'quote_nightmare': 'Les cauchemars sont des peurs qui insistent pour être vues.',
    'room_dream-you-wish-was-real': 'Le Rêve Que Vous Souhaiteriez Réel', 'sub_dream-you-wish-was-real': 'Réveillez-vous. Ce n\'était pas réel.', 'dream_wish_placeholder': 'J\'ai rêvé que...', 'hold_onto_it': 'Retenez-le', 'quote_dream_wish': 'Les rêves que nous regrettons de perdre révèlent la vie que nous voulons vraiment.',
    'room_the-dream-visitor': 'Le Visiteur du Rêve', 'sub_the-dream-visitor': 'Quelqu\'un apparaît sans cesse dans vos rêves.', 'dream_visitor_line_1': 'Il y a quelqu\'un qui vous visite en rêve.', 'dream_visitor_line_2': 'Vous ne l\'avez pas invité.', 'dream_visitor_line_3': 'Mais il revient toujours.', 'dream_visitor_line_4': 'Peut-être essaie-t-il de vous dire quelque chose.', 'quote_dream_visitor': 'Les personnes qui apparaissent dans nos rêves sont des messagers du subconscient.',
    'room_the-blank-canvas': 'La Toile Vierge', 'sub_the-blank-canvas': 'Créez quelque chose à partir de rien.', 'blank_canvas_placeholder': 'Sur cette toile vierge, je crée...', 'create': 'Créer', 'quote_blank_canvas': 'Toute création commence par le courage de faire face au vide.',
    'room_song-that-is-you': 'La Chanson Qui Est Vous', 'sub_song-that-is-you': 'Si vous étiez une chanson, laquelle ?', 'song_placeholder': 'La chanson qui est moi...', 'play_it': 'Jouez-la', 'quote_song': 'La musique est le son des sentiments.',
    'room_color-of-today': 'La Couleur d\'Aujourd\'hui', 'sub_color-of-today': 'Choisissez la couleur de votre état actuel.', 'color_chosen': 'Voici comment vous vous sentez aujourd\'hui. En couleur.', 'quote_color_today': 'Les couleurs n\'ont pas besoin de mots. Les sentiments non plus.',
    'room_the-unwritten-poem': 'Le Poème Non Écrit', 'sub_the-unwritten-poem': 'Écrivez ce que vous gardiez en vous.', 'poem_placeholder': 'Des mots qui attendaient...', 'publish_it': 'Publiez-le', 'quote_unwritten_poem': 'Le poème était toujours là. Vous l\'avez simplement laissé sortir.',
    'room_your-inner-voice': 'Votre Voix Intérieure', 'sub_your-inner-voice': 'Que dit-elle vraiment ?', 'inner_voice_placeholder': 'Ma voix intérieure dit...', 'listen': 'Écoutez', 'inner_voice_reflection': 'Maintenant que vous l\'avez entendue, que ferez-vous ?', 'quote_inner_voice': 'Votre voix intérieure a toujours parlé. Vous avez juste commencé à écouter.',
    'room_fear-you-named': 'La Peur Que Vous Avez Nommée', 'sub_fear-you-named': 'Nommez-la et elle perd son pouvoir.', 'fear_named_placeholder': 'La peur que je nomme enfin...', 'name_it': 'Nommez-la', 'fear_named_reflection': 'Les peurs nommées rétrécissent. Celles sans nom grandissent.', 'quote_fear_named': 'Ce que vous pouvez nommer, vous pouvez affronter.',
    'room_the-leap': 'Le Saut', 'sub_the-leap': 'Dans quoi sauteriez-vous ?', 'leap_placeholder': 'Si je pouvais sauter...', 'leap': 'Sautez', 'quote_leap': 'Le filet apparaît après le saut, pas avant.',
    'room_courage-inventory': 'Inventaire du Courage', 'sub_courage-inventory': 'Listez chaque chose courageuse que vous avez faite.', 'courage_placeholder': 'Quelque chose de courageux que j\'ai fait...', 'courage_count': 'Vous avez listé {count} actes de courage.', 'quote_courage': 'Vous êtes plus courageux que vous ne le pensez.',
    'room_what-scares-you-most': 'Ce Qui Vous Effraie Le Plus', 'sub_what-scares-you-most': 'La peur la plus profonde.', 'scares_placeholder': 'Ce qui me fait le plus peur est...', 'quote_scares': 'Votre peur la plus profonde est souvent votre plus grand professeur.',
    'room_facing-the-dark': 'Face à l\'Obscurité', 'sub_facing-the-dark': 'Asseyez-vous dans l\'obscurité totale.', 'darkness_counting': '{seconds} secondes dans l\'obscurité...', 'return_to_light': 'Revenir à la lumière', 'darkness_survived': 'Vous êtes resté dans l\'obscurité pendant {seconds} secondes.', 'quote_facing_dark': 'Dans l\'obscurité, nous découvrons ce que nous portons en nous.',
    'room_letter-to-future-self': 'Lettre à Votre Futur Vous', 'sub_letter-to-future-self': 'Écrivez à qui vous deviendrez.', 'letter_future_placeholder': 'Cher moi futur,...', 'seal_letter': 'Sceller', 'letter_future_sealed': 'La lettre est scellée. Un jour, vous la lirez.', 'quote_letter_future': 'Chaque lettre au futur est une promesse au présent.',
    'room_the-world-you-want': 'Le Monde Que Vous Voulez', 'sub_the-world-you-want': 'Décrivez le monde que vous souhaiteriez.', 'world_placeholder': 'Le monde que je veux...', 'build_it': 'Construisez-le', 'quote_world_want': 'Le monde que vous imaginez est le premier pas vers le monde que vous créez.',
    'room_your-next-chapter': 'Votre Prochain Chapitre', 'sub_your-next-chapter': 'Que se passe-t-il ensuite dans votre histoire ?', 'next_chapter_placeholder': 'Dans mon prochain chapitre...', 'turn_the_page': 'Tournez la page', 'quote_next_chapter': 'Vous êtes à la fois l\'auteur et le protagoniste.',
    'room_the-wish': 'Le Souhait', 'sub_the-wish': 'Un souhait. Sans règles.', 'wish_placeholder': 'Je souhaite...', 'make_wish': 'Faites le souhait', 'quote_wish': 'Ce que vous souhaitez révèle ce que vous valorisez le plus.',
    'room_if-you-had-one-year': 'Si Vous Aviez Un An', 'sub_if-you-had-one-year': 'Que feriez-vous en 365 jours ?', 'one_year_placeholder': 'S\'il me restait un an...', 'begin_now': 'Commencez maintenant', 'one_year_reflection': 'Alors pourquoi ne le faites-vous pas ?', 'quote_one_year': 'Vivez comme si vous aviez un an. Car on ne sait jamais.',
    'update_v4_languages': '4 nouvelles langues : Hindi, Coréen, Japonais, Turc', 'update_v4_20_rooms': '20 nouvelles salles : Rêves, Créativité, Peur et Avenir', 'update_v4_dreams_category': 'Catégorie Rêves et Subconscient', 'update_v4_creativity_category': 'Catégorie Créativité et Expression', 'update_v4_fear_category': 'Catégorie Peur et Courage', 'update_v4_future_category': 'Catégorie Avenir et Espoir',
    'room_playlists': 'Listes de Salles', 'personal_dashboard': 'Tableau de bord', 'dashboard_subtitle': 'Votre parcours en un coup d\'œil', 'visit_pattern': 'Schéma de visites (14 jours)', 'dashboard_top_rooms': 'Salles les plus visitées', 'category_breakdown': 'Catégories de salles', 'mood_trend': 'Tendance d\'humeur', 'fragment_breakdown': 'Répartition des fragments', 'no_data_yet': 'Pas encore de données', 'current_streak': 'Série de jours',
    'playlist_morning': 'Réflexion Matinale', 'playlist_morning_desc': 'Commencez votre journée avec clarté', 'playlist_identity': 'Plongée Identitaire', 'playlist_identity_desc': 'Qui êtes-vous sous les étiquettes ?', 'playlist_creative': 'Étincelle Créative', 'playlist_creative_desc': 'Libérez votre expression créative', 'playlist_courage': 'Chemin du Courage', 'playlist_courage_desc': 'Affrontez vos peurs salle par salle', 'playlist_dreams': 'Journal des Rêves', 'playlist_dreams_desc': 'Explorez les paysages de votre subconscient', 'playlist_future': 'Vision du Futur', 'playlist_future_desc': 'Lettres et vœux pour votre futur vous', 'playlist_truth': 'Confrontation de Vérité', 'playlist_truth_desc': 'Affrontez les réalités inconfortables', 'playlist_night': 'Voyage Nocturne', 'playlist_night_desc': 'Embrassez le silence, le vide et le néant',
    // v4.2.0
    'room_focus-timer': 'Minuteur de Concentration', 'sub_focus-timer': 'Asseyez-vous en silence. Réfléchissez.',
    'room_gratitude-jar': 'Bocal de Gratitude', 'sub_gratitude-jar': 'Une chose pour laquelle vous êtes reconnaissant',
    'room_mood-compass': 'Boussole d\'Humeur', 'sub_mood-compass': 'Où êtes-vous en ce moment ?',
    'room_memory-fade': 'Mémoire qui S\'efface', 'sub_memory-fade': 'Regardez un souvenir se dissoudre',
    'room_sound-of-your-day': 'Son de Votre Journée', 'sub_sound-of-your-day': 'Comment sonne aujourd\'hui ?',
    'room_the-apology-room': 'La Salle des Excuses', 'sub_the-apology-room': 'Écrivez ce que vous n\'avez jamais dit',
    'room_your-unread-messages': 'Vos Messages Non Lus', 'sub_your-unread-messages': 'Messages que vous n\'avez jamais ouverts',
    'room_the-weight-room': 'La Salle du Poids', 'sub_the-weight-room': 'Nommez ce qui vous pèse',
    'room_parallel-lives': 'Vies Parallèles', 'sub_parallel-lives': 'Dans une autre vie, vous êtes...',
    'room_the-quiet-score': 'Le Score du Silence', 'sub_the-quiet-score': 'Combien de temps pouvez-vous rester immobile ?',
    'focus_choose': 'Combien de temps voulez-vous rester en silence ?', 'focus_sit': 'Asseyez-vous. Respirez. Laissez passer les pensées.', 'focus_done': 'Le silence a parlé.', 'quote_focus_timer': 'Dans l\'immobilité, vous entendez ce que le bruit étouffe.', 'minutes': 'min',
    'gratitude_prompt': 'Écrivez une chose pour laquelle vous êtes reconnaissant aujourd\'hui.', 'gratitude_placeholder': 'Je suis reconnaissant pour...', 'gratitude_add': 'Ajouter au bocal', 'gratitude_added': 'Votre gratitude flotte dans le bocal.', 'gratitude_count': 'moments de gratitude', 'quote_gratitude_jar': 'La gratitude n\'est pas avoir plus. C\'est remarquer ce qui est déjà là.',
    'mood_compass_prompt': 'Cliquez où vous sentez que vous êtes.', 'mood_high_energy': 'Haute Énergie', 'mood_low_energy': 'Basse Énergie', 'mood_positive': 'Positif', 'mood_negative': 'Négatif', 'quote_mood_compass': 'Savoir où vous êtes est la première étape pour savoir où aller.',
    'memory_fade_prompt': 'Tapez un souvenir. Regardez-le se dissoudre.', 'memory_fade_placeholder': 'Un souvenir que vous voulez libérer...', 'memory_fade_start': 'Laissez-le s\'effacer', 'memory_fade_done': 'C\'est parti maintenant. Mais c\'était réel.', 'quote_memory_fade': 'Les souvenirs se dissolvent que nous regardions ou non.',
    'sound_day_prompt': 'Choisissez 3-5 sons qui représentent votre journée.', 'sound_rain': 'Pluie', 'sound_traffic': 'Circulation', 'sound_birds': 'Oiseaux', 'sound_keyboard': 'Clavier', 'sound_silence': 'Silence', 'sound_wind': 'Vent', 'sound_ocean': 'Océan', 'sound_crowd': 'Foule', 'sound_music': 'Musique', 'sound_day_confirm': 'C\'est ma journée', 'sound_day_result': 'La bande sonore de votre journée.', 'quote_sound_day': 'Chaque jour a un son. La plupart oublient d\'écouter.',
    'apology_prompt': 'Écrivez des excuses que vous n\'avez jamais présentées.', 'apology_placeholder': 'Je m\'excuse pour...', 'apology_release': 'Libérez-le', 'apology_done': 'Compressé en rien. Mais vous l\'avez dit.', 'quote_apology': 'Certaines excuses ne sont pas pour eux. Elles sont pour vous.',
    'unread_from_past': 'De : Votre Vous Passé', 'unread_from_future': 'De : Votre Vous Futur', 'unread_from_critic': 'De : Votre Critique Intérieur', 'unread_msg_past': 'Vous souvenez-vous quand vous vous êtes promis que les choses seraient différentes ?', 'unread_msg_future': 'J\'espère que vous êtes plus doux avec vous-même maintenant.', 'unread_msg_critic': 'Vous savez que vous auriez pu faire plus. Mais le ferez-vous ?', 'unread_count': 'non lus', 'unread_reply_placeholder': 'Votre réponse...', 'unread_reply': 'Envoyer', 'unread_sent': 'Message envoyé dans le vide.', 'quote_unread_messages': 'Les messages que nous évitons sont souvent ceux dont nous avons le plus besoin.',
    'weight_prompt': 'Nommez les choses qui vous pèsent.', 'weight_placeholder': 'Quelque chose qui me pèse...', 'weight_release_start': 'Commencer à lâcher', 'weight_release_prompt': 'Cliquez sur chaque poids pour le relâcher.', 'weight_done': 'Le poids s\'est levé. Pour l\'instant.', 'quote_weight_room': 'Vous ne pouvez pas poser ce que vous refusez de nommer.',
    'parallel_intro': 'Dans une autre vie, vous êtes...', 'parallel_life_1': 'Dans une autre vie, vous êtes gardien de phare sur une île isolée.', 'parallel_life_2': 'Dans une autre vie, vous n\'avez jamais quitté votre ville natale.', 'parallel_life_3': 'Dans une autre vie, vous avez dit oui quand vous avez dit non.', 'parallel_life_4': 'Dans une autre vie, vous êtes quelqu\'un d\'autre.', 'parallel_life_5': 'Dans une autre vie, vous êtes exactement qui vous êtes.', 'parallel_life_6': 'Dans une autre vie, vous avez choisi le chemin le plus difficile.', 'parallel_life_7': 'Dans une autre vie, vous cherchez encore cette salle.', 'quote_parallel_lives': 'Chaque choix crée un univers où vous avez choisi différemment.',
    'quiet_instruction': 'N\'interagissez pas. Restez simplement immobile.', 'quiet_reveal_1': 'Vous êtes ici.', 'quiet_reveal_2': 'Le bruit s\'estompe.', 'quiet_reveal_3': 'Quelque chose émerge du silence.', 'quiet_reveal_4': 'Vous êtes plus patient que vous ne le pensez.', 'quiet_reveal_5': 'L\'immobilité connaît votre nom.', 'quiet_score_label': 'Votre score de silence', 'quiet_impressive': 'Immobilité remarquable.', 'quiet_good': 'Vous avez trouvé du silence.', 'quiet_try_again': 'L\'envie d\'agir est forte.', 'quote_quiet_score': 'L\'immobilité n\'est pas l\'absence de mouvement. C\'est la présence de conscience.',
    'update_v42_10_rooms': '10 nouvelles salles : Minuteur, Gratitude, Boussole et plus', 'update_v42_focus_timer': 'Minuteur de Concentration — asseyez-vous en silence', 'update_v42_gratitude_jar': 'Bocal de Gratitude — collectionnez les moments de gratitude', 'update_v42_mood_compass': 'Boussole d\'Humeur — positionnez-vous sur les axes énergie et valence', 'update_v42_weight_room': 'La Salle du Poids — nommez et libérez ce qui vous pèse',
    'update_v43_full_localization': 'Localisation complète — toutes les salles entièrement traduites', 'update_v43_40_rooms_translated': '40+ salles traduites — chaque salle interactive supporte toutes les langues', 'update_v43_10_languages': '10 langues supportées — anglais, arabe, espagnol, portugais, chinois, français, hindi, coréen, japonais, turc', 'update_v43_nav_consistency': 'Navigation cohérente — texte de sortie unifié dans toutes les salles',
    'mirror_lies_title': 'Le Miroir Ment', 'mirror_lies_msg1': 'Regardez-vous.', 'mirror_lies_msg2': 'Est-ce vraiment vous ?', 'mirror_lies_msg3': 'Le miroir montre ce que vous voulez voir.', 'mirror_lies_msg4': 'Cliquez pour révéler la vérité.', 'mirror_lies_msg5': 'Chaque fissure révèle plus.', 'mirror_lies_msg6': 'Il n\'y a rien derrière le reflet.', 'mirror_lies_quote': 'Le miroir ne vous a jamais montré. Seulement ce que vous croyiez.',
    'winter_title': 'La Plus Longue Nuit', 'winter_date': '21 DÉCEMBRE', 'winter_msg1': 'La plus longue nuit est là.', 'winter_msg2': 'Dans l\'obscurité, nous trouvons ce que nous portons.', 'winter_msg3': 'Que libérerez-vous ? Que garderez-vous ?', 'winter_releasing': 'Je libère :', 'winter_keeping': 'Je garde :', 'winter_release_placeholder': 'Ce qui ne vous sert plus...', 'winter_keep_placeholder': 'Ce qui vous donne la force...', 'winter_enter_light': 'Entrez dans la lumière', 'winter_released': 'Libéré :', 'winter_kept': 'Gardé :', 'winter_light_returns': 'À partir de cette nuit, la lumière revient.', 'winter_quote': 'La plus longue nuit nous apprend que même dans l\'obscurité la plus profonde, la lumière revient déjà.',
    'halloween_title': 'La Salle du Voile', 'halloween_date': '31 OCTOBRE', 'halloween_msg1': 'Le voile est mince ce soir.', 'halloween_msg2': 'Que rôde dans vos ombres ?', 'halloween_msg3': 'Nommez la peur que vous portez...', 'halloween_msg4': 'Donnez-lui forme, et libérez-la.', 'halloween_placeholder': 'Tapez votre peur...', 'halloween_release': 'Libérez-la', 'halloween_dissolves': 'Votre peur se dissout dans la nuit...', 'halloween_quote': 'Les choses que nous craignons perdent leur pouvoir quand nous les nommons. Laissez-les partir.',
    'awake_title': 'Ce Qui Vous Empêche de Dormir', 'awake_locked': 'Cette salle n\'est disponible qu\'après 22h.', 'awake_return': 'Revenez quand la nuit tombe.', 'awake_press_enter': 'Appuyez sur Entrée', 'awake_named': 'Maintenant vous l\'avez nommé.', 'awake_quote': 'L\'obscurité sait ce que vous évitez en plein jour.',
    'voice_meaning_title': 'Votre Voix Sans Signification', 'voice_meaning_prompt': 'Fredonnez. Respirez. Faites n\'importe quel son. Pas de mots.', 'voice_meaning_done': 'C\'était votre présence. Rien de plus.', 'voice_meaning_hold': 'Maintenez', 'voice_meaning_recording': 'Enregistrement...', 'voice_meaning_no_playback': 'Pas de lecture. Le son n\'a existé que dans cet instant.', 'voice_meaning_quote': 'Vous existez. C\'est le seul contenu.',
    'watching_msg1': 'Vous êtes seul ici.', 'watching_msg2': 'Complètement seul.', 'watching_msg3': 'Ou l\'êtes-vous ?', 'watching_msg4': 'Ils étaient là depuis le début.', 'watching_msg5': 'Ils regardent toujours.',
    'shortcut_title': 'Le Raccourci Que Vous Prenez Toujours', 'shortcut_instruction': 'Sélectionnez ceux que vous reconnaissez.', 'shortcut_1': 'Dire "ça va" quand ce n\'est pas le cas', 'shortcut_2': 'Vérifier son téléphone au lieu d\'être présent', 'shortcut_3': 'Rester silencieux pour éviter le conflit', 'shortcut_4': 'Acheter au lieu de faire', 'shortcut_5': 'Attendre le "bon moment"', 'shortcut_6': 'Blâmer les circonstances au lieu d\'agir', 'shortcut_7': 'Choisir le confort plutôt que la croissance', 'shortcut_8': 'Défiler au lieu de dormir', 'shortcut_9': 'Dire oui quand on veut dire non', 'shortcut_10': 'Planifier au lieu de faire', 'shortcut_pattern': 'Votre Modèle', 'shortcut_selected': 'Vous avez sélectionné :', 'shortcut_show_pattern': 'Montrer mon modèle', 'shortcut_quote': 'Le raccourci devient le chemin.', 'shortcut_pattern_own': 'Votre modèle est le vôtre.', 'pattern_avoidance': 'Vous évitez ce qui est inconfortable.', 'pattern_distraction': 'Vous fuyez dans la distraction.', 'pattern_convenience': 'Vous échangez l\'effort contre la facilité.', 'pattern_delay': 'Vous attendez des conditions qui ne viennent jamais.', 'pattern_comfort': 'Vous vous protégez de l\'inconfort.', 'pattern_pleasing': 'Vous disparaissez pour garder les autres à l\'aise.',
    'shapes_q1': 'À quelle fréquence vous sentez-vous en paix ?', 'shapes_q2': 'Combien vous faites-vous confiance ?', 'shapes_q3': 'À quel point vous sentez-vous connecté aux autres ?', 'shapes_q4': 'Combien de votre temps est vraiment le vôtre ?', 'shapes_q5': 'Combien le futur semble-t-il lourd ?', 'shapes_result_title': 'Votre Vie en Formes', 'shapes_no_labels': 'Pas d\'étiquettes. Pas d\'interprétation.', 'shapes_yours': 'Ce que vous voyez est à vous de comprendre.', 'shapes_quote': 'La forme est un sens sans mots.', 'shapes_scale': '1 = pas du tout, 5 = complètement',
    'read_title': 'Lisez Ceci à Voix Haute', 'read_stmt1': 'J\'évite quelque chose.', 'read_stmt2': 'J\'ai peur.', 'read_stmt3': 'Je ne sais pas ce que je fais.', 'read_stmt4': 'J\'ai besoin d\'aide.', 'read_stmt5': 'Je suis suffisant.', 'read_stmt6': 'J\'ai fait semblant.', 'read_stmt7': 'Je suis fatigué.', 'read_stmt8': 'Je veux plus.', 'read_i_read': 'Je l\'ai lu à voix haute', 'read_really': 'L\'avez-vous vraiment dit ?', 'read_skipped': 'Je l\'ai sauté', 'read_result': 'Vous avez dit des vérités à voix haute.', 'read_quote': 'Dire la vérité la rend réelle.',
    'sentence_title': 'La Phrase Que Vous Évitez', 'sentence_sub': 'Complétez cette phrase. Vous ne pouvez pas partir avant.', 'sentence_prompt': 'Je serais plus heureux si j\'arrêtais de', 'sentence_cant_leave': 'Vous ne pouvez pas partir sans écrire quelque chose.', 'sentence_knew': 'Vous le saviez déjà.', 'sentence_quote': 'Le premier pas est de le dire.',
    'say_once_title': 'Dites-le Une Fois', 'say_once_prompt': 'Enregistrez une phrase. Pas de relecture. Pas de ré-enregistrement.', 'say_once_done': 'C\'est dit. Pas de relecture. Pas de reprise.', 'say_once_stop': 'Arrêter', 'say_once_record': 'Enregistrer', 'say_once_quote': 'Certaines choses ne sont destinées à être dites qu\'une seule fois.',
    'newyear_title': 'Lettre à Votre Futur Vous', 'newyear_threshold': 'LE SEUIL', 'newyear_placeholder': 'Cher moi futur...', 'newyear_seal': 'Sceller la lettre', 'newyear_waiting': 'Cette lettre vous attendra.', 'newyear_quote': 'Chaque fin est une porte. Franchissez-la avec intention.',
    'avoid_title': 'La Salle Que Vous Évitez', 'avoid_sub': 'Ce que vous sautez dit quelque chose.', 'avoid_not_entered': 'Vous n\'êtes pas entré dans :', 'avoid_visited_all': 'Vous avez visité chaque salle gratuite.', 'avoid_not_pattern': 'Peut-être que l\'évitement n\'est pas votre modèle.', 'avoid_quote': 'Éviter est aussi un choix.',
    'who_q1': 'Que feriez-vous si l\'argent n\'était pas un souci ?', 'who_q2': 'Comment vous présenteriez-vous à une fête ?', 'who_q3': 'Qu\'est-ce qui vous fait sentir vivant ?', 'who_q4': 'Que diraient vos amis que vous faites bien ?', 'who_q5': 'Qu\'aimiez-vous faire enfant ?', 'who_result_title': 'Vous, Sans Titre', 'who_done': 'C\'est qui vous êtes quand le titre disparaît.', 'who_quote': 'L\'identité n\'est pas ce que vous faites. C\'est ce qui reste quand vous arrêtez.', 'who_placeholder': 'Soyez honnête...', 'who_see_myself': 'Me voir', 'question': 'Question',
    'lastconv_title': 'La Dernière Conversation', 'lastconv_sub': 'Écrivez ce que vous n\'enverrez jamais.', 'lastconv_to': 'À :', 'lastconv_message': 'Message :', 'lastconv_to_placeholder': 'Quelqu\'un qui a besoin d\'entendre ceci...', 'lastconv_msg_placeholder': 'Ce que vous avez toujours voulu dire...', 'lastconv_not_saved': 'Ce message ne sera ni sauvegardé ni partagé.', 'lastconv_seal': 'Sceller la Lettre', 'lastconv_wrote_to': 'Vous avez écrit à :', 'lastconv_never_sent': 'Ce message ne sera jamais envoyé.', 'lastconv_point': 'Mais l\'écrire était le but.', 'lastconv_quote': 'Certains mots doivent exister, même s\'ils ne sont jamais entendus.',
    'priorities_title': 'Vos Vraies Priorités', 'priorities_sub': 'Combien d\'heures par semaine consacrez-vous à chacune ?', 'priorities_result': 'Vos Vraies Priorités', 'priorities_most_time': 'Vous passez le plus de temps sur :', 'priorities_truth': 'Le temps révèle la vérité. Les mots mentent, les emplois du temps non.', 'priorities_quote': 'Vous êtes ce que vous faites de façon répétée.', 'priorities_show': 'Montrez-moi Ma Vérité', 'priority_work': 'Travail', 'priority_family': 'Famille', 'priority_friends': 'Amis', 'priority_health': 'Santé', 'priority_learning': 'Apprentissage', 'priority_entertainment': 'Divertissement', 'priority_social': 'Réseaux Sociaux', 'priority_sleep': 'Sommeil',
    'voice_intro': 'Une voix parle dans l\'obscurité...', 'voice_silence': '(silence)', 'voice_quote': 'Certaines voix ne trouvent jamais le chemin du retour.',
    'knows_tell': 'Dites-moi votre nom.', 'knows_placeholder': 'Votre nom...', 'knows_warning': '(Vous n\'auriez pas dû venir ici)', 'knows_quote': 'Les noms ont du pouvoir. Le vôtre était déjà connu.',
    'door_msg1': 'Quelque chose est à la porte.', 'door_stopped': '(Les coups ont cessé)', 'door_quote': 'Certaines portes sont destinées à rester fermées.',
    'eyes_msg1': 'Une paire d\'yeux observe.', 'eyes_unsee': 'Vous ne pourrez jamais les ignorer.', 'eyes_quote': 'Certaines choses, une fois remarquées, ne peuvent être ignorées.',
    'moon_locked': 'Cette salle ne s\'ouvre que pendant la pleine lune.', 'moon_not_full': 'La lune n\'est pas pleine ce soir. Revenez quand le cycle sera complet.', 'moon_quote': 'Certaines vérités ne se révèlent que par cycles.',
    'at317_title': 'Vous à 3h17', 'at317_locked': 'Cette salle s\'ouvre entre 2h et 5h du matin.', 'at317_current': 'Heure actuelle :', 'at317_quote': 'La nuit connaît votre nom.',
    'mirrors_you_are_here': 'Vous êtes ici.',
    'listen_title': 'Écoutez-Vous', 'listen_recording': 'Enregistrement...', 'listen_start': 'Commencer', 'listen_play': 'Écouter', 'listen_instruction': 'Enregistrez 10 secondes. Dites n\'importe quoi.', 'listen_wait': 'Attendez. Puis écoutez.', 'listen_done': 'C\'était votre voix. Ralentie. Étrange.', 'listen_speed': 'Lecture à vitesse 0.5x', 'listen_quote': 'Votre voix semble différente parce que vous l\'avez enfin entendue.',
    'export_title': 'Exportez Votre Voyage', 'export_subtitle': 'Emportez un morceau de cet endroit', 'export_tab_export': 'Exporter', 'export_tab_import': 'Importer', 'export_your_journey': 'VOTRE VOYAGE', 'export_since': 'Depuis', 'export_time_spent': 'temps passé', 'export_rooms_visited': 'salles visitées', 'export_rooms_completed': 'salles complétées', 'export_fragments': 'fragments', 'export_achievements_unlocked': 'succès débloqués', 'export_truths_recorded': 'vérités enregistrées', 'export_text_file': 'Fichier texte', 'export_json_file': 'Fichier JSON', 'export_copied': 'Copié !', 'export_copy_clipboard': 'Copier dans le presse-papiers', 'export_data_stays': 'Vos données restent avec vous. Nous ne les gardons pas.', 'export_import_desc': 'Importer un voyage exporté précédemment', 'export_json_only': 'Format JSON uniquement', 'export_select_file': 'Sélectionner un fichier', 'export_merge_note': 'Cela sera fusionné avec vos données de voyage actuelles.', 'export_done': 'Exporté.', 'export_done_desc': 'Un enregistrement de vos passages. De ce que vous avez affronté.', 'export_quote': 'Certains voyages méritent d\'être documentés. Pas pour les autres. Pour vous.', 'export_import_success': 'Voyage importé avec succès', 'export_import_error': 'Fichier de voyage invalide',
    'waitroom_title': 'La Salle d\'Attente', 'waitroom_subtitle': 'Certaines portes ne s\'ouvrent que pour les groupes.', 'waitroom_open': 'La porte est ouverte.', 'waitroom_waiting': 'en attente', 'waitroom_time_waited': 'Temps attendu', 'waitroom_users_needed': 'Utilisateurs nécessaires', 'waitroom_someone_joined': 'Quelqu\'un a rejoint l\'attente.', 'waitroom_someone_left': 'Quelqu\'un est parti.', 'waitroom_room_opens': 'La salle s\'ouvre.', 'waitroom_patience': 'La patience et la présence ouvrent ce que la force ne peut pas.', 'waitroom_souls_waited': '{count} âmes ont attendu ensemble.', 'waitroom_seconds_stillness': '{count} secondes de quiétude collective.', 'waitroom_quote': 'Certaines choses ne peuvent pas être précipitées. Certaines portes ne peuvent pas être ouvertes seul.',
    'capsule_title': 'Capsule Temporelle', 'capsule_subtitle': 'Un message pour votre futur vous', 'capsule_what_tell': 'Que devez-vous vous dire ?', 'capsule_placeholder': 'Cher moi du futur...', 'capsule_when_arrive': 'Quand doit-il arriver ?', 'capsule_30_days': '30 jours', 'capsule_90_days': '90 jours', 'capsule_1_year': '1 an', 'capsule_seal': 'Sceller la capsule', 'capsule_waiting': 'capsule(s) en attente', 'capsule_sealed': 'Scellée.', 'capsule_will_wait': 'Votre message vous attendra.', 'capsule_return_in': 'Revenez dans {days} jours pour l\'ouvrir.', 'capsule_quote_sealed': 'Le temps change tout. Même vous.', 'capsule_arrived': 'Un message est arrivé.', 'capsule_from_past': 'Du vous d\'il y a {days} jours.', 'capsule_open': 'Ouvrir la capsule', 'capsule_you_wrote': 'Vous avez écrit ceci pour vous-même :', 'capsule_remember': 'Vous souvenez-vous de l\'avoir écrit ?', 'capsule_quote_opened': 'La personne qui a écrit ceci est partie. Vous restez.',
    'contradiction_title': 'La Salle des Contradictions', 'contradiction_subtitle': 'Où vos vérités se heurtent', 'contradiction_not_enough': 'Pas assez de données encore.', 'contradiction_answer_more': 'Répondez à plus de questions dans les salles.', 'contradiction_answers_recorded': '{count}/5 réponses enregistrées', 'contradiction_quote_early': 'Les contradictions nécessitent de la complexité. Construisez la vôtre d\'abord.', 'contradiction_no_found': 'Aucune contradiction trouvée.', 'contradiction_consistent': 'Soit vous êtes remarquablement cohérent...', 'contradiction_careful': '...soit remarquablement prudent.', 'contradiction_quote_none': 'La cohérence est soit sagesse soit performance.', 'contradiction_found': 'Nous avons trouvé {count} contradiction(s)', 'contradiction_you_said': 'Vous avez dit :', 'contradiction_but_also': 'Mais aussi :', 'contradiction_see_another': 'Voir une autre →', 'contradiction_not_weakness': 'La contradiction n\'est pas une faiblesse. C\'est la preuve de profondeur.', 'contradiction_quote': 'Nous contenons des multitudes. Certaines d\'entre elles ne sont pas d\'accord.',
    'entropy_living': 'vivantes', 'entropy_dead': 'mortes', 'entropy_plant_seed': 'Planter une graine', 'entropy_instructions': 'Cliquez sur les espaces vides pour planter. Cliquez sur les plantes pour arroser. Les plantes mortes peuvent être retirées.', 'entropy_reflect': 'Réfléchir sur votre jardin', 'entropy_lives': 'Votre jardin vit.', 'entropy_thrive': '{living} plantes prospèrent parce que vous êtes revenu.', 'entropy_absence': '{dead} vous rappellent l\'absence.',
    'palace_title': 'Palais de la Mémoire', 'palace_subtitle': 'Construisez un espace mental. Placez des objets pour vous souvenir. Parcourez-le ensuite.', 'palace_room_of': 'Salle {current} sur {total}', 'palace_click_place': 'Cliquez pour placer', 'palace_what_represent': 'Que représente ceci ?', 'palace_cancel': 'Annuler', 'palace_place_object': 'Placer un objet', 'palace_objects_count': '{count} objet(s) dans votre palais', 'palace_take_tour': 'Faire la visite', 'palace_empty_room': 'Cette salle est vide', 'palace_previous': 'Précédent', 'palace_next_room': 'Salle suivante', 'palace_complete_tour': 'Terminer la visite', 'palace_remembers': 'Votre palais se souvient.', 'palace_geography': 'Une géographie de sens que vous avez construite.', 'palace_quote': 'L\'esprit est un palais. Ce que vous y placez détermine qui vous devenez.',
    'echoes_subtitle': 'Fragments d\'autres voyageurs', 'echoes_from': 'de', 'echoes_ago': 'il y a', 'echoes_arriving': 'D\'autres échos arrivent...', 'echoes_reveal': 'Ce ne sont pas leurs réponses. Ce sont les vôtres.', 'echoes_reveal2': '(Chaque écho était autrefois votre propre reflet)', 'echoes_quote': 'Nous pensons être uniques. Nous ne sommes que des échos les uns des autres.',
    'revisit_subtitle': 'Comment vous avez changé', 'revisit_not_enough': 'Vous n\'avez pas encore répondu à suffisamment de questions.', 'revisit_explore': 'Explorez plus de salles. Laissez vos traces. Revenez plus tard.', 'revisit_quote1': 'Le changement nécessite une histoire. L\'histoire nécessite du temps.', 'revisit_none_changed': 'Mais aucune n\'a encore changé. Revisitez les salles. Répondez à nouveau.', 'revisit_show_evolved': 'Nous vous montrerons comment vous avez évolué.', 'revisit_quote2': 'Pour voir le changement, vous devez d\'abord changer.', 'revisit_then': 'Alors', 'revisit_now': 'Maintenant', 'revisit_one_shift': 'Un changement. Subtil mais réel.', 'revisit_each_change': 'Chaque changement est une petite mort. Et une petite naissance.', 'revisit_quote3': 'Vous n\'êtes pas qui vous étiez. Vous n\'êtes pas encore qui vous serez.',
  },

  // Hindi translations
  hi: {
    'title': 'मानव कक्ष',
    'tagline': 'कमरों का एक संग्रह जो दर्शाता है कि जब कोई नहीं देख रहा तो आप कौन हैं।',
    'enter': 'प्रवेश करें',
    'footer': 'न चिकित्सा। न सामाजिक। न उत्पादक।',
    'back': '← वापस',
    'hallway': 'गलियारा',
    'leave_room': '← कमरा छोड़ें',
    'return_hallway': 'गलियारे में लौटें',
    'loading': 'लोड हो रहा है...',
    'submit': 'जमा करें',
    'cancel': 'रद्द करें',
    'close': 'बंद करें',
    'next': 'अगला',
    'previous': 'पिछला',
    'start': 'शुरू करें',
    'stop': 'रुकें',
    'yes': 'हां',
    'no': 'नहीं',
    'complete': 'पूर्ण',
    'reveal': 'प्रकट करें',
    'continue': 'जारी रखें',
    'begin': 'आरंभ करें',
    'return': '← लौटें',
    'escape': '← भागें',
    'run': '← दौड़ें',
    'leave': '← छोड़ें',
    'watch': 'देखें...',
    'correct': 'सही',
    'round': 'राउंड',
    'enter_again': 'फिर से प्रवेश करें',
    'steps': 'कदम',
    'depth': 'गहराई',
    'settings': 'सेटिंग्स',
    'language': 'भाषा',
    'updates': 'अपडेट',
    'updates_title': 'नया क्या है',
    'new': 'नया',
    'view_all_updates': 'सभी अपडेट देखें',
    'whats_new_intro': 'नवीनतम अपडेट:',
    'challenges': 'चुनौतियां',
    'random': 'यादृच्छिक',
    'open_rooms': 'खुले कमरे',
    'dark_rooms': 'अंधेरे कमरे',
    'enter_risk': 'अपने जोखिम पर प्रवेश करें',
    'premium_rooms': 'प्रीमियम कमरे',
    'unlock_to_access': 'खोलने के लिए अनलॉक करें',
    'daily_rooms': 'दैनिक कमरे',
    'refreshes_daily': 'हर दिन ताज़ा होता है',
    'time_locked': 'समय-बंद कमरे',
    'seasonal_rooms': 'मौसमी कमरे',
    'available_now': 'अभी उपलब्ध',
    'fragments': 'टुकड़े',
    'boss_unlocked': 'बॉस कमरा खुला',
    'collect_unlock': 'बॉस कमरा खोलने के लिए इकट्ठा करें',
    'dark_fragments': 'अंधेरे टुकड़े',
    'dark_boss_unlocked': 'अंधेरे बॉस खुला',
    'hidden_discovered': 'छिपा कमरा मिला',
    'stillness_revealed': 'आपकी शांति ने कुछ प्रकट किया...',
    'constellation_map': 'तारामंडल नक्शा',
    'your_stats': 'आपके आंकड़े',
    'completed': 'पूर्ण',
    'game_rooms': 'खेल कमरे',
    'experimental_rooms': 'प्रायोगिक कमरे',
    'deep_rooms': 'गहरे कमरे',
    'meta_rooms': 'मेटा कमरे',
    'search_rooms': 'कमरे खोजें...',
    'no_results': 'कोई कमरा मिलान नहीं',
    'clear_filters': 'फ़िल्टर साफ़ करें',
    'explore': 'अन्वेषण करें',
    'all_categories': 'सभी',
    'random_room': 'यादृच्छिक कमरा',
    'rooms_explored': 'कमरे खोजे गए',
    'your_journey': 'आपकी यात्रा',
    'choose_direction': 'एक दिशा चुनें। कुछ दरवाज़े प्रतिक्रिया करते हैं।',
    'achievements': 'उपलब्धियां',
    'achievements_subtitle': 'आपकी यात्रा के मील के पत्थर',
    'points': 'अंक',
    'unlocked': 'अनलॉक',
    'exploration': 'अन्वेषण',
    'consistency': 'निरंतरता',
    'discovery': 'खोज',
    'special': 'विशेष',
    'all': 'सभी',
    'bookmarks': 'बुकमार्क',
    'bookmarked_rooms': 'बुकमार्क किए गए',
    'no_bookmarks': 'अभी तक कोई बुकमार्क नहीं',
    'what_is_this': 'यह क्या जगह है?',
    'total_time': 'कुल समय',
    'rooms_visited': 'देखे गए कमरे',
    'total_visits': 'कुल यात्राएं',
    'most_visited': 'सबसे ज़्यादा देखा',
    'longest_session': 'सबसे लंबा सत्र',
    'fragments_collected': 'एकत्रित टुकड़े',
    'completed_rooms': 'पूर्ण कमरे',
    'privacy_note': 'सभी डेटा आपके डिवाइस पर स्थानीय रूप से संग्रहीत',
    'whisper_wall': 'फुसफुसाहट की दीवार',
    'leave_whisper': 'एक फुसफुसाहट छोड़ें...',
    'whisper_sent': 'आपकी फुसफुसाहट दीवार में बहती है।',
    'whispers_count': 'फुसफुसाहटें',
    'anonymous': 'गुमनाम',
    // Room Titles
    'room_how-many-times-left': 'कितनी बार बाकी है',
    'room_life-in-weeks': 'हफ्तों में आपका जीवन',
    'room_delay-button': 'देरी का बटन',
    'room_one-honest-sentence': 'एक ईमानदार वाक्य',
    'room_this-moment': 'यह क्षण फिर कभी नहीं आएगा',
    'room_reality-receipt': 'वास्तविकता की रसीद',
    'room_nothing-happens': 'यहाँ कुछ नहीं होता',
    'room_default-choice': 'आपकी डिफ़ॉल्ट पसंद',
    'room_thought-loop': 'विचार का चक्र',
    'room_expect-vs-happens': 'उम्मीद बनाम हकीकत',
    'room_question-never-answer': 'वो सवाल जिसका जवाब कभी नहीं देते',
    'room_not-special': 'आप खास नहीं हैं (और ठीक है)',
    'room_shortcut-always-take': 'वो शॉर्टकट जो आप हमेशा लेते हैं',
    'room_blurred-self': 'धुंधला स्व',
    'room_image-zooming': 'वो तस्वीर जिसे आप ज़ूम करते रहते हैं',
    'room_comfort-color': 'सांत्वना का रंग',
    'room_line-you-crossed': 'वो रेखा जो आपने पार की',
    'room_read-aloud': 'ज़ोर से पढ़ें',
    'room_voice-without-meaning': 'बिना अर्थ के आपकी आवाज़',
    'room_sentence-you-avoid': 'वो वाक्य जिससे आप बचते हैं',
    'room_if-nothing-changes': 'अगर कुछ नहीं बदलता',
    'room_real-priorities': 'आपकी असली प्राथमिकताएं',
    'room_last-conversation': 'आखिरी बातचीत',
    'room_who-without-job': 'नौकरी के बिना आप कौन हैं?',
    'room_comfort-zone-scanner': 'कम्फर्ट ज़ोन स्कैनर',
    'room_say-it-once': 'एक बार कहो',
    'room_listen-to-yourself': 'खुद को सुनो',
    'room_life-as-shapes': 'आकारों में आपका जीवन',
    'room_face-without-features': 'बिना चेहरे के चेहरा',
    'room_what-keeps-awake': 'क्या जगाए रखता है',
    'room_you-at-317am': 'सुबह 3:17 पर आप',
    'room_thought-that-returns': 'वो विचार जो लौटता है',
    'room_room-you-visit-most': 'सबसे ज़्यादा देखा गया कमरा',
    'room_room-you-avoid': 'जिस कमरे से बचते हैं',
    'room_left-too-early': 'बहुत जल्दी चले गए',
    'room_daily-quote': 'आज का उद्धरण',
    'room_daily-joke': 'आज का चुटकुला',
    'room_theyre-watching': 'वो देख रहे हैं',
    'room_the-last-visitor': 'आखिरी आगंतुक',
    'room_dont-look-away': 'नज़र मत हटाओ',
    'room_the-whisper': 'फुसफुसाहट',
    'room_you-forgot-something': 'आप कुछ भूल गए',
    'room_the-mirror-lies': 'आईना झूठ बोलता है',
    'room_something-follows': 'कुछ पीछा करता है',
    'room_the-countdown': 'उलटी गिनती',
    'room_eyes-in-the-dark': 'अंधेरे में आँखें',
    'room_the-voice': 'आवाज़',
    'room_it-knows-your-name': 'इसे आपका नाम पता है',
    'room_the-door': 'दरवाज़ा',
    'room_boss-room-free': 'आपकी सच्चाइयों का योग',
    'room_boss-room-horror': 'डर का दिल',
    'room_boss-room-premium': 'सबसे गहरा सच',
    'room_mouse-tracker': 'माउस ट्रैकर',
    'room_scroll-confession': 'स्क्रॉल कन्फेशन',
    'room_waiting-game': 'इंतज़ार का खेल',
    'room_day-in-pixels': 'पिक्सल में आपका दिन',
    'room_memory-maze': 'यादों की भूलभुलैया',
    'room_decision-shadow': 'फ़ैसले की छाया',
    'room_anonymous-mirrors': 'गुमनाम आईने',
    'room_collective-room': 'सामूहिक कमरा',
    'room_secret-room': 'गुप्त कमरा',
    'room_glitch-room': 'अस्थिर कमरा',
    'room_full-moon-room': 'पूर्णिमा कमरा',
    'room_weekend-room': 'सप्ताहांत कमरा',
    'room_317am-room': 'सुबह 3:17 का कमरा',
    'room_idle-room': 'शांत कमरा',
    'room_invite-friend-room': 'साझा कमरा',
    'room_halloween-room': 'पर्दे का कमरा',
    'room_winter-solstice-room': 'सबसे लंबी रात',
    'room_new-year-room': 'भविष्य के आप को पत्र',
    'room_memory-puzzle': 'यादों की पहेली',
    'room_shadow-chase': 'छाया का पीछा',
    'room_timing-room': 'समय कमरा',
    'room_pattern-whisper': 'पैटर्न फुसफुसाहट',
    'room_silence-puzzle': 'मौन पहेली',
    'room_decay-game': 'क्षय का खेल',
    'room_observation-test': 'अवलोकन परीक्षा',
    'room_balance-room': 'संतुलन कमरा',
    'room_word-drift': 'शब्द बहाव',
    'room_color-echo': 'रंग प्रतिध्वनि',
    'room_mirror-tap': 'दर्पण टैप',
    'room_maze-of-doors': 'दरवाज़ों की भूलभुलैया',
    'room_pulse-match': 'धड़कन मिलान',
    'room_light-path': 'प्रकाश पथ',
    'room_sound-match': 'ध्वनि मिलान',
    'room_mirror-room': 'दर्पण कमरा',
    'room_echo-room': 'प्रतिध्वनि कमरा',
    'room_pulse-room': 'धड़कन कमरा',
    'room_vanishing-room': 'गायब होता कमरा',
    'room_letter-room': 'अक्षर कमरा',
    'room_empty-room': 'खाली कमरा',
    'room_reverse-room': 'उलटा कमरा',
    'room_whisper-room': 'फुसफुसाहट कमरा',
    'room_shadow-room': 'छाया कमरा',
    'room_labyrinth-room': 'भूलभुलैया कमरा',
    'room_color-room': 'रंग कमरा',
    'room_surrender-room': 'समर्पण कमरा',
    'room_noise-room': 'शोर कमरा',
    'room_observer-room': 'प्रेक्षक कमरा',
    'room_imagine-yourself': 'खुद की कल्पना करें',
    'room_ai-reflection-room': 'एआई चिंतन कमरा',
    'room_the-other-side': 'दूसरी तरफ',
    'room_the-unraveling': 'उधेड़बुन',
    'room_the-echo': 'प्रतिध्वनि',
    'room_ash-embers': 'राख और अंगारे',
    'room_twilight-stars': 'सांझ के तारे',
    'room_void-abyss': 'रसातल',
    'room_eclipse-shadows': 'ग्रहण की छायाएं',
    'room_dawn-awakening': 'सुबह का जागरण',
    'room_letters-never-sent': 'कभी न भेजे गए पत्र',
    'room_breath-sync': 'सांस समन्वय',
    'room_entropy-garden': 'एन्ट्रॉपी बगीचा',
    'room_memory-palace': 'यादों का महल',
    'room_morning-ritual': 'सुबह का अनुष्ठान',
    'room_evening-ritual': 'शाम का अनुष्ठान',
    'room_weekly-ritual': 'साप्ताहिक अनुष्ठान',
    'room_spring-equinox-room': 'वसंत विषुव',
    'room_summer-solstice-room': 'ग्रीष्म संक्रांति',
    'room_autumn-equinox-room': 'शरद विषुव',
    'room_valentine-room': 'आत्म-प्रेम कमरा',
    'room_fragment-trade': 'टुकड़ों की वेदी',
    'room_the-archive': 'अभिलेखागार',
    'room_mirror-of-all-selves': 'सभी स्वयं का दर्पण',
    'room_the-ending': 'अंत',
    'room_whisper-wall': 'फुसफुसाहट की दीवार',
    'room_collective-memory': 'सामूहिक स्मृति',
    'room_shared-moments': 'साझा पल',
    'room_void-room': 'शून्य',
    'room_pattern-room': 'आपके पैटर्न',
    'room_time-capsule': 'समय कैप्सूल',
    'room_the-revisit': 'पुनः भ्रमण',
    'room_contradiction-room': 'विरोधाभास कमरा',
    'room_export-journey': 'यात्रा निर्यात करें',
    'room_anonymous-echoes': 'गुमनाम प्रतिध्वनियां',
    'room_the-waiting-room': 'प्रतीक्षा कक्ष',
    'room_dream-visualization': 'सपनों का दृश्यांकन',
    'room_future-self': 'भविष्य का स्व',
    'room_regret-machine': 'पछतावे की मशीन',
    'room_memory-reconstruction': 'स्मृति पुनर्निर्माण',
    // v3.21 rooms
    'room_mask-you-wear': 'वो मुखौटा जो आप पहनते हैं',
    'room_recurring-dream': 'बार-बार आने वाला सपना',
    'room_promise-you-broke': 'वो वादा जो आपने तोड़ा',
    'room_words-you-swallowed': 'निगले हुए शब्द',
    'room_age-you-feel': 'जो उम्र आप महसूस करते हैं',
    'room_biggest-lie': 'सबसे बड़ा झूठ',
    'room_person-you-pretend': 'जो व्यक्ति आप बनते हैं',
    'room_person-you-pretend-to-be': 'जो व्यक्ति आप बनते हैं',
    'room_what-you-collect': 'आप क्या इकट्ठा करते हैं',
    'room_apology-you-owe': 'वो माफ़ी जो आपको देनी है',
    'room_unfinished-story': 'अधूरी कहानी',
    'room_version-others-see': 'दूसरे जो संस्करण देखते हैं',
    'room_secret-talent': 'छिपी प्रतिभा',
    'room_fear-you-feed': 'वो डर जिसे आप पालते हैं',
    'room_guilty-pleasure': 'छिपा आनंद',
    'room_rule-you-break': 'वो नियम जो आप हमेशा तोड़ते हैं',
    'room_childhood-promise': 'बचपन का वादा',
    'room_compliment-you-reject': 'वो तारीफ जो आप ठुकराते हैं',
    'room_autopilot-mode': 'ऑटोपायलट मोड',
    'room_boundary-you-need': 'ज़रूरी सीमा',
    'room_inner-critic': 'आंतरिक आलोचक',
    'room_last-sunday': 'पिछला रविवार',
    'room_wasted-hours': 'बर्बाद घंटे',
    'room_year-that-changed': 'वो साल जिसने बदला',
    'room_tomorrows-regret': 'कल का पछतावा',
    'room_morning-autopilot': 'सुबह का ऑटोपायलट',
    'room_season-you-dread': 'वो मौसम जिससे डरते हैं',
    'room_ten-years-ago': 'दस साल पहले आज',
    'room_last-normal-day': 'आखिरी सामान्य दिन',
    'room_clock-you-ignore': 'वो घड़ी जिसे अनदेखा करते हैं',
    'room_your-deadline': 'आपकी अंतिम तिथि',
    'room_the-pause': 'विराम',
    'room_static-room': 'स्थैतिक',
    'room_gap-between-thoughts': 'विचारों के बीच का अंतर',
    'room_sit-with-discomfort': 'बेचैनी के साथ बैठें',
    'room_boring-room': 'उबाऊ कमरा',
    'room_unplug': 'अनप्लग',
    'room_unplug-room': 'अनप्लग',
    'room_last-page': 'आखिरी पन्ना',
    'room_scrolling-pattern': 'स्क्रॉलिंग पैटर्न',
    'room_tab-you-keep-open': 'वो टैब जो खुला रहता है',
    'room_digital-shadow': 'डिजिटल छाया',
    'room_notification-you-ignore': 'वो सूचना जो अनदेखी करते हैं',
    'room_typing-speed': 'टाइपिंग की गति',
    'room_link-you-almost-clicked': 'वो लिंक जो लगभग क्लिक किया',
    'room_bookmark-graveyard': 'बुकमार्क कब्रिस्तान',
    'room_search-you-deleted': 'वो खोज जो मिटा दी',
    'room_gravity-room': 'गुरुत्वाकर्षण कमरा',
    'room_ink-blot': 'स्याही का धब्बा',
    'room_hallway-of-mirrors': 'आईनों का गलियारा',
    'room_dissolving-text': 'घुलता पाठ',
    'room_the-pendulum': 'पेंडुलम',
    'room_pixel-rain': 'पिक्सल बारिश',
    'room_the-threshold': 'दहलीज',
    // v3.22 rooms
    'room_the-friend-you-lost': 'वो दोस्त जो खो गया',
    'room_conversation-you-replay': 'वो बातचीत जो दोहराते हैं',
    'room_the-thank-you': 'धन्यवाद',
    'room_people-you-forgot': 'जिन लोगों को भूल गए',
    'room_the-advice-you-give': 'वो सलाह जो आप देते हैं',
    'room_what-others-dont-know': 'जो दूसरे नहीं जानते',
    'room_the-lie-you-believe': 'वो झूठ जिसे मानते हैं',
    'room_your-shadow-self': 'छाया स्व',
    'room_the-story-you-tell': 'वो कहानी जो आप सुनाते हैं',
    'room_your-safe-place': 'सुरक्षित जगह',
    'room_the-risk-you-didnt-take': 'वो जोखिम जो नहीं उठाया',
    'room_your-last-excuse': 'आखिरी बहाना',
    'room_the-habit-you-hide': 'छिपी आदत',
    'room_what-you-postpone': 'जो टालते रहते हैं',
    'room_the-door-you-closed': 'वो दरवाज़ा जो बंद किया',
    // v3.23 rooms
    'room_the-emotion-you-hide': 'वो भावना जो छिपाते हैं',
    'room_your-comfort-lie': 'सांत्वना का झूठ',
    'room_the-silence-between': 'बीच का मौन',
    'room_what-you-carry': 'जो आप ढोते हैं',
    'room_the-version-you-lost': 'वो संस्करण जो खो गया',
    'room_letter-to-past-self': 'अतीत के स्व को पत्र',
    'room_the-moment-you-knew': 'वो पल जब आपने जाना',
    'room_the-words-you-rehearse': 'रिहर्सल किए शब्द',
    'room_someone-thinks-of-you': 'कोई आपके बारे में सोचता है',
    'room_the-question-you-fear': 'वो सवाल जिससे डरते हैं',
    // v4.0 rooms
    'room_recurring-dream-journal': 'बार-बार आने वाले सपनों की डायरी',
    'room_lucid-moment': 'स्पष्ट क्षण',
    'room_nightmare-you-remember': 'वो बुरा सपना जो याद है',
    'room_dream-you-wish-was-real': 'काश ये सपना सच होता',
    'room_the-dream-visitor': 'सपनों का मेहमान',
    'room_the-blank-canvas': 'खाली कैनवास',
    'room_song-that-is-you': 'वो गाना जो आप हैं',
    'room_color-of-today': 'आज का रंग',
    'room_the-unwritten-poem': 'अनलिखी कविता',
    'room_your-inner-voice': 'आंतरिक आवाज़',
    'room_fear-you-named': 'नाम दिया गया डर',
    'room_the-leap': 'छलांग',
    'room_courage-inventory': 'साहस की सूची',
    'room_what-scares-you-most': 'सबसे ज़्यादा क्या डराता है',
    'room_facing-the-dark': 'अंधेरे का सामना',
    'room_letter-to-future-self': 'भविष्य के स्व को पत्र',
    'room_the-world-you-want': 'वो दुनिया जो आप चाहते हैं',
    'room_your-next-chapter': 'अगला अध्याय',
    'room_the-wish': 'इच्छा',
    'room_if-you-had-one-year': 'अगर एक साल होता',
    // Room Subtitles
    'sub_how-many-times-left': 'बाकी बचे की गिनती',
    'sub_life-in-weeks': '4,000 वर्ग। बस इतना ही।',
    'sub_delay-button': 'हर बार कठिन होता जाता है',
    'sub_one-honest-sentence': 'कोई संपादन नहीं। कोई मिटाना नहीं।',
    'sub_this-moment': 'आप यहाँ हैं। एक बार।',
    'sub_reality-receipt': 'आपके दिनों का सारांश',
    'sub_nothing-happens': 'बिल्कुल वही जो लिखा है',
    'sub_default-choice': 'सुरक्षा या जोखिम। हर बार।',
    'sub_thought-loop': 'अपने मन को दोहराते देखें',
    'sub_expect-vs-happens': 'उम्मीद और हकीकत के बीच का अंतर',
    'sub_question-never-answer': 'ज़्यादातर लोग चले जाते हैं',
    'sub_not-special': 'कोई सांत्वना नहीं। बस सच।',
    'sub_shortcut-always-take': 'पैटर्न खुद को प्रकट करते हैं',
    'sub_blurred-self': 'याददाश्त ऐसे ही काम करती है',
    'sub_image-zooming': 'कुछ भी नहीं में जवाब ढूंढना',
    'sub_comfort-color': 'कब तक रुकेंगे?',
    'sub_line-you-crossed': 'हम देखते हैं कहाँ झिझके',
    'sub_read-aloud': 'शब्द जो बोलने होंगे',
    'sub_voice-without-meaning': 'बिना शब्दों की आवाज़',
    'sub_sentence-you-avoid': 'जब तक लिखेंगे नहीं, जा नहीं सकते',
    'sub_if-nothing-changes': 'अगले पांच साल का अनुमान',
    'sub_real-priorities': 'समय पर आधारित, शब्दों पर नहीं',
    'sub_last-conversation': 'वो लिखें जो कभी नहीं भेजेंगे',
    'sub_who-without-job': 'खुद को फिर से लिखें',
    'sub_comfort-zone-scanner': 'हम आपकी झिझक देखते हैं',
    'sub_say-it-once': 'कोई रिप्ले नहीं। कोई दोबारा नहीं।',
    'sub_listen-to-yourself': 'धीमी की गई आपकी आवाज़',
    'sub_life-as-shapes': 'अमूर्त। बिना लेबल। आपका।',
    'sub_face-without-features': 'हटाते जाएं जब तक कुछ न बचे',
    'sub_what-keeps-awake': 'रात 10 बजे के बाद उपलब्ध',
    'sub_you-at-317am': 'समय-विशिष्ट। व्यक्तिगत।',
    'sub_thought-that-returns': 'हर बार लौटता है',
    'sub_room-you-visit-most': 'आपका पैटर्न प्रकट',
    'sub_room-you-avoid': 'जो छोड़ते हैं वो कुछ कहता है',
    'sub_left-too-early': 'जल्दी निकलने पर सक्रिय',
    'sub_daily-quote': 'रोज़ बदलता ज्ञान',
    'sub_daily-joke': 'गहरा हास्य, रोज़ अपडेट',
    'sub_theyre-watching': 'आप अकेले नहीं हैं',
    'sub_the-last-visitor': 'कोई पहले यहाँ था',
    'sub_dont-look-away': 'इसे पता चलता है जब आप जाते हैं',
    'sub_the-whisper': 'ध्यान से सुनें',
    'sub_you-forgot-something': 'क्या था वो?',
    'sub_the-mirror-lies': 'सच देखने के लिए तोड़ें',
    'sub_something-follows': 'भागते रहें',
    'sub_the-countdown': 'शून्य पर कुछ होता है',
    'sub_eyes-in-the-dark': 'वो बढ़ते जाते हैं',
    'sub_the-voice': 'कोई यहाँ फंसा है',
    'sub_it-knows-your-name': 'आपको नहीं आना चाहिए था',
    'sub_the-door': 'कोई दस्तक दे रहा है',
    'sub_boss-room-free': 'अंतिम मुफ्त कमरा',
    'sub_boss-room-horror': 'अंतिम अंधेरे का सामना करें',
    'sub_boss-room-premium': 'अंतिम गहरा कमरा',
    'sub_mouse-tracker': 'हम देखते हैं आप कैसे चलते हैं',
    'sub_scroll-confession': 'कितनी तेज़ी से भागते हैं?',
    'sub_waiting-game': 'क्या आप कुछ नहीं कर सकते?',
    'sub_day-in-pixels': 'समय आकार बन जाता है',
    'sub_memory-maze': 'पैटर्न बदलता है',
    'sub_decision-shadow': 'आपकी पसंद पैटर्न दिखाती है',
    'sub_anonymous-mirrors': 'अजनबियों के विचार',
    'sub_collective-room': 'सब यहाँ हैं। कोई नहीं रुकता।',
    'sub_secret-room': 'आपने इसे ढूंढ लिया',
    'sub_glitch-room': 'कुछ गलत है',
    'sub_full-moon-room': 'केवल चंद्रमा के शिखर पर खुलता है',
    'sub_weekend-room': 'आराम एक मंज़िल है',
    'sub_317am-room': 'समय-विशिष्ट। व्यक्तिगत।',
    'sub_idle-room': 'धैर्य से मिला',
    'sub_invite-friend-room': 'कुछ यात्राएं साक्षी के लिए होती हैं',
    'sub_halloween-room': '31 अक्टूबर - पर्दा पतला है',
    'sub_winter-solstice-room': '21 दिसंबर - छोड़ें और रखें',
    'sub_new-year-room': '31 दिसंबर - भविष्य के आप को लिखें',
    'sub_memory-puzzle': 'सूक्ष्मता से बदलते पैटर्न मिलाएं',
    'sub_shadow-chase': 'अप्रत्याशित को पकड़ें',
    'sub_timing-room': 'बिल्कुल सही पल में क्लिक करें',
    'sub_pattern-whisper': 'फीकी होती क्रम दोहराएं',
    'sub_silence-puzzle': 'बिना निर्देश के हल करें',
    'sub_decay-game': 'फीकी चीज़ों को संरक्षित करें',
    'sub_observation-test': 'सूक्ष्म अंतर खोजें',
    'sub_balance-room': 'तैरती वस्तुओं को संरेखित रखें',
    'sub_word-drift': 'गिरने से पहले अक्षर पकड़ें',
    'sub_color-echo': 'सूक्ष्म स्वर क्रम दोहराएं',
    'sub_mirror-tap': 'हर टैप कहीं और गूंजता है',
    'sub_maze-of-doors': 'हर चुनाव के बाद दरवाज़े बदलते हैं',
    'sub_pulse-match': 'कमरे की लय में चलें',
    'sub_light-path': 'बदलते अंधेरे में प्रकाश का मार्गदर्शन',
    'sub_sound-match': 'आकारों को मधुर स्वरों से मिलाएं',
    'sub_mirror-room': 'हल्के विकृत प्रतिबिंब',
    'sub_echo-room': 'क्लिक विलंबित प्रतिध्वनि सक्रिय करते हैं',
    'sub_pulse-room': 'वातावरण सांस लेता है',
    'sub_vanishing-room': 'बातचीत से तत्व फीके होते हैं',
    'sub_letter-room': 'शब्द स्मृति की तरह बिखरते हैं',
    'sub_empty-room': 'रहस्य खोजने तक खाली',
    'sub_reverse-room': 'क्रियाएं उलट व्यवहार करती हैं',
    'sub_whisper-room': 'मुश्किल से सुनाई देने वाले सच',
    'sub_shadow-room': 'आपका अंधेरा साथी',
    'sub_labyrinth-room': 'अपना रास्ता खोजें',
    'sub_color-room': 'रंगीन ध्यान',
    'sub_surrender-room': 'नियंत्रण छोड़ दें',
    'sub_noise-room': 'स्थैतिक विचार',
    'sub_observer-room': 'देखे जा रहे हैं',
    'sub_imagine-yourself': 'खुद का वर्णन करें। देखें क्या उभरता है।',
    'sub_ai-reflection-room': 'एआई आपके साथ चिंतन करे',
    'sub_the-other-side': 'अंधेरे में धीरे चलें',
    'sub_the-unraveling': 'हर शब्द से वास्तविकता बिखरती है',
    'sub_the-echo': 'जो कहते हैं वो कभी नहीं जाता',
    'sub_ash-embers': 'केवल राख थीम में दिखता है',
    'sub_twilight-stars': 'केवल सांझ थीम में दिखता है',
    'sub_void-abyss': 'केवल शून्य थीम में दिखता है',
    'sub_eclipse-shadows': 'पूर्ण अंधेरे में बोलें',
    'sub_dawn-awakening': 'पहली रोशनी का स्वागत करें',
    'sub_letters-never-sent': 'किसी को लिखें जो कभी नहीं पढ़ेगा',
    'sub_breath-sync': 'कमरे के साथ सांस लें',
    'sub_entropy-garden': 'विकास और क्षय आपस में गुंथे',
    'sub_memory-palace': 'अपना मानसिक अभयारण्य बनाएं',
    'sub_morning-ritual': 'इरादे से दिन शुरू करें',
    'sub_evening-ritual': 'चिंतन से दिन बंद करें',
    'sub_weekly-ritual': 'रविवार पीछे देखने के लिए है',
    'sub_spring-equinox-room': 'छोड़ें और नवीनीकरण करें',
    'sub_summer-solstice-room': 'सबसे लंबा दिन',
    'sub_autumn-equinox-room': 'फसल और स्वीकृति',
    'sub_valentine-room': 'वो प्यार जो आप हक़दार हैं',
    'sub_fragment-trade': 'रहस्य खोलने के लिए बलिदान',
    'sub_the-archive': 'आपका पूरा यात्रा इतिहास',
    'sub_mirror-of-all-selves': 'एआई आपका व्यवहार चित्र दिखाता है',
    'sub_the-ending': 'एक विदाई जो आपने कमाई',
    'sub_whisper-wall': 'गुमनाम सच अंधेरे में तैरते हैं',
    'sub_collective-memory': 'जो हम याद करते हैं उसके साझा टुकड़े',
    'sub_shared-moments': 'सच में, आप कैसे हैं?',
    'sub_void-room': 'कोड में एक घाव',
    'sub_pattern-room': 'साइट आपमें क्या देखती है',
    'sub_time-capsule': 'भविष्य के स्व को लिखें',
    'sub_the-revisit': 'देखें कैसे जवाब बदले',
    'sub_contradiction-room': 'आपकी सच्चाइयां असहमत हैं',
    'sub_export-journey': 'अपना पथ डाउनलोड करें',
    'sub_anonymous-echoes': 'अजनबियों के टुकड़े',
    'sub_the-waiting-room': 'उपस्थिति से अनलॉक करें',
    'sub_dream-visualization': 'सपने का वर्णन करें। उसे साकार होते देखें।',
    'sub_future-self': 'आप क्या बनना चाहते हैं?',
    'sub_regret-machine': 'जो हो सकता था उसे खोजें',
    'sub_memory-reconstruction': 'एक फीकी याद को फिर से बनाएं',
    // v3.21 subtitles
    'sub_mask-you-wear': 'कौन सा चेहरा दिखाते हैं?',
    'sub_recurring-dream': 'वो सपना जो बार-बार आता है',
    'sub_promise-you-broke': 'टूटे वादों की ईमानदारी',
    'sub_words-you-swallowed': 'जो कभी नहीं कहा',
    'sub_age-you-feel': 'असल में आपकी उम्र क्या है?',
    'sub_biggest-lie': 'सबसे ज़्यादा बोला जाने वाला झूठ',
    'sub_person-you-pretend': 'आपका अभिनय',
    'sub_person-you-pretend-to-be': 'आपका अभिनय',
    'sub_what-you-collect': 'वस्तुएं पहचान दिखाती हैं',
    'sub_apology-you-owe': 'जो कहना चाहिए था वो लिखें',
    'sub_unfinished-story': 'जो कभी पूरा नहीं किया',
    'sub_version-others-see': 'दूसरे आपको कैसे देखते हैं',
    'sub_secret-talent': 'जो कोई नहीं जानता आप कर सकते हैं',
    'sub_fear-you-feed': 'कौन सा डर नियंत्रित करता है?',
    'sub_guilty-pleasure': 'जो आनंद छिपाते हैं',
    'sub_rule-you-break': 'विद्रोह का पैटर्न',
    'sub_childhood-promise': 'जो खुद से वादा किया था',
    'sub_compliment-you-reject': 'तारीफ क्यों स्वीकार नहीं कर सकते?',
    'sub_autopilot-mode': 'बिना सोचे क्या करते हैं',
    'sub_boundary-you-need': 'क्या रक्षा करनी चाहिए',
    'sub_inner-critic': 'वो आवाज़ जो आलोचना करती है',
    'sub_last-sunday': 'असल में क्या किया?',
    'sub_wasted-hours': 'समय कहाँ गायब होता है',
    'sub_year-that-changed': 'वो मोड़',
    'sub_tomorrows-regret': 'काश क्या किया होता?',
    'sub_morning-autopilot': 'पहले घंटे की दिनचर्या',
    'sub_season-you-dread': 'कौन सा मौसम सबसे भारी?',
    'sub_ten-years-ago': 'तब आप कौन थे?',
    'sub_last-normal-day': 'सब कुछ बदलने से पहले',
    'sub_clock-you-ignore': 'वो समय जो बीतता नहीं दिखता',
    'sub_your-deadline': 'क्या टाल रहे हैं?',
    'sub_the-pause': 'बस एक पल सांस लें',
    'sub_static-room': 'सफ़ेद शोर और शांति',
    'sub_gap-between-thoughts': 'वो जगह खोजें',
    'sub_sit-with-discomfort': 'कुछ ठीक मत करें',
    'sub_boring-room': 'कुछ भी दिलचस्प नहीं',
    'sub_unplug': 'कल्पना करें कोई उपकरण नहीं',
    'sub_unplug-room': 'कल्पना करें कोई उपकरण नहीं',
    'sub_last-page': 'कहानी यहीं खत्म होती है',
    'sub_scrolling-pattern': 'आप सामग्री कैसे उपभोग करते हैं',
    'sub_tab-you-keep-open': 'ब्राउज़र में क्या रहता है',
    'sub_digital-shadow': 'आपका ऑनलाइन पदचिह्न',
    'sub_notification-you-ignore': 'जो खारिज करते हैं',
    'sub_typing-speed': 'कितनी तेज़ी से व्यक्त करते हैं?',
    'sub_link-you-almost-clicked': 'झिझक के पैटर्न',
    'sub_bookmark-graveyard': 'सहेजा पर लौटे नहीं',
    'sub_search-you-deleted': 'जो खोजा और मिटा दिया',
    'sub_gravity-room': 'सब कुछ धीरे गिरता है',
    'sub_ink-blot': 'आपको क्या दिखता है?',
    'sub_hallway-of-mirrors': 'अनंत प्रतिबिंब',
    'sub_dissolving-text': 'पढ़ते समय शब्द पिघलते हैं',
    'sub_the-pendulum': 'सम्मोहक गति',
    'sub_pixel-rain': 'डिजिटल बारिश',
    'sub_the-threshold': 'यहाँ और वहाँ के बीच',
    // v3.22 subtitles
    'sub_the-friend-you-lost': 'जिससे दूर हो गए उन्हें लिखें',
    'sub_conversation-you-replay': 'याददाश्त से फिर से बनाएं',
    'sub_the-thank-you': 'जो कृतज्ञता कभी व्यक्त नहीं की',
    'sub_people-you-forgot': 'भूले हुए नाम याद करने की कोशिश',
    'sub_the-advice-you-give': 'सलाह जो देते हैं पर मानते नहीं',
    'sub_what-others-dont-know': 'आपकी छिपी परतें',
    'sub_the-lie-you-believe': 'एक सुकूनदायक असत्य',
    'sub_your-shadow-self': 'जो हिस्सा दबाते हैं',
    'sub_the-story-you-tell': 'अपनी ज़िंदगी कैसे बयान करते हैं',
    'sub_your-safe-place': 'बताएं कहाँ सुरक्षित महसूस करते हैं',
    'sub_the-risk-you-didnt-take': 'जो रास्ते नहीं चुने',
    'sub_your-last-excuse': 'सबसे ज़्यादा इस्तेमाल किया बहाना',
    'sub_the-habit-you-hide': 'गुप्त दिनचर्या',
    'sub_what-you-postpone': 'हमेशा कल की बात',
    'sub_the-door-you-closed': 'ठुकराए हुए मौके',
    // v3.23 subtitles
    'sub_the-emotion-you-hide': 'बस एक बार नाम दें।',
    'sub_your-comfort-lie': 'वो छोटा झूठ जो आरामदायक रखता है।',
    'sub_the-silence-between': 'दो विचारों के बीच बैठें।',
    'sub_what-you-carry': 'अदृश्य वज़न का नाम दें।',
    'sub_the-version-you-lost': 'ज़िंदगी से पहले आप कौन थे।',
    'sub_letter-to-past-self': 'पुराने आप को लिखें।',
    'sub_the-moment-you-knew': 'वो पल जब सब बदल गया।',
    'sub_the-words-you-rehearse': 'जो कहने का अभ्यास करते हैं पर कभी नहीं कहते।',
    'sub_someone-thinks-of-you': 'अभी, कहीं, कोई आपको याद करता है।',
    'sub_the-question-you-fear': 'वो सवाल जो कोई न पूछे यही चाहते हैं।',
    // v4.0 subtitles
    'sub_recurring-dream-journal': 'वो सपना जो बार-बार आता है।',
    'sub_lucid-moment': 'सपने में जागरूक हो जाएं।',
    'sub_nightmare-you-remember': 'वो जो अभी भी परेशान करता है।',
    'sub_dream-you-wish-was-real': 'जागो। वो सच नहीं था।',
    'sub_the-dream-visitor': 'कोई बार-बार सपनों में आता है।',
    'sub_the-blank-canvas': 'शून्य से कुछ बनाएं।',
    'sub_song-that-is-you': 'अगर आप एक गाना होते, कौन सा?',
    'sub_color-of-today': 'अपनी वर्तमान स्थिति का रंग चुनें।',
    'sub_the-unwritten-poem': 'जो अंदर रखा है वो लिखें।',
    'sub_your-inner-voice': 'असल में क्या कहती है?',
    'sub_fear-you-named': 'नाम दें और शक्ति घटे।',
    'sub_the-leap': 'किसमें कूदेंगे?',
    'sub_courage-inventory': 'हर बहादुर काम की सूची।',
    'sub_what-scares-you-most': 'सबसे गहरा डर।',
    'sub_facing-the-dark': 'पूर्ण अंधेरे में बैठें।',
    'sub_letter-to-future-self': 'जो बनेंगे उन्हें लिखें।',
    'sub_the-world-you-want': 'वो दुनिया बताएं जो चाहते हैं।',
    'sub_your-next-chapter': 'कहानी में आगे क्या?',
    'sub_the-wish': 'एक इच्छा। कोई नियम नहीं।',
    'sub_if-you-had-one-year': '365 दिनों में क्या करेंगे?',
    // v4.0 extras
    'dream_journal_placeholder': 'मुझे बार-बार यह सपना आता है...',
    'record_dream': 'दर्ज करें',
    'dream_journal_reflection': 'सपने वो कहते हैं जो जागता मन नहीं कहता।',
    'quote_dream_journal': 'बार-बार आने वाले सपने अपने आप से अधूरी बातचीत हैं।',
    'become_aware': 'मैं जागरूक हूं',
    'lucid_aware': 'जागरूकता का यह क्षण ही एकमात्र सच्ची चीज़ है।',
    'quote_lucid_moment': 'जब आप वास्तविकता पर सवाल उठाते हैं, तभी आप इसे सच में देखते हैं।',
    'wish_placeholder': 'मेरी इच्छा है...',
    'make_wish': 'इच्छा करें',
    'quote_wish': 'आप जो चाहते हैं वो बताता है कि आप सबसे ज़्यादा क्या महत्व देते हैं।',
    'update_v4_languages': '4 नई भाषाएं: हिंदी, कोरियाई, जापानी, तुर्की',
    'update_v4_20_rooms': '20 नए कमरे: सपने, रचनात्मकता, भय और भविष्य',
    'update_v4_dreams_category': 'सपने और अवचेतन कमरे की श्रेणी',
    'update_v4_creativity_category': 'रचनात्मकता और अभिव्यक्ति कमरे की श्रेणी',
    'update_v4_fear_category': 'भय और साहस कमरे की श्रेणी',
    'update_v4_future_category': 'भविष्य और आशा कमरे की श्रेणी',
    'room_playlists': 'कमरा प्लेलिस्ट',
    'personal_dashboard': 'डैशबोर्ड',
    'dashboard_subtitle': 'एक नज़र में आपकी यात्रा',
    'visit_pattern': 'विज़िट पैटर्न (14 दिन)',
    'dashboard_top_rooms': 'सबसे अधिक देखे गए कमरे',
    'category_breakdown': 'कमरे की श्रेणियां',
    'mood_trend': 'मनोदशा प्रवृत्ति',
    'fragment_breakdown': 'टुकड़ों का विवरण',
    'no_data_yet': 'अभी तक कोई डेटा नहीं',
    'current_streak': 'दिन की लकीर',
    'playlist_morning': 'सुबह का चिंतन',
    'playlist_morning_desc': 'स्पष्टता और उपस्थिति के साथ अपना दिन शुरू करें',
    'playlist_identity': 'पहचान गहराई',
    'playlist_identity_desc': 'लेबल के नीचे आप कौन हैं?',
    'playlist_creative': 'रचनात्मक चिंगारी',
    'playlist_creative_desc': 'अपनी रचनात्मक अभिव्यक्ति खोलें',
    'playlist_courage': 'साहस का मार्ग',
    'playlist_courage_desc': 'एक-एक कमरा करके अपने डर का सामना करें',
    'playlist_dreams': 'सपनों की डायरी',
    'playlist_dreams_desc': 'अपने अवचेतन के परिदृश्यों का अन्वेषण करें',
    'playlist_future': 'भविष्य दृष्टि',
    'playlist_future_desc': 'आने वाले आप के लिए पत्र और इच्छाएं',
    'playlist_truth': 'सत्य का सामना',
    'playlist_truth_desc': 'असहज वास्तविकताओं का सामना करें',
    'playlist_night': 'रात की यात्रा',
    'playlist_night_desc': 'मौन, शून्य और कुछ भी नहीं को अपनाएं',
    // v4.2.0
    'room_focus-timer': 'फोकस टाइमर', 'sub_focus-timer': 'चुपचाप बैठें। सोचें।', 'room_gratitude-jar': 'कृतज्ञता का जार', 'sub_gratitude-jar': 'एक चीज़ जिसके लिए आप आभारी हैं', 'room_mood-compass': 'मूड कंपास', 'sub_mood-compass': 'आप अभी कहाँ हैं?', 'room_memory-fade': 'स्मृति विलोप', 'sub_memory-fade': 'एक याद को घुलते देखें', 'room_sound-of-your-day': 'आपके दिन की आवाज़', 'sub_sound-of-your-day': 'आज कैसी आवाज़ है?', 'room_the-apology-room': 'माफ़ी का कमरा', 'sub_the-apology-room': 'वो लिखें जो कभी नहीं कहा', 'room_your-unread-messages': 'आपके अपठित संदेश', 'sub_your-unread-messages': 'संदेश जो कभी नहीं खोले', 'room_the-weight-room': 'भार का कमरा', 'sub_the-weight-room': 'जो बोझ है उसे नाम दें', 'room_parallel-lives': 'समानांतर जीवन', 'sub_parallel-lives': 'एक और जीवन में, आप...', 'room_the-quiet-score': 'शांति का स्कोर', 'sub_the-quiet-score': 'कितनी देर स्थिर रह सकते हैं?',
    'focus_choose': 'कितनी देर चुपचाप बैठना चाहते हैं?', 'focus_sit': 'बैठें। साँस लें। विचारों को जाने दें।', 'focus_done': 'मौन ने बात कही।', 'quote_focus_timer': 'स्थिरता में, आप वो सुनते हैं जो शोर डुबा देता है।', 'minutes': 'मिनट',
    'gratitude_prompt': 'आज जिसके लिए आभारी हैं वो लिखें।', 'gratitude_placeholder': 'मैं आभारी हूँ...', 'gratitude_add': 'जार में डालें', 'gratitude_added': 'आपकी कृतज्ञता जार में तैरती है।', 'gratitude_count': 'कृतज्ञता के पल', 'quote_gratitude_jar': 'कृतज्ञता ज़्यादा पाने के बारे में नहीं। यह पहले से यहाँ जो है उसे देखने के बारे में है।',
    'mood_compass_prompt': 'जहाँ आप महसूस करते हैं वहाँ क्लिक करें।', 'mood_high_energy': 'उच्च ऊर्जा', 'mood_low_energy': 'कम ऊर्जा', 'mood_positive': 'सकारात्मक', 'mood_negative': 'नकारात्मक', 'quote_mood_compass': 'आप कहाँ हैं यह जानना, कहाँ जाना है यह जानने का पहला कदम है।',
    'memory_fade_prompt': 'एक याद लिखें। उसे घुलते देखें।', 'memory_fade_placeholder': 'एक याद जो छोड़ना चाहते हैं...', 'memory_fade_start': 'इसे मिटने दें', 'memory_fade_done': 'अब चली गई। लेकिन वो सच थी।', 'quote_memory_fade': 'यादें घुलती हैं चाहे हम देखें या न देखें।',
    'sound_day_prompt': '3-5 आवाज़ें चुनें जो आपके दिन का प्रतिनिधित्व करें।', 'sound_rain': 'बारिश', 'sound_traffic': 'ट्रैफिक', 'sound_birds': 'पक्षी', 'sound_keyboard': 'कीबोर्ड', 'sound_silence': 'मौन', 'sound_wind': 'हवा', 'sound_ocean': 'समुद्र', 'sound_crowd': 'भीड़', 'sound_music': 'संगीत', 'sound_day_confirm': 'यह मेरा दिन है', 'sound_day_result': 'आपके दिन का संगीत।', 'quote_sound_day': 'हर दिन की एक आवाज़ होती है। अधिकांश लोग सुनना भूल जाते हैं।',
    'apology_prompt': 'एक माफ़ी लिखें जो कभी नहीं दी।', 'apology_placeholder': 'मुझे माफ़ करें...', 'apology_release': 'छोड़ दें', 'apology_done': 'कुछ नहीं में सिमट गई। लेकिन आपने कहा।', 'quote_apology': 'कुछ माफ़ियाँ उनके लिए नहीं होतीं। वे आपके लिए होती हैं।',
    'unread_from_past': 'से: आपका पिछला स्व', 'unread_from_future': 'से: आपका भविष्य स्व', 'unread_from_critic': 'से: आपका आंतरिक आलोचक', 'unread_msg_past': 'याद है जब आपने खुद से वादा किया था कि चीज़ें अलग होंगी?', 'unread_msg_future': 'मुझे उम्मीद है कि अब आप अपने प्रति पहले से ज़्यादा दयालु हैं।', 'unread_msg_critic': 'आप जानते हैं कि आप और कर सकते थे। लेकिन क्या करेंगे?', 'unread_count': 'अपठित', 'unread_reply_placeholder': 'आपका जवाब...', 'unread_reply': 'भेजें', 'unread_sent': 'संदेश शून्य में भेजा गया।', 'quote_unread_messages': 'जो संदेश हम टालते हैं वो अक्सर सबसे ज़रूरी होते हैं।',
    'weight_prompt': 'जो बोझ है उसे नाम दें।', 'weight_placeholder': 'कुछ जो मुझ पर भारी है...', 'weight_release_start': 'छोड़ना शुरू करें', 'weight_release_prompt': 'हर बोझ पर क्लिक करें छोड़ने के लिए।', 'weight_done': 'बोझ उठ गया। अभी के लिए।', 'quote_weight_room': 'आप वो नहीं रख सकते जो नाम देने से इनकार करते हैं।',
    'parallel_intro': 'एक और जीवन में, आप...', 'parallel_life_1': 'एक और जीवन में, आप एक दूर के द्वीप पर लाइटहाउस कीपर हैं।', 'parallel_life_2': 'एक और जीवन में, आपने कभी अपना गाँव नहीं छोड़ा।', 'parallel_life_3': 'एक और जीवन में, आपने हाँ कहा जब आपने ना कहा।', 'parallel_life_4': 'एक और जीवन में, आप पूरी तरह कोई और हैं।', 'parallel_life_5': 'एक और जीवन में, आप ठीक वही हैं जो अभी हैं।', 'parallel_life_6': 'एक और जीवन में, आपने कठिन रास्ता चुना।', 'parallel_life_7': 'एक और जीवन में, आप अभी भी यह कमरा ढूंढ रहे हैं।', 'quote_parallel_lives': 'हर चुनाव एक ब्रह्मांड बनाता है जहाँ आपने अलग चुना।',
    'quiet_instruction': 'बातचीत न करें। बस स्थिर रहें।', 'quiet_reveal_1': 'आप यहाँ हैं।', 'quiet_reveal_2': 'शोर कम हो रहा है।', 'quiet_reveal_3': 'शांति से कुछ उभर रहा है।', 'quiet_reveal_4': 'आप उससे ज़्यादा धैर्यवान हैं जितना सोचते हैं।', 'quiet_reveal_5': 'स्थिरता आपका नाम जानती है।', 'quiet_score_label': 'आपका शांति स्कोर', 'quiet_impressive': 'उल्लेखनीय स्थिरता।', 'quiet_good': 'आपने कुछ शांति पाई।', 'quiet_try_again': 'कार्य करने की इच्छा प्रबल है।', 'quote_quiet_score': 'स्थिरता गति की अनुपस्थिति नहीं। यह जागरूकता की उपस्थिति है।',
    'update_v42_10_rooms': '10 नए इंटरैक्टिव कमरे: फोकस टाइमर, कृतज्ञता जार, मूड कंपास और अधिक', 'update_v42_focus_timer': 'फोकस टाइमर — अंधेरा होते टाइमर में चुपचाप बैठें', 'update_v42_gratitude_jar': 'कृतज्ञता का जार — समय के साथ कृतज्ञता के पल इकट्ठा करें', 'update_v42_mood_compass': 'मूड कंपास — ऊर्जा और भावना अक्षों पर खुद को रखें', 'update_v42_weight_room': 'भार का कमरा — नाम दें और छोड़ दें जो बोझ है',
    'update_v43_full_localization': 'पूर्ण स्थानीयकरण — सभी कमरे अब पूरी तरह अनुवादित', 'update_v43_40_rooms_translated': '40+ कमरे अनुवादित — हर इंटरैक्टिव कमरा सभी भाषाओं का समर्थन करता है', 'update_v43_10_languages': '10 भाषाएँ समर्थित — अंग्रेज़ी, अरबी, स्पेनिश, पुर्तगाली, चीनी, फ्रेंच, हिंदी, कोरियाई, जापानी, तुर्की', 'update_v43_nav_consistency': 'सुसंगत नेविगेशन — सभी कमरों में एकीकृत निकास पाठ',
    // Daily Quote Room
    'quote_day_number': 'दिन {day}',
    'quote_come_back': 'कल एक नए उद्धरण के लिए वापस आएं।',
    // Leaderboard
    'leaderboard': 'समुदाय लीडरबोर्ड',
    'leaderboard_subtitle': 'गुमनाम · सामूहिक · कोई प्रतिस्पर्धा नहीं',
    'collective_time': 'सामूहिक समय',
    'achievements_count': 'उपलब्धियां',
    'most_visited_rooms': 'सबसे ज़्यादा देखे गए कमरे',
    'most_time_spent': 'सबसे ज़्यादा समय बिताया',
    'most_unlocked_achievements': 'सबसे ज़्यादा खोली गई उपलब्धियां',
    'no_achievements_yet': 'अभी तक कोई उपलब्धि नहीं खोली गई।',
    // Transfer Data
    'transfer_data': 'डेटा ट्रांसफर',
    'transfer_data_desc': 'एक बार के कोड का उपयोग करके अपनी प्रगति दूसरे डिवाइस पर ले जाएं।',
    'generate_transfer_code': 'ट्रांसफर कोड बनाएं',
    'enter_transfer_code': 'कोड दर्ज करें...',
    'transfer_code_expires': 'कोड 24 घंटे में समाप्त होता है और केवल एक बार उपयोग किया जा सकता है।',
    'generating': 'बन रहा है...',
    'import': 'आयात करें',
    'copied': 'कॉपी हो गया!',
    
    // Waiting Game Room
    'waiting_instruction': 'हिलो मत। क्लिक मत करो। बस इंतज़ार करो।',
    'waiting_slow': 'धीमा होना जितना लगता है उतना आसान नहीं है।',
    'waiting_unfamiliar': 'यह स्थिरता अजीब लगती है, है ना?',
    'waiting_most': 'ज़्यादातर लोग अब तक चले जाते हैं।',
    'waiting_learning': 'आप अपने बारे में कुछ सीख रहे हैं।',
    'waiting_rewards': 'धैर्य के अपने इनाम होते हैं।',
    'waiting_seconds': 'सेकंड की स्थिरता',
    'waiting_waiting': 'इंतज़ार कर रहे हैं...',
    'waiting_moved': 'आपने {count} बार हिला।',
    'waiting_waited': 'आपने इंतज़ार किया। यह दुर्लभ है।',
    'waiting_most_cant': 'ज़्यादातर लोग खाली बैठ नहीं सकते। आपने किया।',
    'waiting_quote': 'वर्तमान क्षण ही एकमात्र क्षण है जो हमारे लिए उपलब्ध है, और यह सभी क्षणों का द्वार है।',
    // Feature Navigation
    'shared_room': 'साझा कमरा',
    'imagine_room': 'कल्पना',
    'dream_room': 'सपने',
    'future_room': 'भविष्य',
    'ai_reflection': 'एआई चिंतन',
    // Imagine Yourself Room
    'imagine_title': 'खुद की कल्पना करें',
    'imagine_subtitle': 'वर्णन करें कि आप खुद को कैसे देखते हैं। देखें कि आपके शब्दों से क्या उभरता है।',
    'imagine_placeholder': 'मैं खुद को ऐसा देखता/देखती हूँ...\n\nअपने सार का वर्णन करें, अपनी प्रकृति का, अंदर से कैसा महसूस करते हैं।',
    'imagine_hint': 'ईमानदार रहें। छवि आपके शब्दों को दर्शाती है।',
    'imagine_generate': 'खुद को देखें',
    'imagine_generating': 'बना रहे हैं...',
    'imagine_try_again': 'फिर से वर्णन करें',
    'imagine_result_alt': 'आपका आत्म-चित्र',
    'imagine_quote': 'हम खुद को स्पष्ट नहीं देखते। हम वो देखते हैं जो हम मानते हैं।',
    'imagine_empty_error': 'पहले अपने बारे में कुछ लिखें',
    'imagine_short_error': 'हमें अपने बारे में और बताएं',
    'rate_limit_error': 'बहुत ज़्यादा अनुरोध। कृपया प्रतीक्षा करें।',
    'credits_error': 'सेवा अस्थायी रूप से अनुपलब्ध',
    'generation_error': 'छवि नहीं बन सकी। फिर से प्रयास करें।',
    // Dream Visualization Room
    'dream_title': 'सपनों का दृश्यांकन',
    'dream_subtitle': 'एक बार-बार आने वाले सपने का वर्णन करें। उसे साकार होते देखें।',
    'dream_placeholder': 'मैं बार-बार सपना देखता/देखती हूँ...\n\nदृश्यों, भावनाओं, बार-बार आने वाले तत्वों का वर्णन करें।',
    'dream_hint': 'सपने प्रतीकों में बोलते हैं।',
    'dream_generate': 'सपना देखें',
    'dream_generating': 'सपना देख रहे हैं...',
    'dream_try_again': 'फिर से सपना देखें',
    'dream_result_alt': 'आपके सपने का दृश्यांकन',
    'dream_quote': 'सपने अवचेतन मन का हमें कुछ बताने का प्रयास हैं।',
    'dream_empty_error': 'पहले अपने सपने का वर्णन करें',
    'dream_short_error': 'हमें अपने सपने के बारे में और बताएं',
    // Future Self Room
    'future_title': 'भविष्य का स्व',
    'future_subtitle': 'वर्णन करें कि आप क्या बनना चाहते हैं। अपनी संभावना देखें।',
    'future_placeholder': 'भविष्य में, मैं खुद को ऐसा देखता/देखती हूँ...\n\nउस व्यक्ति का वर्णन करें जो आप बनने के लिए काम कर रहे हैं।',
    'future_hint': 'आकांक्षा वास्तविकता को आकार देती है।',
    'future_generate': 'अपना भविष्य देखें',
    'future_generating': 'कल्पना कर रहे हैं...',
    'future_try_again': 'फिर से कल्पना करें',
    'future_result_alt': 'आपका भविष्य स्व',
    'future_quote': 'भविष्य वो नहीं जहाँ हम जाते हैं। वो है जो हम बनाते हैं।',
    'future_empty_error': 'पहले अपने भविष्य स्व का वर्णन करें',
    'future_short_error': 'हमें अपने भविष्य स्व के बारे में और बताएं',
    // Guided Journeys
    'guided_journeys_title': 'निर्देशित यात्राएं',
    'guided_journeys_subtitle': 'कथात्मक कनेक्टर के साथ क्यूरेटेड कमरा अनुक्रम। निर्देशित ध्यान प्लेलिस्ट की तरह।',
    'journey_complete': 'यात्रा पूर्ण',
    'journey_complete_desc': 'आपने पूरा रास्ता तय किया। कमरे आपकी यात्रा याद रखते हैं।',
    'more_journeys': 'और यात्राएं',
    'next_room': 'अगला कमरा',
    'enter_room': 'कमरे में प्रवेश',
    'finish_journey': 'यात्रा समाप्त',
    'step': 'चरण',
    // Fragment Collection
    'fragment_collection_title': 'टुकड़ों का संग्रह',
    'fragment_collection_subtitle': 'हर कमरा कुछ पीछे छोड़ता है। सब इकट्ठा करें।',
    'collection_milestones': 'मील के पत्थर',
    'collection_secrets': 'रहस्य',
    'collection_footer': 'कुछ टुकड़े केवल कुछ शर्तों पर दिखते हैं।',
    'rarity_common': 'सामान्य',
    'rarity_rare': 'दुर्लभ',
    'rarity_legendary': 'पौराणिक',
    // AI Companion
    'companion_create': 'अपना साथी बनाएं',
    'companion_setup_desc': 'एक प्रतिबिंब जो आपके साथ चलता है।',
    'companion_name': 'अपने साथी का नाम रखें',
    'companion_name_placeholder': 'जैसे, प्रतिध्वनि, दर्पण, छाया...',
    'companion_lens': 'व्यक्तित्व लेंस',
    'companion_mood': 'संवाद शैली',
    'companion_fears': 'आप किससे डरते हैं? (वैकल्पिक)',
    'companion_struggles': 'आप किससे जूझते हैं? (वैकल्पिक)',
    'create_companion': 'बनाएं',
    'companion_input': 'कुछ कहें...',
    'companion_greeting': 'आपका साथी यहाँ है। कुछ कहें।',
    'companion_starter_1': 'आज मेरे मन में क्या है?',
    'companion_starter_2': 'मैं अटका/अटकी महसूस करता/करती हूँ।',
    'companion_starter_3': 'मुझे बताओ जो मैं टालता/टालती हूँ।',
    'fears_placeholder': 'जैसे, असफलता, अस्वीकृति, भुला दिया जाना...',
    'struggles_placeholder': 'जैसे, अति-सोच, टालमटोल, अकेलापन...',
    'back_step': 'वापस',
    'lens_reflective': 'चिंतनशील',
    'lens_reflective_desc': 'गहरे सवाल, पैटर्न पहचान',
    'lens_supportive': 'सहायक',
    'lens_supportive_desc': 'गर्म, प्रोत्साहक, मान्य',
    'lens_challenging': 'चुनौतीपूर्ण',
    'lens_challenging_desc': 'प्रत्यक्ष, विचारोत्तेजक',
    'lens_curious': 'जिज्ञासु',
    'lens_curious_desc': 'अप्रत्याशित सवाल, आश्चर्य',
    'lens_shadow': 'छाया',
    'lens_shadow_desc': 'छिपा पक्ष, ईमानदार बेचैनी',
    'companion_mood_calm': 'शांत',
    'companion_mood_calm_desc': 'संतुलित और शांतिपूर्ण',
    'companion_mood_intense': 'तीव्र',
    'companion_mood_intense_desc': 'तत्काल और गहरा',
    'companion_mood_playful': 'चंचल',
    'companion_mood_playful_desc': 'मजाकिया और हल्का',
    'companion_mood_melancholic': 'उदास',
    'companion_mood_melancholic_desc': 'सुंदर उदासी',
    // AI Reflection Room
    'reflection_room_title': 'चिंतन कक्ष',
    'reflection_room_desc': 'कुछ ईमानदार लिखें। दर्पण आपको वो दिखाएगा जो आप शायद न देखें।',
    'reflection_room_privacy': 'कुछ सेव नहीं होता। कुछ साझा नहीं होता।',
    'reflection_begin': 'शुरू करें',
    'reflection_prompt': 'आपके मन में क्या है? स्वतंत्र रूप से लिखें।',
    'reflection_placeholder': 'यहाँ शुरू करें...',
    'reflection_characters': 'अक्षर',
    'reflection_min_chars': 'कम से कम 20 चाहिए',
    'reflection_send': 'दर्पण को भेजें',
    'reflection_waiting': 'दर्पण विचार कर रहा है...',
    'reflection_fragment_label': 'टुकड़ा',
    'reflection_label': 'चिंतन',
    'reflection_question_label': 'वो सवाल जिससे आप बच सकते हैं',
    'reflection_leave': 'दर्पण छोड़ें',
    'reflection_error': 'दर्पण धुंधला है',
    'reflection_default_quote': 'दर्पण याद रखता है',
    // Room Content - Image Zooming
    'image_zoom_title': 'वो तस्वीर जिसे आप ज़ूम करते रहते हैं',
    'image_zoom_zoom': 'ज़ूम',
    'image_zoom_quote': 'और कुछ खोजने को नहीं है। कभी था ही नहीं।',
    // Room Content - Shadow Chase
    'shadow_chase_title': 'छाया का पीछा',
    'shadow_chase_subtitle': 'छाया को पकड़ो • यह अप्रत्याशित रूप से प्रतिक्रिया करती है',
    'shadow_chase_catches': 'पकड़ा',
    'shadow_chase_behavior': 'व्यवहार',
    'shadow_chase_flee': 'भागना',
    'shadow_chase_wander': 'भटकना',
    'shadow_chase_mirror': 'दर्पण',
    'shadow_chase_msg_1': 'छाया जानती है आप कहाँ जा रहे हैं।',
    'shadow_chase_msg_2': 'जिसका पीछा करते हैं वो अक्सर भागता है।',
    'shadow_chase_msg_3': 'कभी-कभी शांति गति से ज़्यादा पकड़ती है।',
    'shadow_chase_msg_4': 'आपकी छाया हर गति याद रखती है।',
    'shadow_chase_msg_5': 'यह आपसे सीखती है।',
    // Room Content - Left Too Early
    'left_early_title': 'बहुत जल्दी चले गए',
    'left_early_subtitle': '5 सेकंड से कम में छोड़े गए कमरे।',
    'left_early_none': 'आपने कोई कमरा जल्दी नहीं छोड़ा।',
    'left_early_patience': 'शायद आप चीज़ों को समय देते हैं।',
    'left_early_quote': 'कभी-कभी हम कमरे के कुछ दिखाने से पहले ही चले जाते हैं।',
    // Room Content - Face Without Features
    'face_title': 'बिना चेहरे के चेहरा',
    'face_instruction': 'हटाने के लिए किसी विशेषता पर क्लिक करें।',
    'face_nothing': 'कुछ नहीं बचा।',
    'face_remove': 'हटाएं',
    'face_remaining': 'बाकी',
    'face_eyes': 'आँखें',
    'face_nose': 'नाक',
    'face_mouth': 'मुँह',
    'face_eyebrows': 'भौंहें',
    'face_outline': 'चेहरा',
    'face_quote': 'पहचान बस वो विशेषताएं हैं जिन्हें पहचानने पर हम सहमत हैं।',
    // Room Content - The Last Visitor
    'last_visitor_title': 'आखिरी आगंतुक',
    'last_visitor_someone': 'कोई यहाँ था',
    'last_visitor_first': 'आप पहले आगंतुक हैं।',
    'last_visitor_no_one': 'आपसे पहले कोई नहीं आया।',
    'last_visitor_next': 'अगला व्यक्ति आपका समय देखेगा।',
    'last_visitor_know': 'उन्हें पता चलेगा कि आप यहाँ थे।',
    'last_visitor_quote': 'हम अंधेरे में भी निशान छोड़ते हैं।',
    'last_visitor_days_ago': '{count} दिन पहले',
    'last_visitor_hours_ago': '{count} घंटे पहले',
    'last_visitor_minutes_ago': '{count} मिनट पहले',
    'last_visitor_moments_ago': 'कुछ पल पहले',
    // Room Content - Room You Visit Most
    'visit_most_title': 'सबसे ज़्यादा देखा गया कमरा',
    'visit_most_subtitle': 'आपका व्यवहार आपके पैटर्न प्रकट करता है।',
    'visit_most_visits': '{count} बार',
    'visit_most_none': 'अभी कोई पैटर्न नहीं। खोजते रहें।',
    'visit_most_quote': 'हम वहाँ लौटते हैं जहाँ ज़रूरत है, वहाँ नहीं जहाँ चाहत।',
    // Room Content - 3:17 AM Room
    'room_317_locked': 'यह कमरा सिर्फ सुबह 3:17 पर खुलता है।',
    'room_317_opens_in': '{hours}घंटे {minutes}मिनट में खुलेगा',
    'room_317_msg_1': 'सुबह के 3:17 बज रहे हैं।',
    'room_317_msg_2': 'आप जागे हुए हैं।',
    'room_317_msg_3': 'क्यों?',
    'room_317_msg_4': 'दुनिया सो रही है। लेकिन आप नहीं।',
    'room_317_msg_5': 'कौन सा विचार आपको छोड़ नहीं रहा?',
    'room_317_msg_6': 'इस समय, मुखौटे गिर जाते हैं।',
    'room_317_msg_7': 'बस आप हैं और मौन।',
    'room_317_msg_8': 'यह पल सिर्फ अभी है।',
    'room_317_quote': 'इस समय, मुखौटे गिर जाते हैं।',
    // Room Content - Weekend Room
    'weekend_locked': 'यह कमरा सिर्फ सप्ताहांत में खुलता है।',
    'weekend_days_until': 'इस कमरे के खुलने में {count} दिन।',
    'weekend_label': 'सप्ताहांत कमरा',
    'weekend_prompt': 'हफ्ता खत्म हो गया। इन दो दिनों में आप क्या लेकर आए?',
    'weekend_placeholder': 'एक बोझ, एक उम्मीद, एक सवाल...',
    'weekend_release': 'छोड़ दें',
    'weekend_wrote': 'आपने लिखा:',
    'weekend_letting_go': 'सप्ताहांत छोड़ने के लिए है।',
    // Room Content - Something Follows
    'follows_title': 'कुछ पीछा करता है',
    'follows_distance': 'दूरी',
    'follows_run': 'भागो',
    'follows_keep_running': 'भागते रहो। यह कभी नहीं रुकता।',
    'follows_caught': 'इसने आपको पकड़ लिया।',
    'follows_couldnt': 'आप हमेशा नहीं भाग सकते थे।',
    'follows_quote': 'जो पीछा करता है वो हमेशा आप खुद हैं।',
    // Room Content - Line You Crossed
    'line_title': 'वो रेखा जो आपने पार की',
    'line_instruction': 'बाएं से दाएं एक रेखा खींचें।',
    'line_click_drag': 'क्लिक करें और खींचें',
    'line_hesitations': 'हमें {count} झिझक मिली।',
    'line_quote': 'हर विराम एक संदेह प्रकट करता है।',
    // Scary Room Content
    'dont_look_title': 'नज़र मत हटाओ',
    'dont_look_away_msg': 'नज़र मत हटाओ।',
    'dont_look_looked': 'आपने नज़र हटा ली।',
    'dont_look_seconds': '{count} सेकंड का ध्यान',
    'dont_look_times': '{count} बार नज़र हटाई',
    'dont_look_quote': 'उपस्थिति देना सबसे कठिन है।',
    // Batch 2 Room Content
    'mirror_lies_title': 'शीशा झूठ बोलता है', 'mirror_lies_msg1': 'खुद को देखो।', 'mirror_lies_msg2': 'क्या वो सच में तुम हो?', 'mirror_lies_msg3': 'शीशा वो दिखाता है जो तुम देखना चाहते हो।', 'mirror_lies_msg4': 'सत्य प्रकट करने के लिए क्लिक करें।', 'mirror_lies_msg5': 'हर दरार और अधिक प्रकट करती है।', 'mirror_lies_msg6': 'प्रतिबिंब के पीछे कुछ नहीं है।', 'mirror_lies_quote': 'शीशे ने तुम्हें कभी नहीं दिखाया। बस वो जो तुम मानते थे।',
    'winter_title': 'सबसे लंबी रात', 'winter_date': '21 दिसंबर', 'winter_msg1': 'सबसे लंबी रात आ गई।', 'winter_msg2': 'अंधेरे में, हम वो पाते हैं जो हम ढोते हैं।', 'winter_msg3': 'तुम क्या छोड़ोगे? क्या रखोगे?', 'winter_releasing': 'मैं छोड़ रहा हूँ:', 'winter_keeping': 'मैं रख रहा हूँ:', 'winter_release_placeholder': 'जो अब काम नहीं आता...', 'winter_keep_placeholder': 'जो ताकत देता है...', 'winter_enter_light': 'प्रकाश में प्रवेश करें', 'winter_released': 'छोड़ दिया:', 'winter_kept': 'रख लिया:', 'winter_light_returns': 'इस रात से, प्रकाश लौटता है।', 'winter_quote': 'सबसे लंबी रात सिखाती है कि गहरे अंधेरे में भी, प्रकाश पहले से लौट रहा है।',
    'halloween_title': 'पर्दे का कमरा', 'halloween_date': '31 अक्टूबर', 'halloween_msg1': 'आज रात पर्दा पतला है।', 'halloween_msg2': 'तुम्हारी छायाओं में क्या छिपा है?', 'halloween_msg3': 'उस डर का नाम लो जो तुम ढोते हो...', 'halloween_msg4': 'इसे आकार दो, और छोड़ दो।', 'halloween_placeholder': 'अपना डर लिखो...', 'halloween_release': 'छोड़ दो', 'halloween_dissolves': 'तुम्हारा डर रात में घुल रहा है...', 'halloween_quote': 'जिन चीज़ों से हम डरते हैं, नाम देने पर उनकी शक्ति खो जाती है। जाने दो।',
    'awake_title': 'जो तुम्हें जगाए रखता है', 'awake_locked': 'यह कमरा केवल रात 10 बजे के बाद उपलब्ध है।', 'awake_return': 'रात आने पर लौटें।', 'awake_press_enter': 'एंटर दबाएं', 'awake_named': 'अब तुमने इसका नाम ले लिया।', 'awake_quote': 'अंधेरा जानता है कि तुम दिन में क्या टालते हो।',
    'voice_meaning_title': 'बिना अर्थ की तुम्हारी आवाज़', 'voice_meaning_prompt': 'गुनगुनाओ। साँस लो। कोई भी आवाज़ निकालो। शब्द नहीं।', 'voice_meaning_done': 'यह तुम्हारी उपस्थिति थी। बस इतना।', 'voice_meaning_hold': 'दबाए रखें', 'voice_meaning_recording': 'रिकॉर्ड हो रहा है...', 'voice_meaning_no_playback': 'वापस नहीं सुन सकते। आवाज़ केवल उस पल में थी।', 'voice_meaning_quote': 'तुम मौजूद हो। बस यही सामग्री है।',
    'watching_msg1': 'तुम यहाँ अकेले हो।', 'watching_msg2': 'बिल्कुल अकेले।', 'watching_msg3': 'या हो?', 'watching_msg4': 'वे पूरे समय यहाँ थे।', 'watching_msg5': 'वे हमेशा देख रहे हैं।',
    'shortcut_title': 'वो शॉर्टकट जो तुम हमेशा लेते हो', 'shortcut_instruction': 'जो पहचानते हो उन्हें चुनो।', 'shortcut_1': '"मैं ठीक हूँ" कहना जब नहीं हो', 'shortcut_2': 'मौजूद रहने की बजाय फोन देखना', 'shortcut_3': 'टकराव से बचने के लिए चुप रहना', 'shortcut_4': 'बनाने की बजाय खरीदना', 'shortcut_5': '"सही समय" का इंतज़ार करना', 'shortcut_6': 'करने की बजाय हालात को दोष देना', 'shortcut_7': 'विकास की बजाय आराम चुनना', 'shortcut_8': 'सोने की बजाय स्क्रॉल करना', 'shortcut_9': 'ना कहना चाहते हो पर हाँ कहना', 'shortcut_10': 'करने की बजाय योजना बनाना', 'shortcut_pattern': 'तुम्हारा पैटर्न', 'shortcut_selected': 'तुमने चुना:', 'shortcut_show_pattern': 'मेरा पैटर्न दिखाओ', 'shortcut_quote': 'शॉर्टकट ही रास्ता बन जाता है।', 'shortcut_pattern_own': 'तुम्हारा पैटर्न तुम्हारा है।', 'pattern_avoidance': 'तुम असुविधाजनक चीज़ों से बचते हो।', 'pattern_distraction': 'तुम विचलन में भागते हो।', 'pattern_convenience': 'तुम मेहनत को आसानी से बदलते हो।', 'pattern_delay': 'तुम उन हालात का इंतज़ार करते हो जो कभी नहीं आते।', 'pattern_comfort': 'तुम खुद को असुविधा से बचाते हो।', 'pattern_pleasing': 'तुम दूसरों को आरामदेह रखने के लिए गायब हो जाते हो।',
    'shapes_q1': 'तुम कितनी बार शांति महसूस करते हो?', 'shapes_q2': 'तुम खुद पर कितना भरोसा करते हो?', 'shapes_q3': 'तुम दूसरों से कितना जुड़ा महसूस करते हो?', 'shapes_q4': 'तुम्हारा कितना समय सच में तुम्हारा है?', 'shapes_q5': 'भविष्य कितना भारी लगता है?', 'shapes_result_title': 'आकारों में तुम्हारी ज़िंदगी', 'shapes_no_labels': 'कोई लेबल नहीं। कोई व्याख्या नहीं।', 'shapes_yours': 'जो तुम देखते हो वो तुम्हारा है समझने के लिए।', 'shapes_quote': 'आकार बिना शब्दों का अर्थ है।', 'shapes_scale': '1 = बिल्कुल नहीं, 5 = पूरी तरह',
    'read_title': 'इसे ज़ोर से पढ़ो', 'read_stmt1': 'मैं किसी चीज़ से बच रहा हूँ।', 'read_stmt2': 'मैं डरा हुआ हूँ।', 'read_stmt3': 'मुझे नहीं पता मैं क्या कर रहा हूँ।', 'read_stmt4': 'मुझे मदद चाहिए।', 'read_stmt5': 'मैं काफ़ी हूँ।', 'read_stmt6': 'मैं दिखावा कर रहा था।', 'read_stmt7': 'मैं थक गया हूँ।', 'read_stmt8': 'मैं और चाहता हूँ।', 'read_i_read': 'मैंने ज़ोर से पढ़ा', 'read_really': 'क्या तुमने सच में कहा?', 'read_skipped': 'मैंने छोड़ दिया', 'read_result': 'तुमने सच ज़ोर से बोले।', 'read_quote': 'सच बोलना उसे असली बनाता है।',
    'sentence_title': 'वो वाक्य जो तुम टालते हो', 'sentence_sub': 'यह वाक्य पूरा करो। बिना पूरा किए जा नहीं सकते।', 'sentence_prompt': 'मैं ज़्यादा खुश होता अगर मैं बंद कर देता', 'sentence_cant_leave': 'कुछ लिखे बिना नहीं जा सकते।', 'sentence_knew': 'तुम यह पहले से जानते थे।', 'sentence_quote': 'पहला कदम है इसे कहना।',
    'say_once_title': 'एक बार कहो', 'say_once_prompt': 'एक वाक्य रिकॉर्ड करो। दोबारा सुन नहीं सकते। दोबारा रिकॉर्ड नहीं कर सकते।', 'say_once_done': 'कह दिया गया। कोई रिप्ले नहीं। कोई दोबारा नहीं।', 'say_once_stop': 'रुको', 'say_once_record': 'रिकॉर्ड', 'say_once_quote': 'कुछ चीज़ें बस एक बार कहने के लिए होती हैं।',
    'newyear_title': 'भविष्य के खुद को पत्र', 'newyear_threshold': 'दहलीज़', 'newyear_placeholder': 'प्रिय भविष्य के मैं...', 'newyear_seal': 'पत्र सील करें', 'newyear_waiting': 'यह पत्र तुम्हारा इंतज़ार करेगा।', 'newyear_quote': 'हर अंत एक दरवाज़ा है। इरादे से गुज़रो।',
    'avoid_title': 'वो कमरा जो तुम टालते हो', 'avoid_sub': 'जो तुम छोड़ते हो वो कुछ कहता है।', 'avoid_not_entered': 'तुमने प्रवेश नहीं किया:', 'avoid_visited_all': 'तुमने हर मुफ़्त कमरा देखा।', 'avoid_not_pattern': 'शायद टालना तुम्हारा पैटर्न नहीं है।', 'avoid_quote': 'टालना भी एक चुनाव है।',
    'who_q1': 'अगर पैसे की चिंता न हो तो तुम क्या करोगे?', 'who_q2': 'पार्टी में अपना परिचय कैसे दोगे?', 'who_q3': 'क्या तुम्हें जीवित महसूस कराता है?', 'who_q4': 'तुम्हारे दोस्त कहेंगे तुम किसमें अच्छे हो?', 'who_q5': 'बचपन में क्या करना पसंद था?', 'who_result_title': 'बिना उपाधि के तुम', 'who_done': 'यह वो है जो तुम हो जब उपाधि चली जाती है।', 'who_quote': 'पहचान वो नहीं है जो तुम करते हो। वो है जो रुकने पर बचता है।', 'who_placeholder': 'ईमानदार रहो...', 'who_see_myself': 'खुद को देखो', 'question': 'सवाल',
    'lastconv_title': 'आखिरी बातचीत', 'lastconv_sub': 'वो लिखो जो तुम कभी नहीं भेजोगे।', 'lastconv_to': 'को:', 'lastconv_message': 'संदेश:', 'lastconv_to_placeholder': 'कोई जिसे यह सुनना चाहिए...', 'lastconv_msg_placeholder': 'जो तुम हमेशा कहना चाहते थे...', 'lastconv_not_saved': 'यह संदेश सहेजा या साझा नहीं किया जाएगा।', 'lastconv_seal': 'पत्र सील करें', 'lastconv_wrote_to': 'तुमने लिखा:', 'lastconv_never_sent': 'यह संदेश कभी नहीं भेजा जाएगा।', 'lastconv_point': 'लेकिन लिखना ही उद्देश्य था।', 'lastconv_quote': 'कुछ शब्दों को अस्तित्व में होना ज़रूरी है, भले ही कभी सुने न जाएं।',
    'priorities_title': 'तुम्हारी असली प्राथमिकताएं', 'priorities_sub': 'हर हफ्ते कितने घंटे हर चीज़ पर बिताते हो?', 'priorities_result': 'तुम्हारी असली प्राथमिकताएं', 'priorities_most_time': 'सबसे ज़्यादा समय यहाँ बिताते हो:', 'priorities_truth': 'समय सच बताता है। शब्द झूठ बोल सकते हैं, समय-सारणी नहीं।', 'priorities_quote': 'तुम वो हो जो बार-बार करते हो।', 'priorities_show': 'मेरा सच दिखाओ', 'priority_work': 'काम', 'priority_family': 'परिवार', 'priority_friends': 'दोस्त', 'priority_health': 'स्वास्थ्य', 'priority_learning': 'सीखना', 'priority_entertainment': 'मनोरंजन', 'priority_social': 'सोशल मीडिया', 'priority_sleep': 'नींद',
    'voice_intro': 'अंधेरे में एक आवाज़ बोलती है...', 'voice_silence': '(चुप्पी)', 'voice_quote': 'कुछ आवाज़ें कभी घर का रास्ता नहीं पातीं।',
    'knows_tell': 'मुझे अपना नाम बताओ।', 'knows_placeholder': 'तुम्हारा नाम...', 'knows_warning': '(तुम्हें यहाँ नहीं आना चाहिए था)', 'knows_quote': 'नामों में शक्ति होती है। तुम्हारा पहले से जाना जाता था।',
    'door_msg1': 'दरवाज़े पर कुछ है।', 'door_stopped': '(दस्तक बंद हो गई)', 'door_quote': 'कुछ दरवाज़े बंद ही रहने चाहिए।',
    'eyes_msg1': 'एक जोड़ी आँखें देख रही हैं।', 'eyes_unsee': 'तुम उन्हें कभी अनदेखा नहीं कर पाओगे।', 'eyes_quote': 'कुछ चीज़ें, एक बार नज़र आने पर, अनदेखी नहीं की जा सकतीं।',
    'moon_locked': 'यह कमरा केवल पूर्णिमा के दौरान खुलता है।', 'moon_not_full': 'आज रात चाँद पूरा नहीं है। चक्र पूरा होने पर लौटें।', 'moon_quote': 'कुछ सत्य केवल चक्रों में प्रकट होते हैं।',
    'at317_title': '3:17 AM पर तुम', 'at317_locked': 'यह कमरा सुबह 2 से 5 बजे के बीच खुलता है।', 'at317_current': 'वर्तमान समय:', 'at317_quote': 'रात तुम्हारा नाम जानती है।',
    'mirrors_you_are_here': 'तुम यहाँ हो।',
    'listen_title': 'खुद को सुनो', 'listen_recording': 'रिकॉर्डिंग...', 'listen_start': 'शुरू करो', 'listen_play': 'चलाओ', 'listen_instruction': '10 सेकंड रिकॉर्ड करो। कुछ भी कहो।', 'listen_wait': 'रुको। फिर सुनो।', 'listen_done': 'वो तुम्हारी आवाज़ थी। धीमी। अजीब।', 'listen_speed': '0.5x गति पर चल रहा है', 'listen_quote': 'तुम्हारी आवाज़ अलग लगती है क्योंकि तुमने आखिरकार इसे सुना।',
    'export_title': 'अपनी यात्रा निर्यात करें', 'export_subtitle': 'इस जगह का एक टुकड़ा अपने साथ ले जाओ', 'export_tab_export': 'निर्यात', 'export_tab_import': 'आयात', 'export_your_journey': 'तुम्हारी यात्रा', 'export_since': 'तब से', 'export_time_spent': 'बिताया समय', 'export_rooms_visited': 'देखे गए कमरे', 'export_rooms_completed': 'पूरे किए कमरे', 'export_fragments': 'टुकड़े', 'export_achievements_unlocked': 'उपलब्धियाँ अनलॉक', 'export_truths_recorded': 'सत्य दर्ज', 'export_text_file': 'टेक्स्ट फ़ाइल', 'export_json_file': 'JSON फ़ाइल', 'export_copied': 'कॉपी हो गया!', 'export_copy_clipboard': 'क्लिपबोर्ड पर कॉपी करें', 'export_data_stays': 'तुम्हारा डेटा तुम्हारे पास रहता है। हम इसे नहीं रखते।', 'export_import_desc': 'पहले निर्यात की गई यात्रा आयात करें', 'export_json_only': 'केवल JSON प्रारूप', 'export_select_file': 'फ़ाइल चुनें', 'export_merge_note': 'यह तुम्हारी वर्तमान यात्रा डेटा के साथ मिला दिया जाएगा।', 'export_done': 'निर्यात हो गया।', 'export_done_desc': 'तुम कहाँ गए उसका रिकॉर्ड। तुमने क्या सामना किया।', 'export_quote': 'कुछ यात्राएँ दस्तावेज़ करने लायक हैं। दूसरों के लिए नहीं। खुद के लिए।', 'export_import_success': 'यात्रा सफलतापूर्वक आयात हुई', 'export_import_error': 'अमान्य यात्रा फ़ाइल',
    'waitroom_title': 'प्रतीक्षा कक्ष', 'waitroom_subtitle': 'कुछ दरवाज़े सिर्फ समूहों के लिए खुलते हैं।', 'waitroom_open': 'दरवाज़ा खुला है।', 'waitroom_waiting': 'इंतज़ार कर रहे हैं', 'waitroom_time_waited': 'इंतज़ार किया समय', 'waitroom_users_needed': 'आवश्यक उपयोगकर्ता', 'waitroom_someone_joined': 'कोई इंतज़ार में शामिल हुआ।', 'waitroom_someone_left': 'कोई चला गया।', 'waitroom_room_opens': 'कमरा खुल गया।', 'waitroom_patience': 'धैर्य और उपस्थिति वो खोलते हैं जो बल नहीं खोल सकता।', 'waitroom_souls_waited': '{count} आत्माओं ने साथ इंतज़ार किया।', 'waitroom_seconds_stillness': '{count} सेकंड सामूहिक शांति।', 'waitroom_quote': 'कुछ चीज़ें जल्दी नहीं हो सकतीं। कुछ दरवाज़े अकेले नहीं खुल सकते।',
    'capsule_title': 'टाइम कैप्सूल', 'capsule_subtitle': 'अपने भविष्य के स्वयं को एक संदेश', 'capsule_what_tell': 'तुम्हें खुद से क्या कहना है?', 'capsule_placeholder': 'प्रिय भविष्य के मैं...', 'capsule_when_arrive': 'यह कब पहुँचना चाहिए?', 'capsule_30_days': '30 दिन', 'capsule_90_days': '90 दिन', 'capsule_1_year': '1 साल', 'capsule_seal': 'कैप्सूल सील करो', 'capsule_waiting': 'कैप्सूल इंतज़ार में', 'capsule_sealed': 'सील हो गया।', 'capsule_will_wait': 'तुम्हारा संदेश तुम्हारा इंतज़ार करेगा।', 'capsule_return_in': '{days} दिनों में वापस आओ इसे खोलने के लिए।', 'capsule_quote_sealed': 'समय सब कुछ बदल देता है। तुम्हें भी।', 'capsule_arrived': 'एक संदेश आया है।', 'capsule_from_past': '{days} दिन पहले के तुमसे।', 'capsule_open': 'कैप्सूल खोलो', 'capsule_you_wrote': 'तुमने यह खुद को लिखा था:', 'capsule_remember': 'क्या तुम्हें यह लिखना याद है?', 'capsule_quote_opened': 'जिसने यह लिखा वो चला गया। तुम बाकी हो।',
    'contradiction_title': 'विरोधाभास कक्ष', 'contradiction_subtitle': 'जहाँ तुम्हारे सत्य टकराते हैं', 'contradiction_not_enough': 'अभी पर्याप्त डेटा नहीं है।', 'contradiction_answer_more': 'कमरों में और सवालों के जवाब दो।', 'contradiction_answers_recorded': '{count}/5 जवाब दर्ज', 'contradiction_quote_early': 'विरोधाभासों को जटिलता चाहिए। पहले अपनी बनाओ।', 'contradiction_no_found': 'कोई विरोधाभास नहीं मिला।', 'contradiction_consistent': 'या तो तुम उल्लेखनीय रूप से सुसंगत हो...', 'contradiction_careful': '...या उल्लेखनीय रूप से सावधान।', 'contradiction_quote_none': 'सुसंगतता या तो ज्ञान है या प्रदर्शन।', 'contradiction_found': 'हमने {count} विरोधाभास पाया', 'contradiction_you_said': 'तुमने कहा:', 'contradiction_but_also': 'लेकिन यह भी:', 'contradiction_see_another': 'एक और देखो →', 'contradiction_not_weakness': 'विरोधाभास कमज़ोरी नहीं है। यह गहराई का प्रमाण है।', 'contradiction_quote': 'हम बहुलताएँ समेटे हैं। उनमें से कुछ असहमत हैं।',
    'entropy_living': 'जीवित', 'entropy_dead': 'मृत', 'entropy_plant_seed': 'बीज बोओ', 'entropy_instructions': 'बोने के लिए खाली जगह पर क्लिक करो। पौधों को पानी देने के लिए क्लिक करो। मृत पौधे हटाए जा सकते हैं।', 'entropy_reflect': 'अपने बगीचे पर चिंतन करो', 'entropy_lives': 'तुम्हारा बगीचा जीवित है।', 'entropy_thrive': '{living} पौधे फल-फूल रहे हैं क्योंकि तुम वापस आए।', 'entropy_absence': '{dead} तुम्हें अनुपस्थिति की याद दिलाते हैं।',
    'palace_title': 'स्मृति महल', 'palace_subtitle': 'एक मानसिक स्थान बनाओ। याद करने के लिए वस्तुएँ रखो। बाद में घूमो।', 'palace_room_of': 'कमरा {current} / {total}', 'palace_click_place': 'रखने के लिए क्लिक करो', 'palace_what_represent': 'यह क्या दर्शाता है?', 'palace_cancel': 'रद्द करो', 'palace_place_object': 'वस्तु रखो', 'palace_objects_count': 'तुम्हारे महल में {count} वस्तु(एँ)', 'palace_take_tour': 'यात्रा शुरू करो', 'palace_empty_room': 'यह कमरा खाली है', 'palace_previous': 'पिछला', 'palace_next_room': 'अगला कमरा', 'palace_complete_tour': 'यात्रा पूरी करो', 'palace_remembers': 'तुम्हारा महल याद रखता है।', 'palace_geography': 'अर्थ की भूगोल जो तुमने निर्मित की।', 'palace_quote': 'मन एक महल है। तुम जो उसमें रखते हो वो तय करता है तुम कौन बनोगे।',
    'echoes_subtitle': 'अन्य यात्रियों के टुकड़े', 'echoes_from': 'से', 'echoes_ago': 'पहले', 'echoes_arriving': 'और गूँज आ रही हैं...', 'echoes_reveal': 'ये उनके जवाब नहीं हैं। ये तुम्हारे हैं।', 'echoes_reveal2': '(हर गूँज कभी तुम्हारा अपना प्रतिबिंब था)', 'echoes_quote': 'हम सोचते हैं कि हम अनोखे हैं। हम बस एक दूसरे की गूँज हैं।',
    'revisit_subtitle': 'तुम कैसे बदले', 'revisit_not_enough': 'तुमने अभी पर्याप्त सवालों के जवाब नहीं दिए हैं।', 'revisit_explore': 'और कमरे खोजो। अपने निशान छोड़ो। बाद में लौटो।', 'revisit_quote1': 'बदलाव के लिए इतिहास चाहिए। इतिहास के लिए समय चाहिए।', 'revisit_none_changed': 'लेकिन अभी कोई नहीं बदला। कमरों पर फिर जाओ। फिर जवाब दो।', 'revisit_show_evolved': 'हम तुम्हें दिखाएँगे कि तुम कैसे विकसित हुए।', 'revisit_quote2': 'बदलाव देखने के लिए, पहले तुम्हें बदलना होगा।', 'revisit_then': 'तब', 'revisit_now': 'अब', 'revisit_one_shift': 'एक बदलाव। सूक्ष्म लेकिन वास्तविक।', 'revisit_each_change': 'हर बदलाव एक छोटी मृत्यु है। और एक छोटा जन्म।', 'revisit_quote3': 'तुम वो नहीं हो जो थे। अभी वो नहीं हो जो होगे।',
  },

  // Korean translations
  ko: {
    'title': '인간의 방',
    'tagline': '아무도 보지 않을 때 당신이 누구인지를 비추는 방들의 모음.',
    'enter': '입장',
    'footer': '치료가 아닙니다. 소셜이 아닙니다. 생산적이지 않습니다.',
    'back': '← 뒤로',
    'hallway': '복도',
    'leave_room': '← 방 나가기',
    'return_hallway': '복도로 돌아가기',
    'loading': '로딩 중...',
    'submit': '제출',
    'cancel': '취소',
    'close': '닫기',
    'next': '다음',
    'previous': '이전',
    'start': '시작',
    'stop': '정지',
    'yes': '네',
    'no': '아니오',
    'complete': '완료',
    'reveal': '드러내기',
    'continue': '계속',
    'begin': '시작하기',
    'return': '← 돌아가기',
    'escape': '← 탈출',
    'run': '← 도망',
    'leave': '← 떠나기',
    'watch': '지켜보기...',
    'correct': '정답',
    'round': '라운드',
    'enter_again': '다시 입장',
    'steps': '단계',
    'depth': '깊이',
    'settings': '설정',
    'language': '언어',
    'updates': '업데이트',
    'updates_title': '새로운 소식',
    'new': '새로운',
    'view_all_updates': '모든 업데이트 보기',
    'whats_new_intro': '최신 업데이트:',
    'challenges': '도전',
    'random': '랜덤',
    'open_rooms': '열린 방',
    'dark_rooms': '어두운 방',
    'enter_risk': '위험을 감수하고 입장',
    'premium_rooms': '프리미엄 방',
    'unlock_to_access': '잠금 해제하여 접근',
    'daily_rooms': '일일 방',
    'refreshes_daily': '매일 새로고침',
    'time_locked': '시간 잠금 방',
    'seasonal_rooms': '계절 방',
    'available_now': '현재 이용 가능',
    'fragments': '조각',
    'boss_unlocked': '보스 방 해제',
    'collect_unlock': '보스 방 해제를 위해 수집',
    'dark_fragments': '어둠의 조각',
    'dark_boss_unlocked': '어둠의 보스 해제',
    'hidden_discovered': '숨겨진 방 발견',
    'stillness_revealed': '당신의 고요함이 무언가를 드러냈습니다...',
    'constellation_map': '별자리 지도',
    'your_stats': '당신의 통계',
    'completed': '완료됨',
    'game_rooms': '게임 방',
    'experimental_rooms': '실험적 방',
    'deep_rooms': '깊은 방',
    'meta_rooms': '메타 방',
    'search_rooms': '방 검색...',
    'no_results': '검색 결과 없음',
    'clear_filters': '필터 초기화',
    'explore': '탐험',
    'all_categories': '전체',
    'random_room': '랜덤 방',
    'rooms_explored': '탐험한 방',
    'your_journey': '당신의 여정',
    'choose_direction': '방향을 선택하세요. 일부 문은 반응합니다.',
    'achievements': '업적',
    'achievements_subtitle': '여정의 이정표',
    'points': '점',
    'unlocked': '해제됨',
    'exploration': '탐험',
    'consistency': '꾸준함',
    'discovery': '발견',
    'special': '특별',
    'all': '전체',
    'bookmarks': '북마크',
    'bookmarked_rooms': '북마크된 방',
    'no_bookmarks': '아직 북마크 없음',
    'what_is_this': '이곳은 무엇인가요?',
    'total_time': '총 시간',
    'rooms_visited': '방문한 방',
    'total_visits': '총 방문',
    'most_visited': '가장 많이 방문',
    'longest_session': '가장 긴 세션',
    'fragments_collected': '수집한 조각',
    'completed_rooms': '완료한 방',
    'privacy_note': '모든 데이터는 기기에 로컬 저장됩니다',
    'whisper_wall': '속삭임의 벽',
    'leave_whisper': '속삭임을 남기세요...',
    'whisper_sent': '당신의 속삭임이 벽으로 흘러갑니다.',
    'whispers_count': '속삭임',
    'anonymous': '익명',
    // Room Titles
    'room_how-many-times-left': '남은 횟수',
    'room_life-in-weeks': '주 단위로 본 인생',
    'room_delay-button': '지연 버튼',
    'room_one-honest-sentence': '하나의 솔직한 문장',
    'room_this-moment': '이 순간은 다시 오지 않는다',
    'room_reality-receipt': '현실 영수증',
    'room_nothing-happens': '여기서는 아무 일도 일어나지 않는다',
    'room_default-choice': '당신의 기본 선택',
    'room_thought-loop': '생각의 순환',
    'room_expect-vs-happens': '기대 vs 현실',
    'room_question-never-answer': '절대 답하지 않는 질문',
    'room_not-special': '당신은 특별하지 않다 (괜찮다)',
    'room_shortcut-always-take': '항상 택하는 지름길',
    'room_blurred-self': '흐릿한 자아',
    'room_image-zooming': '계속 확대하는 이미지',
    'room_comfort-color': '위안의 색',
    'room_line-you-crossed': '넘었던 선',
    'room_read-aloud': '소리 내어 읽기',
    'room_voice-without-meaning': '의미 없는 목소리',
    'room_sentence-you-avoid': '피하는 문장',
    'room_if-nothing-changes': '아무것도 변하지 않는다면',
    'room_real-priorities': '진짜 우선순위',
    'room_last-conversation': '마지막 대화',
    'room_who-without-job': '직업 없는 당신은 누구?',
    'room_comfort-zone-scanner': '안전지대 스캐너',
    'room_say-it-once': '한 번만 말해',
    'room_listen-to-yourself': '자신의 목소리를 들어',
    'room_life-as-shapes': '도형으로 본 인생',
    'room_face-without-features': '특징 없는 얼굴',
    'room_what-keeps-awake': '잠 못 들게 하는 것',
    'room_you-at-317am': '새벽 3시 17분의 당신',
    'room_thought-that-returns': '돌아오는 생각',
    'room_room-you-visit-most': '가장 많이 방문한 방',
    'room_room-you-avoid': '피하는 방',
    'room_left-too-early': '너무 일찍 떠났다',
    'room_daily-quote': '오늘의 명언',
    'room_daily-joke': '오늘의 농담',
    'room_theyre-watching': '그들이 지켜보고 있다',
    'room_the-last-visitor': '마지막 방문자',
    'room_dont-look-away': '눈을 돌리지 마',
    'room_the-whisper': '속삭임',
    'room_you-forgot-something': '뭔가를 잊었다',
    'room_the-mirror-lies': '거울은 거짓말한다',
    'room_something-follows': '무언가가 따라온다',
    'room_the-countdown': '카운트다운',
    'room_eyes-in-the-dark': '어둠 속의 눈',
    'room_the-voice': '목소리',
    'room_it-knows-your-name': '그것은 당신의 이름을 안다',
    'room_the-door': '문',
    'room_boss-room-free': '진실의 합',
    'room_boss-room-horror': '공포의 심장',
    'room_boss-room-premium': '가장 깊은 진실',
    'room_mouse-tracker': '마우스 추적기',
    'room_scroll-confession': '스크롤 고백',
    'room_waiting-game': '기다림의 게임',
    'room_day-in-pixels': '픽셀로 본 하루',
    'room_memory-maze': '기억의 미로',
    'room_decision-shadow': '결정의 그림자',
    'room_anonymous-mirrors': '익명의 거울',
    'room_collective-room': '집단의 방',
    'room_secret-room': '비밀의 방',
    'room_glitch-room': '불안정한 방',
    'room_full-moon-room': '보름달 방',
    'room_weekend-room': '주말 방',
    'room_317am-room': '새벽 3:17 방',
    'room_idle-room': '고요한 방',
    'room_invite-friend-room': '공유의 방',
    'room_halloween-room': '장막의 방',
    'room_winter-solstice-room': '가장 긴 밤',
    'room_new-year-room': '미래의 나에게 보내는 편지',
    'room_memory-puzzle': '기억 퍼즐',
    'room_shadow-chase': '그림자 추격',
    'room_timing-room': '타이밍 방',
    'room_pattern-whisper': '패턴 속삭임',
    'room_silence-puzzle': '침묵 퍼즐',
    'room_decay-game': '부식 게임',
    'room_observation-test': '관찰력 테스트',
    'room_balance-room': '균형 방',
    'room_word-drift': '단어 흐름',
    'room_color-echo': '색의 메아리',
    'room_mirror-tap': '거울 탭',
    'room_maze-of-doors': '문의 미로',
    'room_pulse-match': '맥박 맞추기',
    'room_light-path': '빛의 길',
    'room_sound-match': '소리 맞추기',
    'room_mirror-room': '거울 방',
    'room_echo-room': '메아리 방',
    'room_pulse-room': '맥박 방',
    'room_vanishing-room': '사라지는 방',
    'room_letter-room': '글자 방',
    'room_empty-room': '빈 방',
    'room_reverse-room': '역전 방',
    'room_whisper-room': '속삭임 방',
    'room_shadow-room': '그림자 방',
    'room_labyrinth-room': '미궁 방',
    'room_color-room': '색 방',
    'room_surrender-room': '항복 방',
    'room_noise-room': '소음 방',
    'room_observer-room': '관찰자 방',
    'room_imagine-yourself': '자신을 상상해',
    'room_ai-reflection-room': 'AI 성찰 방',
    'room_the-other-side': '반대편',
    'room_the-unraveling': '풀려가기',
    'room_the-echo': '메아리',
    'room_ash-embers': '재와 불씨',
    'room_twilight-stars': '황혼의 별',
    'room_void-abyss': '심연',
    'room_eclipse-shadows': '일식의 그림자',
    'room_dawn-awakening': '새벽 깨어남',
    'room_letters-never-sent': '보내지 않은 편지',
    'room_breath-sync': '호흡 동기화',
    'room_entropy-garden': '엔트로피 정원',
    'room_memory-palace': '기억의 궁전',
    'room_morning-ritual': '아침 의식',
    'room_evening-ritual': '저녁 의식',
    'room_weekly-ritual': '주간 의식',
    'room_spring-equinox-room': '춘분',
    'room_summer-solstice-room': '하지',
    'room_autumn-equinox-room': '추분',
    'room_valentine-room': '자기 사랑의 방',
    'room_fragment-trade': '조각의 제단',
    'room_the-archive': '기록 보관소',
    'room_mirror-of-all-selves': '모든 자아의 거울',
    'room_the-ending': '끝',
    'room_whisper-wall': '속삭임의 벽',
    'room_collective-memory': '집단 기억',
    'room_shared-moments': '공유된 순간',
    'room_void-room': '공허',
    'room_pattern-room': '당신의 패턴',
    'room_time-capsule': '타임캡슐',
    'room_the-revisit': '재방문',
    'room_contradiction-room': '모순의 방',
    'room_export-journey': '여정 내보내기',
    'room_anonymous-echoes': '익명의 메아리',
    'room_the-waiting-room': '대기실',
    'room_dream-visualization': '꿈 시각화',
    'room_future-self': '미래의 나',
    'room_regret-machine': '후회 기계',
    'room_memory-reconstruction': '기억 재구성',
    'room_mask-you-wear': '쓰고 있는 가면',
    'room_recurring-dream': '반복되는 꿈',
    'room_promise-you-broke': '어긴 약속',
    'room_words-you-swallowed': '삼킨 말',
    'room_age-you-feel': '느끼는 나이',
    'room_biggest-lie': '가장 큰 거짓말',
    'room_person-you-pretend': '연기하는 사람',
    'room_person-you-pretend-to-be': '연기하는 사람',
    'room_what-you-collect': '수집하는 것',
    'room_apology-you-owe': '빚진 사과',
    'room_unfinished-story': '미완성 이야기',
    'room_version-others-see': '타인이 보는 버전',
    'room_secret-talent': '숨겨진 재능',
    'room_fear-you-feed': '키우는 두려움',
    'room_guilty-pleasure': '죄책감의 쾌락',
    'room_rule-you-break': '항상 어기는 규칙',
    'room_childhood-promise': '어린 시절의 약속',
    'room_compliment-you-reject': '거부하는 칭찬',
    'room_autopilot-mode': '자동 조종 모드',
    'room_boundary-you-need': '필요한 경계',
    'room_inner-critic': '내면의 비평가',
    'room_last-sunday': '지난 일요일',
    'room_wasted-hours': '낭비한 시간',
    'room_year-that-changed': '당신을 바꾼 해',
    'room_tomorrows-regret': '내일의 후회',
    'room_morning-autopilot': '아침 자동 조종',
    'room_season-you-dread': '두려운 계절',
    'room_ten-years-ago': '10년 전 오늘',
    'room_last-normal-day': '마지막 평범한 날',
    'room_clock-you-ignore': '무시하는 시계',
    'room_your-deadline': '당신의 마감',
    'room_the-pause': '멈춤',
    'room_static-room': '정적',
    'room_gap-between-thoughts': '생각 사이의 틈',
    'room_sit-with-discomfort': '불편함과 함께 앉기',
    'room_boring-room': '지루한 방',
    'room_unplug': '플러그 빼기',
    'room_unplug-room': '플러그 빼기',
    'room_last-page': '마지막 페이지',
    'room_scrolling-pattern': '스크롤 패턴',
    'room_tab-you-keep-open': '닫지 않는 탭',
    'room_digital-shadow': '디지털 그림자',
    'room_notification-you-ignore': '무시하는 알림',
    'room_typing-speed': '타이핑 속도',
    'room_link-you-almost-clicked': '거의 클릭할 뻔한 링크',
    'room_bookmark-graveyard': '북마크 묘지',
    'room_search-you-deleted': '삭제한 검색',
    'room_gravity-room': '중력 방',
    'room_ink-blot': '잉크 얼룩',
    'room_hallway-of-mirrors': '거울의 복도',
    'room_dissolving-text': '녹아내리는 글자',
    'room_the-pendulum': '진자',
    'room_pixel-rain': '픽셀 비',
    'room_the-threshold': '문턱',
    'room_the-friend-you-lost': '잃어버린 친구',
    'room_conversation-you-replay': '되새기는 대화',
    'room_the-thank-you': '감사',
    'room_people-you-forgot': '잊은 사람들',
    'room_the-advice-you-give': '당신이 하는 조언',
    'room_what-others-dont-know': '다른 사람이 모르는 것',
    'room_the-lie-you-believe': '믿는 거짓말',
    'room_your-shadow-self': '그림자 자아',
    'room_the-story-you-tell': '당신이 하는 이야기',
    'room_your-safe-place': '안전한 장소',
    'room_the-risk-you-didnt-take': '감수하지 않은 위험',
    'room_your-last-excuse': '마지막 핑계',
    'room_the-habit-you-hide': '숨기는 습관',
    'room_what-you-postpone': '미루는 것',
    'room_the-door-you-closed': '닫은 문',
    'room_the-emotion-you-hide': '숨기는 감정',
    'room_your-comfort-lie': '위안의 거짓말',
    'room_the-silence-between': '사이의 침묵',
    'room_what-you-carry': '짊어진 것',
    'room_the-version-you-lost': '잃어버린 버전',
    'room_letter-to-past-self': '과거의 나에게 보내는 편지',
    'room_the-moment-you-knew': '알게 된 순간',
    'room_the-words-you-rehearse': '연습하는 말',
    'room_someone-thinks-of-you': '누군가 당신을 생각한다',
    'room_the-question-you-fear': '두려운 질문',
    'room_recurring-dream-journal': '반복되는 꿈 일기',
    'room_lucid-moment': '자각의 순간',
    'room_nightmare-you-remember': '기억나는 악몽',
    'room_dream-you-wish-was-real': '현실이었으면 하는 꿈',
    'room_the-dream-visitor': '꿈의 방문자',
    'room_the-blank-canvas': '빈 캔버스',
    'room_song-that-is-you': '당신인 노래',
    'room_color-of-today': '오늘의 색',
    'room_the-unwritten-poem': '쓰지 않은 시',
    'room_your-inner-voice': '내면의 목소리',
    'room_fear-you-named': '이름 붙인 두려움',
    'room_the-leap': '도약',
    'room_courage-inventory': '용기 목록',
    'room_what-scares-you-most': '가장 무서운 것',
    'room_facing-the-dark': '어둠을 마주하다',
    'room_letter-to-future-self': '미래의 나에게 보내는 편지',
    'room_the-world-you-want': '원하는 세상',
    'room_your-next-chapter': '다음 챕터',
    'room_the-wish': '소원',
    'room_if-you-had-one-year': '1년이 남았다면',
    // Room Subtitles
    'sub_how-many-times-left': '남은 것의 수',
    'sub_life-in-weeks': '4,000개의 칸. 그게 전부.',
    'sub_delay-button': '매번 더 어려워진다',
    'sub_one-honest-sentence': '수정 불가. 삭제 불가.',
    'sub_this-moment': '당신은 여기에. 한 번.',
    'sub_reality-receipt': '당신의 날들의 요약',
    'sub_nothing-happens': '말 그대로',
    'sub_default-choice': '안전 또는 위험. 매번.',
    'sub_thought-loop': '반복하는 마음을 지켜봐',
    'sub_expect-vs-happens': '희망과 현실 사이의 간격',
    'sub_question-never-answer': '대부분의 사람은 떠난다',
    'sub_not-special': '위로 없음. 진실만.',
    'sub_shortcut-always-take': '패턴이 드러난다',
    'sub_blurred-self': '기억은 이렇게 작동한다',
    'sub_image-zooming': '아무것도 아닌 곳에서 답을 찾다',
    'sub_comfort-color': '얼마나 오래 머물 건가요?',
    'sub_line-you-crossed': '어디서 망설였는지 보인다',
    'sub_read-aloud': '소리 내어 말해야 할 단어',
    'sub_voice-without-meaning': '말 없는 소리',
    'sub_sentence-you-avoid': '쓸 때까지 떠날 수 없다',
    'sub_if-nothing-changes': '앞으로 5년의 예측',
    'sub_real-priorities': '말이 아닌 시간 기반',
    'sub_last-conversation': '절대 보내지 않을 것을 써라',
    'sub_who-without-job': '자신을 다시 써라',
    'sub_comfort-zone-scanner': '당신의 망설임이 보인다',
    'sub_say-it-once': '다시 듣기 없음. 다시 하기 없음.',
    'sub_listen-to-yourself': '느려진 당신의 목소리',
    'sub_life-as-shapes': '추상적. 라벨 없음. 당신의 것.',
    'sub_face-without-features': '아무것도 남지 않을 때까지 제거',
    'sub_what-keeps-awake': '밤 10시 이후 이용 가능',
    'sub_you-at-317am': '시간 특정. 개인적.',
    'sub_thought-that-returns': '매번 돌아온다',
    'sub_room-you-visit-most': '당신의 패턴 공개',
    'sub_room-you-avoid': '건너뛰는 것이 무언가를 말한다',
    'sub_left-too-early': '빨리 나가면 작동',
    'sub_daily-quote': '매일 바뀌는 지혜',
    'sub_daily-joke': '블랙 유머, 매일 업데이트',
    'sub_theyre-watching': '혼자가 아니다',
    'sub_the-last-visitor': '누군가 먼저 왔다',
    'sub_dont-look-away': '떠나면 알아차린다',
    'sub_the-whisper': '가까이 들어봐',
    'sub_you-forgot-something': '뭐였지?',
    'sub_the-mirror-lies': '진실을 보려면 깨뜨려',
    'sub_something-follows': '계속 달려',
    'sub_the-countdown': '0에서 무언가 일어난다',
    'sub_eyes-in-the-dark': '그것들은 늘어난다',
    'sub_the-voice': '누군가 갇혀 있다',
    'sub_it-knows-your-name': '오지 말았어야 했다',
    'sub_the-door': '무언가가 노크한다',
    'sub_boss-room-free': '마지막 무료 방',
    'sub_boss-room-horror': '최후의 어둠을 마주해',
    'sub_boss-room-premium': '마지막 깊은 방',
    'sub_mouse-tracker': '움직임이 보인다',
    'sub_scroll-confession': '얼마나 빨리 지나치나요?',
    'sub_waiting-game': '아무것도 안 할 수 있나요?',
    'sub_day-in-pixels': '시간이 형태가 된다',
    'sub_memory-maze': '패턴이 바뀐다',
    'sub_decision-shadow': '선택이 패턴을 드러낸다',
    'sub_anonymous-mirrors': '낯선 이의 생각',
    'sub_collective-room': '모두 여기에. 아무도 안 남는다.',
    'sub_secret-room': '찾았다',
    'sub_glitch-room': '뭔가 잘못됐다',
    'sub_full-moon-room': '달이 찰 때만 열린다',
    'sub_weekend-room': '쉼은 목적지다',
    'sub_317am-room': '시간 특정. 개인적.',
    'sub_idle-room': '인내로 발견',
    'sub_invite-friend-room': '어떤 여정은 목격되어야 한다',
    'sub_halloween-room': '10월 31일 - 장막이 얇다',
    'sub_winter-solstice-room': '12월 21일 - 놓아주고 간직해',
    'sub_new-year-room': '12월 31일 - 될 사람에게 써',
    'sub_memory-puzzle': '미묘하게 변하는 패턴 맞추기',
    'sub_shadow-chase': '예측 불가능한 것을 잡아',
    'sub_timing-room': '정확한 순간에 클릭',
    'sub_pattern-whisper': '사라지는 순서를 반복해',
    'sub_silence-puzzle': '설명 없이 풀어',
    'sub_decay-game': '사라지는 것을 보존해',
    'sub_observation-test': '미묘한 차이를 찾아',
    'sub_balance-room': '떠다니는 물체를 정렬해',
    'sub_word-drift': '떨어지기 전에 글자를 잡아',
    'sub_color-echo': '미묘한 톤 순서를 반복해',
    'sub_mirror-tap': '모든 탭이 다른 곳에서 울린다',
    'sub_maze-of-doors': '선택할 때마다 문이 재배치',
    'sub_pulse-match': '방의 리듬에 맞춰 움직여',
    'sub_light-path': '변하는 어둠 속에서 빛을 인도해',
    'sub_sound-match': '도형을 부드러운 음에 맞춰',
    'sub_mirror-room': '약간 왜곡된 반사',
    'sub_echo-room': '클릭이 지연된 메아리를 일으킨다',
    'sub_pulse-room': '환경이 숨 쉰다',
    'sub_vanishing-room': '상호작용하면 요소가 사라진다',
    'sub_letter-room': '기억처럼 글자가 흩어진다',
    'sub_empty-room': '비밀을 찾을 때까지 비어있다',
    'sub_reverse-room': '행동이 반대로 작동',
    'sub_whisper-room': '거의 들리지 않는 진실',
    'sub_shadow-room': '어두운 동반자',
    'sub_labyrinth-room': '길을 찾아',
    'sub_color-room': '색채 명상',
    'sub_surrender-room': '통제를 놓아줘',
    'sub_noise-room': '정적인 생각',
    'sub_observer-room': '관찰당하는 느낌',
    'sub_imagine-yourself': '자신을 묘사해. 무엇이 나타나는지 봐.',
    'sub_ai-reflection-room': 'AI가 함께 성찰',
    'sub_the-other-side': '어둠 속을 천천히 이동',
    'sub_the-unraveling': '말할 때마다 현실이 닳아간다',
    'sub_the-echo': '말한 것은 절대 떠나지 않는다',
    'sub_ash-embers': '재 테마에서만 보임',
    'sub_twilight-stars': '황혼 테마에서만 보임',
    'sub_void-abyss': '공허 테마에서만 보임',
    'sub_eclipse-shadows': '완전한 어둠 속에서 말해',
    'sub_dawn-awakening': '첫 빛을 맞이해',
    'sub_letters-never-sent': '절대 읽지 않을 사람에게 써',
    'sub_breath-sync': '방과 함께 호흡해',
    'sub_entropy-garden': '성장과 쇠퇴가 얽혀있다',
    'sub_memory-palace': '정신적 성소를 지어',
    'sub_morning-ritual': '의도를 가지고 하루를 시작해',
    'sub_evening-ritual': '성찰로 하루를 마감해',
    'sub_weekly-ritual': '일요일은 되돌아보는 날',
    'sub_spring-equinox-room': '놓아주고 새로워져',
    'sub_summer-solstice-room': '가장 긴 날',
    'sub_autumn-equinox-room': '수확하고 받아들여',
    'sub_valentine-room': '당신이 받을 자격이 있는 사랑',
    'sub_fragment-trade': '비밀을 열기 위해 희생해',
    'sub_the-archive': '완전한 여정 기록',
    'sub_mirror-of-all-selves': 'AI가 행동 초상화를 보여줘',
    'sub_the-ending': '얻은 작별',
    'sub_whisper-wall': '익명의 진실이 어둠 속에 떠다닌다',
    'sub_collective-memory': '우리가 기억하는 것의 공유 조각',
    'sub_shared-moments': '솔직히, 어떠세요?',
    'sub_void-room': '코드의 상처',
    'sub_pattern-room': '사이트가 당신에게서 보는 것',
    'sub_time-capsule': '미래의 자신에게 써',
    'sub_the-revisit': '답이 어떻게 바뀌었는지 봐',
    'sub_contradiction-room': '진실들이 충돌한다',
    'sub_export-journey': '경로를 다운로드해',
    'sub_anonymous-echoes': '낯선 이들의 조각',
    'sub_the-waiting-room': '존재로 해제해',
    'sub_dream-visualization': '꿈을 묘사해. 실체화되는 걸 봐.',
    'sub_future-self': '누가 되고 싶나요?',
    'sub_regret-machine': '있을 수 있었던 것을 탐험해',
    'sub_memory-reconstruction': '사라져가는 기억을 재구성해',
    'sub_mask-you-wear': '어떤 얼굴을 보여주나요?',
    'sub_recurring-dream': '계속 돌아오는 꿈',
    'sub_promise-you-broke': '깨진 약속의 정직함',
    'sub_words-you-swallowed': '하지 못한 말',
    'sub_age-you-feel': '정말 몇 살인가요?',
    'sub_biggest-lie': '가장 많이 하는 거짓말',
    'sub_person-you-pretend': '당신의 연기',
    'sub_person-you-pretend-to-be': '당신의 연기',
    'sub_what-you-collect': '물건이 정체성을 드러낸다',
    'sub_apology-you-owe': '해야 했던 말을 써',
    'sub_unfinished-story': '끝내지 못한 것',
    'sub_version-others-see': '타인의 시선',
    'sub_secret-talent': '아무도 모르는 능력',
    'sub_fear-you-feed': '어떤 두려움이 지배하나?',
    'sub_guilty-pleasure': '숨기는 즐거움',
    'sub_rule-you-break': '반항의 패턴',
    'sub_childhood-promise': '자신에게 한 약속',
    'sub_compliment-you-reject': '칭찬을 왜 못 받나?',
    'sub_autopilot-mode': '생각 없이 하는 것',
    'sub_boundary-you-need': '지켜야 할 것',
    'sub_inner-critic': '심판하는 목소리',
    'sub_last-sunday': '실제로 뭘 했나요?',
    'sub_wasted-hours': '시간이 사라지는 곳',
    'sub_year-that-changed': '전환점',
    'sub_tomorrows-regret': '뭘 했으면 좋겠나요?',
    'sub_morning-autopilot': '첫 시간의 의식',
    'sub_season-you-dread': '어떤 계절이 가장 무거운가?',
    'sub_ten-years-ago': '그때 당신은 누구였나?',
    'sub_last-normal-day': '모든 것이 바뀌기 전',
    'sub_clock-you-ignore': '지나가지 않는 척하는 시간',
    'sub_your-deadline': '뭘 미루고 있나요?',
    'sub_the-pause': '잠깐 숨 쉬어',
    'sub_static-room': '백색소음과 고요',
    'sub_gap-between-thoughts': '공간을 찾아',
    'sub_sit-with-discomfort': '아무것도 고치지 마',
    'sub_boring-room': '흥미로운 것 없음',
    'sub_unplug': '기기 없는 상상',
    'sub_unplug-room': '기기 없는 상상',
    'sub_last-page': '이야기가 여기서 끝난다',
    'sub_scrolling-pattern': '콘텐츠 소비 방식',
    'sub_tab-you-keep-open': '브라우저에 남아있는 것',
    'sub_digital-shadow': '온라인 발자국',
    'sub_notification-you-ignore': '무시하는 것',
    'sub_typing-speed': '얼마나 빨리 표현하나?',
    'sub_link-you-almost-clicked': '망설임의 패턴',
    'sub_bookmark-graveyard': '저장했지만 돌아가지 않은',
    'sub_search-you-deleted': '검색하고 지운 것',
    'sub_gravity-room': '모든 것이 천천히 떨어진다',
    'sub_ink-blot': '무엇이 보이나요?',
    'sub_hallway-of-mirrors': '무한한 반사',
    'sub_dissolving-text': '읽으면 글자가 녹는다',
    'sub_the-pendulum': '최면적 움직임',
    'sub_pixel-rain': '디지털 비',
    'sub_the-threshold': '여기와 저기 사이',
    'sub_the-friend-you-lost': '멀어진 사람에게 써',
    'sub_conversation-you-replay': '기억에서 재현해',
    'sub_the-thank-you': '표현하지 못한 감사',
    'sub_people-you-forgot': '잊힌 이름을 떠올려봐',
    'sub_the-advice-you-give': '하지만 따르지 않는 조언',
    'sub_what-others-dont-know': '숨겨진 층',
    'sub_the-lie-you-believe': '위안이 되는 거짓',
    'sub_your-shadow-self': '억누르는 부분',
    'sub_the-story-you-tell': '인생을 어떻게 서술하나',
    'sub_your-safe-place': '안전함을 느끼는 곳',
    'sub_the-risk-you-didnt-take': '가지 않은 길',
    'sub_your-last-excuse': '가장 많이 쓰는 핑계',
    'sub_the-habit-you-hide': '비밀 루틴',
    'sub_what-you-postpone': '항상 "내일"인 것',
    'sub_the-door-you-closed': '거절한 기회',
    'sub_the-emotion-you-hide': '한 번만 이름 붙여.',
    'sub_your-comfort-lie': '편안하게 해주는 작은 거짓말.',
    'sub_the-silence-between': '두 생각 사이에 앉아.',
    'sub_what-you-carry': '보이지 않는 무게에 이름을.',
    'sub_the-version-you-lost': '삶이 일어나기 전의 당신.',
    'sub_letter-to-past-self': '예전의 나에게 써.',
    'sub_the-moment-you-knew': '모든 것이 바뀐 순간.',
    'sub_the-words-you-rehearse': '연습하지만 말하지 않는 것.',
    'sub_someone-thinks-of-you': '지금 어딘가에서 누군가 당신을 기억한다.',
    'sub_the-question-you-fear': '아무도 묻지 않길 바라는 질문.',
    'sub_recurring-dream-journal': '계속 돌아오는 꿈.',
    'sub_lucid-moment': '꿈 속에서 깨어나세요.',
    'sub_nightmare-you-remember': '아직도 괴롭히는 악몽.',
    'sub_dream-you-wish-was-real': '깨어나. 현실이 아니었어.',
    'sub_the-dream-visitor': '누군가 계속 꿈에 나타난다.',
    'sub_the-blank-canvas': '무에서 유를 창조하세요.',
    'sub_song-that-is-you': '당신이 노래라면, 어떤 노래?',
    'sub_color-of-today': '현재 상태의 색을 골라.',
    'sub_the-unwritten-poem': '안에 담아둔 것을 써.',
    'sub_your-inner-voice': '실제로 뭐라고 하나?',
    'sub_fear-you-named': '이름 붙이면 힘을 잃는다.',
    'sub_the-leap': '무엇에 뛰어들 건가?',
    'sub_courage-inventory': '용감했던 모든 것의 목록.',
    'sub_what-scares-you-most': '가장 깊은 두려움.',
    'sub_facing-the-dark': '완전한 어둠 속에 앉아.',
    'sub_letter-to-future-self': '될 사람에게 써.',
    'sub_the-world-you-want': '존재했으면 하는 세상을 묘사해.',
    'sub_your-next-chapter': '이야기에서 다음은?',
    'sub_the-wish': '소원 하나. 규칙 없음.',
    'sub_if-you-had-one-year': '365일이 있다면 뭘 할 건가?',
    // v4.0 extras
    'dream_journal_placeholder': '나는 계속 이런 꿈을 꿉니다...',
    'record_dream': '기록',
    'dream_journal_reflection': '꿈은 깨어있는 마음이 말하지 않는 것을 말합니다.',
    'quote_dream_journal': '반복되는 꿈은 자신과의 미완성 대화입니다.',
    'become_aware': '나는 깨어있다',
    'lucid_aware': '이 자각의 순간만이 진짜입니다.',
    'quote_lucid_moment': '현실을 의심하는 순간이 진정으로 보는 순간입니다.',
    'wish_placeholder': '나의 소원은...',
    'make_wish': '소원 빌기',
    'quote_wish': '당신이 원하는 것이 당신이 가장 소중히 여기는 것을 드러냅니다.',
    'update_v4_languages': '4개의 새로운 언어: 힌디어, 한국어, 일본어, 터키어',
    'update_v4_20_rooms': '20개의 새로운 방: 꿈, 창의성, 두려움, 미래',
    'update_v4_dreams_category': '꿈과 잠재의식 방 카테고리',
    'update_v4_creativity_category': '창의성과 표현 방 카테고리',
    'update_v4_fear_category': '두려움과 용기 방 카테고리',
    'update_v4_future_category': '미래와 희망 방 카테고리',
    'room_playlists': '방 플레이리스트',
    'personal_dashboard': '대시보드',
    'dashboard_subtitle': '한눈에 보는 여정',
    'visit_pattern': '방문 패턴 (14일)',
    'dashboard_top_rooms': '가장 많이 방문한 방',
    'category_breakdown': '방 카테고리',
    'mood_trend': '기분 추세',
    'fragment_breakdown': '조각 분포',
    'no_data_yet': '아직 데이터 없음',
    'current_streak': '연속 일수',
    'playlist_morning': '아침 성찰',
    'playlist_morning_desc': '명확함과 현존으로 하루를 시작하세요',
    'playlist_identity': '정체성 깊이 탐구',
    'playlist_identity_desc': '라벨 아래의 당신은 누구인가요?',
    'playlist_creative': '창의적 불꽃',
    'playlist_creative_desc': '창의적 표현을 열어보세요',
    'playlist_courage': '용기의 길',
    'playlist_courage_desc': '방 하나씩 두려움을 마주하세요',
    'playlist_dreams': '꿈 일기',
    'playlist_dreams_desc': '잠재의식의 풍경을 탐험하세요',
    'playlist_future': '미래 비전',
    'playlist_future_desc': '다가올 나에게 보내는 편지와 소원',
    'playlist_truth': '진실 대면',
    'playlist_truth_desc': '불편한 현실을 마주하세요',
    'playlist_night': '밤의 여정',
    'playlist_night_desc': '침묵, 공허, 무를 받아들이세요',
    // v4.2.0
    'room_focus-timer': '집중 타이머', 'sub_focus-timer': '조용히 앉아 성찰하세요.', 'room_gratitude-jar': '감사의 항아리', 'sub_gratitude-jar': '감사한 한 가지', 'room_mood-compass': '기분 나침반', 'sub_mood-compass': '지금 어디에 있나요?', 'room_memory-fade': '기억의 소멸', 'sub_memory-fade': '기억이 녹는 것을 보세요', 'room_sound-of-your-day': '당신의 하루 소리', 'sub_sound-of-your-day': '오늘은 어떤 소리인가요?', 'room_the-apology-room': '사과의 방', 'sub_the-apology-room': '말하지 못한 것을 쓰세요', 'room_your-unread-messages': '읽지 않은 메시지', 'sub_your-unread-messages': '열지 않은 메시지들', 'room_the-weight-room': '무게의 방', 'sub_the-weight-room': '짐이 되는 것에 이름을 붙이세요', 'room_parallel-lives': '평행 인생', 'sub_parallel-lives': '다른 삶에서 당신은...', 'room_the-quiet-score': '고요함 점수', 'sub_the-quiet-score': '얼마나 가만히 있을 수 있나요?',
    'focus_choose': '얼마나 조용히 앉고 싶나요?', 'focus_sit': '앉으세요. 숨 쉬세요. 생각을 흘려보내세요.', 'focus_done': '침묵이 말했습니다.', 'quote_focus_timer': '고요 속에서 소음이 삼킨 것을 듣습니다.', 'minutes': '분',
    'gratitude_prompt': '오늘 감사한 것을 쓰세요.', 'gratitude_placeholder': '감사합니다...', 'gratitude_add': '항아리에 넣기', 'gratitude_added': '감사가 항아리에 떠다닙니다.', 'gratitude_count': '감사의 순간', 'quote_gratitude_jar': '감사는 더 많이 갖는 것이 아닙니다. 이미 있는 것을 알아차리는 것입니다.',
    'mood_compass_prompt': '지금 느끼는 위치를 클릭하세요.', 'mood_high_energy': '높은 에너지', 'mood_low_energy': '낮은 에너지', 'mood_positive': '긍정적', 'mood_negative': '부정적', 'quote_mood_compass': '어디에 있는지 아는 것이 어디로 갈지 아는 첫걸음입니다.',
    'memory_fade_prompt': '기억을 입력하세요. 사라지는 걸 보세요.', 'memory_fade_placeholder': '놓아주고 싶은 기억...', 'memory_fade_start': '사라지게 하기', 'memory_fade_done': '이제 사라졌습니다. 하지만 진짜였습니다.', 'quote_memory_fade': '기억은 우리가 보든 말든 사라집니다.',
    'sound_day_prompt': '하루를 대표하는 3-5개 소리를 고르세요.', 'sound_rain': '비', 'sound_traffic': '교통', 'sound_birds': '새', 'sound_keyboard': '키보드', 'sound_silence': '침묵', 'sound_wind': '바람', 'sound_ocean': '바다', 'sound_crowd': '군중', 'sound_music': '음악', 'sound_day_confirm': '이것이 내 하루', 'sound_day_result': '당신 하루의 사운드트랙.', 'quote_sound_day': '매일에는 소리가 있습니다. 대부분 듣는 것을 잊습니다.',
    'apology_prompt': '전하지 못한 사과를 쓰세요.', 'apology_placeholder': '미안합니다...', 'apology_release': '놓아주기', 'apology_done': '아무것도 아닌 것으로 압축되었습니다. 하지만 말했습니다.', 'quote_apology': '어떤 사과는 그들을 위한 것이 아닙니다. 당신을 위한 것입니다.',
    'unread_from_past': '보낸 이: 과거의 나', 'unread_from_future': '보낸 이: 미래의 나', 'unread_from_critic': '보낸 이: 내면의 비평가', 'unread_msg_past': '모든 것이 달라질 거라 약속했던 때를 기억하나요?', 'unread_msg_future': '지금은 예전보다 자신에게 더 친절하길 바랍니다.', 'unread_msg_critic': '더 할 수 있었다는 걸 알잖아요. 하지만 할 건가요?', 'unread_count': '읽지 않음', 'unread_reply_placeholder': '답장...', 'unread_reply': '보내기', 'unread_sent': '메시지가 공허 속으로 보내졌습니다.', 'quote_unread_messages': '피하는 메시지가 가장 필요한 메시지인 경우가 많습니다.',
    'weight_prompt': '짐이 되는 것에 이름을 붙이세요.', 'weight_placeholder': '나를 무겁게 하는 것...', 'weight_release_start': '놓아주기 시작', 'weight_release_prompt': '각 무게를 클릭해 놓아주세요.', 'weight_done': '무게가 걷혔습니다. 지금은.', 'quote_weight_room': '이름 붙이기를 거부하는 것은 내려놓을 수 없습니다.',
    'parallel_intro': '다른 삶에서 당신은...', 'parallel_life_1': '다른 삶에서 당신은 외딴 섬의 등대지기입니다.', 'parallel_life_2': '다른 삶에서 당신은 고향을 떠나지 않았습니다.', 'parallel_life_3': '다른 삶에서 당신은 아니요라 했을 때 네라고 했습니다.', 'parallel_life_4': '다른 삶에서 당신은 완전히 다른 사람입니다.', 'parallel_life_5': '다른 삶에서 당신은 지금의 당신 그대로입니다.', 'parallel_life_6': '다른 삶에서 당신은 더 어려운 길을 택했습니다.', 'parallel_life_7': '다른 삶에서 당신은 여전히 이 방을 찾고 있습니다.', 'quote_parallel_lives': '모든 선택은 다르게 선택한 우주를 만듭니다.',
    'quiet_instruction': '상호작용하지 마세요. 그냥 가만히 있으세요.', 'quiet_reveal_1': '당신은 여기 있습니다.', 'quiet_reveal_2': '소음이 사라지고 있습니다.', 'quiet_reveal_3': '고요에서 무언가 나타나고 있습니다.', 'quiet_reveal_4': '생각보다 더 인내심이 있습니다.', 'quiet_reveal_5': '고요함이 당신의 이름을 압니다.', 'quiet_score_label': '고요함 점수', 'quiet_impressive': '놀라운 고요함.', 'quiet_good': '약간의 고요를 찾았습니다.', 'quiet_try_again': '행동하려는 충동이 강합니다.', 'quote_quiet_score': '고요함은 움직임의 부재가 아닙니다. 인식의 존재입니다.',
    'update_v42_10_rooms': '10개의 새 인터랙티브 방: 집중 타이머, 감사 항아리, 기분 나침반 등', 'update_v42_focus_timer': '집중 타이머 — 점점 어두워지는 타이머와 함께 침묵 속에 앉기', 'update_v42_gratitude_jar': '감사의 항아리 — 시간이 지남에 따라 감사의 순간 수집', 'update_v42_mood_compass': '기분 나침반 — 에너지와 감정 축에 자신을 배치', 'update_v42_weight_room': '무게의 방 — 짐이 되는 것에 이름을 붙이고 놓아주기',
    'update_v43_full_localization': '완전한 현지화 — 모든 방이 완전히 번역됨', 'update_v43_40_rooms_translated': '40개 이상의 방 번역 — 모든 인터랙티브 방이 모든 언어 지원', 'update_v43_10_languages': '10개 언어 지원 — 영어, 아랍어, 스페인어, 포르투갈어, 중국어, 프랑스어, 힌디어, 한국어, 일본어, 터키어', 'update_v43_nav_consistency': '일관된 내비게이션 — 모든 방에서 통일된 나가기 텍스트',
    // Daily Quote Room
    'quote_day_number': '{day}일째',
    'quote_come_back': '내일 새로운 명언을 보러 오세요.',
    // Leaderboard
    'leaderboard': '커뮤니티 리더보드',
    'leaderboard_subtitle': '익명 · 집단 · 경쟁 없음',
    'collective_time': '집단 시간',
    'achievements_count': '업적',
    'most_visited_rooms': '가장 많이 방문한 방',
    'most_time_spent': '가장 많은 시간 보낸 방',
    'most_unlocked_achievements': '가장 많이 해제된 업적',
    'no_achievements_yet': '아직 해제된 업적이 없습니다.',
    // Transfer Data
    'transfer_data': '데이터 전송',
    'transfer_data_desc': '일회용 코드로 진행 상황을 다른 기기로 이동하세요.',
    'generate_transfer_code': '전송 코드 생성',
    'enter_transfer_code': '코드 입력...',
    'transfer_code_expires': '코드는 24시간 후 만료되며 한 번만 사용할 수 있습니다.',
    'generating': '생성 중...',
    'import': '가져오기',
    'copied': '복사됨!',
    
    // Waiting Game Room
    'waiting_instruction': '움직이지 마세요. 클릭하지 마세요. 그냥 기다리세요.',
    'waiting_slow': '느려지는 것은 생각보다 어렵습니다.',
    'waiting_unfamiliar': '이 고요함이 낯설게 느껴지지 않나요?',
    'waiting_most': '대부분의 사람들은 이쯤에서 떠납니다.',
    'waiting_learning': '당신은 자신에 대해 무언가를 배우고 있습니다.',
    'waiting_rewards': '인내에는 그 나름의 보상이 있습니다.',
    'waiting_seconds': '초의 고요',
    'waiting_waiting': '기다리는 중...',
    'waiting_moved': '{count}번 움직였습니다.',
    'waiting_waited': '기다렸군요. 드문 일입니다.',
    'waiting_most_cant': '대부분은 아무것도 없이 앉아 있지 못합니다. 당신은 해냈습니다.',
    'waiting_quote': '현재의 순간은 우리에게 주어진 유일한 순간이며, 모든 순간으로 가는 문입니다.',
    // Feature Navigation
    'shared_room': '공유의 방',
    'imagine_room': '상상',
    'dream_room': '꿈',
    'future_room': '미래',
    'ai_reflection': 'AI 성찰',
    // Imagine Yourself Room
    'imagine_title': '자신을 상상해',
    'imagine_subtitle': '자신을 어떻게 보는지 묘사하세요. 말에서 무엇이 나타나는지 보세요.',
    'imagine_placeholder': '나는 나 자신을 이런 사람으로 봅니다...\n\n본질, 성격, 내면의 느낌을 묘사하세요.',
    'imagine_hint': '솔직하세요. 이미지는 당신의 말을 반영합니다.',
    'imagine_generate': '자신을 보기',
    'imagine_generating': '생성 중...',
    'imagine_try_again': '다시 묘사하기',
    'imagine_result_alt': '당신의 자화상',
    'imagine_quote': '우리는 자신을 명확히 보지 못합니다. 믿는 것을 봅니다.',
    'imagine_empty_error': '먼저 자신에 대해 써주세요',
    'imagine_short_error': '자신에 대해 더 알려주세요',
    'rate_limit_error': '요청이 너무 많습니다. 잠시 기다려주세요.',
    'credits_error': '서비스가 일시적으로 이용 불가합니다',
    'generation_error': '이미지를 생성할 수 없습니다. 다시 시도하세요.',
    // Dream Visualization Room
    'dream_title': '꿈 시각화',
    'dream_subtitle': '반복되는 꿈을 묘사하세요. 실체화되는 것을 보세요.',
    'dream_placeholder': '나는 계속 이런 꿈을 꿉니다...\n\n이미지, 감정, 반복되는 요소를 묘사하세요.',
    'dream_hint': '꿈은 상징으로 말합니다.',
    'dream_generate': '꿈 시각화',
    'dream_generating': '꿈꾸는 중...',
    'dream_try_again': '다시 꿈꾸기',
    'dream_result_alt': '당신의 꿈 시각화',
    'dream_quote': '꿈은 잠재의식이 우리에게 무언가를 말하려는 시도입니다.',
    'dream_empty_error': '먼저 꿈을 묘사해주세요',
    'dream_short_error': '꿈에 대해 더 알려주세요',
    // Future Self Room
    'future_title': '미래의 나',
    'future_subtitle': '되고 싶은 사람을 묘사하세요. 가능성을 보세요.',
    'future_placeholder': '미래에 나는...\n\n되기 위해 노력하는 사람을 묘사하세요.',
    'future_hint': '열망이 현실을 만듭니다.',
    'future_generate': '미래를 보기',
    'future_generating': '구상 중...',
    'future_try_again': '다시 구상하기',
    'future_result_alt': '미래의 당신',
    'future_quote': '미래는 들어가는 곳이 아닙니다. 만들어가는 것입니다.',
    'future_empty_error': '먼저 미래의 자신을 묘사해주세요',
    'future_short_error': '미래의 자신에 대해 더 알려주세요',
    // Guided Journeys
    'guided_journeys_title': '가이드 여정',
    'guided_journeys_subtitle': '내러티브 연결이 있는 큐레이션된 방 시퀀스. 가이드 명상 플레이리스트처럼.',
    'journey_complete': '여정 완료',
    'journey_complete_desc': '전체 경로를 걸었습니다. 방들이 당신의 여정을 기억합니다.',
    'more_journeys': '더 많은 여정',
    'next_room': '다음 방',
    'enter_room': '방 입장',
    'finish_journey': '여정 마무리',
    'step': '단계',
    // Fragment Collection
    'fragment_collection_title': '조각 컬렉션',
    'fragment_collection_subtitle': '모든 방은 무언가를 남깁니다. 모두 모으세요.',
    'collection_milestones': '이정표',
    'collection_secrets': '비밀',
    'collection_footer': '일부 조각은 특정 조건에서만 나타납니다.',
    'rarity_common': '일반',
    'rarity_rare': '희귀',
    'rarity_legendary': '전설',
    // AI Companion
    'companion_create': '동반자 만들기',
    'companion_setup_desc': '함께 걷는 성찰.',
    'companion_name': '동반자 이름 짓기',
    'companion_name_placeholder': '예: 메아리, 거울, 그림자...',
    'companion_lens': '성격 렌즈',
    'companion_mood': '소통 분위기',
    'companion_fears': '무엇이 두렵나요? (선택사항)',
    'companion_struggles': '무엇으로 힘들어하나요? (선택사항)',
    'create_companion': '만들기',
    'companion_input': '무언가 말해보세요...',
    'companion_greeting': '동반자가 왔습니다. 무언가 말해보세요.',
    'companion_starter_1': '오늘 내 마음에 있는 것은?',
    'companion_starter_2': '막혀있는 느낌이에요.',
    'companion_starter_3': '내가 피하는 것을 말해줘.',
    'fears_placeholder': '예: 실패, 거절, 잊혀지는 것...',
    'struggles_placeholder': '예: 과한 생각, 미루기, 외로움...',
    'back_step': '뒤로',
    'lens_reflective': '성찰적',
    'lens_reflective_desc': '깊은 질문, 패턴 인식',
    'lens_supportive': '지지적',
    'lens_supportive_desc': '따뜻하고, 격려하며, 인정하는',
    'lens_challenging': '도전적',
    'lens_challenging_desc': '직접적이고 생각을 자극하는',
    'lens_curious': '호기심',
    'lens_curious_desc': '예상 못한 질문, 경이',
    'lens_shadow': '그림자',
    'lens_shadow_desc': '숨겨진 면, 솔직한 불편함',
    'companion_mood_calm': '차분한',
    'companion_mood_calm_desc': '절제되고 평화로운',
    'companion_mood_intense': '강렬한',
    'companion_mood_intense_desc': '절박하고 깊은',
    'companion_mood_playful': '유쾌한',
    'companion_mood_playful_desc': '재치있고 가벼운',
    'companion_mood_melancholic': '우울한',
    'companion_mood_melancholic_desc': '아름다운 슬픔',
    // AI Reflection Room
    'reflection_room_title': '성찰의 방',
    'reflection_room_desc': '솔직한 무언가를 쓰세요. 거울이 보이지 않는 것을 보여줄 것입니다.',
    'reflection_room_privacy': '저장되지 않습니다. 공유되지 않습니다.',
    'reflection_begin': '시작하기',
    'reflection_prompt': '마음에 무엇이 있나요? 자유롭게 쓰세요.',
    'reflection_placeholder': '여기서 시작...',
    'reflection_characters': '글자',
    'reflection_min_chars': '최소 20자 필요',
    'reflection_send': '거울에 보내기',
    'reflection_waiting': '거울이 생각하고 있습니다...',
    'reflection_fragment_label': '조각',
    'reflection_label': '성찰',
    'reflection_question_label': '피하고 싶은 질문',
    'reflection_leave': '거울 떠나기',
    'reflection_error': '거울이 흐려졌습니다',
    'reflection_default_quote': '거울은 기억합니다',
    // Room Content - Image Zooming
    'image_zoom_title': '계속 확대하는 이미지',
    'image_zoom_zoom': '확대',
    'image_zoom_quote': '더 찾을 것은 없습니다. 처음부터 없었습니다.',
    // Room Content - Shadow Chase
    'shadow_chase_title': '그림자 쫓기',
    'shadow_chase_subtitle': '그림자를 잡으세요 • 예측 불가능하게 반응합니다',
    'shadow_chase_catches': '잡기',
    'shadow_chase_behavior': '행동',
    'shadow_chase_flee': '도주',
    'shadow_chase_wander': '방황',
    'shadow_chase_mirror': '거울',
    'shadow_chase_msg_1': '그림자는 당신이 어디로 가는지 압니다.',
    'shadow_chase_msg_2': '쫓는 것은 종종 달아납니다.',
    'shadow_chase_msg_3': '때로는 고요함이 속도보다 더 많이 잡습니다.',
    'shadow_chase_msg_4': '당신의 그림자는 모든 움직임을 기억합니다.',
    'shadow_chase_msg_5': '그것은 당신에게서 배웁니다.',
    // Room Content - Left Too Early
    'left_early_title': '너무 일찍 떠났습니다',
    'left_early_subtitle': '5초 이내에 나간 방들.',
    'left_early_none': '어떤 방도 너무 빨리 나가지 않았습니다.',
    'left_early_patience': '아마도 당신은 시간을 주는 편입니다.',
    'left_early_quote': '때로 우리는 방이 무언가를 보여주기 전에 떠납니다.',
    // Room Content - Face Without Features
    'face_title': '특징 없는 얼굴',
    'face_instruction': '특징을 클릭하여 제거하세요.',
    'face_nothing': '아무것도 남지 않았습니다.',
    'face_remove': '제거',
    'face_remaining': '남음',
    'face_eyes': '눈',
    'face_nose': '코',
    'face_mouth': '입',
    'face_eyebrows': '눈썹',
    'face_outline': '얼굴',
    'face_quote': '정체성은 우리가 인식하기로 합의한 특징일 뿐입니다.',
    // Room Content - The Last Visitor
    'last_visitor_title': '마지막 방문자',
    'last_visitor_someone': '누군가 여기 있었습니다',
    'last_visitor_first': '당신이 첫 방문자입니다.',
    'last_visitor_no_one': '당신 전에 아무도 오지 않았습니다.',
    'last_visitor_next': '다음 사람이 당신의 타임스탬프를 볼 것입니다.',
    'last_visitor_know': '그들은 당신이 여기 있었음을 알 것입니다.',
    'last_visitor_quote': '우리는 어둠 속에서도 흔적을 남깁니다.',
    'last_visitor_days_ago': '{count}일 전',
    'last_visitor_hours_ago': '{count}시간 전',
    'last_visitor_minutes_ago': '{count}분 전',
    'last_visitor_moments_ago': '방금 전',
    // Room Content - Room You Visit Most
    'visit_most_title': '가장 많이 방문한 방',
    'visit_most_subtitle': '당신의 행동이 패턴을 드러냅니다.',
    'visit_most_visits': '{count}회 방문',
    'visit_most_none': '아직 패턴이 없습니다. 계속 탐험하세요.',
    'visit_most_quote': '우리는 원하는 곳이 아닌 필요한 곳으로 돌아갑니다.',
    // Room Content - 3:17 AM Room
    'room_317_locked': '이 방은 새벽 3시 17분에만 열립니다.',
    'room_317_opens_in': '{hours}시간 {minutes}분 후 열림',
    'room_317_msg_1': '새벽 3시 17분입니다.',
    'room_317_msg_2': '당신은 깨어 있습니다.',
    'room_317_msg_3': '왜요?',
    'room_317_msg_4': '세상은 잠들었습니다. 하지만 당신은 아닙니다.',
    'room_317_msg_5': '어떤 생각이 당신을 놓아주지 않나요?',
    'room_317_msg_6': '이 시간에는 가면이 벗겨집니다.',
    'room_317_msg_7': '당신과 침묵만 있습니다.',
    'room_317_msg_8': '이 순간은 지금만 존재합니다.',
    'room_317_quote': '이 시간에는 가면이 벗겨집니다.',
    // Room Content - Weekend Room
    'weekend_locked': '이 방은 주말에만 열립니다.',
    'weekend_days_until': '이 방이 열리기까지 {count}일.',
    'weekend_label': '주말의 방',
    'weekend_prompt': '한 주가 끝났습니다. 이 이틀에 무엇을 가져왔나요?',
    'weekend_placeholder': '무게, 희망, 질문...',
    'weekend_release': '놓아주기',
    'weekend_wrote': '당신이 쓴 것:',
    'weekend_letting_go': '주말은 놓아주기 위한 것입니다.',
    // Room Content - Something Follows
    'follows_title': '무언가가 따라온다',
    'follows_distance': '거리',
    'follows_run': '달려',
    'follows_keep_running': '계속 달려. 절대 멈추지 않아.',
    'follows_caught': '잡혔습니다.',
    'follows_couldnt': '영원히 달릴 수는 없었습니다.',
    'follows_quote': '따라오는 것은 항상 자기 자신입니다.',
    // Room Content - Line You Crossed
    'line_title': '당신이 넘은 선',
    'line_instruction': '왼쪽에서 오른쪽으로 선을 그으세요.',
    'line_click_drag': '클릭하고 드래그하세요',
    'line_hesitations': '{count}개의 망설임을 발견했습니다.',
    'line_quote': '모든 멈춤은 의심을 드러냅니다.',
    // Scary Room Content
    'dont_look_title': '시선을 돌리지 마세요',
    'dont_look_away_msg': '시선을 돌리지 마세요.',
    'dont_look_looked': '시선을 돌렸습니다.',
    'dont_look_seconds': '{count}초의 집중',
    'dont_look_times': '{count}번 시선을 돌림',
    'dont_look_quote': '존재감은 가장 주기 어려운 것입니다.',
    'mirror_lies_title': '거울은 거짓말한다', 'mirror_lies_msg1': '자신을 보세요.', 'mirror_lies_msg2': '정말 당신인가요?', 'mirror_lies_msg3': '거울은 당신이 보고 싶은 것을 보여줍니다.', 'mirror_lies_msg4': '진실을 드러내려면 클릭하세요.', 'mirror_lies_msg5': '모든 균열이 더 많은 것을 드러냅니다.', 'mirror_lies_msg6': '반사 뒤에는 아무것도 없습니다.', 'mirror_lies_quote': '거울은 당신을 보여준 적이 없습니다. 당신이 믿었던 것만 보여줬을 뿐.',
    'winter_title': '가장 긴 밤', 'winter_date': '12월 21일', 'winter_msg1': '가장 긴 밤이 왔습니다.', 'winter_msg2': '어둠 속에서, 우리는 우리가 지닌 것을 발견합니다.', 'winter_msg3': '무엇을 놓을 건가요? 무엇을 간직할 건가요?', 'winter_releasing': '나는 놓아줍니다:', 'winter_keeping': '나는 간직합니다:', 'winter_release_placeholder': '더 이상 필요 없는 것...', 'winter_keep_placeholder': '힘을 주는 것...', 'winter_enter_light': '빛으로 들어가기', 'winter_released': '놓아줌:', 'winter_kept': '간직함:', 'winter_light_returns': '이 밤부터, 빛이 돌아옵니다.', 'winter_quote': '가장 긴 밤은 가장 깊은 어둠 속에서도 빛이 이미 돌아오고 있음을 가르쳐줍니다.',
    'halloween_title': '베일의 방', 'halloween_date': '10월 31일', 'halloween_msg1': '오늘 밤 베일이 얇습니다.', 'halloween_msg2': '당신의 그림자 속에 무엇이 숨어있나요?', 'halloween_msg3': '당신이 지닌 두려움의 이름을 대세요...', 'halloween_msg4': '형태를 주고, 놓아주세요.', 'halloween_placeholder': '두려움을 적으세요...', 'halloween_release': '놓아주기', 'halloween_dissolves': '당신의 두려움이 밤 속에 녹아듭니다...', 'halloween_quote': '우리가 두려워하는 것은 이름을 붙이면 힘을 잃습니다. 보내주세요.',
    'awake_title': '당신을 깨어있게 하는 것', 'awake_locked': '이 방은 밤 10시 이후에만 이용 가능합니다.', 'awake_return': '밤이 오면 돌아오세요.', 'awake_press_enter': '엔터를 누르세요', 'awake_named': '이제 이름을 붙였습니다.', 'awake_quote': '어둠은 당신이 낮에 피하는 것을 압니다.',
    'voice_meaning_title': '의미 없는 당신의 목소리', 'voice_meaning_prompt': '흥얼거리세요. 숨 쉬세요. 아무 소리나 내세요. 말은 없이.', 'voice_meaning_done': '이것이 당신의 존재였습니다. 그 이상도 이하도 아닙니다.', 'voice_meaning_hold': '누르고 있기', 'voice_meaning_recording': '녹음 중...', 'voice_meaning_no_playback': '재생 없음. 소리는 그 순간에만 존재했습니다.', 'voice_meaning_quote': '당신은 존재합니다. 그것이 유일한 내용입니다.',
    'watching_msg1': '여기 혼자입니다.', 'watching_msg2': '완전히 혼자.', 'watching_msg3': '정말 그런가요?', 'watching_msg4': '그들은 처음부터 여기 있었습니다.', 'watching_msg5': '그들은 항상 지켜보고 있습니다.',
    'shortcut_title': '당신이 항상 택하는 지름길', 'shortcut_instruction': '인식하는 것들을 선택하세요.', 'shortcut_1': '괜찮지 않은데 "괜찮아"라고 말하기', 'shortcut_2': '현재에 머무는 대신 핸드폰 확인하기', 'shortcut_3': '갈등을 피하려 조용히 있기', 'shortcut_4': '만드는 대신 사기', 'shortcut_5': '"적절한 때"를 기다리기', 'shortcut_6': '행동 대신 상황 탓하기', 'shortcut_7': '성장 대신 편안함 선택하기', 'shortcut_8': '잠자는 대신 스크롤하기', 'shortcut_9': '아니라고 말하고 싶을 때 네라고 말하기', 'shortcut_10': '하는 대신 계획하기', 'shortcut_pattern': '당신의 패턴', 'shortcut_selected': '선택한 것:', 'shortcut_show_pattern': '내 패턴 보기', 'shortcut_quote': '지름길이 길이 됩니다.', 'shortcut_pattern_own': '당신의 패턴은 당신만의 것입니다.', 'pattern_avoidance': '불편한 것을 피합니다.', 'pattern_distraction': '산만함으로 도망칩니다.', 'pattern_convenience': '노력을 편리함으로 바꿉니다.', 'pattern_delay': '오지 않는 조건을 기다립니다.', 'pattern_comfort': '불편함으로부터 자신을 보호합니다.', 'pattern_pleasing': '다른 사람을 편안하게 하려고 사라집니다.',
    'shapes_q1': '얼마나 자주 평화를 느끼나요?', 'shapes_q2': '자신을 얼마나 신뢰하나요?', 'shapes_q3': '다른 사람들과 얼마나 연결되어 있다고 느끼나요?', 'shapes_q4': '당신의 시간 중 얼마나 진정으로 당신의 것인가요?', 'shapes_q5': '미래가 얼마나 무겁게 느껴지나요?', 'shapes_result_title': '도형으로 본 당신의 삶', 'shapes_no_labels': '라벨 없음. 해석 없음.', 'shapes_yours': '보이는 것은 당신이 이해할 것입니다.', 'shapes_quote': '형태는 말 없는 의미입니다.', 'shapes_scale': '1 = 전혀 아님, 5 = 완전히',
    'read_title': '이것을 소리 내어 읽으세요', 'read_stmt1': '나는 무언가를 피하고 있습니다.', 'read_stmt2': '나는 두렵습니다.', 'read_stmt3': '나는 내가 뭘 하고 있는지 모릅니다.', 'read_stmt4': '나는 도움이 필요합니다.', 'read_stmt5': '나는 충분합니다.', 'read_stmt6': '나는 척해왔습니다.', 'read_stmt7': '나는 피곤합니다.', 'read_stmt8': '나는 더 원합니다.', 'read_i_read': '소리 내어 읽었습니다', 'read_really': '정말 말했나요?', 'read_skipped': '건너뛰었습니다', 'read_result': '진실을 소리 내어 말했습니다.', 'read_quote': '진실을 말하면 그것이 현실이 됩니다.',
    'sentence_title': '당신이 피하는 문장', 'sentence_sub': '이 문장을 완성하세요. 완성하기 전에는 떠날 수 없습니다.', 'sentence_prompt': '내가 ...을 멈추면 더 행복할 텐데', 'sentence_cant_leave': '무언가를 쓰지 않으면 떠날 수 없습니다.', 'sentence_knew': '이미 알고 있었습니다.', 'sentence_quote': '첫 번째 단계는 말하는 것입니다.',
    'say_once_title': '한 번만 말하세요', 'say_once_prompt': '한 문장을 녹음하세요. 다시 들을 수 없습니다. 다시 녹음할 수 없습니다.', 'say_once_done': '말해졌습니다. 재생 없음. 다시 없음.', 'say_once_stop': '중지', 'say_once_record': '녹음', 'say_once_quote': '어떤 것들은 한 번만 말하기 위한 것입니다.',
    'newyear_title': '미래의 나에게 보내는 편지', 'newyear_threshold': '문턱', 'newyear_placeholder': '미래의 나에게...', 'newyear_seal': '편지 봉인하기', 'newyear_waiting': '이 편지가 당신을 기다릴 것입니다.', 'newyear_quote': '모든 끝은 문입니다. 의도를 가지고 건너세요.',
    'avoid_title': '당신이 피하는 방', 'avoid_sub': '건너뛰는 것이 무언가를 말합니다.', 'avoid_not_entered': '들어가지 않은 곳:', 'avoid_visited_all': '모든 무료 방을 방문했습니다.', 'avoid_not_pattern': '어쩌면 회피가 당신의 패턴이 아닐 수도.', 'avoid_quote': '회피도 선택입니다.',
    'who_q1': '돈이 문제가 아니라면 무엇을 하겠나요?', 'who_q2': '파티에서 자신을 어떻게 소개하겠나요?', 'who_q3': '무엇이 살아있다고 느끼게 하나요?', 'who_q4': '친구들은 당신이 무엇을 잘한다고 말할까요?', 'who_q5': '어릴 때 무엇을 좋아했나요?', 'who_result_title': '직함 없는 당신', 'who_done': '직함이 사라졌을 때의 당신입니다.', 'who_quote': '정체성은 당신이 하는 것이 아닙니다. 멈췄을 때 남는 것입니다.', 'who_placeholder': '솔직하게...', 'who_see_myself': '나를 보기', 'question': '질문',
    'lastconv_title': '마지막 대화', 'lastconv_sub': '절대 보내지 않을 것을 쓰세요.', 'lastconv_to': '수신:', 'lastconv_message': '메시지:', 'lastconv_to_placeholder': '이것을 들어야 할 사람...', 'lastconv_msg_placeholder': '항상 하고 싶었던 말...', 'lastconv_not_saved': '이 메시지는 저장되거나 공유되지 않습니다.', 'lastconv_seal': '편지 봉인', 'lastconv_wrote_to': '당신이 쓴 대상:', 'lastconv_never_sent': '이 메시지는 절대 보내지지 않을 것입니다.', 'lastconv_point': '하지만 쓰는 것이 목적이었습니다.', 'lastconv_quote': '어떤 말은 존재해야 합니다, 들리지 않더라도.',
    'priorities_title': '진짜 우선순위', 'priorities_sub': '각각에 주당 몇 시간을 쓰나요?', 'priorities_result': '진짜 우선순위', 'priorities_most_time': '가장 많은 시간을 쓰는 것:', 'priorities_truth': '시간은 진실을 드러냅니다. 말은 거짓말할 수 있지만, 일정은 못합니다.', 'priorities_quote': '당신은 반복하는 것이 됩니다.', 'priorities_show': '내 진실 보여줘', 'priority_work': '일', 'priority_family': '가족', 'priority_friends': '친구', 'priority_health': '건강', 'priority_learning': '배움', 'priority_entertainment': '오락', 'priority_social': '소셜 미디어', 'priority_sleep': '수면',
    'voice_intro': '어둠 속에서 목소리가 말합니다...', 'voice_silence': '(침묵)', 'voice_quote': '어떤 목소리는 집으로 가는 길을 찾지 못합니다.',
    'knows_tell': '이름을 알려주세요.', 'knows_placeholder': '당신의 이름...', 'knows_warning': '(여기 오지 말았어야 했습니다)', 'knows_quote': '이름에는 힘이 있습니다. 당신의 이름은 이미 알려져 있었습니다.',
    'door_msg1': '문 앞에 무언가가 있습니다.', 'door_stopped': '(노크가 멈췄습니다)', 'door_quote': '어떤 문은 닫혀 있어야 합니다.',
    'eyes_msg1': '한 쌍의 눈이 지켜봅니다.', 'eyes_unsee': '한번 보면 잊을 수 없습니다.', 'eyes_quote': '어떤 것은 한번 알아차리면 무시할 수 없습니다.',
    'moon_locked': '이 방은 보름달 때만 열립니다.', 'moon_not_full': '오늘 밤은 보름달이 아닙니다. 주기가 완성되면 돌아오세요.', 'moon_quote': '어떤 진실은 주기 안에서만 드러납니다.',
    'at317_title': '새벽 3:17의 당신', 'at317_locked': '이 방은 새벽 2시에서 5시 사이에 열립니다.', 'at317_current': '현재 시간:', 'at317_quote': '밤은 당신의 이름을 압니다.',
    'mirrors_you_are_here': '당신은 여기 있습니다.',
    'listen_title': '자신의 소리를 들어보세요', 'listen_recording': '녹음 중...', 'listen_start': '시작', 'listen_play': '재생', 'listen_instruction': '10초를 녹음하세요. 아무 말이나 하세요.', 'listen_wait': '기다리세요. 그런 다음 들으세요.', 'listen_done': '그건 당신의 목소리였습니다. 느리게. 이상하게.', 'listen_speed': '0.5배속으로 재생 중', 'listen_quote': '당신의 목소리가 다르게 들리는 건 마침내 들었기 때문입니다.',
    'export_title': '여정 내보내기', 'export_subtitle': '이 장소의 일부를 가져가세요', 'export_tab_export': '내보내기', 'export_tab_import': '가져오기', 'export_your_journey': '당신의 여정', 'export_since': '시작일', 'export_time_spent': '소요 시간', 'export_rooms_visited': '방문한 방', 'export_rooms_completed': '완료한 방', 'export_fragments': '조각', 'export_achievements_unlocked': '달성한 업적', 'export_truths_recorded': '기록된 진실', 'export_text_file': '텍스트 파일', 'export_json_file': 'JSON 파일', 'export_copied': '복사됨!', 'export_copy_clipboard': '클립보드에 복사', 'export_data_stays': '당신의 데이터는 당신에게 남습니다. 우리는 보관하지 않습니다.', 'export_import_desc': '이전에 내보낸 여정 가져오기', 'export_json_only': 'JSON 형식만 가능', 'export_select_file': '파일 선택', 'export_merge_note': '현재 여정 데이터와 병합됩니다.', 'export_done': '내보내기 완료.', 'export_done_desc': '당신이 어디에 있었는지의 기록. 무엇을 직면했는지.', 'export_quote': '어떤 여정은 기록할 가치가 있습니다. 다른 사람을 위해서가 아닙니다. 자신을 위해서.', 'export_import_success': '여정이 성공적으로 가져왔습니다', 'export_import_error': '잘못된 여정 파일',
    'waitroom_title': '대기실', 'waitroom_subtitle': '어떤 문은 그룹에게만 열립니다.', 'waitroom_open': '문이 열렸습니다.', 'waitroom_waiting': '대기 중', 'waitroom_time_waited': '대기 시간', 'waitroom_users_needed': '필요한 사용자', 'waitroom_someone_joined': '누군가 대기에 합류했습니다.', 'waitroom_someone_left': '누군가 떠났습니다.', 'waitroom_room_opens': '방이 열립니다.', 'waitroom_patience': '인내와 존재가 힘으로 열 수 없는 것을 엽니다.', 'waitroom_souls_waited': '{count}개의 영혼이 함께 기다렸습니다.', 'waitroom_seconds_stillness': '{count}초의 집단적 고요.', 'waitroom_quote': '어떤 것은 서두를 수 없습니다. 어떤 문은 혼자서는 열 수 없습니다.',
    'capsule_title': '타임캡슐', 'capsule_subtitle': '미래의 자신에게 보내는 메시지', 'capsule_what_tell': '자신에게 무엇을 말해야 하나요?', 'capsule_placeholder': '미래의 나에게...', 'capsule_when_arrive': '언제 도착해야 하나요?', 'capsule_30_days': '30일', 'capsule_90_days': '90일', 'capsule_1_year': '1년', 'capsule_seal': '캡슐 봉인', 'capsule_waiting': '캡슐 대기 중', 'capsule_sealed': '봉인됨.', 'capsule_will_wait': '당신의 메시지가 기다릴 것입니다.', 'capsule_return_in': '{days}일 후에 돌아와 열어보세요.', 'capsule_quote_sealed': '시간은 모든 것을 바꿉니다. 당신까지도.', 'capsule_arrived': '메시지가 도착했습니다.', 'capsule_from_past': '{days}일 전의 당신으로부터.', 'capsule_open': '캡슐 열기', 'capsule_you_wrote': '당신이 자신에게 쓴 것:', 'capsule_remember': '이것을 쓴 것을 기억하나요?', 'capsule_quote_opened': '이것을 쓴 사람은 떠났습니다. 당신은 남아있습니다.',
    'contradiction_title': '모순의 방', 'contradiction_subtitle': '당신의 진실이 충돌하는 곳', 'contradiction_not_enough': '아직 데이터가 충분하지 않습니다.', 'contradiction_answer_more': '방들에서 더 많은 질문에 답하세요.', 'contradiction_answers_recorded': '{count}/5 답변 기록됨', 'contradiction_quote_early': '모순은 복잡성을 필요로 합니다. 먼저 당신의 것을 만드세요.', 'contradiction_no_found': '모순이 발견되지 않았습니다.', 'contradiction_consistent': '당신은 놀랍도록 일관적이거나...', 'contradiction_careful': '...놀랍도록 조심스럽습니다.', 'contradiction_quote_none': '일관성은 지혜이거나 연기입니다.', 'contradiction_found': '{count}개의 모순을 발견했습니다', 'contradiction_you_said': '당신이 말한 것:', 'contradiction_but_also': '하지만 또한:', 'contradiction_see_another': '다른 것 보기 →', 'contradiction_not_weakness': '모순은 약점이 아닙니다. 깊이의 증거입니다.', 'contradiction_quote': '우리는 다양함을 담고 있습니다. 그 중 일부는 동의하지 않습니다.',
    'entropy_living': '살아있는', 'entropy_dead': '죽은', 'entropy_plant_seed': '씨앗 심기', 'entropy_instructions': '빈 곳을 클릭하여 심으세요. 식물을 클릭하여 물주세요. 죽은 식물은 제거할 수 있습니다.', 'entropy_reflect': '정원을 돌아보기', 'entropy_lives': '당신의 정원은 살아있습니다.', 'entropy_thrive': '당신이 돌아왔기에 {living}개의 식물이 번성합니다.', 'entropy_absence': '{dead}개가 부재를 상기시킵니다.',
    'palace_title': '기억의 궁전', 'palace_subtitle': '정신적 공간을 만드세요. 기억할 물건을 놓으세요. 나중에 걸어보세요.', 'palace_room_of': '방 {current} / {total}', 'palace_click_place': '클릭하여 놓기', 'palace_what_represent': '이것은 무엇을 나타내나요?', 'palace_cancel': '취소', 'palace_place_object': '물건 놓기', 'palace_objects_count': '궁전에 {count}개의 물건', 'palace_take_tour': '투어하기', 'palace_empty_room': '이 방은 비어있습니다', 'palace_previous': '이전', 'palace_next_room': '다음 방', 'palace_complete_tour': '투어 완료', 'palace_remembers': '당신의 궁전은 기억합니다.', 'palace_geography': '당신이 구축한 의미의 지리.', 'palace_quote': '마음은 궁전입니다. 그 안에 무엇을 넣느냐가 당신이 누가 될지 결정합니다.',
    'echoes_subtitle': '다른 여행자들의 조각', 'echoes_from': '에서', 'echoes_ago': '전', 'echoes_arriving': '더 많은 메아리가 도착하고 있습니다...', 'echoes_reveal': '이것은 그들의 답이 아닙니다. 당신의 것입니다.', 'echoes_reveal2': '(모든 메아리는 한때 당신 자신의 반영이었습니다)', 'echoes_quote': '우리는 자신이 특별하다고 생각합니다. 우리는 단지 서로의 메아리일 뿐입니다.',
    'revisit_subtitle': '당신이 어떻게 변했는지', 'revisit_not_enough': '아직 충분한 질문에 답하지 않았습니다.', 'revisit_explore': '더 많은 방을 탐험하세요. 흔적을 남기세요. 나중에 돌아오세요.', 'revisit_quote1': '변화에는 역사가 필요합니다. 역사에는 시간이 필요합니다.', 'revisit_none_changed': '하지만 아직 변한 것이 없습니다. 방을 다시 방문하세요. 다시 답하세요.', 'revisit_show_evolved': '당신이 어떻게 진화했는지 보여드리겠습니다.', 'revisit_quote2': '변화를 보려면 먼저 변해야 합니다.', 'revisit_then': '그때', 'revisit_now': '지금', 'revisit_one_shift': '하나의 변화. 미묘하지만 실제.', 'revisit_each_change': '모든 변화는 작은 죽음입니다. 그리고 작은 탄생입니다.', 'revisit_quote3': '당신은 과거의 당신이 아닙니다. 아직 미래의 당신도 아닙니다.',
  },

  // Japanese translations
  ja: {
    'title': 'ヒューマンルーム',
    'tagline': '誰も見ていないときのあなたを映し出す部屋のコレクション。',
    'enter': '入る',
    'footer': 'セラピーではない。ソーシャルではない。生産的ではない。',
    'back': '← 戻る',
    'hallway': '廊下',
    'leave_room': '← 部屋を出る',
    'return_hallway': '廊下に戻る',
    'loading': '読み込み中...',
    'submit': '送信',
    'cancel': 'キャンセル',
    'close': '閉じる',
    'next': '次へ',
    'previous': '前へ',
    'start': '始める',
    'stop': '止める',
    'yes': 'はい',
    'no': 'いいえ',
    'complete': '完了',
    'reveal': '明かす',
    'continue': '続ける',
    'begin': '始める',
    'return': '← 戻る',
    'escape': '← 逃げる',
    'run': '← 走れ',
    'leave': '← 去る',
    'watch': '見守る...',
    'correct': '正解',
    'round': 'ラウンド',
    'enter_again': 'もう一度入る',
    'steps': 'ステップ',
    'depth': '深さ',
    'settings': '設定',
    'language': '言語',
    'updates': 'アップデート',
    'updates_title': '新着情報',
    'new': '新着',
    'view_all_updates': 'すべてのアップデートを見る',
    'whats_new_intro': '最新アップデート:',
    'challenges': 'チャレンジ',
    'random': 'ランダム',
    'open_rooms': 'オープンルーム',
    'dark_rooms': 'ダークルーム',
    'enter_risk': '自己責任で入室',
    'premium_rooms': 'プレミアムルーム',
    'unlock_to_access': 'アンロックしてアクセス',
    'daily_rooms': 'デイリールーム',
    'refreshes_daily': '毎日更新',
    'time_locked': '時間制限付きルーム',
    'seasonal_rooms': 'シーズナルルーム',
    'available_now': '現在利用可能',
    'fragments': 'フラグメント',
    'boss_unlocked': 'ボスルーム解放',
    'collect_unlock': 'ボスルーム解放のために収集',
    'dark_fragments': 'ダークフラグメント',
    'dark_boss_unlocked': 'ダークボス解放',
    'hidden_discovered': '隠し部屋を発見',
    'stillness_revealed': 'あなたの静けさが何かを明かした...',
    'constellation_map': '星座マップ',
    'your_stats': '統計',
    'completed': '完了済み',
    'game_rooms': 'ゲームルーム',
    'experimental_rooms': '実験的ルーム',
    'deep_rooms': 'ディープルーム',
    'meta_rooms': 'メタルーム',
    'search_rooms': '部屋を検索...',
    'no_results': '該当する部屋なし',
    'clear_filters': 'フィルターをクリア',
    'explore': '探索',
    'all_categories': 'すべて',
    'random_room': 'ランダムルーム',
    'rooms_explored': '探索した部屋',
    'your_journey': 'あなたの旅',
    'choose_direction': '方向を選んでください。いくつかの扉は応答します。',
    'achievements': '実績',
    'achievements_subtitle': '旅のマイルストーン',
    'points': 'ポイント',
    'unlocked': '解除済み',
    'exploration': '探索',
    'consistency': '継続',
    'discovery': '発見',
    'special': '特別',
    'all': 'すべて',
    'bookmarks': 'ブックマーク',
    'bookmarked_rooms': 'ブックマーク済み',
    'no_bookmarks': 'ブックマークなし',
    'what_is_this': 'この場所は何？',
    'total_time': '合計時間',
    'rooms_visited': '訪問した部屋',
    'total_visits': '合計訪問',
    'most_visited': '最も訪問',
    'longest_session': '最長セッション',
    'fragments_collected': '収集フラグメント',
    'completed_rooms': '完了した部屋',
    'privacy_note': 'すべてのデータはデバイスにローカル保存',
    'whisper_wall': 'ささやきの壁',
    'leave_whisper': 'ささやきを残す...',
    'whisper_sent': 'あなたのささやきが壁に流れていきます。',
    'whispers_count': 'ささやき',
    'anonymous': '匿名',
    // Room Titles
    'room_how-many-times-left': 'あと何回',
    'room_life-in-weeks': '週で見る人生',
    'room_delay-button': '遅延ボタン',
    'room_one-honest-sentence': '一つの正直な文',
    'room_this-moment': 'この瞬間は二度と来ない',
    'room_reality-receipt': '現実のレシート',
    'room_nothing-happens': 'ここでは何も起きない',
    'room_default-choice': 'デフォルトの選択',
    'room_thought-loop': '思考のループ',
    'room_expect-vs-happens': '期待 vs 現実',
    'room_question-never-answer': '決して答えない質問',
    'room_not-special': '特別じゃない（それでいい）',
    'room_shortcut-always-take': 'いつも選ぶ近道',
    'room_blurred-self': 'ぼやけた自己',
    'room_image-zooming': 'ズームし続ける画像',
    'room_comfort-color': '安らぎの色',
    'room_line-you-crossed': '越えた線',
    'room_read-aloud': '声に出して読む',
    'room_voice-without-meaning': '意味のない声',
    'room_sentence-you-avoid': '避ける文',
    'room_if-nothing-changes': '何も変わらなければ',
    'room_real-priorities': '本当の優先順位',
    'room_last-conversation': '最後の会話',
    'room_who-without-job': '仕事なしのあなたは誰？',
    'room_comfort-zone-scanner': 'コンフォートゾーンスキャナー',
    'room_say-it-once': '一度だけ言って',
    'room_listen-to-yourself': '自分の声を聴く',
    'room_life-as-shapes': '形で見る人生',
    'room_face-without-features': '特徴のない顔',
    'room_what-keeps-awake': '眠れない理由',
    'room_you-at-317am': '午前3時17分のあなた',
    'room_thought-that-returns': '戻ってくる思考',
    'room_room-you-visit-most': '最も訪れる部屋',
    'room_room-you-avoid': '避ける部屋',
    'room_left-too-early': '早すぎる退室',
    'room_daily-quote': '今日の名言',
    'room_daily-joke': '今日のジョーク',
    'room_theyre-watching': '見られている',
    'room_the-last-visitor': '最後の訪問者',
    'room_dont-look-away': '目を逸らすな',
    'room_the-whisper': 'ささやき',
    'room_you-forgot-something': '何か忘れた',
    'room_the-mirror-lies': '鏡は嘘をつく',
    'room_something-follows': '何かがついてくる',
    'room_the-countdown': 'カウントダウン',
    'room_eyes-in-the-dark': '闇の中の目',
    'room_the-voice': '声',
    'room_it-knows-your-name': 'それはあなたの名前を知っている',
    'room_the-door': 'ドア',
    'room_boss-room-free': '真実の総和',
    'room_boss-room-horror': '恐怖の心臓',
    'room_boss-room-premium': '最も深い真実',
    'room_mouse-tracker': 'マウストラッカー',
    'room_scroll-confession': 'スクロール告白',
    'room_waiting-game': '待機のゲーム',
    'room_day-in-pixels': 'ピクセルで見る一日',
    'room_memory-maze': '記憶の迷路',
    'room_decision-shadow': '決断の影',
    'room_anonymous-mirrors': '匿名の鏡',
    'room_collective-room': '集団の部屋',
    'room_secret-room': '秘密の部屋',
    'room_glitch-room': '不安定な部屋',
    'room_full-moon-room': '満月の部屋',
    'room_weekend-room': '週末の部屋',
    'room_317am-room': '午前3:17の部屋',
    'room_idle-room': '静寂の部屋',
    'room_invite-friend-room': '共有の部屋',
    'room_halloween-room': 'ヴェールの部屋',
    'room_winter-solstice-room': '最も長い夜',
    'room_new-year-room': '未来の自分への手紙',
    'room_memory-puzzle': '記憶パズル',
    'room_shadow-chase': '影の追跡',
    'room_timing-room': 'タイミングルーム',
    'room_pattern-whisper': 'パターンの囁き',
    'room_silence-puzzle': '沈黙のパズル',
    'room_decay-game': '崩壊ゲーム',
    'room_observation-test': '観察テスト',
    'room_balance-room': 'バランスルーム',
    'room_word-drift': '言葉の漂流',
    'room_color-echo': '色の反響',
    'room_mirror-tap': 'ミラータップ',
    'room_maze-of-doors': 'ドアの迷路',
    'room_pulse-match': '脈拍マッチ',
    'room_light-path': '光の道',
    'room_sound-match': '音のマッチ',
    'room_mirror-room': '鏡の部屋',
    'room_echo-room': '反響の部屋',
    'room_pulse-room': '脈動の部屋',
    'room_vanishing-room': '消える部屋',
    'room_letter-room': '文字の部屋',
    'room_empty-room': '空の部屋',
    'room_reverse-room': '逆転の部屋',
    'room_whisper-room': 'ささやきの部屋',
    'room_shadow-room': '影の部屋',
    'room_labyrinth-room': '迷宮の部屋',
    'room_color-room': '色の部屋',
    'room_surrender-room': '降伏の部屋',
    'room_noise-room': 'ノイズの部屋',
    'room_observer-room': '観察者の部屋',
    'room_imagine-yourself': '自分を想像して',
    'room_ai-reflection-room': 'AI省察の部屋',
    'room_the-other-side': '向こう側',
    'room_the-unraveling': '解れ',
    'room_the-echo': '反響',
    'room_ash-embers': '灰と燃え殻',
    'room_twilight-stars': '黄昏の星',
    'room_void-abyss': '深淵',
    'room_eclipse-shadows': '日食の影',
    'room_dawn-awakening': '夜明けの目覚め',
    'room_letters-never-sent': '送らなかった手紙',
    'room_breath-sync': '呼吸シンク',
    'room_entropy-garden': 'エントロピーの庭',
    'room_memory-palace': '記憶の宮殿',
    'room_morning-ritual': '朝の儀式',
    'room_evening-ritual': '夕べの儀式',
    'room_weekly-ritual': '週間の儀式',
    'room_spring-equinox-room': '春分',
    'room_summer-solstice-room': '夏至',
    'room_autumn-equinox-room': '秋分',
    'room_valentine-room': '自己愛の部屋',
    'room_fragment-trade': 'フラグメントの祭壇',
    'room_the-archive': 'アーカイブ',
    'room_mirror-of-all-selves': 'すべての自己の鏡',
    'room_the-ending': '終わり',
    'room_whisper-wall': 'ささやきの壁',
    'room_collective-memory': '集合的記憶',
    'room_shared-moments': '共有された瞬間',
    'room_void-room': '虚無',
    'room_pattern-room': 'あなたのパターン',
    'room_time-capsule': 'タイムカプセル',
    'room_the-revisit': '再訪',
    'room_contradiction-room': '矛盾の部屋',
    'room_export-journey': '旅をエクスポート',
    'room_anonymous-echoes': '匿名の反響',
    'room_the-waiting-room': '待合室',
    'room_dream-visualization': '夢の視覚化',
    'room_future-self': '未来の自分',
    'room_regret-machine': '後悔マシン',
    'room_memory-reconstruction': '記憶の再構成',
    'room_mask-you-wear': 'かぶっている仮面',
    'room_recurring-dream': '繰り返す夢',
    'room_promise-you-broke': '破った約束',
    'room_words-you-swallowed': '飲み込んだ言葉',
    'room_age-you-feel': '感じる年齢',
    'room_biggest-lie': '最大の嘘',
    'room_person-you-pretend': '演じている人',
    'room_person-you-pretend-to-be': '演じている人',
    'room_what-you-collect': '集めるもの',
    'room_apology-you-owe': '返すべき謝罪',
    'room_unfinished-story': '未完の物語',
    'room_version-others-see': '他人が見るバージョン',
    'room_secret-talent': '秘めた才能',
    'room_fear-you-feed': '育てる恐怖',
    'room_guilty-pleasure': '後ろめたい楽しみ',
    'room_rule-you-break': 'いつも破るルール',
    'room_childhood-promise': '子供時代の約束',
    'room_compliment-you-reject': '拒む褒め言葉',
    'room_autopilot-mode': 'オートパイロットモード',
    'room_boundary-you-need': '必要な境界線',
    'room_inner-critic': '内なる批評家',
    'room_last-sunday': '先週の日曜日',
    'room_wasted-hours': '無駄にした時間',
    'room_year-that-changed': 'あなたを変えた年',
    'room_tomorrows-regret': '明日の後悔',
    'room_morning-autopilot': '朝のオートパイロット',
    'room_season-you-dread': '恐れる季節',
    'room_ten-years-ago': '10年前の今日',
    'room_last-normal-day': '最後の普通の日',
    'room_clock-you-ignore': '無視する時計',
    'room_your-deadline': '締め切り',
    'room_the-pause': '一時停止',
    'room_static-room': '静寂',
    'room_gap-between-thoughts': '思考の間の隙間',
    'room_sit-with-discomfort': '不快感と共に座る',
    'room_boring-room': '退屈な部屋',
    'room_unplug': 'アンプラグ',
    'room_unplug-room': 'アンプラグ',
    'room_last-page': '最後のページ',
    'room_scrolling-pattern': 'スクロールパターン',
    'room_tab-you-keep-open': '閉じないタブ',
    'room_digital-shadow': 'デジタルシャドウ',
    'room_notification-you-ignore': '無視する通知',
    'room_typing-speed': 'タイピング速度',
    'room_link-you-almost-clicked': 'クリックしかけたリンク',
    'room_bookmark-graveyard': 'ブックマーク墓地',
    'room_search-you-deleted': '削除した検索',
    'room_gravity-room': '重力の部屋',
    'room_ink-blot': 'インクの染み',
    'room_hallway-of-mirrors': '鏡の廊下',
    'room_dissolving-text': '溶けるテキスト',
    'room_the-pendulum': '振り子',
    'room_pixel-rain': 'ピクセルの雨',
    'room_the-threshold': '敷居',
    'room_the-friend-you-lost': '失った友人',
    'room_conversation-you-replay': '繰り返す会話',
    'room_the-thank-you': '感謝',
    'room_people-you-forgot': '忘れた人々',
    'room_the-advice-you-give': 'あなたがするアドバイス',
    'room_what-others-dont-know': '他人が知らないこと',
    'room_the-lie-you-believe': '信じている嘘',
    'room_your-shadow-self': '影の自己',
    'room_the-story-you-tell': 'あなたが語る物語',
    'room_your-safe-place': '安全な場所',
    'room_the-risk-you-didnt-take': '取らなかったリスク',
    'room_your-last-excuse': '最後の言い訳',
    'room_the-habit-you-hide': '隠す習慣',
    'room_what-you-postpone': '先延ばしにすること',
    'room_the-door-you-closed': '閉じたドア',
    'room_the-emotion-you-hide': '隠す感情',
    'room_your-comfort-lie': '安らぎの嘘',
    'room_the-silence-between': '間の沈黙',
    'room_what-you-carry': '背負っているもの',
    'room_the-version-you-lost': '失ったバージョン',
    'room_letter-to-past-self': '過去の自分への手紙',
    'room_the-moment-you-knew': '知った瞬間',
    'room_the-words-you-rehearse': 'リハーサルする言葉',
    'room_someone-thinks-of-you': '誰かがあなたを思っている',
    'room_the-question-you-fear': '恐れる質問',
    'room_recurring-dream-journal': '繰り返す夢の日記',
    'room_lucid-moment': '明晰な瞬間',
    'room_nightmare-you-remember': '覚えている悪夢',
    'room_dream-you-wish-was-real': '現実だったらと願う夢',
    'room_the-dream-visitor': '夢の訪問者',
    'room_the-blank-canvas': '白いキャンバス',
    'room_song-that-is-you': 'あなたという曲',
    'room_color-of-today': '今日の色',
    'room_the-unwritten-poem': '書かれなかった詩',
    'room_your-inner-voice': '内なる声',
    'room_fear-you-named': '名前をつけた恐怖',
    'room_the-leap': '跳躍',
    'room_courage-inventory': '勇気の棚卸し',
    'room_what-scares-you-most': '最も怖いもの',
    'room_facing-the-dark': '闇に向き合う',
    'room_letter-to-future-self': '未来の自分への手紙',
    'room_the-world-you-want': '望む世界',
    'room_your-next-chapter': '次の章',
    'room_the-wish': '願い',
    'room_if-you-had-one-year': '1年あったら',
    // Room Subtitles
    'sub_how-many-times-left': '残りの回数',
    'sub_life-in-weeks': '4,000個のマス。それだけ。',
    'sub_delay-button': '毎回難しくなる',
    'sub_one-honest-sentence': '編集不可。削除不可。',
    'sub_this-moment': 'あなたはここに。一度だけ。',
    'sub_reality-receipt': 'あなたの日々の要約',
    'sub_nothing-happens': 'そのまま',
    'sub_default-choice': '安全か危険か。毎回。',
    'sub_thought-loop': '繰り返す心を見て',
    'sub_expect-vs-happens': '希望と現実の間',
    'sub_question-never-answer': 'ほとんどの人は去る',
    'sub_not-special': '慰めなし。真実だけ。',
    'sub_shortcut-always-take': 'パターンが現れる',
    'sub_blurred-self': '記憶はこう機能する',
    'sub_image-zooming': '無の中に答えを探す',
    'sub_comfort-color': 'どのくらい留まりますか？',
    'sub_line-you-crossed': 'ためらいが見える',
    'sub_read-aloud': '声に出すべき言葉',
    'sub_voice-without-meaning': '言葉のない音',
    'sub_sentence-you-avoid': '書くまで出られない',
    'sub_if-nothing-changes': '次の5年の予測',
    'sub_real-priorities': '言葉ではなく時間に基づく',
    'sub_last-conversation': '決して送らないものを書く',
    'sub_who-without-job': '自分を書き直す',
    'sub_comfort-zone-scanner': 'ためらいが見える',
    'sub_say-it-once': 'リプレイなし。やり直しなし。',
    'sub_listen-to-yourself': 'スローにした声',
    'sub_life-as-shapes': '抽象的。ラベルなし。あなたの。',
    'sub_face-without-features': '何も残らなくなるまで削除',
    'sub_what-keeps-awake': '午後10時以降利用可能',
    'sub_you-at-317am': '時間特定。個人的。',
    'sub_thought-that-returns': '毎回戻ってくる',
    'sub_room-you-visit-most': 'パターンが明らかに',
    'sub_room-you-avoid': 'スキップすることが何かを語る',
    'sub_left-too-early': '早く出ると作動',
    'sub_daily-quote': '毎日変わる知恵',
    'sub_daily-joke': 'ブラックユーモア、毎日更新',
    'sub_theyre-watching': '一人ではない',
    'sub_the-last-visitor': '誰かが先に来た',
    'sub_dont-look-away': '離れると気づく',
    'sub_the-whisper': 'よく聴いて',
    'sub_you-forgot-something': '何だったか？',
    'sub_the-mirror-lies': '真実を見るために割れ',
    'sub_something-follows': '走り続けて',
    'sub_the-countdown': 'ゼロで何かが起きる',
    'sub_eyes-in-the-dark': '増えていく',
    'sub_the-voice': '誰かが閉じ込められている',
    'sub_it-knows-your-name': '来るべきではなかった',
    'sub_the-door': '何かがノックしている',
    'sub_boss-room-free': '最後の無料の部屋',
    'sub_boss-room-horror': '最後の闇に立ち向かえ',
    'sub_boss-room-premium': '最後の深い部屋',
    'sub_mouse-tracker': '動き方が見える',
    'sub_scroll-confession': 'どれだけ急いでいるか？',
    'sub_waiting-game': '何もしないでいられますか？',
    'sub_day-in-pixels': '時間が形になる',
    'sub_memory-maze': 'パターンが変わる',
    'sub_decision-shadow': '選択がパターンを明かす',
    'sub_anonymous-mirrors': '見知らぬ人の思考',
    'sub_collective-room': 'みんなここに。誰も残らない。',
    'sub_secret-room': '見つけた',
    'sub_glitch-room': '何かがおかしい',
    'sub_full-moon-room': '月のピーク時のみ',
    'sub_weekend-room': '休息は目的地',
    'sub_317am-room': '時間特定。個人的。',
    'sub_idle-room': '忍耐で見つかる',
    'sub_invite-friend-room': '目撃されるべき旅もある',
    'sub_halloween-room': '10月31日 - ヴェールが薄い',
    'sub_winter-solstice-room': '12月21日 - 手放して残す',
    'sub_new-year-room': '12月31日 - なりたい自分に書く',
    'sub_memory-puzzle': '微妙に変化するパターンを合わせる',
    'sub_shadow-chase': '予測不能なものを捕まえる',
    'sub_timing-room': '正確な瞬間にクリック',
    'sub_pattern-whisper': '消えゆくシーケンスを繰り返す',
    'sub_silence-puzzle': '説明なしで解く',
    'sub_decay-game': '消えゆくものを保存する',
    'sub_observation-test': '微妙な違いを見つける',
    'sub_balance-room': '浮かぶ物体を整列させる',
    'sub_word-drift': '落ちる前に文字を捕まえる',
    'sub_color-echo': '微妙なトーンの順序を繰り返す',
    'sub_mirror-tap': 'タップが別の場所で響く',
    'sub_maze-of-doors': '選択するたびにドアが再配置',
    'sub_pulse-match': '部屋のリズムに合わせて動く',
    'sub_light-path': '変わる闇の中で光を導く',
    'sub_sound-match': '形を柔らかい音に合わせる',
    'sub_mirror-room': '少し歪んだ反射',
    'sub_echo-room': 'クリックが遅延エコーを引き起こす',
    'sub_pulse-room': '環境が呼吸する',
    'sub_vanishing-room': '操作すると要素が消える',
    'sub_letter-room': '記憶のように文字が散る',
    'sub_empty-room': '秘密を見つけるまで空',
    'sub_reverse-room': '行動が反対に',
    'sub_whisper-room': 'かすかに聞こえる真実',
    'sub_shadow-room': '暗い仲間',
    'sub_labyrinth-room': '道を見つけて',
    'sub_color-room': '色彩瞑想',
    'sub_surrender-room': 'コントロールを手放す',
    'sub_noise-room': '静的な思考',
    'sub_observer-room': '見られている感覚',
    'sub_imagine-yourself': '自分を描写。何が現れるか見て。',
    'sub_ai-reflection-room': 'AIと共に省察',
    'sub_the-other-side': '暗闇をゆっくり移動',
    'sub_the-unraveling': '言葉ごとに現実がほつれる',
    'sub_the-echo': '言ったことは決して消えない',
    'sub_ash-embers': '灰テーマでのみ表示',
    'sub_twilight-stars': '黄昏テーマでのみ表示',
    'sub_void-abyss': '虚無テーマでのみ表示',
    'sub_eclipse-shadows': '完全な闇の中で語る',
    'sub_dawn-awakening': '最初の光を迎える',
    'sub_letters-never-sent': '読まれることのない人へ書く',
    'sub_breath-sync': '部屋と呼吸を合わせる',
    'sub_entropy-garden': '成長と衰退が絡み合う',
    'sub_memory-palace': '精神の聖域を建てる',
    'sub_morning-ritual': '意図を持って一日を始める',
    'sub_evening-ritual': '振り返りで一日を終える',
    'sub_weekly-ritual': '日曜日は振り返りの日',
    'sub_spring-equinox-room': '手放して新しくなる',
    'sub_summer-solstice-room': '最も長い日',
    'sub_autumn-equinox-room': '収穫と受容',
    'sub_valentine-room': '受けるべき愛',
    'sub_fragment-trade': '秘密を開くために犠牲にする',
    'sub_the-archive': '完全な旅の歴史',
    'sub_mirror-of-all-selves': 'AIが行動の肖像を見せる',
    'sub_the-ending': '得た別れ',
    'sub_whisper-wall': '匿名の真実が闇に漂う',
    'sub_collective-memory': '私たちが覚えていることの共有断片',
    'sub_shared-moments': '正直に、調子はどう？',
    'sub_void-room': 'コードの傷',
    'sub_pattern-room': 'サイトがあなたに見るもの',
    'sub_time-capsule': '未来の自分に書く',
    'sub_the-revisit': '答えがどう変わったか見る',
    'sub_contradiction-room': '真実が衝突する',
    'sub_export-journey': 'パスをダウンロード',
    'sub_anonymous-echoes': '見知らぬ人の断片',
    'sub_the-waiting-room': '存在で解除',
    'sub_dream-visualization': '夢を描写。実体化を見る。',
    'sub_future-self': '誰になりたいですか？',
    'sub_regret-machine': 'あり得たことを探る',
    'sub_memory-reconstruction': '薄れゆく記憶を再構成',
    'sub_mask-you-wear': 'どんな顔を見せている？',
    'sub_recurring-dream': '繰り返し戻ってくる夢',
    'sub_promise-you-broke': '破った約束の正直さ',
    'sub_words-you-swallowed': '言わなかったこと',
    'sub_age-you-feel': '本当は何歳？',
    'sub_biggest-lie': '最もよくつく嘘',
    'sub_person-you-pretend': 'あなたの演技',
    'sub_person-you-pretend-to-be': 'あなたの演技',
    'sub_what-you-collect': '物がアイデンティティを明かす',
    'sub_apology-you-owe': '言うべきだったことを書く',
    'sub_unfinished-story': '完成しなかったもの',
    'sub_version-others-see': '他人の目に映るもの',
    'sub_secret-talent': '誰も知らない能力',
    'sub_fear-you-feed': 'どの恐怖が支配する？',
    'sub_guilty-pleasure': '隠している楽しみ',
    'sub_rule-you-break': '反抗のパターン',
    'sub_childhood-promise': '自分にした約束',
    'sub_compliment-you-reject': 'なぜ褒め言葉を受け入れられない？',
    'sub_autopilot-mode': '考えずにすること',
    'sub_boundary-you-need': '守るべきもの',
    'sub_inner-critic': '裁く声',
    'sub_last-sunday': '実際に何をした？',
    'sub_wasted-hours': '時間が消える場所',
    'sub_year-that-changed': '転換点',
    'sub_tomorrows-regret': '何をしておけば良かった？',
    'sub_morning-autopilot': '最初の1時間の儀式',
    'sub_season-you-dread': 'どの季節が最も重い？',
    'sub_ten-years-ago': 'あの頃の自分は？',
    'sub_last-normal-day': 'すべてが変わる前',
    'sub_clock-you-ignore': '過ぎないふりをする時間',
    'sub_your-deadline': '何を先延ばしにしている？',
    'sub_the-pause': '少し息をして',
    'sub_static-room': 'ホワイトノイズと静寂',
    'sub_gap-between-thoughts': '空間を見つけて',
    'sub_sit-with-discomfort': '何も直さないで',
    'sub_boring-room': '面白いものは何もない',
    'sub_unplug': 'デバイスのない想像',
    'sub_unplug-room': 'デバイスのない想像',
    'sub_last-page': '物語はここで終わる',
    'sub_scrolling-pattern': 'コンテンツの消費方法',
    'sub_tab-you-keep-open': 'ブラウザに残っているもの',
    'sub_digital-shadow': 'オンラインの足跡',
    'sub_notification-you-ignore': '無視するもの',
    'sub_typing-speed': 'どれだけ速く表現する？',
    'sub_link-you-almost-clicked': 'ためらいのパターン',
    'sub_bookmark-graveyard': '保存して戻らなかったもの',
    'sub_search-you-deleted': '検索して消したもの',
    'sub_gravity-room': 'すべてがゆっくり落ちる',
    'sub_ink-blot': '何が見える？',
    'sub_hallway-of-mirrors': '無限の反射',
    'sub_dissolving-text': '読むと文字が溶ける',
    'sub_the-pendulum': '催眠的な動き',
    'sub_pixel-rain': 'デジタルの雨',
    'sub_the-threshold': 'ここと向こうの間',
    'sub_the-friend-you-lost': '離れた人に書く',
    'sub_conversation-you-replay': '記憶から再現',
    'sub_the-thank-you': '表現しなかった感謝',
    'sub_people-you-forgot': '忘れた名前を思い出して',
    'sub_the-advice-you-give': 'でも従わないアドバイス',
    'sub_what-others-dont-know': '隠された層',
    'sub_the-lie-you-believe': '心地よい嘘',
    'sub_your-shadow-self': '抑圧する部分',
    'sub_the-story-you-tell': '人生の語り方',
    'sub_your-safe-place': '安全を感じる場所',
    'sub_the-risk-you-didnt-take': '歩まなかった道',
    'sub_your-last-excuse': '最もよく使う言い訳',
    'sub_the-habit-you-hide': '秘密のルーティン',
    'sub_what-you-postpone': 'いつも「明日」のこと',
    'sub_the-door-you-closed': '拒んだ機会',
    'sub_the-emotion-you-hide': '一度だけ名前をつけて。',
    'sub_your-comfort-lie': '安らぎの小さな嘘。',
    'sub_the-silence-between': '二つの思考の間に座る。',
    'sub_what-you-carry': '見えない重さに名前を。',
    'sub_the-version-you-lost': '人生が起きる前のあなた。',
    'sub_letter-to-past-self': 'かつての自分に書く。',
    'sub_the-moment-you-knew': 'すべてが変わった瞬間。',
    'sub_the-words-you-rehearse': '練習するけど言わないこと。',
    'sub_someone-thinks-of-you': '今、どこかで誰かがあなたを覚えている。',
    'sub_the-question-you-fear': '誰にも聞かれたくない質問。',
    'sub_recurring-dream-journal': '何度も戻ってくる夢。',
    'sub_lucid-moment': '夢の中で意識的になる。',
    'sub_nightmare-you-remember': 'まだ悩ます悪夢。',
    'sub_dream-you-wish-was-real': '目覚めて。現実じゃなかった。',
    'sub_the-dream-visitor': '誰かが夢に現れ続ける。',
    'sub_the-blank-canvas': '無から何かを創り出す。',
    'sub_song-that-is-you': 'あなたが曲なら、どの曲？',
    'sub_color-of-today': '現在の状態の色を選んで。',
    'sub_the-unwritten-poem': '中に溜めていたものを書く。',
    'sub_your-inner-voice': '実際何と言っている？',
    'sub_fear-you-named': '名前をつければ力を失う。',
    'sub_the-leap': '何に飛び込む？',
    'sub_courage-inventory': '勇敢だったことすべてのリスト。',
    'sub_what-scares-you-most': '最も深い恐怖。',
    'sub_facing-the-dark': '完全な暗闇に座る。',
    'sub_letter-to-future-self': 'なる自分に書く。',
    'sub_the-world-you-want': '存在してほしい世界を描写。',
    'sub_your-next-chapter': '物語の次は？',
    'sub_the-wish': '一つの願い。ルールなし。',
    'sub_if-you-had-one-year': '365日で何をする？',
    // v4.0 extras
    'dream_journal_placeholder': '私が繰り返し見る夢は...',
    'record_dream': '記録する',
    'dream_journal_reflection': '夢は目覚めた心が言わないことを語ります。',
    'quote_dream_journal': '繰り返す夢は自分自身との未完の対話です。',
    'become_aware': '私は意識している',
    'lucid_aware': 'この意識の瞬間だけが本物です。',
    'quote_lucid_moment': '現実を疑う瞬間こそ、本当に見える瞬間です。',
    'wish_placeholder': '私の願いは...',
    'make_wish': '願いを込めて',
    'quote_wish': 'あなたが願うことは、最も大切にしているものを明かします。',
    'update_v4_languages': '4つの新言語: ヒンディー語、韓国語、日本語、トルコ語',
    'update_v4_20_rooms': '20の新しい部屋: 夢、創造性、恐怖、未来',
    'update_v4_dreams_category': '夢と潜在意識の部屋カテゴリ',
    'update_v4_creativity_category': '創造性と表現の部屋カテゴリ',
    'update_v4_fear_category': '恐怖と勇気の部屋カテゴリ',
    'update_v4_future_category': '未来と希望の部屋カテゴリ',
    'room_playlists': 'ルームプレイリスト',
    'personal_dashboard': 'ダッシュボード',
    'dashboard_subtitle': '一目でわかるあなたの旅',
    'visit_pattern': '訪問パターン（14日間）',
    'dashboard_top_rooms': '最も訪問した部屋',
    'category_breakdown': '部屋のカテゴリ',
    'mood_trend': '気分の傾向',
    'fragment_breakdown': 'フラグメント内訳',
    'no_data_yet': 'まだデータがありません',
    'current_streak': '連続日数',
    'playlist_morning': '朝の振り返り',
    'playlist_morning_desc': '明晰さと存在感で一日を始めましょう',
    'playlist_identity': 'アイデンティティの深掘り',
    'playlist_identity_desc': 'ラベルの下のあなたは誰？',
    'playlist_creative': '創造の火花',
    'playlist_creative_desc': '創造的表現を解放しましょう',
    'playlist_courage': '勇気の道',
    'playlist_courage_desc': '一部屋ずつ恐怖に立ち向かう',
    'playlist_dreams': '夢日記',
    'playlist_dreams_desc': '潜在意識の風景を探索しましょう',
    'playlist_future': '未来のビジョン',
    'playlist_future_desc': '来るべき自分への手紙と願い',
    'playlist_truth': '真実との対峙',
    'playlist_truth_desc': '不快な現実に向き合う',
    'playlist_night': '夜の旅',
    'playlist_night_desc': '沈黙、虚空、無を受け入れる',
    // v4.2.0
    'room_focus-timer': '集中タイマー', 'sub_focus-timer': '静かに座って。振り返って。', 'room_gratitude-jar': '感謝の瓶', 'sub_gratitude-jar': '感謝していること一つ', 'room_mood-compass': 'ムードコンパス', 'sub_mood-compass': '今どこにいますか？', 'room_memory-fade': '記憶の消失', 'sub_memory-fade': '記憶が溶けるのを見て', 'room_sound-of-your-day': '今日の音', 'sub_sound-of-your-day': '今日はどんな音？', 'room_the-apology-room': '謝罪の部屋', 'sub_the-apology-room': '言えなかったことを書いて', 'room_your-unread-messages': '未読メッセージ', 'sub_your-unread-messages': '開かなかったメッセージ', 'room_the-weight-room': '重荷の部屋', 'sub_the-weight-room': '重荷に名前をつけて', 'room_parallel-lives': 'パラレルライフ', 'sub_parallel-lives': '別の人生では、あなたは...', 'room_the-quiet-score': '静寂スコア', 'sub_the-quiet-score': 'どれだけ静かでいられますか？',
    'focus_choose': 'どれくらい静かに座りたいですか？', 'focus_sit': '座って。呼吸して。思考を流して。', 'focus_done': '静寂が語りました。', 'quote_focus_timer': '静けさの中で、騒音が消した声が聞こえます。', 'minutes': '分',
    'gratitude_prompt': '今日感謝していることを書いてください。', 'gratitude_placeholder': '感謝しています...', 'gratitude_add': '瓶に入れる', 'gratitude_added': '感謝が瓶の中を漂います。', 'gratitude_count': '感謝の瞬間', 'quote_gratitude_jar': '感謝とはもっと持つことではない。すでにここにあるものに気づくこと。',
    'mood_compass_prompt': '今いると感じる場所をクリック。', 'mood_high_energy': '高エネルギー', 'mood_low_energy': '低エネルギー', 'mood_positive': 'ポジティブ', 'mood_negative': 'ネガティブ', 'quote_mood_compass': '自分がどこにいるか知ることが、どこへ行くか知る第一歩。',
    'memory_fade_prompt': '記憶を入力。溶けるのを見て。', 'memory_fade_placeholder': '手放したい記憶...', 'memory_fade_start': '消えさせる', 'memory_fade_done': 'もう消えました。でも本物でした。', 'quote_memory_fade': '記憶は見ていても見ていなくても溶けていく。',
    'sound_day_prompt': '一日を表す3-5の音を選んで。', 'sound_rain': '雨', 'sound_traffic': '交通', 'sound_birds': '鳥', 'sound_keyboard': 'キーボード', 'sound_silence': '静寂', 'sound_wind': '風', 'sound_ocean': '海', 'sound_crowd': '群衆', 'sound_music': '音楽', 'sound_day_confirm': 'これが私の一日', 'sound_day_result': 'あなたの一日のサウンドトラック。', 'quote_sound_day': '毎日に音がある。ほとんどの人は聴くことを忘れている。',
    'apology_prompt': '届けなかった謝罪を書いて。', 'apology_placeholder': 'ごめんなさい...', 'apology_release': '手放す', 'apology_done': '何もないところに圧縮された。でもあなたは言った。', 'quote_apology': 'ある謝罪は相手のためではない。自分のためだ。',
    'unread_from_past': '差出人：過去の自分', 'unread_from_future': '差出人：未来の自分', 'unread_from_critic': '差出人：内なる批評家', 'unread_msg_past': '物事が変わると自分に約束した時を覚えていますか？', 'unread_msg_future': '今は以前より自分に優しくあってほしい。', 'unread_msg_critic': 'もっとできたと知っているでしょう。でもやりますか？', 'unread_count': '未読', 'unread_reply_placeholder': '返信...', 'unread_reply': '送信', 'unread_sent': 'メッセージが虚空に送られました。', 'quote_unread_messages': '避けているメッセージこそ最も必要なものが多い。',
    'weight_prompt': '重荷に名前をつけて。', 'weight_placeholder': '重くのしかかるもの...', 'weight_release_start': '手放し始める', 'weight_release_prompt': '各重荷をクリックして手放して。', 'weight_done': '重荷が上がりました。今のところ。', 'quote_weight_room': '名前をつけることを拒否するものは下ろせない。',
    'parallel_intro': '別の人生では、あなたは...', 'parallel_life_1': '別の人生では、離島の灯台守。', 'parallel_life_2': '別の人生では、故郷を離れなかった。', 'parallel_life_3': '別の人生では、いいえと言った時にはいと言った。', 'parallel_life_4': '別の人生では、まったく別の誰か。', 'parallel_life_5': '別の人生では、今のあなたそのもの。', 'parallel_life_6': '別の人生では、より困難な道を選んだ。', 'parallel_life_7': '別の人生では、まだこの部屋を探している。', 'quote_parallel_lives': 'すべての選択が、異なる選択をした宇宙を作る。',
    'quiet_instruction': '操作しないで。ただ静かに。', 'quiet_reveal_1': 'あなたはここにいます。', 'quiet_reveal_2': '騒音が消えていく。', 'quiet_reveal_3': '静寂から何かが現れている。', 'quiet_reveal_4': '思っているより忍耐強い。', 'quiet_reveal_5': '静寂はあなたの名前を知っている。', 'quiet_score_label': '静寂スコア', 'quiet_impressive': '驚くべき静寂。', 'quiet_good': '少し静けさを見つけた。', 'quiet_try_again': '行動への衝動は強い。', 'quote_quiet_score': '静寂は動きの不在ではない。意識の存在だ。',
    'update_v42_10_rooms': '10の新しいインタラクティブルーム：集中タイマー、感謝の瓶、ムードコンパスなど', 'update_v42_focus_timer': '集中タイマー — 徐々に暗くなるタイマーで静寂に座る', 'update_v42_gratitude_jar': '感謝の瓶 — 時間とともに感謝の瞬間を集める', 'update_v42_mood_compass': 'ムードコンパス — エネルギーと感情の軸に自分を配置', 'update_v42_weight_room': '重荷の部屋 — 重荷に名前をつけて手放す',
    'update_v43_full_localization': '完全ローカライズ — すべての部屋が完全翻訳済み', 'update_v43_40_rooms_translated': '40以上の部屋を翻訳 — すべてのインタラクティブルームが全言語対応', 'update_v43_10_languages': '10言語対応 — 英語、アラビア語、スペイン語、ポルトガル語、中国語、フランス語、ヒンディー語、韓国語、日本語、トルコ語', 'update_v43_nav_consistency': '統一ナビゲーション — すべての部屋で統一された退出テキスト',
    // Daily Quote Room
    'quote_day_number': '{day}日目',
    'quote_come_back': '明日また新しい名言をお届けします。',
    // Leaderboard
    'leaderboard': 'コミュニティリーダーボード',
    'leaderboard_subtitle': '匿名 · 集団 · 競争なし',
    'collective_time': '集団時間',
    'achievements_count': '実績',
    'most_visited_rooms': '最も訪問された部屋',
    'most_time_spent': '最も時間を費やした部屋',
    'most_unlocked_achievements': '最も解除された実績',
    'no_achievements_yet': 'まだ実績が解除されていません。',
    // Transfer Data
    'transfer_data': 'データ転送',
    'transfer_data_desc': 'ワンタイムコードで別のデバイスに進捗を移動します。',
    'generate_transfer_code': '転送コード生成',
    'enter_transfer_code': 'コードを入力...',
    'transfer_code_expires': 'コードは24時間で期限切れとなり、1回のみ使用できます。',
    'generating': '生成中...',
    'import': 'インポート',
    'copied': 'コピーしました！',
    
    // Waiting Game Room
    'waiting_instruction': '動かないで。クリックしないで。ただ待って。',
    'waiting_slow': 'ゆっくりすることは思ったより難しい。',
    'waiting_unfamiliar': 'この静けさは馴染みがないでしょう？',
    'waiting_most': 'ほとんどの人はもう去っています。',
    'waiting_learning': 'あなたは自分自身について何かを学んでいます。',
    'waiting_rewards': '忍耐にはそれなりの報酬があります。',
    'waiting_seconds': '秒の静止',
    'waiting_waiting': '待機中...',
    'waiting_moved': '{count}回動きました。',
    'waiting_waited': '待ちましたね。それは珍しいことです。',
    'waiting_most_cant': 'ほとんどの人は何もせずに座っていられません。あなたはできました。',
    'waiting_quote': '今この瞬間は私たちに与えられた唯一の瞬間であり、すべての瞬間への扉です。',
    // Feature Navigation
    'shared_room': '共有の部屋',
    'imagine_room': '想像',
    'dream_room': '夢',
    'future_room': '未来',
    'ai_reflection': 'AI省察',
    // Imagine Yourself Room
    'imagine_title': '自分を想像して',
    'imagine_subtitle': '自分をどう見ているか描写してください。言葉から何が現れるか見てみましょう。',
    'imagine_placeholder': '私は自分をこういう人だと思います...\n\n本質、性格、内面の感覚を描写してください。',
    'imagine_hint': '正直に。イメージはあなたの言葉を反映します。',
    'imagine_generate': '自分を見る',
    'imagine_generating': '作成中...',
    'imagine_try_again': 'もう一度描写する',
    'imagine_result_alt': 'あなたの自画像',
    'imagine_quote': '私たちは自分を明確に見ません。信じていることを見ます。',
    'imagine_empty_error': 'まず自分について書いてください',
    'imagine_short_error': 'もっと自分について教えてください',
    'rate_limit_error': 'リクエストが多すぎます。少々お待ちください。',
    'credits_error': 'サービスが一時的に利用不可です',
    'generation_error': '画像を作成できませんでした。もう一度お試しください。',
    // Dream Visualization Room
    'dream_title': '夢の視覚化',
    'dream_subtitle': '繰り返す夢を描写してください。実体化するのを見てください。',
    'dream_placeholder': '私が繰り返し見る夢は...\n\nイメージ、感覚、繰り返す要素を描写してください。',
    'dream_hint': '夢はシンボルで語ります。',
    'dream_generate': '夢を視覚化',
    'dream_generating': '夢見中...',
    'dream_try_again': 'もう一度夢を見る',
    'dream_result_alt': 'あなたの夢の視覚化',
    'dream_quote': '夢は潜在意識が私たちに何かを伝えようとする試みです。',
    'dream_empty_error': 'まず夢を描写してください',
    'dream_short_error': '夢についてもっと教えてください',
    // Future Self Room
    'future_title': '未来の自分',
    'future_subtitle': 'なりたい自分を描写してください。可能性を見てください。',
    'future_placeholder': '未来の私は...\n\nなりたい自分を描写してください。',
    'future_hint': '志が現実を形作ります。',
    'future_generate': '未来を見る',
    'future_generating': '構想中...',
    'future_try_again': 'もう一度構想する',
    'future_result_alt': '未来の自分',
    'future_quote': '未来は入る場所ではない。作るものだ。',
    'future_empty_error': 'まず未来の自分を描写してください',
    'future_short_error': '未来の自分についてもっと教えてください',
    // Guided Journeys
    'guided_journeys_title': 'ガイド付きジャーニー',
    'guided_journeys_subtitle': 'ナラティブで繋がるキュレーションされた部屋シーケンス。ガイド瞑想プレイリストのように。',
    'journey_complete': 'ジャーニー完了',
    'journey_complete_desc': '全行程を歩みました。部屋たちはあなたの旅を覚えています。',
    'more_journeys': 'もっとジャーニー',
    'next_room': '次の部屋',
    'enter_room': '部屋に入る',
    'finish_journey': 'ジャーニーを終える',
    'step': 'ステップ',
    // Fragment Collection
    'fragment_collection_title': 'フラグメントコレクション',
    'fragment_collection_subtitle': 'すべての部屋は何かを残します。すべて集めましょう。',
    'collection_milestones': 'マイルストーン',
    'collection_secrets': '秘密',
    'collection_footer': '一部のフラグメントは特定の条件でのみ現れます。',
    'rarity_common': '一般',
    'rarity_rare': 'レア',
    'rarity_legendary': 'レジェンダリー',
    // AI Companion
    'companion_create': 'コンパニオンを作る',
    'companion_setup_desc': '一緒に歩む省察。',
    'companion_name': 'コンパニオンに名前をつける',
    'companion_name_placeholder': '例：エコー、ミラー、シャドウ...',
    'companion_lens': '性格レンズ',
    'companion_mood': 'コミュニケーションスタイル',
    'companion_fears': '何が怖いですか？（任意）',
    'companion_struggles': '何に苦しんでいますか？（任意）',
    'create_companion': '作成',
    'companion_input': '何か言ってみて...',
    'companion_greeting': 'コンパニオンが来ました。何か言ってみてください。',
    'companion_starter_1': '今日心にあることは？',
    'companion_starter_2': '行き詰まっている感じがします。',
    'companion_starter_3': '私が避けていることを教えて。',
    'fears_placeholder': '例：失敗、拒絶、忘れられること...',
    'struggles_placeholder': '例：考えすぎ、先延ばし、孤独...',
    'back_step': '戻る',
    'lens_reflective': '省察的',
    'lens_reflective_desc': '深い質問、パターン認識',
    'lens_supportive': '支援的',
    'lens_supportive_desc': '温かく、励まし、認める',
    'lens_challenging': '挑戦的',
    'lens_challenging_desc': '直接的で思考を刺激する',
    'lens_curious': '好奇心',
    'lens_curious_desc': '予想外の質問、驚き',
    'lens_shadow': '影',
    'lens_shadow_desc': '隠された面、正直な不快感',
    'companion_mood_calm': '穏やか',
    'companion_mood_calm_desc': '節度があり平和',
    'companion_mood_intense': '激しい',
    'companion_mood_intense_desc': '切迫して深い',
    'companion_mood_playful': '遊び心',
    'companion_mood_playful_desc': 'ウィットがあり軽やか',
    'companion_mood_melancholic': '憂鬱',
    'companion_mood_melancholic_desc': '美しい悲しみ',
    // AI Reflection Room
    'reflection_room_title': '省察の部屋',
    'reflection_room_desc': '正直なことを書いてください。鏡が見えないものを見せてくれます。',
    'reflection_room_privacy': '保存されません。共有されません。',
    'reflection_begin': '始める',
    'reflection_prompt': '心にあることは？自由に書いてください。',
    'reflection_placeholder': 'ここから始めて...',
    'reflection_characters': '文字',
    'reflection_min_chars': '最低20文字必要',
    'reflection_send': '鏡に送る',
    'reflection_waiting': '鏡が考えています...',
    'reflection_fragment_label': 'フラグメント',
    'reflection_label': '省察',
    'reflection_question_label': '避けたい質問',
    'reflection_leave': '鏡を離れる',
    'reflection_error': '鏡が曇っています',
    'reflection_default_quote': '鏡は覚えている',
    // Room Content - Image Zooming
    'image_zoom_title': 'ズームし続ける画像',
    'image_zoom_zoom': 'ズーム',
    'image_zoom_quote': 'これ以上見つけるものはありません。最初からなかったのです。',
    // Room Content - Shadow Chase
    'shadow_chase_title': '影を追え',
    'shadow_chase_subtitle': '影を捕まえろ • 予測不能に反応する',
    'shadow_chase_catches': '捕獲',
    'shadow_chase_behavior': '行動',
    'shadow_chase_flee': '逃走',
    'shadow_chase_wander': '彷徨',
    'shadow_chase_mirror': '鏡像',
    'shadow_chase_msg_1': '影はあなたの行き先を知っている。',
    'shadow_chase_msg_2': '追いかけるものはしばしば逃げる。',
    'shadow_chase_msg_3': '時には静止が速さより多くを捕らえる。',
    'shadow_chase_msg_4': 'あなたの影はすべての動きを覚えている。',
    'shadow_chase_msg_5': 'それはあなたから学ぶ。',
    // Room Content - Left Too Early
    'left_early_title': '早すぎた退出',
    'left_early_subtitle': '5秒以内に出た部屋。',
    'left_early_none': 'どの部屋も急いで出ていません。',
    'left_early_patience': 'おそらくあなたは物事に時間を与える人です。',
    'left_early_quote': '部屋が何かを見せる前に去ってしまうことがある。',
    // Room Content - Face Without Features
    'face_title': '特徴のない顔',
    'face_instruction': '特徴をクリックして削除してください。',
    'face_nothing': '何も残っていません。',
    'face_remove': '削除',
    'face_remaining': '残り',
    'face_eyes': '目',
    'face_nose': '鼻',
    'face_mouth': '口',
    'face_eyebrows': '眉',
    'face_outline': '顔',
    'face_quote': 'アイデンティティとは、認識することに同意した特徴にすぎない。',
    // Room Content - The Last Visitor
    'last_visitor_title': '最後の訪問者',
    'last_visitor_someone': '誰かがここにいました',
    'last_visitor_first': 'あなたが最初の訪問者です。',
    'last_visitor_no_one': 'あなたの前には誰も来ませんでした。',
    'last_visitor_next': '次の人があなたのタイムスタンプを見ます。',
    'last_visitor_know': '彼らはあなたがここにいたことを知るでしょう。',
    'last_visitor_quote': '暗闇の中でも痕跡を残す。',
    'last_visitor_days_ago': '{count}日前',
    'last_visitor_hours_ago': '{count}時間前',
    'last_visitor_minutes_ago': '{count}分前',
    'last_visitor_moments_ago': 'たった今',
    // Room Content - Room You Visit Most
    'visit_most_title': '最も訪れる部屋',
    'visit_most_subtitle': 'あなたの行動がパターンを明かす。',
    'visit_most_visits': '{count}回訪問',
    'visit_most_none': 'まだパターンはありません。探索を続けてください。',
    'visit_most_quote': '私たちは欲しいものではなく、必要なものに戻る。',
    // Room Content - 3:17 AM Room
    'room_317_locked': 'この部屋は午前3時17分にのみ開きます。',
    'room_317_opens_in': '{hours}時間{minutes}分後に開きます',
    'room_317_msg_1': '午前3時17分です。',
    'room_317_msg_2': 'あなたは起きています。',
    'room_317_msg_3': 'なぜ？',
    'room_317_msg_4': '世界は眠っている。でもあなたは違う。',
    'room_317_msg_5': 'どんな考えがあなたを離さないのですか？',
    'room_317_msg_6': 'この時間、仮面は落ちる。',
    'room_317_msg_7': 'あなたと沈黙だけがある。',
    'room_317_msg_8': 'この瞬間は今だけ存在する。',
    'room_317_quote': 'この時間、仮面は落ちる。',
    // Room Content - Weekend Room
    'weekend_locked': 'この部屋は週末にのみ開きます。',
    'weekend_days_until': 'この部屋が開くまであと{count}日。',
    'weekend_label': '週末の部屋',
    'weekend_prompt': '一週間が終わりました。この二日間に何を持ち込みましたか？',
    'weekend_placeholder': '重荷、希望、質問...',
    'weekend_release': '手放す',
    'weekend_wrote': 'あなたが書いたこと：',
    'weekend_letting_go': '週末は手放すためにある。',
    // Room Content - Something Follows
    'follows_title': '何かが追ってくる',
    'follows_distance': '距離',
    'follows_run': '走れ',
    'follows_keep_running': '走り続けろ。止まらない。',
    'follows_caught': '捕まりました。',
    'follows_couldnt': '永遠には走れなかった。',
    'follows_quote': '追いかけてくるものは常に自分自身だ。',
    // Room Content - Line You Crossed
    'line_title': 'あなたが越えた線',
    'line_instruction': '左から右へ一本の線を引いてください。',
    'line_click_drag': 'クリックしてドラッグ',
    'line_hesitations': '{count}個のためらいが見つかりました。',
    'line_quote': 'すべての停止は疑いを明かす。',
    // Scary Room Content
    'dont_look_title': '目をそらすな',
    'dont_look_away_msg': '目をそらすな。',
    'dont_look_looked': '目をそらしました。',
    'dont_look_seconds': '{count}秒の注意',
    'dont_look_times': '{count}回目をそらした',
    'dont_look_quote': '存在することは最も難しい贈り物だ。',
    'mirror_lies_title': '鏡は嘘をつく', 'mirror_lies_msg1': '自分を見て。', 'mirror_lies_msg2': 'それは本当にあなた？', 'mirror_lies_msg3': '鏡はあなたが見たいものを映す。', 'mirror_lies_msg4': 'クリックして真実を明かす。', 'mirror_lies_msg5': 'ひび割れるたびに、もっと見える。', 'mirror_lies_msg6': '映り込みの向こうには何もない。', 'mirror_lies_quote': '鏡はあなたを映したことがない。信じていたものだけを。',
    'winter_title': '最も長い夜', 'winter_date': '12月21日', 'winter_msg1': '最も長い夜が来た。', 'winter_msg2': '闇の中で、私たちは自分が運ぶものを見つける。', 'winter_msg3': '何を手放す？何を残す？', 'winter_releasing': '手放すもの：', 'winter_keeping': '残すもの：', 'winter_release_placeholder': 'もう必要のないもの...', 'winter_keep_placeholder': '力をくれるもの...', 'winter_enter_light': '光へ入る', 'winter_released': '手放した：', 'winter_kept': '残した：', 'winter_light_returns': 'この夜から、光が戻る。', 'winter_quote': '最も長い夜は教えてくれる。最も深い闇の中でさえ、光はすでに戻り始めていることを。',
    'halloween_title': 'ヴェールの部屋', 'halloween_date': '10月31日', 'halloween_msg1': '今夜、ヴェールは薄い。', 'halloween_msg2': 'あなたの影に何が潜んでいる？', 'halloween_msg3': '持ち歩く恐怖に名前をつけて...', 'halloween_msg4': '形を与えて、解放しよう。', 'halloween_placeholder': '恐怖を入力...', 'halloween_release': '解放する', 'halloween_dissolves': 'あなたの恐怖が夜に溶けていく...', 'halloween_quote': '恐れるものは名前をつけると力を失う。手放そう。',
    'awake_title': 'あなたを起こしているもの', 'awake_locked': 'この部屋は午後10時以降のみ利用可能です。', 'awake_return': '夜が来たら戻ってきて。', 'awake_press_enter': 'Enterを押して', 'awake_named': '今、名前をつけた。', 'awake_quote': '闇は昼に避けているものを知っている。',
    'voice_meaning_title': '意味のないあなたの声', 'voice_meaning_prompt': 'ハミング。呼吸。何でも音を出して。言葉はなしで。', 'voice_meaning_done': 'これがあなたの存在だった。それ以上でもそれ以下でもない。', 'voice_meaning_hold': '押し続ける', 'voice_meaning_recording': '録音中...', 'voice_meaning_no_playback': '再生なし。音はその瞬間にだけ存在した。', 'voice_meaning_quote': 'あなたは存在する。それが唯一の内容。',
    'watching_msg1': 'ここにはあなただけ。', 'watching_msg2': '完全に一人。', 'watching_msg3': '本当に？', 'watching_msg4': '彼らはずっとここにいた。', 'watching_msg5': '彼らはいつも見ている。',
    'shortcut_title': 'いつも使うショートカット', 'shortcut_instruction': '心当たりのあるものを選んで。', 'shortcut_1': '大丈夫じゃないのに「大丈夫」と言う', 'shortcut_2': '今にいる代わりにスマホを見る', 'shortcut_3': '対立を避けるために黙る', 'shortcut_4': '作る代わりに買う', 'shortcut_5': '「正しい時」を待つ', 'shortcut_6': '行動の代わりに状況を責める', 'shortcut_7': '成長より快適さを選ぶ', 'shortcut_8': '寝る代わりにスクロールする', 'shortcut_9': 'ノーと言いたい時にイエスと言う', 'shortcut_10': 'やる代わりに計画する', 'shortcut_pattern': 'あなたのパターン', 'shortcut_selected': '選んだもの：', 'shortcut_show_pattern': 'パターンを表示', 'shortcut_quote': 'ショートカットが道になる。', 'shortcut_pattern_own': 'あなたのパターンはあなたのもの。', 'pattern_avoidance': '不快なものを避ける。', 'pattern_distraction': '気晴らしに逃げる。', 'pattern_convenience': '努力を楽さと交換する。', 'pattern_delay': '来ない条件を待つ。', 'pattern_comfort': '不快さから自分を守る。', 'pattern_pleasing': '他の人を快適にするために消える。',
    'shapes_q1': 'どのくらい平和を感じますか？', 'shapes_q2': '自分をどれくらい信頼していますか？', 'shapes_q3': '他の人とどれくらいつながりを感じますか？', 'shapes_q4': '自分の時間はどれくらい本当に自分のものですか？', 'shapes_q5': '未来はどれくらい重く感じますか？', 'shapes_result_title': '形としてのあなたの人生', 'shapes_no_labels': 'ラベルなし。解釈なし。', 'shapes_yours': '見えるものはあなた自身が理解するもの。', 'shapes_quote': '形は言葉のない意味。', 'shapes_scale': '1 = 全くない, 5 = 完全に',
    'read_title': 'これを声に出して読んで', 'read_stmt1': '何かを避けている。', 'read_stmt2': '怖い。', 'read_stmt3': '何をしているかわからない。', 'read_stmt4': '助けが必要。', 'read_stmt5': '私は十分。', 'read_stmt6': 'ふりをしていた。', 'read_stmt7': '疲れている。', 'read_stmt8': 'もっと欲しい。', 'read_i_read': '声に出して読んだ', 'read_really': '本当に言った？', 'read_skipped': 'スキップした', 'read_result': '真実を声に出して言った。', 'read_quote': '真実を口にすると、それは現実になる。',
    'sentence_title': 'あなたが避ける文章', 'sentence_sub': 'この文を完成させて。完成するまで出られません。', 'sentence_prompt': '...をやめたらもっと幸せなのに', 'sentence_cant_leave': '何か書かないと出られません。', 'sentence_knew': 'もう知っていたはず。', 'sentence_quote': '最初の一歩はそれを言うこと。',
    'say_once_title': '一度だけ言って', 'say_once_prompt': '一文を録音して。再生できません。再録音できません。', 'say_once_done': '言われた。再生なし。やり直しなし。', 'say_once_stop': '停止', 'say_once_record': '録音', 'say_once_quote': '一度だけ言うべきことがある。',
    'newyear_title': '未来の自分への手紙', 'newyear_threshold': '境界', 'newyear_placeholder': '親愛なる未来の私へ...', 'newyear_seal': '手紙を封印する', 'newyear_waiting': 'この手紙はあなたを待っています。', 'newyear_quote': 'すべての終わりは扉。意図を持って踏み出して。',
    'avoid_title': 'あなたが避ける部屋', 'avoid_sub': 'スキップするものが何かを語る。', 'avoid_not_entered': '入っていない：', 'avoid_visited_all': 'すべての無料の部屋を訪れました。', 'avoid_not_pattern': '回避はあなたのパターンではないかもしれない。', 'avoid_quote': '回避も選択。',
    'who_q1': 'お金が心配なければ何をしますか？', 'who_q2': 'パーティーでどう自己紹介しますか？', 'who_q3': '何が生きていると感じさせますか？', 'who_q4': '友達はあなたが何が得意だと言いますか？', 'who_q5': '子供の頃何をするのが好きでしたか？', 'who_result_title': '肩書きのないあなた', 'who_done': '肩書きがなくなった時のあなた。', 'who_quote': 'アイデンティティはあなたがすることではない。やめた時に残るもの。', 'who_placeholder': '正直に...', 'who_see_myself': '自分を見る', 'question': '質問',
    'lastconv_title': '最後の会話', 'lastconv_sub': '決して送らないものを書いて。', 'lastconv_to': '宛先：', 'lastconv_message': 'メッセージ：', 'lastconv_to_placeholder': 'これを聞くべき人...', 'lastconv_msg_placeholder': 'ずっと言いたかったこと...', 'lastconv_not_saved': 'このメッセージは保存も共有もされません。', 'lastconv_seal': '手紙を封印', 'lastconv_wrote_to': '書いた相手：', 'lastconv_never_sent': 'このメッセージは送られません。', 'lastconv_point': 'でも書くことが目的だった。', 'lastconv_quote': '言葉は存在する必要がある。聞かれなくても。',
    'priorities_title': '本当の優先順位', 'priorities_sub': '週に何時間をそれぞれに費やしていますか？', 'priorities_result': '本当の優先順位', 'priorities_most_time': '最も時間を費やしているのは：', 'priorities_truth': '時間は真実を明かす。言葉は嘘をつけるが、スケジュールはつけない。', 'priorities_quote': 'あなたは繰り返すことそのもの。', 'priorities_show': '真実を見せて', 'priority_work': '仕事', 'priority_family': '家族', 'priority_friends': '友達', 'priority_health': '健康', 'priority_learning': '学び', 'priority_entertainment': '娯楽', 'priority_social': 'SNS', 'priority_sleep': '睡眠',
    'voice_intro': '闇の中で声が語る...', 'voice_silence': '（沈黙）', 'voice_quote': '帰り道を見つけられない声もある。',
    'knows_tell': '名前を教えて。', 'knows_placeholder': 'あなたの名前...', 'knows_warning': '（ここに来るべきではなかった）', 'knows_quote': '名前には力がある。あなたの名前はすでに知られていた。',
    'door_msg1': 'ドアの前に何かがいる。', 'door_stopped': '（ノックが止まった）', 'door_quote': '閉じたままにすべきドアもある。',
    'eyes_msg1': '一対の目が見つめている。', 'eyes_unsee': '一度見たら忘れられない。', 'eyes_quote': '気づいたものは、無視できない。',
    'moon_locked': 'この部屋は満月の時だけ開きます。', 'moon_not_full': '今夜は満月ではありません。周期が完了したら戻ってきて。', 'moon_quote': '周期の中でだけ明かされる真実がある。',
    'at317_title': '午前3:17のあなた', 'at317_locked': 'この部屋は午前2時から5時の間に開きます。', 'at317_current': '現在の時刻：', 'at317_quote': '夜はあなたの名前を知っている。',
    'mirrors_you_are_here': 'あなたはここにいる。',
    'listen_title': '自分の声を聴く', 'listen_recording': '録音中...', 'listen_start': '開始', 'listen_play': '再生', 'listen_instruction': '10秒間録音してください。何でも言ってください。', 'listen_wait': '待って。それから聴いて。', 'listen_done': 'それはあなたの声でした。スロー再生。不思議。', 'listen_speed': '0.5倍速で再生中', 'listen_quote': 'あなたの声が違って聞こえるのは、ついに聞いたからです。',
    'export_title': '旅をエクスポート', 'export_subtitle': 'この場所の一部を持ち帰ろう', 'export_tab_export': 'エクスポート', 'export_tab_import': 'インポート', 'export_your_journey': 'あなたの旅', 'export_since': '開始日', 'export_time_spent': '費やした時間', 'export_rooms_visited': '訪問した部屋', 'export_rooms_completed': '完了した部屋', 'export_fragments': 'かけら', 'export_achievements_unlocked': '解除した実績', 'export_truths_recorded': '記録された真実', 'export_text_file': 'テキストファイル', 'export_json_file': 'JSONファイル', 'export_copied': 'コピーしました！', 'export_copy_clipboard': 'クリップボードにコピー', 'export_data_stays': 'あなたのデータはあなたのものです。私たちは保存しません。', 'export_import_desc': '以前エクスポートした旅をインポート', 'export_json_only': 'JSON形式のみ', 'export_select_file': 'ファイルを選択', 'export_merge_note': '現在の旅のデータと統合されます。', 'export_done': 'エクスポート完了。', 'export_done_desc': 'あなたがどこにいたかの記録。何に向き合ったか。', 'export_quote': '記録に値する旅がある。他の人のためではなく。自分のために。', 'export_import_success': '旅のインポートに成功しました', 'export_import_error': '無効な旅ファイル',
    'waitroom_title': '待合室', 'waitroom_subtitle': 'グループにしか開かない扉もある。', 'waitroom_open': '扉が開いた。', 'waitroom_waiting': '待機中', 'waitroom_time_waited': '待った時間', 'waitroom_users_needed': '必要なユーザー', 'waitroom_someone_joined': '誰かが待ちに加わった。', 'waitroom_someone_left': '誰かが去った。', 'waitroom_room_opens': '部屋が開く。', 'waitroom_patience': '忍耐と存在が、力では開けられないものを開く。', 'waitroom_souls_waited': '{count}つの魂が共に待った。', 'waitroom_seconds_stillness': '{count}秒の集団的静寂。', 'waitroom_quote': '急げないものがある。一人では開けられない扉がある。',
    'capsule_title': 'タイムカプセル', 'capsule_subtitle': '未来の自分へのメッセージ', 'capsule_what_tell': '自分に何を伝える必要がありますか？', 'capsule_placeholder': '親愛なる未来の私へ...', 'capsule_when_arrive': 'いつ届くべきですか？', 'capsule_30_days': '30日', 'capsule_90_days': '90日', 'capsule_1_year': '1年', 'capsule_seal': 'カプセルを封印', 'capsule_waiting': 'カプセルが待機中', 'capsule_sealed': '封印されました。', 'capsule_will_wait': 'あなたのメッセージは待っています。', 'capsule_return_in': '{days}日後に戻って開けてください。', 'capsule_quote_sealed': '時間はすべてを変える。あなたさえも。', 'capsule_arrived': 'メッセージが届きました。', 'capsule_from_past': '{days}日前のあなたから。', 'capsule_open': 'カプセルを開ける', 'capsule_you_wrote': 'あなたが自分に書いたこと：', 'capsule_remember': 'これを書いたことを覚えていますか？', 'capsule_quote_opened': 'これを書いた人はもういない。あなたは残っている。',
    'contradiction_title': '矛盾の部屋', 'contradiction_subtitle': 'あなたの真実がぶつかる場所', 'contradiction_not_enough': 'まだデータが足りません。', 'contradiction_answer_more': '部屋でもっと質問に答えてください。', 'contradiction_answers_recorded': '{count}/5の回答が記録済み', 'contradiction_quote_early': '矛盾には複雑さが必要です。まず自分のものを築いてください。', 'contradiction_no_found': '矛盾は見つかりませんでした。', 'contradiction_consistent': 'あなたは驚くほど一貫しているか...', 'contradiction_careful': '...驚くほど慎重です。', 'contradiction_quote_none': '一貫性は知恵か演技のどちらかです。', 'contradiction_found': '{count}個の矛盾が見つかりました', 'contradiction_you_said': 'あなたは言った：', 'contradiction_but_also': 'でもこうも：', 'contradiction_see_another': '別のを見る →', 'contradiction_not_weakness': '矛盾は弱さではない。深さの証明です。', 'contradiction_quote': '私たちは多くのものを内に秘めている。その中には意見が合わないものもある。',
    'entropy_living': '生存', 'entropy_dead': '枯死', 'entropy_plant_seed': '種を植える', 'entropy_instructions': '空いた場所をクリックして植えます。植物をクリックして水をやります。枯れた植物は除去できます。', 'entropy_reflect': '庭園を振り返る', 'entropy_lives': 'あなたの庭園は生きています。', 'entropy_thrive': 'あなたが戻ったから{living}本の植物が繁栄しています。', 'entropy_absence': '{dead}本が不在を思い出させます。',
    'palace_title': '記憶の宮殿', 'palace_subtitle': '精神的な空間を作りましょう。記憶するための物を置きましょう。後で歩きましょう。', 'palace_room_of': '部屋 {current} / {total}', 'palace_click_place': 'クリックして配置', 'palace_what_represent': 'これは何を表していますか？', 'palace_cancel': 'キャンセル', 'palace_place_object': '物を配置', 'palace_objects_count': '宮殿に{count}個の物', 'palace_take_tour': 'ツアーに出発', 'palace_empty_room': 'この部屋は空です', 'palace_previous': '前へ', 'palace_next_room': '次の部屋', 'palace_complete_tour': 'ツアー完了', 'palace_remembers': 'あなたの宮殿は覚えている。', 'palace_geography': 'あなたが構築した意味の地理。', 'palace_quote': '心は宮殿です。その中に何を置くかがあなたを決める。',
    'echoes_subtitle': '他の旅人たちの断片', 'echoes_from': 'から', 'echoes_ago': '前', 'echoes_arriving': 'さらなるエコーが到着中...', 'echoes_reveal': 'これらは彼らの答えではありません。あなたのものです。', 'echoes_reveal2': '（すべてのエコーはかつてあなた自身の反映でした）', 'echoes_quote': '私たちは自分が特別だと思っている。私たちはただお互いのエコーに過ぎない。',
    'revisit_subtitle': 'あなたがどう変わったか', 'revisit_not_enough': 'まだ十分な質問に答えていません。', 'revisit_explore': 'もっと部屋を探索してください。足跡を残してください。後で戻ってきてください。', 'revisit_quote1': '変化には歴史が必要です。歴史には時間が必要です。', 'revisit_none_changed': 'でもまだ何も変わっていません。部屋を再訪してください。もう一度答えてください。', 'revisit_show_evolved': 'あなたがどう進化したかお見せします。', 'revisit_quote2': '変化を見るには、まず変わらなければなりません。', 'revisit_then': 'その時', 'revisit_now': '今', 'revisit_one_shift': 'ひとつの変化。微妙だが確か。', 'revisit_each_change': 'すべての変化は小さな死です。そして小さな誕生です。', 'revisit_quote3': 'あなたはかつてのあなたではない。まだこれからのあなたでもない。',
  },

  // Turkish translations
  tr: {
    'title': 'İNSAN ODALARI',
    'tagline': 'Kimse izlemediğinde kim olduğunuzu yansıtan odalar koleksiyonu.',
    'enter': 'Gir',
    'footer': 'Terapi değil. Sosyal değil. Verimli değil.',
    'back': '← Geri',
    'hallway': 'Koridor',
    'leave_room': '← Odadan çık',
    'return_hallway': 'Koridora dön',
    'loading': 'Yükleniyor...',
    'submit': 'Gönder',
    'cancel': 'İptal',
    'close': 'Kapat',
    'next': 'İleri',
    'previous': 'Önceki',
    'start': 'Başla',
    'stop': 'Dur',
    'yes': 'Evet',
    'no': 'Hayır',
    'complete': 'Tamamla',
    'reveal': 'Göster',
    'continue': 'Devam',
    'begin': 'Başla',
    'return': '← Dön',
    'escape': '← Kaç',
    'run': '← Koş',
    'leave': '← Ayrıl',
    'watch': 'İzle...',
    'correct': 'Doğru',
    'round': 'Tur',
    'enter_again': 'Tekrar gir',
    'steps': 'Adım',
    'depth': 'Derinlik',
    'settings': 'Ayarlar',
    'language': 'Dil',
    'updates': 'Güncellemeler',
    'updates_title': 'Yenilikler',
    'new': 'YENİ',
    'view_all_updates': 'Tüm güncellemeleri gör',
    'whats_new_intro': 'Son güncellemeler:',
    'challenges': 'Meydan Okumalar',
    'random': 'Rastgele',
    'open_rooms': 'Açık Odalar',
    'dark_rooms': 'Karanlık Odalar',
    'enter_risk': 'Riski göze alarak gir',
    'premium_rooms': 'Premium Odalar',
    'unlock_to_access': 'Erişmek için kilidi aç',
    'daily_rooms': 'Günlük Odalar',
    'refreshes_daily': 'Her gün yenilenir',
    'time_locked': 'Zamana kilitli Odalar',
    'seasonal_rooms': 'Mevsimsel Odalar',
    'available_now': 'Şu anda mevcut',
    'fragments': 'Parçalar',
    'boss_unlocked': 'Patron Odası açıldı',
    'collect_unlock': 'Patron odasını açmak için topla',
    'dark_fragments': 'Karanlık Parçalar',
    'dark_boss_unlocked': 'Karanlık Patron açıldı',
    'hidden_discovered': 'Gizli oda keşfedildi',
    'stillness_revealed': 'Sessizliğiniz bir şey ortaya çıkardı...',
    'constellation_map': 'Takımyıldız Haritası',
    'your_stats': 'İstatistikleriniz',
    'completed': 'Tamamlandı',
    'game_rooms': 'Oyun Odaları',
    'experimental_rooms': 'Deneysel Odalar',
    'deep_rooms': 'Derin Odalar',
    'meta_rooms': 'Meta Odalar',
    'search_rooms': 'Oda ara...',
    'no_results': 'Eşleşen oda yok',
    'clear_filters': 'Filtreleri temizle',
    'explore': 'Keşfet',
    'all_categories': 'Tümü',
    'random_room': 'Rastgele Oda',
    'rooms_explored': 'keşfedilen odalar',
    'your_journey': 'Yolculuğunuz',
    'choose_direction': 'Bir yön seçin. Bazı kapılar tepki verir.',
    'achievements': 'Başarılar',
    'achievements_subtitle': 'Yolculuğunuzdaki kilometre taşları',
    'points': 'puan',
    'unlocked': 'açıldı',
    'exploration': 'Keşif',
    'consistency': 'Tutarlılık',
    'discovery': 'Keşif',
    'special': 'Özel',
    'all': 'Tümü',
    'bookmarks': 'Yer İmleri',
    'bookmarked_rooms': 'İşaretlenenler',
    'no_bookmarks': 'Henüz yer imi yok',
    'what_is_this': 'Burası ne?',
    'total_time': 'Toplam Süre',
    'rooms_visited': 'Ziyaret Edilen Odalar',
    'total_visits': 'Toplam Ziyaret',
    'most_visited': 'En Çok Ziyaret',
    'longest_session': 'En Uzun Oturum',
    'fragments_collected': 'Toplanan Parçalar',
    'completed_rooms': 'Tamamlanan Odalar',
    'privacy_note': 'Tüm veriler cihazınızda yerel olarak saklanır',
    'whisper_wall': 'Fısıltı Duvarı',
    'leave_whisper': 'Bir fısıltı bırak...',
    'whisper_sent': 'Fısıltınız duvara akar.',
    'whispers_count': 'fısıltı',
    'anonymous': 'anonim',
    // Room Titles
    'room_how-many-times-left': 'Kaç Kez Kaldı',
    'room_life-in-weeks': 'Haftalık Hayatınız',
    'room_delay-button': 'Erteleme Düğmesi',
    'room_one-honest-sentence': 'Tek Dürüst Cümle',
    'room_this-moment': 'Bu An Bir Daha Gelmeyecek',
    'room_reality-receipt': 'Gerçeklik Fişi',
    'room_nothing-happens': 'Burada Hiçbir Şey Olmuyor',
    'room_default-choice': 'Varsayılan Seçiminiz',
    'room_thought-loop': 'Düşünce Döngüsü',
    'room_expect-vs-happens': 'Beklenti vs Gerçek',
    'room_question-never-answer': 'Hiç Yanıtlamadığınız Soru',
    'room_not-special': 'Özel Değilsiniz (Sorun Yok)',
    'room_shortcut-always-take': 'Hep Aldığınız Kısayol',
    'room_blurred-self': 'Bulanık Benlik',
    'room_image-zooming': 'Yakınlaştırdığınız Görüntü',
    'room_comfort-color': 'Teselli Rengi',
    'room_line-you-crossed': 'Aştığınız Çizgi',
    'room_read-aloud': 'Sesli Oku',
    'room_voice-without-meaning': 'Anlamsız Sesiniz',
    'room_sentence-you-avoid': 'Kaçındığınız Cümle',
    'room_if-nothing-changes': 'Hiçbir Şey Değişmezse',
    'room_real-priorities': 'Gerçek Öncelikleriniz',
    'room_last-conversation': 'Son Konuşma',
    'room_who-without-job': 'İşsiz Kim Olurdunuz?',
    'room_comfort-zone-scanner': 'Konfor Alanı Tarayıcı',
    'room_say-it-once': 'Bir Kere Söyle',
    'room_listen-to-yourself': 'Kendini Dinle',
    'room_life-as-shapes': 'Şekillerle Hayatınız',
    'room_face-without-features': 'Özelliksiz Yüz',
    'room_what-keeps-awake': 'Sizi Uyanık Tutan',
    'room_you-at-317am': 'Sabah 3:17\'de Siz',
    'room_thought-that-returns': 'Dönen Düşünce',
    'room_room-you-visit-most': 'En Çok Ziyaret Ettiğiniz Oda',
    'room_room-you-avoid': 'Kaçındığınız Oda',
    'room_left-too-early': 'Erken Ayrıldınız',
    'room_daily-quote': 'Günün Sözü',
    'room_daily-joke': 'Günün Şakası',
    'room_theyre-watching': 'İzliyorlar',
    'room_the-last-visitor': 'Son Ziyaretçi',
    'room_dont-look-away': 'Bakışını Çevirme',
    'room_the-whisper': 'Fısıltı',
    'room_you-forgot-something': 'Bir Şey Unuttunuz',
    'room_the-mirror-lies': 'Ayna Yalan Söyler',
    'room_something-follows': 'Bir Şey Takip Ediyor',
    'room_the-countdown': 'Geri Sayım',
    'room_eyes-in-the-dark': 'Karanlıktaki Gözler',
    'room_the-voice': 'Ses',
    'room_it-knows-your-name': 'Adınızı Biliyor',
    'room_the-door': 'Kapı',
    'room_boss-room-free': 'Gerçeklerin Toplamı',
    'room_boss-room-horror': 'Korkunun Kalbi',
    'room_boss-room-premium': 'En Derin Gerçek',
    'room_mouse-tracker': 'Fare Takipçisi',
    'room_scroll-confession': 'Kaydırma İtirafı',
    'room_waiting-game': 'Bekleme Oyunu',
    'room_day-in-pixels': 'Piksellerde Gününüz',
    'room_memory-maze': 'Hafıza Labirenti',
    'room_decision-shadow': 'Karar Gölgesi',
    'room_anonymous-mirrors': 'Anonim Aynalar',
    'room_collective-room': 'Kolektif Oda',
    'room_secret-room': 'Gizli Oda',
    'room_glitch-room': 'Kararsız Oda',
    'room_full-moon-room': 'Dolunay Odası',
    'room_weekend-room': 'Hafta Sonu Odası',
    'room_317am-room': 'Sabah 3:17 Odası',
    'room_idle-room': 'Durgun Oda',
    'room_invite-friend-room': 'Paylaşılan Oda',
    'room_halloween-room': 'Perde Odası',
    'room_winter-solstice-room': 'En Uzun Gece',
    'room_new-year-room': 'Gelecekteki Kendinize Mektup',
    'room_memory-puzzle': 'Hafıza Bulmacası',
    'room_shadow-chase': 'Gölge Kovalamaca',
    'room_timing-room': 'Zamanlama Odası',
    'room_pattern-whisper': 'Desen Fısıltısı',
    'room_silence-puzzle': 'Sessizlik Bulmacası',
    'room_decay-game': 'Çürüme Oyunu',
    'room_observation-test': 'Gözlem Testi',
    'room_balance-room': 'Denge Odası',
    'room_word-drift': 'Kelime Sürüklenmesi',
    'room_color-echo': 'Renk Yankısı',
    'room_mirror-tap': 'Ayna Dokunuşu',
    'room_maze-of-doors': 'Kapılar Labirenti',
    'room_pulse-match': 'Nabız Eşleştirme',
    'room_light-path': 'Işık Yolu',
    'room_sound-match': 'Ses Eşleştirme',
    'room_mirror-room': 'Ayna Odası',
    'room_echo-room': 'Yankı Odası',
    'room_pulse-room': 'Nabız Odası',
    'room_vanishing-room': 'Kaybolan Oda',
    'room_letter-room': 'Harf Odası',
    'room_empty-room': 'Boş Oda',
    'room_reverse-room': 'Ters Oda',
    'room_whisper-room': 'Fısıltı Odası',
    'room_shadow-room': 'Gölge Odası',
    'room_labyrinth-room': 'Labirent Odası',
    'room_color-room': 'Renk Odası',
    'room_surrender-room': 'Teslim Odası',
    'room_noise-room': 'Gürültü Odası',
    'room_observer-room': 'Gözlemci Odası',
    'room_imagine-yourself': 'Kendini Hayal Et',
    'room_ai-reflection-room': 'AI Yansıma Odası',
    'room_the-other-side': 'Diğer Taraf',
    'room_the-unraveling': 'Çözülme',
    'room_the-echo': 'Yankı',
    'room_ash-embers': 'Kül ve Korlar',
    'room_twilight-stars': 'Alacakaranlık Yıldızları',
    'room_void-abyss': 'Uçurum',
    'room_eclipse-shadows': 'Tutulma Gölgeleri',
    'room_dawn-awakening': 'Şafak Uyanışı',
    'room_letters-never-sent': 'Gönderilmemiş Mektuplar',
    'room_breath-sync': 'Nefes Senkronizasyonu',
    'room_entropy-garden': 'Entropi Bahçesi',
    'room_memory-palace': 'Hafıza Sarayı',
    'room_morning-ritual': 'Sabah Ritüeli',
    'room_evening-ritual': 'Akşam Ritüeli',
    'room_weekly-ritual': 'Haftalık Ritüel',
    'room_spring-equinox-room': 'İlkbahar Ekinoksu',
    'room_summer-solstice-room': 'Yaz Gündönümü',
    'room_autumn-equinox-room': 'Sonbahar Ekinoksu',
    'room_valentine-room': 'Öz Sevgi Odası',
    'room_fragment-trade': 'Parça Sunağı',
    'room_the-archive': 'Arşiv',
    'room_mirror-of-all-selves': 'Tüm Benliklerin Aynası',
    'room_the-ending': 'Son',
    'room_whisper-wall': 'Fısıltı Duvarı',
    'room_collective-memory': 'Kolektif Hafıza',
    'room_shared-moments': 'Paylaşılan Anlar',
    'room_void-room': 'Boşluk',
    'room_pattern-room': 'Kalıplarınız',
    'room_time-capsule': 'Zaman Kapsülü',
    'room_the-revisit': 'Yeniden Ziyaret',
    'room_contradiction-room': 'Çelişki Odası',
    'room_export-journey': 'Yolculuğu Dışa Aktar',
    'room_anonymous-echoes': 'Anonim Yankılar',
    'room_the-waiting-room': 'Bekleme Odası',
    'room_dream-visualization': 'Rüya Görselleştirme',
    'room_future-self': 'Gelecekteki Ben',
    'room_regret-machine': 'Pişmanlık Makinesi',
    'room_memory-reconstruction': 'Hafıza Yeniden Yapılanması',
    'room_mask-you-wear': 'Taktığınız Maske',
    'room_recurring-dream': 'Tekrarlayan Rüyanız',
    'room_promise-you-broke': 'Bozduğunuz Söz',
    'room_words-you-swallowed': 'Yuttuğunuz Sözler',
    'room_age-you-feel': 'Hissettiğiniz Yaş',
    'room_biggest-lie': 'En Büyük Yalanınız',
    'room_person-you-pretend': 'Taklid Ettiğiniz Kişi',
    'room_person-you-pretend-to-be': 'Taklid Ettiğiniz Kişi',
    'room_what-you-collect': 'Topladıklarınız',
    'room_apology-you-owe': 'Borçlu Olduğunuz Özür',
    'room_unfinished-story': 'Bitmemiş Hikayeniz',
    'room_version-others-see': 'Başkalarının Gördüğü Versiyon',
    'room_secret-talent': 'Gizli Yeteneğiniz',
    'room_fear-you-feed': 'Beslediğiniz Korku',
    'room_guilty-pleasure': 'Suçlu Zevkiniz',
    'room_rule-you-break': 'Hep Kırdığınız Kural',
    'room_childhood-promise': 'Çocukluk Sözünüz',
    'room_compliment-you-reject': 'Reddettiğiniz İltifat',
    'room_autopilot-mode': 'Otomatik Pilot Modu',
    'room_boundary-you-need': 'İhtiyacınız Olan Sınır',
    'room_inner-critic': 'İç Eleştirmeniniz',
    'room_last-sunday': 'Geçen Pazar',
    'room_wasted-hours': 'Boşa Geçen Saatler',
    'room_year-that-changed': 'Sizi Değiştiren Yıl',
    'room_tomorrows-regret': 'Yarının Pişmanlığı',
    'room_morning-autopilot': 'Sabah Otomatik Pilotu',
    'room_season-you-dread': 'Korktuğunuz Mevsim',
    'room_ten-years-ago': 'On Yıl Önce Bugün',
    'room_last-normal-day': 'Son Normal Gün',
    'room_clock-you-ignore': 'Görmezden Geldiğiniz Saat',
    'room_your-deadline': 'Son Tarihiniz',
    'room_the-pause': 'Durak',
    'room_static-room': 'Durağan',
    'room_gap-between-thoughts': 'Düşünceler Arasındaki Boşluk',
    'room_sit-with-discomfort': 'Rahatsızlıkla Otur',
    'room_boring-room': 'Sıkıcı Oda',
    'room_unplug': 'Fişi Çek',
    'room_unplug-room': 'Fişi Çek',
    'room_last-page': 'Son Sayfa',
    'room_scrolling-pattern': 'Kaydırma Düzeniniz',
    'room_tab-you-keep-open': 'Açık Tuttuğunuz Sekme',
    'room_digital-shadow': 'Dijital Gölgeniz',
    'room_notification-you-ignore': 'Görmezden Geldiğiniz Bildirim',
    'room_typing-speed': 'Yazma Hızınız',
    'room_link-you-almost-clicked': 'Tıklamaya Yaklaştığınız Link',
    'room_bookmark-graveyard': 'Yer İmi Mezarlığı',
    'room_search-you-deleted': 'Sildiğiniz Arama',
    'room_gravity-room': 'Yerçekimi Odası',
    'room_ink-blot': 'Mürekkep Lekesi',
    'room_hallway-of-mirrors': 'Aynalar Koridoru',
    'room_dissolving-text': 'Eriyen Metin',
    'room_the-pendulum': 'Sarkaç',
    'room_pixel-rain': 'Piksel Yağmuru',
    'room_the-threshold': 'Eşik',
    'room_the-friend-you-lost': 'Kaybettiğiniz Arkadaş',
    'room_conversation-you-replay': 'Tekrarladığınız Konuşma',
    'room_the-thank-you': 'Teşekkür',
    'room_people-you-forgot': 'Unuttuğunuz İnsanlar',
    'room_the-advice-you-give': 'Verdiğiniz Tavsiye',
    'room_what-others-dont-know': 'Başkalarının Bilmediği',
    'room_the-lie-you-believe': 'İnandığınız Yalan',
    'room_your-shadow-self': 'Gölge Benliğiniz',
    'room_the-story-you-tell': 'Anlattığınız Hikaye',
    'room_your-safe-place': 'Güvenli Yeriniz',
    'room_the-risk-you-didnt-take': 'Almadığınız Risk',
    'room_your-last-excuse': 'Son Bahaneniz',
    'room_the-habit-you-hide': 'Sakladığınız Alışkanlık',
    'room_what-you-postpone': 'Ertelediğiniz Şey',
    'room_the-door-you-closed': 'Kapattığınız Kapı',
    'room_the-emotion-you-hide': 'Sakladığınız Duygu',
    'room_your-comfort-lie': 'Rahatlık Yalanınız',
    'room_the-silence-between': 'Aradaki Sessizlik',
    'room_what-you-carry': 'Taşıdığınız Yük',
    'room_the-version-you-lost': 'Kaybettiğiniz Versiyon',
    'room_letter-to-past-self': 'Geçmişteki Kendinize Mektup',
    'room_the-moment-you-knew': 'Bildiğiniz An',
    'room_the-words-you-rehearse': 'Prova Ettiğiniz Kelimeler',
    'room_someone-thinks-of-you': 'Biri Sizi Düşünüyor',
    'room_the-question-you-fear': 'Korktuğunuz Soru',
    'room_recurring-dream-journal': 'Tekrarlayan Rüya Günlüğü',
    'room_lucid-moment': 'Berrak An',
    'room_nightmare-you-remember': 'Hatırladığınız Kabus',
    'room_dream-you-wish-was-real': 'Gerçek Olmasını Dilediğiniz Rüya',
    'room_the-dream-visitor': 'Rüya Ziyaretçisi',
    'room_the-blank-canvas': 'Boş Tuval',
    'room_song-that-is-you': 'Siz Olan Şarkı',
    'room_color-of-today': 'Bugünün Rengi',
    'room_the-unwritten-poem': 'Yazılmamış Şiir',
    'room_your-inner-voice': 'İç Sesiniz',
    'room_fear-you-named': 'Adlandırdığınız Korku',
    'room_the-leap': 'Sıçrayış',
    'room_courage-inventory': 'Cesaret Envanteri',
    'room_what-scares-you-most': 'En Çok Korkutan',
    'room_facing-the-dark': 'Karanlıkla Yüzleşme',
    'room_letter-to-future-self': 'Gelecekteki Kendinize Mektup',
    'room_the-world-you-want': 'İstediğiniz Dünya',
    'room_your-next-chapter': 'Sonraki Bölümünüz',
    'room_the-wish': 'Dilek',
    'room_if-you-had-one-year': 'Bir Yılınız Olsaydı',
    // Room Subtitles
    'sub_how-many-times-left': 'Kalanların sayımı',
    'sub_life-in-weeks': '4.000 kare. Hepsi bu.',
    'sub_delay-button': 'Her seferinde zorlaşır',
    'sub_one-honest-sentence': 'Düzenleme yok. Silme yok.',
    'sub_this-moment': 'Buradasınız. Bir kez.',
    'sub_reality-receipt': 'Günlerinizin özeti',
    'sub_nothing-happens': 'Tam olarak yazdığı gibi',
    'sub_default-choice': 'Güvenlik veya risk. Her seferinde.',
    'sub_thought-loop': 'Zihninizin kendini tekrarlamasını izleyin',
    'sub_expect-vs-happens': 'Umut ve gerçek arasındaki boşluk',
    'sub_question-never-answer': 'Çoğu kullanıcı ayrılır',
    'sub_not-special': 'Teselli yok. Sadece gerçek.',
    'sub_shortcut-always-take': 'Kalıplar kendini gösterir',
    'sub_blurred-self': 'Hafıza böyle çalışır',
    'sub_image-zooming': 'Hiçlikte cevap aramak',
    'sub_comfort-color': 'Ne kadar kalacaksınız?',
    'sub_line-you-crossed': 'Nerede tereddüt ettiğinizi görüyoruz',
    'sub_read-aloud': 'Söylemeniz gereken kelimeler',
    'sub_voice-without-meaning': 'Kelimesiz ses',
    'sub_sentence-you-avoid': 'Yazana kadar gidemezsiniz',
    'sub_if-nothing-changes': 'Gelecek beş yılın projeksiyonu',
    'sub_real-priorities': 'Kelimelere değil, zamana dayalı',
    'sub_last-conversation': 'Asla göndermeyeceğinizi yazın',
    'sub_who-without-job': 'Kendinizi yeniden yazın',
    'sub_comfort-zone-scanner': 'Tereddüdünüzü görüyoruz',
    'sub_say-it-once': 'Tekrar yok. Yeniden yok.',
    'sub_listen-to-yourself': 'Yavaşlatılmış sesiniz',
    'sub_life-as-shapes': 'Soyut. Etiketsiz. Sizin.',
    'sub_face-without-features': 'Hiçbir şey kalmayana kadar kaldırın',
    'sub_what-keeps-awake': 'Gece 10\'dan sonra mevcut',
    'sub_you-at-317am': 'Zamana özel. Kişisel.',
    'sub_thought-that-returns': 'Her seferinde geri geliyor',
    'sub_room-you-visit-most': 'Kalıbınız ortaya çıktı',
    'sub_room-you-avoid': 'Atladığınız şey bir şey söylüyor',
    'sub_left-too-early': 'Hızlı çıkınca tetiklenir',
    'sub_daily-quote': 'Her gün değişen bilgelik',
    'sub_daily-joke': 'Kara mizah, günlük güncelleme',
    'sub_theyre-watching': 'Yalnız değilsiniz',
    'sub_the-last-visitor': 'Sizden önce biri vardı',
    'sub_dont-look-away': 'Ayrıldığınızda bilir',
    'sub_the-whisper': 'Dikkatlice dinleyin',
    'sub_you-forgot-something': 'Neydi?',
    'sub_the-mirror-lies': 'Gerçeği görmek için kırın',
    'sub_something-follows': 'Koşmaya devam edin',
    'sub_the-countdown': 'Sıfırda bir şey oluyor',
    'sub_eyes-in-the-dark': 'Çoğalıyorlar',
    'sub_the-voice': 'Biri burada mahsur',
    'sub_it-knows-your-name': 'Gelmemeliydınız',
    'sub_the-door': 'Bir şey kapıyı çalıyor',
    'sub_boss-room-free': 'Son ücretsiz oda',
    'sub_boss-room-horror': 'Son karanlıkla yüzleşin',
    'sub_boss-room-premium': 'Son derin oda',
    'sub_mouse-tracker': 'Nasıl hareket ettiğinizi görüyoruz',
    'sub_scroll-confession': 'Ne kadar hızlı geçiyorsunuz?',
    'sub_waiting-game': 'Hiçbir şey yapmadan durabilir misiniz?',
    'sub_day-in-pixels': 'Zaman şekil olur',
    'sub_memory-maze': 'Desen değişir',
    'sub_decision-shadow': 'Seçimleriniz kalıpları ortaya koyar',
    'sub_anonymous-mirrors': 'Yabancıların düşünceleri',
    'sub_collective-room': 'Herkes burada. Kimse kalmıyor.',
    'sub_secret-room': 'Buldunuz',
    'sub_glitch-room': 'Bir şeyler yanlış',
    'sub_full-moon-room': 'Sadece ay dorukta açılır',
    'sub_weekend-room': 'Dinlenme bir varış noktasıdır',
    'sub_317am-room': 'Zamana özel. Kişisel.',
    'sub_idle-room': 'Sabırla bulundu',
    'sub_invite-friend-room': 'Bazı yolculuklar tanıklık edilmelidir',
    'sub_halloween-room': '31 Ekim - Perde ince',
    'sub_winter-solstice-room': '21 Aralık - Bırak ve tut',
    'sub_new-year-room': '31 Aralık - Olacağınız kişiye yazın',
    'sub_memory-puzzle': 'İnce değişen desenleri eşleştirin',
    'sub_shadow-chase': 'Öngörülemez olanı yakalayın',
    'sub_timing-room': 'Tam o anda tıklayın',
    'sub_pattern-whisper': 'Solan diziyi tekrarlayın',
    'sub_silence-puzzle': 'Talimatsız çözün',
    'sub_decay-game': 'Solan şeyleri koruyun',
    'sub_observation-test': 'İnce farkları bulun',
    'sub_balance-room': 'Yüzen nesneleri hizalayın',
    'sub_word-drift': 'Düşmeden harfleri yakalayın',
    'sub_color-echo': 'İnce ton dizisini tekrarlayın',
    'sub_mirror-tap': 'Her dokunuş başka yerde yankılanır',
    'sub_maze-of-doors': 'Her seçimde kapılar yeniden düzenlenir',
    'sub_pulse-match': 'Odanın ritmiyle hareket edin',
    'sub_light-path': 'Değişen karanlıkta ışığı yönlendirin',
    'sub_sound-match': 'Şekilleri yumuşak tonlarla eşleştirin',
    'sub_mirror-room': 'Hafifçe çarpık yansımalar',
    'sub_echo-room': 'Tıklamalar gecikmeli yankı oluşturur',
    'sub_pulse-room': 'Ortam nefes alır',
    'sub_vanishing-room': 'Etkileşimle elementler solar',
    'sub_letter-room': 'Kelimeler hafıza gibi dağılır',
    'sub_empty-room': 'Sırları bulana kadar boş',
    'sub_reverse-room': 'Eylemler tersine davranır',
    'sub_whisper-room': 'Zar zor duyulan gerçekler',
    'sub_shadow-room': 'Karanlık yoldaşınız',
    'sub_labyrinth-room': 'Yolunuzu bulun',
    'sub_color-room': 'Kromatik meditasyon',
    'sub_surrender-room': 'Kontrolü bırakın',
    'sub_noise-room': 'Statik düşünceler',
    'sub_observer-room': 'İzleniyorsunuz',
    'sub_imagine-yourself': 'Kendinizi tanımlayın. Ne ortaya çıkacak görün.',
    'sub_ai-reflection-room': 'AI sizinle düşünsün',
    'sub_the-other-side': 'Karanlıkta yavaşça ilerleyin',
    'sub_the-unraveling': 'Her kelimeyle gerçeklik yıpranır',
    'sub_the-echo': 'Söylediğiniz hiçbir şey gitmez',
    'sub_ash-embers': 'Sadece Kül temasında görünür',
    'sub_twilight-stars': 'Sadece Alacakaranlık temasında görünür',
    'sub_void-abyss': 'Sadece Boşluk temasında görünür',
    'sub_eclipse-shadows': 'Tam karanlığa konuşun',
    'sub_dawn-awakening': 'İlk ışığı karşılayın',
    'sub_letters-never-sent': 'Asla okumayacak birine yazın',
    'sub_breath-sync': 'Odayla birlikte nefes alın',
    'sub_entropy-garden': 'Büyüme ve çürüme iç içe',
    'sub_memory-palace': 'Zihinsel sığınağınızı inşa edin',
    'sub_morning-ritual': 'Niyetle güne başlayın',
    'sub_evening-ritual': 'Düşünceyle günü kapatın',
    'sub_weekly-ritual': 'Pazar günleri geriye bakmak içindir',
    'sub_spring-equinox-room': 'Bırakın ve yenilenin',
    'sub_summer-solstice-room': 'En uzun gün',
    'sub_autumn-equinox-room': 'Hasat edin ve kabul edin',
    'sub_valentine-room': 'Hak ettiğiniz sevgi',
    'sub_fragment-trade': 'Sırları açmak için feda edin',
    'sub_the-archive': 'Eksiksiz yolculuk geçmişiniz',
    'sub_mirror-of-all-selves': 'AI davranış portrenizi gösterir',
    'sub_the-ending': 'Hak ettiğiniz veda',
    'sub_whisper-wall': 'Anonim gerçekler karanlıkta süzülür',
    'sub_collective-memory': 'Hatırladıklarımızın paylaşılan parçaları',
    'sub_shared-moments': 'Dürüstçe, nasılsınız?',
    'sub_void-room': 'Koddaki bir yara',
    'sub_pattern-room': 'Sitenin sizde gördüğü',
    'sub_time-capsule': 'Gelecekteki kendinize yazın',
    'sub_the-revisit': 'Cevaplarınızın nasıl değiştiğini görün',
    'sub_contradiction-room': 'Gerçekleriniz çelişiyor',
    'sub_export-journey': 'Yolunuzu indirin',
    'sub_anonymous-echoes': 'Yabancılardan parçalar',
    'sub_the-waiting-room': 'Varlıkla kilidi açın',
    'sub_dream-visualization': 'Bir rüyayı tarif edin. Gerçekleşmesini izleyin.',
    'sub_future-self': 'Kim olmak istiyorsunuz?',
    'sub_regret-machine': 'Olabilecekleri keşfedin',
    'sub_memory-reconstruction': 'Solan bir anıyı yeniden oluşturun',
    'sub_mask-you-wear': 'Hangi yüzü gösteriyorsunuz?',
    'sub_recurring-dream': 'Hep geri gelen rüya',
    'sub_promise-you-broke': 'Bozulan sözlerin dürüstlüğü',
    'sub_words-you-swallowed': 'Hiç söylemedikleriniz',
    'sub_age-you-feel': 'Gerçekten kaç yaşındasınız?',
    'sub_biggest-lie': 'En çok söylediğiniz yalan',
    'sub_person-you-pretend': 'Performansınız',
    'sub_person-you-pretend-to-be': 'Performansınız',
    'sub_what-you-collect': 'Nesneler kimliği ortaya koyar',
    'sub_apology-you-owe': 'Söylemeniz gerekeni yazın',
    'sub_unfinished-story': 'Hiç tamamlamadığınız şey',
    'sub_version-others-see': 'Başkalarının algısı',
    'sub_secret-talent': 'Kimsenin bilmediği yapabildiğiniz',
    'sub_fear-you-feed': 'Hangi korku kontrol ediyor?',
    'sub_guilty-pleasure': 'Zevk aldığınızı sakladığınız',
    'sub_rule-you-break': 'İsyan kalıbınız',
    'sub_childhood-promise': 'Kendinize verdiğiniz söz',
    'sub_compliment-you-reject': 'Övgüyü neden kabul edemiyorsunuz?',
    'sub_autopilot-mode': 'Düşünmeden yaptığınız',
    'sub_boundary-you-need': 'Korumanız gereken',
    'sub_inner-critic': 'Yargılayan ses',
    'sub_last-sunday': 'Aslında ne yaptınız?',
    'sub_wasted-hours': 'Zamanın kaybolduğu yer',
    'sub_year-that-changed': 'Dönüm noktası',
    'sub_tomorrows-regret': 'Keşke ne yapsaydınız?',
    'sub_morning-autopilot': 'İlk saat ritüeli',
    'sub_season-you-dread': 'Hangi mevsim en ağır?',
    'sub_ten-years-ago': 'O zaman kimdiniz?',
    'sub_last-normal-day': 'Her şey değişmeden önce',
    'sub_clock-you-ignore': 'Geçmiyormuş gibi davrandığınız zaman',
    'sub_your-deadline': 'Neyi erteliyorsunuz?',
    'sub_the-pause': 'Bir an nefes alın',
    'sub_static-room': 'Beyaz gürültü ve durağanlık',
    'sub_gap-between-thoughts': 'Boşluğu bulun',
    'sub_sit-with-discomfort': 'Hiçbir şeyi düzeltmeyin',
    'sub_boring-room': 'İlginç hiçbir şey yok',
    'sub_unplug': 'Cihaz olmadan hayal edin',
    'sub_unplug-room': 'Cihaz olmadan hayal edin',
    'sub_last-page': 'Hikaye burada bitiyor',
    'sub_scrolling-pattern': 'İçeriği nasıl tüketiyorsunuz',
    'sub_tab-you-keep-open': 'Tarayıcınızda kalan',
    'sub_digital-shadow': 'Çevrimiçi ayak iziniz',
    'sub_notification-you-ignore': 'Görmezden geldiğiniz',
    'sub_typing-speed': 'Ne kadar hızlı ifade ediyorsunuz?',
    'sub_link-you-almost-clicked': 'Tereddüt kalıpları',
    'sub_bookmark-graveyard': 'Kaydedilmiş ama dönülmemiş',
    'sub_search-you-deleted': 'Aradığınız ve sildiğiniz',
    'sub_gravity-room': 'Her şey yavaşça düşer',
    'sub_ink-blot': 'Ne görüyorsunuz?',
    'sub_hallway-of-mirrors': 'Sonsuz yansımalar',
    'sub_dissolving-text': 'Okudukça kelimeler erir',
    'sub_the-pendulum': 'Hipnotik hareket',
    'sub_pixel-rain': 'Dijital yağış',
    'sub_the-threshold': 'Burada ve orada arasında',
    'sub_the-friend-you-lost': 'Uzaklaştığınız birine yazın',
    'sub_conversation-you-replay': 'Hafızadan yeniden oluşturun',
    'sub_the-thank-you': 'İfade etmediğiniz şükran',
    'sub_people-you-forgot': 'Unutulan isimleri hatırlamaya çalışın',
    'sub_the-advice-you-give': 'Verip ama uymadiğınız tavsiye',
    'sub_what-others-dont-know': 'Gizli katmanlarınız',
    'sub_the-lie-you-believe': 'Rahatlatıcı bir yalan',
    'sub_your-shadow-self': 'Bastırdığınız kısım',
    'sub_the-story-you-tell': 'Hayatınızı nasıl anlatıyorsunuz',
    'sub_your-safe-place': 'Güvende hissettiğiniz yeri tarif edin',
    'sub_the-risk-you-didnt-take': 'Yürünmemiş yollar',
    'sub_your-last-excuse': 'En çok kullandığınız bahane',
    'sub_the-habit-you-hide': 'Gizli rutinler',
    'sub_what-you-postpone': 'Hep "yarın"a kalan şey',
    'sub_the-door-you-closed': 'Reddettiğiniz fırsatlar',
    'sub_the-emotion-you-hide': 'Bir kez adlandırın.',
    'sub_your-comfort-lie': 'Rahat tutan küçük yalan.',
    'sub_the-silence-between': 'İki düşünce arasında oturun.',
    'sub_what-you-carry': 'Görünmez yüke ad verin.',
    'sub_the-version-you-lost': 'Hayat olmadan önceki siz.',
    'sub_letter-to-past-self': 'Eskiden olduğunuz kişiye yazın.',
    'sub_the-moment-you-knew': 'Her şeyin değiştiği an.',
    'sub_the-words-you-rehearse': 'Prova edip ama söylemediğiniz.',
    'sub_someone-thinks-of-you': 'Şu an, bir yerde, biri sizi hatırlıyor.',
    'sub_the-question-you-fear': 'Kimsenin sormamasını umduğunuz soru.',
    'sub_recurring-dream-journal': 'Hep geri gelen rüya.',
    'sub_lucid-moment': 'Rüyada farkına varın.',
    'sub_nightmare-you-remember': 'Hâlâ rahatsız eden.',
    'sub_dream-you-wish-was-real': 'Uyanın. Gerçek değildi.',
    'sub_the-dream-visitor': 'Biri rüyalarınızda belirmeye devam ediyor.',
    'sub_the-blank-canvas': 'Hiçlikten bir şey yaratın.',
    'sub_song-that-is-you': 'Bir şarkı olsaydınız, hangisi?',
    'sub_color-of-today': 'Şu anki halinizin rengini seçin.',
    'sub_the-unwritten-poem': 'İçinizde tuttuğunuzu yazın.',
    'sub_your-inner-voice': 'Aslında ne diyor?',
    'sub_fear-you-named': 'Adlandırın ve gücünü kaybetsin.',
    'sub_the-leap': 'Neye atlardınız?',
    'sub_courage-inventory': 'Yaptığınız her cesur şeyin listesi.',
    'sub_what-scares-you-most': 'En derin korku.',
    'sub_facing-the-dark': 'Tam karanlıkta oturun.',
    'sub_letter-to-future-self': 'Olacağınız kişiye yazın.',
    'sub_the-world-you-want': 'Var olmasını dilediğiniz dünyayı tarif edin.',
    'sub_your-next-chapter': 'Hikayenizde sırada ne var?',
    'sub_the-wish': 'Bir dilek. Kural yok.',
    'sub_if-you-had-one-year': '365 günle ne yapardınız?',
    // v4.0 extras
    'dream_journal_placeholder': 'Hep gördüğüm rüya...',
    'record_dream': 'Kaydet',
    'dream_journal_reflection': 'Rüyalar uyanık zihnin söylemediğini söyler.',
    'quote_dream_journal': 'Tekrarlayan rüyalar kendinle bitmemiş konuşmalardır.',
    'become_aware': 'Farkındayım',
    'lucid_aware': 'Bu farkındalık anı tek gerçek olan şey.',
    'quote_lucid_moment': 'Gerçekliği sorguladığınız an, onu gerçekten gördüğünüz andır.',
    'wish_placeholder': 'Dileğim...',
    'make_wish': 'Dilek tut',
    'quote_wish': 'Dilediğiniz şey en çok neye değer verdiğinizi ortaya koyar.',
    'update_v4_languages': '4 yeni dil: Hintçe, Korece, Japonca, Türkçe',
    'update_v4_20_rooms': '20 yeni oda: Rüyalar, Yaratıcılık, Korku ve Gelecek',
    'update_v4_dreams_category': 'Rüyalar ve Bilinçaltı oda kategorisi',
    'update_v4_creativity_category': 'Yaratıcılık ve İfade oda kategorisi',
    'update_v4_fear_category': 'Korku ve Cesaret oda kategorisi',
    'update_v4_future_category': 'Gelecek ve Umut oda kategorisi',
    'room_playlists': 'Oda Çalma Listeleri',
    'personal_dashboard': 'Gösterge Paneli',
    'dashboard_subtitle': 'Yolculuğunuz bir bakışta',
    'visit_pattern': 'Ziyaret Düzeni (14 gün)',
    'dashboard_top_rooms': 'En Çok Ziyaret Edilen Odalar',
    'category_breakdown': 'Oda Kategorileri',
    'mood_trend': 'Ruh Hali Eğilimi',
    'fragment_breakdown': 'Parça Dağılımı',
    'no_data_yet': 'Henüz veri yok',
    'current_streak': 'Gün Serisi',
    'playlist_morning': 'Sabah Düşüncesi',
    'playlist_morning_desc': 'Gününüze berraklık ve mevcudiyetle başlayın',
    'playlist_identity': 'Kimlik Derinliği',
    'playlist_identity_desc': 'Etiketlerin altında kim var?',
    'playlist_creative': 'Yaratıcı Kıvılcım',
    'playlist_creative_desc': 'Yaratıcı ifadenizi açın',
    'playlist_courage': 'Cesaret Yolu',
    'playlist_courage_desc': 'Korkularınızla oda oda yüzleşin',
    'playlist_dreams': 'Rüya Günlüğü',
    'playlist_dreams_desc': 'Bilinçaltınızın manzaralarını keşfedin',
    'playlist_future': 'Gelecek Vizyonu',
    'playlist_future_desc': 'Gelecekteki sizin için mektuplar ve dilekler',
    'playlist_truth': 'Gerçekle Yüzleşme',
    'playlist_truth_desc': 'Rahatsız edici gerçeklerle yüzleşin',
    'playlist_night': 'Gece Yolculuğu',
    'playlist_night_desc': 'Sessizliği, boşluğu ve hiçliği kucaklayın',
    // v4.2.0
    'room_focus-timer': 'Odak Zamanlayıcı', 'sub_focus-timer': 'Sessizce oturun. Düşünün.', 'room_gratitude-jar': 'Şükran Kavanozu', 'sub_gratitude-jar': 'Minnettar olduğunuz bir şey', 'room_mood-compass': 'Ruh Hali Pusulası', 'sub_mood-compass': 'Şu an neredesiniz?', 'room_memory-fade': 'Hafıza Solması', 'sub_memory-fade': 'Bir anının erimesini izleyin', 'room_sound-of-your-day': 'Gününüzün Sesi', 'sub_sound-of-your-day': 'Bugün nasıl duyuluyor?', 'room_the-apology-room': 'Özür Odası', 'sub_the-apology-room': 'Hiç söylemediğinizi yazın', 'room_your-unread-messages': 'Okunmamış Mesajlarınız', 'sub_your-unread-messages': 'Hiç açmadığınız mesajlar', 'room_the-weight-room': 'Ağırlık Odası', 'sub_the-weight-room': 'Sizi ağırlaştırana ad verin', 'room_parallel-lives': 'Paralel Hayatlar', 'sub_parallel-lives': 'Başka bir hayatta, siz...', 'room_the-quiet-score': 'Sessizlik Skoru', 'sub_the-quiet-score': 'Ne kadar hareketsiz kalabilirsiniz?',
    'focus_choose': 'Ne kadar sessiz oturmak istiyorsunuz?', 'focus_sit': 'Oturun. Nefes alın. Düşünceleri bırakın.', 'focus_done': 'Sessizlik konuştu.', 'quote_focus_timer': 'Durgunlukta, gürültünün bastırdığını duyarsınız.', 'minutes': 'dk',
    'gratitude_prompt': 'Bugün minnettar olduğunuz bir şey yazın.', 'gratitude_placeholder': 'Minnettar olduğum...', 'gratitude_add': 'Kavanoza ekle', 'gratitude_added': 'Şükranınız kavanozda süzülüyor.', 'gratitude_count': 'şükran anı', 'quote_gratitude_jar': 'Şükran daha fazlasına sahip olmak değil. Zaten burada olanı fark etmektir.',
    'mood_compass_prompt': 'Kendinizi hissettiğiniz yere tıklayın.', 'mood_high_energy': 'Yüksek Enerji', 'mood_low_energy': 'Düşük Enerji', 'mood_positive': 'Pozitif', 'mood_negative': 'Negatif', 'quote_mood_compass': 'Nerede olduğunuzu bilmek, nereye gideceğinizi bilmenin ilk adımıdır.',
    'memory_fade_prompt': 'Bir anı yazın. Erimesini izleyin.', 'memory_fade_placeholder': 'Bırakmak istediğiniz bir anı...', 'memory_fade_start': 'Solmasına izin ver', 'memory_fade_done': 'Artık gitti. Ama gerçekti.', 'quote_memory_fade': 'Anılar izlesek de izlemesek de erir.',
    'sound_day_prompt': 'Gününüzü temsil eden 3-5 ses seçin.', 'sound_rain': 'Yağmur', 'sound_traffic': 'Trafik', 'sound_birds': 'Kuşlar', 'sound_keyboard': 'Klavye', 'sound_silence': 'Sessizlik', 'sound_wind': 'Rüzgar', 'sound_ocean': 'Okyanus', 'sound_crowd': 'Kalabalık', 'sound_music': 'Müzik', 'sound_day_confirm': 'Bu benim günüm', 'sound_day_result': 'Gününüzün film müziği.', 'quote_sound_day': 'Her günün bir sesi var. Çoğumuz dinlemeyi unutuyoruz.',
    'apology_prompt': 'Hiç iletmediğiniz bir özür yazın.', 'apology_placeholder': 'Özür dilerim...', 'apology_release': 'Bırakın', 'apology_done': 'Hiçliğe sıkıştı. Ama söylediniz.', 'quote_apology': 'Bazı özürler onlar için değildir. Sizin içindir.',
    'unread_from_past': 'Gönderen: Geçmiş Benliğiniz', 'unread_from_future': 'Gönderen: Gelecek Benliğiniz', 'unread_from_critic': 'Gönderen: İç Eleştirmeniniz', 'unread_msg_past': 'Her şeyin farklı olacağına söz verdiğiniz zamanı hatırlıyor musunuz?', 'unread_msg_future': 'Umarım şimdi kendinize eskisinden daha naziksiniz.', 'unread_msg_critic': 'Daha fazlasını yapabileceğinizi biliyorsunuz. Ama yapacak mısınız?', 'unread_count': 'okunmamış', 'unread_reply_placeholder': 'Yanıtınız...', 'unread_reply': 'Gönder', 'unread_sent': 'Mesaj boşluğa gönderildi.', 'quote_unread_messages': 'Kaçındığımız mesajlar genellikle en çok ihtiyaç duyduklarımızdır.',
    'weight_prompt': 'Sizi ağırlaştıranları adlandırın.', 'weight_placeholder': 'Beni ağırlaştıran...', 'weight_release_start': 'Bırakmaya başla', 'weight_release_prompt': 'Her ağırlığa tıklayarak bırakın.', 'weight_done': 'Ağırlık kalktı. Şimdilik.', 'quote_weight_room': 'Adlandırmayı reddettiğinizi bırakamazsınız.',
    'parallel_intro': 'Başka bir hayatta, siz...', 'parallel_life_1': 'Başka bir hayatta, uzak bir adada deniz feneri bekçisisiniz.', 'parallel_life_2': 'Başka bir hayatta, memleketinizi hiç terk etmediniz.', 'parallel_life_3': 'Başka bir hayatta, hayır dediğinizde evet dediniz.', 'parallel_life_4': 'Başka bir hayatta, tamamen başka birisiniz.', 'parallel_life_5': 'Başka bir hayatta, tam olarak şu anki sizsiniz.', 'parallel_life_6': 'Başka bir hayatta, daha zor yolu seçtiniz.', 'parallel_life_7': 'Başka bir hayatta, hâlâ bu odayı arıyorsunuz.', 'quote_parallel_lives': 'Her seçim, farklı seçtiğiniz bir evren yaratır.',
    'quiet_instruction': 'Etkileşime girmeyin. Sadece hareketsiz kalın.', 'quiet_reveal_1': 'Buradasınız.', 'quiet_reveal_2': 'Gürültü azalıyor.', 'quiet_reveal_3': 'Sessizlikten bir şey beliriyor.', 'quiet_reveal_4': 'Düşündüğünüzden daha sabırlısınız.', 'quiet_reveal_5': 'Durgunluk adınızı biliyor.', 'quiet_score_label': 'Sessizlik skorunuz', 'quiet_impressive': 'Olağanüstü durgunluk.', 'quiet_good': 'Biraz sessizlik buldunuz.', 'quiet_try_again': 'Harekete geçme dürtüsü güçlü.', 'quote_quiet_score': 'Durgunluk hareketin yokluğu değildir. Farkındalığın varlığıdır.',
    'update_v42_10_rooms': '10 yeni interaktif oda: Odak Zamanlayıcı, Şükran Kavanozu, Ruh Hali Pusulası ve daha fazlası', 'update_v42_focus_timer': 'Odak Zamanlayıcı — karararak ilerleyen zamanlayıcıda sessizce oturun', 'update_v42_gratitude_jar': 'Şükran Kavanozu — zamanla şükran anları toplayın', 'update_v42_mood_compass': 'Ruh Hali Pusulası — enerji ve duygu eksenlerinde kendinizi konumlandırın', 'update_v42_weight_room': 'Ağırlık Odası — sizi ağırlaştırana ad verin ve bırakın',
    'update_v43_full_localization': 'Tam yerelleştirme — tüm odalar artık tamamen çevrildi', 'update_v43_40_rooms_translated': '40+ oda çevrildi — her interaktif oda tüm dilleri destekliyor', 'update_v43_10_languages': '10 dil destekleniyor — İngilizce, Arapça, İspanyolca, Portekizce, Çince, Fransızca, Hintçe, Korece, Japonca, Türkçe', 'update_v43_nav_consistency': 'Tutarlı navigasyon — tüm odalarda birleşik çıkış metni',
    // Daily Quote Room
    'quote_day_number': 'Gün {day}',
    'quote_come_back': 'Yarın yeni bir alıntı için geri gelin.',
    // Leaderboard
    'leaderboard': 'Topluluk Sıralaması',
    'leaderboard_subtitle': 'anonim · kolektif · rekabet yok',
    'collective_time': 'kolektif süre',
    'achievements_count': 'başarılar',
    'most_visited_rooms': 'En Çok Ziyaret Edilen Odalar',
    'most_time_spent': 'En Çok Zaman Harcanan',
    'most_unlocked_achievements': 'En Çok Açılan Başarılar',
    'no_achievements_yet': 'Henüz açılmış başarı yok.',
    // Transfer Data
    'transfer_data': 'Veri Aktarımı',
    'transfer_data_desc': 'Tek kullanımlık kod ile ilerlemenizi başka bir cihaza taşıyın.',
    'generate_transfer_code': 'Aktarım kodu oluştur',
    'enter_transfer_code': 'Kodu girin...',
    'transfer_code_expires': 'Kod 24 saat içinde sona erer ve yalnızca bir kez kullanılabilir.',
    'generating': 'Oluşturuluyor...',
    'import': 'İçe aktar',
    'copied': 'Kopyalandı!',
    
    // Waiting Game Room
    'waiting_instruction': 'Hareket etmeyin. Tıklamayın. Sadece bekleyin.',
    'waiting_slow': 'Yavaşlamak göründüğünden daha zor.',
    'waiting_unfamiliar': 'Bu durgunluk tanıdık gelmiyor, değil mi?',
    'waiting_most': 'Çoğu insan şimdiye kadar gider.',
    'waiting_learning': 'Kendiniz hakkında bir şeyler öğreniyorsunuz.',
    'waiting_rewards': 'Sabrın kendi ödülleri vardır.',
    'waiting_seconds': 'saniye durgunluk',
    'waiting_waiting': 'bekleniyor...',
    'waiting_moved': '{count} kez hareket ettiniz.',
    'waiting_waited': 'Beklediniz. Bu nadir bir şey.',
    'waiting_most_cant': 'Çoğu insan hiçbir şey yapmadan oturamaz. Siz yaptınız.',
    'waiting_quote': 'Şu an, bizim için mevcut olan tek andır ve tüm anlara açılan kapıdır.',
    // Feature Navigation
    'shared_room': 'Paylaşılan Oda',
    'imagine_room': 'Hayal Et',
    'dream_room': 'Rüyalar',
    'future_room': 'Gelecek',
    'ai_reflection': 'AI Yansıma',
    // Imagine Yourself Room
    'imagine_title': 'Kendini Hayal Et',
    'imagine_subtitle': 'Kendinizi nasıl gördüğünüzü tarif edin. Kelimelerinizden ne çıktığını görün.',
    'imagine_placeholder': 'Kendimi şöyle biri olarak görüyorum...\n\nÖzünüzü, doğanızı, içten nasıl hissettiğinizi tarif edin.',
    'imagine_hint': 'Dürüst olun. Görüntü kelimelerinizi yansıtır.',
    'imagine_generate': 'Kendini Gör',
    'imagine_generating': 'Oluşturuluyor...',
    'imagine_try_again': 'Tekrar Tarif Et',
    'imagine_result_alt': 'Otoportreniz',
    'imagine_quote': 'Kendimizi net görmeyiz. İnandığımızı görürüz.',
    'imagine_empty_error': 'Önce kendiniz hakkında bir şeyler yazın',
    'imagine_short_error': 'Kendiniz hakkında daha fazla bilgi verin',
    'rate_limit_error': 'Çok fazla istek. Lütfen biraz bekleyin.',
    'credits_error': 'Hizmet geçici olarak kullanılamıyor',
    'generation_error': 'Görüntü oluşturulamadı. Tekrar deneyin.',
    // Dream Visualization Room
    'dream_title': 'Rüya Görselleştirme',
    'dream_subtitle': 'Tekrarlayan bir rüyanızı tarif edin. Gerçekleşmesini izleyin.',
    'dream_placeholder': 'Hep şöyle rüya görürüm...\n\nGörüntüleri, duyguları, tekrarlayan unsurları tarif edin.',
    'dream_hint': 'Rüyalar sembollerle konuşur.',
    'dream_generate': 'Rüyayı Görselleştir',
    'dream_generating': 'Rüya görülüyor...',
    'dream_try_again': 'Tekrar Rüya Gör',
    'dream_result_alt': 'Rüya görselleştirmeniz',
    'dream_quote': 'Rüyalar bilinçaltının bize bir şey söyleme çabasıdır.',
    'dream_empty_error': 'Önce rüyanızı tarif edin',
    'dream_short_error': 'Rüyanız hakkında daha fazla bilgi verin',
    // Future Self Room
    'future_title': 'Gelecekteki Ben',
    'future_subtitle': 'Olmak istediğiniz kişiyi tarif edin. Potansiyelinizi görün.',
    'future_placeholder': 'Gelecekte kendimi şöyle görüyorum...\n\nOlmak için çalıştığınız kişiyi tarif edin.',
    'future_hint': 'Özlem gerçeği şekillendirir.',
    'future_generate': 'Geleceğini Gör',
    'future_generating': 'Tasavvur ediliyor...',
    'future_try_again': 'Tekrar Tasavvur Et',
    'future_result_alt': 'Gelecekteki siz',
    'future_quote': 'Gelecek girdiğimiz bir yer değil. Yarattığımız bir şey.',
    'future_empty_error': 'Önce gelecekteki kendinizi tarif edin',
    'future_short_error': 'Gelecekteki kendiniz hakkında daha fazla bilgi verin',
    // Guided Journeys
    'guided_journeys_title': 'Rehberli Yolculuklar',
    'guided_journeys_subtitle': 'Anlatı bağlantılı küratörlü oda dizileri. Rehberli meditasyon çalma listesi gibi.',
    'journey_complete': 'Yolculuk Tamamlandı',
    'journey_complete_desc': 'Tüm yolu yürüdünüz. Odalar geçişinizi hatırlıyor.',
    'more_journeys': 'Daha Fazla Yolculuk',
    'next_room': 'Sonraki Oda',
    'enter_room': 'Odaya Gir',
    'finish_journey': 'Yolculuğu Bitir',
    'step': 'Adım',
    // Fragment Collection
    'fragment_collection_title': 'Parça Koleksiyonu',
    'fragment_collection_subtitle': 'Her oda arkasında bir şey bırakır. Hepsini toplayın.',
    'collection_milestones': 'Kilometre Taşları',
    'collection_secrets': 'Sırlar',
    'collection_footer': 'Bazı parçalar yalnızca belirli koşullarda görünür.',
    'rarity_common': 'Yaygın',
    'rarity_rare': 'Nadir',
    'rarity_legendary': 'Efsanevi',
    // AI Companion
    'companion_create': 'Yoldaşınızı Oluşturun',
    'companion_setup_desc': 'Yanınızda yürüyen bir yansıma.',
    'companion_name': 'Yoldaşınıza isim verin',
    'companion_name_placeholder': 'Örn: Yankı, Ayna, Gölge...',
    'companion_lens': 'Kişilik lensi',
    'companion_mood': 'İletişim havası',
    'companion_fears': 'Nelerden korkuyorsunuz? (isteğe bağlı)',
    'companion_struggles': 'Nelerde zorlanıyorsunuz? (isteğe bağlı)',
    'create_companion': 'Oluştur',
    'companion_input': 'Bir şey söyleyin...',
    'companion_greeting': 'Yoldaşınız burada. Bir şey söyleyin.',
    'companion_starter_1': 'Bugün aklımda ne var?',
    'companion_starter_2': 'Tıkanmış hissediyorum.',
    'companion_starter_3': 'Kaçındığım şeyi söyle.',
    'fears_placeholder': 'Örn: başarısızlık, reddedilme, unutulmak...',
    'struggles_placeholder': 'Örn: aşırı düşünme, erteleme, yalnızlık...',
    'back_step': 'Geri',
    'lens_reflective': 'Düşünceli',
    'lens_reflective_desc': 'Derin sorular, kalıp tanıma',
    'lens_supportive': 'Destekleyici',
    'lens_supportive_desc': 'Sıcak, cesaretlendirici, onaylayıcı',
    'lens_challenging': 'Zorlayıcı',
    'lens_challenging_desc': 'Doğrudan, düşündürücü',
    'lens_curious': 'Meraklı',
    'lens_curious_desc': 'Beklenmedik sorular, merak',
    'lens_shadow': 'Gölge',
    'lens_shadow_desc': 'Gizli taraf, dürüst rahatsızlık',
    'companion_mood_calm': 'Sakin',
    'companion_mood_calm_desc': 'Ölçülü ve huzurlu',
    'companion_mood_intense': 'Yoğun',
    'companion_mood_intense_desc': 'Acil ve derin',
    'companion_mood_playful': 'Eğlenceli',
    'companion_mood_playful_desc': 'Esprili ve hafif',
    'companion_mood_melancholic': 'Melankolik',
    'companion_mood_melancholic_desc': 'Güzel hüzün',
    // AI Reflection Room
    'reflection_room_title': 'Yansıma Odası',
    'reflection_room_desc': 'Dürüst bir şeyler yazın. Ayna göremediğinizi gösterecek.',
    'reflection_room_privacy': 'Hiçbir şey kaydedilmez. Hiçbir şey paylaşılmaz.',
    'reflection_begin': 'başla',
    'reflection_prompt': 'Aklınızda ne var? Özgürce yazın.',
    'reflection_placeholder': 'Buradan başlayın...',
    'reflection_characters': 'karakter',
    'reflection_min_chars': 'en az 20 gerekli',
    'reflection_send': 'aynaya gönder',
    'reflection_waiting': 'Ayna düşünüyor...',
    'reflection_fragment_label': 'parça',
    'reflection_label': 'yansıma',
    'reflection_question_label': 'kaçınabileceğiniz soru',
    'reflection_leave': 'aynayı terk et',
    'reflection_error': 'Ayna buğulandı',
    'reflection_default_quote': 'Ayna hatırlar',
    // Room Content - Image Zooming
    'image_zoom_title': 'Yakınlaştırmaya Devam Ettiğiniz Görüntü',
    'image_zoom_zoom': 'Yakınlaştırma',
    'image_zoom_quote': 'Bulunacak bir şey daha yok. Hiç olmadı.',
    // Room Content - Shadow Chase
    'shadow_chase_title': 'Gölge Kovalamaca',
    'shadow_chase_subtitle': 'gölgeyi yakala • öngörülemez tepki verir',
    'shadow_chase_catches': 'yakalama',
    'shadow_chase_behavior': 'davranış',
    'shadow_chase_flee': 'kaçış',
    'shadow_chase_wander': 'dolaşma',
    'shadow_chase_mirror': 'ayna',
    'shadow_chase_msg_1': 'Gölge nereye gittiğini biliyor.',
    'shadow_chase_msg_2': 'Kovaladığın şey genellikle kaçar.',
    'shadow_chase_msg_3': 'Bazen durgunluk hızdan daha çok yakalar.',
    'shadow_chase_msg_4': 'Gölgen her hareketini hatırlıyor.',
    'shadow_chase_msg_5': 'Senden öğreniyor.',
    // Room Content - Left Too Early
    'left_early_title': 'Çok Erken Ayrıldınız',
    'left_early_subtitle': '5 saniyeden kısa sürede çıktığınız odalar.',
    'left_early_none': 'Hiçbir odadan çok hızlı ayrılmadınız.',
    'left_early_patience': 'Belki de şeylere zaman veriyorsunuz.',
    'left_early_quote': 'Bazen oda bize bir şey gösteremeden ayrılırız.',
    // Room Content - Face Without Features
    'face_title': 'Özellikleri Olmayan Yüz',
    'face_instruction': 'Kaldırmak için bir özelliğe tıklayın.',
    'face_nothing': 'Hiçbir şey kalmadı.',
    'face_remove': 'Kaldır',
    'face_remaining': 'kalan',
    'face_eyes': 'Gözler',
    'face_nose': 'Burun',
    'face_mouth': 'Ağız',
    'face_eyebrows': 'Kaşlar',
    'face_outline': 'Yüz',
    'face_quote': 'Kimlik sadece tanımayı kabul ettiğimiz özelliklerdir.',
    // Room Content - The Last Visitor
    'last_visitor_title': 'Son Ziyaretçi',
    'last_visitor_someone': 'Birisi buradaydı',
    'last_visitor_first': 'İlk ziyaretçi sizsiniz.',
    'last_visitor_no_one': 'Sizden önce kimse gelmedi.',
    'last_visitor_next': 'Sonraki kişi zaman damganızı görecek.',
    'last_visitor_know': 'Burada olduğunuzu bilecekler.',
    'last_visitor_quote': 'Karanlıkta bile izler bırakırız.',
    'last_visitor_days_ago': '{count} gün önce',
    'last_visitor_hours_ago': '{count} saat önce',
    'last_visitor_minutes_ago': '{count} dakika önce',
    'last_visitor_moments_ago': 'az önce',
    // Room Content - Room You Visit Most
    'visit_most_title': 'En Çok Ziyaret Ettiğiniz Oda',
    'visit_most_subtitle': 'Davranışınız kalıplarınızı ortaya koyar.',
    'visit_most_visits': '{count} ziyaret',
    'visit_most_none': 'Henüz kalıp yok. Keşfetmeye devam edin.',
    'visit_most_quote': 'İstediğimiz yere değil, ihtiyaç duyduğumuz yere döneriz.',
    // Room Content - 3:17 AM Room
    'room_317_locked': 'Bu oda sadece sabah 3:17\'de açılır.',
    'room_317_opens_in': '{hours}s {minutes}dk sonra açılır',
    'room_317_msg_1': 'Saat sabah 3:17.',
    'room_317_msg_2': 'Uyanıksınız.',
    'room_317_msg_3': 'Neden?',
    'room_317_msg_4': 'Dünya uyuyor. Ama siz değil.',
    'room_317_msg_5': 'Hangi düşünce sizi bırakmıyor?',
    'room_317_msg_6': 'Bu saatte maskeler düşer.',
    'room_317_msg_7': 'Sadece siz ve sessizlik var.',
    'room_317_msg_8': 'Bu an sadece şimdi var.',
    'room_317_quote': 'Bu saatte maskeler düşer.',
    // Room Content - Weekend Room
    'weekend_locked': 'Bu oda sadece hafta sonları açıktır.',
    'weekend_days_until': 'Bu odanın açılmasına {count} gün.',
    'weekend_label': 'Hafta Sonu Odası',
    'weekend_prompt': 'Hafta bitti. Bu iki güne ne taşıdınız?',
    'weekend_placeholder': 'Bir ağırlık, bir umut, bir soru...',
    'weekend_release': 'Bırakın',
    'weekend_wrote': 'Yazdığınız:',
    'weekend_letting_go': 'Hafta sonu bırakmak içindir.',
    // Room Content - Something Follows
    'follows_title': 'Bir Şey Takip Ediyor',
    'follows_distance': 'Mesafe',
    'follows_run': 'Koş',
    'follows_keep_running': 'Koşmaya devam et. Asla durmuyor.',
    'follows_caught': 'Sizi yakaladı.',
    'follows_couldnt': 'Sonsuza kadar koşamazdınız.',
    'follows_quote': 'Takip eden şey her zaman kendinizdir.',
    // Room Content - Line You Crossed
    'line_title': 'Geçtiğiniz Çizgi',
    'line_instruction': 'Soldan sağa tek bir çizgi çizin.',
    'line_click_drag': 'Tıklayın ve sürükleyin',
    'line_hesitations': '{count} tereddüt bulduk.',
    'line_quote': 'Her duraklama bir şüpheyi ortaya çıkarır.',
    // Scary Room Content
    'dont_look_title': 'Bakışını Çevirme',
    'dont_look_away_msg': 'Bakışını çevirme.',
    'dont_look_looked': 'Bakışını çevirdin.',
    'dont_look_seconds': '{count} saniye dikkat',
    'dont_look_times': '{count} kez bakışını çevirdin',
    'dont_look_quote': 'Var olmak verilecek en zor şeydir.',
    'mirror_lies_title': 'Ayna Yalan Söylüyor', 'mirror_lies_msg1': 'Kendine bak.', 'mirror_lies_msg2': 'Bu gerçekten sen misin?', 'mirror_lies_msg3': 'Ayna görmek istediğini gösterir.', 'mirror_lies_msg4': 'Gerçeği ortaya çıkarmak için tıkla.', 'mirror_lies_msg5': 'Her çatlak daha fazlasını açığa çıkarır.', 'mirror_lies_msg6': 'Yansımanın arkasında hiçbir şey yok.', 'mirror_lies_quote': 'Ayna seni hiç göstermedi. Sadece inandığını.',
    'winter_title': 'En Uzun Gece', 'winter_date': '21 ARALIK', 'winter_msg1': 'En uzun gece geldi.', 'winter_msg2': 'Karanlıkta, taşıdıklarımızı buluruz.', 'winter_msg3': 'Neyi bırakacaksın? Neyi tutacaksın?', 'winter_releasing': 'Bırakıyorum:', 'winter_keeping': 'Tutuyorum:', 'winter_release_placeholder': 'Artık işe yaramayan...', 'winter_keep_placeholder': 'Güç veren...', 'winter_enter_light': 'Işığa gir', 'winter_released': 'Bırakılan:', 'winter_kept': 'Tutulan:', 'winter_light_returns': 'Bu geceden itibaren, ışık geri dönüyor.', 'winter_quote': 'En uzun gece bize en derin karanlıkta bile ışığın zaten geri döndüğünü öğretir.',
    'halloween_title': 'Perde Odası', 'halloween_date': '31 EKİM', 'halloween_msg1': 'Bu gece perde ince.', 'halloween_msg2': 'Gölgelerinde ne gizleniyor?', 'halloween_msg3': 'Taşıdığın korkuya isim ver...', 'halloween_msg4': 'Ona biçim ver ve bırak.', 'halloween_placeholder': 'Korkunu yaz...', 'halloween_release': 'Bırak', 'halloween_dissolves': 'Korkun geceye karışıyor...', 'halloween_quote': 'Korktuğumuz şeyler isim verdiğimizde güçlerini kaybeder. Bırak gitsin.',
    'awake_title': 'Seni Uyanık Tutan', 'awake_locked': 'Bu oda yalnızca gece 10\'dan sonra kullanılabilir.', 'awake_return': 'Gece geldiğinde geri dön.', 'awake_press_enter': 'Enter\'a bas', 'awake_named': 'Şimdi adını koydun.', 'awake_quote': 'Karanlık, gün ışığında kaçındığını bilir.',
    'voice_meaning_title': 'Anlamsız Sesin', 'voice_meaning_prompt': 'Mırıldan. Nefes al. Herhangi bir ses çıkar. Kelime yok.', 'voice_meaning_done': 'Bu senin varlığındı. Başka bir şey değil.', 'voice_meaning_hold': 'Basılı tut', 'voice_meaning_recording': 'Kaydediliyor...', 'voice_meaning_no_playback': 'Geri oynatma yok. Ses sadece o anda var oldu.', 'voice_meaning_quote': 'Varsın. Bu tek içerik.',
    'watching_msg1': 'Burada yalnızsın.', 'watching_msg2': 'Tamamen yalnız.', 'watching_msg3': 'Öyle misin?', 'watching_msg4': 'Başından beri buradaydılar.', 'watching_msg5': 'Her zaman izliyorlar.',
    'shortcut_title': 'Her Zaman Aldığın Kısayol', 'shortcut_instruction': 'Tanıdıklarını seç.', 'shortcut_1': 'İyi değilken "iyiyim" demek', 'shortcut_2': 'Mevcut olmak yerine telefona bakmak', 'shortcut_3': 'Çatışmadan kaçınmak için sessiz kalmak', 'shortcut_4': 'Yapmak yerine satın almak', 'shortcut_5': '"Doğru zamanı" beklemek', 'shortcut_6': 'Harekete geçmek yerine koşulları suçlamak', 'shortcut_7': 'Büyüme yerine konforu seçmek', 'shortcut_8': 'Uyumak yerine kaydırmak', 'shortcut_9': 'Hayır demek isterken evet demek', 'shortcut_10': 'Yapmak yerine planlamak', 'shortcut_pattern': 'Senin Kalıbın', 'shortcut_selected': 'Seçtiklerin:', 'shortcut_show_pattern': 'Kalıbımı göster', 'shortcut_quote': 'Kısayol yol olur.', 'shortcut_pattern_own': 'Kalıbın senin.', 'pattern_avoidance': 'Rahatsız edici olanlardan kaçınırsın.', 'pattern_distraction': 'Dikkat dağınıklığına kaçarsın.', 'pattern_convenience': 'Çabayı kolaylıkla takas edersin.', 'pattern_delay': 'Asla gelmeyen koşulları beklersin.', 'pattern_comfort': 'Kendini rahatsızlıktan korursun.', 'pattern_pleasing': 'Başkalarını rahat tutmak için kaybolursun.',
    'shapes_q1': 'Ne sıklıkla huzur hissedersin?', 'shapes_q2': 'Kendine ne kadar güvenirsin?', 'shapes_q3': 'Başkalarıyla ne kadar bağlı hissedersin?', 'shapes_q4': 'Zamanının ne kadarı gerçekten senin?', 'shapes_q5': 'Gelecek ne kadar ağır hissettiriyor?', 'shapes_result_title': 'Şekiller Olarak Hayatın', 'shapes_no_labels': 'Etiket yok. Yorum yok.', 'shapes_yours': 'Gördüğün senin anlamlandırman için.', 'shapes_quote': 'Şekil, kelimesiz anlamdır.', 'shapes_scale': '1 = hiç, 5 = tamamen',
    'read_title': 'Bunu Sesli Oku', 'read_stmt1': 'Bir şeyden kaçınıyorum.', 'read_stmt2': 'Korkuyorum.', 'read_stmt3': 'Ne yaptığımı bilmiyorum.', 'read_stmt4': 'Yardıma ihtiyacım var.', 'read_stmt5': 'Yeterliyim.', 'read_stmt6': 'Numara yapıyordum.', 'read_stmt7': 'Yorgunum.', 'read_stmt8': 'Daha fazlasını istiyorum.', 'read_i_read': 'Sesli okudum', 'read_really': 'Gerçekten söyledin mi?', 'read_skipped': 'Atladım', 'read_result': 'Gerçekleri sesli söyledin.', 'read_quote': 'Gerçeği söylemek onu gerçek yapar.',
    'sentence_title': 'Kaçındığın Cümle', 'sentence_sub': 'Bu cümleyi tamamla. Tamamlamadan gidemezsin.', 'sentence_prompt': '...yı bıraksam daha mutlu olurdum', 'sentence_cant_leave': 'Bir şey yazmadan gidemezsin.', 'sentence_knew': 'Bunu zaten biliyordun.', 'sentence_quote': 'İlk adım onu söylemek.',
    'say_once_title': 'Bir Kez Söyle', 'say_once_prompt': 'Bir cümle kaydet. Tekrar dinleyemezsin. Tekrar kaydedemezsin.', 'say_once_done': 'Söylendi. Tekrar yok. Yeniden yok.', 'say_once_stop': 'Durdur', 'say_once_record': 'Kaydet', 'say_once_quote': 'Bazı şeyler sadece bir kez söylenmek içindir.',
    'newyear_title': 'Gelecekteki Kendine Mektup', 'newyear_threshold': 'EŞİK', 'newyear_placeholder': 'Sevgili gelecekteki ben...', 'newyear_seal': 'Mektubu mühürle', 'newyear_waiting': 'Bu mektup seni bekleyecek.', 'newyear_quote': 'Her son bir kapıdır. Niyetle geç.',
    'avoid_title': 'Kaçındığın Oda', 'avoid_sub': 'Atladığın şey bir şey söyler.', 'avoid_not_entered': 'Girmediğin:', 'avoid_visited_all': 'Her ücretsiz odayı ziyaret ettin.', 'avoid_not_pattern': 'Belki kaçınma senin kalıbın değil.', 'avoid_quote': 'Kaçınma da bir seçim.',
    'who_q1': 'Para sorun olmasaydı ne yapardın?', 'who_q2': 'Bir partide kendini nasıl tanıtırdın?', 'who_q3': 'Seni ne canlı hissettirir?', 'who_q4': 'Arkadaşların nede iyi olduğunu söylerdi?', 'who_q5': 'Çocukken ne yapmayı severdin?', 'who_result_title': 'Unvansız Sen', 'who_done': 'Unvan gittiğinde sen bu oluyorsun.', 'who_quote': 'Kimlik ne yaptığın değil. Durduğunda kalan.', 'who_placeholder': 'Dürüst ol...', 'who_see_myself': 'Kendimi gör', 'question': 'Soru',
    'lastconv_title': 'Son Konuşma', 'lastconv_sub': 'Asla göndermeyeceğini yaz.', 'lastconv_to': 'Kime:', 'lastconv_message': 'Mesaj:', 'lastconv_to_placeholder': 'Bunu duyması gereken biri...', 'lastconv_msg_placeholder': 'Hep söylemek istediğin...', 'lastconv_not_saved': 'Bu mesaj kaydedilmeyecek veya paylaşılmayacak.', 'lastconv_seal': 'Mektubu Mühürle', 'lastconv_wrote_to': 'Yazdığın kişi:', 'lastconv_never_sent': 'Bu mesaj asla gönderilmeyecek.', 'lastconv_point': 'Ama yazmak amaçtı.', 'lastconv_quote': 'Bazı kelimeler var olmalı, duyulmasa bile.',
    'priorities_title': 'Gerçek Önceliklerin', 'priorities_sub': 'Her birine haftada kaç saat ayırıyorsun?', 'priorities_result': 'Gerçek Önceliklerin', 'priorities_most_time': 'En çok zamanını harcadığın:', 'priorities_truth': 'Zaman gerçeği ortaya çıkarır. Kelimeler yalan söyleyebilir, programlar söyleyemez.', 'priorities_quote': 'Tekrar tekrar yaptığın şey sensin.', 'priorities_show': 'Gerçeğimi Göster', 'priority_work': 'İş', 'priority_family': 'Aile', 'priority_friends': 'Arkadaşlar', 'priority_health': 'Sağlık', 'priority_learning': 'Öğrenme', 'priority_entertainment': 'Eğlence', 'priority_social': 'Sosyal Medya', 'priority_sleep': 'Uyku',
    'voice_intro': 'Karanlıkta bir ses konuşuyor...', 'voice_silence': '(sessizlik)', 'voice_quote': 'Bazı sesler eve giden yolu bulamaz.',
    'knows_tell': 'Adını söyle.', 'knows_placeholder': 'Adın...', 'knows_warning': '(Buraya gelmemeliydın)', 'knows_quote': 'İsimlerin gücü var. Seninki zaten biliniyordu.',
    'door_msg1': 'Kapıda bir şey var.', 'door_stopped': '(Vuruşlar durdu)', 'door_quote': 'Bazı kapılar kapalı kalmalı.',
    'eyes_msg1': 'Bir çift göz izliyor.', 'eyes_unsee': 'Onları asla görmemiş olamazsın.', 'eyes_quote': 'Bazı şeyler fark edildiğinde görmezden gelinemez.',
    'moon_locked': 'Bu oda sadece dolunayda açılır.', 'moon_not_full': 'Bu gece dolunay değil. Döngü tamamlandığında geri dön.', 'moon_quote': 'Bazı gerçekler sadece döngülerde ortaya çıkar.',
    'at317_title': 'Sabah 3:17\'de Sen', 'at317_locked': 'Bu oda sabah 2 ile 5 arasında açılır.', 'at317_current': 'Şu anki saat:', 'at317_quote': 'Gece adını bilir.',
    'mirrors_you_are_here': 'Buradasın.',
    'listen_title': 'Kendini Dinle', 'listen_recording': 'Kaydediyor...', 'listen_start': 'Başla', 'listen_play': 'Oynat', 'listen_instruction': '10 saniye kaydet. Ne istersen söyle.', 'listen_wait': 'Bekle. Sonra dinle.', 'listen_done': 'Bu senin sesindi. Yavaşlatılmış. Tuhaf.', 'listen_speed': '0.5x hızda çalıyor', 'listen_quote': 'Sesin farklı geliyor çünkü sonunda duydun.',
    'export_title': 'Yolculuğunu Dışa Aktar', 'export_subtitle': 'Bu yerin bir parçasını yanında götür', 'export_tab_export': 'Dışa Aktar', 'export_tab_import': 'İçe Aktar', 'export_your_journey': 'YOLCULUĞUN', 'export_since': 'Başlangıç', 'export_time_spent': 'harcanan süre', 'export_rooms_visited': 'ziyaret edilen odalar', 'export_rooms_completed': 'tamamlanan odalar', 'export_fragments': 'parçalar', 'export_achievements_unlocked': 'kilidi açılan başarılar', 'export_truths_recorded': 'kaydedilen gerçekler', 'export_text_file': 'Metin dosyası', 'export_json_file': 'JSON dosyası', 'export_copied': 'Kopyalandı!', 'export_copy_clipboard': 'Panoya kopyala', 'export_data_stays': 'Veriler sende kalır. Biz saklamayız.', 'export_import_desc': 'Daha önce dışa aktarılmış bir yolculuğu içe aktar', 'export_json_only': 'Yalnızca JSON formatı', 'export_select_file': 'Dosya seç', 'export_merge_note': 'Mevcut yolculuk verileriyle birleştirilecek.', 'export_done': 'Dışa aktarıldı.', 'export_done_desc': 'Nerede olduğunun kaydı. Neyle yüzleştiğin.', 'export_quote': 'Bazı yolculuklar belgelemeye değer. Başkaları için değil. Kendin için.', 'export_import_success': 'Yolculuk başarıyla içe aktarıldı', 'export_import_error': 'Geçersiz yolculuk dosyası',
    'waitroom_title': 'Bekleme Odası', 'waitroom_subtitle': 'Bazı kapılar sadece gruplar için açılır.', 'waitroom_open': 'Kapı açık.', 'waitroom_waiting': 'bekliyor', 'waitroom_time_waited': 'Beklenen süre', 'waitroom_users_needed': 'Gereken kullanıcılar', 'waitroom_someone_joined': 'Biri beklemeye katıldı.', 'waitroom_someone_left': 'Biri ayrıldı.', 'waitroom_room_opens': 'Oda açılıyor.', 'waitroom_patience': 'Sabır ve varlık, gücün açamayacağını açar.', 'waitroom_souls_waited': '{count} ruh birlikte bekledi.', 'waitroom_seconds_stillness': '{count} saniye kolektif sessizlik.', 'waitroom_quote': 'Bazı şeyler acele ettirilemez. Bazı kapılar tek başına açılamaz.',
    'capsule_title': 'Zaman Kapsülü', 'capsule_subtitle': 'Gelecekteki kendine bir mesaj', 'capsule_what_tell': 'Kendine ne söylemen gerekiyor?', 'capsule_placeholder': 'Sevgili gelecekteki ben...', 'capsule_when_arrive': 'Ne zaman ulaşmalı?', 'capsule_30_days': '30 gün', 'capsule_90_days': '90 gün', 'capsule_1_year': '1 yıl', 'capsule_seal': 'Kapsülü mühürle', 'capsule_waiting': 'kapsül bekliyor', 'capsule_sealed': 'Mühürlendi.', 'capsule_will_wait': 'Mesajın seni bekleyecek.', 'capsule_return_in': 'Açmak için {days} gün sonra geri gel.', 'capsule_quote_sealed': 'Zaman her şeyi değiştirir. Seni bile.', 'capsule_arrived': 'Bir mesaj geldi.', 'capsule_from_past': '{days} gün önceki senden.', 'capsule_open': 'Kapsülü aç', 'capsule_you_wrote': 'Bunu kendine yazdın:', 'capsule_remember': 'Bunu yazdığını hatırlıyor musun?', 'capsule_quote_opened': 'Bunu yazan kişi gitti. Sen kaldın.',
    'contradiction_title': 'Çelişki Odası', 'contradiction_subtitle': 'Gerçeklerinin çarpıştığı yer', 'contradiction_not_enough': 'Henüz yeterli veri yok.', 'contradiction_answer_more': 'Odalarda daha fazla soruyu yanıtla.', 'contradiction_answers_recorded': '{count}/5 yanıt kaydedildi', 'contradiction_quote_early': 'Çelişkiler karmaşıklık gerektirir. Önce kendininkini oluştur.', 'contradiction_no_found': 'Çelişki bulunamadı.', 'contradiction_consistent': 'Ya olağanüstü tutarlısın...', 'contradiction_careful': '...ya da olağanüstü dikkatli.', 'contradiction_quote_none': 'Tutarlılık ya bilgeliktir ya da gösteri.', 'contradiction_found': '{count} çelişki bulduk', 'contradiction_you_said': 'Dedin ki:', 'contradiction_but_also': 'Ama aynı zamanda:', 'contradiction_see_another': 'Başka birini gör →', 'contradiction_not_weakness': 'Çelişki zayıflık değildir. Derinliğin kanıtıdır.', 'contradiction_quote': 'İçimizde çoğulluklar barınır. Bazıları anlaşamaz.',
    'entropy_living': 'canlı', 'entropy_dead': 'ölü', 'entropy_plant_seed': 'Tohum ek', 'entropy_instructions': 'Ekmek için boş alanlara tıkla. Sulamak için bitkilere tıkla. Ölü bitkiler kaldırılabilir.', 'entropy_reflect': 'Bahçen üzerine düşün', 'entropy_lives': 'Bahçen yaşıyor.', 'entropy_thrive': 'Döndüğün için {living} bitki gelişiyor.', 'entropy_absence': '{dead} sana yokluğu hatırlatıyor.',
    'palace_title': 'Hafıza Sarayı', 'palace_subtitle': 'Zihinsel bir alan inşa et. Hatırlamak için nesneler yerleştir. Sonra gezin.', 'palace_room_of': 'Oda {current} / {total}', 'palace_click_place': 'Yerleştirmek için tıkla', 'palace_what_represent': 'Bu neyi temsil ediyor?', 'palace_cancel': 'İptal', 'palace_place_object': 'Nesne yerleştir', 'palace_objects_count': 'Sarayında {count} nesne', 'palace_take_tour': 'Tura çık', 'palace_empty_room': 'Bu oda boş', 'palace_previous': 'Önceki', 'palace_next_room': 'Sonraki oda', 'palace_complete_tour': 'Turu tamamla', 'palace_remembers': 'Sarayın hatırlıyor.', 'palace_geography': 'İnşa ettiğin anlam coğrafyası.', 'palace_quote': 'Zihin bir saraydır. İçine ne koyduğun kim olacağını belirler.',
    'echoes_subtitle': 'Diğer yolculardan parçalar', 'echoes_from': 'dan', 'echoes_ago': 'önce', 'echoes_arriving': 'Daha fazla yankı geliyor...', 'echoes_reveal': 'Bunlar onların cevapları değil. Senin cevapların.', 'echoes_reveal2': '(Her yankı bir zamanlar senin kendi yansımandı)', 'echoes_quote': 'Eşsiz olduğumuzu sanırız. Sadece birbirimizin yankısıyız.',
    'revisit_subtitle': 'Nasıl değiştin', 'revisit_not_enough': 'Henüz yeterince soruyu yanıtlamadın.', 'revisit_explore': 'Daha fazla oda keşfet. İzlerini bırak. Sonra geri dön.', 'revisit_quote1': 'Değişim tarih gerektirir. Tarih zaman gerektirir.', 'revisit_none_changed': 'Ama henüz hiçbiri değişmedi. Odaları tekrar ziyaret et. Tekrar cevapla.', 'revisit_show_evolved': 'Nasıl evrildiğini sana göstereceğiz.', 'revisit_quote2': 'Değişimi görmek için önce değişmelisin.', 'revisit_then': 'O zaman', 'revisit_now': 'Şimdi', 'revisit_one_shift': 'Bir değişim. İnce ama gerçek.', 'revisit_each_change': 'Her değişim küçük bir ölümdür. Ve küçük bir doğum.', 'revisit_quote3': 'Eskiden olduğun kişi değilsin. Henüz olacağın kişi de değilsin.',
  },
};
